//     Underscore.js 1.9.1
//     http://underscorejs.org
//     (c) 2009-2018 Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
//     Underscore may be freely distributed under the MIT license.

(function() {

  // Baseline setup
  // --------------

  // Establish the root object, `window` (`self`) in the browser, `global`
  // on the server, or `this` in some virtual machines. We use `self`
  // instead of `window` for `WebWorker` support.
  var root = typeof self == 'object' && self.self === self && self ||
            typeof global == 'object' && global.global === global && global ||
            this ||
            {};

  // Save the previous value of the `_` variable.
  var previousUnderscore = root._;

  // Save bytes in the minified (but not gzipped) version:
  var ArrayProto = Array.prototype, ObjProto = Object.prototype;
  var SymbolProto = typeof Symbol !== 'undefined' ? Symbol.prototype : null;

  // Create quick reference variables for speed access to core prototypes.
  var push = ArrayProto.push,
      slice = ArrayProto.slice,
      toString = ObjProto.toString,
      hasOwnProperty = ObjProto.hasOwnProperty;

  // All **ECMAScript 5** native function implementations that we hope to use
  // are declared here.
  var nativeIsArray = Array.isArray,
      nativeKeys = Object.keys,
      nativeCreate = Object.create;

  // Naked function reference for surrogate-prototype-swapping.
  var Ctor = function(){};

  // Create a safe reference to the Underscore object for use below.
  var _ = function(obj) {
    if (obj instanceof _) return obj;
    if (!(this instanceof _)) return new _(obj);
    this._wrapped = obj;
  };

  // Export the Underscore object for **Node.js**, with
  // backwards-compatibility for their old module API. If we're in
  // the browser, add `_` as a global object.
  // (`nodeType` is checked to ensure that `module`
  // and `exports` are not HTML elements.)
  if (typeof exports != 'undefined' && !exports.nodeType) {
    if (typeof module != 'undefined' && !module.nodeType && module.exports) {
      exports = module.exports = _;
    }
    exports._ = _;
  } else {
    root._ = _;
  }

  // Current version.
  _.VERSION = '1.9.1';

  // Internal function that returns an efficient (for current engines) version
  // of the passed-in callback, to be repeatedly applied in other Underscore
  // functions.
  var optimizeCb = function(func, context, argCount) {
    if (context === void 0) return func;
    switch (argCount == null ? 3 : argCount) {
      case 1: return function(value) {
        return func.call(context, value);
      };
      // The 2-argument case is omitted because we’re not using it.
      case 3: return function(value, index, collection) {
        return func.call(context, value, index, collection);
      };
      case 4: return function(accumulator, value, index, collection) {
        return func.call(context, accumulator, value, index, collection);
      };
    }
    return function() {
      return func.apply(context, arguments);
    };
  };

  var builtinIteratee;

  // An internal function to generate callbacks that can be applied to each
  // element in a collection, returning the desired result — either `identity`,
  // an arbitrary callback, a property matcher, or a property accessor.
  var cb = function(value, context, argCount) {
    if (_.iteratee !== builtinIteratee) return _.iteratee(value, context);
    if (value == null) return _.identity;
    if (_.isFunction(value)) return optimizeCb(value, context, argCount);
    if (_.isObject(value) && !_.isArray(value)) return _.matcher(value);
    return _.property(value);
  };

  // External wrapper for our callback generator. Users may customize
  // `_.iteratee` if they want additional predicate/iteratee shorthand styles.
  // This abstraction hides the internal-only argCount argument.
  _.iteratee = builtinIteratee = function(value, context) {
    return cb(value, context, Infinity);
  };

  // Some functions take a variable number of arguments, or a few expected
  // arguments at the beginning and then a variable number of values to operate
  // on. This helper accumulates all remaining arguments past the function’s
  // argument length (or an explicit `startIndex`), into an array that becomes
  // the last argument. Similar to ES6’s "rest parameter".
  var restArguments = function(func, startIndex) {
    startIndex = startIndex == null ? func.length - 1 : +startIndex;
    return function() {
      var length = Math.max(arguments.length - startIndex, 0),
          rest = Array(length),
          index = 0;
      for (; index < length; index++) {
        rest[index] = arguments[index + startIndex];
      }
      switch (startIndex) {
        case 0: return func.call(this, rest);
        case 1: return func.call(this, arguments[0], rest);
        case 2: return func.call(this, arguments[0], arguments[1], rest);
      }
      var args = Array(startIndex + 1);
      for (index = 0; index < startIndex; index++) {
        args[index] = arguments[index];
      }
      args[startIndex] = rest;
      return func.apply(this, args);
    };
  };

  // An internal function for creating a new object that inherits from another.
  var baseCreate = function(prototype) {
    if (!_.isObject(prototype)) return {};
    if (nativeCreate) return nativeCreate(prototype);
    Ctor.prototype = prototype;
    var result = new Ctor;
    Ctor.prototype = null;
    return result;
  };

  var shallowProperty = function(key) {
    return function(obj) {
      return obj == null ? void 0 : obj[key];
    };
  };

  var has = function(obj, path) {
    return obj != null && hasOwnProperty.call(obj, path);
  }

  var deepGet = function(obj, path) {
    var length = path.length;
    for (var i = 0; i < length; i++) {
      if (obj == null) return void 0;
      obj = obj[path[i]];
    }
    return length ? obj : void 0;
  };

  // Helper for collection methods to determine whether a collection
  // should be iterated as an array or as an object.
  // Related: http://people.mozilla.org/~jorendorff/es6-draft.html#sec-tolength
  // Avoids a very nasty iOS 8 JIT bug on ARM-64. #2094
  var MAX_ARRAY_INDEX = Math.pow(2, 53) - 1;
  var getLength = shallowProperty('length');
  var isArrayLike = function(collection) {
    var length = getLength(collection);
    return typeof length == 'number' && length >= 0 && length <= MAX_ARRAY_INDEX;
  };

  // Collection Functions
  // --------------------

  // The cornerstone, an `each` implementation, aka `forEach`.
  // Handles raw objects in addition to array-likes. Treats all
  // sparse array-likes as if they were dense.
  _.each = _.forEach = function(obj, iteratee, context) {
    iteratee = optimizeCb(iteratee, context);
    var i, length;
    if (isArrayLike(obj)) {
      for (i = 0, length = obj.length; i < length; i++) {
        iteratee(obj[i], i, obj);
      }
    } else {
      var keys = _.keys(obj);
      for (i = 0, length = keys.length; i < length; i++) {
        iteratee(obj[keys[i]], keys[i], obj);
      }
    }
    return obj;
  };

  // Return the results of applying the iteratee to each element.
  _.map = _.collect = function(obj, iteratee, context) {
    iteratee = cb(iteratee, context);
    var keys = !isArrayLike(obj) && _.keys(obj),
        length = (keys || obj).length,
        results = Array(length);
    for (var index = 0; index < length; index++) {
      var currentKey = keys ? keys[index] : index;
      results[index] = iteratee(obj[currentKey], currentKey, obj);
    }
    return results;
  };

  // Create a reducing function iterating left or right.
  var createReduce = function(dir) {
    // Wrap code that reassigns argument variables in a separate function than
    // the one that accesses `arguments.length` to avoid a perf hit. (#1991)
    var reducer = function(obj, iteratee, memo, initial) {
      var keys = !isArrayLike(obj) && _.keys(obj),
          length = (keys || obj).length,
          index = dir > 0 ? 0 : length - 1;
      if (!initial) {
        memo = obj[keys ? keys[index] : index];
        index += dir;
      }
      for (; index >= 0 && index < length; index += dir) {
        var currentKey = keys ? keys[index] : index;
        memo = iteratee(memo, obj[currentKey], currentKey, obj);
      }
      return memo;
    };

    return function(obj, iteratee, memo, context) {
      var initial = arguments.length >= 3;
      return reducer(obj, optimizeCb(iteratee, context, 4), memo, initial);
    };
  };

  // **Reduce** builds up a single result from a list of values, aka `inject`,
  // or `foldl`.
  _.reduce = _.foldl = _.inject = createReduce(1);

  // The right-associative version of reduce, also known as `foldr`.
  _.reduceRight = _.foldr = createReduce(-1);

  // Return the first value which passes a truth test. Aliased as `detect`.
  _.find = _.detect = function(obj, predicate, context) {
    var keyFinder = isArrayLike(obj) ? _.findIndex : _.findKey;
    var key = keyFinder(obj, predicate, context);
    if (key !== void 0 && key !== -1) return obj[key];
  };

  // Return all the elements that pass a truth test.
  // Aliased as `select`.
  _.filter = _.select = function(obj, predicate, context) {
    var results = [];
    predicate = cb(predicate, context);
    _.each(obj, function(value, index, list) {
      if (predicate(value, index, list)) results.push(value);
    });
    return results;
  };

  // Return all the elements for which a truth test fails.
  _.reject = function(obj, predicate, context) {
    return _.filter(obj, _.negate(cb(predicate)), context);
  };

  // Determine whether all of the elements match a truth test.
  // Aliased as `all`.
  _.every = _.all = function(obj, predicate, context) {
    predicate = cb(predicate, context);
    var keys = !isArrayLike(obj) && _.keys(obj),
        length = (keys || obj).length;
    for (var index = 0; index < length; index++) {
      var currentKey = keys ? keys[index] : index;
      if (!predicate(obj[currentKey], currentKey, obj)) return false;
    }
    return true;
  };

  // Determine if at least one element in the object matches a truth test.
  // Aliased as `any`.
  _.some = _.any = function(obj, predicate, context) {
    predicate = cb(predicate, context);
    var keys = !isArrayLike(obj) && _.keys(obj),
        length = (keys || obj).length;
    for (var index = 0; index < length; index++) {
      var currentKey = keys ? keys[index] : index;
      if (predicate(obj[currentKey], currentKey, obj)) return true;
    }
    return false;
  };

  // Determine if the array or object contains a given item (using `===`).
  // Aliased as `includes` and `include`.
  _.contains = _.includes = _.include = function(obj, item, fromIndex, guard) {
    if (!isArrayLike(obj)) obj = _.values(obj);
    if (typeof fromIndex != 'number' || guard) fromIndex = 0;
    return _.indexOf(obj, item, fromIndex) >= 0;
  };

  // Invoke a method (with arguments) on every item in a collection.
  _.invoke = restArguments(function(obj, path, args) {
    var contextPath, func;
    if (_.isFunction(path)) {
      func = path;
    } else if (_.isArray(path)) {
      contextPath = path.slice(0, -1);
      path = path[path.length - 1];
    }
    return _.map(obj, function(context) {
      var method = func;
      if (!method) {
        if (contextPath && contextPath.length) {
          context = deepGet(context, contextPath);
        }
        if (context == null) return void 0;
        method = context[path];
      }
      return method == null ? method : method.apply(context, args);
    });
  });

  // Convenience version of a common use case of `map`: fetching a property.
  _.pluck = function(obj, key) {
    return _.map(obj, _.property(key));
  };

  // Convenience version of a common use case of `filter`: selecting only objects
  // containing specific `key:value` pairs.
  _.where = function(obj, attrs) {
    return _.filter(obj, _.matcher(attrs));
  };

  // Convenience version of a common use case of `find`: getting the first object
  // containing specific `key:value` pairs.
  _.findWhere = function(obj, attrs) {
    return _.find(obj, _.matcher(attrs));
  };

  // Return the maximum element (or element-based computation).
  _.max = function(obj, iteratee, context) {
    var result = -Infinity, lastComputed = -Infinity,
        value, computed;
    if (iteratee == null || typeof iteratee == 'number' && typeof obj[0] != 'object' && obj != null) {
      obj = isArrayLike(obj) ? obj : _.values(obj);
      for (var i = 0, length = obj.length; i < length; i++) {
        value = obj[i];
        if (value != null && value > result) {
          result = value;
        }
      }
    } else {
      iteratee = cb(iteratee, context);
      _.each(obj, function(v, index, list) {
        computed = iteratee(v, index, list);
        if (computed > lastComputed || computed === -Infinity && result === -Infinity) {
          result = v;
          lastComputed = computed;
        }
      });
    }
    return result;
  };

  // Return the minimum element (or element-based computation).
  _.min = function(obj, iteratee, context) {
    var result = Infinity, lastComputed = Infinity,
        value, computed;
    if (iteratee == null || typeof iteratee == 'number' && typeof obj[0] != 'object' && obj != null) {
      obj = isArrayLike(obj) ? obj : _.values(obj);
      for (var i = 0, length = obj.length; i < length; i++) {
        value = obj[i];
        if (value != null && value < result) {
          result = value;
        }
      }
    } else {
      iteratee = cb(iteratee, context);
      _.each(obj, function(v, index, list) {
        computed = iteratee(v, index, list);
        if (computed < lastComputed || computed === Infinity && result === Infinity) {
          result = v;
          lastComputed = computed;
        }
      });
    }
    return result;
  };

  // Shuffle a collection.
  _.shuffle = function(obj) {
    return _.sample(obj, Infinity);
  };

  // Sample **n** random values from a collection using the modern version of the
  // [Fisher-Yates shuffle](http://en.wikipedia.org/wiki/Fisher–Yates_shuffle).
  // If **n** is not specified, returns a single random element.
  // The internal `guard` argument allows it to work with `map`.
  _.sample = function(obj, n, guard) {
    if (n == null || guard) {
      if (!isArrayLike(obj)) obj = _.values(obj);
      return obj[_.random(obj.length - 1)];
    }
    var sample = isArrayLike(obj) ? _.clone(obj) : _.values(obj);
    var length = getLength(sample);
    n = Math.max(Math.min(n, length), 0);
    var last = length - 1;
    for (var index = 0; index < n; index++) {
      var rand = _.random(index, last);
      var temp = sample[index];
      sample[index] = sample[rand];
      sample[rand] = temp;
    }
    return sample.slice(0, n);
  };

  // Sort the object's values by a criterion produced by an iteratee.
  _.sortBy = function(obj, iteratee, context) {
    var index = 0;
    iteratee = cb(iteratee, context);
    return _.pluck(_.map(obj, function(value, key, list) {
      return {
        value: value,
        index: index++,
        criteria: iteratee(value, key, list)
      };
    }).sort(function(left, right) {
      var a = left.criteria;
      var b = right.criteria;
      if (a !== b) {
        if (a > b || a === void 0) return 1;
        if (a < b || b === void 0) return -1;
      }
      return left.index - right.index;
    }), 'value');
  };

  // An internal function used for aggregate "group by" operations.
  var group = function(behavior, partition) {
    return function(obj, iteratee, context) {
      var result = partition ? [[], []] : {};
      iteratee = cb(iteratee, context);
      _.each(obj, function(value, index) {
        var key = iteratee(value, index, obj);
        behavior(result, value, key);
      });
      return result;
    };
  };

  // Groups the object's values by a criterion. Pass either a string attribute
  // to group by, or a function that returns the criterion.
  _.groupBy = group(function(result, value, key) {
    if (has(result, key)) result[key].push(value); else result[key] = [value];
  });

  // Indexes the object's values by a criterion, similar to `groupBy`, but for
  // when you know that your index values will be unique.
  _.indexBy = group(function(result, value, key) {
    result[key] = value;
  });

  // Counts instances of an object that group by a certain criterion. Pass
  // either a string attribute to count by, or a function that returns the
  // criterion.
  _.countBy = group(function(result, value, key) {
    if (has(result, key)) result[key]++; else result[key] = 1;
  });

  var reStrSymbol = /[^\ud800-\udfff]|[\ud800-\udbff][\udc00-\udfff]|[\ud800-\udfff]/g;
  // Safely create a real, live array from anything iterable.
  _.toArray = function(obj) {
    if (!obj) return [];
    if (_.isArray(obj)) return slice.call(obj);
    if (_.isString(obj)) {
      // Keep surrogate pair characters together
      return obj.match(reStrSymbol);
    }
    if (isArrayLike(obj)) return _.map(obj, _.identity);
    return _.values(obj);
  };

  // Return the number of elements in an object.
  _.size = function(obj) {
    if (obj == null) return 0;
    return isArrayLike(obj) ? obj.length : _.keys(obj).length;
  };

  // Split a collection into two arrays: one whose elements all satisfy the given
  // predicate, and one whose elements all do not satisfy the predicate.
  _.partition = group(function(result, value, pass) {
    result[pass ? 0 : 1].push(value);
  }, true);

  // Array Functions
  // ---------------

  // Get the first element of an array. Passing **n** will return the first N
  // values in the array. Aliased as `head` and `take`. The **guard** check
  // allows it to work with `_.map`.
  _.first = _.head = _.take = function(array, n, guard) {
    if (array == null || array.length < 1) return n == null ? void 0 : [];
    if (n == null || guard) return array[0];
    return _.initial(array, array.length - n);
  };

  // Returns everything but the last entry of the array. Especially useful on
  // the arguments object. Passing **n** will return all the values in
  // the array, excluding the last N.
  _.initial = function(array, n, guard) {
    return slice.call(array, 0, Math.max(0, array.length - (n == null || guard ? 1 : n)));
  };

  // Get the last element of an array. Passing **n** will return the last N
  // values in the array.
  _.last = function(array, n, guard) {
    if (array == null || array.length < 1) return n == null ? void 0 : [];
    if (n == null || guard) return array[array.length - 1];
    return _.rest(array, Math.max(0, array.length - n));
  };

  // Returns everything but the first entry of the array. Aliased as `tail` and `drop`.
  // Especially useful on the arguments object. Passing an **n** will return
  // the rest N values in the array.
  _.rest = _.tail = _.drop = function(array, n, guard) {
    return slice.call(array, n == null || guard ? 1 : n);
  };

  // Trim out all falsy values from an array.
  _.compact = function(array) {
    return _.filter(array, Boolean);
  };

  // Internal implementation of a recursive `flatten` function.
  var flatten = function(input, shallow, strict, output) {
    output = output || [];
    var idx = output.length;
    for (var i = 0, length = getLength(input); i < length; i++) {
      var value = input[i];
      if (isArrayLike(value) && (_.isArray(value) || _.isArguments(value))) {
        // Flatten current level of array or arguments object.
        if (shallow) {
          var j = 0, len = value.length;
          while (j < len) output[idx++] = value[j++];
        } else {
          flatten(value, shallow, strict, output);
          idx = output.length;
        }
      } else if (!strict) {
        output[idx++] = value;
      }
    }
    return output;
  };

  // Flatten out an array, either recursively (by default), or just one level.
  _.flatten = function(array, shallow) {
    return flatten(array, shallow, false);
  };

  // Return a version of the array that does not contain the specified value(s).
  _.without = restArguments(function(array, otherArrays) {
    return _.difference(array, otherArrays);
  });

  // Produce a duplicate-free version of the array. If the array has already
  // been sorted, you have the option of using a faster algorithm.
  // The faster algorithm will not work with an iteratee if the iteratee
  // is not a one-to-one function, so providing an iteratee will disable
  // the faster algorithm.
  // Aliased as `unique`.
  _.uniq = _.unique = function(array, isSorted, iteratee, context) {
    if (!_.isBoolean(isSorted)) {
      context = iteratee;
      iteratee = isSorted;
      isSorted = false;
    }
    if (iteratee != null) iteratee = cb(iteratee, context);
    var result = [];
    var seen = [];
    for (var i = 0, length = getLength(array); i < length; i++) {
      var value = array[i],
          computed = iteratee ? iteratee(value, i, array) : value;
      if (isSorted && !iteratee) {
        if (!i || seen !== computed) result.push(value);
        seen = computed;
      } else if (iteratee) {
        if (!_.contains(seen, computed)) {
          seen.push(computed);
          result.push(value);
        }
      } else if (!_.contains(result, value)) {
        result.push(value);
      }
    }
    return result;
  };

  // Produce an array that contains the union: each distinct element from all of
  // the passed-in arrays.
  _.union = restArguments(function(arrays) {
    return _.uniq(flatten(arrays, true, true));
  });

  // Produce an array that contains every item shared between all the
  // passed-in arrays.
  _.intersection = function(array) {
    var result = [];
    var argsLength = arguments.length;
    for (var i = 0, length = getLength(array); i < length; i++) {
      var item = array[i];
      if (_.contains(result, item)) continue;
      var j;
      for (j = 1; j < argsLength; j++) {
        if (!_.contains(arguments[j], item)) break;
      }
      if (j === argsLength) result.push(item);
    }
    return result;
  };

  // Take the difference between one array and a number of other arrays.
  // Only the elements present in just the first array will remain.
  _.difference = restArguments(function(array, rest) {
    rest = flatten(rest, true, true);
    return _.filter(array, function(value){
      return !_.contains(rest, value);
    });
  });

  // Complement of _.zip. Unzip accepts an array of arrays and groups
  // each array's elements on shared indices.
  _.unzip = function(array) {
    var length = array && _.max(array, getLength).length || 0;
    var result = Array(length);

    for (var index = 0; index < length; index++) {
      result[index] = _.pluck(array, index);
    }
    return result;
  };

  // Zip together multiple lists into a single array -- elements that share
  // an index go together.
  _.zip = restArguments(_.unzip);

  // Converts lists into objects. Pass either a single array of `[key, value]`
  // pairs, or two parallel arrays of the same length -- one of keys, and one of
  // the corresponding values. Passing by pairs is the reverse of _.pairs.
  _.object = function(list, values) {
    var result = {};
    for (var i = 0, length = getLength(list); i < length; i++) {
      if (values) {
        result[list[i]] = values[i];
      } else {
        result[list[i][0]] = list[i][1];
      }
    }
    return result;
  };

  // Generator function to create the findIndex and findLastIndex functions.
  var createPredicateIndexFinder = function(dir) {
    return function(array, predicate, context) {
      predicate = cb(predicate, context);
      var length = getLength(array);
      var index = dir > 0 ? 0 : length - 1;
      for (; index >= 0 && index < length; index += dir) {
        if (predicate(array[index], index, array)) return index;
      }
      return -1;
    };
  };

  // Returns the first index on an array-like that passes a predicate test.
  _.findIndex = createPredicateIndexFinder(1);
  _.findLastIndex = createPredicateIndexFinder(-1);

  // Use a comparator function to figure out the smallest index at which
  // an object should be inserted so as to maintain order. Uses binary search.
  _.sortedIndex = function(array, obj, iteratee, context) {
    iteratee = cb(iteratee, context, 1);
    var value = iteratee(obj);
    var low = 0, high = getLength(array);
    while (low < high) {
      var mid = Math.floor((low + high) / 2);
      if (iteratee(array[mid]) < value) low = mid + 1; else high = mid;
    }
    return low;
  };

  // Generator function to create the indexOf and lastIndexOf functions.
  var createIndexFinder = function(dir, predicateFind, sortedIndex) {
    return function(array, item, idx) {
      var i = 0, length = getLength(array);
      if (typeof idx == 'number') {
        if (dir > 0) {
          i = idx >= 0 ? idx : Math.max(idx + length, i);
        } else {
          length = idx >= 0 ? Math.min(idx + 1, length) : idx + length + 1;
        }
      } else if (sortedIndex && idx && length) {
        idx = sortedIndex(array, item);
        return array[idx] === item ? idx : -1;
      }
      if (item !== item) {
        idx = predicateFind(slice.call(array, i, length), _.isNaN);
        return idx >= 0 ? idx + i : -1;
      }
      for (idx = dir > 0 ? i : length - 1; idx >= 0 && idx < length; idx += dir) {
        if (array[idx] === item) return idx;
      }
      return -1;
    };
  };

  // Return the position of the first occurrence of an item in an array,
  // or -1 if the item is not included in the array.
  // If the array is large and already in sort order, pass `true`
  // for **isSorted** to use binary search.
  _.indexOf = createIndexFinder(1, _.findIndex, _.sortedIndex);
  _.lastIndexOf = createIndexFinder(-1, _.findLastIndex);

  // Generate an integer Array containing an arithmetic progression. A port of
  // the native Python `range()` function. See
  // [the Python documentation](http://docs.python.org/library/functions.html#range).
  _.range = function(start, stop, step) {
    if (stop == null) {
      stop = start || 0;
      start = 0;
    }
    if (!step) {
      step = stop < start ? -1 : 1;
    }

    var length = Math.max(Math.ceil((stop - start) / step), 0);
    var range = Array(length);

    for (var idx = 0; idx < length; idx++, start += step) {
      range[idx] = start;
    }

    return range;
  };

  // Chunk a single array into multiple arrays, each containing `count` or fewer
  // items.
  _.chunk = function(array, count) {
    if (count == null || count < 1) return [];
    var result = [];
    var i = 0, length = array.length;
    while (i < length) {
      result.push(slice.call(array, i, i += count));
    }
    return result;
  };

  // Function (ahem) Functions
  // ------------------

  // Determines whether to execute a function as a constructor
  // or a normal function with the provided arguments.
  var executeBound = function(sourceFunc, boundFunc, context, callingContext, args) {
    if (!(callingContext instanceof boundFunc)) return sourceFunc.apply(context, args);
    var self = baseCreate(sourceFunc.prototype);
    var result = sourceFunc.apply(self, args);
    if (_.isObject(result)) return result;
    return self;
  };

  // Create a function bound to a given object (assigning `this`, and arguments,
  // optionally). Delegates to **ECMAScript 5**'s native `Function.bind` if
  // available.
  _.bind = restArguments(function(func, context, args) {
    if (!_.isFunction(func)) throw new TypeError('Bind must be called on a function');
    var bound = restArguments(function(callArgs) {
      return executeBound(func, bound, context, this, args.concat(callArgs));
    });
    return bound;
  });

  // Partially apply a function by creating a version that has had some of its
  // arguments pre-filled, without changing its dynamic `this` context. _ acts
  // as a placeholder by default, allowing any combination of arguments to be
  // pre-filled. Set `_.partial.placeholder` for a custom placeholder argument.
  _.partial = restArguments(function(func, boundArgs) {
    var placeholder = _.partial.placeholder;
    var bound = function() {
      var position = 0, length = boundArgs.length;
      var args = Array(length);
      for (var i = 0; i < length; i++) {
        args[i] = boundArgs[i] === placeholder ? arguments[position++] : boundArgs[i];
      }
      while (position < arguments.length) args.push(arguments[position++]);
      return executeBound(func, bound, this, this, args);
    };
    return bound;
  });

  _.partial.placeholder = _;

  // Bind a number of an object's methods to that object. Remaining arguments
  // are the method names to be bound. Useful for ensuring that all callbacks
  // defined on an object belong to it.
  _.bindAll = restArguments(function(obj, keys) {
    keys = flatten(keys, false, false);
    var index = keys.length;
    if (index < 1) throw new Error('bindAll must be passed function names');
    while (index--) {
      var key = keys[index];
      obj[key] = _.bind(obj[key], obj);
    }
  });

  // Memoize an expensive function by storing its results.
  _.memoize = function(func, hasher) {
    var memoize = function(key) {
      var cache = memoize.cache;
      var address = '' + (hasher ? hasher.apply(this, arguments) : key);
      if (!has(cache, address)) cache[address] = func.apply(this, arguments);
      return cache[address];
    };
    memoize.cache = {};
    return memoize;
  };

  // Delays a function for the given number of milliseconds, and then calls
  // it with the arguments supplied.
  _.delay = restArguments(function(func, wait, args) {
    return setTimeout(function() {
      return func.apply(null, args);
    }, wait);
  });

  // Defers a function, scheduling it to run after the current call stack has
  // cleared.
  _.defer = _.partial(_.delay, _, 1);

  // Returns a function, that, when invoked, will only be triggered at most once
  // during a given window of time. Normally, the throttled function will run
  // as much as it can, without ever going more than once per `wait` duration;
  // but if you'd like to disable the execution on the leading edge, pass
  // `{leading: false}`. To disable execution on the trailing edge, ditto.
  _.throttle = function(func, wait, options) {
    var timeout, context, args, result;
    var previous = 0;
    if (!options) options = {};

    var later = function() {
      previous = options.leading === false ? 0 : _.now();
      timeout = null;
      result = func.apply(context, args);
      if (!timeout) context = args = null;
    };

    var throttled = function() {
      var now = _.now();
      if (!previous && options.leading === false) previous = now;
      var remaining = wait - (now - previous);
      context = this;
      args = arguments;
      if (remaining <= 0 || remaining > wait) {
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
        previous = now;
        result = func.apply(context, args);
        if (!timeout) context = args = null;
      } else if (!timeout && options.trailing !== false) {
        timeout = setTimeout(later, remaining);
      }
      return result;
    };

    throttled.cancel = function() {
      clearTimeout(timeout);
      previous = 0;
      timeout = context = args = null;
    };

    return throttled;
  };

  // Returns a function, that, as long as it continues to be invoked, will not
  // be triggered. The function will be called after it stops being called for
  // N milliseconds. If `immediate` is passed, trigger the function on the
  // leading edge, instead of the trailing.
  _.debounce = function(func, wait, immediate) {
    var timeout, result;

    var later = function(context, args) {
      timeout = null;
      if (args) result = func.apply(context, args);
    };

    var debounced = restArguments(function(args) {
      if (timeout) clearTimeout(timeout);
      if (immediate) {
        var callNow = !timeout;
        timeout = setTimeout(later, wait);
        if (callNow) result = func.apply(this, args);
      } else {
        timeout = _.delay(later, wait, this, args);
      }

      return result;
    });

    debounced.cancel = function() {
      clearTimeout(timeout);
      timeout = null;
    };

    return debounced;
  };

  // Returns the first function passed as an argument to the second,
  // allowing you to adjust arguments, run code before and after, and
  // conditionally execute the original function.
  _.wrap = function(func, wrapper) {
    return _.partial(wrapper, func);
  };

  // Returns a negated version of the passed-in predicate.
  _.negate = function(predicate) {
    return function() {
      return !predicate.apply(this, arguments);
    };
  };

  // Returns a function that is the composition of a list of functions, each
  // consuming the return value of the function that follows.
  _.compose = function() {
    var args = arguments;
    var start = args.length - 1;
    return function() {
      var i = start;
      var result = args[start].apply(this, arguments);
      while (i--) result = args[i].call(this, result);
      return result;
    };
  };

  // Returns a function that will only be executed on and after the Nth call.
  _.after = function(times, func) {
    return function() {
      if (--times < 1) {
        return func.apply(this, arguments);
      }
    };
  };

  // Returns a function that will only be executed up to (but not including) the Nth call.
  _.before = function(times, func) {
    var memo;
    return function() {
      if (--times > 0) {
        memo = func.apply(this, arguments);
      }
      if (times <= 1) func = null;
      return memo;
    };
  };

  // Returns a function that will be executed at most one time, no matter how
  // often you call it. Useful for lazy initialization.
  _.once = _.partial(_.before, 2);

  _.restArguments = restArguments;

  // Object Functions
  // ----------------

  // Keys in IE < 9 that won't be iterated by `for key in ...` and thus missed.
  var hasEnumBug = !{toString: null}.propertyIsEnumerable('toString');
  var nonEnumerableProps = ['valueOf', 'isPrototypeOf', 'toString',
    'propertyIsEnumerable', 'hasOwnProperty', 'toLocaleString'];

  var collectNonEnumProps = function(obj, keys) {
    var nonEnumIdx = nonEnumerableProps.length;
    var constructor = obj.constructor;
    var proto = _.isFunction(constructor) && constructor.prototype || ObjProto;

    // Constructor is a special case.
    var prop = 'constructor';
    if (has(obj, prop) && !_.contains(keys, prop)) keys.push(prop);

    while (nonEnumIdx--) {
      prop = nonEnumerableProps[nonEnumIdx];
      if (prop in obj && obj[prop] !== proto[prop] && !_.contains(keys, prop)) {
        keys.push(prop);
      }
    }
  };

  // Retrieve the names of an object's own properties.
  // Delegates to **ECMAScript 5**'s native `Object.keys`.
  _.keys = function(obj) {
    if (!_.isObject(obj)) return [];
    if (nativeKeys) return nativeKeys(obj);
    var keys = [];
    for (var key in obj) if (has(obj, key)) keys.push(key);
    // Ahem, IE < 9.
    if (hasEnumBug) collectNonEnumProps(obj, keys);
    return keys;
  };

  // Retrieve all the property names of an object.
  _.allKeys = function(obj) {
    if (!_.isObject(obj)) return [];
    var keys = [];
    for (var key in obj) keys.push(key);
    // Ahem, IE < 9.
    if (hasEnumBug) collectNonEnumProps(obj, keys);
    return keys;
  };

  // Retrieve the values of an object's properties.
  _.values = function(obj) {
    var keys = _.keys(obj);
    var length = keys.length;
    var values = Array(length);
    for (var i = 0; i < length; i++) {
      values[i] = obj[keys[i]];
    }
    return values;
  };

  // Returns the results of applying the iteratee to each element of the object.
  // In contrast to _.map it returns an object.
  _.mapObject = function(obj, iteratee, context) {
    iteratee = cb(iteratee, context);
    var keys = _.keys(obj),
        length = keys.length,
        results = {};
    for (var index = 0; index < length; index++) {
      var currentKey = keys[index];
      results[currentKey] = iteratee(obj[currentKey], currentKey, obj);
    }
    return results;
  };

  // Convert an object into a list of `[key, value]` pairs.
  // The opposite of _.object.
  _.pairs = function(obj) {
    var keys = _.keys(obj);
    var length = keys.length;
    var pairs = Array(length);
    for (var i = 0; i < length; i++) {
      pairs[i] = [keys[i], obj[keys[i]]];
    }
    return pairs;
  };

  // Invert the keys and values of an object. The values must be serializable.
  _.invert = function(obj) {
    var result = {};
    var keys = _.keys(obj);
    for (var i = 0, length = keys.length; i < length; i++) {
      result[obj[keys[i]]] = keys[i];
    }
    return result;
  };

  // Return a sorted list of the function names available on the object.
  // Aliased as `methods`.
  _.functions = _.methods = function(obj) {
    var names = [];
    for (var key in obj) {
      if (_.isFunction(obj[key])) names.push(key);
    }
    return names.sort();
  };

  // An internal function for creating assigner functions.
  var createAssigner = function(keysFunc, defaults) {
    return function(obj) {
      var length = arguments.length;
      if (defaults) obj = Object(obj);
      if (length < 2 || obj == null) return obj;
      for (var index = 1; index < length; index++) {
        var source = arguments[index],
            keys = keysFunc(source),
            l = keys.length;
        for (var i = 0; i < l; i++) {
          var key = keys[i];
          if (!defaults || obj[key] === void 0) obj[key] = source[key];
        }
      }
      return obj;
    };
  };

  // Extend a given object with all the properties in passed-in object(s).
  _.extend = createAssigner(_.allKeys);

  // Assigns a given object with all the own properties in the passed-in object(s).
  // (https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Object/assign)
  _.extendOwn = _.assign = createAssigner(_.keys);

  // Returns the first key on an object that passes a predicate test.
  _.findKey = function(obj, predicate, context) {
    predicate = cb(predicate, context);
    var keys = _.keys(obj), key;
    for (var i = 0, length = keys.length; i < length; i++) {
      key = keys[i];
      if (predicate(obj[key], key, obj)) return key;
    }
  };

  // Internal pick helper function to determine if `obj` has key `key`.
  var keyInObj = function(value, key, obj) {
    return key in obj;
  };

  // Return a copy of the object only containing the whitelisted properties.
  _.pick = restArguments(function(obj, keys) {
    var result = {}, iteratee = keys[0];
    if (obj == null) return result;
    if (_.isFunction(iteratee)) {
      if (keys.length > 1) iteratee = optimizeCb(iteratee, keys[1]);
      keys = _.allKeys(obj);
    } else {
      iteratee = keyInObj;
      keys = flatten(keys, false, false);
      obj = Object(obj);
    }
    for (var i = 0, length = keys.length; i < length; i++) {
      var key = keys[i];
      var value = obj[key];
      if (iteratee(value, key, obj)) result[key] = value;
    }
    return result;
  });

  // Return a copy of the object without the blacklisted properties.
  _.omit = restArguments(function(obj, keys) {
    var iteratee = keys[0], context;
    if (_.isFunction(iteratee)) {
      iteratee = _.negate(iteratee);
      if (keys.length > 1) context = keys[1];
    } else {
      keys = _.map(flatten(keys, false, false), String);
      iteratee = function(value, key) {
        return !_.contains(keys, key);
      };
    }
    return _.pick(obj, iteratee, context);
  });

  // Fill in a given object with default properties.
  _.defaults = createAssigner(_.allKeys, true);

  // Creates an object that inherits from the given prototype object.
  // If additional properties are provided then they will be added to the
  // created object.
  _.create = function(prototype, props) {
    var result = baseCreate(prototype);
    if (props) _.extendOwn(result, props);
    return result;
  };

  // Create a (shallow-cloned) duplicate of an object.
  _.clone = function(obj) {
    if (!_.isObject(obj)) return obj;
    return _.isArray(obj) ? obj.slice() : _.extend({}, obj);
  };

  // Invokes interceptor with the obj, and then returns obj.
  // The primary purpose of this method is to "tap into" a method chain, in
  // order to perform operations on intermediate results within the chain.
  _.tap = function(obj, interceptor) {
    interceptor(obj);
    return obj;
  };

  // Returns whether an object has a given set of `key:value` pairs.
  _.isMatch = function(object, attrs) {
    var keys = _.keys(attrs), length = keys.length;
    if (object == null) return !length;
    var obj = Object(object);
    for (var i = 0; i < length; i++) {
      var key = keys[i];
      if (attrs[key] !== obj[key] || !(key in obj)) return false;
    }
    return true;
  };


  // Internal recursive comparison function for `isEqual`.
  var eq, deepEq;
  eq = function(a, b, aStack, bStack) {
    // Identical objects are equal. `0 === -0`, but they aren't identical.
    // See the [Harmony `egal` proposal](http://wiki.ecmascript.org/doku.php?id=harmony:egal).
    if (a === b) return a !== 0 || 1 / a === 1 / b;
    // `null` or `undefined` only equal to itself (strict comparison).
    if (a == null || b == null) return false;
    // `NaN`s are equivalent, but non-reflexive.
    if (a !== a) return b !== b;
    // Exhaust primitive checks
    var type = typeof a;
    if (type !== 'function' && type !== 'object' && typeof b != 'object') return false;
    return deepEq(a, b, aStack, bStack);
  };

  // Internal recursive comparison function for `isEqual`.
  deepEq = function(a, b, aStack, bStack) {
    // Unwrap any wrapped objects.
    if (a instanceof _) a = a._wrapped;
    if (b instanceof _) b = b._wrapped;
    // Compare `[[Class]]` names.
    var className = toString.call(a);
    if (className !== toString.call(b)) return false;
    switch (className) {
      // Strings, numbers, regular expressions, dates, and booleans are compared by value.
      case '[object RegExp]':
      // RegExps are coerced to strings for comparison (Note: '' + /a/i === '/a/i')
      case '[object String]':
        // Primitives and their corresponding object wrappers are equivalent; thus, `"5"` is
        // equivalent to `new String("5")`.
        return '' + a === '' + b;
      case '[object Number]':
        // `NaN`s are equivalent, but non-reflexive.
        // Object(NaN) is equivalent to NaN.
        if (+a !== +a) return +b !== +b;
        // An `egal` comparison is performed for other numeric values.
        return +a === 0 ? 1 / +a === 1 / b : +a === +b;
      case '[object Date]':
      case '[object Boolean]':
        // Coerce dates and booleans to numeric primitive values. Dates are compared by their
        // millisecond representations. Note that invalid dates with millisecond representations
        // of `NaN` are not equivalent.
        return +a === +b;
      case '[object Symbol]':
        return SymbolProto.valueOf.call(a) === SymbolProto.valueOf.call(b);
    }

    var areArrays = className === '[object Array]';
    if (!areArrays) {
      if (typeof a != 'object' || typeof b != 'object') return false;

      // Objects with different constructors are not equivalent, but `Object`s or `Array`s
      // from different frames are.
      var aCtor = a.constructor, bCtor = b.constructor;
      if (aCtor !== bCtor && !(_.isFunction(aCtor) && aCtor instanceof aCtor &&
                               _.isFunction(bCtor) && bCtor instanceof bCtor)
                          && ('constructor' in a && 'constructor' in b)) {
        return false;
      }
    }
    // Assume equality for cyclic structures. The algorithm for detecting cyclic
    // structures is adapted from ES 5.1 section 15.12.3, abstract operation `JO`.

    // Initializing stack of traversed objects.
    // It's done here since we only need them for objects and arrays comparison.
    aStack = aStack || [];
    bStack = bStack || [];
    var length = aStack.length;
    while (length--) {
      // Linear search. Performance is inversely proportional to the number of
      // unique nested structures.
      if (aStack[length] === a) return bStack[length] === b;
    }

    // Add the first object to the stack of traversed objects.
    aStack.push(a);
    bStack.push(b);

    // Recursively compare objects and arrays.
    if (areArrays) {
      // Compare array lengths to determine if a deep comparison is necessary.
      length = a.length;
      if (length !== b.length) return false;
      // Deep compare the contents, ignoring non-numeric properties.
      while (length--) {
        if (!eq(a[length], b[length], aStack, bStack)) return false;
      }
    } else {
      // Deep compare objects.
      var keys = _.keys(a), key;
      length = keys.length;
      // Ensure that both objects contain the same number of properties before comparing deep equality.
      if (_.keys(b).length !== length) return false;
      while (length--) {
        // Deep compare each member
        key = keys[length];
        if (!(has(b, key) && eq(a[key], b[key], aStack, bStack))) return false;
      }
    }
    // Remove the first object from the stack of traversed objects.
    aStack.pop();
    bStack.pop();
    return true;
  };

  // Perform a deep comparison to check if two objects are equal.
  _.isEqual = function(a, b) {
    return eq(a, b);
  };

  // Is a given array, string, or object empty?
  // An "empty" object has no enumerable own-properties.
  _.isEmpty = function(obj) {
    if (obj == null) return true;
    if (isArrayLike(obj) && (_.isArray(obj) || _.isString(obj) || _.isArguments(obj))) return obj.length === 0;
    return _.keys(obj).length === 0;
  };

  // Is a given value a DOM element?
  _.isElement = function(obj) {
    return !!(obj && obj.nodeType === 1);
  };

  // Is a given value an array?
  // Delegates to ECMA5's native Array.isArray
  _.isArray = nativeIsArray || function(obj) {
    return toString.call(obj) === '[object Array]';
  };

  // Is a given variable an object?
  _.isObject = function(obj) {
    var type = typeof obj;
    return type === 'function' || type === 'object' && !!obj;
  };

  // Add some isType methods: isArguments, isFunction, isString, isNumber, isDate, isRegExp, isError, isMap, isWeakMap, isSet, isWeakSet.
  _.each(['Arguments', 'Function', 'String', 'Number', 'Date', 'RegExp', 'Error', 'Symbol', 'Map', 'WeakMap', 'Set', 'WeakSet'], function(name) {
    _['is' + name] = function(obj) {
      return toString.call(obj) === '[object ' + name + ']';
    };
  });

  // Define a fallback version of the method in browsers (ahem, IE < 9), where
  // there isn't any inspectable "Arguments" type.
  if (!_.isArguments(arguments)) {
    _.isArguments = function(obj) {
      return has(obj, 'callee');
    };
  }

  // Optimize `isFunction` if appropriate. Work around some typeof bugs in old v8,
  // IE 11 (#1621), Safari 8 (#1929), and PhantomJS (#2236).
  var nodelist = root.document && root.document.childNodes;
  if (typeof /./ != 'function' && typeof Int8Array != 'object' && typeof nodelist != 'function') {
    _.isFunction = function(obj) {
      return typeof obj == 'function' || false;
    };
  }

  // Is a given object a finite number?
  _.isFinite = function(obj) {
    return !_.isSymbol(obj) && isFinite(obj) && !isNaN(parseFloat(obj));
  };

  // Is the given value `NaN`?
  _.isNaN = function(obj) {
    return _.isNumber(obj) && isNaN(obj);
  };

  // Is a given value a boolean?
  _.isBoolean = function(obj) {
    return obj === true || obj === false || toString.call(obj) === '[object Boolean]';
  };

  // Is a given value equal to null?
  _.isNull = function(obj) {
    return obj === null;
  };

  // Is a given variable undefined?
  _.isUndefined = function(obj) {
    return obj === void 0;
  };

  // Shortcut function for checking if an object has a given property directly
  // on itself (in other words, not on a prototype).
  _.has = function(obj, path) {
    if (!_.isArray(path)) {
      return has(obj, path);
    }
    var length = path.length;
    for (var i = 0; i < length; i++) {
      var key = path[i];
      if (obj == null || !hasOwnProperty.call(obj, key)) {
        return false;
      }
      obj = obj[key];
    }
    return !!length;
  };

  // Utility Functions
  // -----------------

  // Run Underscore.js in *noConflict* mode, returning the `_` variable to its
  // previous owner. Returns a reference to the Underscore object.
  _.noConflict = function() {
    root._ = previousUnderscore;
    return this;
  };

  // Keep the identity function around for default iteratees.
  _.identity = function(value) {
    return value;
  };

  // Predicate-generating functions. Often useful outside of Underscore.
  _.constant = function(value) {
    return function() {
      return value;
    };
  };

  _.noop = function(){};

  // Creates a function that, when passed an object, will traverse that object’s
  // properties down the given `path`, specified as an array of keys or indexes.
  _.property = function(path) {
    if (!_.isArray(path)) {
      return shallowProperty(path);
    }
    return function(obj) {
      return deepGet(obj, path);
    };
  };

  // Generates a function for a given object that returns a given property.
  _.propertyOf = function(obj) {
    if (obj == null) {
      return function(){};
    }
    return function(path) {
      return !_.isArray(path) ? obj[path] : deepGet(obj, path);
    };
  };

  // Returns a predicate for checking whether an object has a given set of
  // `key:value` pairs.
  _.matcher = _.matches = function(attrs) {
    attrs = _.extendOwn({}, attrs);
    return function(obj) {
      return _.isMatch(obj, attrs);
    };
  };

  // Run a function **n** times.
  _.times = function(n, iteratee, context) {
    var accum = Array(Math.max(0, n));
    iteratee = optimizeCb(iteratee, context, 1);
    for (var i = 0; i < n; i++) accum[i] = iteratee(i);
    return accum;
  };

  // Return a random integer between min and max (inclusive).
  _.random = function(min, max) {
    if (max == null) {
      max = min;
      min = 0;
    }
    return min + Math.floor(Math.random() * (max - min + 1));
  };

  // A (possibly faster) way to get the current timestamp as an integer.
  _.now = Date.now || function() {
    return new Date().getTime();
  };

  // List of HTML entities for escaping.
  var escapeMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '`': '&#x60;'
  };
  var unescapeMap = _.invert(escapeMap);

  // Functions for escaping and unescaping strings to/from HTML interpolation.
  var createEscaper = function(map) {
    var escaper = function(match) {
      return map[match];
    };
    // Regexes for identifying a key that needs to be escaped.
    var source = '(?:' + _.keys(map).join('|') + ')';
    var testRegexp = RegExp(source);
    var replaceRegexp = RegExp(source, 'g');
    return function(string) {
      string = string == null ? '' : '' + string;
      return testRegexp.test(string) ? string.replace(replaceRegexp, escaper) : string;
    };
  };
  _.escape = createEscaper(escapeMap);
  _.unescape = createEscaper(unescapeMap);

  // Traverses the children of `obj` along `path`. If a child is a function, it
  // is invoked with its parent as context. Returns the value of the final
  // child, or `fallback` if any child is undefined.
  _.result = function(obj, path, fallback) {
    if (!_.isArray(path)) path = [path];
    var length = path.length;
    if (!length) {
      return _.isFunction(fallback) ? fallback.call(obj) : fallback;
    }
    for (var i = 0; i < length; i++) {
      var prop = obj == null ? void 0 : obj[path[i]];
      if (prop === void 0) {
        prop = fallback;
        i = length; // Ensure we don't continue iterating.
      }
      obj = _.isFunction(prop) ? prop.call(obj) : prop;
    }
    return obj;
  };

  // Generate a unique integer id (unique within the entire client session).
  // Useful for temporary DOM ids.
  var idCounter = 0;
  _.uniqueId = function(prefix) {
    var id = ++idCounter + '';
    return prefix ? prefix + id : id;
  };

  // By default, Underscore uses ERB-style template delimiters, change the
  // following template settings to use alternative delimiters.
  _.templateSettings = {
    evaluate: /<%([\s\S]+?)%>/g,
    interpolate: /<%=([\s\S]+?)%>/g,
    escape: /<%-([\s\S]+?)%>/g
  };

  // When customizing `templateSettings`, if you don't want to define an
  // interpolation, evaluation or escaping regex, we need one that is
  // guaranteed not to match.
  var noMatch = /(.)^/;

  // Certain characters need to be escaped so that they can be put into a
  // string literal.
  var escapes = {
    "'": "'",
    '\\': '\\',
    '\r': 'r',
    '\n': 'n',
    '\u2028': 'u2028',
    '\u2029': 'u2029'
  };

  var escapeRegExp = /\\|'|\r|\n|\u2028|\u2029/g;

  var escapeChar = function(match) {
    return '\\' + escapes[match];
  };

  // JavaScript micro-templating, similar to John Resig's implementation.
  // Underscore templating handles arbitrary delimiters, preserves whitespace,
  // and correctly escapes quotes within interpolated code.
  // NB: `oldSettings` only exists for backwards compatibility.
  _.template = function(text, settings, oldSettings) {
    if (!settings && oldSettings) settings = oldSettings;
    settings = _.defaults({}, settings, _.templateSettings);

    // Combine delimiters into one regular expression via alternation.
    var matcher = RegExp([
      (settings.escape || noMatch).source,
      (settings.interpolate || noMatch).source,
      (settings.evaluate || noMatch).source
    ].join('|') + '|$', 'g');

    // Compile the template source, escaping string literals appropriately.
    var index = 0;
    var source = "__p+='";
    text.replace(matcher, function(match, escape, interpolate, evaluate, offset) {
      source += text.slice(index, offset).replace(escapeRegExp, escapeChar);
      index = offset + match.length;

      if (escape) {
        source += "'+\n((__t=(" + escape + "))==null?'':_.escape(__t))+\n'";
      } else if (interpolate) {
        source += "'+\n((__t=(" + interpolate + "))==null?'':__t)+\n'";
      } else if (evaluate) {
        source += "';\n" + evaluate + "\n__p+='";
      }

      // Adobe VMs need the match returned to produce the correct offset.
      return match;
    });
    source += "';\n";

    // If a variable is not specified, place data values in local scope.
    if (!settings.variable) source = 'with(obj||{}){\n' + source + '}\n';

    source = "var __t,__p='',__j=Array.prototype.join," +
      "print=function(){__p+=__j.call(arguments,'');};\n" +
      source + 'return __p;\n';

    var render;
    try {
      render = new Function(settings.variable || 'obj', '_', source);
    } catch (e) {
      e.source = source;
      throw e;
    }

    var template = function(data) {
      return render.call(this, data, _);
    };

    // Provide the compiled source as a convenience for precompilation.
    var argument = settings.variable || 'obj';
    template.source = 'function(' + argument + '){\n' + source + '}';

    return template;
  };

  // Add a "chain" function. Start chaining a wrapped Underscore object.
  _.chain = function(obj) {
    var instance = _(obj);
    instance._chain = true;
    return instance;
  };

  // OOP
  // ---------------
  // If Underscore is called as a function, it returns a wrapped object that
  // can be used OO-style. This wrapper holds altered versions of all the
  // underscore functions. Wrapped objects may be chained.

  // Helper function to continue chaining intermediate results.
  var chainResult = function(instance, obj) {
    return instance._chain ? _(obj).chain() : obj;
  };

  // Add your own custom functions to the Underscore object.
  _.mixin = function(obj) {
    _.each(_.functions(obj), function(name) {
      var func = _[name] = obj[name];
      _.prototype[name] = function() {
        var args = [this._wrapped];
        push.apply(args, arguments);
        return chainResult(this, func.apply(_, args));
      };
    });
    return _;
  };

  // Add all of the Underscore functions to the wrapper object.
  _.mixin(_);

  // Add all mutator Array functions to the wrapper.
  _.each(['pop', 'push', 'reverse', 'shift', 'sort', 'splice', 'unshift'], function(name) {
    var method = ArrayProto[name];
    _.prototype[name] = function() {
      var obj = this._wrapped;
      method.apply(obj, arguments);
      if ((name === 'shift' || name === 'splice') && obj.length === 0) delete obj[0];
      return chainResult(this, obj);
    };
  });

  // Add all accessor Array functions to the wrapper.
  _.each(['concat', 'join', 'slice'], function(name) {
    var method = ArrayProto[name];
    _.prototype[name] = function() {
      return chainResult(this, method.apply(this._wrapped, arguments));
    };
  });

  // Extracts the result from a wrapped and chained object.
  _.prototype.value = function() {
    return this._wrapped;
  };

  // Provide unwrapping proxy for some methods used in engine operations
  // such as arithmetic and JSON stringification.
  _.prototype.valueOf = _.prototype.toJSON = _.prototype.value;

  _.prototype.toString = function() {
    return String(this._wrapped);
  };

  // AMD registration happens at the end for compatibility with AMD loaders
  // that may not enforce next-turn semantics on modules. Even though general
  // practice for AMD registration is to be anonymous, underscore registers
  // as a named module because, like jQuery, it is a base library that is
  // popular enough to be bundled in a third party lib, but not be part of
  // an AMD load request. Those cases could generate an error when an
  // anonymous define() is called outside of a loader request.
  if (typeof define == 'function' && define.amd) {
    define('underscore', [], function() {
      return _;
    });
  }
}());
/*! modernizr 3.2.0 (Custom Build) | MIT *
 * http://modernizr.com/download/?-audio-cssgradients-csstransitions-hashchange-history-ie8compat-input-inputtypes-localstorage-touchevents-video-addtest-domprefixes-hasevent-prefixes-setclasses-shiv-testallprops-testprop-teststyles !*/
!function(e,t,n){function o(e){var t=E.className,n=Modernizr._config.classPrefix||"";if(x&&(t=t.baseVal),Modernizr._config.enableJSClass){var o=new RegExp("(^|\\s)"+n+"no-js(\\s|$)");t=t.replace(o,"$1"+n+"js$2")}Modernizr._config.enableClasses&&(t+=" "+n+e.join(" "+n),x?E.className.baseVal=t:E.className=t)}function r(e,t){return typeof e===t}function a(){var e,t,n,o,a,i,s;for(var l in T)if(T.hasOwnProperty(l)){if(e=[],t=T[l],t.name&&(e.push(t.name.toLowerCase()),t.options&&t.options.aliases&&t.options.aliases.length))for(n=0;n<t.options.aliases.length;n++)e.push(t.options.aliases[n].toLowerCase());for(o=r(t.fn,"function")?t.fn():t.fn,a=0;a<e.length;a++)i=e[a],s=i.split("."),1===s.length?Modernizr[s[0]]=o:(!Modernizr[s[0]]||Modernizr[s[0]]instanceof Boolean||(Modernizr[s[0]]=new Boolean(Modernizr[s[0]])),Modernizr[s[0]][s[1]]=o),b.push((o?"":"no-")+s.join("-"))}}function i(e,t){if("object"==typeof e)for(var n in e)P(e,n)&&i(n,e[n]);else{e=e.toLowerCase();var r=e.split("."),a=Modernizr[r[0]];if(2==r.length&&(a=a[r[1]]),"undefined"!=typeof a)return Modernizr;t="function"==typeof t?t():t,1==r.length?Modernizr[r[0]]=t:(!Modernizr[r[0]]||Modernizr[r[0]]instanceof Boolean||(Modernizr[r[0]]=new Boolean(Modernizr[r[0]])),Modernizr[r[0]][r[1]]=t),o([(t&&0!=t?"":"no-")+r.join("-")]),Modernizr._trigger(e,t)}return Modernizr}function s(){return"function"!=typeof t.createElement?t.createElement(arguments[0]):x?t.createElementNS.call(t,"http://www.w3.org/2000/svg",arguments[0]):t.createElement.apply(t,arguments)}function l(e,t){return!!~(""+e).indexOf(t)}function c(e){return e.replace(/([a-z])-([a-z])/g,function(e,t,n){return t+n.toUpperCase()}).replace(/^-/,"")}function u(){var e=t.body;return e||(e=s(x?"svg":"body"),e.fake=!0),e}function d(e,n,o,r){var a,i,l,c,d="modernizr",f=s("div"),p=u();if(parseInt(o,10))for(;o--;)l=s("div"),l.id=r?r[o]:d+(o+1),f.appendChild(l);return a=s("style"),a.type="text/css",a.id="s"+d,(p.fake?p:f).appendChild(a),p.appendChild(f),a.styleSheet?a.styleSheet.cssText=e:a.appendChild(t.createTextNode(e)),f.id=d,p.fake&&(p.style.background="",p.style.overflow="hidden",c=E.style.overflow,E.style.overflow="hidden",E.appendChild(p)),i=n(f,e),p.fake?(p.parentNode.removeChild(p),E.style.overflow=c,E.offsetHeight):f.parentNode.removeChild(f),!!i}function f(e,t){return function(){return e.apply(t,arguments)}}function p(e,t,n){var o;for(var a in e)if(e[a]in t)return n===!1?e[a]:(o=t[e[a]],r(o,"function")?f(o,n||t):o);return!1}function m(e){return e.replace(/([A-Z])/g,function(e,t){return"-"+t.toLowerCase()}).replace(/^ms-/,"-ms-")}function h(t,o){var r=t.length;if("CSS"in e&&"supports"in e.CSS){for(;r--;)if(e.CSS.supports(m(t[r]),o))return!0;return!1}if("CSSSupportsRule"in e){for(var a=[];r--;)a.push("("+m(t[r])+":"+o+")");return a=a.join(" or "),d("@supports ("+a+") { #modernizr { position: absolute; } }",function(e){return"absolute"==getComputedStyle(e,null).position})}return n}function v(e,t,o,a){function i(){d&&(delete F.style,delete F.modElem)}if(a=r(a,"undefined")?!1:a,!r(o,"undefined")){var u=h(e,o);if(!r(u,"undefined"))return u}for(var d,f,p,m,v,g=["modernizr","tspan"];!F.style;)d=!0,F.modElem=s(g.shift()),F.style=F.modElem.style;for(p=e.length,f=0;p>f;f++)if(m=e[f],v=F.style[m],l(m,"-")&&(m=c(m)),F.style[m]!==n){if(a||r(o,"undefined"))return i(),"pfx"==t?m:!0;try{F.style[m]=o}catch(y){}if(F.style[m]!=v)return i(),"pfx"==t?m:!0}return i(),!1}function g(e,t,n,o,a){var i=e.charAt(0).toUpperCase()+e.slice(1),s=(e+" "+M.join(i+" ")+i).split(" ");return r(t,"string")||r(t,"undefined")?v(s,t,o,a):(s=(e+" "+_.join(i+" ")+i).split(" "),p(s,t,n))}function y(e,t,o){return g(e,n,n,t,o)}var b=[],T=[],w={_version:"3.2.0",_config:{classPrefix:"",enableClasses:!0,enableJSClass:!0,usePrefixes:!0},_q:[],on:function(e,t){var n=this;setTimeout(function(){t(n[e])},0)},addTest:function(e,t,n){T.push({name:e,fn:t,options:n})},addAsyncTest:function(e){T.push({name:null,fn:e})}},Modernizr=function(){};Modernizr.prototype=w,Modernizr=new Modernizr,Modernizr.addTest("history",function(){var t=navigator.userAgent;return-1===t.indexOf("Android 2.")&&-1===t.indexOf("Android 4.0")||-1===t.indexOf("Mobile Safari")||-1!==t.indexOf("Chrome")||-1!==t.indexOf("Windows Phone")?e.history&&"pushState"in e.history:!1}),Modernizr.addTest("ie8compat",!e.addEventListener&&!!t.documentMode&&7===t.documentMode),Modernizr.addTest("localstorage",function(){var e="modernizr";try{return localStorage.setItem(e,e),localStorage.removeItem(e),!0}catch(t){return!1}});var C=w._config.usePrefixes?" -webkit- -moz- -o- -ms- ".split(" "):[];w._prefixes=C;var E=t.documentElement,x="svg"===E.nodeName.toLowerCase();x||!function(e,t){function n(e,t){var n=e.createElement("p"),o=e.getElementsByTagName("head")[0]||e.documentElement;return n.innerHTML="x<style>"+t+"</style>",o.insertBefore(n.lastChild,o.firstChild)}function o(){var e=b.elements;return"string"==typeof e?e.split(" "):e}function r(e,t){var n=b.elements;"string"!=typeof n&&(n=n.join(" ")),"string"!=typeof e&&(e=e.join(" ")),b.elements=n+" "+e,c(t)}function a(e){var t=y[e[v]];return t||(t={},g++,e[v]=g,y[g]=t),t}function i(e,n,o){if(n||(n=t),d)return n.createElement(e);o||(o=a(n));var r;return r=o.cache[e]?o.cache[e].cloneNode():h.test(e)?(o.cache[e]=o.createElem(e)).cloneNode():o.createElem(e),!r.canHaveChildren||m.test(e)||r.tagUrn?r:o.frag.appendChild(r)}function s(e,n){if(e||(e=t),d)return e.createDocumentFragment();n=n||a(e);for(var r=n.frag.cloneNode(),i=0,s=o(),l=s.length;l>i;i++)r.createElement(s[i]);return r}function l(e,t){t.cache||(t.cache={},t.createElem=e.createElement,t.createFrag=e.createDocumentFragment,t.frag=t.createFrag()),e.createElement=function(n){return b.shivMethods?i(n,e,t):t.createElem(n)},e.createDocumentFragment=Function("h,f","return function(){var n=f.cloneNode(),c=n.createElement;h.shivMethods&&("+o().join().replace(/[\w\-:]+/g,function(e){return t.createElem(e),t.frag.createElement(e),'c("'+e+'")'})+");return n}")(b,t.frag)}function c(e){e||(e=t);var o=a(e);return!b.shivCSS||u||o.hasCSS||(o.hasCSS=!!n(e,"article,aside,dialog,figcaption,figure,footer,header,hgroup,main,nav,section{display:block}mark{background:#FF0;color:#000}template{display:none}")),d||l(e,o),e}var u,d,f="3.7.3",p=e.html5||{},m=/^<|^(?:button|map|select|textarea|object|iframe|option|optgroup)$/i,h=/^(?:a|b|code|div|fieldset|h1|h2|h3|h4|h5|h6|i|label|li|ol|p|q|span|strong|style|table|tbody|td|th|tr|ul)$/i,v="_html5shiv",g=0,y={};!function(){try{var e=t.createElement("a");e.innerHTML="<xyz></xyz>",u="hidden"in e,d=1==e.childNodes.length||function(){t.createElement("a");var e=t.createDocumentFragment();return"undefined"==typeof e.cloneNode||"undefined"==typeof e.createDocumentFragment||"undefined"==typeof e.createElement}()}catch(n){u=!0,d=!0}}();var b={elements:p.elements||"abbr article aside audio bdi canvas data datalist details dialog figcaption figure footer header hgroup main mark meter nav output picture progress section summary template time video",version:f,shivCSS:p.shivCSS!==!1,supportsUnknownElements:d,shivMethods:p.shivMethods!==!1,type:"default",shivDocument:c,createElement:i,createDocumentFragment:s,addElements:r};e.html5=b,c(t),"object"==typeof module&&module.exports&&(module.exports=b)}("undefined"!=typeof e?e:this,t);var S="Moz O ms Webkit",_=w._config.usePrefixes?S.toLowerCase().split(" "):[];w._domPrefixes=_;var P;!function(){var e={}.hasOwnProperty;P=r(e,"undefined")||r(e.call,"undefined")?function(e,t){return t in e&&r(e.constructor.prototype[t],"undefined")}:function(t,n){return e.call(t,n)}}(),w._l={},w.on=function(e,t){this._l[e]||(this._l[e]=[]),this._l[e].push(t),Modernizr.hasOwnProperty(e)&&setTimeout(function(){Modernizr._trigger(e,Modernizr[e])},0)},w._trigger=function(e,t){if(this._l[e]){var n=this._l[e];setTimeout(function(){var e,o;for(e=0;e<n.length;e++)(o=n[e])(t)},0),delete this._l[e]}},Modernizr._q.push(function(){w.addTest=i});var k=function(){function e(e,t){var r;return e?(t&&"string"!=typeof t||(t=s(t||"div")),e="on"+e,r=e in t,!r&&o&&(t.setAttribute||(t=s("div")),t.setAttribute(e,""),r="function"==typeof t[e],t[e]!==n&&(t[e]=n),t.removeAttribute(e)),r):!1}var o=!("onblur"in t.documentElement);return e}();w.hasEvent=k,Modernizr.addTest("hashchange",function(){return k("hashchange",e)===!1?!1:t.documentMode===n||t.documentMode>7}),Modernizr.addTest("audio",function(){var e=s("audio"),t=!1;try{(t=!!e.canPlayType)&&(t=new Boolean(t),t.ogg=e.canPlayType('audio/ogg; codecs="vorbis"').replace(/^no$/,""),t.mp3=e.canPlayType('audio/mpeg; codecs="mp3"').replace(/^no$/,""),t.opus=e.canPlayType('audio/ogg; codecs="opus"').replace(/^no$/,""),t.wav=e.canPlayType('audio/wav; codecs="1"').replace(/^no$/,""),t.m4a=(e.canPlayType("audio/x-m4a;")||e.canPlayType("audio/aac;")).replace(/^no$/,""))}catch(n){}return t}),Modernizr.addTest("video",function(){var e=s("video"),t=!1;try{(t=!!e.canPlayType)&&(t=new Boolean(t),t.ogg=e.canPlayType('video/ogg; codecs="theora"').replace(/^no$/,""),t.h264=e.canPlayType('video/mp4; codecs="avc1.42E01E"').replace(/^no$/,""),t.webm=e.canPlayType('video/webm; codecs="vp8, vorbis"').replace(/^no$/,""),t.vp9=e.canPlayType('video/webm; codecs="vp9"').replace(/^no$/,""),t.hls=e.canPlayType('application/x-mpegURL; codecs="avc1.42E01E"').replace(/^no$/,""))}catch(n){}return t}),Modernizr.addTest("cssgradients",function(){for(var e,t="background-image:",n="gradient(linear,left top,right bottom,from(#9f9),to(white));",o="",r=0,a=C.length-1;a>r;r++)e=0===r?"to ":"",o+=t+C[r]+"linear-gradient("+e+"left top, #9f9, white);";Modernizr._config.usePrefixes&&(o+=t+"-webkit-"+n);var i=s("a"),l=i.style;return l.cssText=o,(""+l.backgroundImage).indexOf("gradient")>-1});var $=s("input"),j="autocomplete autofocus list placeholder max min multiple pattern required step".split(" "),N={};Modernizr.input=function(t){for(var n=0,o=t.length;o>n;n++)N[t[n]]=!!(t[n]in $);return N.list&&(N.list=!(!s("datalist")||!e.HTMLDataListElement)),N}(j);var z="search tel url email datetime date month week time datetime-local number range color".split(" "),A={};Modernizr.inputtypes=function(e){for(var o,r,a,i=e.length,s="1)",l=0;i>l;l++)$.setAttribute("type",o=e[l]),a="text"!==$.type&&"style"in $,a&&($.value=s,$.style.cssText="position:absolute;visibility:hidden;",/^range$/.test(o)&&$.style.WebkitAppearance!==n?(E.appendChild($),r=t.defaultView,a=r.getComputedStyle&&"textfield"!==r.getComputedStyle($,null).WebkitAppearance&&0!==$.offsetHeight,E.removeChild($)):/^(search|tel)$/.test(o)||(a=/^(url|email)$/.test(o)?$.checkValidity&&$.checkValidity()===!1:$.value!=s)),A[e[l]]=!!a;return A}(z);var M=w._config.usePrefixes?S.split(" "):[];w._cssomPrefixes=M;var L=w.testStyles=d;Modernizr.addTest("touchevents",function(){var n;if("ontouchstart"in e||e.DocumentTouch&&t instanceof DocumentTouch)n=!0;else{var o=["@media (",C.join("touch-enabled),("),"heartz",")","{#modernizr{top:9px;position:absolute}}"].join("");L(o,function(e){n=9===e.offsetTop})}return n});var O={elem:s("modernizr")};Modernizr._q.push(function(){delete O.elem});var F={style:O.elem.style};Modernizr._q.unshift(function(){delete F.style});w.testProp=function(e,t,o){return v([e],n,t,o)};w.testAllProps=g,w.testAllProps=y,Modernizr.addTest("csstransitions",y("transition","all",!0)),a(),o(b),delete w.addTest,delete w.addAsyncTest;for(var D=0;D<Modernizr._q.length;D++)Modernizr._q[D]();e.Modernizr=Modernizr}(window,document);
/*!
 * jQuery UI Core @VERSION
 * http://jqueryui.com
 *
 * Copyright 2012 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/category/ui-core/
 */
(function( $, undefined ) {

var uuid = 0,
	runiqueId = /^ui-id-\d+$/;

// prevent duplicate loading
// this is only a problem because we proxy existing functions
// and we don't want to double proxy them
$.ui = $.ui || {};
if ( $.ui.version ) {
	return;
}

$.extend( $.ui, {
	version: "@VERSION",

	keyCode: {
		BACKSPACE: 8,
		COMMA: 188,
		DELETE: 46,
		DOWN: 40,
		END: 35,
		ENTER: 13,
		ESCAPE: 27,
		HOME: 36,
		LEFT: 37,
		NUMPAD_ADD: 107,
		NUMPAD_DECIMAL: 110,
		NUMPAD_DIVIDE: 111,
		NUMPAD_ENTER: 108,
		NUMPAD_MULTIPLY: 106,
		NUMPAD_SUBTRACT: 109,
		PAGE_DOWN: 34,
		PAGE_UP: 33,
		PERIOD: 190,
		RIGHT: 39,
		SPACE: 32,
		TAB: 9,
		UP: 38
	}
});

// plugins
$.fn.extend({
	_focus: $.fn.focus,
	focus: function( delay, fn ) {
		return typeof delay === "number" ?
			this.each(function() {
				var elem = this;
				setTimeout(function() {
					$( elem ).focus();
					if ( fn ) {
						fn.call( elem );
					}
				}, delay );
			}) :
			this._focus.apply( this, arguments );
	},

	scrollParent: function() {
		var scrollParent;
		if (($.ui.ie && (/(static|relative)/).test(this.css('position'))) || (/absolute/).test(this.css('position'))) {
			scrollParent = this.parents().filter(function() {
				return (/(relative|absolute|fixed)/).test($.css(this,'position')) && (/(auto|scroll)/).test($.css(this,'overflow')+$.css(this,'overflow-y')+$.css(this,'overflow-x'));
			}).eq(0);
		} else {
			scrollParent = this.parents().filter(function() {
				return (/(auto|scroll)/).test($.css(this,'overflow')+$.css(this,'overflow-y')+$.css(this,'overflow-x'));
			}).eq(0);
		}

		return (/fixed/).test(this.css('position')) || !scrollParent.length ? $(document) : scrollParent;
	},

	zIndex: function( zIndex ) {
		if ( zIndex !== undefined ) {
			return this.css( "zIndex", zIndex );
		}

		if ( this.length ) {
			var elem = $( this[ 0 ] ), position, value;
			while ( elem.length && elem[ 0 ] !== document ) {
				// Ignore z-index if position is set to a value where z-index is ignored by the browser
				// This makes behavior of this function consistent across browsers
				// WebKit always returns auto if the element is positioned
				position = elem.css( "position" );
				if ( position === "absolute" || position === "relative" || position === "fixed" ) {
					// IE returns 0 when zIndex is not specified
					// other browsers return a string
					// we ignore the case of nested elements with an explicit value of 0
					// <div style="z-index: -10;"><div style="z-index: 0;"></div></div>
					value = parseInt( elem.css( "zIndex" ), 10 );
					if ( !isNaN( value ) && value !== 0 ) {
						return value;
					}
				}
				elem = elem.parent();
			}
		}

		return 0;
	},

	uniqueId: function() {
		return this.each(function() {
			if ( !this.id ) {
				this.id = "ui-id-" + (++uuid);
			}
		});
	},

	removeUniqueId: function() {
		return this.each(function() {
			if ( runiqueId.test( this.id ) ) {
				$( this ).removeAttr( "id" );
			}
		});
	}
});

// selectors
function focusable( element, isTabIndexNotNaN ) {
	var map, mapName, img,
		nodeName = element.nodeName.toLowerCase();
	if ( "area" === nodeName ) {
		map = element.parentNode;
		mapName = map.name;
		if ( !element.href || !mapName || map.nodeName.toLowerCase() !== "map" ) {
			return false;
		}
		img = $( "img[usemap=#" + mapName + "]" )[0];
		return !!img && visible( img );
	}
	return ( /input|select|textarea|button|object/.test( nodeName ) ?
		!element.disabled :
		"a" === nodeName ?
			element.href || isTabIndexNotNaN :
			isTabIndexNotNaN) &&
		// the element and all of its ancestors must be visible
		visible( element );
}

function visible( element ) {
	return $.expr.filters.visible( element ) &&
		!$( element ).parents().andSelf().filter(function() {
			return $.css( this, "visibility" ) === "hidden";
		}).length;
}

$.extend( $.expr[ ":" ], {
	data: $.expr.createPseudo ?
		$.expr.createPseudo(function( dataName ) {
			return function( elem ) {
				return !!$.data( elem, dataName );
			};
		}) :
		// support: jQuery <1.8
		function( elem, i, match ) {
			return !!$.data( elem, match[ 3 ] );
		},

	focusable: function( element ) {
		return focusable( element, !isNaN( $.attr( element, "tabindex" ) ) );
	},

	tabbable: function( element ) {
		var tabIndex = $.attr( element, "tabindex" ),
			isTabIndexNaN = isNaN( tabIndex );
		return ( isTabIndexNaN || tabIndex >= 0 ) && focusable( element, !isTabIndexNaN );
	}
});

// support
$(function() {
	var body = document.body,
		div = body.appendChild( div = document.createElement( "div" ) );

	// access offsetHeight before setting the style to prevent a layout bug
	// in IE 9 which causes the element to continue to take up space even
	// after it is removed from the DOM (#8026)
	div.offsetHeight;

	$.extend( div.style, {
		minHeight: "100px",
		height: "auto",
		padding: 0,
		borderWidth: 0
	});

	$.support.minHeight = div.offsetHeight === 100;
	$.support.selectstart = "onselectstart" in div;

	// set display to none to avoid a layout bug in IE
	// http://dev.jquery.com/ticket/4014
	body.removeChild( div ).style.display = "none";
});

// support: jQuery <1.8
if ( !$( "<a>" ).outerWidth( 1 ).jquery ) {
	$.each( [ "Width", "Height" ], function( i, name ) {
		var side = name === "Width" ? [ "Left", "Right" ] : [ "Top", "Bottom" ],
			type = name.toLowerCase(),
			orig = {
				innerWidth: $.fn.innerWidth,
				innerHeight: $.fn.innerHeight,
				outerWidth: $.fn.outerWidth,
				outerHeight: $.fn.outerHeight
			};

		function reduce( elem, size, border, margin ) {
			$.each( side, function() {
				size -= parseFloat( $.css( elem, "padding" + this ) ) || 0;
				if ( border ) {
					size -= parseFloat( $.css( elem, "border" + this + "Width" ) ) || 0;
				}
				if ( margin ) {
					size -= parseFloat( $.css( elem, "margin" + this ) ) || 0;
				}
			});
			return size;
		}

		$.fn[ "inner" + name ] = function( size ) {
			if ( size === undefined ) {
				return orig[ "inner" + name ].call( this );
			}

			return this.each(function() {
				$( this ).css( type, reduce( this, size ) + "px" );
			});
		};

		$.fn[ "outer" + name] = function( size, margin ) {
			if ( typeof size !== "number" ) {
				return orig[ "outer" + name ].call( this, size );
			}

			return this.each(function() {
				$( this).css( type, reduce( this, size, true, margin ) + "px" );
			});
		};
	});
}

// support: jQuery 1.6.1, 1.6.2 (http://bugs.jquery.com/ticket/9413)
if ( $( "<a>" ).data( "a-b", "a" ).removeData( "a-b" ).data( "a-b" ) ) {
	$.fn.removeData = (function( removeData ) {
		return function( key ) {
			if ( arguments.length ) {
				return removeData.call( this, $.camelCase( key ) );
			} else {
				return removeData.call( this );
			}
		};
	})( $.fn.removeData );
}





// deprecated

(function() {
	var uaMatch = /msie ([\w.]+)/.exec( navigator.userAgent.toLowerCase() ) || [];
	$.ui.ie = uaMatch.length ? true : false;
	$.ui.ie6 = parseFloat( uaMatch[ 1 ], 10 ) === 6;
})();

$.fn.extend({
	disableSelection: function() {
		return this.bind( ( $.support.selectstart ? "selectstart" : "mousedown" ) +
			".ui-disableSelection", function( event ) {
				event.preventDefault();
			});
	},

	enableSelection: function() {
		return this.unbind( ".ui-disableSelection" );
	}
});

$.extend( $.ui, {
	// $.ui.plugin is deprecated.  Use the proxy pattern instead.
	plugin: {
		add: function( module, option, set ) {
			var i,
				proto = $.ui[ module ].prototype;
			for ( i in set ) {
				proto.plugins[ i ] = proto.plugins[ i ] || [];
				proto.plugins[ i ].push( [ option, set[ i ] ] );
			}
		},
		call: function( instance, name, args ) {
			var i,
				set = instance.plugins[ name ];
			if ( !set || !instance.element[ 0 ].parentNode || instance.element[ 0 ].parentNode.nodeType === 11 ) {
				return;
			}

			for ( i = 0; i < set.length; i++ ) {
				if ( instance.options[ set[ i ][ 0 ] ] ) {
					set[ i ][ 1 ].apply( instance.element, args );
				}
			}
		}
	},

	contains: $.contains,

	// only used by resizable
	hasScroll: function( el, a ) {

		//If overflow is hidden, the element might have extra content, but the user wants to hide it
		if ( $( el ).css( "overflow" ) === "hidden") {
			return false;
		}

		var scroll = ( a && a === "left" ) ? "scrollLeft" : "scrollTop",
			has = false;

		if ( el[ scroll ] > 0 ) {
			return true;
		}

		// TODO: determine which cases actually cause this to happen
		// if the element doesn't have the scroll set, see if it's possible to
		// set the scroll
		el[ scroll ] = 1;
		has = ( el[ scroll ] > 0 );
		el[ scroll ] = 0;
		return has;
	},

	// these are odd functions, fix the API or move into individual plugins
	isOverAxis: function( x, reference, size ) {
		//Determines when x coordinate is over "b" element axis
		return ( x > reference ) && ( x < ( reference + size ) );
	},
	isOver: function( y, x, top, left, height, width ) {
		//Determines when x, y coordinates is over "b" element
		return $.ui.isOverAxis( y, top, height ) && $.ui.isOverAxis( x, left, width );
	}
});

})( jQuery );
/*!
 * jQuery UI Effects @VERSION
 * http://jqueryui.com
 *
 * Copyright 2012 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/category/effects-core/
 */
;(jQuery.effects || (function($, undefined) {

var backCompat = $.uiBackCompat !== false,
	// prefix used for storing data on .data()
	dataSpace = "ui-effects-";

$.effects = {
	effect: {}
};

/*!
 * jQuery Color Animations v2.1.1
 * https://github.com/jquery/jquery-color
 *
 * Copyright 2012 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * Date: Sun Oct 28 15:08:06 2012 -0400
 */
(function( jQuery, undefined ) {

	var stepHooks = "backgroundColor borderBottomColor borderLeftColor borderRightColor borderTopColor color columnRuleColor outlineColor textDecorationColor textEmphasisColor",

	// plusequals test for += 100 -= 100
	rplusequals = /^([\-+])=\s*(\d+\.?\d*)/,
	// a set of RE's that can match strings and generate color tuples.
	stringParsers = [{
			re: /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(\d?(?:\.\d+)?)\s*)?\)/,
			parse: function( execResult ) {
				return [
					execResult[ 1 ],
					execResult[ 2 ],
					execResult[ 3 ],
					execResult[ 4 ]
				];
			}
		}, {
			re: /rgba?\(\s*(\d+(?:\.\d+)?)\%\s*,\s*(\d+(?:\.\d+)?)\%\s*,\s*(\d+(?:\.\d+)?)\%\s*(?:,\s*(\d?(?:\.\d+)?)\s*)?\)/,
			parse: function( execResult ) {
				return [
					execResult[ 1 ] * 2.55,
					execResult[ 2 ] * 2.55,
					execResult[ 3 ] * 2.55,
					execResult[ 4 ]
				];
			}
		}, {
			// this regex ignores A-F because it's compared against an already lowercased string
			re: /#([a-f0-9]{2})([a-f0-9]{2})([a-f0-9]{2})/,
			parse: function( execResult ) {
				return [
					parseInt( execResult[ 1 ], 16 ),
					parseInt( execResult[ 2 ], 16 ),
					parseInt( execResult[ 3 ], 16 )
				];
			}
		}, {
			// this regex ignores A-F because it's compared against an already lowercased string
			re: /#([a-f0-9])([a-f0-9])([a-f0-9])/,
			parse: function( execResult ) {
				return [
					parseInt( execResult[ 1 ] + execResult[ 1 ], 16 ),
					parseInt( execResult[ 2 ] + execResult[ 2 ], 16 ),
					parseInt( execResult[ 3 ] + execResult[ 3 ], 16 )
				];
			}
		}, {
			re: /hsla?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\%\s*,\s*(\d+(?:\.\d+)?)\%\s*(?:,\s*(\d?(?:\.\d+)?)\s*)?\)/,
			space: "hsla",
			parse: function( execResult ) {
				return [
					execResult[ 1 ],
					execResult[ 2 ] / 100,
					execResult[ 3 ] / 100,
					execResult[ 4 ]
				];
			}
		}],

	// jQuery.Color( )
	color = jQuery.Color = function( color, green, blue, alpha ) {
		return new jQuery.Color.fn.parse( color, green, blue, alpha );
	},
	spaces = {
		rgba: {
			props: {
				red: {
					idx: 0,
					type: "byte"
				},
				green: {
					idx: 1,
					type: "byte"
				},
				blue: {
					idx: 2,
					type: "byte"
				}
			}
		},

		hsla: {
			props: {
				hue: {
					idx: 0,
					type: "degrees"
				},
				saturation: {
					idx: 1,
					type: "percent"
				},
				lightness: {
					idx: 2,
					type: "percent"
				}
			}
		}
	},
	propTypes = {
		"byte": {
			floor: true,
			max: 255
		},
		"percent": {
			max: 1
		},
		"degrees": {
			mod: 360,
			floor: true
		}
	},
	support = color.support = {},

	// element for support tests
	supportElem = jQuery( "<p>" )[ 0 ],

	// colors = jQuery.Color.names
	colors,

	// local aliases of functions called often
	each = jQuery.each;

// determine rgba support immediately
supportElem.style.cssText = "background-color:rgba(1,1,1,.5)";
support.rgba = supportElem.style.backgroundColor.indexOf( "rgba" ) > -1;

// define cache name and alpha properties
// for rgba and hsla spaces
each( spaces, function( spaceName, space ) {
	space.cache = "_" + spaceName;
	space.props.alpha = {
		idx: 3,
		type: "percent",
		def: 1
	};
});

function clamp( value, prop, allowEmpty ) {
	var type = propTypes[ prop.type ] || {};

	if ( value == null ) {
		return (allowEmpty || !prop.def) ? null : prop.def;
	}

	// ~~ is an short way of doing floor for positive numbers
	value = type.floor ? ~~value : parseFloat( value );

	// IE will pass in empty strings as value for alpha,
	// which will hit this case
	if ( isNaN( value ) ) {
		return prop.def;
	}

	if ( type.mod ) {
		// we add mod before modding to make sure that negatives values
		// get converted properly: -10 -> 350
		return (value + type.mod) % type.mod;
	}

	// for now all property types without mod have min and max
	return 0 > value ? 0 : type.max < value ? type.max : value;
}

function stringParse( string ) {
	var inst = color(),
		rgba = inst._rgba = [];

	string = string.toLowerCase();

	each( stringParsers, function( i, parser ) {
		var parsed,
			match = parser.re.exec( string ),
			values = match && parser.parse( match ),
			spaceName = parser.space || "rgba";

		if ( values ) {
			parsed = inst[ spaceName ]( values );

			// if this was an rgba parse the assignment might happen twice
			// oh well....
			inst[ spaces[ spaceName ].cache ] = parsed[ spaces[ spaceName ].cache ];
			rgba = inst._rgba = parsed._rgba;

			// exit each( stringParsers ) here because we matched
			return false;
		}
	});

	// Found a stringParser that handled it
	if ( rgba.length ) {

		// if this came from a parsed string, force "transparent" when alpha is 0
		// chrome, (and maybe others) return "transparent" as rgba(0,0,0,0)
		if ( rgba.join() === "0,0,0,0" ) {
			jQuery.extend( rgba, colors.transparent );
		}
		return inst;
	}

	// named colors
	return colors[ string ];
}

color.fn = jQuery.extend( color.prototype, {
	parse: function( red, green, blue, alpha ) {
		if ( red === undefined ) {
			this._rgba = [ null, null, null, null ];
			return this;
		}
		if ( red.jquery || red.nodeType ) {
			red = jQuery( red ).css( green );
			green = undefined;
		}

		var inst = this,
			type = jQuery.type( red ),
			rgba = this._rgba = [];

		// more than 1 argument specified - assume ( red, green, blue, alpha )
		if ( green !== undefined ) {
			red = [ red, green, blue, alpha ];
			type = "array";
		}

		if ( type === "string" ) {
			return this.parse( stringParse( red ) || colors._default );
		}

		if ( type === "array" ) {
			each( spaces.rgba.props, function( key, prop ) {
				rgba[ prop.idx ] = clamp( red[ prop.idx ], prop );
			});
			return this;
		}

		if ( type === "object" ) {
			if ( red instanceof color ) {
				each( spaces, function( spaceName, space ) {
					if ( red[ space.cache ] ) {
						inst[ space.cache ] = red[ space.cache ].slice();
					}
				});
			} else {
				each( spaces, function( spaceName, space ) {
					var cache = space.cache;
					each( space.props, function( key, prop ) {

						// if the cache doesn't exist, and we know how to convert
						if ( !inst[ cache ] && space.to ) {

							// if the value was null, we don't need to copy it
							// if the key was alpha, we don't need to copy it either
							if ( key === "alpha" || red[ key ] == null ) {
								return;
							}
							inst[ cache ] = space.to( inst._rgba );
						}

						// this is the only case where we allow nulls for ALL properties.
						// call clamp with alwaysAllowEmpty
						inst[ cache ][ prop.idx ] = clamp( red[ key ], prop, true );
					});

					// everything defined but alpha?
					if ( inst[ cache ] && jQuery.inArray( null, inst[ cache ].slice( 0, 3 ) ) < 0 ) {
						// use the default of 1
						inst[ cache ][ 3 ] = 1;
						if ( space.from ) {
							inst._rgba = space.from( inst[ cache ] );
						}
					}
				});
			}
			return this;
		}
	},
	is: function( compare ) {
		var is = color( compare ),
			same = true,
			inst = this;

		each( spaces, function( _, space ) {
			var localCache,
				isCache = is[ space.cache ];
			if (isCache) {
				localCache = inst[ space.cache ] || space.to && space.to( inst._rgba ) || [];
				each( space.props, function( _, prop ) {
					if ( isCache[ prop.idx ] != null ) {
						same = ( isCache[ prop.idx ] === localCache[ prop.idx ] );
						return same;
					}
				});
			}
			return same;
		});
		return same;
	},
	_space: function() {
		var used = [],
			inst = this;
		each( spaces, function( spaceName, space ) {
			if ( inst[ space.cache ] ) {
				used.push( spaceName );
			}
		});
		return used.pop();
	},
	transition: function( other, distance ) {
		var end = color( other ),
			spaceName = end._space(),
			space = spaces[ spaceName ],
			startColor = this.alpha() === 0 ? color( "transparent" ) : this,
			start = startColor[ space.cache ] || space.to( startColor._rgba ),
			result = start.slice();

		end = end[ space.cache ];
		each( space.props, function( key, prop ) {
			var index = prop.idx,
				startValue = start[ index ],
				endValue = end[ index ],
				type = propTypes[ prop.type ] || {};

			// if null, don't override start value
			if ( endValue === null ) {
				return;
			}
			// if null - use end
			if ( startValue === null ) {
				result[ index ] = endValue;
			} else {
				if ( type.mod ) {
					if ( endValue - startValue > type.mod / 2 ) {
						startValue += type.mod;
					} else if ( startValue - endValue > type.mod / 2 ) {
						startValue -= type.mod;
					}
				}
				result[ index ] = clamp( ( endValue - startValue ) * distance + startValue, prop );
			}
		});
		return this[ spaceName ]( result );
	},
	blend: function( opaque ) {
		// if we are already opaque - return ourself
		if ( this._rgba[ 3 ] === 1 ) {
			return this;
		}

		var rgb = this._rgba.slice(),
			a = rgb.pop(),
			blend = color( opaque )._rgba;

		return color( jQuery.map( rgb, function( v, i ) {
			return ( 1 - a ) * blend[ i ] + a * v;
		}));
	},
	toRgbaString: function() {
		var prefix = "rgba(",
			rgba = jQuery.map( this._rgba, function( v, i ) {
				return v == null ? ( i > 2 ? 1 : 0 ) : v;
			});

		if ( rgba[ 3 ] === 1 ) {
			rgba.pop();
			prefix = "rgb(";
		}

		return prefix + rgba.join() + ")";
	},
	toHslaString: function() {
		var prefix = "hsla(",
			hsla = jQuery.map( this.hsla(), function( v, i ) {
				if ( v == null ) {
					v = i > 2 ? 1 : 0;
				}

				// catch 1 and 2
				if ( i && i < 3 ) {
					v = Math.round( v * 100 ) + "%";
				}
				return v;
			});

		if ( hsla[ 3 ] === 1 ) {
			hsla.pop();
			prefix = "hsl(";
		}
		return prefix + hsla.join() + ")";
	},
	toHexString: function( includeAlpha ) {
		var rgba = this._rgba.slice(),
			alpha = rgba.pop();

		if ( includeAlpha ) {
			rgba.push( ~~( alpha * 255 ) );
		}

		return "#" + jQuery.map( rgba, function( v ) {

			// default to 0 when nulls exist
			v = ( v || 0 ).toString( 16 );
			return v.length === 1 ? "0" + v : v;
		}).join("");
	},
	toString: function() {
		return this._rgba[ 3 ] === 0 ? "transparent" : this.toRgbaString();
	}
});
color.fn.parse.prototype = color.fn;

// hsla conversions adapted from:
// https://code.google.com/p/maashaack/source/browse/packages/graphics/trunk/src/graphics/colors/HUE2RGB.as?r=5021

function hue2rgb( p, q, h ) {
	h = ( h + 1 ) % 1;
	if ( h * 6 < 1 ) {
		return p + (q - p) * h * 6;
	}
	if ( h * 2 < 1) {
		return q;
	}
	if ( h * 3 < 2 ) {
		return p + (q - p) * ((2/3) - h) * 6;
	}
	return p;
}

spaces.hsla.to = function ( rgba ) {
	if ( rgba[ 0 ] == null || rgba[ 1 ] == null || rgba[ 2 ] == null ) {
		return [ null, null, null, rgba[ 3 ] ];
	}
	var r = rgba[ 0 ] / 255,
		g = rgba[ 1 ] / 255,
		b = rgba[ 2 ] / 255,
		a = rgba[ 3 ],
		max = Math.max( r, g, b ),
		min = Math.min( r, g, b ),
		diff = max - min,
		add = max + min,
		l = add * 0.5,
		h, s;

	if ( min === max ) {
		h = 0;
	} else if ( r === max ) {
		h = ( 60 * ( g - b ) / diff ) + 360;
	} else if ( g === max ) {
		h = ( 60 * ( b - r ) / diff ) + 120;
	} else {
		h = ( 60 * ( r - g ) / diff ) + 240;
	}

	// chroma (diff) == 0 means greyscale which, by definition, saturation = 0%
	// otherwise, saturation is based on the ratio of chroma (diff) to lightness (add)
	if ( diff === 0 ) {
		s = 0;
	} else if ( l <= 0.5 ) {
		s = diff / add;
	} else {
		s = diff / ( 2 - add );
	}
	return [ Math.round(h) % 360, s, l, a == null ? 1 : a ];
};

spaces.hsla.from = function ( hsla ) {
	if ( hsla[ 0 ] == null || hsla[ 1 ] == null || hsla[ 2 ] == null ) {
		return [ null, null, null, hsla[ 3 ] ];
	}
	var h = hsla[ 0 ] / 360,
		s = hsla[ 1 ],
		l = hsla[ 2 ],
		a = hsla[ 3 ],
		q = l <= 0.5 ? l * ( 1 + s ) : l + s - l * s,
		p = 2 * l - q;

	return [
		Math.round( hue2rgb( p, q, h + ( 1 / 3 ) ) * 255 ),
		Math.round( hue2rgb( p, q, h ) * 255 ),
		Math.round( hue2rgb( p, q, h - ( 1 / 3 ) ) * 255 ),
		a
	];
};


each( spaces, function( spaceName, space ) {
	var props = space.props,
		cache = space.cache,
		to = space.to,
		from = space.from;

	// makes rgba() and hsla()
	color.fn[ spaceName ] = function( value ) {

		// generate a cache for this space if it doesn't exist
		if ( to && !this[ cache ] ) {
			this[ cache ] = to( this._rgba );
		}
		if ( value === undefined ) {
			return this[ cache ].slice();
		}

		var ret,
			type = jQuery.type( value ),
			arr = ( type === "array" || type === "object" ) ? value : arguments,
			local = this[ cache ].slice();

		each( props, function( key, prop ) {
			var val = arr[ type === "object" ? key : prop.idx ];
			if ( val == null ) {
				val = local[ prop.idx ];
			}
			local[ prop.idx ] = clamp( val, prop );
		});

		if ( from ) {
			ret = color( from( local ) );
			ret[ cache ] = local;
			return ret;
		} else {
			return color( local );
		}
	};

	// makes red() green() blue() alpha() hue() saturation() lightness()
	each( props, function( key, prop ) {
		// alpha is included in more than one space
		if ( color.fn[ key ] ) {
			return;
		}
		color.fn[ key ] = function( value ) {
			var vtype = jQuery.type( value ),
				fn = ( key === "alpha" ? ( this._hsla ? "hsla" : "rgba" ) : spaceName ),
				local = this[ fn ](),
				cur = local[ prop.idx ],
				match;

			if ( vtype === "undefined" ) {
				return cur;
			}

			if ( vtype === "function" ) {
				value = value.call( this, cur );
				vtype = jQuery.type( value );
			}
			if ( value == null && prop.empty ) {
				return this;
			}
			if ( vtype === "string" ) {
				match = rplusequals.exec( value );
				if ( match ) {
					value = cur + parseFloat( match[ 2 ] ) * ( match[ 1 ] === "+" ? 1 : -1 );
				}
			}
			local[ prop.idx ] = value;
			return this[ fn ]( local );
		};
	});
});

// add cssHook and .fx.step function for each named hook.
// accept a space separated string of properties
color.hook = function( hook ) {
	var hooks = hook.split( " " );
	each( hooks, function( i, hook ) {
		jQuery.cssHooks[ hook ] = {
			set: function( elem, value ) {
				var parsed, curElem,
					backgroundColor = "";

				if ( jQuery.type( value ) !== "string" || ( parsed = stringParse( value ) ) ) {
					value = color( parsed || value );
					if ( !support.rgba && value._rgba[ 3 ] !== 1 ) {
						curElem = hook === "backgroundColor" ? elem.parentNode : elem;
						while (
							(backgroundColor === "" || backgroundColor === "transparent") &&
							curElem && curElem.style
						) {
							try {
								backgroundColor = jQuery.css( curElem, "backgroundColor" );
								curElem = curElem.parentNode;
							} catch ( e ) {
							}
						}

						value = value.blend( backgroundColor && backgroundColor !== "transparent" ?
							backgroundColor :
							"_default" );
					}

					value = value.toRgbaString();
				}
				try {
					elem.style[ hook ] = value;
				} catch( e ) {
					// wrapped to prevent IE from throwing errors on "invalid" values like 'auto' or 'inherit'
				}
			}
		};
		jQuery.fx.step[ hook ] = function( fx ) {
			if ( !fx.colorInit ) {
				fx.start = color( fx.elem, hook );
				fx.end = color( fx.end );
				fx.colorInit = true;
			}
			jQuery.cssHooks[ hook ].set( fx.elem, fx.start.transition( fx.end, fx.pos ) );
		};
	});

};

color.hook( stepHooks );

jQuery.cssHooks.borderColor = {
	expand: function( value ) {
		var expanded = {};

		each( [ "Top", "Right", "Bottom", "Left" ], function( i, part ) {
			expanded[ "border" + part + "Color" ] = value;
		});
		return expanded;
	}
};

// Basic color names only.
// Usage of any of the other color names requires adding yourself or including
// jquery.color.svg-names.js.
colors = jQuery.Color.names = {
	// 4.1. Basic color keywords
	aqua: "#00ffff",
	black: "#000000",
	blue: "#0000ff",
	fuchsia: "#ff00ff",
	gray: "#808080",
	green: "#008000",
	lime: "#00ff00",
	maroon: "#800000",
	navy: "#000080",
	olive: "#808000",
	purple: "#800080",
	red: "#ff0000",
	silver: "#c0c0c0",
	teal: "#008080",
	white: "#ffffff",
	yellow: "#ffff00",

	// 4.2.3. "transparent" color keyword
	transparent: [ null, null, null, 0 ],

	_default: "#ffffff"
};

})( jQuery );


/******************************************************************************/
/****************************** CLASS ANIMATIONS ******************************/
/******************************************************************************/
(function() {

var classAnimationActions = [ "add", "remove", "toggle" ],
	shorthandStyles = {
		border: 1,
		borderBottom: 1,
		borderColor: 1,
		borderLeft: 1,
		borderRight: 1,
		borderTop: 1,
		borderWidth: 1,
		margin: 1,
		padding: 1
	};

$.each([ "borderLeftStyle", "borderRightStyle", "borderBottomStyle", "borderTopStyle" ], function( _, prop ) {
	$.fx.step[ prop ] = function( fx ) {
		if ( fx.end !== "none" && !fx.setAttr || fx.pos === 1 && !fx.setAttr ) {
			jQuery.style( fx.elem, prop, fx.end );
			fx.setAttr = true;
		}
	};
});

function getElementStyles() {
	var style = this.ownerDocument.defaultView ?
			this.ownerDocument.defaultView.getComputedStyle( this, null ) :
			this.currentStyle,
		newStyle = {},
		key,
		len;

	// webkit enumerates style porperties
	if ( style && style.length && style[ 0 ] && style[ style[ 0 ] ] ) {
		len = style.length;
		while ( len-- ) {
			key = style[ len ];
			if ( typeof style[ key ] === "string" ) {
				newStyle[ $.camelCase( key ) ] = style[ key ];
			}
		}
	} else {
		for ( key in style ) {
			if ( typeof style[ key ] === "string" ) {
				newStyle[ key ] = style[ key ];
			}
		}
	}

	return newStyle;
}


function styleDifference( oldStyle, newStyle ) {
	var diff = {},
		name, value;

	for ( name in newStyle ) {
		value = newStyle[ name ];
		if ( oldStyle[ name ] !== value ) {
			if ( !shorthandStyles[ name ] ) {
				if ( $.fx.step[ name ] || !isNaN( parseFloat( value ) ) ) {
					diff[ name ] = value;
				}
			}
		}
	}

	return diff;
}

$.effects.animateClass = function( value, duration, easing, callback ) {
	var o = $.speed( duration, easing, callback );

	return this.queue( function() {
		var animated = $( this ),
			baseClass = animated.attr( "class" ) || "",
			applyClassChange,
			allAnimations = o.children ? animated.find( "*" ).andSelf() : animated;

		// map the animated objects to store the original styles.
		allAnimations = allAnimations.map(function() {
			var el = $( this );
			return {
				el: el,
				start: getElementStyles.call( this )
			};
		});

		// apply class change
		applyClassChange = function() {
			$.each( classAnimationActions, function(i, action) {
				if ( value[ action ] ) {
					animated[ action + "Class" ]( value[ action ] );
				}
			});
		};
		applyClassChange();

		// map all animated objects again - calculate new styles and diff
		allAnimations = allAnimations.map(function() {
			this.end = getElementStyles.call( this.el[ 0 ] );
			this.diff = styleDifference( this.start, this.end );
			return this;
		});

		// apply original class
		animated.attr( "class", baseClass );

		// map all animated objects again - this time collecting a promise
		allAnimations = allAnimations.map(function() {
			var styleInfo = this,
				dfd = $.Deferred(),
				opts = jQuery.extend({}, o, {
					queue: false,
					complete: function() {
						dfd.resolve( styleInfo );
					}
				});

			this.el.animate( this.diff, opts );
			return dfd.promise();
		});

		// once all animations have completed:
		$.when.apply( $, allAnimations.get() ).done(function() {

			// set the final class
			applyClassChange();

			// for each animated element,
			// clear all css properties that were animated
			$.each( arguments, function() {
				var el = this.el;
				$.each( this.diff, function(key) {
					el.css( key, '' );
				});
			});

			// this is guarnteed to be there if you use jQuery.speed()
			// it also handles dequeuing the next anim...
			o.complete.call( animated[ 0 ] );
		});
	});
};

$.fn.extend({
	_addClass: $.fn.addClass,
	addClass: function( classNames, speed, easing, callback ) {
		return speed ?
			$.effects.animateClass.call( this,
				{ add: classNames }, speed, easing, callback ) :
			this._addClass( classNames );
	},

	_removeClass: $.fn.removeClass,
	removeClass: function( classNames, speed, easing, callback ) {
		return speed ?
			$.effects.animateClass.call( this,
				{ remove: classNames }, speed, easing, callback ) :
			this._removeClass( classNames );
	},

	_toggleClass: $.fn.toggleClass,
	toggleClass: function( classNames, force, speed, easing, callback ) {
		if ( typeof force === "boolean" || force === undefined ) {
			if ( !speed ) {
				// without speed parameter
				return this._toggleClass( classNames, force );
			} else {
				return $.effects.animateClass.call( this,
					(force ? { add: classNames } : { remove: classNames }),
					speed, easing, callback );
			}
		} else {
			// without force parameter
			return $.effects.animateClass.call( this,
				{ toggle: classNames }, force, speed, easing );
		}
	},

	switchClass: function( remove, add, speed, easing, callback) {
		return $.effects.animateClass.call( this, {
			add: add,
			remove: remove
		}, speed, easing, callback );
	}
});

})();

/******************************************************************************/
/*********************************** EFFECTS **********************************/
/******************************************************************************/

(function() {

$.extend( $.effects, {
	version: "@VERSION",

	// Saves a set of properties in a data storage
	save: function( element, set ) {
		for( var i=0; i < set.length; i++ ) {
			if ( set[ i ] !== null ) {
				element.data( dataSpace + set[ i ], element[ 0 ].style[ set[ i ] ] );
			}
		}
	},

	// Restores a set of previously saved properties from a data storage
	restore: function( element, set ) {
		var val, i;
		for( i=0; i < set.length; i++ ) {
			if ( set[ i ] !== null ) {
				val = element.data( dataSpace + set[ i ] );
				// support: jQuery 1.6.2
				// http://bugs.jquery.com/ticket/9917
				// jQuery 1.6.2 incorrectly returns undefined for any falsy value.
				// We can't differentiate between "" and 0 here, so we just assume
				// empty string since it's likely to be a more common value...
				if ( val === undefined ) {
					val = "";
				}
				element.css( set[ i ], val );
			}
		}
	},

	setMode: function( el, mode ) {
		if (mode === "toggle") {
			mode = el.is( ":hidden" ) ? "show" : "hide";
		}
		return mode;
	},

	// Translates a [top,left] array into a baseline value
	// this should be a little more flexible in the future to handle a string & hash
	getBaseline: function( origin, original ) {
		var y, x;
		switch ( origin[ 0 ] ) {
			case "top": y = 0; break;
			case "middle": y = 0.5; break;
			case "bottom": y = 1; break;
			default: y = origin[ 0 ] / original.height;
		}
		switch ( origin[ 1 ] ) {
			case "left": x = 0; break;
			case "center": x = 0.5; break;
			case "right": x = 1; break;
			default: x = origin[ 1 ] / original.width;
		}
		return {
			x: x,
			y: y
		};
	},

	// Wraps the element around a wrapper that copies position properties
	createWrapper: function( element ) {

		// if the element is already wrapped, return it
		if ( element.parent().is( ".ui-effects-wrapper" )) {
			return element.parent();
		}

		// wrap the element
		var props = {
				width: element.outerWidth(true),
				height: element.outerHeight(true),
				"float": element.css( "float" )
			},
			wrapper = $( "<div></div>" )
				.addClass( "ui-effects-wrapper" )
				.css({
					fontSize: "100%",
					background: "transparent",
					border: "none",
					margin: 0,
					padding: 0
				}),
			// Store the size in case width/height are defined in % - Fixes #5245
			size = {
				width: element.width(),
				height: element.height()
			},
			active = document.activeElement;

		// support: Firefox
		// Firefox incorrectly exposes anonymous content
		// https://bugzilla.mozilla.org/show_bug.cgi?id=561664
		try {
			active.id;
		} catch( e ) {
			active = document.body;
		}

		element.wrap( wrapper );

		// Fixes #7595 - Elements lose focus when wrapped.
		if ( element[ 0 ] === active || $.contains( element[ 0 ], active ) ) {
			$( active ).focus();
		}

		wrapper = element.parent(); //Hotfix for jQuery 1.4 since some change in wrap() seems to actually lose the reference to the wrapped element

		// transfer positioning properties to the wrapper
		if ( element.css( "position" ) === "static" ) {
			wrapper.css({ position: "relative" });
			element.css({ position: "relative" });
		} else {
			$.extend( props, {
				position: element.css( "position" ),
				zIndex: element.css( "z-index" )
			});
			$.each([ "top", "left", "bottom", "right" ], function(i, pos) {
				props[ pos ] = element.css( pos );
				if ( isNaN( parseInt( props[ pos ], 10 ) ) ) {
					props[ pos ] = "auto";
				}
			});
			element.css({
				position: "relative",
				top: 0,
				left: 0,
				right: "auto",
				bottom: "auto"
			});
		}
		element.css(size);

		return wrapper.css( props ).show();
	},

	removeWrapper: function( element ) {
		var active = document.activeElement;

		if ( element.parent().is( ".ui-effects-wrapper" ) ) {
			element.parent().replaceWith( element );

			// Fixes #7595 - Elements lose focus when wrapped.
			if ( element[ 0 ] === active || $.contains( element[ 0 ], active ) ) {
				$( active ).focus();
			}
		}


		return element;
	},

	setTransition: function( element, list, factor, value ) {
		value = value || {};
		$.each( list, function( i, x ) {
			var unit = element.cssUnit( x );
			if ( unit[ 0 ] > 0 ) {
				value[ x ] = unit[ 0 ] * factor + unit[ 1 ];
			}
		});
		return value;
	}
});

// return an effect options object for the given parameters:
function _normalizeArguments( effect, options, speed, callback ) {

	// allow passing all options as the first parameter
	if ( $.isPlainObject( effect ) ) {
		options = effect;
		effect = effect.effect;
	}

	// convert to an object
	effect = { effect: effect };

	// catch (effect, null, ...)
	if ( options == null ) {
		options = {};
	}

	// catch (effect, callback)
	if ( $.isFunction( options ) ) {
		callback = options;
		speed = null;
		options = {};
	}

	// catch (effect, speed, ?)
	if ( typeof options === "number" || $.fx.speeds[ options ] ) {
		callback = speed;
		speed = options;
		options = {};
	}

	// catch (effect, options, callback)
	if ( $.isFunction( speed ) ) {
		callback = speed;
		speed = null;
	}

	// add options to effect
	if ( options ) {
		$.extend( effect, options );
	}

	speed = speed || options.duration;
	effect.duration = $.fx.off ? 0 :
		typeof speed === "number" ? speed :
		speed in $.fx.speeds ? $.fx.speeds[ speed ] :
		$.fx.speeds._default;

	effect.complete = callback || options.complete;

	return effect;
}

function standardSpeed( speed ) {
	// valid standard speeds
	if ( !speed || typeof speed === "number" || $.fx.speeds[ speed ] ) {
		return true;
	}

	// invalid strings - treat as "normal" speed
	if ( typeof speed === "string" && !$.effects.effect[ speed ] ) {
		// TODO: remove in 2.0 (#7115)
		if ( backCompat && $.effects[ speed ] ) {
			return false;
		}
		return true;
	}

	return false;
}

$.fn.extend({
	effect: function( /* effect, options, speed, callback */ ) {
		var args = _normalizeArguments.apply( this, arguments ),
			mode = args.mode,
			queue = args.queue,
			effectMethod = $.effects.effect[ args.effect ],

			// DEPRECATED: remove in 2.0 (#7115)
			oldEffectMethod = !effectMethod && backCompat && $.effects[ args.effect ];

		if ( $.fx.off || !( effectMethod || oldEffectMethod ) ) {
			// delegate to the original method (e.g., .show()) if possible
			if ( mode ) {
				return this[ mode ]( args.duration, args.complete );
			} else {
				return this.each( function() {
					if ( args.complete ) {
						args.complete.call( this );
					}
				});
			}
		}

		function run( next ) {
			var elem = $( this ),
				complete = args.complete,
				mode = args.mode;

			function done() {
				if ( $.isFunction( complete ) ) {
					complete.call( elem[0] );
				}
				if ( $.isFunction( next ) ) {
					next();
				}
			}

			// if the element is hiddden and mode is hide,
			// or element is visible and mode is show
			if ( elem.is( ":hidden" ) ? mode === "hide" : mode === "show" ) {
				done();
			} else {
				effectMethod.call( elem[0], args, done );
			}
		}

		// TODO: remove this check in 2.0, effectMethod will always be true
		if ( effectMethod ) {
			return queue === false ? this.each( run ) : this.queue( queue || "fx", run );
		} else {
			// DEPRECATED: remove in 2.0 (#7115)
			return oldEffectMethod.call(this, {
				options: args,
				duration: args.duration,
				callback: args.complete,
				mode: args.mode
			});
		}
	},

	_show: $.fn.show,
	show: function( speed ) {
		if ( standardSpeed( speed ) ) {
			return this._show.apply( this, arguments );
		} else {
			var args = _normalizeArguments.apply( this, arguments );
			args.mode = "show";
			return this.effect.call( this, args );
		}
	},

	_hide: $.fn.hide,
	hide: function( speed ) {
		if ( standardSpeed( speed ) ) {
			return this._hide.apply( this, arguments );
		} else {
			var args = _normalizeArguments.apply( this, arguments );
			args.mode = "hide";
			return this.effect.call( this, args );
		}
	},

	// jQuery core overloads toggle and creates _toggle
	__toggle: $.fn.toggle,
	toggle: function( speed ) {
		if ( standardSpeed( speed ) || typeof speed === "boolean" || $.isFunction( speed ) ) {
			return this.__toggle.apply( this, arguments );
		} else {
			var args = _normalizeArguments.apply( this, arguments );
			args.mode = "toggle";
			return this.effect.call( this, args );
		}
	},

	// helper functions
	cssUnit: function(key) {
		var style = this.css( key ),
			val = [];

		$.each( [ "em", "px", "%", "pt" ], function( i, unit ) {
			if ( style.indexOf( unit ) > 0 ) {
				val = [ parseFloat( style ), unit ];
			}
		});
		return val;
	}
});

})();

/******************************************************************************/
/*********************************** EASING ***********************************/
/******************************************************************************/

(function() {

// based on easing equations from Robert Penner (http://www.robertpenner.com/easing)

var baseEasings = {};

$.each( [ "Quad", "Cubic", "Quart", "Quint", "Expo" ], function( i, name ) {
	baseEasings[ name ] = function( p ) {
		return Math.pow( p, i + 2 );
	};
});

$.extend( baseEasings, {
	Sine: function ( p ) {
		return 1 - Math.cos( p * Math.PI / 2 );
	},
	Circ: function ( p ) {
		return 1 - Math.sqrt( 1 - p * p );
	},
	Elastic: function( p ) {
		return p === 0 || p === 1 ? p :
			-Math.pow( 2, 8 * (p - 1) ) * Math.sin( ( (p - 1) * 80 - 7.5 ) * Math.PI / 15 );
	},
	Back: function( p ) {
		return p * p * ( 3 * p - 2 );
	},
	Bounce: function ( p ) {
		var pow2,
			bounce = 4;

		while ( p < ( ( pow2 = Math.pow( 2, --bounce ) ) - 1 ) / 11 ) {}
		return 1 / Math.pow( 4, 3 - bounce ) - 7.5625 * Math.pow( ( pow2 * 3 - 2 ) / 22 - p, 2 );
	}
});

$.each( baseEasings, function( name, easeIn ) {
	$.easing[ "easeIn" + name ] = easeIn;
	$.easing[ "easeOut" + name ] = function( p ) {
		return 1 - easeIn( 1 - p );
	};
	$.easing[ "easeInOut" + name ] = function( p ) {
		return p < 0.5 ?
			easeIn( p * 2 ) / 2 :
			1 - easeIn( p * -2 + 2 ) / 2;
	};
});

})();

})(jQuery));
/*!
 * jQuery UI Position @VERSION
 * http://jqueryui.com
 *
 * Copyright 2012 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/position/
 */
(function( $, undefined ) {

$.ui = $.ui || {};

var cachedScrollbarWidth,
	max = Math.max,
	abs = Math.abs,
	round = Math.round,
	rhorizontal = /left|center|right/,
	rvertical = /top|center|bottom/,
	roffset = /[\+\-]\d+%?/,
	rposition = /^\w+/,
	rpercent = /%$/,
	_position = $.fn.position;

function getOffsets( offsets, width, height ) {
	return [
		parseInt( offsets[ 0 ], 10 ) * ( rpercent.test( offsets[ 0 ] ) ? width / 100 : 1 ),
		parseInt( offsets[ 1 ], 10 ) * ( rpercent.test( offsets[ 1 ] ) ? height / 100 : 1 )
	];
}
function parseCss( element, property ) {
	return parseInt( $.css( element, property ), 10 ) || 0;
}

$.position = {
	scrollbarWidth: function() {
		if ( cachedScrollbarWidth !== undefined ) {
			return cachedScrollbarWidth;
		}
		var w1, w2,
			div = $( "<div style='display:block;width:50px;height:50px;overflow:hidden;'><div style='height:100px;width:auto;'></div></div>" ),
			innerDiv = div.children()[0];

		$( "body" ).append( div );
		w1 = innerDiv.offsetWidth;
		div.css( "overflow", "scroll" );

		w2 = innerDiv.offsetWidth;

		if ( w1 === w2 ) {
			w2 = div[0].clientWidth;
		}

		div.remove();

		return (cachedScrollbarWidth = w1 - w2);
	},
	getScrollInfo: function( within ) {
		var overflowX = within.isWindow ? "" : within.element.css( "overflow-x" ),
			overflowY = within.isWindow ? "" : within.element.css( "overflow-y" ),
			hasOverflowX = overflowX === "scroll" ||
				( overflowX === "auto" && within.width < within.element[0].scrollWidth ),
			hasOverflowY = overflowY === "scroll" ||
				( overflowY === "auto" && within.height < within.element[0].scrollHeight );
		return {
			width: hasOverflowX ? $.position.scrollbarWidth() : 0,
			height: hasOverflowY ? $.position.scrollbarWidth() : 0
		};
	},
	getWithinInfo: function( element ) {
		var withinElement = $( element || window ),
			isWindow = $.isWindow( withinElement[0] );
		return {
			element: withinElement,
			isWindow: isWindow,
			offset: withinElement.offset() || { left: 0, top: 0 },
			scrollLeft: withinElement.scrollLeft(),
			scrollTop: withinElement.scrollTop(),
			width: isWindow ? withinElement.width() : withinElement.outerWidth(),
			height: isWindow ? withinElement.height() : withinElement.outerHeight()
		};
	}
};

$.fn.position = function( options ) {
	if ( !options || !options.of ) {
		return _position.apply( this, arguments );
	}

	// make a copy, we don't want to modify arguments
	options = $.extend( {}, options );

	var atOffset, targetWidth, targetHeight, targetOffset, basePosition,
		target = $( options.of ),
		within = $.position.getWithinInfo( options.within ),
		scrollInfo = $.position.getScrollInfo( within ),
		targetElem = target[0],
		collision = ( options.collision || "flip" ).split( " " ),
		offsets = {};

	if ( targetElem.nodeType === 9 ) {
		targetWidth = target.width();
		targetHeight = target.height();
		targetOffset = { top: 0, left: 0 };
	} else if ( $.isWindow( targetElem ) ) {
		targetWidth = target.width();
		targetHeight = target.height();
		targetOffset = { top: target.scrollTop(), left: target.scrollLeft() };
	} else if ( targetElem.preventDefault ) {
		// force left top to allow flipping
		options.at = "left top";
		targetWidth = targetHeight = 0;
		targetOffset = { top: targetElem.pageY, left: targetElem.pageX };
	} else {
		targetWidth = target.outerWidth();
		targetHeight = target.outerHeight();
		targetOffset = target.offset();
	}
	// clone to reuse original targetOffset later
	basePosition = $.extend( {}, targetOffset );

	// force my and at to have valid horizontal and vertical positions
	// if a value is missing or invalid, it will be converted to center
	$.each( [ "my", "at" ], function() {
		var pos = ( options[ this ] || "" ).split( " " ),
			horizontalOffset,
			verticalOffset;

		if ( pos.length === 1) {
			pos = rhorizontal.test( pos[ 0 ] ) ?
				pos.concat( [ "center" ] ) :
				rvertical.test( pos[ 0 ] ) ?
					[ "center" ].concat( pos ) :
					[ "center", "center" ];
		}
		pos[ 0 ] = rhorizontal.test( pos[ 0 ] ) ? pos[ 0 ] : "center";
		pos[ 1 ] = rvertical.test( pos[ 1 ] ) ? pos[ 1 ] : "center";

		// calculate offsets
		horizontalOffset = roffset.exec( pos[ 0 ] );
		verticalOffset = roffset.exec( pos[ 1 ] );
		offsets[ this ] = [
			horizontalOffset ? horizontalOffset[ 0 ] : 0,
			verticalOffset ? verticalOffset[ 0 ] : 0
		];

		// reduce to just the positions without the offsets
		options[ this ] = [
			rposition.exec( pos[ 0 ] )[ 0 ],
			rposition.exec( pos[ 1 ] )[ 0 ]
		];
	});

	// normalize collision option
	if ( collision.length === 1 ) {
		collision[ 1 ] = collision[ 0 ];
	}

	if ( options.at[ 0 ] === "right" ) {
		basePosition.left += targetWidth;
	} else if ( options.at[ 0 ] === "center" ) {
		basePosition.left += targetWidth / 2;
	}

	if ( options.at[ 1 ] === "bottom" ) {
		basePosition.top += targetHeight;
	} else if ( options.at[ 1 ] === "center" ) {
		basePosition.top += targetHeight / 2;
	}

	atOffset = getOffsets( offsets.at, targetWidth, targetHeight );
	basePosition.left += atOffset[ 0 ];
	basePosition.top += atOffset[ 1 ];

	return this.each(function() {
		var collisionPosition, using,
			elem = $( this ),
			elemWidth = elem.outerWidth(),
			elemHeight = elem.outerHeight(),
			marginLeft = parseCss( this, "marginLeft" ),
			marginTop = parseCss( this, "marginTop" ),
			collisionWidth = elemWidth + marginLeft + parseCss( this, "marginRight" ) + scrollInfo.width,
			collisionHeight = elemHeight + marginTop + parseCss( this, "marginBottom" ) + scrollInfo.height,
			position = $.extend( {}, basePosition ),
			myOffset = getOffsets( offsets.my, elem.outerWidth(), elem.outerHeight() );

		if ( options.my[ 0 ] === "right" ) {
			position.left -= elemWidth;
		} else if ( options.my[ 0 ] === "center" ) {
			position.left -= elemWidth / 2;
		}

		if ( options.my[ 1 ] === "bottom" ) {
			position.top -= elemHeight;
		} else if ( options.my[ 1 ] === "center" ) {
			position.top -= elemHeight / 2;
		}

		position.left += myOffset[ 0 ];
		position.top += myOffset[ 1 ];

		// if the browser doesn't support fractions, then round for consistent results
		if ( !$.support.offsetFractions ) {
			position.left = round( position.left );
			position.top = round( position.top );
		}

		collisionPosition = {
			marginLeft: marginLeft,
			marginTop: marginTop
		};

		$.each( [ "left", "top" ], function( i, dir ) {
			if ( $.ui.position[ collision[ i ] ] ) {
				$.ui.position[ collision[ i ] ][ dir ]( position, {
					targetWidth: targetWidth,
					targetHeight: targetHeight,
					elemWidth: elemWidth,
					elemHeight: elemHeight,
					collisionPosition: collisionPosition,
					collisionWidth: collisionWidth,
					collisionHeight: collisionHeight,
					offset: [ atOffset[ 0 ] + myOffset[ 0 ], atOffset [ 1 ] + myOffset[ 1 ] ],
					my: options.my,
					at: options.at,
					within: within,
					elem : elem
				});
			}
		});

		if ( $.fn.bgiframe ) {
			elem.bgiframe();
		}

		if ( options.using ) {
			// adds feedback as second argument to using callback, if present
			using = function( props ) {
				var left = targetOffset.left - position.left,
					right = left + targetWidth - elemWidth,
					top = targetOffset.top - position.top,
					bottom = top + targetHeight - elemHeight,
					feedback = {
						target: {
							element: target,
							left: targetOffset.left,
							top: targetOffset.top,
							width: targetWidth,
							height: targetHeight
						},
						element: {
							element: elem,
							left: position.left,
							top: position.top,
							width: elemWidth,
							height: elemHeight
						},
						horizontal: right < 0 ? "left" : left > 0 ? "right" : "center",
						vertical: bottom < 0 ? "top" : top > 0 ? "bottom" : "middle"
					};
				if ( targetWidth < elemWidth && abs( left + right ) < targetWidth ) {
					feedback.horizontal = "center";
				}
				if ( targetHeight < elemHeight && abs( top + bottom ) < targetHeight ) {
					feedback.vertical = "middle";
				}
				if ( max( abs( left ), abs( right ) ) > max( abs( top ), abs( bottom ) ) ) {
					feedback.important = "horizontal";
				} else {
					feedback.important = "vertical";
				}
				options.using.call( this, props, feedback );
			};
		}

		elem.offset( $.extend( position, { using: using } ) );
	});
};

$.ui.position = {
	fit: {
		left: function( position, data ) {
			var within = data.within,
				withinOffset = within.isWindow ? within.scrollLeft : within.offset.left,
				outerWidth = within.width,
				collisionPosLeft = position.left - data.collisionPosition.marginLeft,
				overLeft = withinOffset - collisionPosLeft,
				overRight = collisionPosLeft + data.collisionWidth - outerWidth - withinOffset,
				newOverRight;

			// element is wider than within
			if ( data.collisionWidth > outerWidth ) {
				// element is initially over the left side of within
				if ( overLeft > 0 && overRight <= 0 ) {
					newOverRight = position.left + overLeft + data.collisionWidth - outerWidth - withinOffset;
					position.left += overLeft - newOverRight;
				// element is initially over right side of within
				} else if ( overRight > 0 && overLeft <= 0 ) {
					position.left = withinOffset;
				// element is initially over both left and right sides of within
				} else {
					if ( overLeft > overRight ) {
						position.left = withinOffset + outerWidth - data.collisionWidth;
					} else {
						position.left = withinOffset;
					}
				}
			// too far left -> align with left edge
			} else if ( overLeft > 0 ) {
				position.left += overLeft;
			// too far right -> align with right edge
			} else if ( overRight > 0 ) {
				position.left -= overRight;
			// adjust based on position and margin
			} else {
				position.left = max( position.left - collisionPosLeft, position.left );
			}
		},
		top: function( position, data ) {
			var within = data.within,
				withinOffset = within.isWindow ? within.scrollTop : within.offset.top,
				outerHeight = data.within.height,
				collisionPosTop = position.top - data.collisionPosition.marginTop,
				overTop = withinOffset - collisionPosTop,
				overBottom = collisionPosTop + data.collisionHeight - outerHeight - withinOffset,
				newOverBottom;

			// element is taller than within
			if ( data.collisionHeight > outerHeight ) {
				// element is initially over the top of within
				if ( overTop > 0 && overBottom <= 0 ) {
					newOverBottom = position.top + overTop + data.collisionHeight - outerHeight - withinOffset;
					position.top += overTop - newOverBottom;
				// element is initially over bottom of within
				} else if ( overBottom > 0 && overTop <= 0 ) {
					position.top = withinOffset;
				// element is initially over both top and bottom of within
				} else {
					if ( overTop > overBottom ) {
						position.top = withinOffset + outerHeight - data.collisionHeight;
					} else {
						position.top = withinOffset;
					}
				}
			// too far up -> align with top
			} else if ( overTop > 0 ) {
				position.top += overTop;
			// too far down -> align with bottom edge
			} else if ( overBottom > 0 ) {
				position.top -= overBottom;
			// adjust based on position and margin
			} else {
				position.top = max( position.top - collisionPosTop, position.top );
			}
		}
	},
	flip: {
		left: function( position, data ) {
			var within = data.within,
				withinOffset = within.offset.left + within.scrollLeft,
				outerWidth = within.width,
				offsetLeft = within.isWindow ? within.scrollLeft : within.offset.left,
				collisionPosLeft = position.left - data.collisionPosition.marginLeft,
				overLeft = collisionPosLeft - offsetLeft,
				overRight = collisionPosLeft + data.collisionWidth - outerWidth - offsetLeft,
				myOffset = data.my[ 0 ] === "left" ?
					-data.elemWidth :
					data.my[ 0 ] === "right" ?
						data.elemWidth :
						0,
				atOffset = data.at[ 0 ] === "left" ?
					data.targetWidth :
					data.at[ 0 ] === "right" ?
						-data.targetWidth :
						0,
				offset = -2 * data.offset[ 0 ],
				newOverRight,
				newOverLeft;

			if ( overLeft < 0 ) {
				newOverRight = position.left + myOffset + atOffset + offset + data.collisionWidth - outerWidth - withinOffset;
				if ( newOverRight < 0 || newOverRight < abs( overLeft ) ) {
					position.left += myOffset + atOffset + offset;
				}
			}
			else if ( overRight > 0 ) {
				newOverLeft = position.left - data.collisionPosition.marginLeft + myOffset + atOffset + offset - offsetLeft;
				if ( newOverLeft > 0 || abs( newOverLeft ) < overRight ) {
					position.left += myOffset + atOffset + offset;
				}
			}
		},
		top: function( position, data ) {
			var within = data.within,
				withinOffset = within.offset.top + within.scrollTop,
				outerHeight = within.height,
				offsetTop = within.isWindow ? within.scrollTop : within.offset.top,
				collisionPosTop = position.top - data.collisionPosition.marginTop,
				overTop = collisionPosTop - offsetTop,
				overBottom = collisionPosTop + data.collisionHeight - outerHeight - offsetTop,
				top = data.my[ 1 ] === "top",
				myOffset = top ?
					-data.elemHeight :
					data.my[ 1 ] === "bottom" ?
						data.elemHeight :
						0,
				atOffset = data.at[ 1 ] === "top" ?
					data.targetHeight :
					data.at[ 1 ] === "bottom" ?
						-data.targetHeight :
						0,
				offset = -2 * data.offset[ 1 ],
				newOverTop,
				newOverBottom;
			if ( overTop < 0 ) {
				newOverBottom = position.top + myOffset + atOffset + offset + data.collisionHeight - outerHeight - withinOffset;
				if ( ( position.top + myOffset + atOffset + offset) > overTop && ( newOverBottom < 0 || newOverBottom < abs( overTop ) ) ) {
					position.top += myOffset + atOffset + offset;
				}
			}
			else if ( overBottom > 0 ) {
				newOverTop = position.top -  data.collisionPosition.marginTop + myOffset + atOffset + offset - offsetTop;
				if ( ( position.top + myOffset + atOffset + offset) > overBottom && ( newOverTop > 0 || abs( newOverTop ) < overBottom ) ) {
					position.top += myOffset + atOffset + offset;
				}
			}
		}
	},
	flipfit: {
		left: function() {
			$.ui.position.flip.left.apply( this, arguments );
			$.ui.position.fit.left.apply( this, arguments );
		},
		top: function() {
			$.ui.position.flip.top.apply( this, arguments );
			$.ui.position.fit.top.apply( this, arguments );
		}
	}
};

// fraction support test
(function () {
	var testElement, testElementParent, testElementStyle, offsetLeft, i,
		body = document.getElementsByTagName( "body" )[ 0 ],
		div = document.createElement( "div" );

	//Create a "fake body" for testing based on method used in jQuery.support
	testElement = document.createElement( body ? "div" : "body" );
	testElementStyle = {
		visibility: "hidden",
		width: 0,
		height: 0,
		border: 0,
		margin: 0,
		background: "none"
	};
	if ( body ) {
		$.extend( testElementStyle, {
			position: "absolute",
			left: "-1000px",
			top: "-1000px"
		});
	}
	for ( i in testElementStyle ) {
		testElement.style[ i ] = testElementStyle[ i ];
	}
	testElement.appendChild( div );
	testElementParent = body || document.documentElement;
	testElementParent.insertBefore( testElement, testElementParent.firstChild );

	div.style.cssText = "position: absolute; left: 10.7432222px;";

	offsetLeft = $( div ).offset().left;
	$.support.offsetFractions = offsetLeft > 10 && offsetLeft < 11;

	testElement.innerHTML = "";
	testElementParent.removeChild( testElement );
})();

// DEPRECATED
if ( $.uiBackCompat !== false ) {
	// offset option
	(function( $ ) {
		var _position = $.fn.position;
		$.fn.position = function( options ) {
			if ( !options || !options.offset ) {
				return _position.call( this, options );
			}
			var offset = options.offset.split( " " ),
				at = options.at.split( " " );
			if ( offset.length === 1 ) {
				offset[ 1 ] = offset[ 0 ];
			}
			if ( /^\d/.test( offset[ 0 ] ) ) {
				offset[ 0 ] = "+" + offset[ 0 ];
			}
			if ( /^\d/.test( offset[ 1 ] ) ) {
				offset[ 1 ] = "+" + offset[ 1 ];
			}
			if ( at.length === 1 ) {
				if ( /left|center|right/.test( at[ 0 ] ) ) {
					at[ 1 ] = "center";
				} else {
					at[ 1 ] = at[ 0 ];
					at[ 0 ] = "center";
				}
			}
			return _position.call( this, $.extend( options, {
				at: at[ 0 ] + offset[ 0 ] + " " + at[ 1 ] + offset[ 1 ],
				offset: undefined
			} ) );
		};
	}( jQuery ) );
}

}( jQuery ) );
/*!
 * jQuery UI Datepicker @VERSION
 * http://jqueryui.com
 *
 * Copyright 2012 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/datepicker/
 *
 * Depends:
 *	jquery.ui.core.js
 */
(function( $, undefined ) {

$.extend($.ui, { datepicker: { version: "@VERSION" } });

var PROP_NAME = 'datepicker';
var dpuuid = new Date().getTime();
var instActive;

/* Date picker manager.
   Use the singleton instance of this class, $.datepicker, to interact with the date picker.
   Settings for (groups of) date pickers are maintained in an instance object,
   allowing multiple different settings on the same page. */

function Datepicker() {
	this.debug = false; // Change this to true to start debugging
	this._curInst = null; // The current instance in use
	this._keyEvent = false; // If the last event was a key event
	this._disabledInputs = []; // List of date picker inputs that have been disabled
	this._datepickerShowing = false; // True if the popup picker is showing , false if not
	this._inDialog = false; // True if showing within a "dialog", false if not
	this._mainDivId = 'ui-datepicker-div'; // The ID of the main datepicker division
	this._inlineClass = 'ui-datepicker-inline'; // The name of the inline marker class
	this._appendClass = 'ui-datepicker-append'; // The name of the append marker class
	this._triggerClass = 'ui-datepicker-trigger'; // The name of the trigger marker class
	this._dialogClass = 'ui-datepicker-dialog'; // The name of the dialog marker class
	this._disableClass = 'ui-datepicker-disabled'; // The name of the disabled covering marker class
	this._unselectableClass = 'ui-datepicker-unselectable'; // The name of the unselectable cell marker class
	this._currentClass = 'ui-datepicker-current-day'; // The name of the current day marker class
	this._dayOverClass = 'ui-datepicker-days-cell-over'; // The name of the day hover marker class
	this.regional = []; // Available regional settings, indexed by language code
	this.regional[''] = { // Default regional settings
		closeText: 'Done', // Display text for close link
		prevText: 'Prev', // Display text for previous month link
		nextText: 'Next', // Display text for next month link
		currentText: 'Today', // Display text for current month link
		monthNames: ['January','February','March','April','May','June',
			'July','August','September','October','November','December'], // Names of months for drop-down and formatting
		monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], // For formatting
		dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'], // For formatting
		dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], // For formatting
		dayNamesMin: ['Su','Mo','Tu','We','Th','Fr','Sa'], // Column headings for days starting at Sunday
		weekHeader: 'Wk', // Column header for week of the year
		dateFormat: 'mm/dd/yy', // See format options on parseDate
		firstDay: 0, // The first day of the week, Sun = 0, Mon = 1, ...
		isRTL: false, // True if right-to-left language, false if left-to-right
		showMonthAfterYear: false, // True if the year select precedes month, false for month then year
		yearSuffix: '' // Additional text to append to the year in the month headers
	};
	this._defaults = { // Global defaults for all the date picker instances
		showOn: 'focus', // 'focus' for popup on focus,
			// 'button' for trigger button, or 'both' for either
		showAnim: 'fadeIn', // Name of jQuery animation for popup
		showOptions: {}, // Options for enhanced animations
		defaultDate: null, // Used when field is blank: actual date,
			// +/-number for offset from today, null for today
		appendText: '', // Display text following the input box, e.g. showing the format
		buttonText: '...', // Text for trigger button
		buttonImage: '', // URL for trigger button image
		buttonImageOnly: false, // True if the image appears alone, false if it appears on a button
		hideIfNoPrevNext: false, // True to hide next/previous month links
			// if not applicable, false to just disable them
		navigationAsDateFormat: false, // True if date formatting applied to prev/today/next links
		gotoCurrent: false, // True if today link goes back to current selection instead
		changeMonth: false, // True if month can be selected directly, false if only prev/next
		changeYear: false, // True if year can be selected directly, false if only prev/next
		yearRange: 'c-10:c+10', // Range of years to display in drop-down,
			// either relative to today's year (-nn:+nn), relative to currently displayed year
			// (c-nn:c+nn), absolute (nnnn:nnnn), or a combination of the above (nnnn:-n)
		showOtherMonths: false, // True to show dates in other months, false to leave blank
		selectOtherMonths: false, // True to allow selection of dates in other months, false for unselectable
		showWeek: false, // True to show week of the year, false to not show it
		calculateWeek: this.iso8601Week, // How to calculate the week of the year,
			// takes a Date and returns the number of the week for it
		shortYearCutoff: '+10', // Short year values < this are in the current century,
			// > this are in the previous century,
			// string value starting with '+' for current year + value
		minDate: null, // The earliest selectable date, or null for no limit
		maxDate: null, // The latest selectable date, or null for no limit
		duration: 'fast', // Duration of display/closure
		beforeShowDay: null, // Function that takes a date and returns an array with
			// [0] = true if selectable, false if not, [1] = custom CSS class name(s) or '',
			// [2] = cell title (optional), e.g. $.datepicker.noWeekends
		beforeShow: null, // Function that takes an input field and
			// returns a set of custom settings for the date picker
		onSelect: null, // Define a callback function when a date is selected
		onChangeMonthYear: null, // Define a callback function when the month or year is changed
		onClose: null, // Define a callback function when the datepicker is closed
		numberOfMonths: 1, // Number of months to show at a time
		showCurrentAtPos: 0, // The position in multipe months at which to show the current month (starting at 0)
		stepMonths: 1, // Number of months to step back/forward
		stepBigMonths: 12, // Number of months to step back/forward for the big links
		altField: '', // Selector for an alternate field to store selected dates into
		altFormat: '', // The date format to use for the alternate field
		constrainInput: true, // The input is constrained by the current date format
		showButtonPanel: false, // True to show button panel, false to not show it
		autoSize: false, // True to size the input for the date format, false to leave as is
		disabled: false // The initial disabled state
	};
	$.extend(this._defaults, this.regional['']);
	this.dpDiv = bindHover($('<div id="' + this._mainDivId + '" class="ui-datepicker ui-widget ui-widget-content ui-helper-clearfix ui-corner-all"></div>'));
}

$.extend(Datepicker.prototype, {
	/* Class name added to elements to indicate already configured with a date picker. */
	markerClassName: 'hasDatepicker',

	//Keep track of the maximum number of rows displayed (see #7043)
	maxRows: 4,

	/* Debug logging (if enabled). */
	log: function () {
		if (this.debug)
			console.log.apply('', arguments);
	},

	// TODO rename to "widget" when switching to widget factory
	_widgetDatepicker: function() {
		return this.dpDiv;
	},

	/* Override the default settings for all instances of the date picker.
	   @param  settings  object - the new settings to use as defaults (anonymous object)
	   @return the manager object */
	setDefaults: function(settings) {
		extendRemove(this._defaults, settings || {});
		return this;
	},

	/* Attach the date picker to a jQuery selection.
	   @param  target    element - the target input field or division or span
	   @param  settings  object - the new settings to use for this date picker instance (anonymous) */
	_attachDatepicker: function(target, settings) {
		// check for settings on the control itself - in namespace 'date:'
		var inlineSettings = null;
		for (var attrName in this._defaults) {
			var attrValue = target.getAttribute('date:' + attrName);
			if (attrValue) {
				inlineSettings = inlineSettings || {};
				try {
					inlineSettings[attrName] = eval(attrValue);
				} catch (err) {
					inlineSettings[attrName] = attrValue;
				}
			}
		}
		var nodeName = target.nodeName.toLowerCase();
		var inline = (nodeName == 'div' || nodeName == 'span');
		if (!target.id) {
			this.uuid += 1;
			target.id = 'dp' + this.uuid;
		}
		var inst = this._newInst($(target), inline);
		inst.settings = $.extend({}, settings || {}, inlineSettings || {});
		if (nodeName == 'input') {
			this._connectDatepicker(target, inst);
		} else if (inline) {
			this._inlineDatepicker(target, inst);
		}
	},

	/* Create a new instance object. */
	_newInst: function(target, inline) {
		var id = target[0].id.replace(/([^A-Za-z0-9_-])/g, '\\\\$1'); // escape jQuery meta chars
		return {id: id, input: target, // associated target
			selectedDay: 0, selectedMonth: 0, selectedYear: 0, // current selection
			drawMonth: 0, drawYear: 0, // month being drawn
			inline: inline, // is datepicker inline or not
			dpDiv: (!inline ? this.dpDiv : // presentation div
			bindHover($('<div class="' + this._inlineClass + ' ui-datepicker ui-widget ui-widget-content ui-helper-clearfix ui-corner-all"></div>')))};
	},

	/* Attach the date picker to an input field. */
	_connectDatepicker: function(target, inst) {
		var input = $(target);
		inst.append = $([]);
		inst.trigger = $([]);
		if (input.hasClass(this.markerClassName))
			return;
		this._attachments(input, inst);
		input.addClass(this.markerClassName).keydown(this._doKeyDown).
			keypress(this._doKeyPress).keyup(this._doKeyUp).
			bind("setData.datepicker", function(event, key, value) {
				inst.settings[key] = value;
			}).bind("getData.datepicker", function(event, key) {
				return this._get(inst, key);
			});
		this._autoSize(inst);
		$.data(target, PROP_NAME, inst);
		//If disabled option is true, disable the datepicker once it has been attached to the input (see ticket #5665)
		if( inst.settings.disabled ) {
			this._disableDatepicker( target );
		}
	},

	/* Make attachments based on settings. */
	_attachments: function(input, inst) {
		var appendText = this._get(inst, 'appendText');
		var isRTL = this._get(inst, 'isRTL');
		if (inst.append)
			inst.append.remove();
		if (appendText) {
			inst.append = $('<span class="' + this._appendClass + '">' + appendText + '</span>');
			input[isRTL ? 'before' : 'after'](inst.append);
		}
		input.unbind('focus', this._showDatepicker);
		if (inst.trigger)
			inst.trigger.remove();
		var showOn = this._get(inst, 'showOn');
		if (showOn == 'focus' || showOn == 'both') // pop-up date picker when in the marked field
			input.focus(this._showDatepicker);
		if (showOn == 'button' || showOn == 'both') { // pop-up date picker when button clicked
			var buttonText = this._get(inst, 'buttonText');
			var buttonImage = this._get(inst, 'buttonImage');
			inst.trigger = $(this._get(inst, 'buttonImageOnly') ?
				$('<img/>').addClass(this._triggerClass).
					attr({ src: buttonImage, alt: buttonText, title: buttonText }) :
				$('<button type="button"></button>').addClass(this._triggerClass).
					html(buttonImage == '' ? buttonText : $('<img/>').attr(
					{ src:buttonImage, alt:buttonText, title:buttonText })));
			input[isRTL ? 'before' : 'after'](inst.trigger);
			inst.trigger.click(function() {
				if ($.datepicker._datepickerShowing && $.datepicker._lastInput == input[0])
					$.datepicker._hideDatepicker();
				else if ($.datepicker._datepickerShowing && $.datepicker._lastInput != input[0]) {
					$.datepicker._hideDatepicker();
					$.datepicker._showDatepicker(input[0]);
				} else
					$.datepicker._showDatepicker(input[0]);
				return false;
			});
		}
	},

	/* Apply the maximum length for the date format. */
	_autoSize: function(inst) {
		if (this._get(inst, 'autoSize') && !inst.inline) {
			var date = new Date(2009, 12 - 1, 20); // Ensure double digits
			var dateFormat = this._get(inst, 'dateFormat');
			if (dateFormat.match(/[DM]/)) {
				var findMax = function(names) {
					var max = 0;
					var maxI = 0;
					for (var i = 0; i < names.length; i++) {
						if (names[i].length > max) {
							max = names[i].length;
							maxI = i;
						}
					}
					return maxI;
				};
				date.setMonth(findMax(this._get(inst, (dateFormat.match(/MM/) ?
					'monthNames' : 'monthNamesShort'))));
				date.setDate(findMax(this._get(inst, (dateFormat.match(/DD/) ?
					'dayNames' : 'dayNamesShort'))) + 20 - date.getDay());
			}
			inst.input.attr('size', this._formatDate(inst, date).length);
		}
	},

	/* Attach an inline date picker to a div. */
	_inlineDatepicker: function(target, inst) {
		var divSpan = $(target);
		if (divSpan.hasClass(this.markerClassName))
			return;
		divSpan.addClass(this.markerClassName).append(inst.dpDiv).
			bind("setData.datepicker", function(event, key, value){
				inst.settings[key] = value;
			}).bind("getData.datepicker", function(event, key){
				return this._get(inst, key);
			});
		$.data(target, PROP_NAME, inst);
		this._setDate(inst, this._getDefaultDate(inst), true);
		this._updateDatepicker(inst);
		this._updateAlternate(inst);
		//If disabled option is true, disable the datepicker before showing it (see ticket #5665)
		if( inst.settings.disabled ) {
			this._disableDatepicker( target );
		}
		// Set display:block in place of inst.dpDiv.show() which won't work on disconnected elements
		// http://bugs.jqueryui.com/ticket/7552 - A Datepicker created on a detached div has zero height
		inst.dpDiv.css( "display", "block" );
	},

	/* Pop-up the date picker in a "dialog" box.
	   @param  input     element - ignored
	   @param  date      string or Date - the initial date to display
	   @param  onSelect  function - the function to call when a date is selected
	   @param  settings  object - update the dialog date picker instance's settings (anonymous object)
	   @param  pos       int[2] - coordinates for the dialog's position within the screen or
	                     event - with x/y coordinates or
	                     leave empty for default (screen centre)
	   @return the manager object */
	_dialogDatepicker: function(input, date, onSelect, settings, pos) {
		var inst = this._dialogInst; // internal instance
		if (!inst) {
			this.uuid += 1;
			var id = 'dp' + this.uuid;
			this._dialogInput = $('<input type="text" id="' + id +
				'" style="position: absolute; top: -100px; width: 0px;"/>');
			this._dialogInput.keydown(this._doKeyDown);
			$('body').append(this._dialogInput);
			inst = this._dialogInst = this._newInst(this._dialogInput, false);
			inst.settings = {};
			$.data(this._dialogInput[0], PROP_NAME, inst);
		}
		extendRemove(inst.settings, settings || {});
		date = (date && date.constructor == Date ? this._formatDate(inst, date) : date);
		this._dialogInput.val(date);

		this._pos = (pos ? (pos.length ? pos : [pos.pageX, pos.pageY]) : null);
		if (!this._pos) {
			var browserWidth = document.documentElement.clientWidth;
			var browserHeight = document.documentElement.clientHeight;
			var scrollX = document.documentElement.scrollLeft || document.body.scrollLeft;
			var scrollY = document.documentElement.scrollTop || document.body.scrollTop;
			this._pos = // should use actual width/height below
				[(browserWidth / 2) - 100 + scrollX, (browserHeight / 2) - 150 + scrollY];
		}

		// move input on screen for focus, but hidden behind dialog
		this._dialogInput.css('left', (this._pos[0] + 20) + 'px').css('top', this._pos[1] + 'px');
		inst.settings.onSelect = onSelect;
		this._inDialog = true;
		this.dpDiv.addClass(this._dialogClass);
		this._showDatepicker(this._dialogInput[0]);
		if ($.blockUI)
			$.blockUI(this.dpDiv);
		$.data(this._dialogInput[0], PROP_NAME, inst);
		return this;
	},

	/* Detach a datepicker from its control.
	   @param  target    element - the target input field or division or span */
	_destroyDatepicker: function(target) {
		var $target = $(target);
		var inst = $.data(target, PROP_NAME);
		if (!$target.hasClass(this.markerClassName)) {
			return;
		}
		var nodeName = target.nodeName.toLowerCase();
		$.removeData(target, PROP_NAME);
		if (nodeName == 'input') {
			inst.append.remove();
			inst.trigger.remove();
			$target.removeClass(this.markerClassName).
				unbind('focus', this._showDatepicker).
				unbind('keydown', this._doKeyDown).
				unbind('keypress', this._doKeyPress).
				unbind('keyup', this._doKeyUp);
		} else if (nodeName == 'div' || nodeName == 'span')
			$target.removeClass(this.markerClassName).empty();
	},

	/* Enable the date picker to a jQuery selection.
	   @param  target    element - the target input field or division or span */
	_enableDatepicker: function(target) {
		var $target = $(target);
		var inst = $.data(target, PROP_NAME);
		if (!$target.hasClass(this.markerClassName)) {
			return;
		}
		var nodeName = target.nodeName.toLowerCase();
		if (nodeName == 'input') {
			target.disabled = false;
			inst.trigger.filter('button').
				each(function() { this.disabled = false; }).end().
				filter('img').css({opacity: '1.0', cursor: ''});
		}
		else if (nodeName == 'div' || nodeName == 'span') {
			var inline = $target.children('.' + this._inlineClass);
			inline.children().removeClass('ui-state-disabled');
			inline.find("select.ui-datepicker-month, select.ui-datepicker-year").
				prop("disabled", false);
		}
		this._disabledInputs = $.map(this._disabledInputs,
			function(value) { return (value == target ? null : value); }); // delete entry
	},

	/* Disable the date picker to a jQuery selection.
	   @param  target    element - the target input field or division or span */
	_disableDatepicker: function(target) {
		var $target = $(target);
		var inst = $.data(target, PROP_NAME);
		if (!$target.hasClass(this.markerClassName)) {
			return;
		}
		var nodeName = target.nodeName.toLowerCase();
		if (nodeName == 'input') {
			target.disabled = true;
			inst.trigger.filter('button').
				each(function() { this.disabled = true; }).end().
				filter('img').css({opacity: '0.5', cursor: 'default'});
		}
		else if (nodeName == 'div' || nodeName == 'span') {
			var inline = $target.children('.' + this._inlineClass);
			inline.children().addClass('ui-state-disabled');
			inline.find("select.ui-datepicker-month, select.ui-datepicker-year").
				prop("disabled", true);
		}
		this._disabledInputs = $.map(this._disabledInputs,
			function(value) { return (value == target ? null : value); }); // delete entry
		this._disabledInputs[this._disabledInputs.length] = target;
	},

	/* Is the first field in a jQuery collection disabled as a datepicker?
	   @param  target    element - the target input field or division or span
	   @return boolean - true if disabled, false if enabled */
	_isDisabledDatepicker: function(target) {
		if (!target) {
			return false;
		}
		for (var i = 0; i < this._disabledInputs.length; i++) {
			if (this._disabledInputs[i] == target)
				return true;
		}
		return false;
	},

	/* Retrieve the instance data for the target control.
	   @param  target  element - the target input field or division or span
	   @return  object - the associated instance data
	   @throws  error if a jQuery problem getting data */
	_getInst: function(target) {
		try {
			return $.data(target, PROP_NAME);
		}
		catch (err) {
			throw 'Missing instance data for this datepicker';
		}
	},

	/* Update or retrieve the settings for a date picker attached to an input field or division.
	   @param  target  element - the target input field or division or span
	   @param  name    object - the new settings to update or
	                   string - the name of the setting to change or retrieve,
	                   when retrieving also 'all' for all instance settings or
	                   'defaults' for all global defaults
	   @param  value   any - the new value for the setting
	                   (omit if above is an object or to retrieve a value) */
	_optionDatepicker: function(target, name, value) {
		var inst = this._getInst(target);
		if (arguments.length == 2 && typeof name == 'string') {
			return (name == 'defaults' ? $.extend({}, $.datepicker._defaults) :
				(inst ? (name == 'all' ? $.extend({}, inst.settings) :
				this._get(inst, name)) : null));
		}
		var settings = name || {};
		if (typeof name == 'string') {
			settings = {};
			settings[name] = value;
		}
		if (inst) {
			if (this._curInst == inst) {
				this._hideDatepicker();
			}
			var date = this._getDateDatepicker(target, true);
			var minDate = this._getMinMaxDate(inst, 'min');
			var maxDate = this._getMinMaxDate(inst, 'max');
			extendRemove(inst.settings, settings);
			// reformat the old minDate/maxDate values if dateFormat changes and a new minDate/maxDate isn't provided
			if (minDate !== null && settings['dateFormat'] !== undefined && settings['minDate'] === undefined)
				inst.settings.minDate = this._formatDate(inst, minDate);
			if (maxDate !== null && settings['dateFormat'] !== undefined && settings['maxDate'] === undefined)
				inst.settings.maxDate = this._formatDate(inst, maxDate);
			this._attachments($(target), inst);
			this._autoSize(inst);
			this._setDate(inst, date);
			this._updateAlternate(inst);
			this._updateDatepicker(inst);
		}
	},

	// change method deprecated
	_changeDatepicker: function(target, name, value) {
		this._optionDatepicker(target, name, value);
	},

	/* Redraw the date picker attached to an input field or division.
	   @param  target  element - the target input field or division or span */
	_refreshDatepicker: function(target) {
		var inst = this._getInst(target);
		if (inst) {
			this._updateDatepicker(inst);
		}
	},

	/* Set the dates for a jQuery selection.
	   @param  target   element - the target input field or division or span
	   @param  date     Date - the new date */
	_setDateDatepicker: function(target, date) {
		var inst = this._getInst(target);
		if (inst) {
			this._setDate(inst, date);
			this._updateDatepicker(inst);
			this._updateAlternate(inst);
		}
	},

	/* Get the date(s) for the first entry in a jQuery selection.
	   @param  target     element - the target input field or division or span
	   @param  noDefault  boolean - true if no default date is to be used
	   @return Date - the current date */
	_getDateDatepicker: function(target, noDefault) {
		var inst = this._getInst(target);
		if (inst && !inst.inline)
			this._setDateFromField(inst, noDefault);
		return (inst ? this._getDate(inst) : null);
	},

	/* Handle keystrokes. */
	_doKeyDown: function(event) {
		var inst = $.datepicker._getInst(event.target);
		var handled = true;
		var isRTL = inst.dpDiv.is('.ui-datepicker-rtl');
		inst._keyEvent = true;
		if ($.datepicker._datepickerShowing)
			switch (event.keyCode) {
				case 9: $.datepicker._hideDatepicker();
						handled = false;
						break; // hide on tab out
				case 13: var sel = $('td.' + $.datepicker._dayOverClass + ':not(.' +
									$.datepicker._currentClass + ')', inst.dpDiv);
						if (sel[0])
							$.datepicker._selectDay(event.target, inst.selectedMonth, inst.selectedYear, sel[0]);
							var onSelect = $.datepicker._get(inst, 'onSelect');
							if (onSelect) {
								var dateStr = $.datepicker._formatDate(inst);

								// trigger custom callback
								onSelect.apply((inst.input ? inst.input[0] : null), [dateStr, inst]);
							}
						else
							$.datepicker._hideDatepicker();
						return false; // don't submit the form
						break; // select the value on enter
				case 27: $.datepicker._hideDatepicker();
						break; // hide on escape
				case 33: $.datepicker._adjustDate(event.target, (event.ctrlKey ?
							-$.datepicker._get(inst, 'stepBigMonths') :
							-$.datepicker._get(inst, 'stepMonths')), 'M');
						break; // previous month/year on page up/+ ctrl
				case 34: $.datepicker._adjustDate(event.target, (event.ctrlKey ?
							+$.datepicker._get(inst, 'stepBigMonths') :
							+$.datepicker._get(inst, 'stepMonths')), 'M');
						break; // next month/year on page down/+ ctrl
				case 35: if (event.ctrlKey || event.metaKey) $.datepicker._clearDate(event.target);
						handled = event.ctrlKey || event.metaKey;
						break; // clear on ctrl or command +end
				case 36: if (event.ctrlKey || event.metaKey) $.datepicker._gotoToday(event.target);
						handled = event.ctrlKey || event.metaKey;
						break; // current on ctrl or command +home
				case 37: if (event.ctrlKey || event.metaKey) $.datepicker._adjustDate(event.target, (isRTL ? +1 : -1), 'D');
						handled = event.ctrlKey || event.metaKey;
						// -1 day on ctrl or command +left
						if (event.originalEvent.altKey) $.datepicker._adjustDate(event.target, (event.ctrlKey ?
									-$.datepicker._get(inst, 'stepBigMonths') :
									-$.datepicker._get(inst, 'stepMonths')), 'M');
						// next month/year on alt +left on Mac
						break;
				case 38: if (event.ctrlKey || event.metaKey) $.datepicker._adjustDate(event.target, -7, 'D');
						handled = event.ctrlKey || event.metaKey;
						break; // -1 week on ctrl or command +up
				case 39: if (event.ctrlKey || event.metaKey) $.datepicker._adjustDate(event.target, (isRTL ? -1 : +1), 'D');
						handled = event.ctrlKey || event.metaKey;
						// +1 day on ctrl or command +right
						if (event.originalEvent.altKey) $.datepicker._adjustDate(event.target, (event.ctrlKey ?
									+$.datepicker._get(inst, 'stepBigMonths') :
									+$.datepicker._get(inst, 'stepMonths')), 'M');
						// next month/year on alt +right
						break;
				case 40: if (event.ctrlKey || event.metaKey) $.datepicker._adjustDate(event.target, +7, 'D');
						handled = event.ctrlKey || event.metaKey;
						break; // +1 week on ctrl or command +down
				default: handled = false;
			}
		else if (event.keyCode == 36 && event.ctrlKey) // display the date picker on ctrl+home
			$.datepicker._showDatepicker(this);
		else {
			handled = false;
		}
		if (handled) {
			event.preventDefault();
			event.stopPropagation();
		}
	},

	/* Filter entered characters - based on date format. */
	_doKeyPress: function(event) {
		var inst = $.datepicker._getInst(event.target);
		if ($.datepicker._get(inst, 'constrainInput')) {
			var chars = $.datepicker._possibleChars($.datepicker._get(inst, 'dateFormat'));
			var chr = String.fromCharCode(event.charCode == undefined ? event.keyCode : event.charCode);
			return event.ctrlKey || event.metaKey || (chr < ' ' || !chars || chars.indexOf(chr) > -1);
		}
	},

	/* Synchronise manual entry and field/alternate field. */
	_doKeyUp: function(event) {
		var inst = $.datepicker._getInst(event.target);
		if (inst.input.val() != inst.lastVal) {
			try {
				var date = $.datepicker.parseDate($.datepicker._get(inst, 'dateFormat'),
					(inst.input ? inst.input.val() : null),
					$.datepicker._getFormatConfig(inst));
				if (date) { // only if valid
					$.datepicker._setDateFromField(inst);
					$.datepicker._updateAlternate(inst);
					$.datepicker._updateDatepicker(inst);
				}
			}
			catch (err) {
				$.datepicker.log(err);
			}
		}
		return true;
	},

	/* Pop-up the date picker for a given input field.
	   If false returned from beforeShow event handler do not show.
	   @param  input  element - the input field attached to the date picker or
	                  event - if triggered by focus */
	_showDatepicker: function(input) {
		input = input.target || input;
		if (input.nodeName.toLowerCase() != 'input') // find from button/image trigger
			input = $('input', input.parentNode)[0];
		if ($.datepicker._isDisabledDatepicker(input) || $.datepicker._lastInput == input) // already here
			return;
		var inst = $.datepicker._getInst(input);
		if ($.datepicker._curInst && $.datepicker._curInst != inst) {
			$.datepicker._curInst.dpDiv.stop(true, true);
			if ( inst && $.datepicker._datepickerShowing ) {
				$.datepicker._hideDatepicker( $.datepicker._curInst.input[0] );
			}
		}
		var beforeShow = $.datepicker._get(inst, 'beforeShow');
		var beforeShowSettings = beforeShow ? beforeShow.apply(input, [input, inst]) : {};
		if(beforeShowSettings === false){
			//false
			return;
		}
		extendRemove(inst.settings, beforeShowSettings);
		inst.lastVal = null;
		$.datepicker._lastInput = input;
		$.datepicker._setDateFromField(inst);
		if ($.datepicker._inDialog) // hide cursor
			input.value = '';
		if (!$.datepicker._pos) { // position below input
			$.datepicker._pos = $.datepicker._findPos(input);
			$.datepicker._pos[1] += input.offsetHeight; // add the height
		}
		var isFixed = false;
		$(input).parents().each(function() {
			isFixed |= $(this).css('position') == 'fixed';
			return !isFixed;
		});
		var offset = {left: $.datepicker._pos[0], top: $.datepicker._pos[1]};
		$.datepicker._pos = null;
		//to avoid flashes on Firefox
		inst.dpDiv.empty();
		// determine sizing offscreen
		inst.dpDiv.css({position: 'absolute', display: 'block', top: '-1000px'});
		$.datepicker._updateDatepicker(inst);
		// fix width for dynamic number of date pickers
		// and adjust position before showing
		offset = $.datepicker._checkOffset(inst, offset, isFixed);
		inst.dpDiv.css({position: ($.datepicker._inDialog && $.blockUI ?
			'static' : (isFixed ? 'fixed' : 'absolute')), display: 'none',
			left: offset.left + 'px', top: offset.top + 'px'});
		if (!inst.inline) {
			var showAnim = $.datepicker._get(inst, 'showAnim');
			var duration = $.datepicker._get(inst, 'duration');
			var postProcess = function() {
				var cover = inst.dpDiv.find('iframe.ui-datepicker-cover'); // IE6- only
				if( !! cover.length ){
					var borders = $.datepicker._getBorders(inst.dpDiv);
					cover.css({left: -borders[0], top: -borders[1],
						width: inst.dpDiv.outerWidth(), height: inst.dpDiv.outerHeight()});
				}
			};
			inst.dpDiv.zIndex($(input).zIndex()+1);
			$.datepicker._datepickerShowing = true;

			// DEPRECATED: after BC for 1.8.x $.effects[ showAnim ] is not needed
			if ( $.effects && ( $.effects.effect[ showAnim ] || $.effects[ showAnim ] ) )
				inst.dpDiv.show(showAnim, $.datepicker._get(inst, 'showOptions'), duration, postProcess);
			else
				inst.dpDiv[showAnim || 'show']((showAnim ? duration : null), postProcess);
			if (!showAnim || !duration)
				postProcess();
			if (inst.input.is(':visible') && !inst.input.is(':disabled'))
				inst.input.focus();
			$.datepicker._curInst = inst;
		}
	},

	/* Generate the date picker content. */
	_updateDatepicker: function(inst) {
		this.maxRows = 4; //Reset the max number of rows being displayed (see #7043)
		var borders = $.datepicker._getBorders(inst.dpDiv);
		instActive = inst; // for delegate hover events
		inst.dpDiv.empty().append(this._generateHTML(inst));
		this._attachHandlers(inst);
		var cover = inst.dpDiv.find('iframe.ui-datepicker-cover'); // IE6- only
		if( !!cover.length ){ //avoid call to outerXXXX() when not in IE6
			cover.css({left: -borders[0], top: -borders[1], width: inst.dpDiv.outerWidth(), height: inst.dpDiv.outerHeight()})
		}
		inst.dpDiv.find('.' + this._dayOverClass + ' a').mouseover();
		var numMonths = this._getNumberOfMonths(inst);
		var cols = numMonths[1];
		var width = 17;
		inst.dpDiv.removeClass('ui-datepicker-multi-2 ui-datepicker-multi-3 ui-datepicker-multi-4').width('');
		if (cols > 1)
			inst.dpDiv.addClass('ui-datepicker-multi-' + cols).css('width', (width * cols) + 'em');
		inst.dpDiv[(numMonths[0] != 1 || numMonths[1] != 1 ? 'add' : 'remove') +
			'Class']('ui-datepicker-multi');
		inst.dpDiv[(this._get(inst, 'isRTL') ? 'add' : 'remove') +
			'Class']('ui-datepicker-rtl');
		if (inst == $.datepicker._curInst && $.datepicker._datepickerShowing && inst.input &&
				// #6694 - don't focus the input if it's already focused
				// this breaks the change event in IE
				inst.input.is(':visible') && !inst.input.is(':disabled') && inst.input[0] != document.activeElement)
			inst.input.focus();
		// deffered render of the years select (to avoid flashes on Firefox)
		if( inst.yearshtml ){
			var origyearshtml = inst.yearshtml;
			setTimeout(function(){
				//assure that inst.yearshtml didn't change.
				if( origyearshtml === inst.yearshtml && inst.yearshtml ){
					inst.dpDiv.find('select.ui-datepicker-year:first').replaceWith(inst.yearshtml);
				}
				origyearshtml = inst.yearshtml = null;
			}, 0);
		}
	},

	/* Retrieve the size of left and top borders for an element.
	   @param  elem  (jQuery object) the element of interest
	   @return  (number[2]) the left and top borders */
	_getBorders: function(elem) {
		var convert = function(value) {
			return {thin: 1, medium: 2, thick: 3}[value] || value;
		};
		return [parseFloat(convert(elem.css('border-left-width'))),
			parseFloat(convert(elem.css('border-top-width')))];
	},

	/* Check positioning to remain on screen. */
	_checkOffset: function(inst, offset, isFixed) {
		var dpWidth = inst.dpDiv.outerWidth();
		var dpHeight = inst.dpDiv.outerHeight();
		var inputWidth = inst.input ? inst.input.outerWidth() : 0;
		var inputHeight = inst.input ? inst.input.outerHeight() : 0;
		var viewWidth = document.documentElement.clientWidth + (isFixed ? 0 : $(document).scrollLeft());
		var viewHeight = document.documentElement.clientHeight + (isFixed ? 0 : $(document).scrollTop());

		offset.left -= (this._get(inst, 'isRTL') ? (dpWidth - inputWidth) : 0);
		offset.left -= (isFixed && offset.left == inst.input.offset().left) ? $(document).scrollLeft() : 0;
		offset.top -= (isFixed && offset.top == (inst.input.offset().top + inputHeight)) ? $(document).scrollTop() : 0;

		// now check if datepicker is showing outside window viewport - move to a better place if so.
		offset.left -= Math.min(offset.left, (offset.left + dpWidth > viewWidth && viewWidth > dpWidth) ?
			Math.abs(offset.left + dpWidth - viewWidth) : 0);
		offset.top -= Math.min(offset.top, (offset.top + dpHeight > viewHeight && viewHeight > dpHeight) ?
			Math.abs(dpHeight + inputHeight) : 0);

		return offset;
	},

	/* Find an object's position on the screen. */
	_findPos: function(obj) {
		var inst = this._getInst(obj);
		var isRTL = this._get(inst, 'isRTL');
		while (obj && (obj.type == 'hidden' || obj.nodeType != 1 || $.expr.filters.hidden(obj))) {
			obj = obj[isRTL ? 'previousSibling' : 'nextSibling'];
		}
		var position = $(obj).offset();
		return [position.left, position.top];
	},

	/* Hide the date picker from view.
	   @param  input  element - the input field attached to the date picker */
	_hideDatepicker: function(input) {
		var inst = this._curInst;
		if (!inst || (input && inst != $.data(input, PROP_NAME)))
			return;
		if (this._datepickerShowing) {
			var showAnim = this._get(inst, 'showAnim');
			var duration = this._get(inst, 'duration');
			var postProcess = function() {
				$.datepicker._tidyDialog(inst);
			};

			// DEPRECATED: after BC for 1.8.x $.effects[ showAnim ] is not needed
			if ( $.effects && ( $.effects.effect[ showAnim ] || $.effects[ showAnim ] ) )
				inst.dpDiv.hide(showAnim, $.datepicker._get(inst, 'showOptions'), duration, postProcess);
			else
				inst.dpDiv[(showAnim == 'slideDown' ? 'slideUp' :
					(showAnim == 'fadeIn' ? 'fadeOut' : 'hide'))]((showAnim ? duration : null), postProcess);
			if (!showAnim)
				postProcess();
			this._datepickerShowing = false;
			var onClose = this._get(inst, 'onClose');
			if (onClose)
				onClose.apply((inst.input ? inst.input[0] : null),
					[(inst.input ? inst.input.val() : ''), inst]);
			this._lastInput = null;
			if (this._inDialog) {
				this._dialogInput.css({ position: 'absolute', left: '0', top: '-100px' });
				if ($.blockUI) {
					$.unblockUI();
					$('body').append(this.dpDiv);
				}
			}
			this._inDialog = false;
		}
	},

	/* Tidy up after a dialog display. */
	_tidyDialog: function(inst) {
		inst.dpDiv.removeClass(this._dialogClass).unbind('.ui-datepicker-calendar');
	},

	/* Close date picker if clicked elsewhere. */
	_checkExternalClick: function(event) {
		if (!$.datepicker._curInst)
			return;

		var $target = $(event.target),
			inst = $.datepicker._getInst($target[0]);

		if ( ( ( $target[0].id != $.datepicker._mainDivId &&
				$target.parents('#' + $.datepicker._mainDivId).length == 0 &&
				!$target.hasClass($.datepicker.markerClassName) &&
				!$target.closest("." + $.datepicker._triggerClass).length &&
				$.datepicker._datepickerShowing && !($.datepicker._inDialog && $.blockUI) ) ) ||
			( $target.hasClass($.datepicker.markerClassName) && $.datepicker._curInst != inst ) )
			$.datepicker._hideDatepicker();
	},

	/* Adjust one of the date sub-fields. */
	_adjustDate: function(id, offset, period) {
		var target = $(id);
		var inst = this._getInst(target[0]);
		if (this._isDisabledDatepicker(target[0])) {
			return;
		}
		this._adjustInstDate(inst, offset +
			(period == 'M' ? this._get(inst, 'showCurrentAtPos') : 0), // undo positioning
			period);
		this._updateDatepicker(inst);
	},

	/* Action for current link. */
	_gotoToday: function(id) {
		var target = $(id);
		var inst = this._getInst(target[0]);
		if (this._get(inst, 'gotoCurrent') && inst.currentDay) {
			inst.selectedDay = inst.currentDay;
			inst.drawMonth = inst.selectedMonth = inst.currentMonth;
			inst.drawYear = inst.selectedYear = inst.currentYear;
		}
		else {
			var date = new Date();
			inst.selectedDay = date.getDate();
			inst.drawMonth = inst.selectedMonth = date.getMonth();
			inst.drawYear = inst.selectedYear = date.getFullYear();
		}
		this._notifyChange(inst);
		this._adjustDate(target);
	},

	/* Action for selecting a new month/year. */
	_selectMonthYear: function(id, select, period) {
		var target = $(id);
		var inst = this._getInst(target[0]);
		inst['selected' + (period == 'M' ? 'Month' : 'Year')] =
		inst['draw' + (period == 'M' ? 'Month' : 'Year')] =
			parseInt(select.options[select.selectedIndex].value,10);
		this._notifyChange(inst);
		this._adjustDate(target);
	},

	/* Action for selecting a day. */
	_selectDay: function(id, month, year, td) {
		var target = $(id);
		if ($(td).hasClass(this._unselectableClass) || this._isDisabledDatepicker(target[0])) {
			return;
		}
		var inst = this._getInst(target[0]);
		inst.selectedDay = inst.currentDay = $('a', td).html();
		inst.selectedMonth = inst.currentMonth = month;
		inst.selectedYear = inst.currentYear = year;
		this._selectDate(id, this._formatDate(inst,
			inst.currentDay, inst.currentMonth, inst.currentYear));
	},

	/* Erase the input field and hide the date picker. */
	_clearDate: function(id) {
		var target = $(id);
		var inst = this._getInst(target[0]);
		this._selectDate(target, '');
	},

	/* Update the input field with the selected date. */
	_selectDate: function(id, dateStr) {
		var target = $(id);
		var inst = this._getInst(target[0]);
		dateStr = (dateStr != null ? dateStr : this._formatDate(inst));
		if (inst.input)
			inst.input.val(dateStr);
		this._updateAlternate(inst);
		var onSelect = this._get(inst, 'onSelect');
		if (onSelect)
			onSelect.apply((inst.input ? inst.input[0] : null), [dateStr, inst]);  // trigger custom callback
		else if (inst.input)
			inst.input.trigger('change'); // fire the change event
		if (inst.inline)
			this._updateDatepicker(inst);
		else {
			this._hideDatepicker();
			this._lastInput = inst.input[0];
			if (typeof(inst.input[0]) != 'object')
				inst.input.focus(); // restore focus
			this._lastInput = null;
		}
	},

	/* Update any alternate field to synchronise with the main field. */
	_updateAlternate: function(inst) {
		var altField = this._get(inst, 'altField');
		if (altField) { // update alternate field too
			var altFormat = this._get(inst, 'altFormat') || this._get(inst, 'dateFormat');
			var date = this._getDate(inst);
			var dateStr = this.formatDate(altFormat, date, this._getFormatConfig(inst));
			$(altField).each(function() { $(this).val(dateStr); });
		}
	},

	/* Set as beforeShowDay function to prevent selection of weekends.
	   @param  date  Date - the date to customise
	   @return [boolean, string] - is this date selectable?, what is its CSS class? */
	noWeekends: function(date) {
		var day = date.getDay();
		return [(day > 0 && day < 6), ''];
	},

	/* Set as calculateWeek to determine the week of the year based on the ISO 8601 definition.
	   @param  date  Date - the date to get the week for
	   @return  number - the number of the week within the year that contains this date */
	iso8601Week: function(date) {
		var checkDate = new Date(date.getTime());
		// Find Thursday of this week starting on Monday
		checkDate.setDate(checkDate.getDate() + 4 - (checkDate.getDay() || 7));
		var time = checkDate.getTime();
		checkDate.setMonth(0); // Compare with Jan 1
		checkDate.setDate(1);
		return Math.floor(Math.round((time - checkDate) / 86400000) / 7) + 1;
	},

	/* Parse a string value into a date object.
	   See formatDate below for the possible formats.

	   @param  format    string - the expected format of the date
	   @param  value     string - the date in the above format
	   @param  settings  Object - attributes include:
	                     shortYearCutoff  number - the cutoff year for determining the century (optional)
	                     dayNamesShort    string[7] - abbreviated names of the days from Sunday (optional)
	                     dayNames         string[7] - names of the days from Sunday (optional)
	                     monthNamesShort  string[12] - abbreviated names of the months (optional)
	                     monthNames       string[12] - names of the months (optional)
	   @return  Date - the extracted date value or null if value is blank */
	parseDate: function (format, value, settings) {
		if (format == null || value == null)
			throw 'Invalid arguments';
		value = (typeof value == 'object' ? value.toString() : value + '');
		if (value == '')
			return null;
		var shortYearCutoff = (settings ? settings.shortYearCutoff : null) || this._defaults.shortYearCutoff;
		shortYearCutoff = (typeof shortYearCutoff != 'string' ? shortYearCutoff :
				new Date().getFullYear() % 100 + parseInt(shortYearCutoff, 10));
		var dayNamesShort = (settings ? settings.dayNamesShort : null) || this._defaults.dayNamesShort;
		var dayNames = (settings ? settings.dayNames : null) || this._defaults.dayNames;
		var monthNamesShort = (settings ? settings.monthNamesShort : null) || this._defaults.monthNamesShort;
		var monthNames = (settings ? settings.monthNames : null) || this._defaults.monthNames;
		var year = -1;
		var month = -1;
		var day = -1;
		var doy = -1;
		var literal = false;
		// Check whether a format character is doubled
		var lookAhead = function(match) {
			var matches = (iFormat + 1 < format.length && format.charAt(iFormat + 1) == match);
			if (matches)
				iFormat++;
			return matches;
		};
		// Extract a number from the string value
		var getNumber = function(match) {
			var isDoubled = lookAhead(match);
			var size = (match == '@' ? 14 : (match == '!' ? 20 :
				(match == 'y' && isDoubled ? 4 : (match == 'o' ? 3 : 2))));
			var digits = new RegExp('^\\d{1,' + size + '}');
			var num = value.substring(iValue).match(digits);
			if (!num)
				throw 'Missing number at position ' + iValue;
			iValue += num[0].length;
			return parseInt(num[0], 10);
		};
		// Extract a name from the string value and convert to an index
		var getName = function(match, shortNames, longNames) {
			var names = $.map(lookAhead(match) ? longNames : shortNames, function (v, k) {
				return [ [k, v] ];
			}).sort(function (a, b) {
				return -(a[1].length - b[1].length);
			});
			var index = -1;
			$.each(names, function (i, pair) {
				var name = pair[1];
				if (value.substr(iValue, name.length).toLowerCase() == name.toLowerCase()) {
					index = pair[0];
					iValue += name.length;
					return false;
				}
			});
			if (index != -1)
				return index + 1;
			else
				throw 'Unknown name at position ' + iValue;
		};
		// Confirm that a literal character matches the string value
		var checkLiteral = function() {
			if (value.charAt(iValue) != format.charAt(iFormat))
				throw 'Unexpected literal at position ' + iValue;
			iValue++;
		};
		var iValue = 0;
		for (var iFormat = 0; iFormat < format.length; iFormat++) {
			if (literal)
				if (format.charAt(iFormat) == "'" && !lookAhead("'"))
					literal = false;
				else
					checkLiteral();
			else
				switch (format.charAt(iFormat)) {
					case 'd':
						day = getNumber('d');
						break;
					case 'D':
						getName('D', dayNamesShort, dayNames);
						break;
					case 'o':
						doy = getNumber('o');
						break;
					case 'm':
						month = getNumber('m');
						break;
					case 'M':
						month = getName('M', monthNamesShort, monthNames);
						break;
					case 'y':
						year = getNumber('y');
						break;
					case '@':
						var date = new Date(getNumber('@'));
						year = date.getFullYear();
						month = date.getMonth() + 1;
						day = date.getDate();
						break;
					case '!':
						var date = new Date((getNumber('!') - this._ticksTo1970) / 10000);
						year = date.getFullYear();
						month = date.getMonth() + 1;
						day = date.getDate();
						break;
					case "'":
						if (lookAhead("'"))
							checkLiteral();
						else
							literal = true;
						break;
					default:
						checkLiteral();
				}
		}
		if (iValue < value.length){
			var extra = value.substr(iValue);
			if (!/^\s+/.test(extra)) {
				throw "Extra/unparsed characters found in date: " + extra;
			}
		}
		if (year == -1)
			year = new Date().getFullYear();
		else if (year < 100)
			year += new Date().getFullYear() - new Date().getFullYear() % 100 +
				(year <= shortYearCutoff ? 0 : -100);
		if (doy > -1) {
			month = 1;
			day = doy;
			do {
				var dim = this._getDaysInMonth(year, month - 1);
				if (day <= dim)
					break;
				month++;
				day -= dim;
			} while (true);
		}
		var date = this._daylightSavingAdjust(new Date(year, month - 1, day));
		if (date.getFullYear() != year || date.getMonth() + 1 != month || date.getDate() != day)
			throw 'Invalid date'; // E.g. 31/02/00
		return date;
	},

	/* Standard date formats. */
	ATOM: 'yy-mm-dd', // RFC 3339 (ISO 8601)
	COOKIE: 'D, dd M yy',
	ISO_8601: 'yy-mm-dd',
	RFC_822: 'D, d M y',
	RFC_850: 'DD, dd-M-y',
	RFC_1036: 'D, d M y',
	RFC_1123: 'D, d M yy',
	RFC_2822: 'D, d M yy',
	RSS: 'D, d M y', // RFC 822
	TICKS: '!',
	TIMESTAMP: '@',
	W3C: 'yy-mm-dd', // ISO 8601

	_ticksTo1970: (((1970 - 1) * 365 + Math.floor(1970 / 4) - Math.floor(1970 / 100) +
		Math.floor(1970 / 400)) * 24 * 60 * 60 * 10000000),

	/* Format a date object into a string value.
	   The format can be combinations of the following:
	   d  - day of month (no leading zero)
	   dd - day of month (two digit)
	   o  - day of year (no leading zeros)
	   oo - day of year (three digit)
	   D  - day name short
	   DD - day name long
	   m  - month of year (no leading zero)
	   mm - month of year (two digit)
	   M  - month name short
	   MM - month name long
	   y  - year (two digit)
	   yy - year (four digit)
	   @ - Unix timestamp (ms since 01/01/1970)
	   ! - Windows ticks (100ns since 01/01/0001)
	   '...' - literal text
	   '' - single quote

	   @param  format    string - the desired format of the date
	   @param  date      Date - the date value to format
	   @param  settings  Object - attributes include:
	                     dayNamesShort    string[7] - abbreviated names of the days from Sunday (optional)
	                     dayNames         string[7] - names of the days from Sunday (optional)
	                     monthNamesShort  string[12] - abbreviated names of the months (optional)
	                     monthNames       string[12] - names of the months (optional)
	   @return  string - the date in the above format */
	formatDate: function (format, date, settings) {
		if (!date)
			return '';
		var dayNamesShort = (settings ? settings.dayNamesShort : null) || this._defaults.dayNamesShort;
		var dayNames = (settings ? settings.dayNames : null) || this._defaults.dayNames;
		var monthNamesShort = (settings ? settings.monthNamesShort : null) || this._defaults.monthNamesShort;
		var monthNames = (settings ? settings.monthNames : null) || this._defaults.monthNames;
		// Check whether a format character is doubled
		var lookAhead = function(match) {
			var matches = (iFormat + 1 < format.length && format.charAt(iFormat + 1) == match);
			if (matches)
				iFormat++;
			return matches;
		};
		// Format a number, with leading zero if necessary
		var formatNumber = function(match, value, len) {
			var num = '' + value;
			if (lookAhead(match))
				while (num.length < len)
					num = '0' + num;
			return num;
		};
		// Format a name, short or long as requested
		var formatName = function(match, value, shortNames, longNames) {
			return (lookAhead(match) ? longNames[value] : shortNames[value]);
		};
		var output = '';
		var literal = false;
		if (date)
			for (var iFormat = 0; iFormat < format.length; iFormat++) {
				if (literal)
					if (format.charAt(iFormat) == "'" && !lookAhead("'"))
						literal = false;
					else
						output += format.charAt(iFormat);
				else
					switch (format.charAt(iFormat)) {
						case 'd':
							output += formatNumber('d', date.getDate(), 2);
							break;
						case 'D':
							output += formatName('D', date.getDay(), dayNamesShort, dayNames);
							break;
						case 'o':
							output += formatNumber('o',
								Math.round((new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() - new Date(date.getFullYear(), 0, 0).getTime()) / 86400000), 3);
							break;
						case 'm':
							output += formatNumber('m', date.getMonth() + 1, 2);
							break;
						case 'M':
							output += formatName('M', date.getMonth(), monthNamesShort, monthNames);
							break;
						case 'y':
							output += (lookAhead('y') ? date.getFullYear() :
								(date.getYear() % 100 < 10 ? '0' : '') + date.getYear() % 100);
							break;
						case '@':
							output += date.getTime();
							break;
						case '!':
							output += date.getTime() * 10000 + this._ticksTo1970;
							break;
						case "'":
							if (lookAhead("'"))
								output += "'";
							else
								literal = true;
							break;
						default:
							output += format.charAt(iFormat);
					}
			}
		return output;
	},

	/* Extract all possible characters from the date format. */
	_possibleChars: function (format) {
		var chars = '';
		var literal = false;
		// Check whether a format character is doubled
		var lookAhead = function(match) {
			var matches = (iFormat + 1 < format.length && format.charAt(iFormat + 1) == match);
			if (matches)
				iFormat++;
			return matches;
		};
		for (var iFormat = 0; iFormat < format.length; iFormat++)
			if (literal)
				if (format.charAt(iFormat) == "'" && !lookAhead("'"))
					literal = false;
				else
					chars += format.charAt(iFormat);
			else
				switch (format.charAt(iFormat)) {
					case 'd': case 'm': case 'y': case '@':
						chars += '0123456789';
						break;
					case 'D': case 'M':
						return null; // Accept anything
					case "'":
						if (lookAhead("'"))
							chars += "'";
						else
							literal = true;
						break;
					default:
						chars += format.charAt(iFormat);
				}
		return chars;
	},

	/* Get a setting value, defaulting if necessary. */
	_get: function(inst, name) {
		return inst.settings[name] !== undefined ?
			inst.settings[name] : this._defaults[name];
	},

	/* Parse existing date and initialise date picker. */
	_setDateFromField: function(inst, noDefault) {
		if (inst.input.val() == inst.lastVal) {
			return;
		}
		var dateFormat = this._get(inst, 'dateFormat');
		var dates = inst.lastVal = inst.input ? inst.input.val() : null;
		var date, defaultDate;
		date = defaultDate = this._getDefaultDate(inst);
		var settings = this._getFormatConfig(inst);
		try {
			date = this.parseDate(dateFormat, dates, settings) || defaultDate;
		} catch (event) {
			this.log(event);
			dates = (noDefault ? '' : dates);
		}
		inst.selectedDay = date.getDate();
		inst.drawMonth = inst.selectedMonth = date.getMonth();
		inst.drawYear = inst.selectedYear = date.getFullYear();
		inst.currentDay = (dates ? date.getDate() : 0);
		inst.currentMonth = (dates ? date.getMonth() : 0);
		inst.currentYear = (dates ? date.getFullYear() : 0);
		this._adjustInstDate(inst);
	},

	/* Retrieve the default date shown on opening. */
	_getDefaultDate: function(inst) {
		return this._restrictMinMax(inst,
			this._determineDate(inst, this._get(inst, 'defaultDate'), new Date()));
	},

	/* A date may be specified as an exact value or a relative one. */
	_determineDate: function(inst, date, defaultDate) {
		var offsetNumeric = function(offset) {
			var date = new Date();
			date.setDate(date.getDate() + offset);
			return date;
		};
		var offsetString = function(offset) {
			try {
				return $.datepicker.parseDate($.datepicker._get(inst, 'dateFormat'),
					offset, $.datepicker._getFormatConfig(inst));
			}
			catch (e) {
				// Ignore
			}
			var date = (offset.toLowerCase().match(/^c/) ?
				$.datepicker._getDate(inst) : null) || new Date();
			var year = date.getFullYear();
			var month = date.getMonth();
			var day = date.getDate();
			var pattern = /([+-]?[0-9]+)\s*(d|D|w|W|m|M|y|Y)?/g;
			var matches = pattern.exec(offset);
			while (matches) {
				switch (matches[2] || 'd') {
					case 'd' : case 'D' :
						day += parseInt(matches[1],10); break;
					case 'w' : case 'W' :
						day += parseInt(matches[1],10) * 7; break;
					case 'm' : case 'M' :
						month += parseInt(matches[1],10);
						day = Math.min(day, $.datepicker._getDaysInMonth(year, month));
						break;
					case 'y': case 'Y' :
						year += parseInt(matches[1],10);
						day = Math.min(day, $.datepicker._getDaysInMonth(year, month));
						break;
				}
				matches = pattern.exec(offset);
			}
			return new Date(year, month, day);
		};
		var newDate = (date == null || date === '' ? defaultDate : (typeof date == 'string' ? offsetString(date) :
			(typeof date == 'number' ? (isNaN(date) ? defaultDate : offsetNumeric(date)) : new Date(date.getTime()))));
		newDate = (newDate && newDate.toString() == 'Invalid Date' ? defaultDate : newDate);
		if (newDate) {
			newDate.setHours(0);
			newDate.setMinutes(0);
			newDate.setSeconds(0);
			newDate.setMilliseconds(0);
		}
		return this._daylightSavingAdjust(newDate);
	},

	/* Handle switch to/from daylight saving.
	   Hours may be non-zero on daylight saving cut-over:
	   > 12 when midnight changeover, but then cannot generate
	   midnight datetime, so jump to 1AM, otherwise reset.
	   @param  date  (Date) the date to check
	   @return  (Date) the corrected date */
	_daylightSavingAdjust: function(date) {
		if (!date) return null;
		date.setHours(date.getHours() > 12 ? date.getHours() + 2 : 0);
		return date;
	},

	/* Set the date(s) directly. */
	_setDate: function(inst, date, noChange) {
		var clear = !date;
		var origMonth = inst.selectedMonth;
		var origYear = inst.selectedYear;
		var newDate = this._restrictMinMax(inst, this._determineDate(inst, date, new Date()));
		inst.selectedDay = inst.currentDay = newDate.getDate();
		inst.drawMonth = inst.selectedMonth = inst.currentMonth = newDate.getMonth();
		inst.drawYear = inst.selectedYear = inst.currentYear = newDate.getFullYear();
		if ((origMonth != inst.selectedMonth || origYear != inst.selectedYear) && !noChange)
			this._notifyChange(inst);
		this._adjustInstDate(inst);
		if (inst.input) {
			inst.input.val(clear ? '' : this._formatDate(inst));
		}
	},

	/* Retrieve the date(s) directly. */
	_getDate: function(inst) {
		var startDate = (!inst.currentYear || (inst.input && inst.input.val() == '') ? null :
			this._daylightSavingAdjust(new Date(
			inst.currentYear, inst.currentMonth, inst.currentDay)));
			return startDate;
	},

	/* Attach the onxxx handlers.  These are declared statically so
	 * they work with static code transformers like Caja.
	 */
	_attachHandlers: function(inst) {
		var stepMonths = this._get(inst, 'stepMonths');
		var id = '#' + inst.id.replace( /\\\\/g, "\\" );
		inst.dpDiv.find('[data-handler]').map(function () {
			var handler = {
				prev: function () {
					window['DP_jQuery_' + dpuuid].datepicker._adjustDate(id, -stepMonths, 'M');
				},
				next: function () {
					window['DP_jQuery_' + dpuuid].datepicker._adjustDate(id, +stepMonths, 'M');
				},
				hide: function () {
					window['DP_jQuery_' + dpuuid].datepicker._hideDatepicker();
				},
				today: function () {
					window['DP_jQuery_' + dpuuid].datepicker._gotoToday(id);
				},
				selectDay: function () {
					window['DP_jQuery_' + dpuuid].datepicker._selectDay(id, +this.getAttribute('data-month'), +this.getAttribute('data-year'), this);
					return false;
				},
				selectMonth: function () {
					window['DP_jQuery_' + dpuuid].datepicker._selectMonthYear(id, this, 'M');
					return false;
				},
				selectYear: function () {
					window['DP_jQuery_' + dpuuid].datepicker._selectMonthYear(id, this, 'Y');
					return false;
				}
			};
			$(this).bind(this.getAttribute('data-event'), handler[this.getAttribute('data-handler')]);
		});
	},

	/* Generate the HTML for the current state of the date picker. */
	_generateHTML: function(inst) {
		var today = new Date();
		today = this._daylightSavingAdjust(
			new Date(today.getFullYear(), today.getMonth(), today.getDate())); // clear time
		var isRTL = this._get(inst, 'isRTL');
		var showButtonPanel = this._get(inst, 'showButtonPanel');
		var hideIfNoPrevNext = this._get(inst, 'hideIfNoPrevNext');
		var navigationAsDateFormat = this._get(inst, 'navigationAsDateFormat');
		var numMonths = this._getNumberOfMonths(inst);
		var showCurrentAtPos = this._get(inst, 'showCurrentAtPos');
		var stepMonths = this._get(inst, 'stepMonths');
		var isMultiMonth = (numMonths[0] != 1 || numMonths[1] != 1);
		var currentDate = this._daylightSavingAdjust((!inst.currentDay ? new Date(9999, 9, 9) :
			new Date(inst.currentYear, inst.currentMonth, inst.currentDay)));
		var minDate = this._getMinMaxDate(inst, 'min');
		var maxDate = this._getMinMaxDate(inst, 'max');
		var drawMonth = inst.drawMonth - showCurrentAtPos;
		var drawYear = inst.drawYear;
		if (drawMonth < 0) {
			drawMonth += 12;
			drawYear--;
		}
		if (maxDate) {
			var maxDraw = this._daylightSavingAdjust(new Date(maxDate.getFullYear(),
				maxDate.getMonth() - (numMonths[0] * numMonths[1]) + 1, maxDate.getDate()));
			maxDraw = (minDate && maxDraw < minDate ? minDate : maxDraw);
			while (this._daylightSavingAdjust(new Date(drawYear, drawMonth, 1)) > maxDraw) {
				drawMonth--;
				if (drawMonth < 0) {
					drawMonth = 11;
					drawYear--;
				}
			}
		}
		inst.drawMonth = drawMonth;
		inst.drawYear = drawYear;
		var prevText = this._get(inst, 'prevText');
		prevText = (!navigationAsDateFormat ? prevText : this.formatDate(prevText,
			this._daylightSavingAdjust(new Date(drawYear, drawMonth - stepMonths, 1)),
			this._getFormatConfig(inst)));
		var prev = (this._canAdjustMonth(inst, -1, drawYear, drawMonth) ?
			'<a class="ui-datepicker-prev ui-corner-all" data-handler="prev" data-event="click"' +
			' title="' + prevText + '"><span class="ui-icon ui-icon-circle-triangle-' + ( isRTL ? 'e' : 'w') + '">' + prevText + '</span></a>' :
			(hideIfNoPrevNext ? '' : '<a class="ui-datepicker-prev ui-corner-all ui-state-disabled" title="'+ prevText +'"><span class="ui-icon ui-icon-circle-triangle-' + ( isRTL ? 'e' : 'w') + '">' + prevText + '</span></a>'));
		var nextText = this._get(inst, 'nextText');
		nextText = (!navigationAsDateFormat ? nextText : this.formatDate(nextText,
			this._daylightSavingAdjust(new Date(drawYear, drawMonth + stepMonths, 1)),
			this._getFormatConfig(inst)));
		var next = (this._canAdjustMonth(inst, +1, drawYear, drawMonth) ?
			'<a class="ui-datepicker-next ui-corner-all" data-handler="next" data-event="click"' +
			' title="' + nextText + '"><span class="ui-icon ui-icon-circle-triangle-' + ( isRTL ? 'w' : 'e') + '">' + nextText + '</span></a>' :
			(hideIfNoPrevNext ? '' : '<a class="ui-datepicker-next ui-corner-all ui-state-disabled" title="'+ nextText + '"><span class="ui-icon ui-icon-circle-triangle-' + ( isRTL ? 'w' : 'e') + '">' + nextText + '</span></a>'));
		var currentText = this._get(inst, 'currentText');
		var gotoDate = (this._get(inst, 'gotoCurrent') && inst.currentDay ? currentDate : today);
		currentText = (!navigationAsDateFormat ? currentText :
			this.formatDate(currentText, gotoDate, this._getFormatConfig(inst)));
		var controls = (!inst.inline ? '<button type="button" class="ui-datepicker-close ui-state-default ui-priority-primary ui-corner-all" data-handler="hide" data-event="click">' +
			this._get(inst, 'closeText') + '</button>' : '');
		var buttonPanel = (showButtonPanel) ? '<div class="ui-datepicker-buttonpane ui-widget-content">' + (isRTL ? controls : '') +
			(this._isInRange(inst, gotoDate) ? '<button type="button" class="ui-datepicker-current ui-state-default ui-priority-secondary ui-corner-all" data-handler="today" data-event="click"' +
			'>' + currentText + '</button>' : '') + (isRTL ? '' : controls) + '</div>' : '';
		var firstDay = parseInt(this._get(inst, 'firstDay'),10);
		firstDay = (isNaN(firstDay) ? 0 : firstDay);
		var showWeek = this._get(inst, 'showWeek');
		var dayNames = this._get(inst, 'dayNames');
		var dayNamesShort = this._get(inst, 'dayNamesShort');
		var dayNamesMin = this._get(inst, 'dayNamesMin');
		var monthNames = this._get(inst, 'monthNames');
		var monthNamesShort = this._get(inst, 'monthNamesShort');
		var beforeShowDay = this._get(inst, 'beforeShowDay');
		var showOtherMonths = this._get(inst, 'showOtherMonths');
		var selectOtherMonths = this._get(inst, 'selectOtherMonths');
		var calculateWeek = this._get(inst, 'calculateWeek') || this.iso8601Week;
		var defaultDate = this._getDefaultDate(inst);
		var html = '';
		for (var row = 0; row < numMonths[0]; row++) {
			var group = '';
			this.maxRows = 4;
			for (var col = 0; col < numMonths[1]; col++) {
				var selectedDate = this._daylightSavingAdjust(new Date(drawYear, drawMonth, inst.selectedDay));
				var cornerClass = ' ui-corner-all';
				var calender = '';
				if (isMultiMonth) {
					calender += '<div class="ui-datepicker-group';
					if (numMonths[1] > 1)
						switch (col) {
							case 0: calender += ' ui-datepicker-group-first';
								cornerClass = ' ui-corner-' + (isRTL ? 'right' : 'left'); break;
							case numMonths[1]-1: calender += ' ui-datepicker-group-last';
								cornerClass = ' ui-corner-' + (isRTL ? 'left' : 'right'); break;
							default: calender += ' ui-datepicker-group-middle'; cornerClass = ''; break;
						}
					calender += '">';
				}
				calender += '<div class="ui-datepicker-header ui-widget-header ui-helper-clearfix' + cornerClass + '">' +
					(/all|left/.test(cornerClass) && row == 0 ? (isRTL ? next : prev) : '') +
					(/all|right/.test(cornerClass) && row == 0 ? (isRTL ? prev : next) : '') +
					this._generateMonthYearHeader(inst, drawMonth, drawYear, minDate, maxDate,
					row > 0 || col > 0, monthNames, monthNamesShort) + // draw month headers
					'</div><table class="ui-datepicker-calendar"><thead>' +
					'<tr>';
				var thead = (showWeek ? '<th class="ui-datepicker-week-col">' + this._get(inst, 'weekHeader') + '</th>' : '');
				for (var dow = 0; dow < 7; dow++) { // days of the week
					var day = (dow + firstDay) % 7;
					thead += '<th' + ((dow + firstDay + 6) % 7 >= 5 ? ' class="ui-datepicker-week-end"' : '') + '>' +
						'<span title="' + dayNames[day] + '">' + dayNamesMin[day] + '</span></th>';
				}
				calender += thead + '</tr></thead><tbody>';
				var daysInMonth = this._getDaysInMonth(drawYear, drawMonth);
				if (drawYear == inst.selectedYear && drawMonth == inst.selectedMonth)
					inst.selectedDay = Math.min(inst.selectedDay, daysInMonth);
				var leadDays = (this._getFirstDayOfMonth(drawYear, drawMonth) - firstDay + 7) % 7;
				var curRows = Math.ceil((leadDays + daysInMonth) / 7); // calculate the number of rows to generate
				var numRows = (isMultiMonth ? this.maxRows > curRows ? this.maxRows : curRows : curRows); //If multiple months, use the higher number of rows (see #7043)
				this.maxRows = numRows;
				var printDate = this._daylightSavingAdjust(new Date(drawYear, drawMonth, 1 - leadDays));
				for (var dRow = 0; dRow < numRows; dRow++) { // create date picker rows
					calender += '<tr>';
					var tbody = (!showWeek ? '' : '<td class="ui-datepicker-week-col">' +
						this._get(inst, 'calculateWeek')(printDate) + '</td>');
					for (var dow = 0; dow < 7; dow++) { // create date picker days
						var daySettings = (beforeShowDay ?
							beforeShowDay.apply((inst.input ? inst.input[0] : null), [printDate]) : [true, '']);
						var otherMonth = (printDate.getMonth() != drawMonth);
						var unselectable = (otherMonth && !selectOtherMonths) || !daySettings[0] ||
							(minDate && printDate < minDate) || (maxDate && printDate > maxDate);
						tbody += '<td class="' +
							((dow + firstDay + 6) % 7 >= 5 ? ' ui-datepicker-week-end' : '') + // highlight weekends
							(otherMonth ? ' ui-datepicker-other-month' : '') + // highlight days from other months
							((printDate.getTime() == selectedDate.getTime() && drawMonth == inst.selectedMonth && inst._keyEvent) || // user pressed key
							(defaultDate.getTime() == printDate.getTime() && defaultDate.getTime() == selectedDate.getTime()) ?
							// or defaultDate is current printedDate and defaultDate is selectedDate
							' ' + this._dayOverClass : '') + // highlight selected day
							(unselectable ? ' ' + this._unselectableClass + ' ui-state-disabled': '') +  // highlight unselectable days
							(otherMonth && !showOtherMonths ? '' : ' ' + daySettings[1] + // highlight custom dates
							(printDate.getTime() == currentDate.getTime() ? ' ' + this._currentClass : '') + // highlight selected day
							(printDate.getTime() == today.getTime() ? ' ui-datepicker-today' : '')) + '"' + // highlight today (if different)
							((!otherMonth || showOtherMonths) && daySettings[2] ? ' title="' + daySettings[2] + '"' : '') + // cell title
							(unselectable ? '' : ' data-handler="selectDay" data-event="click" data-month="' + printDate.getMonth() + '" data-year="' + printDate.getFullYear() + '"') + '>' + // actions
							(otherMonth && !showOtherMonths ? '&#xa0;' : // display for other months
							(unselectable ? '<span class="ui-state-default">' + printDate.getDate() + '</span>' : '<a class="ui-state-default' +
							(printDate.getTime() == today.getTime() ? ' ui-state-highlight' : '') +
							(printDate.getTime() == currentDate.getTime() ? ' ui-state-active' : '') + // highlight selected day
							(otherMonth ? ' ui-priority-secondary' : '') + // distinguish dates from other months
							'" href="#">' + printDate.getDate() + '</a>')) + '</td>'; // display selectable date
						printDate.setDate(printDate.getDate() + 1);
						printDate = this._daylightSavingAdjust(printDate);
					}
					calender += tbody + '</tr>';
				}
				drawMonth++;
				if (drawMonth > 11) {
					drawMonth = 0;
					drawYear++;
				}
				calender += '</tbody></table>' + (isMultiMonth ? '</div>' +
							((numMonths[0] > 0 && col == numMonths[1]-1) ? '<div class="ui-datepicker-row-break"></div>' : '') : '');
				group += calender;
			}
			html += group;
		}
		html += buttonPanel + ($.ui.ie6 && !inst.inline ?
			'<iframe src="javascript:false;" class="ui-datepicker-cover" frameborder="0"></iframe>' : '');
		inst._keyEvent = false;
		return html;
	},

	/* Generate the month and year header. */
	_generateMonthYearHeader: function(inst, drawMonth, drawYear, minDate, maxDate,
			secondary, monthNames, monthNamesShort) {
		var changeMonth = this._get(inst, 'changeMonth');
		var changeYear = this._get(inst, 'changeYear');
		var showMonthAfterYear = this._get(inst, 'showMonthAfterYear');
		var html = '<div class="ui-datepicker-title">';
		var monthHtml = '';
		// month selection
		if (secondary || !changeMonth)
			monthHtml += '<span class="ui-datepicker-month">' + monthNames[drawMonth] + '</span>';
		else {
			var inMinYear = (minDate && minDate.getFullYear() == drawYear);
			var inMaxYear = (maxDate && maxDate.getFullYear() == drawYear);
			monthHtml += '<select class="ui-datepicker-month" data-handler="selectMonth" data-event="change">';
			for (var month = 0; month < 12; month++) {
				if ((!inMinYear || month >= minDate.getMonth()) &&
						(!inMaxYear || month <= maxDate.getMonth()))
					monthHtml += '<option value="' + month + '"' +
						(month == drawMonth ? ' selected="selected"' : '') +
						'>' + monthNamesShort[month] + '</option>';
			}
			monthHtml += '</select>';
		}
		if (!showMonthAfterYear)
			html += monthHtml + (secondary || !(changeMonth && changeYear) ? '&#xa0;' : '');
		// year selection
		if ( !inst.yearshtml ) {
			inst.yearshtml = '';
			if (secondary || !changeYear)
				html += '<span class="ui-datepicker-year">' + drawYear + '</span>';
			else {
				// determine range of years to display
				var years = this._get(inst, 'yearRange').split(':');
				var thisYear = new Date().getFullYear();
				var determineYear = function(value) {
					var year = (value.match(/c[+-].*/) ? drawYear + parseInt(value.substring(1), 10) :
						(value.match(/[+-].*/) ? thisYear + parseInt(value, 10) :
						parseInt(value, 10)));
					return (isNaN(year) ? thisYear : year);
				};
				var year = determineYear(years[0]);
				var endYear = Math.max(year, determineYear(years[1] || ''));
				year = (minDate ? Math.max(year, minDate.getFullYear()) : year);
				endYear = (maxDate ? Math.min(endYear, maxDate.getFullYear()) : endYear);
				inst.yearshtml += '<select class="ui-datepicker-year" data-handler="selectYear" data-event="change">';
				for (; year <= endYear; year++) {
					inst.yearshtml += '<option value="' + year + '"' +
						(year == drawYear ? ' selected="selected"' : '') +
						'>' + year + '</option>';
				}
				inst.yearshtml += '</select>';

				html += inst.yearshtml;
				inst.yearshtml = null;
			}
		}
		html += this._get(inst, 'yearSuffix');
		if (showMonthAfterYear)
			html += (secondary || !(changeMonth && changeYear) ? '&#xa0;' : '') + monthHtml;
		html += '</div>'; // Close datepicker_header
		return html;
	},

	/* Adjust one of the date sub-fields. */
	_adjustInstDate: function(inst, offset, period) {
		var year = inst.drawYear + (period == 'Y' ? offset : 0);
		var month = inst.drawMonth + (period == 'M' ? offset : 0);
		var day = Math.min(inst.selectedDay, this._getDaysInMonth(year, month)) +
			(period == 'D' ? offset : 0);
		var date = this._restrictMinMax(inst,
			this._daylightSavingAdjust(new Date(year, month, day)));
		inst.selectedDay = date.getDate();
		inst.drawMonth = inst.selectedMonth = date.getMonth();
		inst.drawYear = inst.selectedYear = date.getFullYear();
		if (period == 'M' || period == 'Y')
			this._notifyChange(inst);
	},

	/* Ensure a date is within any min/max bounds. */
	_restrictMinMax: function(inst, date) {
		var minDate = this._getMinMaxDate(inst, 'min');
		var maxDate = this._getMinMaxDate(inst, 'max');
		var newDate = (minDate && date < minDate ? minDate : date);
		newDate = (maxDate && newDate > maxDate ? maxDate : newDate);
		return newDate;
	},

	/* Notify change of month/year. */
	_notifyChange: function(inst) {
		var onChange = this._get(inst, 'onChangeMonthYear');
		if (onChange)
			onChange.apply((inst.input ? inst.input[0] : null),
				[inst.selectedYear, inst.selectedMonth + 1, inst]);
	},

	/* Determine the number of months to show. */
	_getNumberOfMonths: function(inst) {
		var numMonths = this._get(inst, 'numberOfMonths');
		return (numMonths == null ? [1, 1] : (typeof numMonths == 'number' ? [1, numMonths] : numMonths));
	},

	/* Determine the current maximum date - ensure no time components are set. */
	_getMinMaxDate: function(inst, minMax) {
		return this._determineDate(inst, this._get(inst, minMax + 'Date'), null);
	},

	/* Find the number of days in a given month. */
	_getDaysInMonth: function(year, month) {
		return 32 - this._daylightSavingAdjust(new Date(year, month, 32)).getDate();
	},

	/* Find the day of the week of the first of a month. */
	_getFirstDayOfMonth: function(year, month) {
		return new Date(year, month, 1).getDay();
	},

	/* Determines if we should allow a "next/prev" month display change. */
	_canAdjustMonth: function(inst, offset, curYear, curMonth) {
		var numMonths = this._getNumberOfMonths(inst);
		var date = this._daylightSavingAdjust(new Date(curYear,
			curMonth + (offset < 0 ? offset : numMonths[0] * numMonths[1]), 1));
		if (offset < 0)
			date.setDate(this._getDaysInMonth(date.getFullYear(), date.getMonth()));
		return this._isInRange(inst, date);
	},

	/* Is the given date in the accepted range? */
	_isInRange: function(inst, date) {
		var minDate = this._getMinMaxDate(inst, 'min');
		var maxDate = this._getMinMaxDate(inst, 'max');
		return ((!minDate || date.getTime() >= minDate.getTime()) &&
			(!maxDate || date.getTime() <= maxDate.getTime()));
	},

	/* Provide the configuration settings for formatting/parsing. */
	_getFormatConfig: function(inst) {
		var shortYearCutoff = this._get(inst, 'shortYearCutoff');
		shortYearCutoff = (typeof shortYearCutoff != 'string' ? shortYearCutoff :
			new Date().getFullYear() % 100 + parseInt(shortYearCutoff, 10));
		return {shortYearCutoff: shortYearCutoff,
			dayNamesShort: this._get(inst, 'dayNamesShort'), dayNames: this._get(inst, 'dayNames'),
			monthNamesShort: this._get(inst, 'monthNamesShort'), monthNames: this._get(inst, 'monthNames')};
	},

	/* Format the given date for display. */
	_formatDate: function(inst, day, month, year) {
		if (!day) {
			inst.currentDay = inst.selectedDay;
			inst.currentMonth = inst.selectedMonth;
			inst.currentYear = inst.selectedYear;
		}
		var date = (day ? (typeof day == 'object' ? day :
			this._daylightSavingAdjust(new Date(year, month, day))) :
			this._daylightSavingAdjust(new Date(inst.currentYear, inst.currentMonth, inst.currentDay)));
		return this.formatDate(this._get(inst, 'dateFormat'), date, this._getFormatConfig(inst));
	}
});

/*
 * Bind hover events for datepicker elements.
 * Done via delegate so the binding only occurs once in the lifetime of the parent div.
 * Global instActive, set by _updateDatepicker allows the handlers to find their way back to the active picker.
 */
function bindHover(dpDiv) {
	var selector = 'button, .ui-datepicker-prev, .ui-datepicker-next, .ui-datepicker-calendar td a';
	return dpDiv.delegate(selector, 'mouseout', function() {
			$(this).removeClass('ui-state-hover');
			if (this.className.indexOf('ui-datepicker-prev') != -1) $(this).removeClass('ui-datepicker-prev-hover');
			if (this.className.indexOf('ui-datepicker-next') != -1) $(this).removeClass('ui-datepicker-next-hover');
		})
		.delegate(selector, 'mouseover', function(){
			if (!$.datepicker._isDisabledDatepicker( instActive.inline ? dpDiv.parent()[0] : instActive.input[0])) {
				$(this).parents('.ui-datepicker-calendar').find('a').removeClass('ui-state-hover');
				$(this).addClass('ui-state-hover');
				if (this.className.indexOf('ui-datepicker-prev') != -1) $(this).addClass('ui-datepicker-prev-hover');
				if (this.className.indexOf('ui-datepicker-next') != -1) $(this).addClass('ui-datepicker-next-hover');
			}
		});
}

/* jQuery extend now ignores nulls! */
function extendRemove(target, props) {
	$.extend(target, props);
	for (var name in props)
		if (props[name] == null || props[name] == undefined)
			target[name] = props[name];
	return target;
};

/* Invoke the datepicker functionality.
   @param  options  string - a command, optionally followed by additional parameters or
	                Object - settings for attaching new datepicker functionality
   @return  jQuery object */
$.fn.datepicker = function(options){

	/* Verify an empty collection wasn't passed - Fixes #6976 */
	if ( !this.length ) {
		return this;
	}

	/* Initialise the date picker. */
	if (!$.datepicker.initialized) {
		$(document).mousedown($.datepicker._checkExternalClick).
			find(document.body).append($.datepicker.dpDiv);
		$.datepicker.initialized = true;
	}

	var otherArgs = Array.prototype.slice.call(arguments, 1);
	if (typeof options == 'string' && (options == 'isDisabled' || options == 'getDate' || options == 'widget'))
		return $.datepicker['_' + options + 'Datepicker'].
			apply($.datepicker, [this[0]].concat(otherArgs));
	if (options == 'option' && arguments.length == 2 && typeof arguments[1] == 'string')
		return $.datepicker['_' + options + 'Datepicker'].
			apply($.datepicker, [this[0]].concat(otherArgs));
	return this.each(function() {
		typeof options == 'string' ?
			$.datepicker['_' + options + 'Datepicker'].
				apply($.datepicker, [this].concat(otherArgs)) :
			$.datepicker._attachDatepicker(this, options);
	});
};

$.datepicker = new Datepicker(); // singleton instance
$.datepicker.initialized = false;
$.datepicker.uuid = new Date().getTime();
$.datepicker.version = "@VERSION";

// Workaround for #4055
// Add another global to avoid noConflict issues with inline event handlers
window['DP_jQuery_' + dpuuid] = $;

})(jQuery);
/*!
 * jQuery UI Widget @VERSION
 * http://jqueryui.com
 *
 * Copyright 2012 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/jQuery.widget/
 */
(function( $, undefined ) {

var uuid = 0,
	slice = Array.prototype.slice,
	_cleanData = $.cleanData;
$.cleanData = function( elems ) {
	for ( var i = 0, elem; (elem = elems[i]) != null; i++ ) {
		try {
			$( elem ).triggerHandler( "remove" );
		// http://bugs.jquery.com/ticket/8235
		} catch( e ) {}
	}
	_cleanData( elems );
};

$.widget = function( name, base, prototype ) {
	var fullName, existingConstructor, constructor, basePrototype,
		namespace = name.split( "." )[ 0 ];

	name = name.split( "." )[ 1 ];
	fullName = namespace + "-" + name;

	if ( !prototype ) {
		prototype = base;
		base = $.Widget;
	}

	// create selector for plugin
	$.expr[ ":" ][ fullName.toLowerCase() ] = function( elem ) {
		return !!$.data( elem, fullName );
	};

	$[ namespace ] = $[ namespace ] || {};
	existingConstructor = $[ namespace ][ name ];
	constructor = $[ namespace ][ name ] = function( options, element ) {
		// allow instantiation without "new" keyword
		if ( !this._createWidget ) {
			return new constructor( options, element );
		}

		// allow instantiation without initializing for simple inheritance
		// must use "new" keyword (the code above always passes args)
		if ( arguments.length ) {
			this._createWidget( options, element );
		}
	};
	// extend with the existing constructor to carry over any static properties
	$.extend( constructor, existingConstructor, {
		version: prototype.version,
		// copy the object used to create the prototype in case we need to
		// redefine the widget later
		_proto: $.extend( {}, prototype ),
		// track widgets that inherit from this widget in case this widget is
		// redefined after a widget inherits from it
		_childConstructors: []
	});

	basePrototype = new base();
	// we need to make the options hash a property directly on the new instance
	// otherwise we'll modify the options hash on the prototype that we're
	// inheriting from
	basePrototype.options = $.widget.extend( {}, basePrototype.options );
	$.each( prototype, function( prop, value ) {
		if ( $.isFunction( value ) ) {
			prototype[ prop ] = (function() {
				var _super = function() {
						return base.prototype[ prop ].apply( this, arguments );
					},
					_superApply = function( args ) {
						return base.prototype[ prop ].apply( this, args );
					};
				return function() {
					var __super = this._super,
						__superApply = this._superApply,
						returnValue;

					this._super = _super;
					this._superApply = _superApply;

					returnValue = value.apply( this, arguments );

					this._super = __super;
					this._superApply = __superApply;

					return returnValue;
				};
			})();
		}
	});
	constructor.prototype = $.widget.extend( basePrototype, {
		// TODO: remove support for widgetEventPrefix
		// always use the name + a colon as the prefix, e.g., draggable:start
		// don't prefix for widgets that aren't DOM-based
		widgetEventPrefix: existingConstructor ? basePrototype.widgetEventPrefix : name
	}, prototype, {
		constructor: constructor,
		namespace: namespace,
		widgetName: name,
		// TODO remove widgetBaseClass, see #8155
		widgetBaseClass: fullName,
		widgetFullName: fullName
	});

	// If this widget is being redefined then we need to find all widgets that
	// are inheriting from it and redefine all of them so that they inherit from
	// the new version of this widget. We're essentially trying to replace one
	// level in the prototype chain.
	if ( existingConstructor ) {
		$.each( existingConstructor._childConstructors, function( i, child ) {
			var childPrototype = child.prototype;

			// redefine the child widget using the same prototype that was
			// originally used, but inherit from the new version of the base
			$.widget( childPrototype.namespace + "." + childPrototype.widgetName, constructor, child._proto );
		});
		// remove the list of existing child constructors from the old constructor
		// so the old child constructors can be garbage collected
		delete existingConstructor._childConstructors;
	} else {
		base._childConstructors.push( constructor );
	}

	$.widget.bridge( name, constructor );
};

$.widget.extend = function( target ) {
	var input = slice.call( arguments, 1 ),
		inputIndex = 0,
		inputLength = input.length,
		key,
		value;
	for ( ; inputIndex < inputLength; inputIndex++ ) {
		for ( key in input[ inputIndex ] ) {
			value = input[ inputIndex ][ key ];
			if ( input[ inputIndex ].hasOwnProperty( key ) && value !== undefined ) {
				// Clone objects
				if ( $.isPlainObject( value ) ) {
					target[ key ] = $.isPlainObject( target[ key ] ) ?
						$.widget.extend( {}, target[ key ], value ) :
						// Don't extend strings, arrays, etc. with objects
						$.widget.extend( {}, value );
				// Copy everything else by reference
				} else {
					target[ key ] = value;
				}
			}
		}
	}
	return target;
};

$.widget.bridge = function( name, object ) {
	var fullName = object.prototype.widgetFullName || name;
	$.fn[ name ] = function( options ) {
		var isMethodCall = typeof options === "string",
			args = slice.call( arguments, 1 ),
			returnValue = this;

		// allow multiple hashes to be passed on init
		options = !isMethodCall && args.length ?
			$.widget.extend.apply( null, [ options ].concat(args) ) :
			options;

		if ( isMethodCall ) {
			this.each(function() {
				var methodValue,
					instance = $.data( this, fullName );
				if ( !instance ) {
					return $.error( "cannot call methods on " + name + " prior to initialization; " +
						"attempted to call method '" + options + "'" );
				}
				if ( !$.isFunction( instance[options] ) || options.charAt( 0 ) === "_" ) {
					return $.error( "no such method '" + options + "' for " + name + " widget instance" );
				}
				methodValue = instance[ options ].apply( instance, args );
				if ( methodValue !== instance && methodValue !== undefined ) {
					returnValue = methodValue && methodValue.jquery ?
						returnValue.pushStack( methodValue.get() ) :
						methodValue;
					return false;
				}
			});
		} else {
			this.each(function() {
				var instance = $.data( this, fullName );
				if ( instance ) {
					instance.option( options || {} )._init();
				} else {
					$.data( this, fullName, new object( options, this ) );
				}
			});
		}

		return returnValue;
	};
};

$.Widget = function( /* options, element */ ) {};
$.Widget._childConstructors = [];

$.Widget.prototype = {
	widgetName: "widget",
	widgetEventPrefix: "",
	defaultElement: "<div>",
	options: {
		disabled: false,

		// callbacks
		create: null
	},
	_createWidget: function( options, element ) {
		element = $( element || this.defaultElement || this )[ 0 ];
		this.element = $( element );
		this.uuid = uuid++;
		this.eventNamespace = "." + this.widgetName + this.uuid;
		this.options = $.widget.extend( {},
			this.options,
			this._getCreateOptions(),
			options );

		this.bindings = $();
		this.hoverable = $();
		this.focusable = $();

		if ( element !== this ) {
			// 1.9 BC for #7810
			// TODO remove dual storage
			$.data( element, this.widgetName, this );
			$.data( element, this.widgetFullName, this );
			this._on( true, this.element, {
				remove: function( event ) {
					if ( event.target === element ) {
						this.destroy();
					}
				}
			});
			this.document = $( element.style ?
				// element within the document
				element.ownerDocument :
				// element is window or document
				element.document || element );
			this.window = $( this.document[0].defaultView || this.document[0].parentWindow );
		}

		this._create();
		this._trigger( "create", null, this._getCreateEventData() );
		this._init();
	},
	_getCreateOptions: $.noop,
	_getCreateEventData: $.noop,
	_create: $.noop,
	_init: $.noop,

	destroy: function() {
		this._destroy();
		// we can probably remove the unbind calls in 2.0
		// all event bindings should go through this._on()
		this.element
			.unbind( this.eventNamespace )
			// 1.9 BC for #7810
			// TODO remove dual storage
			.removeData( this.widgetName )
			.removeData( this.widgetFullName )
			// support: jquery <1.6.3
			// http://bugs.jquery.com/ticket/9413
			.removeData( $.camelCase( this.widgetFullName ) );
		this.widget()
			.unbind( this.eventNamespace )
			.removeAttr( "aria-disabled" )
			.removeClass(
				this.widgetFullName + "-disabled " +
				"ui-state-disabled" );

		// clean up events and states
		this.bindings.unbind( this.eventNamespace );
		this.hoverable.removeClass( "ui-state-hover" );
		this.focusable.removeClass( "ui-state-focus" );
	},
	_destroy: $.noop,

	widget: function() {
		return this.element;
	},

	option: function( key, value ) {
		var options = key,
			parts,
			curOption,
			i;

		if ( arguments.length === 0 ) {
			// don't return a reference to the internal hash
			return $.widget.extend( {}, this.options );
		}

		if ( typeof key === "string" ) {
			// handle nested keys, e.g., "foo.bar" => { foo: { bar: ___ } }
			options = {};
			parts = key.split( "." );
			key = parts.shift();
			if ( parts.length ) {
				curOption = options[ key ] = $.widget.extend( {}, this.options[ key ] );
				for ( i = 0; i < parts.length - 1; i++ ) {
					curOption[ parts[ i ] ] = curOption[ parts[ i ] ] || {};
					curOption = curOption[ parts[ i ] ];
				}
				key = parts.pop();
				if ( value === undefined ) {
					return curOption[ key ] === undefined ? null : curOption[ key ];
				}
				curOption[ key ] = value;
			} else {
				if ( value === undefined ) {
					return this.options[ key ] === undefined ? null : this.options[ key ];
				}
				options[ key ] = value;
			}
		}

		this._setOptions( options );

		return this;
	},
	_setOptions: function( options ) {
		var key;

		for ( key in options ) {
			this._setOption( key, options[ key ] );
		}

		return this;
	},
	_setOption: function( key, value ) {
		this.options[ key ] = value;

		if ( key === "disabled" ) {
			this.widget()
				.toggleClass( this.widgetFullName + "-disabled ui-state-disabled", !!value )
				.attr( "aria-disabled", value );
			this.hoverable.removeClass( "ui-state-hover" );
			this.focusable.removeClass( "ui-state-focus" );
		}

		return this;
	},

	enable: function() {
		return this._setOption( "disabled", false );
	},
	disable: function() {
		return this._setOption( "disabled", true );
	},

	_on: function( suppressDisabledCheck, element, handlers ) {
		var delegateElement,
			instance = this;

		// no suppressDisabledCheck flag, shuffle arguments
		if ( typeof suppressDisabledCheck !== "boolean" ) {
			handlers = element;
			element = suppressDisabledCheck;
			suppressDisabledCheck = false;
		}

		// no element argument, shuffle and use this.element
		if ( !handlers ) {
			handlers = element;
			element = this.element;
			delegateElement = this.widget();
		} else {
			// accept selectors, DOM elements
			element = delegateElement = $( element );
			this.bindings = this.bindings.add( element );
		}

		$.each( handlers, function( event, handler ) {
			function handlerProxy() {
				// allow widgets to customize the disabled handling
				// - disabled as an array instead of boolean
				// - disabled class as method for disabling individual parts
				if ( !suppressDisabledCheck &&
						( instance.options.disabled === true ||
							$( this ).hasClass( "ui-state-disabled" ) ) ) {
					return;
				}
				return ( typeof handler === "string" ? instance[ handler ] : handler )
					.apply( instance, arguments );
			}

			// copy the guid so direct unbinding works
			if ( typeof handler !== "string" ) {
				handlerProxy.guid = handler.guid =
					handler.guid || handlerProxy.guid || $.guid++;
			}

			var match = event.match( /^(\w+)\s*(.*)$/ ),
				eventName = match[1] + instance.eventNamespace,
				selector = match[2];
			if ( selector ) {
				delegateElement.delegate( selector, eventName, handlerProxy );
			} else {
				element.bind( eventName, handlerProxy );
			}
		});
	},

	_off: function( element, eventName ) {
		eventName = (eventName || "").split( " " ).join( this.eventNamespace + " " ) + this.eventNamespace;
		element.unbind( eventName ).undelegate( eventName );
	},

	_delay: function( handler, delay ) {
		function handlerProxy() {
			return ( typeof handler === "string" ? instance[ handler ] : handler )
				.apply( instance, arguments );
		}
		var instance = this;
		return setTimeout( handlerProxy, delay || 0 );
	},

	_hoverable: function( element ) {
		this.hoverable = this.hoverable.add( element );
		this._on( element, {
			mouseenter: function( event ) {
				$( event.currentTarget ).addClass( "ui-state-hover" );
			},
			mouseleave: function( event ) {
				$( event.currentTarget ).removeClass( "ui-state-hover" );
			}
		});
	},

	_focusable: function( element ) {
		this.focusable = this.focusable.add( element );
		this._on( element, {
			focusin: function( event ) {
				$( event.currentTarget ).addClass( "ui-state-focus" );
			},
			focusout: function( event ) {
				$( event.currentTarget ).removeClass( "ui-state-focus" );
			}
		});
	},

	_trigger: function( type, event, data ) {
		var prop, orig,
			callback = this.options[ type ];

		data = data || {};
		event = $.Event( event );
		event.type = ( type === this.widgetEventPrefix ?
			type :
			this.widgetEventPrefix + type ).toLowerCase();
		// the original event may come from any element
		// so we need to reset the target on the new event
		event.target = this.element[ 0 ];

		// copy original event properties over to the new event
		orig = event.originalEvent;
		if ( orig ) {
			for ( prop in orig ) {
				if ( !( prop in event ) ) {
					event[ prop ] = orig[ prop ];
				}
			}
		}

		this.element.trigger( event, data );
		return !( $.isFunction( callback ) &&
			callback.apply( this.element[0], [ event ].concat( data ) ) === false ||
			event.isDefaultPrevented() );
	}
};

$.each( { show: "fadeIn", hide: "fadeOut" }, function( method, defaultEffect ) {
	$.Widget.prototype[ "_" + method ] = function( element, options, callback ) {
		if ( typeof options === "string" ) {
			options = { effect: options };
		}
		var hasOptions,
			effectName = !options ?
				method :
				options === true || typeof options === "number" ?
					defaultEffect :
					options.effect || defaultEffect;
		options = options || {};
		if ( typeof options === "number" ) {
			options = { duration: options };
		}
		hasOptions = !$.isEmptyObject( options );
		options.complete = callback;
		if ( options.delay ) {
			element.delay( options.delay );
		}
		if ( hasOptions && $.effects && ( $.effects.effect[ effectName ] || $.uiBackCompat !== false && $.effects[ effectName ] ) ) {
			element[ method ]( options );
		} else if ( effectName !== method && element[ effectName ] ) {
			element[ effectName ]( options.duration, options.easing, callback );
		} else {
			element.queue(function( next ) {
				$( this )[ method ]();
				if ( callback ) {
					callback.call( element[ 0 ] );
				}
				next();
			});
		}
	};
});

// DEPRECATED
if ( $.uiBackCompat !== false ) {
	$.Widget.prototype._getCreateOptions = function() {
		return $.metadata && $.metadata.get( this.element[0] )[ this.widgetName ];
	};
}

})( jQuery );
/*!
 * jQuery UI Mouse @VERSION
 * http://jqueryui.com
 *
 * Copyright 2012 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/mouse/
 *
 * Depends:
 *	jquery.ui.widget.js
 */
(function( $, undefined ) {

var mouseHandled = false;
$( document ).mouseup( function( e ) {
	mouseHandled = false;
});

$.widget("ui.mouse", {
	version: "@VERSION",
	options: {
		cancel: 'input,textarea,button,select,option',
		distance: 1,
		delay: 0
	},
	_mouseInit: function() {
		var that = this;

		this.element
			.bind('mousedown.'+this.widgetName, function(event) {
				return that._mouseDown(event);
			})
			.bind('click.'+this.widgetName, function(event) {
				if (true === $.data(event.target, that.widgetName + '.preventClickEvent')) {
					$.removeData(event.target, that.widgetName + '.preventClickEvent');
					event.stopImmediatePropagation();
					return false;
				}
			});

		this.started = false;
	},

	// TODO: make sure destroying one instance of mouse doesn't mess with
	// other instances of mouse
	_mouseDestroy: function() {
		this.element.unbind('.'+this.widgetName);
		if ( this._mouseMoveDelegate ) {
			$(document)
				.unbind('mousemove.'+this.widgetName, this._mouseMoveDelegate)
				.unbind('mouseup.'+this.widgetName, this._mouseUpDelegate);
		}
	},

	_mouseDown: function(event) {
		// don't let more than one widget handle mouseStart
		if( mouseHandled ) { return; }

		// we may have missed mouseup (out of window)
		(this._mouseStarted && this._mouseUp(event));

		this._mouseDownEvent = event;

		var that = this,
			btnIsLeft = (event.which === 1),
			// event.target.nodeName works around a bug in IE 8 with
			// disabled inputs (#7620)
			elIsCancel = (typeof this.options.cancel === "string" && event.target.nodeName ? $(event.target).closest(this.options.cancel).length : false);
		if (!btnIsLeft || elIsCancel || !this._mouseCapture(event)) {
			return true;
		}

		this.mouseDelayMet = !this.options.delay;
		if (!this.mouseDelayMet) {
			this._mouseDelayTimer = setTimeout(function() {
				that.mouseDelayMet = true;
			}, this.options.delay);
		}

		if (this._mouseDistanceMet(event) && this._mouseDelayMet(event)) {
			this._mouseStarted = (this._mouseStart(event) !== false);
			if (!this._mouseStarted) {
				event.preventDefault();
				return true;
			}
		}

		// Click event may never have fired (Gecko & Opera)
		if (true === $.data(event.target, this.widgetName + '.preventClickEvent')) {
			$.removeData(event.target, this.widgetName + '.preventClickEvent');
		}

		// these delegates are required to keep context
		this._mouseMoveDelegate = function(event) {
			return that._mouseMove(event);
		};
		this._mouseUpDelegate = function(event) {
			return that._mouseUp(event);
		};
		$(document)
			.bind('mousemove.'+this.widgetName, this._mouseMoveDelegate)
			.bind('mouseup.'+this.widgetName, this._mouseUpDelegate);

		event.preventDefault();

		mouseHandled = true;
		return true;
	},

	_mouseMove: function(event) {
		// IE mouseup check - mouseup happened when mouse was out of window
		if ($.ui.ie && !(document.documentMode >= 9) && !event.button) {
			return this._mouseUp(event);
		}

		if (this._mouseStarted) {
			this._mouseDrag(event);
			return event.preventDefault();
		}

		if (this._mouseDistanceMet(event) && this._mouseDelayMet(event)) {
			this._mouseStarted =
				(this._mouseStart(this._mouseDownEvent, event) !== false);
			(this._mouseStarted ? this._mouseDrag(event) : this._mouseUp(event));
		}

		return !this._mouseStarted;
	},

	_mouseUp: function(event) {
		$(document)
			.unbind('mousemove.'+this.widgetName, this._mouseMoveDelegate)
			.unbind('mouseup.'+this.widgetName, this._mouseUpDelegate);

		if (this._mouseStarted) {
			this._mouseStarted = false;

			if (event.target === this._mouseDownEvent.target) {
				$.data(event.target, this.widgetName + '.preventClickEvent', true);
			}

			this._mouseStop(event);
		}

		return false;
	},

	_mouseDistanceMet: function(event) {
		return (Math.max(
				Math.abs(this._mouseDownEvent.pageX - event.pageX),
				Math.abs(this._mouseDownEvent.pageY - event.pageY)
			) >= this.options.distance
		);
	},

	_mouseDelayMet: function(event) {
		return this.mouseDelayMet;
	},

	// These are placeholder methods, to be overriden by extending plugin
	_mouseStart: function(event) {},
	_mouseDrag: function(event) {},
	_mouseStop: function(event) {},
	_mouseCapture: function(event) { return true; }
});

})(jQuery);
/*!
 * jQuery UI Draggable @VERSION
 * http://jqueryui.com
 *
 * Copyright 2012 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/draggable/
 *
 * Depends:
 *	jquery.ui.core.js
 *	jquery.ui.mouse.js
 *	jquery.ui.widget.js
 */
(function( $, undefined ) {

$.widget("ui.draggable", $.ui.mouse, {
	version: "@VERSION",
	widgetEventPrefix: "drag",
	options: {
		addClasses: true,
		appendTo: "parent",
		axis: false,
		connectToSortable: false,
		containment: false,
		cursor: "auto",
		cursorAt: false,
		grid: false,
		handle: false,
		helper: "original",
		iframeFix: false,
		opacity: false,
		refreshPositions: false,
		revert: false,
		revertDuration: 500,
		scope: "default",
		scroll: true,
		scrollSensitivity: 20,
		scrollSpeed: 20,
		snap: false,
		snapMode: "both",
		snapTolerance: 20,
		stack: false,
		zIndex: false
	},
	_create: function() {

		if (this.options.helper == 'original' && !(/^(?:r|a|f)/).test(this.element.css("position")))
			this.element[0].style.position = 'relative';

		(this.options.addClasses && this.element.addClass("ui-draggable"));
		(this.options.disabled && this.element.addClass("ui-draggable-disabled"));

		this._mouseInit();

	},

	_destroy: function() {
		this.element.removeClass( "ui-draggable ui-draggable-dragging ui-draggable-disabled" );
		this._mouseDestroy();
	},

	_mouseCapture: function(event) {

		var o = this.options;

		// among others, prevent a drag on a resizable-handle
		if (this.helper || o.disabled || $(event.target).is('.ui-resizable-handle'))
			return false;

		//Quit if we're not on a valid handle
		this.handle = this._getHandle(event);
		if (!this.handle)
			return false;

		$(o.iframeFix === true ? "iframe" : o.iframeFix).each(function() {
			$('<div class="ui-draggable-iframeFix" style="background: #fff;"></div>')
			.css({
				width: this.offsetWidth+"px", height: this.offsetHeight+"px",
				position: "absolute", opacity: "0.001", zIndex: 1000
			})
			.css($(this).offset())
			.appendTo("body");
		});

		return true;

	},

	_mouseStart: function(event) {

		var o = this.options;

		//Create and append the visible helper
		this.helper = this._createHelper(event);

		this.helper.addClass("ui-draggable-dragging");

		//Cache the helper size
		this._cacheHelperProportions();

		//If ddmanager is used for droppables, set the global draggable
		if($.ui.ddmanager)
			$.ui.ddmanager.current = this;

		/*
		 * - Position generation -
		 * This block generates everything position related - it's the core of draggables.
		 */

		//Cache the margins of the original element
		this._cacheMargins();

		//Store the helper's css position
		this.cssPosition = this.helper.css("position");
		this.scrollParent = this.helper.scrollParent();

		//The element's absolute position on the page minus margins
		this.offset = this.positionAbs = this.element.offset();
		this.offset = {
			top: this.offset.top - this.margins.top,
			left: this.offset.left - this.margins.left
		};

		$.extend(this.offset, {
			click: { //Where the click happened, relative to the element
				left: event.pageX - this.offset.left,
				top: event.pageY - this.offset.top
			},
			parent: this._getParentOffset(),
			relative: this._getRelativeOffset() //This is a relative to absolute position minus the actual position calculation - only used for relative positioned helper
		});

		//Generate the original position
		this.originalPosition = this.position = this._generatePosition(event);
		this.originalPageX = event.pageX;
		this.originalPageY = event.pageY;

		//Adjust the mouse offset relative to the helper if 'cursorAt' is supplied
		(o.cursorAt && this._adjustOffsetFromHelper(o.cursorAt));

		//Set a containment if given in the options
		if(o.containment)
			this._setContainment();

		//Trigger event + callbacks
		if(this._trigger("start", event) === false) {
			this._clear();
			return false;
		}

		//Recache the helper size
		this._cacheHelperProportions();

		//Prepare the droppable offsets
		if ($.ui.ddmanager && !o.dropBehaviour)
			$.ui.ddmanager.prepareOffsets(this, event);


		this._mouseDrag(event, true); //Execute the drag once - this causes the helper not to be visible before getting its correct position

		//If the ddmanager is used for droppables, inform the manager that dragging has started (see #5003)
		if ( $.ui.ddmanager ) $.ui.ddmanager.dragStart(this, event);

		return true;
	},

	_mouseDrag: function(event, noPropagation) {

		//Compute the helpers position
		this.position = this._generatePosition(event);
		this.positionAbs = this._convertPositionTo("absolute");

		//Call plugins and callbacks and use the resulting position if something is returned
		if (!noPropagation) {
			var ui = this._uiHash();
			if(this._trigger('drag', event, ui) === false) {
				this._mouseUp({});
				return false;
			}
			this.position = ui.position;
		}

		if(!this.options.axis || this.options.axis != "y") this.helper[0].style.left = this.position.left+'px';
		if(!this.options.axis || this.options.axis != "x") this.helper[0].style.top = this.position.top+'px';
		if($.ui.ddmanager) $.ui.ddmanager.drag(this, event);

		return false;
	},

	_mouseStop: function(event) {

		//If we are using droppables, inform the manager about the drop
		var dropped = false;
		if ($.ui.ddmanager && !this.options.dropBehaviour)
			dropped = $.ui.ddmanager.drop(this, event);

		//if a drop comes from outside (a sortable)
		if(this.dropped) {
			dropped = this.dropped;
			this.dropped = false;
		}

		//if the original element is no longer in the DOM don't bother to continue (see #8269)
		var element = this.element[0], elementInDom = false;
		while ( element && (element = element.parentNode) ) {
			if (element == document ) {
				elementInDom = true;
			}
		}
		if ( !elementInDom && this.options.helper === "original" )
			return false;

		if((this.options.revert == "invalid" && !dropped) || (this.options.revert == "valid" && dropped) || this.options.revert === true || ($.isFunction(this.options.revert) && this.options.revert.call(this.element, dropped))) {
			var that = this;
			$(this.helper).animate(this.originalPosition, parseInt(this.options.revertDuration, 10), function() {
				if(that._trigger("stop", event) !== false) {
					that._clear();
				}
			});
		} else {
			if(this._trigger("stop", event) !== false) {
				this._clear();
			}
		}

		return false;
	},

	_mouseUp: function(event) {
		//Remove frame helpers
		$("div.ui-draggable-iframeFix").each(function() {
			this.parentNode.removeChild(this);
		});

		//If the ddmanager is used for droppables, inform the manager that dragging has stopped (see #5003)
		if( $.ui.ddmanager ) $.ui.ddmanager.dragStop(this, event);

		return $.ui.mouse.prototype._mouseUp.call(this, event);
	},

	cancel: function() {

		if(this.helper.is(".ui-draggable-dragging")) {
			this._mouseUp({});
		} else {
			this._clear();
		}

		return this;

	},

	_getHandle: function(event) {

		var handle = !this.options.handle || !$(this.options.handle, this.element).length ? true : false;
		$(this.options.handle, this.element)
			.find("*")
			.andSelf()
			.each(function() {
				if(this == event.target) handle = true;
			});

		return handle;

	},

	_createHelper: function(event) {

		var o = this.options;
		var helper = $.isFunction(o.helper) ? $(o.helper.apply(this.element[0], [event])) : (o.helper == 'clone' ? this.element.clone().removeAttr('id') : this.element);

		if(!helper.parents('body').length)
			helper.appendTo((o.appendTo == 'parent' ? this.element[0].parentNode : o.appendTo));

		if(helper[0] != this.element[0] && !(/(fixed|absolute)/).test(helper.css("position")))
			helper.css("position", "absolute");

		return helper;

	},

	_adjustOffsetFromHelper: function(obj) {
		if (typeof obj == 'string') {
			obj = obj.split(' ');
		}
		if ($.isArray(obj)) {
			obj = {left: +obj[0], top: +obj[1] || 0};
		}
		if ('left' in obj) {
			this.offset.click.left = obj.left + this.margins.left;
		}
		if ('right' in obj) {
			this.offset.click.left = this.helperProportions.width - obj.right + this.margins.left;
		}
		if ('top' in obj) {
			this.offset.click.top = obj.top + this.margins.top;
		}
		if ('bottom' in obj) {
			this.offset.click.top = this.helperProportions.height - obj.bottom + this.margins.top;
		}
	},

	_getParentOffset: function() {

		//Get the offsetParent and cache its position
		this.offsetParent = this.helper.offsetParent();
		var po = this.offsetParent.offset();

		// This is a special case where we need to modify a offset calculated on start, since the following happened:
		// 1. The position of the helper is absolute, so it's position is calculated based on the next positioned parent
		// 2. The actual offset parent is a child of the scroll parent, and the scroll parent isn't the document, which means that
		//    the scroll is included in the initial calculation of the offset of the parent, and never recalculated upon drag
		if(this.cssPosition == 'absolute' && this.scrollParent[0] != document && $.contains(this.scrollParent[0], this.offsetParent[0])) {
			po.left += this.scrollParent.scrollLeft();
			po.top += this.scrollParent.scrollTop();
		}

		if((this.offsetParent[0] == document.body) //This needs to be actually done for all browsers, since pageX/pageY includes this information
		|| (this.offsetParent[0].tagName && this.offsetParent[0].tagName.toLowerCase() == 'html' && $.ui.ie)) //Ugly IE fix
			po = { top: 0, left: 0 };

		return {
			top: po.top + (parseInt(this.offsetParent.css("borderTopWidth"),10) || 0),
			left: po.left + (parseInt(this.offsetParent.css("borderLeftWidth"),10) || 0)
		};

	},

	_getRelativeOffset: function() {

		if(this.cssPosition == "relative") {
			var p = this.element.position();
			return {
				top: p.top - (parseInt(this.helper.css("top"),10) || 0) + this.scrollParent.scrollTop(),
				left: p.left - (parseInt(this.helper.css("left"),10) || 0) + this.scrollParent.scrollLeft()
			};
		} else {
			return { top: 0, left: 0 };
		}

	},

	_cacheMargins: function() {
		this.margins = {
			left: (parseInt(this.element.css("marginLeft"),10) || 0),
			top: (parseInt(this.element.css("marginTop"),10) || 0),
			right: (parseInt(this.element.css("marginRight"),10) || 0),
			bottom: (parseInt(this.element.css("marginBottom"),10) || 0)
		};
	},

	_cacheHelperProportions: function() {
		this.helperProportions = {
			width: this.helper.outerWidth(),
			height: this.helper.outerHeight()
		};
	},

	_setContainment: function() {

		var o = this.options;
		if(o.containment == 'parent') o.containment = this.helper[0].parentNode;
		if(o.containment == 'document' || o.containment == 'window') this.containment = [
			o.containment == 'document' ? 0 : $(window).scrollLeft() - this.offset.relative.left - this.offset.parent.left,
			o.containment == 'document' ? 0 : $(window).scrollTop() - this.offset.relative.top - this.offset.parent.top,
			(o.containment == 'document' ? 0 : $(window).scrollLeft()) + $(o.containment == 'document' ? document : window).width() - this.helperProportions.width - this.margins.left,
			(o.containment == 'document' ? 0 : $(window).scrollTop()) + ($(o.containment == 'document' ? document : window).height() || document.body.parentNode.scrollHeight) - this.helperProportions.height - this.margins.top
		];

		if(!(/^(document|window|parent)$/).test(o.containment) && o.containment.constructor != Array) {
			var c = $(o.containment);
			var ce = c[0]; if(!ce) return;
			var co = c.offset();
			var over = ($(ce).css("overflow") != 'hidden');

			this.containment = [
				(parseInt($(ce).css("borderLeftWidth"),10) || 0) + (parseInt($(ce).css("paddingLeft"),10) || 0),
				(parseInt($(ce).css("borderTopWidth"),10) || 0) + (parseInt($(ce).css("paddingTop"),10) || 0),
				(over ? Math.max(ce.scrollWidth,ce.offsetWidth) : ce.offsetWidth) - (parseInt($(ce).css("borderLeftWidth"),10) || 0) - (parseInt($(ce).css("paddingRight"),10) || 0) - this.helperProportions.width - this.margins.left - this.margins.right,
				(over ? Math.max(ce.scrollHeight,ce.offsetHeight) : ce.offsetHeight) - (parseInt($(ce).css("borderTopWidth"),10) || 0) - (parseInt($(ce).css("paddingBottom"),10) || 0) - this.helperProportions.height - this.margins.top  - this.margins.bottom
			];
			this.relative_container = c;

		} else if(o.containment.constructor == Array) {
			this.containment = o.containment;
		}

	},

	_convertPositionTo: function(d, pos) {

		if(!pos) pos = this.position;
		var mod = d == "absolute" ? 1 : -1;
		var o = this.options, scroll = this.cssPosition == 'absolute' && !(this.scrollParent[0] != document && $.contains(this.scrollParent[0], this.offsetParent[0])) ? this.offsetParent : this.scrollParent, scrollIsRootNode = (/(html|body)/i).test(scroll[0].tagName);

		return {
			top: (
				pos.top																	// The absolute mouse position
				+ this.offset.relative.top * mod										// Only for relative positioned nodes: Relative offset from element to offset parent
				+ this.offset.parent.top * mod											// The offsetParent's offset without borders (offset + border)
				- ( ( this.cssPosition == 'fixed' ? -this.scrollParent.scrollTop() : ( scrollIsRootNode ? 0 : scroll.scrollTop() ) ) * mod)
			),
			left: (
				pos.left																// The absolute mouse position
				+ this.offset.relative.left * mod										// Only for relative positioned nodes: Relative offset from element to offset parent
				+ this.offset.parent.left * mod											// The offsetParent's offset without borders (offset + border)
				- ( ( this.cssPosition == 'fixed' ? -this.scrollParent.scrollLeft() : scrollIsRootNode ? 0 : scroll.scrollLeft() ) * mod)
			)
		};

	},

	_generatePosition: function(event) {

		var o = this.options, scroll = this.cssPosition == 'absolute' && !(this.scrollParent[0] != document && $.contains(this.scrollParent[0], this.offsetParent[0])) ? this.offsetParent : this.scrollParent, scrollIsRootNode = (/(html|body)/i).test(scroll[0].tagName);
		var pageX = event.pageX;
		var pageY = event.pageY;

		/*
		 * - Position constraining -
		 * Constrain the position to a mix of grid, containment.
		 */

		if(this.originalPosition) { //If we are not dragging yet, we won't check for options
			var containment;
			if(this.containment) {
			if (this.relative_container){
				var co = this.relative_container.offset();
				containment = [ this.containment[0] + co.left,
					this.containment[1] + co.top,
					this.containment[2] + co.left,
					this.containment[3] + co.top ];
			}
			else {
				containment = this.containment;
			}

				if(event.pageX - this.offset.click.left < containment[0]) pageX = containment[0] + this.offset.click.left;
				if(event.pageY - this.offset.click.top < containment[1]) pageY = containment[1] + this.offset.click.top;
				if(event.pageX - this.offset.click.left > containment[2]) pageX = containment[2] + this.offset.click.left;
				if(event.pageY - this.offset.click.top > containment[3]) pageY = containment[3] + this.offset.click.top;
			}

			if(o.grid) {
				//Check for grid elements set to 0 to prevent divide by 0 error causing invalid argument errors in IE (see ticket #6950)
				var top = o.grid[1] ? this.originalPageY + Math.round((pageY - this.originalPageY) / o.grid[1]) * o.grid[1] : this.originalPageY;
				pageY = containment ? (!(top - this.offset.click.top < containment[1] || top - this.offset.click.top > containment[3]) ? top : (!(top - this.offset.click.top < containment[1]) ? top - o.grid[1] : top + o.grid[1])) : top;

				var left = o.grid[0] ? this.originalPageX + Math.round((pageX - this.originalPageX) / o.grid[0]) * o.grid[0] : this.originalPageX;
				pageX = containment ? (!(left - this.offset.click.left < containment[0] || left - this.offset.click.left > containment[2]) ? left : (!(left - this.offset.click.left < containment[0]) ? left - o.grid[0] : left + o.grid[0])) : left;
			}

		}

		return {
			top: (
				pageY																// The absolute mouse position
				- this.offset.click.top													// Click offset (relative to the element)
				- this.offset.relative.top												// Only for relative positioned nodes: Relative offset from element to offset parent
				- this.offset.parent.top												// The offsetParent's offset without borders (offset + border)
				+ ( ( this.cssPosition == 'fixed' ? -this.scrollParent.scrollTop() : ( scrollIsRootNode ? 0 : scroll.scrollTop() ) ))
			),
			left: (
				pageX																// The absolute mouse position
				- this.offset.click.left												// Click offset (relative to the element)
				- this.offset.relative.left												// Only for relative positioned nodes: Relative offset from element to offset parent
				- this.offset.parent.left												// The offsetParent's offset without borders (offset + border)
				+ ( ( this.cssPosition == 'fixed' ? -this.scrollParent.scrollLeft() : scrollIsRootNode ? 0 : scroll.scrollLeft() ))
			)
		};

	},

	_clear: function() {
		this.helper.removeClass("ui-draggable-dragging");
		if(this.helper[0] != this.element[0] && !this.cancelHelperRemoval) this.helper.remove();
		//if($.ui.ddmanager) $.ui.ddmanager.current = null;
		this.helper = null;
		this.cancelHelperRemoval = false;
	},

	// From now on bulk stuff - mainly helpers

	_trigger: function(type, event, ui) {
		ui = ui || this._uiHash();
		$.ui.plugin.call(this, type, [event, ui]);
		if(type == "drag") this.positionAbs = this._convertPositionTo("absolute"); //The absolute position has to be recalculated after plugins
		return $.Widget.prototype._trigger.call(this, type, event, ui);
	},

	plugins: {},

	_uiHash: function(event) {
		return {
			helper: this.helper,
			position: this.position,
			originalPosition: this.originalPosition,
			offset: this.positionAbs
		};
	}

});

$.ui.plugin.add("draggable", "connectToSortable", {
	start: function(event, ui) {

		var inst = $(this).data("draggable"), o = inst.options,
			uiSortable = $.extend({}, ui, { item: inst.element });
		inst.sortables = [];
		$(o.connectToSortable).each(function() {
			var sortable = $.data(this, 'sortable');
			if (sortable && !sortable.options.disabled) {
				inst.sortables.push({
					instance: sortable,
					shouldRevert: sortable.options.revert
				});
				sortable.refreshPositions();	// Call the sortable's refreshPositions at drag start to refresh the containerCache since the sortable container cache is used in drag and needs to be up to date (this will ensure it's initialised as well as being kept in step with any changes that might have happened on the page).
				sortable._trigger("activate", event, uiSortable);
			}
		});

	},
	stop: function(event, ui) {

		//If we are still over the sortable, we fake the stop event of the sortable, but also remove helper
		var inst = $(this).data("draggable"),
			uiSortable = $.extend({}, ui, { item: inst.element });

		$.each(inst.sortables, function() {
			if(this.instance.isOver) {

				this.instance.isOver = 0;

				inst.cancelHelperRemoval = true; //Don't remove the helper in the draggable instance
				this.instance.cancelHelperRemoval = false; //Remove it in the sortable instance (so sortable plugins like revert still work)

				//The sortable revert is supported, and we have to set a temporary dropped variable on the draggable to support revert: 'valid/invalid'
				if(this.shouldRevert) this.instance.options.revert = true;

				//Trigger the stop of the sortable
				this.instance._mouseStop(event);

				this.instance.options.helper = this.instance.options._helper;

				//If the helper has been the original item, restore properties in the sortable
				if(inst.options.helper == 'original')
					this.instance.currentItem.css({ top: 'auto', left: 'auto' });

			} else {
				this.instance.cancelHelperRemoval = false; //Remove the helper in the sortable instance
				this.instance._trigger("deactivate", event, uiSortable);
			}

		});

	},
	drag: function(event, ui) {

		var inst = $(this).data("draggable"), that = this;

		var checkPos = function(o) {
			var dyClick = this.offset.click.top, dxClick = this.offset.click.left;
			var helperTop = this.positionAbs.top, helperLeft = this.positionAbs.left;
			var itemHeight = o.height, itemWidth = o.width;
			var itemTop = o.top, itemLeft = o.left;

			return $.ui.isOver(helperTop + dyClick, helperLeft + dxClick, itemTop, itemLeft, itemHeight, itemWidth);
		};

		$.each(inst.sortables, function(i) {

			var innermostIntersecting = false;
			var thisSortable = this;
			//Copy over some variables to allow calling the sortable's native _intersectsWith
			this.instance.positionAbs = inst.positionAbs;
			this.instance.helperProportions = inst.helperProportions;
			this.instance.offset.click = inst.offset.click;

			if(this.instance._intersectsWith(this.instance.containerCache)) {
				innermostIntersecting = true;
				$.each(inst.sortables, function () {
					this.instance.positionAbs = inst.positionAbs;
					this.instance.helperProportions = inst.helperProportions;
					this.instance.offset.click = inst.offset.click;
					if  (this != thisSortable
						&& this.instance._intersectsWith(this.instance.containerCache)
						&& $.ui.contains(thisSortable.instance.element[0], this.instance.element[0]))
						innermostIntersecting = false;
						return innermostIntersecting;
				});
			}


			if(innermostIntersecting) {
				//If it intersects, we use a little isOver variable and set it once, so our move-in stuff gets fired only once
				if(!this.instance.isOver) {

					this.instance.isOver = 1;
					//Now we fake the start of dragging for the sortable instance,
					//by cloning the list group item, appending it to the sortable and using it as inst.currentItem
					//We can then fire the start event of the sortable with our passed browser event, and our own helper (so it doesn't create a new one)
					this.instance.currentItem = $(that).clone().removeAttr('id').appendTo(this.instance.element).data("sortable-item", true);
					this.instance.options._helper = this.instance.options.helper; //Store helper option to later restore it
					this.instance.options.helper = function() { return ui.helper[0]; };

					event.target = this.instance.currentItem[0];
					this.instance._mouseCapture(event, true);
					this.instance._mouseStart(event, true, true);

					//Because the browser event is way off the new appended portlet, we modify a couple of variables to reflect the changes
					this.instance.offset.click.top = inst.offset.click.top;
					this.instance.offset.click.left = inst.offset.click.left;
					this.instance.offset.parent.left -= inst.offset.parent.left - this.instance.offset.parent.left;
					this.instance.offset.parent.top -= inst.offset.parent.top - this.instance.offset.parent.top;

					inst._trigger("toSortable", event);
					inst.dropped = this.instance.element; //draggable revert needs that
					//hack so receive/update callbacks work (mostly)
					inst.currentItem = inst.element;
					this.instance.fromOutside = inst;

				}

				//Provided we did all the previous steps, we can fire the drag event of the sortable on every draggable drag, when it intersects with the sortable
				if(this.instance.currentItem) this.instance._mouseDrag(event);

			} else {

				//If it doesn't intersect with the sortable, and it intersected before,
				//we fake the drag stop of the sortable, but make sure it doesn't remove the helper by using cancelHelperRemoval
				if(this.instance.isOver) {

					this.instance.isOver = 0;
					this.instance.cancelHelperRemoval = true;

					//Prevent reverting on this forced stop
					this.instance.options.revert = false;

					// The out event needs to be triggered independently
					this.instance._trigger('out', event, this.instance._uiHash(this.instance));

					this.instance._mouseStop(event, true);
					this.instance.options.helper = this.instance.options._helper;

					//Now we remove our currentItem, the list group clone again, and the placeholder, and animate the helper back to it's original size
					this.instance.currentItem.remove();
					if(this.instance.placeholder) this.instance.placeholder.remove();

					inst._trigger("fromSortable", event);
					inst.dropped = false; //draggable revert needs that
				}

			};

		});

	}
});

$.ui.plugin.add("draggable", "cursor", {
	start: function(event, ui) {
		var t = $('body'), o = $(this).data('draggable').options;
		if (t.css("cursor")) o._cursor = t.css("cursor");
		t.css("cursor", o.cursor);
	},
	stop: function(event, ui) {
		var o = $(this).data('draggable').options;
		if (o._cursor) $('body').css("cursor", o._cursor);
	}
});

$.ui.plugin.add("draggable", "opacity", {
	start: function(event, ui) {
		var t = $(ui.helper), o = $(this).data('draggable').options;
		if(t.css("opacity")) o._opacity = t.css("opacity");
		t.css('opacity', o.opacity);
	},
	stop: function(event, ui) {
		var o = $(this).data('draggable').options;
		if(o._opacity) $(ui.helper).css('opacity', o._opacity);
	}
});

$.ui.plugin.add("draggable", "scroll", {
	start: function(event, ui) {
		var i = $(this).data("draggable");
		if(i.scrollParent[0] != document && i.scrollParent[0].tagName != 'HTML') i.overflowOffset = i.scrollParent.offset();
	},
	drag: function(event, ui) {

		var i = $(this).data("draggable"), o = i.options, scrolled = false;

		if(i.scrollParent[0] != document && i.scrollParent[0].tagName != 'HTML') {

			if(!o.axis || o.axis != 'x') {
				if((i.overflowOffset.top + i.scrollParent[0].offsetHeight) - event.pageY < o.scrollSensitivity)
					i.scrollParent[0].scrollTop = scrolled = i.scrollParent[0].scrollTop + o.scrollSpeed;
				else if(event.pageY - i.overflowOffset.top < o.scrollSensitivity)
					i.scrollParent[0].scrollTop = scrolled = i.scrollParent[0].scrollTop - o.scrollSpeed;
			}

			if(!o.axis || o.axis != 'y') {
				if((i.overflowOffset.left + i.scrollParent[0].offsetWidth) - event.pageX < o.scrollSensitivity)
					i.scrollParent[0].scrollLeft = scrolled = i.scrollParent[0].scrollLeft + o.scrollSpeed;
				else if(event.pageX - i.overflowOffset.left < o.scrollSensitivity)
					i.scrollParent[0].scrollLeft = scrolled = i.scrollParent[0].scrollLeft - o.scrollSpeed;
			}

		} else {

			if(!o.axis || o.axis != 'x') {
				if(event.pageY - $(document).scrollTop() < o.scrollSensitivity)
					scrolled = $(document).scrollTop($(document).scrollTop() - o.scrollSpeed);
				else if($(window).height() - (event.pageY - $(document).scrollTop()) < o.scrollSensitivity)
					scrolled = $(document).scrollTop($(document).scrollTop() + o.scrollSpeed);
			}

			if(!o.axis || o.axis != 'y') {
				if(event.pageX - $(document).scrollLeft() < o.scrollSensitivity)
					scrolled = $(document).scrollLeft($(document).scrollLeft() - o.scrollSpeed);
				else if($(window).width() - (event.pageX - $(document).scrollLeft()) < o.scrollSensitivity)
					scrolled = $(document).scrollLeft($(document).scrollLeft() + o.scrollSpeed);
			}

		}

		if(scrolled !== false && $.ui.ddmanager && !o.dropBehaviour)
			$.ui.ddmanager.prepareOffsets(i, event);

	}
});

$.ui.plugin.add("draggable", "snap", {
	start: function(event, ui) {

		var i = $(this).data("draggable"), o = i.options;
		i.snapElements = [];

		$(o.snap.constructor != String ? ( o.snap.items || ':data(draggable)' ) : o.snap).each(function() {
			var $t = $(this); var $o = $t.offset();
			if(this != i.element[0]) i.snapElements.push({
				item: this,
				width: $t.outerWidth(), height: $t.outerHeight(),
				top: $o.top, left: $o.left
			});
		});

	},
	drag: function(event, ui) {

		var inst = $(this).data("draggable"), o = inst.options;
		var d = o.snapTolerance;

		var x1 = ui.offset.left, x2 = x1 + inst.helperProportions.width,
			y1 = ui.offset.top, y2 = y1 + inst.helperProportions.height;

		for (var i = inst.snapElements.length - 1; i >= 0; i--){

			var l = inst.snapElements[i].left, r = l + inst.snapElements[i].width,
				t = inst.snapElements[i].top, b = t + inst.snapElements[i].height;

			//Yes, I know, this is insane ;)
			if(!((l-d < x1 && x1 < r+d && t-d < y1 && y1 < b+d) || (l-d < x1 && x1 < r+d && t-d < y2 && y2 < b+d) || (l-d < x2 && x2 < r+d && t-d < y1 && y1 < b+d) || (l-d < x2 && x2 < r+d && t-d < y2 && y2 < b+d))) {
				if(inst.snapElements[i].snapping) (inst.options.snap.release && inst.options.snap.release.call(inst.element, event, $.extend(inst._uiHash(), { snapItem: inst.snapElements[i].item })));
				inst.snapElements[i].snapping = false;
				continue;
			}

			if(o.snapMode != 'inner') {
				var ts = Math.abs(t - y2) <= d;
				var bs = Math.abs(b - y1) <= d;
				var ls = Math.abs(l - x2) <= d;
				var rs = Math.abs(r - x1) <= d;
				if(ts) ui.position.top = inst._convertPositionTo("relative", { top: t - inst.helperProportions.height, left: 0 }).top - inst.margins.top;
				if(bs) ui.position.top = inst._convertPositionTo("relative", { top: b, left: 0 }).top - inst.margins.top;
				if(ls) ui.position.left = inst._convertPositionTo("relative", { top: 0, left: l - inst.helperProportions.width }).left - inst.margins.left;
				if(rs) ui.position.left = inst._convertPositionTo("relative", { top: 0, left: r }).left - inst.margins.left;
			}

			var first = (ts || bs || ls || rs);

			if(o.snapMode != 'outer') {
				var ts = Math.abs(t - y1) <= d;
				var bs = Math.abs(b - y2) <= d;
				var ls = Math.abs(l - x1) <= d;
				var rs = Math.abs(r - x2) <= d;
				if(ts) ui.position.top = inst._convertPositionTo("relative", { top: t, left: 0 }).top - inst.margins.top;
				if(bs) ui.position.top = inst._convertPositionTo("relative", { top: b - inst.helperProportions.height, left: 0 }).top - inst.margins.top;
				if(ls) ui.position.left = inst._convertPositionTo("relative", { top: 0, left: l }).left - inst.margins.left;
				if(rs) ui.position.left = inst._convertPositionTo("relative", { top: 0, left: r - inst.helperProportions.width }).left - inst.margins.left;
			}

			if(!inst.snapElements[i].snapping && (ts || bs || ls || rs || first))
				(inst.options.snap.snap && inst.options.snap.snap.call(inst.element, event, $.extend(inst._uiHash(), { snapItem: inst.snapElements[i].item })));
			inst.snapElements[i].snapping = (ts || bs || ls || rs || first);

		};

	}
});

$.ui.plugin.add("draggable", "stack", {
	start: function(event, ui) {

		var o = $(this).data("draggable").options;

		var group = $.makeArray($(o.stack)).sort(function(a,b) {
			return (parseInt($(a).css("zIndex"),10) || 0) - (parseInt($(b).css("zIndex"),10) || 0);
		});
		if (!group.length) { return; }

		var min = parseInt(group[0].style.zIndex) || 0;
		$(group).each(function(i) {
			this.style.zIndex = min + i;
		});

		this[0].style.zIndex = min + group.length;

	}
});

$.ui.plugin.add("draggable", "zIndex", {
	start: function(event, ui) {
		var t = $(ui.helper), o = $(this).data("draggable").options;
		if(t.css("zIndex")) o._zIndex = t.css("zIndex");
		t.css('zIndex', o.zIndex);
	},
	stop: function(event, ui) {
		var o = $(this).data("draggable").options;
		if(o._zIndex) $(ui.helper).css('zIndex', o._zIndex);
	}
});

})(jQuery);
/*!
 * jQuery UI Slider @VERSION
 * http://jqueryui.com
 *
 * Copyright 2012 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/slider/
 *
 * Depends:
 *	jquery.ui.core.js
 *	jquery.ui.mouse.js
 *	jquery.ui.widget.js
 */
(function( $, undefined ) {

// number of pages in a slider
// (how many times can you page up/down to go through the whole range)
var numPages = 5;

$.widget( "ui.slider", $.ui.mouse, {
	version: "@VERSION",
	widgetEventPrefix: "slide",

	options: {
		animate: false,
		distance: 0,
		max: 100,
		min: 0,
		orientation: "horizontal",
		range: false,
		step: 1,
		value: 0,
		values: null
	},

	_create: function() {
		var i, handleCount,
			o = this.options,
			existingHandles = this.element.find( ".ui-slider-handle" ).addClass( "ui-state-default ui-corner-all" ),
			handle = "<a class='ui-slider-handle ui-state-default ui-corner-all' href='#'></a>",
			handles = [];

		this._keySliding = false;
		this._mouseSliding = false;
		this._animateOff = true;
		this._handleIndex = null;
		this._detectOrientation();
		this._mouseInit();

		this.element
			.addClass( "ui-slider" +
				" ui-slider-" + this.orientation +
				" ui-widget" +
				" ui-widget-content" +
				" ui-corner-all" +
				( o.disabled ? " ui-slider-disabled ui-disabled" : "" ) );

		this.range = $([]);

		if ( o.range ) {
			if ( o.range === true ) {
				if ( !o.values ) {
					o.values = [ this._valueMin(), this._valueMin() ];
				}
				if ( o.values.length && o.values.length !== 2 ) {
					o.values = [ o.values[0], o.values[0] ];
				}
			}

			this.range = $( "<div></div>" )
				.appendTo( this.element )
				.addClass( "ui-slider-range" +
				// note: this isn't the most fittingly semantic framework class for this element,
				// but worked best visually with a variety of themes
				" ui-widget-header" +
				( ( o.range === "min" || o.range === "max" ) ? " ui-slider-range-" + o.range : "" ) );
		}

		handleCount = ( o.values && o.values.length ) || 1;

		for ( i = existingHandles.length; i < handleCount; i++ ) {
			handles.push( handle );
		}

		this.handles = existingHandles.add( $( handles.join( "" ) ).appendTo( this.element ) );

		this.handle = this.handles.eq( 0 );

		this.handles.add( this.range ).filter( "a" )
			.click(function( event ) {
				event.preventDefault();
			})
			.mouseenter(function() {
				if ( !o.disabled ) {
					$( this ).addClass( "ui-state-hover" );
				}
			})
			.mouseleave(function() {
				$( this ).removeClass( "ui-state-hover" );
			})
			.focus(function() {
				if ( !o.disabled ) {
					$( ".ui-slider .ui-state-focus" ).removeClass( "ui-state-focus" );
					$( this ).addClass( "ui-state-focus" );
				} else {
					$( this ).blur();
				}
			})
			.blur(function() {
				$( this ).removeClass( "ui-state-focus" );
			});

		this.handles.each(function( i ) {
			$( this ).data( "ui-slider-handle-index", i );
		});

		this._on( this.handles, {
			keydown: function( event ) {
				var allowed, curVal, newVal, step,
					index = $( event.target ).data( "ui-slider-handle-index" );

				switch ( event.keyCode ) {
					case $.ui.keyCode.HOME:
					case $.ui.keyCode.END:
					case $.ui.keyCode.PAGE_UP:
					case $.ui.keyCode.PAGE_DOWN:
					case $.ui.keyCode.UP:
					case $.ui.keyCode.RIGHT:
					case $.ui.keyCode.DOWN:
					case $.ui.keyCode.LEFT:
						event.preventDefault();
						if ( !this._keySliding ) {
							this._keySliding = true;
							$( event.target ).addClass( "ui-state-active" );
							allowed = this._start( event, index );
							if ( allowed === false ) {
								return;
							}
						}
						break;
				}

				step = this.options.step;
				if ( this.options.values && this.options.values.length ) {
					curVal = newVal = this.values( index );
				} else {
					curVal = newVal = this.value();
				}

				switch ( event.keyCode ) {
					case $.ui.keyCode.HOME:
						newVal = this._valueMin();
						break;
					case $.ui.keyCode.END:
						newVal = this._valueMax();
						break;
					case $.ui.keyCode.PAGE_UP:
						newVal = this._trimAlignValue( curVal + ( (this._valueMax() - this._valueMin()) / numPages ) );
						break;
					case $.ui.keyCode.PAGE_DOWN:
						newVal = this._trimAlignValue( curVal - ( (this._valueMax() - this._valueMin()) / numPages ) );
						break;
					case $.ui.keyCode.UP:
					case $.ui.keyCode.RIGHT:
						if ( curVal === this._valueMax() ) {
							return;
						}
						newVal = this._trimAlignValue( curVal + step );
						break;
					case $.ui.keyCode.DOWN:
					case $.ui.keyCode.LEFT:
						if ( curVal === this._valueMin() ) {
							return;
						}
						newVal = this._trimAlignValue( curVal - step );
						break;
				}

				this._slide( event, index, newVal );
			},
			keyup: function( event ) {
				var index = $( event.target ).data( "ui-slider-handle-index" );

				if ( this._keySliding ) {
					this._keySliding = false;
					this._stop( event, index );
					this._change( event, index );
					$( event.target ).removeClass( "ui-state-active" );
				}
			}
		});

		this._refreshValue();

		this._animateOff = false;
	},

	_destroy: function() {
		this.handles.remove();
		this.range.remove();

		this.element
			.removeClass( "ui-slider" +
				" ui-slider-horizontal" +
				" ui-slider-vertical" +
				" ui-slider-disabled" +
				" ui-widget" +
				" ui-widget-content" +
				" ui-corner-all" );

		this._mouseDestroy();
	},

	_mouseCapture: function( event ) {
		var position, normValue, distance, closestHandle, index, allowed, offset, mouseOverHandle,
			that = this,
			o = this.options;

		if ( o.disabled ) {
			return false;
		}

		this.elementSize = {
			width: this.element.outerWidth(),
			height: this.element.outerHeight()
		};
		this.elementOffset = this.element.offset();

		position = { x: event.pageX, y: event.pageY };
		normValue = this._normValueFromMouse( position );
		distance = this._valueMax() - this._valueMin() + 1;
		this.handles.each(function( i ) {
			var thisDistance = Math.abs( normValue - that.values(i) );
			if ( distance > thisDistance ) {
				distance = thisDistance;
				closestHandle = $( this );
				index = i;
			}
		});

		// workaround for bug #3736 (if both handles of a range are at 0,
		// the first is always used as the one with least distance,
		// and moving it is obviously prevented by preventing negative ranges)
		if( o.range === true && this.values(1) === o.min ) {
			index += 1;
			closestHandle = $( this.handles[index] );
		}

		allowed = this._start( event, index );
		if ( allowed === false ) {
			return false;
		}
		this._mouseSliding = true;

		this._handleIndex = index;

		closestHandle
			.addClass( "ui-state-active" )
			.focus();

		offset = closestHandle.offset();
		mouseOverHandle = !$( event.target ).parents().andSelf().is( ".ui-slider-handle" );
		this._clickOffset = mouseOverHandle ? { left: 0, top: 0 } : {
			left: event.pageX - offset.left - ( closestHandle.width() / 2 ),
			top: event.pageY - offset.top -
				( closestHandle.height() / 2 ) -
				( parseInt( closestHandle.css("borderTopWidth"), 10 ) || 0 ) -
				( parseInt( closestHandle.css("borderBottomWidth"), 10 ) || 0) +
				( parseInt( closestHandle.css("marginTop"), 10 ) || 0)
		};

		if ( !this.handles.hasClass( "ui-state-hover" ) ) {
			this._slide( event, index, normValue );
		}
		this._animateOff = true;
		return true;
	},

	_mouseStart: function() {
		return true;
	},

	_mouseDrag: function( event ) {
		var position = { x: event.pageX, y: event.pageY },
			normValue = this._normValueFromMouse( position );

		this._slide( event, this._handleIndex, normValue );

		return false;
	},

	_mouseStop: function( event ) {
		this.handles.removeClass( "ui-state-active" );
		this._mouseSliding = false;

		this._stop( event, this._handleIndex );
		this._change( event, this._handleIndex );

		this._handleIndex = null;
		this._clickOffset = null;
		this._animateOff = false;

		return false;
	},

	_detectOrientation: function() {
		this.orientation = ( this.options.orientation === "vertical" ) ? "vertical" : "horizontal";
	},

	_normValueFromMouse: function( position ) {
		var pixelTotal,
			pixelMouse,
			percentMouse,
			valueTotal,
			valueMouse;

		if ( this.orientation === "horizontal" ) {
			pixelTotal = this.elementSize.width;
			pixelMouse = position.x - this.elementOffset.left - ( this._clickOffset ? this._clickOffset.left : 0 );
		} else {
			pixelTotal = this.elementSize.height;
			pixelMouse = position.y - this.elementOffset.top - ( this._clickOffset ? this._clickOffset.top : 0 );
		}

		percentMouse = ( pixelMouse / pixelTotal );
		if ( percentMouse > 1 ) {
			percentMouse = 1;
		}
		if ( percentMouse < 0 ) {
			percentMouse = 0;
		}
		if ( this.orientation === "vertical" ) {
			percentMouse = 1 - percentMouse;
		}

		valueTotal = this._valueMax() - this._valueMin();
		valueMouse = this._valueMin() + percentMouse * valueTotal;

		return this._trimAlignValue( valueMouse );
	},

	_start: function( event, index ) {
		var uiHash = {
			handle: this.handles[ index ],
			value: this.value()
		};
		if ( this.options.values && this.options.values.length ) {
			uiHash.value = this.values( index );
			uiHash.values = this.values();
		}
		return this._trigger( "start", event, uiHash );
	},

	_slide: function( event, index, newVal ) {
		var otherVal,
			newValues,
			allowed;

		if ( this.options.values && this.options.values.length ) {
			otherVal = this.values( index ? 0 : 1 );

			if ( ( this.options.values.length === 2 && this.options.range === true ) &&
					( ( index === 0 && newVal > otherVal) || ( index === 1 && newVal < otherVal ) )
				) {
				newVal = otherVal;
			}

			if ( newVal !== this.values( index ) ) {
				newValues = this.values();
				newValues[ index ] = newVal;
				// A slide can be canceled by returning false from the slide callback
				allowed = this._trigger( "slide", event, {
					handle: this.handles[ index ],
					value: newVal,
					values: newValues
				} );
				otherVal = this.values( index ? 0 : 1 );
				if ( allowed !== false ) {
					this.values( index, newVal, true );
				}
			}
		} else {
			if ( newVal !== this.value() ) {
				// A slide can be canceled by returning false from the slide callback
				allowed = this._trigger( "slide", event, {
					handle: this.handles[ index ],
					value: newVal
				} );
				if ( allowed !== false ) {
					this.value( newVal );
				}
			}
		}
	},

	_stop: function( event, index ) {
		var uiHash = {
			handle: this.handles[ index ],
			value: this.value()
		};
		if ( this.options.values && this.options.values.length ) {
			uiHash.value = this.values( index );
			uiHash.values = this.values();
		}

		this._trigger( "stop", event, uiHash );
	},

	_change: function( event, index ) {
		if ( !this._keySliding && !this._mouseSliding ) {
			var uiHash = {
				handle: this.handles[ index ],
				value: this.value()
			};
			if ( this.options.values && this.options.values.length ) {
				uiHash.value = this.values( index );
				uiHash.values = this.values();
			}

			this._trigger( "change", event, uiHash );
		}
	},

	value: function( newValue ) {
		if ( arguments.length ) {
			this.options.value = this._trimAlignValue( newValue );
			this._refreshValue();
			this._change( null, 0 );
			return;
		}

		return this._value();
	},

	values: function( index, newValue ) {
		var vals,
			newValues,
			i;

		if ( arguments.length > 1 ) {
			this.options.values[ index ] = this._trimAlignValue( newValue );
			this._refreshValue();
			this._change( null, index );
			return;
		}

		if ( arguments.length ) {
			if ( $.isArray( arguments[ 0 ] ) ) {
				vals = this.options.values;
				newValues = arguments[ 0 ];
				for ( i = 0; i < vals.length; i += 1 ) {
					vals[ i ] = this._trimAlignValue( newValues[ i ] );
					this._change( null, i );
				}
				this._refreshValue();
			} else {
				if ( this.options.values && this.options.values.length ) {
					return this._values( index );
				} else {
					return this.value();
				}
			}
		} else {
			return this._values();
		}
	},

	_setOption: function( key, value ) {
		var i,
			valsLength = 0;

		if ( $.isArray( this.options.values ) ) {
			valsLength = this.options.values.length;
		}

		$.Widget.prototype._setOption.apply( this, arguments );

		switch ( key ) {
			case "disabled":
				if ( value ) {
					this.handles.filter( ".ui-state-focus" ).blur();
					this.handles.removeClass( "ui-state-hover" );
					this.handles.prop( "disabled", true );
					this.element.addClass( "ui-disabled" );
				} else {
					this.handles.prop( "disabled", false );
					this.element.removeClass( "ui-disabled" );
				}
				break;
			case "orientation":
				this._detectOrientation();
				this.element
					.removeClass( "ui-slider-horizontal ui-slider-vertical" )
					.addClass( "ui-slider-" + this.orientation );
				this._refreshValue();
				break;
			case "value":
				this._animateOff = true;
				this._refreshValue();
				this._change( null, 0 );
				this._animateOff = false;
				break;
			case "values":
				this._animateOff = true;
				this._refreshValue();
				for ( i = 0; i < valsLength; i += 1 ) {
					this._change( null, i );
				}
				this._animateOff = false;
				break;
			case "min":
			case "max":
				this._animateOff = true;
				this._refreshValue();
				this._animateOff = false;
				break;
		}
	},

	//internal value getter
	// _value() returns value trimmed by min and max, aligned by step
	_value: function() {
		var val = this.options.value;
		val = this._trimAlignValue( val );

		return val;
	},

	//internal values getter
	// _values() returns array of values trimmed by min and max, aligned by step
	// _values( index ) returns single value trimmed by min and max, aligned by step
	_values: function( index ) {
		var val,
			vals,
			i;

		if ( arguments.length ) {
			val = this.options.values[ index ];
			val = this._trimAlignValue( val );

			return val;
		} else {
			// .slice() creates a copy of the array
			// this copy gets trimmed by min and max and then returned
			vals = this.options.values.slice();
			for ( i = 0; i < vals.length; i+= 1) {
				vals[ i ] = this._trimAlignValue( vals[ i ] );
			}

			return vals;
		}
	},

	// returns the step-aligned value that val is closest to, between (inclusive) min and max
	_trimAlignValue: function( val ) {
		if ( val <= this._valueMin() ) {
			return this._valueMin();
		}
		if ( val >= this._valueMax() ) {
			return this._valueMax();
		}
		var step = ( this.options.step > 0 ) ? this.options.step : 1,
			valModStep = (val - this._valueMin()) % step,
			alignValue = val - valModStep;

		if ( Math.abs(valModStep) * 2 >= step ) {
			alignValue += ( valModStep > 0 ) ? step : ( -step );
		}

		// Since JavaScript has problems with large floats, round
		// the final value to 5 digits after the decimal point (see #4124)
		return parseFloat( alignValue.toFixed(5) );
	},

	_valueMin: function() {
		return this.options.min;
	},

	_valueMax: function() {
		return this.options.max;
	},

	_refreshValue: function() {
		var lastValPercent, valPercent, value, valueMin, valueMax,
			oRange = this.options.range,
			o = this.options,
			that = this,
			animate = ( !this._animateOff ) ? o.animate : false,
			_set = {};

		if ( this.options.values && this.options.values.length ) {
			this.handles.each(function( i ) {
				valPercent = ( that.values(i) - that._valueMin() ) / ( that._valueMax() - that._valueMin() ) * 100;
				_set[ that.orientation === "horizontal" ? "left" : "bottom" ] = valPercent + "%";
				$( this ).stop( 1, 1 )[ animate ? "animate" : "css" ]( _set, o.animate );
				if ( that.options.range === true ) {
					if ( that.orientation === "horizontal" ) {
						if ( i === 0 ) {
							that.range.stop( 1, 1 )[ animate ? "animate" : "css" ]( { left: valPercent + "%" }, o.animate );
						}
						if ( i === 1 ) {
							that.range[ animate ? "animate" : "css" ]( { width: ( valPercent - lastValPercent ) + "%" }, { queue: false, duration: o.animate } );
						}
					} else {
						if ( i === 0 ) {
							that.range.stop( 1, 1 )[ animate ? "animate" : "css" ]( { bottom: ( valPercent ) + "%" }, o.animate );
						}
						if ( i === 1 ) {
							that.range[ animate ? "animate" : "css" ]( { height: ( valPercent - lastValPercent ) + "%" }, { queue: false, duration: o.animate } );
						}
					}
				}
				lastValPercent = valPercent;
			});
		} else {
			value = this.value();
			valueMin = this._valueMin();
			valueMax = this._valueMax();
			valPercent = ( valueMax !== valueMin ) ?
					( value - valueMin ) / ( valueMax - valueMin ) * 100 :
					0;
			_set[ this.orientation === "horizontal" ? "left" : "bottom" ] = valPercent + "%";
			this.handle.stop( 1, 1 )[ animate ? "animate" : "css" ]( _set, o.animate );

			if ( oRange === "min" && this.orientation === "horizontal" ) {
				this.range.stop( 1, 1 )[ animate ? "animate" : "css" ]( { width: valPercent + "%" }, o.animate );
			}
			if ( oRange === "max" && this.orientation === "horizontal" ) {
				this.range[ animate ? "animate" : "css" ]( { width: ( 100 - valPercent ) + "%" }, { queue: false, duration: o.animate } );
			}
			if ( oRange === "min" && this.orientation === "vertical" ) {
				this.range.stop( 1, 1 )[ animate ? "animate" : "css" ]( { height: valPercent + "%" }, o.animate );
			}
			if ( oRange === "max" && this.orientation === "vertical" ) {
				this.range[ animate ? "animate" : "css" ]( { height: ( 100 - valPercent ) + "%" }, { queue: false, duration: o.animate } );
			}
		}
	}

});

}(jQuery));
/*!
 * jQuery UI Sortable @VERSION
 * http://jqueryui.com
 *
 * Copyright 2012 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/sortable/
 *
 * Depends:
 *	jquery.ui.core.js
 *	jquery.ui.mouse.js
 *	jquery.ui.widget.js
 */
(function( $, undefined ) {

$.widget("ui.sortable", $.ui.mouse, {
	version: "@VERSION",
	widgetEventPrefix: "sort",
	ready: false,
	options: {
		appendTo: "parent",
		axis: false,
		connectWith: false,
		containment: false,
		cursor: 'auto',
		cursorAt: false,
		dropOnEmpty: true,
		forcePlaceholderSize: false,
		forceHelperSize: false,
		grid: false,
		handle: false,
		helper: "original",
		items: '> *',
		opacity: false,
		placeholder: false,
		revert: false,
		scroll: true,
		scrollSensitivity: 20,
		scrollSpeed: 20,
		scope: "default",
		tolerance: "intersect",
		zIndex: 1000
	},
	_create: function() {

		var o = this.options;
		this.containerCache = {};
		this.element.addClass("ui-sortable");

		//Get the items
		this.refresh();

		//Let's determine if the items are being displayed horizontally
		this.floating = this.items.length ? o.axis === 'x' || (/left|right/).test(this.items[0].item.css('float')) || (/inline|table-cell/).test(this.items[0].item.css('display')) : false;

		//Let's determine the parent's offset
		this.offset = this.element.offset();

		//Initialize mouse events for interaction
		this._mouseInit();

		//We're ready to go
		this.ready = true

	},

	_destroy: function() {
		this.element
			.removeClass("ui-sortable ui-sortable-disabled");
		this._mouseDestroy();

		for ( var i = this.items.length - 1; i >= 0; i-- )
			this.items[i].item.removeData(this.widgetName + "-item");

		return this;
	},

	_setOption: function(key, value){
		if ( key === "disabled" ) {
			this.options[ key ] = value;

			this.widget().toggleClass( "ui-sortable-disabled", !!value );
		} else {
			// Don't call widget base _setOption for disable as it adds ui-state-disabled class
			$.Widget.prototype._setOption.apply(this, arguments);
		}
	},

	_mouseCapture: function(event, overrideHandle) {
		var that = this;

		if (this.reverting) {
			return false;
		}

		if(this.options.disabled || this.options.type == 'static') return false;

		//We have to refresh the items data once first
		this._refreshItems(event);

		//Find out if the clicked node (or one of its parents) is a actual item in this.items
		var currentItem = null, nodes = $(event.target).parents().each(function() {
			if($.data(this, that.widgetName + '-item') == that) {
				currentItem = $(this);
				return false;
			}
		});
		if($.data(event.target, that.widgetName + '-item') == that) currentItem = $(event.target);

		if(!currentItem) return false;
		if(this.options.handle && !overrideHandle) {
			var validHandle = false;

			$(this.options.handle, currentItem).find("*").andSelf().each(function() { if(this == event.target) validHandle = true; });
			if(!validHandle) return false;
		}

		this.currentItem = currentItem;
		this._removeCurrentsFromItems();
		return true;

	},

	_mouseStart: function(event, overrideHandle, noActivation) {

		var o = this.options;
		this.currentContainer = this;

		//We only need to call refreshPositions, because the refreshItems call has been moved to mouseCapture
		this.refreshPositions();

		//Create and append the visible helper
		this.helper = this._createHelper(event);

		//Cache the helper size
		this._cacheHelperProportions();

		/*
		 * - Position generation -
		 * This block generates everything position related - it's the core of draggables.
		 */

		//Cache the margins of the original element
		this._cacheMargins();

		//Get the next scrolling parent
		this.scrollParent = this.helper.scrollParent();

		//The element's absolute position on the page minus margins
		this.offset = this.currentItem.offset();
		this.offset = {
			top: this.offset.top - this.margins.top,
			left: this.offset.left - this.margins.left
		};

		$.extend(this.offset, {
			click: { //Where the click happened, relative to the element
				left: event.pageX - this.offset.left,
				top: event.pageY - this.offset.top
			},
			parent: this._getParentOffset(),
			relative: this._getRelativeOffset() //This is a relative to absolute position minus the actual position calculation - only used for relative positioned helper
		});

		// Only after we got the offset, we can change the helper's position to absolute
		// TODO: Still need to figure out a way to make relative sorting possible
		this.helper.css("position", "absolute");
		this.cssPosition = this.helper.css("position");

		//Generate the original position
		this.originalPosition = this._generatePosition(event);
		this.originalPageX = event.pageX;
		this.originalPageY = event.pageY;

		//Adjust the mouse offset relative to the helper if 'cursorAt' is supplied
		(o.cursorAt && this._adjustOffsetFromHelper(o.cursorAt));

		//Cache the former DOM position
		this.domPosition = { prev: this.currentItem.prev()[0], parent: this.currentItem.parent()[0] };

		//If the helper is not the original, hide the original so it's not playing any role during the drag, won't cause anything bad this way
		if(this.helper[0] != this.currentItem[0]) {
			this.currentItem.hide();
		}

		//Create the placeholder
		this._createPlaceholder();

		//Set a containment if given in the options
		if(o.containment)
			this._setContainment();

		if(o.cursor) { // cursor option
			if ($('body').css("cursor")) this._storedCursor = $('body').css("cursor");
			$('body').css("cursor", o.cursor);
		}

		if(o.opacity) { // opacity option
			if (this.helper.css("opacity")) this._storedOpacity = this.helper.css("opacity");
			this.helper.css("opacity", o.opacity);
		}

		if(o.zIndex) { // zIndex option
			if (this.helper.css("zIndex")) this._storedZIndex = this.helper.css("zIndex");
			this.helper.css("zIndex", o.zIndex);
		}

		//Prepare scrolling
		if(this.scrollParent[0] != document && this.scrollParent[0].tagName != 'HTML')
			this.overflowOffset = this.scrollParent.offset();

		//Call callbacks
		this._trigger("start", event, this._uiHash());

		//Recache the helper size
		if(!this._preserveHelperProportions)
			this._cacheHelperProportions();


		//Post 'activate' events to possible containers
		if(!noActivation) {
			 for (var i = this.containers.length - 1; i >= 0; i--) { this.containers[i]._trigger("activate", event, this._uiHash(this)); }
		}

		//Prepare possible droppables
		if($.ui.ddmanager)
			$.ui.ddmanager.current = this;

		if ($.ui.ddmanager && !o.dropBehaviour)
			$.ui.ddmanager.prepareOffsets(this, event);

		this.dragging = true;

		this.helper.addClass("ui-sortable-helper");
		this._mouseDrag(event); //Execute the drag once - this causes the helper not to be visible before getting its correct position
		return true;

	},

	_mouseDrag: function(event) {

		//Compute the helpers position
		this.position = this._generatePosition(event);
		this.positionAbs = this._convertPositionTo("absolute");

		if (!this.lastPositionAbs) {
			this.lastPositionAbs = this.positionAbs;
		}

		//Do scrolling
		if(this.options.scroll) {
			var o = this.options, scrolled = false;
			if(this.scrollParent[0] != document && this.scrollParent[0].tagName != 'HTML') {

				if((this.overflowOffset.top + this.scrollParent[0].offsetHeight) - event.pageY < o.scrollSensitivity)
					this.scrollParent[0].scrollTop = scrolled = this.scrollParent[0].scrollTop + o.scrollSpeed;
				else if(event.pageY - this.overflowOffset.top < o.scrollSensitivity)
					this.scrollParent[0].scrollTop = scrolled = this.scrollParent[0].scrollTop - o.scrollSpeed;

				if((this.overflowOffset.left + this.scrollParent[0].offsetWidth) - event.pageX < o.scrollSensitivity)
					this.scrollParent[0].scrollLeft = scrolled = this.scrollParent[0].scrollLeft + o.scrollSpeed;
				else if(event.pageX - this.overflowOffset.left < o.scrollSensitivity)
					this.scrollParent[0].scrollLeft = scrolled = this.scrollParent[0].scrollLeft - o.scrollSpeed;

			} else {

				if(event.pageY - $(document).scrollTop() < o.scrollSensitivity)
					scrolled = $(document).scrollTop($(document).scrollTop() - o.scrollSpeed);
				else if($(window).height() - (event.pageY - $(document).scrollTop()) < o.scrollSensitivity)
					scrolled = $(document).scrollTop($(document).scrollTop() + o.scrollSpeed);

				if(event.pageX - $(document).scrollLeft() < o.scrollSensitivity)
					scrolled = $(document).scrollLeft($(document).scrollLeft() - o.scrollSpeed);
				else if($(window).width() - (event.pageX - $(document).scrollLeft()) < o.scrollSensitivity)
					scrolled = $(document).scrollLeft($(document).scrollLeft() + o.scrollSpeed);

			}

			if(scrolled !== false && $.ui.ddmanager && !o.dropBehaviour)
				$.ui.ddmanager.prepareOffsets(this, event);
		}

		//Regenerate the absolute position used for position checks
		this.positionAbs = this._convertPositionTo("absolute");

		//Set the helper position
		if(!this.options.axis || this.options.axis != "y") this.helper[0].style.left = this.position.left+'px';
		if(!this.options.axis || this.options.axis != "x") this.helper[0].style.top = this.position.top+'px';

		//Rearrange
		for (var i = this.items.length - 1; i >= 0; i--) {

			//Cache variables and intersection, continue if no intersection
			var item = this.items[i], itemElement = item.item[0], intersection = this._intersectsWithPointer(item);
			if (!intersection) continue;

			// Only put the placeholder inside the current Container, skip all
			// items form other containers. This works because when moving
			// an item from one container to another the
			// currentContainer is switched before the placeholder is moved.
			//
			// Without this moving items in "sub-sortables" can cause the placeholder to jitter
			// beetween the outer and inner container.
			if (item.instance !== this.currentContainer) continue;

			if (itemElement != this.currentItem[0] //cannot intersect with itself
				&&	this.placeholder[intersection == 1 ? "next" : "prev"]()[0] != itemElement //no useless actions that have been done before
				&&	!$.contains(this.placeholder[0], itemElement) //no action if the item moved is the parent of the item checked
				&& (this.options.type == 'semi-dynamic' ? !$.contains(this.element[0], itemElement) : true)
				//&& itemElement.parentNode == this.placeholder[0].parentNode // only rearrange items within the same container
			) {

				this.direction = intersection == 1 ? "down" : "up";

				if (this.options.tolerance == "pointer" || this._intersectsWithSides(item)) {
					this._rearrange(event, item);
				} else {
					break;
				}

				this._trigger("change", event, this._uiHash());
				break;
			}
		}

		//Post events to containers
		this._contactContainers(event);

		//Interconnect with droppables
		if($.ui.ddmanager) $.ui.ddmanager.drag(this, event);

		//Call callbacks
		this._trigger('sort', event, this._uiHash());

		this.lastPositionAbs = this.positionAbs;
		return false;

	},

	_mouseStop: function(event, noPropagation) {

		if(!event) return;

		//If we are using droppables, inform the manager about the drop
		if ($.ui.ddmanager && !this.options.dropBehaviour)
			$.ui.ddmanager.drop(this, event);

		if(this.options.revert) {
			var that = this;
			var cur = this.placeholder.offset();

			this.reverting = true;

			$(this.helper).animate({
				left: cur.left - this.offset.parent.left - this.margins.left + (this.offsetParent[0] == document.body ? 0 : this.offsetParent[0].scrollLeft),
				top: cur.top - this.offset.parent.top - this.margins.top + (this.offsetParent[0] == document.body ? 0 : this.offsetParent[0].scrollTop)
			}, parseInt(this.options.revert, 10) || 500, function() {
				that._clear(event);
			});
		} else {
			this._clear(event, noPropagation);
		}

		return false;

	},

	cancel: function() {

		if(this.dragging) {

			this._mouseUp({ target: null });

			if(this.options.helper == "original")
				this.currentItem.css(this._storedCSS).removeClass("ui-sortable-helper");
			else
				this.currentItem.show();

			//Post deactivating events to containers
			for (var i = this.containers.length - 1; i >= 0; i--){
				this.containers[i]._trigger("deactivate", null, this._uiHash(this));
				if(this.containers[i].containerCache.over) {
					this.containers[i]._trigger("out", null, this._uiHash(this));
					this.containers[i].containerCache.over = 0;
				}
			}

		}

		if (this.placeholder) {
			//$(this.placeholder[0]).remove(); would have been the jQuery way - unfortunately, it unbinds ALL events from the original node!
			if(this.placeholder[0].parentNode) this.placeholder[0].parentNode.removeChild(this.placeholder[0]);
			if(this.options.helper != "original" && this.helper && this.helper[0].parentNode) this.helper.remove();

			$.extend(this, {
				helper: null,
				dragging: false,
				reverting: false,
				_noFinalSort: null
			});

			if(this.domPosition.prev) {
				$(this.domPosition.prev).after(this.currentItem);
			} else {
				$(this.domPosition.parent).prepend(this.currentItem);
			}
		}

		return this;

	},

	serialize: function(o) {

		var items = this._getItemsAsjQuery(o && o.connected);
		var str = []; o = o || {};

		$(items).each(function() {
			var res = ($(o.item || this).attr(o.attribute || 'id') || '').match(o.expression || (/(.+)[-=_](.+)/));
			if(res) str.push((o.key || res[1]+'[]')+'='+(o.key && o.expression ? res[1] : res[2]));
		});

		if(!str.length && o.key) {
			str.push(o.key + '=');
		}

		return str.join('&');

	},

	toArray: function(o) {

		var items = this._getItemsAsjQuery(o && o.connected);
		var ret = []; o = o || {};

		items.each(function() { ret.push($(o.item || this).attr(o.attribute || 'id') || ''); });
		return ret;

	},

	/* Be careful with the following core functions */
	_intersectsWith: function(item) {

		var x1 = this.positionAbs.left,
			x2 = x1 + this.helperProportions.width,
			y1 = this.positionAbs.top,
			y2 = y1 + this.helperProportions.height;

		var l = item.left,
			r = l + item.width,
			t = item.top,
			b = t + item.height;

		var dyClick = this.offset.click.top,
			dxClick = this.offset.click.left;

		var isOverElement = (y1 + dyClick) > t && (y1 + dyClick) < b && (x1 + dxClick) > l && (x1 + dxClick) < r;

		if(	   this.options.tolerance == "pointer"
			|| this.options.forcePointerForContainers
			|| (this.options.tolerance != "pointer" && this.helperProportions[this.floating ? 'width' : 'height'] > item[this.floating ? 'width' : 'height'])
		) {
			return isOverElement;
		} else {

			return (l < x1 + (this.helperProportions.width / 2) // Right Half
				&& x2 - (this.helperProportions.width / 2) < r // Left Half
				&& t < y1 + (this.helperProportions.height / 2) // Bottom Half
				&& y2 - (this.helperProportions.height / 2) < b ); // Top Half

		}
	},

	_intersectsWithPointer: function(item) {

		var isOverElementHeight = (this.options.axis === 'x') || $.ui.isOverAxis(this.positionAbs.top + this.offset.click.top, item.top, item.height),
			isOverElementWidth = (this.options.axis === 'y') || $.ui.isOverAxis(this.positionAbs.left + this.offset.click.left, item.left, item.width),
			isOverElement = isOverElementHeight && isOverElementWidth,
			verticalDirection = this._getDragVerticalDirection(),
			horizontalDirection = this._getDragHorizontalDirection();

		if (!isOverElement)
			return false;

		return this.floating ?
			( ((horizontalDirection && horizontalDirection == "right") || verticalDirection == "down") ? 2 : 1 )
			: ( verticalDirection && (verticalDirection == "down" ? 2 : 1) );

	},

	_intersectsWithSides: function(item) {

		var isOverBottomHalf = $.ui.isOverAxis(this.positionAbs.top + this.offset.click.top, item.top + (item.height/2), item.height),
			isOverRightHalf = $.ui.isOverAxis(this.positionAbs.left + this.offset.click.left, item.left + (item.width/2), item.width),
			verticalDirection = this._getDragVerticalDirection(),
			horizontalDirection = this._getDragHorizontalDirection();

		if (this.floating && horizontalDirection) {
			return ((horizontalDirection == "right" && isOverRightHalf) || (horizontalDirection == "left" && !isOverRightHalf));
		} else {
			return verticalDirection && ((verticalDirection == "down" && isOverBottomHalf) || (verticalDirection == "up" && !isOverBottomHalf));
		}

	},

	_getDragVerticalDirection: function() {
		var delta = this.positionAbs.top - this.lastPositionAbs.top;
		return delta != 0 && (delta > 0 ? "down" : "up");
	},

	_getDragHorizontalDirection: function() {
		var delta = this.positionAbs.left - this.lastPositionAbs.left;
		return delta != 0 && (delta > 0 ? "right" : "left");
	},

	refresh: function(event) {
		this._refreshItems(event);
		this.refreshPositions();
		return this;
	},

	_connectWith: function() {
		var options = this.options;
		return options.connectWith.constructor == String
			? [options.connectWith]
			: options.connectWith;
	},

	_getItemsAsjQuery: function(connected) {

		var items = [];
		var queries = [];
		var connectWith = this._connectWith();

		if(connectWith && connected) {
			for (var i = connectWith.length - 1; i >= 0; i--){
				var cur = $(connectWith[i]);
				for (var j = cur.length - 1; j >= 0; j--){
					var inst = $.data(cur[j], this.widgetName);
					if(inst && inst != this && !inst.options.disabled) {
						queries.push([$.isFunction(inst.options.items) ? inst.options.items.call(inst.element) : $(inst.options.items, inst.element).not(".ui-sortable-helper").not('.ui-sortable-placeholder'), inst]);
					}
				};
			};
		}

		queries.push([$.isFunction(this.options.items) ? this.options.items.call(this.element, null, { options: this.options, item: this.currentItem }) : $(this.options.items, this.element).not(".ui-sortable-helper").not('.ui-sortable-placeholder'), this]);

		for (var i = queries.length - 1; i >= 0; i--){
			queries[i][0].each(function() {
				items.push(this);
			});
		};

		return $(items);

	},

	_removeCurrentsFromItems: function() {

		var list = this.currentItem.find(":data(" + this.widgetName + "-item)");

		this.items = $.grep(this.items, function (item) {
			for (var j=0; j < list.length; j++) {
				if(list[j] == item.item[0])
					return false;
			};
			return true;
		});

	},

	_refreshItems: function(event) {

		this.items = [];
		this.containers = [this];
		var items = this.items;
		var queries = [[$.isFunction(this.options.items) ? this.options.items.call(this.element[0], event, { item: this.currentItem }) : $(this.options.items, this.element), this]];
		var connectWith = this._connectWith();

		if(connectWith && this.ready) { //Shouldn't be run the first time through due to massive slow-down
			for (var i = connectWith.length - 1; i >= 0; i--){
				var cur = $(connectWith[i]);
				for (var j = cur.length - 1; j >= 0; j--){
					var inst = $.data(cur[j], this.widgetName);
					if(inst && inst != this && !inst.options.disabled) {
						queries.push([$.isFunction(inst.options.items) ? inst.options.items.call(inst.element[0], event, { item: this.currentItem }) : $(inst.options.items, inst.element), inst]);
						this.containers.push(inst);
					}
				};
			};
		}

		for (var i = queries.length - 1; i >= 0; i--) {
			var targetData = queries[i][1];
			var _queries = queries[i][0];

			for (var j=0, queriesLength = _queries.length; j < queriesLength; j++) {
				var item = $(_queries[j]);

				item.data(this.widgetName + '-item', targetData); // Data for target checking (mouse manager)

				items.push({
					item: item,
					instance: targetData,
					width: 0, height: 0,
					left: 0, top: 0
				});
			};
		};

	},

	refreshPositions: function(fast) {

		//This has to be redone because due to the item being moved out/into the offsetParent, the offsetParent's position will change
		if(this.offsetParent && this.helper) {
			this.offset.parent = this._getParentOffset();
		}

		for (var i = this.items.length - 1; i >= 0; i--){
			var item = this.items[i];

			//We ignore calculating positions of all connected containers when we're not over them
			if(item.instance != this.currentContainer && this.currentContainer && item.item[0] != this.currentItem[0])
				continue;

			var t = this.options.toleranceElement ? $(this.options.toleranceElement, item.item) : item.item;

			if (!fast) {
				item.width = t.outerWidth();
				item.height = t.outerHeight();
			}

			var p = t.offset();
			item.left = p.left;
			item.top = p.top;
		};

		if(this.options.custom && this.options.custom.refreshContainers) {
			this.options.custom.refreshContainers.call(this);
		} else {
			for (var i = this.containers.length - 1; i >= 0; i--){
				var p = this.containers[i].element.offset();
				this.containers[i].containerCache.left = p.left;
				this.containers[i].containerCache.top = p.top;
				this.containers[i].containerCache.width	= this.containers[i].element.outerWidth();
				this.containers[i].containerCache.height = this.containers[i].element.outerHeight();
			};
		}

		return this;
	},

	_createPlaceholder: function(that) {
		that = that || this;
		var o = that.options;

		if(!o.placeholder || o.placeholder.constructor == String) {
			var className = o.placeholder;
			o.placeholder = {
				element: function() {

					var el = $(document.createElement(that.currentItem[0].nodeName))
						.addClass(className || that.currentItem[0].className+" ui-sortable-placeholder")
						.removeClass("ui-sortable-helper")[0];

					if(!className)
						el.style.visibility = "hidden";

					return el;
				},
				update: function(container, p) {

					// 1. If a className is set as 'placeholder option, we don't force sizes - the class is responsible for that
					// 2. The option 'forcePlaceholderSize can be enabled to force it even if a class name is specified
					if(className && !o.forcePlaceholderSize) return;

					//If the element doesn't have a actual height by itself (without styles coming from a stylesheet), it receives the inline height from the dragged item
					if(!p.height()) { p.height(that.currentItem.innerHeight() - parseInt(that.currentItem.css('paddingTop')||0, 10) - parseInt(that.currentItem.css('paddingBottom')||0, 10)); };
					if(!p.width()) { p.width(that.currentItem.innerWidth() - parseInt(that.currentItem.css('paddingLeft')||0, 10) - parseInt(that.currentItem.css('paddingRight')||0, 10)); };
				}
			};
		}

		//Create the placeholder
		that.placeholder = $(o.placeholder.element.call(that.element, that.currentItem));

		//Append it after the actual current item
		that.currentItem.after(that.placeholder);

		//Update the size of the placeholder (TODO: Logic to fuzzy, see line 316/317)
		o.placeholder.update(that, that.placeholder);

	},

	_contactContainers: function(event) {

		// get innermost container that intersects with item
		var innermostContainer = null, innermostIndex = null;


		for (var i = this.containers.length - 1; i >= 0; i--){

			// never consider a container that's located within the item itself
			if($.contains(this.currentItem[0], this.containers[i].element[0]))
				continue;

			if(this._intersectsWith(this.containers[i].containerCache)) {

				// if we've already found a container and it's more "inner" than this, then continue
				if(innermostContainer && $.contains(this.containers[i].element[0], innermostContainer.element[0]))
					continue;

				innermostContainer = this.containers[i];
				innermostIndex = i;

			} else {
				// container doesn't intersect. trigger "out" event if necessary
				if(this.containers[i].containerCache.over) {
					this.containers[i]._trigger("out", event, this._uiHash(this));
					this.containers[i].containerCache.over = 0;
				}
			}

		}

		// if no intersecting containers found, return
		if(!innermostContainer) return;

		// move the item into the container if it's not there already
		if(this.containers.length === 1) {
			this.containers[innermostIndex]._trigger("over", event, this._uiHash(this));
			this.containers[innermostIndex].containerCache.over = 1;
		} else {

			//When entering a new container, we will find the item with the least distance and append our item near it
			var dist = 10000; var itemWithLeastDistance = null;
			var posProperty = this.containers[innermostIndex].floating ? 'left' : 'top';
			var sizeProperty = this.containers[innermostIndex].floating ? 'width' : 'height';
			var base = this.positionAbs[posProperty] + this.offset.click[posProperty];
			for (var j = this.items.length - 1; j >= 0; j--) {
				if(!$.contains(this.containers[innermostIndex].element[0], this.items[j].item[0])) continue;
				if(this.items[j].item[0] == this.currentItem[0]) continue;
				var cur = this.items[j].item.offset()[posProperty];
				var nearBottom = false;
				if(Math.abs(cur - base) > Math.abs(cur + this.items[j][sizeProperty] - base)){
					nearBottom = true;
					cur += this.items[j][sizeProperty];
				}

				if(Math.abs(cur - base) < dist) {
					dist = Math.abs(cur - base); itemWithLeastDistance = this.items[j];
					this.direction = nearBottom ? "up": "down";
				}
			}

			if(!itemWithLeastDistance && !this.options.dropOnEmpty) //Check if dropOnEmpty is enabled
				return;

			this.currentContainer = this.containers[innermostIndex];
			itemWithLeastDistance ? this._rearrange(event, itemWithLeastDistance, null, true) : this._rearrange(event, null, this.containers[innermostIndex].element, true);
			this._trigger("change", event, this._uiHash());
			this.containers[innermostIndex]._trigger("change", event, this._uiHash(this));

			//Update the placeholder
			this.options.placeholder.update(this.currentContainer, this.placeholder);

			this.containers[innermostIndex]._trigger("over", event, this._uiHash(this));
			this.containers[innermostIndex].containerCache.over = 1;
		}


	},

	_createHelper: function(event) {

		var o = this.options;
		var helper = $.isFunction(o.helper) ? $(o.helper.apply(this.element[0], [event, this.currentItem])) : (o.helper == 'clone' ? this.currentItem.clone() : this.currentItem);

		if(!helper.parents('body').length) //Add the helper to the DOM if that didn't happen already
			$(o.appendTo != 'parent' ? o.appendTo : this.currentItem[0].parentNode)[0].appendChild(helper[0]);

		if(helper[0] == this.currentItem[0])
			this._storedCSS = { width: this.currentItem[0].style.width, height: this.currentItem[0].style.height, position: this.currentItem.css("position"), top: this.currentItem.css("top"), left: this.currentItem.css("left") };

		if(helper[0].style.width == '' || o.forceHelperSize) helper.width(this.currentItem.width());
		if(helper[0].style.height == '' || o.forceHelperSize) helper.height(this.currentItem.height());

		return helper;

	},

	_adjustOffsetFromHelper: function(obj) {
		if (typeof obj == 'string') {
			obj = obj.split(' ');
		}
		if ($.isArray(obj)) {
			obj = {left: +obj[0], top: +obj[1] || 0};
		}
		if ('left' in obj) {
			this.offset.click.left = obj.left + this.margins.left;
		}
		if ('right' in obj) {
			this.offset.click.left = this.helperProportions.width - obj.right + this.margins.left;
		}
		if ('top' in obj) {
			this.offset.click.top = obj.top + this.margins.top;
		}
		if ('bottom' in obj) {
			this.offset.click.top = this.helperProportions.height - obj.bottom + this.margins.top;
		}
	},

	_getParentOffset: function() {


		//Get the offsetParent and cache its position
		this.offsetParent = this.helper.offsetParent();
		var po = this.offsetParent.offset();

		// This is a special case where we need to modify a offset calculated on start, since the following happened:
		// 1. The position of the helper is absolute, so it's position is calculated based on the next positioned parent
		// 2. The actual offset parent is a child of the scroll parent, and the scroll parent isn't the document, which means that
		//    the scroll is included in the initial calculation of the offset of the parent, and never recalculated upon drag
		if(this.cssPosition == 'absolute' && this.scrollParent[0] != document && $.contains(this.scrollParent[0], this.offsetParent[0])) {
			po.left += this.scrollParent.scrollLeft();
			po.top += this.scrollParent.scrollTop();
		}

		if((this.offsetParent[0] == document.body) //This needs to be actually done for all browsers, since pageX/pageY includes this information
		|| (this.offsetParent[0].tagName && this.offsetParent[0].tagName.toLowerCase() == 'html' && $.ui.ie)) //Ugly IE fix
			po = { top: 0, left: 0 };

		return {
			top: po.top + (parseInt(this.offsetParent.css("borderTopWidth"),10) || 0),
			left: po.left + (parseInt(this.offsetParent.css("borderLeftWidth"),10) || 0)
		};

	},

	_getRelativeOffset: function() {

		if(this.cssPosition == "relative") {
			var p = this.currentItem.position();
			return {
				top: p.top - (parseInt(this.helper.css("top"),10) || 0) + this.scrollParent.scrollTop(),
				left: p.left - (parseInt(this.helper.css("left"),10) || 0) + this.scrollParent.scrollLeft()
			};
		} else {
			return { top: 0, left: 0 };
		}

	},

	_cacheMargins: function() {
		this.margins = {
			left: (parseInt(this.currentItem.css("marginLeft"),10) || 0),
			top: (parseInt(this.currentItem.css("marginTop"),10) || 0)
		};
	},

	_cacheHelperProportions: function() {
		this.helperProportions = {
			width: this.helper.outerWidth(),
			height: this.helper.outerHeight()
		};
	},

	_setContainment: function() {

		var o = this.options;
		if(o.containment == 'parent') o.containment = this.helper[0].parentNode;
		if(o.containment == 'document' || o.containment == 'window') this.containment = [
			0 - this.offset.relative.left - this.offset.parent.left,
			0 - this.offset.relative.top - this.offset.parent.top,
			$(o.containment == 'document' ? document : window).width() - this.helperProportions.width - this.margins.left,
			($(o.containment == 'document' ? document : window).height() || document.body.parentNode.scrollHeight) - this.helperProportions.height - this.margins.top
		];

		if(!(/^(document|window|parent)$/).test(o.containment)) {
			var ce = $(o.containment)[0];
			var co = $(o.containment).offset();
			var over = ($(ce).css("overflow") != 'hidden');

			this.containment = [
				co.left + (parseInt($(ce).css("borderLeftWidth"),10) || 0) + (parseInt($(ce).css("paddingLeft"),10) || 0) - this.margins.left,
				co.top + (parseInt($(ce).css("borderTopWidth"),10) || 0) + (parseInt($(ce).css("paddingTop"),10) || 0) - this.margins.top,
				co.left+(over ? Math.max(ce.scrollWidth,ce.offsetWidth) : ce.offsetWidth) - (parseInt($(ce).css("borderLeftWidth"),10) || 0) - (parseInt($(ce).css("paddingRight"),10) || 0) - this.helperProportions.width - this.margins.left,
				co.top+(over ? Math.max(ce.scrollHeight,ce.offsetHeight) : ce.offsetHeight) - (parseInt($(ce).css("borderTopWidth"),10) || 0) - (parseInt($(ce).css("paddingBottom"),10) || 0) - this.helperProportions.height - this.margins.top
			];
		}

	},

	_convertPositionTo: function(d, pos) {

		if(!pos) pos = this.position;
		var mod = d == "absolute" ? 1 : -1;
		var o = this.options, scroll = this.cssPosition == 'absolute' && !(this.scrollParent[0] != document && $.contains(this.scrollParent[0], this.offsetParent[0])) ? this.offsetParent : this.scrollParent, scrollIsRootNode = (/(html|body)/i).test(scroll[0].tagName);

		return {
			top: (
				pos.top																	// The absolute mouse position
				+ this.offset.relative.top * mod										// Only for relative positioned nodes: Relative offset from element to offset parent
				+ this.offset.parent.top * mod											// The offsetParent's offset without borders (offset + border)
				- ( ( this.cssPosition == 'fixed' ? -this.scrollParent.scrollTop() : ( scrollIsRootNode ? 0 : scroll.scrollTop() ) ) * mod)
			),
			left: (
				pos.left																// The absolute mouse position
				+ this.offset.relative.left * mod										// Only for relative positioned nodes: Relative offset from element to offset parent
				+ this.offset.parent.left * mod											// The offsetParent's offset without borders (offset + border)
				- ( ( this.cssPosition == 'fixed' ? -this.scrollParent.scrollLeft() : scrollIsRootNode ? 0 : scroll.scrollLeft() ) * mod)
			)
		};

	},

	_generatePosition: function(event) {

		var o = this.options, scroll = this.cssPosition == 'absolute' && !(this.scrollParent[0] != document && $.contains(this.scrollParent[0], this.offsetParent[0])) ? this.offsetParent : this.scrollParent, scrollIsRootNode = (/(html|body)/i).test(scroll[0].tagName);

		// This is another very weird special case that only happens for relative elements:
		// 1. If the css position is relative
		// 2. and the scroll parent is the document or similar to the offset parent
		// we have to refresh the relative offset during the scroll so there are no jumps
		if(this.cssPosition == 'relative' && !(this.scrollParent[0] != document && this.scrollParent[0] != this.offsetParent[0])) {
			this.offset.relative = this._getRelativeOffset();
		}

		var pageX = event.pageX;
		var pageY = event.pageY;

		/*
		 * - Position constraining -
		 * Constrain the position to a mix of grid, containment.
		 */

		if(this.originalPosition) { //If we are not dragging yet, we won't check for options

			if(this.containment) {
				if(event.pageX - this.offset.click.left < this.containment[0]) pageX = this.containment[0] + this.offset.click.left;
				if(event.pageY - this.offset.click.top < this.containment[1]) pageY = this.containment[1] + this.offset.click.top;
				if(event.pageX - this.offset.click.left > this.containment[2]) pageX = this.containment[2] + this.offset.click.left;
				if(event.pageY - this.offset.click.top > this.containment[3]) pageY = this.containment[3] + this.offset.click.top;
			}

			if(o.grid) {
				var top = this.originalPageY + Math.round((pageY - this.originalPageY) / o.grid[1]) * o.grid[1];
				pageY = this.containment ? (!(top - this.offset.click.top < this.containment[1] || top - this.offset.click.top > this.containment[3]) ? top : (!(top - this.offset.click.top < this.containment[1]) ? top - o.grid[1] : top + o.grid[1])) : top;

				var left = this.originalPageX + Math.round((pageX - this.originalPageX) / o.grid[0]) * o.grid[0];
				pageX = this.containment ? (!(left - this.offset.click.left < this.containment[0] || left - this.offset.click.left > this.containment[2]) ? left : (!(left - this.offset.click.left < this.containment[0]) ? left - o.grid[0] : left + o.grid[0])) : left;
			}

		}

		return {
			top: (
				pageY																// The absolute mouse position
				- this.offset.click.top													// Click offset (relative to the element)
				- this.offset.relative.top												// Only for relative positioned nodes: Relative offset from element to offset parent
				- this.offset.parent.top												// The offsetParent's offset without borders (offset + border)
				+ ( ( this.cssPosition == 'fixed' ? -this.scrollParent.scrollTop() : ( scrollIsRootNode ? 0 : scroll.scrollTop() ) ))
			),
			left: (
				pageX																// The absolute mouse position
				- this.offset.click.left												// Click offset (relative to the element)
				- this.offset.relative.left												// Only for relative positioned nodes: Relative offset from element to offset parent
				- this.offset.parent.left												// The offsetParent's offset without borders (offset + border)
				+ ( ( this.cssPosition == 'fixed' ? -this.scrollParent.scrollLeft() : scrollIsRootNode ? 0 : scroll.scrollLeft() ))
			)
		};

	},

	_rearrange: function(event, i, a, hardRefresh) {

		a ? a[0].appendChild(this.placeholder[0]) : i.item[0].parentNode.insertBefore(this.placeholder[0], (this.direction == 'down' ? i.item[0] : i.item[0].nextSibling));

		//Various things done here to improve the performance:
		// 1. we create a setTimeout, that calls refreshPositions
		// 2. on the instance, we have a counter variable, that get's higher after every append
		// 3. on the local scope, we copy the counter variable, and check in the timeout, if it's still the same
		// 4. this lets only the last addition to the timeout stack through
		this.counter = this.counter ? ++this.counter : 1;
		var counter = this.counter;

		this._delay(function() {
			if(counter == this.counter) this.refreshPositions(!hardRefresh); //Precompute after each DOM insertion, NOT on mousemove
		});

	},

	_clear: function(event, noPropagation) {

		this.reverting = false;
		// We delay all events that have to be triggered to after the point where the placeholder has been removed and
		// everything else normalized again
		var delayedTriggers = [];

		// We first have to update the dom position of the actual currentItem
		// Note: don't do it if the current item is already removed (by a user), or it gets reappended (see #4088)
		if(!this._noFinalSort && this.currentItem.parent().length) this.placeholder.before(this.currentItem);
		this._noFinalSort = null;

		if(this.helper[0] == this.currentItem[0]) {
			for(var i in this._storedCSS) {
				if(this._storedCSS[i] == 'auto' || this._storedCSS[i] == 'static') this._storedCSS[i] = '';
			}
			this.currentItem.css(this._storedCSS).removeClass("ui-sortable-helper");
		} else {
			this.currentItem.show();
		}

		if(this.fromOutside && !noPropagation) delayedTriggers.push(function(event) { this._trigger("receive", event, this._uiHash(this.fromOutside)); });
		if((this.fromOutside || this.domPosition.prev != this.currentItem.prev().not(".ui-sortable-helper")[0] || this.domPosition.parent != this.currentItem.parent()[0]) && !noPropagation) delayedTriggers.push(function(event) { this._trigger("update", event, this._uiHash()); }); //Trigger update callback if the DOM position has changed

		// Check if the items Container has Changed and trigger appropriate
		// events.
		if (this !== this.currentContainer) {
			if(!noPropagation) {
				delayedTriggers.push(function(event) { this._trigger("remove", event, this._uiHash()); });
				delayedTriggers.push((function(c) { return function(event) { c._trigger("receive", event, this._uiHash(this)); };  }).call(this, this.currentContainer));
				delayedTriggers.push((function(c) { return function(event) { c._trigger("update", event, this._uiHash(this));  }; }).call(this, this.currentContainer));
			}
		}


		//Post events to containers
		for (var i = this.containers.length - 1; i >= 0; i--){
			if(!noPropagation) delayedTriggers.push((function(c) { return function(event) { c._trigger("deactivate", event, this._uiHash(this)); };  }).call(this, this.containers[i]));
			if(this.containers[i].containerCache.over) {
				delayedTriggers.push((function(c) { return function(event) { c._trigger("out", event, this._uiHash(this)); };  }).call(this, this.containers[i]));
				this.containers[i].containerCache.over = 0;
			}
		}

		//Do what was originally in plugins
		if(this._storedCursor) $('body').css("cursor", this._storedCursor); //Reset cursor
		if(this._storedOpacity) this.helper.css("opacity", this._storedOpacity); //Reset opacity
		if(this._storedZIndex) this.helper.css("zIndex", this._storedZIndex == 'auto' ? '' : this._storedZIndex); //Reset z-index

		this.dragging = false;
		if(this.cancelHelperRemoval) {
			if(!noPropagation) {
				this._trigger("beforeStop", event, this._uiHash());
				for (var i=0; i < delayedTriggers.length; i++) { delayedTriggers[i].call(this, event); }; //Trigger all delayed events
				this._trigger("stop", event, this._uiHash());
			}

			this.fromOutside = false;
			return false;
		}

		if(!noPropagation) this._trigger("beforeStop", event, this._uiHash());

		//$(this.placeholder[0]).remove(); would have been the jQuery way - unfortunately, it unbinds ALL events from the original node!
		this.placeholder[0].parentNode.removeChild(this.placeholder[0]);

		if(this.helper[0] != this.currentItem[0]) this.helper.remove(); this.helper = null;

		if(!noPropagation) {
			for (var i=0; i < delayedTriggers.length; i++) { delayedTriggers[i].call(this, event); }; //Trigger all delayed events
			this._trigger("stop", event, this._uiHash());
		}

		this.fromOutside = false;
		return true;

	},

	_trigger: function() {
		if ($.Widget.prototype._trigger.apply(this, arguments) === false) {
			this.cancel();
		}
	},

	_uiHash: function(_inst) {
		var inst = _inst || this;
		return {
			helper: inst.helper,
			placeholder: inst.placeholder || $([]),
			position: inst.position,
			originalPosition: inst.originalPosition,
			offset: inst.positionAbs,
			item: inst.currentItem,
			sender: _inst ? _inst.element : null
		};
	}

});

})(jQuery);
/*

TO DO: Test in all browsers, clean up theme file, prepare documentation, minisite?

Uniform v1.5
Copyright © 2009 Josh Pyles / Pixelmatrix Design LLC
http://pixelmatrixdesign.com

Requires jQuery 1.3 or newer

Much thanks to Thomas Reynolds and Buck Wilson for their help and advice on this

Disabling text selection is made possible by Mathias Bynens <http://mathiasbynens.be/>
and his noSelect plugin. <http://github.com/mathiasbynens/noSelect-jQuery-Plugin>

Also, thanks to David Kaneda and Eugene Bond for their contributions to the plugin

License:
MIT License - http://www.opensource.org/licenses/mit-license.php

Usage:

$(function(){
  $("select, :radio, :checkbox").uniform();
});

You can customize the classes that Uniform uses:

$("select, :radio, :checkbox").uniform({
  selectClass: 'mySelectClass',
  radioClass: 'myRadioClass',
  checkboxClass: 'myCheckboxClass',
  checkedClass: 'myCheckedClass',
  focusClass: 'myFocusClass'
});

Enjoy!

*/

(function($) {
  $.uniform = {
    options: {
      spanClass:     'uniformSpan',
      selectClass:   'selector',
      radioClass: 'radio',
      checkboxClass: 'checker',
      fileClass: 'uploader',
      filenameClass: 'filename',
      fileBtnClass: 'action',
      fileDefaultText: 'No file selected',
      fileBtnText: 'Choose File',
      checkedClass: 'checked',
      focusClass: 'focus',
      disabledClass: 'disabled',
      activeClass: 'active',
      hoverClass: 'hover',
      useID: true,
      idPrefix: 'uniform',
      resetSelector: false
    },
    elements: []
  };

  if($.browser.msie && $.browser.version < 7){
    $.support.selectOpacity = false;
  }else{
    $.support.selectOpacity = true;
  }

  $.fn.uniform = function(options) {

    options = $.extend($.uniform.options, options);

    var el = this;
    //code for specifying a reset button
    if(options.resetSelector != false){
      $(options.resetSelector).mouseup(function(){
        function resetThis(){
          $.uniform.update(el);
        }
        setTimeout(resetThis, 10);
      });
    }

    function doSelect(elem){

      var divTag = $('<div />'),
          spanTag = $('<span />');

      divTag.addClass(options.selectClass);
      spanTag.addClass(options.spanClass);


      if(options.useID){
        divTag.attr("id", options.idPrefix+"-"+elem.attr("id"));
      }
      
      var selected = elem.find(":selected:first");
      if(selected.length == 0){
        selected = elem.find("option:first");
      }
      spanTag.text(selected.text());
      
      elem.css('opacity', 0);
      elem.wrap(divTag);
      elem.before(spanTag);

      //redefine variables
      divTag = elem.parent("div");
      spanTag = elem.siblings("span." + options.spanClass);

      elem.change(function() {
        spanTag.text(elem.find(":selected").text());
        divTag.removeClass(options.activeClass);
      })
      .focus(function() {
        divTag.addClass(options.focusClass);
      })
      .blur(function() {
        divTag.removeClass(options.focusClass);
        divTag.removeClass(options.activeClass);
      })
      .mousedown(function() {
        divTag.addClass(options.activeClass);
      })
      .mouseup(function() {
        divTag.removeClass(options.activeClass);
      })
      .click(function(){
        divTag.removeClass(options.activeClass);
      })
      .hover(function() {
        divTag.addClass(options.hoverClass);
      }, function() {
        divTag.removeClass(options.hoverClass);
      })
      .keyup(function(){
        spanTag.text(elem.find(":selected").text());
      });

      //handle disabled state
      if($(elem).attr("disabled")){
        //box is checked by default, check our box
        divTag.addClass(options.disabledClass);
      }
      $.uniform.noSelect(spanTag);
      
      storeElement(elem);

    }

    function doCheckbox(elem){

      var divTag = $('<div />'),
          spanTag = $('<span />');

      divTag.addClass(options.checkboxClass);
      spanTag.addClass(options.spanClass);

      //assign the id of the element
      if(options.useID){
        divTag.attr("id", options.idPrefix+"-"+elem.attr("id"));
      }

      //wrap with the proper elements
      $(elem).wrap(divTag);
      $(elem).wrap(spanTag);

      //redefine variables
      spanTag = elem.parent();
      divTag = spanTag.parent();

      //hide normal input and add focus classes
      $(elem)
      .css("opacity", 0)
      .focus(function(){

        divTag.addClass(options.focusClass);
      })
      .blur(function(){

        divTag.removeClass(options.focusClass);
      })
      .click(function(){

        if(!$(elem).attr("checked")){
          //box was just unchecked, uncheck span
          spanTag.removeClass(options.checkedClass);
        }else{
          //box was just checked, check span.
          spanTag.addClass(options.checkedClass);
        }
      })
      .mousedown(function() {
        divTag.addClass(options.activeClass);
      })
      .mouseup(function() {
        divTag.removeClass(options.activeClass);
      })
      .hover(function() {
        divTag.addClass(options.hoverClass);
      }, function() {
        divTag.removeClass(options.hoverClass);
      });

      //handle defaults
      if($(elem).attr("checked")){
        //box is checked by default, check our box
        spanTag.addClass(options.checkedClass);
      }

      //handle disabled state
      if($(elem).attr("disabled")){
        //box is checked by default, check our box
        divTag.addClass(options.disabledClass);
      }

      storeElement(elem);

    }

    function doRadio(elem){

      var divTag = $('<div />'),
          spanTag = $('<span />');

      divTag.addClass(options.radioClass);
      spanTag.addClass(options.spanClass);

      if(options.useID){
        divTag.attr("id", options.idPrefix+"-"+elem.attr("id"));
      }

      //wrap with the proper elements
      $(elem).wrap(divTag);
      $(elem).wrap(spanTag);

      //redefine variables
      spanTag = elem.parent();
      divTag = spanTag.parent();

      //hide normal input and add focus classes
      $(elem)
      .css("opacity", 0)
      .focus(function(){
        divTag.addClass(options.focusClass);
      })
      .blur(function(){
        divTag.removeClass(options.focusClass);
      })
      .click(function(){
        if(!$(elem).attr("checked")){
          //box was just unchecked, uncheck span
          spanTag.removeClass(options.checkedClass);
        }else{
          //box was just checked, check span
          $("."+options.radioClass + " span."+options.checkedClass + ":has([name='" + $(elem).attr('name') + "'])").removeClass(options.checkedClass);
          spanTag.addClass(options.checkedClass);
        }
      })
      .mousedown(function() {
        if(!$(elem).is(":disabled")){
          divTag.addClass(options.activeClass);
        }
      })
      .mouseup(function() {
        divTag.removeClass(options.activeClass);
      })
      .hover(function() {
        divTag.addClass(options.hoverClass);
      }, function() {
        divTag.removeClass(options.hoverClass);
      });

      //handle defaults
      if($(elem).attr("checked")){
        //box is checked by default, check span
        spanTag.addClass(options.checkedClass);
      }
      //handle disabled state
      if($(elem).attr("disabled")){
        //box is checked by default, check our box
        divTag.addClass(options.disabledClass);
      }

      storeElement(elem);

    }

    function doFile(elem){
      //sanitize input
      $el = $(elem);

      var divTag = $('<div />'),
          filenameTag = $('<span>'+options.fileDefaultText+'</span>'),
          btnTag = $('<span>'+options.fileBtnText+'</span>');

      divTag.addClass(options.fileClass);
      spanTag.addClass(options.spanClass);
      filenameTag.addClass(options.filenameClass);
      btnTag.addClass(options.fileBtnClass);

      if(options.useID){
        divTag.attr("id", options.idPrefix+"-"+$el.attr("id"));
      }

      //wrap with the proper elements
      $el.wrap(divTag);
      $el.after(btnTag);
      $el.after(filenameTag);

      //redefine variables
      divTag = $el.closest("div");
      filenameTag = $el.siblings("."+options.filenameClass);
      btnTag = $el.siblings("."+options.fileBtnClass);

      //set the size
      if(!$el.attr("size")){
        var divWidth = divTag.width();
        //$el.css("width", divWidth);
        $el.attr("size", divWidth/10);
      }

      //actions
      $el
      .css("opacity", 0)
      .focus(function(){
        divTag.addClass(options.focusClass);
      })
      .blur(function(){
        divTag.removeClass(options.focusClass);
      })
      .change(function(){
        var filename = $(this).val();
        filename = filename.split(/[\/\\]+/);
        filename = filename[(filename.length-1)];
        filenameTag.text(filename);
      })
      .mousedown(function() {
        if(!$(elem).is(":disabled")){
          divTag.addClass(options.activeClass);
        }
      })
      .mouseup(function() {
        divTag.removeClass(options.activeClass);
      })
      .hover(function() {
        divTag.addClass(options.hoverClass);
      }, function() {
        divTag.removeClass(options.hoverClass);
      });

      //handle defaults
      if($el.attr("disabled")){
        //box is checked by default, check our box
        divTag.addClass(options.disabledClass);
      }
      
      $.uniform.noSelect(filenameTag);
      $.uniform.noSelect(btnTag);
      storeElement(elem);

    }

    function storeElement(elem){
      //store this element in our global array
      elem = $(elem).get();
      if(elem.length > 1){
        $.each(elem, function(i, val){
          $.uniform.elements.push(val);
        });
      }else{
        $.uniform.elements.push(elem);
      }
    }
    
    //noSelect v1.0
    $.uniform.noSelect = function(elem) {
      function f() {
       return false;
      };
      $(elem).each(function() {
       this.onselectstart = this.ondragstart = f; // Webkit & IE
       $(this)
        .mousedown(f) // Webkit & Opera
        .css({ MozUserSelect: 'none' }); // Firefox
      });
     };

    $.uniform.update = function(elem){
      if(elem == undefined){
        elem = $($.uniform.elements);
      }
      //sanitize input
      elem = $(elem);

      elem.each(function(){
        //do to each item in the selector
        //function to reset all classes
        $e = $(this);

        if($e.is("select")){
          //element is a select
          spanTag = $e.siblings("span." + options.spanClass);
          divTag = $e.parent("div");

          divTag.removeClass(options.hoverClass+" "+options.focusClass+" "+options.activeClass);

          //reset current selected text
          spanTag.text($e.find(":selected").text());

          if($e.is(":disabled")){
            divTag.addClass(options.disabledClass);
          }else{
            divTag.removeClass(options.disabledClass);
          }

        }else if($e.is(":checkbox")){
          //element is a checkbox
          spanTag = $e.closest("span." + options.spanClass);
          divTag = $e.closest("div");

          divTag.removeClass(options.hoverClass+" "+options.focusClass+" "+options.activeClass);
          spanTag.removeClass(options.checkedClass);

          if($e.is(":checked")){
            spanTag.addClass(options.checkedClass);
          }
          if($e.is(":disabled")){
            divTag.addClass(options.disabledClass);
          }else{
            divTag.removeClass(options.disabledClass);
          }

        }else if($e.is(":radio")){
          //element is a radio
          spanTag = $e.closest("span." + options.spanClass);
          divTag = $e.closest("div");

          divTag.removeClass(options.hoverClass+" "+options.focusClass+" "+options.activeClass);
          spanTag.removeClass(options.checkedClass);

          if($e.is(":checked")){
            spanTag.addClass(options.checkedClass);
          }

          if($e.is(":disabled")){
            divTag.addClass(options.disabledClass);
          }else{
            divTag.removeClass(options.disabledClass);
          }
        }else if($e.is(":file")){
          divTag = $e.parent("div");
          filenameTag = $e.siblings(options.filenameClass);
          btnTag = $e.siblings(options.fileBtnClass);

          divTag.removeClass(options.hoverClass+" "+options.focusClass+" "+options.activeClass);

          filenameTag.text($e.val());

          if($e.is(":disabled")){
            divTag.addClass(options.disabledClass);
          }else{
            divTag.removeClass(options.disabledClass);
          }
        }
      });
    }

    return this.each(function() {
	  var elem = $(this);

	  if (elem.is('.has-uniform')) return;

      if($.support.selectOpacity){

        if(elem.is("select")){
          //element is a select
          if(elem.attr("multiple") != true){
            //element is not a multi-select
            doSelect(elem);
          }
        }else if(elem.is(":checkbox")){
          //element is a checkbox
          doCheckbox(elem);
        }else if(elem.is(":radio")){
          //element is a radio
          doRadio(elem);
        }else if(elem.is(":file")){
          //element is a file upload
          doFile(elem);
        }

        elem.addClass('has-uniform');

      }
    });
  };
})(jQuery);
/*
 * Better Select Multiple - jQuery Plugin
 *
 * based on Alternate Select Multiple (asmSelect) 1.0.4a beta (http://www.ryancramer.com/projects/asmselect/)
 * 
 * Copyright (c) 2009 by Ryan Cramer - http://www.ryancramer.com
 * Copyright (c) 2010 by Victor Berchet - http://www.github.com/vicb
 *
 * Dual licensed under the MIT (MIT-LICENSE.txt) and GPL (GPL-LICENSE.txt) licenses.
 *
 * bsmSelect version:
 * 
 *   latest:
 *    - improved custom animations
 *    - support for optgroup
 *    - ability to set the default select title via the configuration
 *    - make the original label point to the new select
 *    - ability to customize the way list label gets extracted from the option
 *    
 *   v1.0 - 2010-07-02:
 *    - initial release
 *
 */

(function($) {

  $.fn.bsmSelect = function(customOptions) {

    var options = $.extend({}, $.fn.bsmSelect.conf, customOptions);

    return this.each(function(index) {

      var conf = $.extend({}, options, {
        $original:      $(this),          // the original select multiple
        $container:     null,             // a container that is wrapped around our widget
        $select:        null,             // the new select we have created
        $selectRemoved: null,
        $ol:            null,             // the list that we are manipulating
        buildingSelect: false,            // is the new select being constructed right now?
        ieClick:        false,            // in IE, has a click event occurred? ignore if not
        ignoreOriginalChangeEvent: false, // originalChangeEvent bypassed when this is true
        index:          index,            // Select Index (internal)
        optIndex:       0                 // Option Index (internal)
      });
      
      // do nothing if this <select> has already been bsm'd
      if (conf.$original.hasClass(conf.originalClass)) return;

      // this loop ensures uniqueness, in case of existing bsmSelects placed by ajax (1.0.3)
      while($('#' + conf.containerClass + conf.index).size()) { conf.index++; }

      conf.$select = $('<select>', {
        'class': conf.selectClass,
        name: conf.selectClass + conf.index,
        id: conf.selectClass + conf.index,
        change: function(e) {selectChangeEvent.call(this, e, conf);},
        click: function(e) {selectClickEvent.call(this, e, conf);}
      });

      conf.$selectRemoved = $('<select>');
      
      if (typeof conf.listType == 'function') {
          conf.$ol = conf.listType(conf.$original).addClass(conf.listClass);
      } else {
          conf.$ol = $('<' + conf.listType + '>', {
              'class' : conf.listClass,
              id: conf.listClass + conf.index
            });
      }
      
      conf.$container = $('<div>', {
        'class':  conf.containerClass,
        id: conf.containerClass + conf.index
      });

      buildSelect(conf);

      conf.$original
        .addClass(conf.originalClass)
        .change(function(e) {originalChangeEvent.call(this, e, conf);})
        .wrap(conf.$container).before(conf.$select);
        
      // if the list isn't already in the document, add it (it might be inserted by a custom callback)
      if (! conf.$ol.parent().length) conf.$original.before(conf.$ol);
      
      if (conf.$original.attr('id')) {
          $('label[for=' + conf.$original.attr('id') + ']').attr('for', conf.$select.attr('id'));
      }

      if (conf.sortable) { $.fn.bsmSelect.plugins.makeSortable(conf); }
      
      // set up remove event (may be a link, or the LI itself)
      conf.$ol.delegate('.' + conf.removeClass, 'click', function() {
          dropListItem($(this).closest('li').attr('rel'), conf);
          return false;
      });
    });
  };

  function selectChangeEvent(e, conf) {

    // an item has been selected on the regular select we created
    // check to make sure it's not an IE screwup, and add it to the list

    if ($.browser.msie && $.browser.version < 7 && !conf.ieClick) { return; }
    var id = $(this).find('option:selected:eq(0)').attr('rel');
    addListItem(id, conf);
    conf.ieClick = false;
    triggerOriginalChange(id, 'add', conf); // for use by user-defined callbacks
  }

  function selectClickEvent(e, conf) {

    // IE6 lets you scroll around in a select without it being pulled down
    // making sure a click preceded the change() event reduces the chance
    // if unintended items being added. there may be a better solution?
    conf.ieClick = true;
  }

  function originalChangeEvent(e, conf) {

    // select or option change event manually triggered
    // on the original <select multiple>, so rebuild ours

    if (conf.ignoreOriginalChangeEvent) {
      conf.ignoreOriginalChangeEvent = false;
      return;
    }

    conf.$select.empty();
    conf.$ol.empty();
    buildSelect(conf);

    // opera has an issue where it needs a force redraw, otherwise
    // the items won't appear until something else forces a redraw
    if ($.browser.opera) { conf.$ol.hide().fadeIn('fast'); }
  }

  function buildSelect(conf) {

    // build or rebuild the new select that the user
    // will select items from

    conf.buildingSelect = true;
    conf.optIndex = 0;

    // add a first option to be the home option / default selectLabel
    conf.$select.prepend($('<option>').text(conf.$original.attr('title') || conf.title));

    conf.$original.children().each(function() {
      if ($(this).is('option')) {
        addSelectOption(conf.$select, $(this), conf);
      } else if ($(this).is('optgroup')) {
        addSelectOptionGroup(conf.$select, $(this), conf);
      }
    });

    if (!conf.debugMode) { conf.$original.hide(); } // IE6 requires this on every buildSelect()
    selectFirstItem(conf);
    conf.buildingSelect = false;
  }

  function addSelectOption($parent, $option, conf) {

    // Add an option to the parent

    if (!$option.attr('id')) { $option.attr('id', 'bsm' + conf.index + 'option' + conf.optIndex); }    
    var id = $option.attr('id');

    var $O = $('<option>', {
      text: $option.text(),
      val: $option.val(),
      rel: id
    }).appendTo($parent);

    if ($option.is(':selected')) {
      addListItem(id, conf);
      disableSelectOption($O, conf);
    }
    conf.optIndex++;
  }

  function addSelectOptionGroup($select, $group, conf)
  {

    // Add an option group to the select input

    $G = $('<optgroup>', { label: $group.attr('label')} ).appendTo($select);

    if ($group.is(':disabled')) { $G.attr('disabled', 'disabled'); }

    $group.find('option').each(function(i, option) {
      addSelectOption($G, $(option), conf);
    });
  }

  function selectFirstItem(conf) {

    // select the firm item from the regular select that we created

    conf.$select.find('option:eq(0)').attr('selected', 'selected');
  }

  function disableSelectOption($option, conf) {

    // make an option disabled, indicating that it's already been selected
    // because safari is the only browser that makes disabled items look 'disabled'
    // we apply a class that reproduces the disabled look in other browsers

    $option.addClass(conf.optionDisabledClass)
      .removeAttr('selected')
      .attr('disabled', 'disabled');

    if (conf.hideWhenAdded) { $option.hide(); }
    if ($.browser.msie) { conf.$select.hide().show(); } // this forces IE to update display
  }

  function enableSelectOption($option, conf) {

    // given an already disabled select option, enable it

    $option.removeClass(conf.optionDisabledClass).removeAttr('disabled');

    if (conf.hideWhenAdded) { $option.show(); }
    if ($.browser.msie) { conf.$select.hide().show(); } // this forces IE to update display
  }

  function addListItem(optionId, conf) {

    // add a new item to the html list

    var $O = $('#' + optionId);

    if (!$O) { return; } // this is the first item, selectLabel

    if (!conf.buildingSelect) {
      if ($O.is(':selected')) { return; } // already have it
      $O.attr('selected', 'selected');
    }

    var $removeLink = $('<a>', {
      href: '#',
      'class': conf.removeClass
    }).prepend(conf.removeLabel);

    var $itemLabel = $('<span>', {
      'class': conf.listItemLabelClass,
      html: conf.extractLabel($O, conf)
    });

    var $item = $('<li>', {
      rel:  optionId,
      'class': conf.listItemClass
    }).append($itemLabel)
      .append($removeLink)
      .hide();

    if (conf.addItemTarget == 'top' && !conf.buildingSelect) {
      conf.$ol.prepend($item);
      if (conf.sortable) { conf.$original.prepend($O); }
    } else {
      conf.$ol.append($item);
      if (conf.sortable) { conf.$original.append($O); }
    }

    addListItemShow($item, conf);

    disableSelectOption($('[rel=' + optionId + ']', conf.$select), conf);

    if (!conf.buildingSelect) {
      setHighlight($item, conf.highlightAddedLabel, conf);
      selectFirstItem(conf);
      if (conf.sortable) { conf.$ol.sortable('refresh'); }
    }

  }

  function addListItemShow($item, conf) {

    // reveal the currently hidden item with optional animation
    // used only by addListItem()

    if (!conf.buildingSelect) {
      if (conf.animate === true) {
        $.fn.bsmSelect.effects.verticalListAdd($item);
      } else if ($.isFunction(conf.animate.add)) {
        conf.animate.add($item);
      } else if (typeof(conf.animate.add) == 'string' && $.isFunction($.fn.bsmSelect.effects[conf.animate.add])) {
        $.fn.bsmSelect.effects[conf.animate.add]($item); 
      } else { 
        $item.show();
      }
    } else {
      $item.show();
    }
  }

  function dropListItem(optionId, conf, _highlightItem) {

    // remove an item from the html list

    var highlightItem = typeof(_highlightItem) == 'undefined'?true:_highlightItem;
    var $O = $('#' + optionId);

    $O.removeAttr('selected');
    var $item = conf.$ol.children('li[rel=' + optionId + ']');

    dropListItemHide($item, conf);
    enableSelectOption($('[rel=' + optionId + ']', conf.removeWhenAdded ? conf.$selectRemoved : conf.$select), conf);

    if (highlightItem) { setHighlight($item, conf.highlightRemovedLabel, conf); }

    triggerOriginalChange(optionId, 'drop', conf);

  }

  function dropListItemHide($item, conf) {

    // remove the currently visible item with optional animation
    // used only by dropListItem()

    if (!conf.buildingSelect) {
      if (conf.animate === true) {
        $.fn.bsmSelect.effects.verticalListRemove($item);
      } else if ($.isFunction(conf.animate.drop)) {
        conf.animate.drop($item);
      } else if (typeof(conf.animate.drop) == 'string' && $.isFunction($.fn.bsmSelect.effects[conf.animate.drop])) {
        $.fn.bsmSelect.effects[conf.animate.drop]($item);
      } else {
        $item.remove();
      }
    } else {
      $item.remove();
    }
  }

  function setHighlight($item, label, conf) {

    if (conf.highlight === true) {
      $.fn.bsmSelect.effects.highlight($item, label, conf);
    } else if ($.isFunction(conf.highlight)) {
      conf.highlight($item, label, conf);
    } else if (typeof(conf.highlight) == 'string' && $.isFunction($.fn.bsmSelect.effects[conf.highlight])) {
      $.fn.bsmSelect.effects[conf.highlight]($item, label, conf);
    }    
  }

  function triggerOriginalChange(optionId, type, conf) {

    // trigger a change event on the original select multiple
    // so that other scripts can pick them up

    conf.ignoreOriginalChangeEvent = true;
    var $option = $('#' + optionId);

    conf.$original.trigger('change', [{
      'option': $option,
      'value': $option.val(),
      'id': optionId,
      'item': conf.$ol.children('[rel=' + optionId + ']'),
      'type': type
    }]);
  }

  
  $.extend($.fn.bsmSelect, {
    // Default configuration
    conf: {
      listType: 'ol',                             // Ordered list 'ol', or unordered list 'ul'
                                                  // or a function that accepts the original <select> and returns
                                                  // a jQuery object with a single <ul>
                                                  // 
      sortable: false,                            // Should the list be sortable?
      highlight: false,                           // Use the highlight feature?
      animate: false,                             // Animate the the adding/removing of items in the list?
      addItemTarget: 'bottom',                    // Where to place new selected items in list: top or bottom
      hideWhenAdded: false,                       // Hide the option when added to the list? works only in FF
      debugMode: false,                           // Debug mode keeps original select visible

      title: 'Select...',                         // Text used for the default select label
      removeLabel: 'remove',                      // Text used in the 'remove' link
      highlightAddedLabel: 'Added: ',             // Text that precedes highlight of added item
      highlightRemovedLabel: 'Removed: ',         // Text that precedes highlight of removed item
      extractLabel: function($option) { return $option.html(); },

      containerClass: 'bsmContainer',             // Class for container that wraps this widget
      selectClass: 'bsmSelect',                   // Class for the newly created <select>
      optionDisabledClass: 'bsmOptionDisabled',   // Class for items that are already selected / disabled
      listClass: 'bsmList',                       // Class for the list ($ol)
      listSortableClass: 'bsmListSortable',       // Another class given to the list when it is sortable
      listItemClass: 'bsmListItem',               // Class for the <li> list items
      listItemLabelClass: 'bsmListItemLabel',     // Class for the label text that appears in list items
      removeClass: 'bsmListItemRemove',           // Class given to the 'remove' link
      highlightClass: 'bsmHighlight',             // Class given to the highlight <span>
      originalClass: 'bsmOriginalSelect'          // Class applied to the original <select>
    },
    // Plugins
    plugins: {
      makeSortable:  function(conf) {
        // make any items in the selected list sortable
        // requires jQuery UI sortables, draggables, droppables

        conf.$ol.sortable({
          items: 'li.' + conf.listItemClass,
          handle: '.' + conf.listItemLabelClass,
          axis: 'y',
          update: function(e, data) {

            var updatedOptionId;

            $(this).children('li').each(function(n) {

              var $option = $('#' + $(this).attr('rel'));

              if ($(this).is('.ui-sortable-helper')) {
                updatedOptionId = $option.attr('id');
                return;
              }

              conf.$original.append($option);
            });

            if (updatedOptionId) { triggerOriginalChange(updatedOptionId, 'sort', conf); }
          }

        }).addClass(conf.listSortableClass);
      }
    },
    effects: {
      highlight: function ($item, label, conf) {
        conf.$select.next('#' + conf.highlightClass + conf.index).remove();

        var $highlight = $('<span>', {
          'class': conf.highlightClass,
          id: conf.highlightClass + conf.index,
          html: label + $item.children('.' + conf.listItemLabelClass).eq(0).text()
        }).hide();

        conf.$select.after($highlight.fadeIn('fast').delay(50).fadeOut('slow', function() {$(this).remove();}));
      },
      verticalListAdd: function ($el) {
        $el.animate({
          opacity: 'show',
          height: 'show'
        }, 100, 'swing', function() {
          $el.animate({
            height: '+=2px'
          }, 50, 'swing', function() {
            $el.animate({
              height: '-=2px'
            }, 25, 'swing');
          });
        });
      },
      verticalListRemove: function($el) {
        var $prevItem = $el.prev('li');
        $el.animate({
          opacity: 'hide',
          height: 'hide'
        }, 100, 'linear', function() {
          $prevItem.animate({
            height: '-=2px'
          }, 50, 'swing', function() {
            $prevItem.animate({
              height: '+=2px'
            }, 100, 'swing');
          });
          $el.remove();
        });
      }
    }
  });

})(jQuery);// http://jdbartlett.github.com/innershiv
window.innerShiv=(function(){var d,r;return function(h,u){if(!d){d=document.createElement('div');r=document.createDocumentFragment();/*@cc_on d.style.display = 'none'@*/}var e=d.cloneNode(true);/*@cc_on document.body.appendChild(e);@*/e.innerHTML=h;/*@cc_on document.body.removeChild(e);@*/if(u===false)return e.childNodes;var f=r.cloneNode(true),i=e.childNodes.length;while(i--)f.appendChild(e.firstChild);return f}}());/*
 * A time picker for jQuery
 * Based on original timePicker by Sam Collet (http://www.texotela.co.uk) -
 * copyright (c) 2006 Sam Collett (http://www.texotela.co.uk)
 *
 * Dual licensed under the MIT and GPL licenses.
 * Copyright (c) 2009 Anders Fajerson
 * @name     timePicker
 * @version  0.2
 * @author   Anders Fajerson (http://perifer.se)
 * @example  $("#mytime").timePicker();
 * @example  $("#mytime").timePicker({step:30, startTime:"15:00", endTime:"18:00"});
 */

(function($){
  $.fn.timePicker = function(options) {
    // Build main options before element iteration
    var settings = $.extend({}, $.fn.timePicker.defaults, options);

    return this.each(function() {
      $.timePicker(this, settings);
    });
  };

  $.timePicker = function (elm, settings) {
    var e = $(elm)[0];
    return e.timePicker || (e.timePicker = new jQuery._timePicker(e, settings));
  };

  $._timePicker = function(elm, settings) {

    var tpOver = false;
    var keyDown = false;
    var timeOnOpen = elm.value;
    var startTime = timeToDate(settings.startTime, settings);
    var endTime = timeToDate(settings.endTime, settings);

    $(elm).attr('autocomplete', 'OFF'); // Disable browser autocomplete

    var times = [];
    var time = new Date(startTime); // Create a new date object.
    while(time <= endTime) {
      times[times.length] = formatTime(time, settings);
      time = new Date(time.setMinutes(time.getMinutes() + settings.step));
    }

    var $tpDiv = $('<div class="time-picker'+ (settings.show24Hours ? '' : ' time-picker-12hours') +'"></div>');
    var $tpScrollable = $('<div class="time-picker-container"></div>');
    $tpDiv.append($tpScrollable);
    var $tpList = $('<ul></ul>');

    // Build the list.
    for(var i = 0; i < times.length; i++) {
      var listItem = $("<li>" + times[i] + "</li>");
      listItem.data('time', times[i]);
      $tpList.append(listItem);
    }
    $tpScrollable.append($tpList);
    // Append the timPicker to the body and position it.
    $tpDiv.appendTo('body').hide();

    // Store the mouse state, used by the blur event. Use mouseover instead of
    // mousedown since Opera fires blur before mousedown.
    $tpDiv.mouseover(function() {
      tpOver = true;
    }).mouseout(function() {
      tpOver = false;
    });

    $("li", $tpList).mouseover(function() {
      if (!keyDown) {
        $("li.selected", $tpScrollable).removeClass("selected");
        $(this).addClass("selected");
      }
    }).mousedown(function() {
       tpOver = true;
    }).click(function() {
      setTimeVal(elm, this, $tpDiv, settings);
      tpOver = false;
      if (settings.blurOnSelect) elm.blur();
    });

    var showPicker = function() {
      if ($tpDiv.is(":visible")) {
        return false;
      }
      $("li", $tpDiv).removeClass("selected");

      // Position
      var elmOffset = $(elm).offset();
      $tpDiv.css({'top':elmOffset.top + elm.offsetHeight, 'left':elmOffset.left});

      // Show picker. This has to be done before scrollTop is set since that
      // can't be done on hidden elements.
      $tpDiv.show();

      //store time shown when timepicker was opened.
      timeOnOpen = elm.value;

      // Try to find a time in the list that matches the entered time.
      var time = elm.value ? timeStringToDate(elm.value, settings) : startTime;
      var startMin = startTime.getHours() * 60 + startTime.getMinutes();
      var min = (time.getHours() * 60 + time.getMinutes()) - startMin;
      var steps = Math.round(min / settings.step);
      var roundTime = normaliseTime(new Date(0, 0, 0, 0, (steps * settings.step + startMin), 0));
      roundTime = (startTime < roundTime && roundTime <= endTime) ? roundTime : startTime;
      var formattedTime = formatTime(roundTime, settings);
      var $matchedTime = $tpDiv.find('li').filter(function() {
        return ($(this).data('time') == formattedTime);
      });

      if ($matchedTime.length) {
        $matchedTime.addClass("selected");
        $tpScrollable[0].scrollTop = $matchedTime[0].offsetTop;
      }

      return true;
    };
    // Attach to click as well as focus so timePicker can be shown again when
    // clicking on the input when it already has focus.
    $(elm).focus(showPicker).click(showPicker);
    // Hide timepicker on blur
    $(elm).blur(function() {
      if (!tpOver) {
        $tpDiv.hide();
      }
    });
    // Keypress doesn't repeat on Safari for non-text keys.
    // Keydown doesn't repeat on Firefox and Opera on Mac.
    // Using kepress for Opera and Firefox and keydown for the rest seems to
    // work with up/down/enter/esc.
    var event = ($.browser.opera || $.browser.mozilla) ? 'keypress' : 'keydown';
    $(elm)[event](function(e) {
      var $selected;
      keyDown = true;
      var top = $tpScrollable[0].scrollTop;
      switch (e.keyCode) {
        case 38: // Up arrow.
          // Just show picker if it's hidden.
          if (showPicker()) {
            return false;
          };
          $selected = $("li.selected", $tpList);
          var prev = $selected.prev().addClass("selected")[0];
          if (prev) {
            $selected.removeClass("selected");
            // Scroll item into view.
            if (prev.offsetTop < top) {
              $tpScrollable[0].scrollTop = top - prev.offsetHeight;
            }
          }
          else {
            // Loop to next item.
            $selected.removeClass("selected");
            prev = $("li:last", $tpList).addClass("selected")[0];
            $tpScrollable[0].scrollTop = prev.offsetTop - prev.offsetHeight;
          }
          return false;
          break;
        case 40: // Down arrow, similar in behaviour to up arrow.
          if (showPicker()) {
            return false;
          };
          $selected = $("li.selected", $tpList);
          var next = $selected.next().addClass("selected")[0];
          if (next) {
            $selected.removeClass("selected");
            if (next.offsetTop + next.offsetHeight > top + $tpScrollable[0].offsetHeight) {
              $tpScrollable[0].scrollTop = top + next.offsetHeight;
            }
          }
          else {
            $selected.removeClass("selected");
            next = $("li:first", $tpList).addClass("selected")[0];
            $tpScrollable[0].scrollTop = 0;
          }
          return false;
          break;
        case 13: // Enter
          if ($tpDiv.is(":visible")) {

            if (elm.value != timeOnOpen && !tpOver)
              $(elm).change();
            else {
              var sel = $("li.selected", $tpList)[0];
              setTimeVal(elm, sel, $tpDiv, settings);
            }
            //blurring the timepicker also closes it.
            elm.blur();
            //called here because the timepicker is hidden before the keyup event can trigger
            keyDown = false;
          }
          return false;
          break;
        case 27: // Esc
          $tpDiv.hide();
          keyDown = false;
          return false;
          break;
      }
      return true;
    });
    $(elm).keyup(function(e) {
      keyDown = false;
    });
    // Helper function to get an inputs current time as Date object.
    // Returns a Date object.
    this.getTime = function() {
      return timeStringToDate(elm.value, settings);
    };
    // Helper function to set a time input.
    // Takes a Date object.
    this.setTime = function(time) {
      elm.value = formatTime(normaliseTime(time), settings);
      // Trigger element's change events.
      $(elm).change();
    };

  }; // End fn;

  // Plugin defaults.
  $.fn.timePicker.defaults = {
    step:30,
    startTime: new Date(0, 0, 0, 0, 0, 0),
    endTime: new Date(0, 0, 0, 23, 30, 0),
    separator: ':',
    leadingZeros: false,
    show24Hours: true,
    blurOnSelect: false
  };

  // Private functions.

  function setTimeVal(elm, sel, $tpDiv, settings) {
    // Update input field
    elm.value = $(sel).text();
    // Trigger element's change events.
    $(elm).change();
    // Keep focus for all but IE (which doesn't like it)
    if (!$.browser.msie) {
      elm.focus();
    }
    // Hide picker
    $tpDiv.hide();
  }

  function formatTime(time, settings) {
    var h = time.getHours(),
        hours = settings.show24Hours ? h : (((h + 11) % 12) + 1),
        minutes = time.getMinutes();
    
    // pad with zeros if necessary
    minutes = (minutes < 10 ? '0' : '') + minutes;
    hours = (hours < 10 && settings.leadingZeros ? '0' : '') + hours;
        
    return hours + settings.separator + minutes + (settings.show24Hours ? '' : ((h < 12) ? ' AM' : ' PM'));
  }

  function timeToDate(input, settings) {
    return (typeof input == 'object') ? normaliseTime(input) : timeStringToDate(input, settings);
  }

  function timeStringToDate(input, settings) {
    if (input) {
      var array = input.split(settings.separator);
      var hours = parseFloat(array[0]);
      var minutes = parseFloat(array[1]);

      // Convert AM/PM hour to 24-hour format.
      if (!settings.show24Hours) {
        if (hours === 12 && input.indexOf('AM') !== -1) {
          hours = 0;
        }
        else if (hours !== 12 && input.indexOf('PM') !== -1) {
          hours += 12;
        }
      }
      var time = new Date(0, 0, 0, hours, minutes, 0);
      return normaliseTime(time);
    }
    return null;
  }

  /* Normalise time object to a common date. */
  function normaliseTime(time) {
    time.setFullYear(2001);
    time.setMonth(0);
    time.setDate(0);
    return time;
  }

})(jQuery);
/*
 * Mentions Input
 * Version 1.0.2
 * Written by: Kenneth Auchenberg (Podio)
 *
 * Using underscore.js
 *
 * License: MIT License - http://www.opensource.org/licenses/mit-license.php
 */

(function ($, _, undefined) {

  // Settings
  var KEY = { BACKSPACE : 8, TAB : 9, RETURN : 13, ESC : 27, LEFT : 37, UP : 38, RIGHT : 39, DOWN : 40, COMMA : 188, SPACE : 32, HOME : 36, END : 35 }; // Keys "enum"
  var defaultSettings = {
    triggerChar   : '@',
    onDataRequest : $.noop,
    minChars      : 2,
    showAvatars   : true,
    elastic       : true,
    onCaret       : false,
    minCharsNoTrigger : 4,   // trigger complete after this many chars without a space; false to disable
    searchDelay       : 300, // won't start searching until there's no typing for Xms
    spaceResetDelay   : 200, // won't reset on space if typing within Xms
    onTriggerChar     : null, // callback when the user hits the triggerChar, for displaying initial lists
    classes       : {
      autoCompleteItemActive : "active"
    },
    templates     : {
      wrapper                    : _.template('<div class="mentions-input-box"></div>'),
      autocompleteList           : _.template('<div class="mentions-autocomplete-list"></div>'),
      autocompleteListItem       : _.template('<li data-ref-id="<%= id %>" data-ref-type="<%= type %>" data-display="<%= display %>"><%= content %></li>'),
      autocompleteListItemAvatar : _.template('<img src="<%= avatar %>" />'),
      autocompleteListItemIcon   : _.template('<div class="icon <%= icon %>"></div>'),
      mentionsOverlay            : _.template('<div class="mentions"><div></div></div>'),
      mentionItemSyntax          : _.template('@[<%= value %>](<%= type %>:<%= id %>)'),
      mentionItemHighlight       : _.template('<strong><span><%= value %></span></strong>')
    }
  };

  var utils = {
    htmlEncode       : function (str) {
      return _.escape(str);
    },
    highlightTerm    : function (value, term) {
      if (!term && !term.length) {
        return value;
      }
      return value.replace(new RegExp("(?![^&;]+;)(?!<[^<>]*)(" + term + ")(?![^<>]*>)(?![^&;]+;)", "gi"), "<b>$1</b>");
    },
    setCaratPosition : function (domNode, caretPos) {
      if (domNode.createTextRange) {
        var range = domNode.createTextRange();
        range.move('character', caretPos);
        range.select();
      } else {
        if (domNode.selectionStart) {
          domNode.focus();
          domNode.setSelectionRange(caretPos, caretPos);
        } else {
          domNode.focus();
        }
      }
    },
    rtrim: function(string) {
      return string.replace(/\s+$/,"");
    }
  };

  var MentionsInput = function (settings) {

    var domInput, elmInputBox, elmInputWrapper, elmAutocompleteList, elmWrapperBox, elmMentionsOverlay, elmActiveAutoCompleteItem;
    var mentionsCollection = [];
    var autocompleteItemCollection = {};
    var inputBuffer = [];
    var currentDataQuery;
    var timeout = null;

    settings = $.extend(true, {}, defaultSettings, settings );

    function initTextarea() {
      elmInputBox = $(domInput);

      if (elmInputBox.attr('data-mentions-input') == 'true') {
        return;
      }

      elmInputWrapper = elmInputBox.parent();
      elmWrapperBox = $(settings.templates.wrapper());
      elmInputBox.wrapAll(elmWrapperBox);
      elmWrapperBox = elmInputWrapper.find('> div.mentions-input-box');

      elmInputBox.attr('data-mentions-input', 'true');
      elmInputBox.bind('keydown', onInputBoxKeyDown);
      elmInputBox.bind('keypress', onInputBoxKeyPress);
      elmInputBox.bind('input', onInputBoxInput);
      elmInputBox.bind('click', onInputBoxClick);
      elmInputBox.bind('blur', onInputBoxBlur);

      // Elastic textareas, internal setting for the Dispora guys
      if( settings.elastic ) {
        elmInputBox.elastic();
      }

    }

    function initAutocomplete() {
      elmAutocompleteList = $(settings.templates.autocompleteList());
      elmAutocompleteList.appendTo(elmWrapperBox);
      elmAutocompleteList.delegate('li', 'mousedown', onAutoCompleteItemClick);
    }

    function initMentionsOverlay() {
      elmMentionsOverlay = $(settings.templates.mentionsOverlay());
      elmMentionsOverlay.prependTo(elmWrapperBox);
    }

    function updateValues() {
      var syntaxMessage = getInputBoxValue();

      _.each(mentionsCollection, function (mention) {
        var textSyntax = settings.templates.mentionItemSyntax(mention);
        syntaxMessage = syntaxMessage.replace(mention.value, textSyntax);
      });

      var mentionText = utils.htmlEncode(syntaxMessage);

      _.each(mentionsCollection, function (mention) {
        var formattedMention = _.extend({}, mention, {value: utils.htmlEncode(mention.value)});
        var textSyntax = settings.templates.mentionItemSyntax(formattedMention);
        var textHighlight = settings.templates.mentionItemHighlight(formattedMention);

        mentionText = mentionText.replace(textSyntax, textHighlight);
      });

      mentionText = mentionText.replace(/\n/g, '<br />');
      mentionText = mentionText.replace(/ {2}/g, '&nbsp; ');

      elmInputBox.data('messageText', syntaxMessage);
      elmMentionsOverlay.find('div').html(mentionText);
    }

    function resetBuffer() {
      clearTimeout(this.timeout);
      inputBuffer = [];
    }

    function updateMentionsCollection() {
      var inputText = getInputBoxValue();

      mentionsCollection = _.reject(mentionsCollection, function (mention, index) {
        return !mention.value || inputText.indexOf(mention.value) == -1;
      });
      mentionsCollection = _.compact(mentionsCollection);
    }

    function addMention(mention) {

      var currentMessage = getInputBoxValue();

      // Using a regex to figure out positions
      var regex = new RegExp("\\" + settings.triggerChar + currentDataQuery, "gi");
      var result = regex.exec(currentMessage);
      var startCaretPosition;
      var currentCaretPosition;

      if (!result) {
        var caret = elmInputBox.caret();

        currentCaretPosition = caret.begin + currentMessage.substr(caret.begin).match("\\s|$").index;
        startCaretPosition = currentCaretPosition - currentDataQuery.length;
        if (currentMessage[startCaretPosition] == ' ') {
          startCaretPosition += 1;
        }
      } else {
        startCaretPosition = regex.lastIndex - result[0].length;
        currentCaretPosition = regex.lastIndex;
      }

      var start = currentMessage.substr(0, startCaretPosition);
      var end = currentMessage.substr(currentCaretPosition, currentMessage.length);
      var startEndIndex = (start + mention.value).length + 1;

      mentionsCollection.push(mention);

      // Cleaning before inserting the value, otherwise auto-complete would be triggered with "old" inputbuffer
      resetBuffer();
      currentDataQuery = '';
      hideAutoComplete();

      // Mentions & syntax message
      if (end[0] != ' ') end = ' ' + end;
      var updatedMessageText = start + mention.value + end;
      elmInputBox.val(updatedMessageText);
      updateValues();

      // Set correct focus and selection
      elmInputBox.focus();
      utils.setCaratPosition(elmInputBox[0], startEndIndex);
    }

    function getInputBoxValue() {
      return elmInputBox.val();
    }

    // returns either the last word or up to two words following the
    // triggerChar, based on the position in the string
    function getQuery(string, position) {
        var spacePos = position + string.substr(position).match("\\s|$").index,
          substring = string.substr(0, spacePos),
          // start of regex changes depending on whether minCharsNoTrigger is enabled
          trigger = (settings.minCharsNoTrigger === false) ? (settings.triggerChar + "(") : ("(" + settings.triggerChar),
          lastWord = substring.match(trigger + "\\S+\\s)?\\S+$", "i");
        if (lastWord) return lastWord[0];
        // so we can still call onTriggerChar without any characters
        if (substring.match("(^|\\s)" + settings.triggerChar + "$")) return settings.triggerChar;
        return null;
    }

    // This is taken straight from live (as of Sep 2012) GitHub code. The
    // technique is known around the web. Just google it. Github's is quite
    // succint though. NOTE: relies on selectionEnd, which as far as IE is concerned,
    // it'll only work on 9+. Good news is nothing will happen if the browser
    // doesn't support it.
    function textareaSelectionPosition($el) {
      var a, b, c, d, e, f, g, h, i, j, k;
      if (!(i = $el[0])) return;
      if (!$(i).is("textarea")) return;
      if (i.selectionEnd == null) return;
      g = {
        position: "absolute",
        overflow: "auto",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        boxSizing: "content-box",
        top: 0,
        left: -9999
      }, h = ["boxSizing", "fontFamily", "fontSize", "fontStyle", "fontVariant", "fontWeight", "height", "letterSpacing", "lineHeight", "paddingBottom", "paddingLeft", "paddingRight", "paddingTop", "textDecoration", "textIndent", "textTransform", "width", "word-spacing"];
      for (j = 0, k = h.length; j < k; j++) e = h[j], g[e] = $(i).css(e);
      return c = document.createElement("div"), $(c).css(g), $(i).after(c), b = document.createTextNode(i.value.substring(0, i.selectionEnd)), a = document.createTextNode(i.value.substring(i.selectionEnd)), d = document.createElement("span"), d.innerHTML = "&nbsp;", c.appendChild(b), c.appendChild(d), c.appendChild(a), c.scrollTop = i.scrollTop, f = $(d).position(), $(c).remove(), f
    }    

    function onAutoCompleteItemClick(e) {
      var elmTarget = $(this);
      var mention = autocompleteItemCollection[elmTarget.attr('data-uid')];

      addMention(mention);

      return false;
    }

    function onInputBoxClick(e) {
      resetBuffer();
    }

    function onInputBoxBlur(e) {
      hideAutoComplete();
    }

    function onInputBoxInput(e) {
      updateValues();
      updateMentionsCollection();

      // need to defer so elmInputBox.caret() gets the accurate caret position
      _.defer(function() {
        var lastWord = getQuery(getInputBoxValue(), elmInputBox.caret().begin);
        if (settings.minCharsNoTrigger && lastWord && lastWord.length >= settings.minCharsNoTrigger) {
          currentDataQuery = lastWord;
          doSearch.call(this, currentDataQuery);
        } else if (!settings.minCharsNoTrigger && lastWord && lastWord[0] === settings.triggerChar && lastWord.length >= (settings.minChars + 1)) {
          currentDataQuery = (lastWord.length > 1) ? lastWord.substr(1) : '';
          doSearch.call(this, currentDataQuery);
        } else if (lastWord && lastWord[0] === settings.triggerChar && _.isFunction(settings.onTriggerChar)) {
          currentDataQuery = (lastWord.length > 1) ? lastWord.substr(1) : '';
          settings.onTriggerChar.call(this, currentDataQuery, function (responseData) {
            populateDropdown(currentDataQuery, responseData);
          });
        } else {
          hideAutoComplete();
        }
      });
    }

    function onInputBoxKeyPress(e) {
      clearTimeout(this.resetTimeout);
      if(e.keyCode == KEY.SPACE && (_.contains(inputBuffer, ' ') || !_.contains(inputBuffer, settings.triggerChar))) {
        resetBuffer();
        hideAutoComplete();
        return;
      }

      if(e.keyCode !== KEY.BACKSPACE) {
        var typedValue = String.fromCharCode(e.which || e.keyCode);
        inputBuffer.push(typedValue);
      }
    }

    function onInputBoxKeyDown(e) {

      // This also matches HOME/END on OSX which is CMD+LEFT, CMD+RIGHT
      if (e.keyCode == KEY.LEFT || e.keyCode == KEY.RIGHT || e.keyCode == KEY.HOME || e.keyCode == KEY.END) {
        // Defer execution to ensure carat pos has changed after HOME/END keys
        _.defer(resetBuffer);

        // IE9 doesn't fire the oninput event when backspace or delete is pressed. This causes the highlighting
        // to stay on the screen whenever backspace is pressed after a highlighed word. This is simply a hack
        // to force updateValues() to fire when backspace/delete is pressed in IE9.
        if (navigator.userAgent.indexOf("MSIE 9") > -1) {
          _.defer(updateValues);
        }

        return;
      }

      if (e.keyCode == KEY.BACKSPACE) {
        _.defer(function() {
          if (getInputBoxValue().length === 0) {
            hideAutoComplete();
            resetBuffer();
          }
          if (navigator.userAgent.indexOf("MSIE 9") > -1) {
            resetBuffer();
            updateValues();
          }
        });
        
        inputBuffer = inputBuffer.slice(0, -1 + inputBuffer.length); // Can't use splice, not available in IE
        return;
      }

      if (!elmAutocompleteList.is(':visible')) {
        return true;
      }

      switch (e.keyCode) {
        case KEY.UP:
        case KEY.DOWN:
          var elmCurrentAutoCompleteItem = null;
          if (e.keyCode == KEY.DOWN) {
            if (elmActiveAutoCompleteItem && elmActiveAutoCompleteItem.length) {
              elmCurrentAutoCompleteItem = elmActiveAutoCompleteItem.next();
            } else {
              elmCurrentAutoCompleteItem = elmAutocompleteList.find('li').first();
            }
          } else {
            elmCurrentAutoCompleteItem = $(elmActiveAutoCompleteItem).prev();
          }

          if (elmCurrentAutoCompleteItem.length) {
            selectAutoCompleteItem(elmCurrentAutoCompleteItem);
          }

          return false;

        case KEY.RETURN:
        case KEY.TAB:
          if (elmActiveAutoCompleteItem && elmActiveAutoCompleteItem.length) {
            elmActiveAutoCompleteItem.trigger('mousedown');
            return false;
          }

          break;
      }

      return true;
    }

    function hideAutoComplete() {
      elmActiveAutoCompleteItem = null;
      elmAutocompleteList.empty().hide();
    }

    function selectAutoCompleteItem(elmItem) {
      elmItem.addClass(settings.classes.autoCompleteItemActive);
      elmItem.siblings().removeClass(settings.classes.autoCompleteItemActive);

      // if in an overflow container, make sure the current item is visibile (for keyboard nav)
      elmAutocompleteList.scrollTop(elmItem[0].offsetTop);

      elmActiveAutoCompleteItem = elmItem;
    }

    function populateDropdown(query, results) {
      elmAutocompleteList.show();

      // Filter items that has already been mentioned
      var mentionValues = _.map(mentionsCollection, function(item) {
        return item.type + item.id;
      });

      results = _.reject(results, function (item) {
        return _.include(mentionValues, item.type + item.id);
      });

      if (!results.length) {
        hideAutoComplete();
        return;
      }

      elmAutocompleteList.empty();
      var elmDropDownList = $("<ul>").appendTo(elmAutocompleteList).hide();

      _.each(results, function (item, index) {
        var itemUid = _.uniqueId('mention_');

        autocompleteItemCollection[itemUid] = _.extend({}, item, {value: item.name});
        if (_.isObject(item.rawListItem)) {
          elmListItem = $(item.rawListItem);
        } else if (_.isFunction(settings.templates.autocompleteListItem)) {
          var elmListItem = $(settings.templates.autocompleteListItem({
            'id'      : utils.htmlEncode(item.id),
            'display' : utils.htmlEncode(item.name),
            'type'    : utils.htmlEncode(item.type),
            'content' : utils.highlightTerm(utils.htmlEncode((item.name)), query)
          }));

          if (settings.showAvatars) {
            var elmIcon;

            if (item.avatar) {
              elmIcon = $(settings.templates.autocompleteListItemAvatar({ avatar : item.avatar }));
            } else {
              elmIcon = $(settings.templates.autocompleteListItemIcon({ icon : item.icon }));
            }
            elmIcon.prependTo(elmListItem);
          }
        }
        if (index === 0) {
          selectAutoCompleteItem(elmListItem);
        }
        elmListItem = elmListItem.attr('data-uid', itemUid).appendTo(elmDropDownList);
      });

      elmAutocompleteList.show();
      if (settings.onCaret) positionAutocomplete(elmAutocompleteList, elmInputBox);
      elmDropDownList.show();
    }

    function doSearch(query) {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(function() {

        var usingTriggerChar = (query[0] == settings.triggerChar);
        query = usingTriggerChar ? query.substr(1) : query;
        currentDataQuery = query;
        
        if (query && query.length && query.length >= settings.minChars) {
          settings.onDataRequest.call(this, 'search', query, function (responseData) {
            populateDropdown(query, responseData);
          }, usingTriggerChar);
        } else {
          hideAutoComplete();
        }
      }, settings.searchDelay);
    }

    function positionAutocomplete(elmAutocompleteList, elmInputBox) {
      var position = textareaSelectionPosition(elmInputBox),
          lineHeight = parseInt(elmInputBox.css('line-height'), 10) || 18;
      elmAutocompleteList.css('width', '12em'); // Sort of a guess
      elmAutocompleteList.css('left', position.left);
      elmAutocompleteList.css('top', lineHeight + position.top);
    }

    function resetInput() {
      elmInputBox.val('');
      mentionsCollection = [];
      updateValues();
    }

    // Public methods
    return {
      init : function (domTarget) {

        domInput = domTarget;

        initTextarea();
        initAutocomplete();
        initMentionsOverlay();
        resetInput();

        if( settings.prefillMention ) {
          addMention( settings.prefillMention );
        }
      },

      renderNote : function(rawText, mentions) {
        //rawText example: "@[user:123-users] some string"
        if (rawText === undefined) return;

        var regex,
            result,
            displayText,
            displayTextOverlay;

        displayText = displayTextOverlay = _.escape(rawText);

        mentions = mentions || mentionsCollection;
        _.each(mentions, function(m){
          if (m.type === 'Leads') {
            regex = new RegExp("Leads?-" + m.id, "gi");  
          } else {
            regex = new RegExp("@\\[" + m.type + ":" + m.id + "\\]", "gi");
          }
          result = regex.exec(displayText);
          
          if (result && result.length) {
            mentionsCollection.push(m);
             // @[user:123-users] -> Flavio daCosta
            displayText = displayText.replace(result[0], m.value);
             // @[user:123-users] -> <span>..Flavio daCosta..</span>
            displayTextOverlay = displayTextOverlay.replace(result[0], settings.templates.mentionItemHighlight(m));
          }
        }, this);

        //this is what the user sees
        elmInputBox.val(_.unescape(displayText));

        //this is what we send to the database.
        elmInputBox.data('messageText', rawText);

        //this is the html in the hidden div overlay
        elmMentionsOverlay.find('div').html(displayTextOverlay);
      },

      val : function (callback) {
        if (!_.isFunction(callback)) {
          return;
        }

        var value = mentionsCollection.length ? elmInputBox.data('messageText') : getInputBoxValue();
        callback.call(this, value);
      },

      reset : function () {
        resetInput();
      },

      getMentions : function (callback) {
        if (!_.isFunction(callback)) {
          return;
        }

        callback.call(this, mentionsCollection);
      }
    };
  };

  $.fn.mentionsInput = function (method, settings) {

    var outerArguments = arguments;

    if (typeof method === 'object' || !method) {
      settings = method;
    }

    return this.each(function () {
      var instance = $.data(this, 'mentionsInput') || $.data(this, 'mentionsInput', new MentionsInput(settings));

      if (_.isFunction(instance[method])) {
        return instance[method].apply(this, Array.prototype.slice.call(outerArguments, 1));

      } else if (typeof method === 'object' || !method) {
        return instance.init.call(this, this);

      } else {
        $.error('Method ' + method + ' does not exist');
      }

    });
  };

})(jQuery, _);

$.fn.caret = function (begin, end) {
    if (this.length == 0) return;
    if (typeof begin == 'number')
    {
        end = (typeof end == 'number') ? end : begin;
        return this.each(function ()
        {
            if (this.setSelectionRange)
            {
                this.setSelectionRange(begin, end);
            } else if (this.createTextRange)
            {
                var range = this.createTextRange();
                range.collapse(true);
                range.moveEnd('character', end);
                range.moveStart('character', begin);
                try { range.select(); } catch (ex) { }
            }
        });
    } else
    {
        if (this[0].setSelectionRange)
        {
            begin = this[0].selectionStart;
            end = this[0].selectionEnd;
        } else if (document.selection && document.selection.createRange)
        {
            var range = document.selection.createRange();
            begin = 0 - range.duplicate().moveStart('character', -100000);
            end = begin + range.text.length;
        }
        return { begin: begin, end: end };
    }
};
/* Javascript plotting library for jQuery, v. 0.6.
 *
 * Released under the MIT license by IOLA, December 2007.
 *
 */

// first an inline dependency, jquery.colorhelpers.js, we inline it here
// for convenience

/* Plugin for jQuery for working with colors.
 * 
 * Version 1.0.
 * 
 * Inspiration from jQuery color animation plugin by John Resig.
 *
 * Released under the MIT license by Ole Laursen, October 2009.
 *
 * Examples:
 *
 *   $.color.parse("#fff").scale('rgb', 0.25).add('a', -0.5).toString()
 *   var c = $.color.extract($("#mydiv"), 'background-color');
 *   console.log(c.r, c.g, c.b, c.a);
 *   $.color.make(100, 50, 25, 0.4).toString() // returns "rgba(100,50,25,0.4)"
 *
 * Note that .scale() and .add() work in-place instead of returning
 * new objects.
 */ 
(function(){jQuery.color={};jQuery.color.make=function(E,D,B,C){var F={};F.r=E||0;F.g=D||0;F.b=B||0;F.a=C!=null?C:1;F.add=function(I,H){for(var G=0;G<I.length;++G){F[I.charAt(G)]+=H}return F.normalize()};F.scale=function(I,H){for(var G=0;G<I.length;++G){F[I.charAt(G)]*=H}return F.normalize()};F.toString=function(){if(F.a>=1){return"rgb("+[F.r,F.g,F.b].join(",")+")"}else{return"rgba("+[F.r,F.g,F.b,F.a].join(",")+")"}};F.normalize=function(){function G(I,J,H){return J<I?I:(J>H?H:J)}F.r=G(0,parseInt(F.r),255);F.g=G(0,parseInt(F.g),255);F.b=G(0,parseInt(F.b),255);F.a=G(0,F.a,1);return F};F.clone=function(){return jQuery.color.make(F.r,F.b,F.g,F.a)};return F.normalize()};jQuery.color.extract=function(C,B){var D;do{D=C.css(B).toLowerCase();if(D!=""&&D!="transparent"){break}C=C.parent()}while(!jQuery.nodeName(C.get(0),"body"));if(D=="rgba(0, 0, 0, 0)"){D="transparent"}return jQuery.color.parse(D)};jQuery.color.parse=function(E){var D,B=jQuery.color.make;if(D=/rgb\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*\)/.exec(E)){return B(parseInt(D[1],10),parseInt(D[2],10),parseInt(D[3],10))}if(D=/rgba\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]+(?:\.[0-9]+)?)\s*\)/.exec(E)){return B(parseInt(D[1],10),parseInt(D[2],10),parseInt(D[3],10),parseFloat(D[4]))}if(D=/rgb\(\s*([0-9]+(?:\.[0-9]+)?)\%\s*,\s*([0-9]+(?:\.[0-9]+)?)\%\s*,\s*([0-9]+(?:\.[0-9]+)?)\%\s*\)/.exec(E)){return B(parseFloat(D[1])*2.55,parseFloat(D[2])*2.55,parseFloat(D[3])*2.55)}if(D=/rgba\(\s*([0-9]+(?:\.[0-9]+)?)\%\s*,\s*([0-9]+(?:\.[0-9]+)?)\%\s*,\s*([0-9]+(?:\.[0-9]+)?)\%\s*,\s*([0-9]+(?:\.[0-9]+)?)\s*\)/.exec(E)){return B(parseFloat(D[1])*2.55,parseFloat(D[2])*2.55,parseFloat(D[3])*2.55,parseFloat(D[4]))}if(D=/#([a-fA-F0-9]{2})([a-fA-F0-9]{2})([a-fA-F0-9]{2})/.exec(E)){return B(parseInt(D[1],16),parseInt(D[2],16),parseInt(D[3],16))}if(D=/#([a-fA-F0-9])([a-fA-F0-9])([a-fA-F0-9])/.exec(E)){return B(parseInt(D[1]+D[1],16),parseInt(D[2]+D[2],16),parseInt(D[3]+D[3],16))}var C=jQuery.trim(E).toLowerCase();if(C=="transparent"){return B(255,255,255,0)}else{D=A[C];return B(D[0],D[1],D[2])}};var A={aqua:[0,255,255],azure:[240,255,255],beige:[245,245,220],black:[0,0,0],blue:[0,0,255],brown:[165,42,42],cyan:[0,255,255],darkblue:[0,0,139],darkcyan:[0,139,139],darkgrey:[169,169,169],darkgreen:[0,100,0],darkkhaki:[189,183,107],darkmagenta:[139,0,139],darkolivegreen:[85,107,47],darkorange:[255,140,0],darkorchid:[153,50,204],darkred:[139,0,0],darksalmon:[233,150,122],darkviolet:[148,0,211],fuchsia:[255,0,255],gold:[255,215,0],green:[0,128,0],indigo:[75,0,130],khaki:[240,230,140],lightblue:[173,216,230],lightcyan:[224,255,255],lightgreen:[144,238,144],lightgrey:[211,211,211],lightpink:[255,182,193],lightyellow:[255,255,224],lime:[0,255,0],magenta:[255,0,255],maroon:[128,0,0],navy:[0,0,128],olive:[128,128,0],orange:[255,165,0],pink:[255,192,203],purple:[128,0,128],violet:[128,0,128],red:[255,0,0],silver:[192,192,192],white:[255,255,255],yellow:[255,255,0]}})();

// the actual Flot code
(function($) {
    function Plot(placeholder, data_, options_, plugins) {
        // data is on the form:
        //   [ series1, series2 ... ]
        // where series is either just the data as [ [x1, y1], [x2, y2], ... ]
        // or { data: [ [x1, y1], [x2, y2], ... ], label: "some label", ... }
        
        var series = [],
            options = {
                // the color theme used for graphs
                colors: ["#edc240", "#afd8f8", "#cb4b4b", "#4da74d", "#9440ed"],
                legend: {
                    show: true,
                    noColumns: 1, // number of colums in legend table
                    labelFormatter: null, // fn: string -> string
                    labelBoxBorderColor: "#ccc", // border color for the little label boxes
                    container: null, // container (as jQuery object) to put legend in, null means default on top of graph
                    position: "ne", // position of default legend container within plot
                    margin: 5, // distance from grid edge to default legend container within plot
                    backgroundColor: null, // null means auto-detect
                    backgroundOpacity: 0.85, // set to 0 to avoid background
                    suppressStyles: false // set to true to use your own styles
                },
                xaxis: {
                    position: "bottom", // or "top"
                    mode: null, // null or "time"
                    color: null, // base color, labels, ticks
                    tickColor: null, // possibly different color of ticks, e.g. "rgba(0,0,0,0.15)"
                    transform: null, // null or f: number -> number to transform axis
                    inverseTransform: null, // if transform is set, this should be the inverse function
                    min: null, // min. value to show, null means set automatically
                    max: null, // max. value to show, null means set automatically
                    autoscaleMargin: null, // margin in % to add if auto-setting min/max
                    ticks: null, // either [1, 3] or [[1, "a"], 3] or (fn: axis info -> ticks) or app. number of ticks for auto-ticks
                    tickFormatter: null, // fn: number -> string
                    labelWidth: null, // size of tick labels in pixels
                    labelHeight: null,
                    tickLength: null, // size in pixels of ticks, or "full" for whole line
                    alignTicksWithAxis: null, // axis number or null for no sync
                    
                    // mode specific options
                    tickDecimals: null, // no. of decimals, null means auto
                    tickSize: null, // number or [number, "unit"]
                    minTickSize: null, // number or [number, "unit"]
                    monthNames: null, // list of names of months
                    timeformat: null, // format string to use
                    twelveHourClock: false // 12 or 24 time in time mode
                },
                yaxis: {
                    autoscaleMargin: 0.02,
                    position: "left" // or "right"
                },
                xaxes: [],
                yaxes: [],
                series: {
                    points: {
                        show: false,
                        radius: 3,
                        lineWidth: 2, // in pixels
                        fill: true,
                        fillColor: "#ffffff"
                    },
                    lines: {
                        // we don't put in show: false so we can see
                        // whether lines were actively disabled 
                        lineWidth: 2, // in pixels
                        fill: false,
                        fillColor: null,
                        steps: false
                    },
                    bars: {
                        show: false,
                        lineWidth: 2, // in pixels
                        barWidth: 1, // in units of the x axis or 'auto'
                        margin: 20, // if auto barWidth, a percentage of the chart devoted to whitespace
                        fill: true,
                        fillColor: null,
                        align: "left", // or "center" 
                        horizontal: false
                    },
                    shadowSize: 3
                },
                grid: {
                    show: true,
                    aboveData: false,
                    color: "#545454", // primary color used for outline and labels
                    backgroundColor: null, // null for transparent, else color
                    borderColor: null, // set if different from the grid color
                    tickColor: null, // color for the ticks, e.g. "rgba(0,0,0,0.15)"
                    labelMargin: 5, // in pixels
                    axisMargin: 8, // in pixels
                    borderWidth: 2, // in pixels
                    drawAllAxes: false, // draw all axes (i.e. don't skip innermost)
                    markings: null, // array of ranges or fn: axes -> array of ranges
                    markingsColor: "#f4f4f4",
                    markingsLineWidth: 2,
                    // interactive stuff
                    clickable: false,
                    hoverable: false,
                    autoHighlight: true, // highlight in case mouse is near
                    mouseActiveRadius: 10 // how far the mouse can be away to activate an item
                },
                hooks: {}
            },
        canvas = null,      // the canvas for the plot itself
        overlay = null,     // canvas for interactive stuff on top of plot
        eventHolder = null, // jQuery object that events should be bound to
        ctx = null, octx = null,
        xaxes = [], yaxes = [],
        plotOffset = { left: 0, right: 0, top: 0, bottom: 0},
        canvasWidth = 0, canvasHeight = 0,
        plotWidth = 0, plotHeight = 0,
        hooks = {
            processOptions: [],
            processRawData: [],
            processDatapoints: [],
            drawSeries: [],
            draw: [],
            bindEvents: [],
            drawOverlay: []
        },
        plot = this;

        // public functions
        plot.setData = setData;
        plot.setupGrid = setupGrid;
        plot.draw = draw;
        plot.getPlaceholder = function() { return placeholder; };
        plot.getCanvas = function() { return canvas; };
        plot.getPlotOffset = function() { return plotOffset; };
        plot.width = function () { return plotWidth; };
        plot.height = function () { return plotHeight; };
        plot.offset = function () {
            var o = eventHolder.offset();
            o.left += plotOffset.left;
            o.top += plotOffset.top;
            return o;
        };
        plot.getData = function () { return series; };
        plot.getAxis = function (dir, number) {
            var a = (dir == x ? xaxes : yaxes)[number - 1];
            if (a && !a.used)
                a = null;
            return a;
        };
        plot.getAxes = function () {
            var res = {}, i;
            for (i = 0; i < xaxes.length; ++i)
                res["x" + (i ? (i + 1) : "") + "axis"] = xaxes[i] || {};
            for (i = 0; i < yaxes.length; ++i)
                res["y" + (i ? (i + 1) : "") + "axis"] = yaxes[i] || {};

            // backwards compatibility - to be removed
            if (!res.x2axis)
                res.x2axis = { n: 2 };
            if (!res.y2axis)
                res.y2axis = { n: 2 };
            
            return res;
        };
        plot.getXAxes = function () { return xaxes; };
        plot.getYAxes = function () { return yaxes; };
        plot.getUsedAxes = getUsedAxes; // return flat array with x and y axes that are in use
        plot.c2p = canvasToAxisCoords;
        plot.p2c = axisToCanvasCoords;
        plot.getOptions = function () { return options; };
        plot.highlight = highlight;
        plot.unhighlight = unhighlight;
        plot.triggerRedrawOverlay = triggerRedrawOverlay;
        plot.pointOffset = function(point) {
            return {
                left: parseInt(xaxes[axisNumber(point, "x") - 1].p2c(+point.x) + plotOffset.left),
                top: parseInt(yaxes[axisNumber(point, "y") - 1].p2c(+point.y) + plotOffset.top)
            };
        };

        // public attributes
        plot.hooks = hooks;
        
        // initialize
        initPlugins(plot);
        parseOptions(options_);
        constructCanvas();
        setData(data_);
        setupGrid();
        draw();
        bindEvents();


        function executeHooks(hook, args) {
            args = [plot].concat(args);
            for (var i = 0; i < hook.length; ++i)
                hook[i].apply(this, args);
        }

        function initPlugins() {
            for (var i = 0; i < plugins.length; ++i) {
                var p = plugins[i];
                p.init(plot);
                if (p.options)
                    $.extend(true, options, p.options);
            }
        }
        
        function parseOptions(opts) {
            var i;
            
            $.extend(true, options, opts);
            
            if (options.xaxis.color == null)
                options.xaxis.color = options.grid.color;
            if (options.yaxis.color == null)
                options.yaxis.color = options.grid.color;
            
            if (options.xaxis.tickColor == null) // backwards-compatibility
                options.xaxis.tickColor = options.grid.tickColor;
            if (options.yaxis.tickColor == null) // backwards-compatibility
                options.yaxis.tickColor = options.grid.tickColor;

            if (options.grid.borderColor == null)
                options.grid.borderColor = options.grid.color;
            if (options.grid.tickColor == null)
                options.grid.tickColor = $.color.parse(options.grid.color).scale('a', 0.22).toString();
            
            // fill in defaults in axes, copy at least always the
            // first as the rest of the code assumes it'll be there
            for (i = 0; i < Math.max(1, options.xaxes.length); ++i)
                options.xaxes[i] = $.extend(true, {}, options.xaxis, options.xaxes[i]);
            for (i = 0; i < Math.max(1, options.yaxes.length); ++i)
                options.yaxes[i] = $.extend(true, {}, options.yaxis, options.yaxes[i]);
            // backwards compatibility, to be removed in future
            if (options.xaxis.noTicks && options.xaxis.ticks == null)
                options.xaxis.ticks = options.xaxis.noTicks;
            if (options.yaxis.noTicks && options.yaxis.ticks == null)
                options.yaxis.ticks = options.yaxis.noTicks;
            if (options.x2axis) {
                options.y2axis.position = "top";
                options.xaxes[1] = options.x2axis;
            }
            if (options.y2axis) {
                if (options.y2axis.autoscaleMargin === undefined)
                    options.y2axis.autoscaleMargin = 0.02;
                options.y2axis.position = "right";
                options.yaxes[1] = options.y2axis;
            }
            if (options.grid.coloredAreas)
                options.grid.markings = options.grid.coloredAreas;
            if (options.grid.coloredAreasColor)
                options.grid.markingsColor = options.grid.coloredAreasColor;
            if (options.lines)
                $.extend(true, options.series.lines, options.lines);
            if (options.points)
                $.extend(true, options.series.points, options.points);
            if (options.bars)
                $.extend(true, options.series.bars, options.bars);
            if (options.shadowSize)
                options.series.shadowSize = options.shadowSize;

            for (i = 0; i < options.xaxes.length; ++i)
                getOrCreateAxis(xaxes, i + 1).options = options.xaxes[i];
            for (i = 0; i < options.yaxes.length; ++i)
                getOrCreateAxis(yaxes, i + 1).options = options.yaxes[i];

            // add hooks from options
            for (var n in hooks)
                if (options.hooks[n] && options.hooks[n].length)
                    hooks[n] = hooks[n].concat(options.hooks[n]);

            executeHooks(hooks.processOptions, [options]);
        }

        function setData(d) {
            series = parseData(d);
            fillInSeriesOptions();
            processData();
        }
        
        function parseData(d) {
            var res = [];
            for (var i = 0; i < d.length; ++i) {
                var s = $.extend(true, {}, options.series);

                if (d[i].data) {
                    s.data = d[i].data; // move the data instead of deep-copy
                    delete d[i].data;

                    $.extend(true, s, d[i]);

                    d[i].data = s.data;
                }
                else
                    s.data = d[i];
                res.push(s);
            }

            return res;
        }
        
        function axisNumber(obj, coord) {
            var a = obj[coord + "axis"];
            if (typeof a == "object") // if we got a real axis, extract number
                a = a.n;
            if (typeof a != "number")
                a = 1; // default to first axis
            return a;
        }

        function canvasToAxisCoords(pos) {
            // return an object with x/y corresponding to all used axes 
            var res = {}, i, axis;
            for (i = 0; i < xaxes.length; ++i) {
                axis = xaxes[i];
                if (axis && axis.used)
                    res["x" + axis.n] = axis.c2p(pos.left);
            }

            for (i = 0; i < yaxes.length; ++i) {
                axis = yaxes[i];
                if (axis && axis.used)
                    res["y" + axis.n] = axis.c2p(pos.top);
            }
            
            if (res.x1 !== undefined)
                res.x = res.x1;
            if (res.y1 !== undefined)
                res.y = res.y1;

            return res;
        }
        
        function axisToCanvasCoords(pos) {
            // get canvas coords from the first pair of x/y found in pos
            var res = {}, i, axis, key;

            for (i = 0; i < xaxes.length; ++i) {
                axis = xaxes[i];
                if (axis && axis.used) {
                    key = "x" + axis.n;
                    if (pos[key] == null && axis.n == 1)
                        key = "x";

                    if (pos[key]) {
                        res.left = axis.p2c(pos[key]);
                        break;
                    }
                }
            }
            
            for (i = 0; i < yaxes.length; ++i) {
                axis = yaxes[i];
                if (axis && axis.used) {
                    key = "y" + axis.n;
                    if (pos[key] == null && axis.n == 1)
                        key = "y";

                    if (pos[key]) {
                        res.top = axis.p2c(pos[key]);
                        break;
                    }
                }
            }
            
            return res;
        }
        
        function getUsedAxes() {
            var res = [], i, axis;
            for (i = 0; i < xaxes.length; ++i) {
                axis = xaxes[i];
                if (axis && axis.used)
                    res.push(axis);
            }
            for (i = 0; i < yaxes.length; ++i) {
                axis = yaxes[i];
                if (axis && axis.used)
                    res.push(axis);
            }
            return res;
        }

        function getOrCreateAxis(axes, number) {
            if (!axes[number - 1])
                axes[number - 1] = {
                    n: number, // save the number for future reference
                    direction: axes == xaxes ? "x" : "y",
                    options: $.extend(true, {}, axes == xaxes ? options.xaxis : options.yaxis)
                };
                
            return axes[number - 1];
        }

        function fillInSeriesOptions() {
            var i;
            
            // collect what we already got of colors
            var neededColors = series.length,
                usedColors = [],
                assignedColors = [];
            for (i = 0; i < series.length; ++i) {
                var sc = series[i].color;
                if (sc != null) {
                    --neededColors;
                    if (typeof sc == "number")
                        assignedColors.push(sc);
                    else
                        usedColors.push($.color.parse(series[i].color));
                }
            }
            
            // we might need to generate more colors if higher indices
            // are assigned
            for (i = 0; i < assignedColors.length; ++i) {
                neededColors = Math.max(neededColors, assignedColors[i] + 1);
            }

            // produce colors as needed
            var colors = [], variation = 0;
            i = 0;
            while (colors.length < neededColors) {
                var c;
                if (options.colors.length == i) // check degenerate case
                    c = $.color.make(100, 100, 100);
                else
                    c = $.color.parse(options.colors[i]);

                // vary color if needed
                var sign = variation % 2 == 1 ? -1 : 1;
                c.scale('rgb', 1 + sign * Math.ceil(variation / 2) * 0.2)

                // FIXME: if we're getting to close to something else,
                // we should probably skip this one
                colors.push(c);
                
                ++i;
                if (i >= options.colors.length) {
                    i = 0;
                    ++variation;
                }
            }

            // fill in the options
            var colori = 0, s;
            for (i = 0; i < series.length; ++i) {
                s = series[i];
                
                // assign colors
                if (s.color == null) {
                    s.color = colors[colori].toString();
                    ++colori;
                }
                else if (typeof s.color == "number")
                    s.color = colors[s.color].toString();

                // turn on lines automatically in case nothing is set
                if (s.lines.show == null) {
                    var v, show = true;
                    for (v in s)
                        if (s[v] && s[v].show) {
                            show = false;
                            break;
                        }
                    if (show)
                        s.lines.show = true;
                }

                // setup axes
                s.xaxis = getOrCreateAxis(xaxes, axisNumber(s, "x"));
                s.yaxis = getOrCreateAxis(yaxes, axisNumber(s, "y"));
            }
        }
        
        function processData() {
            var topSentry = Number.POSITIVE_INFINITY,
                bottomSentry = Number.NEGATIVE_INFINITY,
                i, j, k, m, length,
                s, points, ps, x, y, axis, val, f, p;

            function initAxis(axis, number) {
                if (!axis)
                    return;
                
                axis.datamin = topSentry;
                axis.datamax = bottomSentry;
                axis.used = false;
            }

            function updateAxis(axis, min, max) {
                if (min < axis.datamin)
                    axis.datamin = min;
                if (max > axis.datamax)
                    axis.datamax = max;
            }

            for (i = 0; i < xaxes.length; ++i)
                initAxis(xaxes[i]);
            for (i = 0; i < yaxes.length; ++i)
                initAxis(yaxes[i]);
            
            for (i = 0; i < series.length; ++i) {
                s = series[i];
                s.datapoints = { points: [] };
                
                executeHooks(hooks.processRawData, [ s, s.data, s.datapoints ]);
            }
            
            // first pass: clean and copy data
            for (i = 0; i < series.length; ++i) {
                s = series[i];

                var data = s.data, format = s.datapoints.format;

                if (!format) {
                    format = [];
                    // find out how to copy
                    format.push({ x: true, number: true, required: true });
                    format.push({ y: true, number: true, required: true });

                    if (s.bars.show || (s.lines.show && s.lines.fill)) {
                        format.push({ y: true, number: true, required: false, defaultValue: 0 });
                        if (s.bars.horizontal) {
                            delete format[format.length - 1].y;
                            format[format.length - 1].x = true;
                        }
                    }
                    
                    s.datapoints.format = format;
                }

                if (s.datapoints.pointsize != null)
                    continue; // already filled in

                if (s.datapoints.pointsize == null)
                    s.datapoints.pointsize = format.length;
                
                ps = s.datapoints.pointsize;
                points = s.datapoints.points;

                insertSteps = s.lines.show && s.lines.steps;
                s.xaxis.used = s.yaxis.used = true;
                
                for (j = k = 0; j < data.length; ++j, k += ps) {
                    p = data[j];

                    var nullify = p == null;
                    if (!nullify) {
                        for (m = 0; m < ps; ++m) {
                            val = p[m];
                            f = format[m];

                            if (f) {
                                if (f.number && val != null) {
                                    val = +val; // convert to number
                                    if (isNaN(val))
                                        val = null;
                                }

                                if (val == null) {
                                    if (f.required)
                                        nullify = true;
                                    
                                    if (f.defaultValue != null)
                                        val = f.defaultValue;
                                }
                            }
                            
                            points[k + m] = val;
                        }
                    }
                    
                    if (nullify) {
                        for (m = 0; m < ps; ++m) {
                            val = points[k + m];
                            if (val != null) {
                                f = format[m];
                                // extract min/max info
                                if (f.x)
                                    updateAxis(s.xaxis, val, val);
                                if (f.y)
                                    updateAxis(s.yaxis, val, val);
                            }
                            points[k + m] = null;
                        }
                    }
                    else {
                        // a little bit of line specific stuff that
                        // perhaps shouldn't be here, but lacking
                        // better means...
                        if (insertSteps && k > 0
                            && points[k - ps] != null
                            && points[k - ps] != points[k]
                            && points[k - ps + 1] != points[k + 1]) {
                            // copy the point to make room for a middle point
                            for (m = 0; m < ps; ++m)
                                points[k + ps + m] = points[k + m];

                            // middle point has same y
                            points[k + 1] = points[k - ps + 1];

                            // we've added a point, better reflect that
                            k += ps;
                        }
                    }
                }
            }

            // give the hooks a chance to run
            for (i = 0; i < series.length; ++i) {
                s = series[i];
                
                executeHooks(hooks.processDatapoints, [ s, s.datapoints]);
            }

            // second pass: find datamax/datamin for auto-scaling
            for (i = 0; i < series.length; ++i) {
                s = series[i];
                points = s.datapoints.points,
                ps = s.datapoints.pointsize;

                var xmin = topSentry, ymin = topSentry,
                    xmax = bottomSentry, ymax = bottomSentry;
                
                for (j = 0; j < points.length; j += ps) {
                    if (points[j] == null)
                        continue;

                    for (m = 0; m < ps; ++m) {
                        val = points[j + m];
                        f = format[m];
                        if (!f)
                            continue;
                        
                        if (f.x) {
                            if (val < xmin)
                                xmin = val;
                            if (val > xmax)
                                xmax = val;
                        }
                        if (f.y) {
                            if (val < ymin)
                                ymin = val;
                            if (val > ymax)
                                ymax = val;
                        }
                    }
                }

                
                if (s.bars.show) {
                    // auto-width bars for histogram-type charts
                    // uses bars.margin to automatically calculate width
                    if (s.bars.barWidth == 'auto') {
                        var numPoints = s.data.length,
                            distance = s.data[0][0] - s.data[numPoints - 1][0];
                            perBar = distance / numPoints;
                            barWidth = Math.abs(perBar * ((100 - s.bars.margin) / 100));
                            
                        s.bars.barWidth = barWidth;
                    }
    
                    // make sure we got room for the bar on the dancing floor
                    var delta = s.bars.align == "left" ? 0 : -s.bars.barWidth/2;
                    if (s.bars.horizontal) {
                        ymin += delta;
                        ymax += delta + s.bars.barWidth;
                    }
                    else {
                        xmin += delta;
                        xmax += delta + s.bars.barWidth;
                    }
                }
                
                updateAxis(s.xaxis, xmin, xmax);
                updateAxis(s.yaxis, ymin, ymax);
            }

            $.each(getUsedAxes(), function (i, axis) {
                if (axis.datamin == topSentry)
                    axis.datamin = null;
                if (axis.datamax == bottomSentry)
                    axis.datamax = null;
            });
        }

        function constructCanvas() {
            function makeCanvas(width, height) {
                var c = document.createElement('canvas');
                c.width = width;
                c.height = height;
                if (!c.getContext) // excanvas hack
                    c = window.G_vmlCanvasManager.initElement(c);
                return c;
            }
            
            canvasWidth = placeholder.width();
            canvasHeight = placeholder.height();
            placeholder.html(""); // clear placeholder
            if (placeholder.css("position") == 'static')
                placeholder.css("position", "relative"); // for positioning labels and overlay

            if (canvasWidth <= 0 || canvasHeight <= 0)
                throw "Invalid dimensions for plot, width = " + canvasWidth + ", height = " + canvasHeight;

            if (window.G_vmlCanvasManager) // excanvas hack
                window.G_vmlCanvasManager.init_(document); // make sure everything is setup
            
            // the canvas
            canvas = $(makeCanvas(canvasWidth, canvasHeight)).appendTo(placeholder).get(0);
            ctx = canvas.getContext("2d");

            // overlay canvas for interactive features
            overlay = $(makeCanvas(canvasWidth, canvasHeight)).css({ position: 'absolute', left: 0, top: 0 }).appendTo(placeholder).get(0);
            octx = overlay.getContext("2d");
            octx.stroke();
        }

        function bindEvents() {
            // we include the canvas in the event holder too, because IE 7
            // sometimes has trouble with the stacking order
            eventHolder = $([overlay, canvas]);

            // bind events
            if (options.grid.hoverable)
                eventHolder.mousemove(onMouseMove);

            if (options.grid.clickable)
                eventHolder.click(onClick);

            executeHooks(hooks.bindEvents, [eventHolder]);
        }

        function setTransformationHelpers(axis) {
            // set helper functions on the axis, assumes plot area
            // has been computed already
            
            function identity(x) { return x; }
            
            var s, m, t = axis.options.transform || identity,
                it = axis.options.inverseTransform;
            
            if (axis.direction == "x") {
                // precompute how much the axis is scaling a point
                // in canvas space
                s = axis.scale = plotWidth / (t(axis.max) - t(axis.min));
                m = t(axis.min);

                // data point to canvas coordinate
                if (t == identity) // slight optimization
                    axis.p2c = function (p) { return (p - m) * s; };
                else
                    axis.p2c = function (p) { return (t(p) - m) * s; };
                // canvas coordinate to data point
                if (!it)
                    axis.c2p = function (c) { return m + c / s; };
                else
                    axis.c2p = function (c) { return it(m + c / s); };
            }
            else {
                s = axis.scale = plotHeight / (t(axis.max) - t(axis.min));
                m = t(axis.max);
                
                if (t == identity)
                    axis.p2c = function (p) { return (m - p) * s; };
                else
                    axis.p2c = function (p) { return (m - t(p)) * s; };
                if (!it)
                    axis.c2p = function (c) { return m - c / s; };
                else
                    axis.c2p = function (c) { return it(m - c / s); };
            }
        }

        function measureTickLabels(axis) {
            if (!axis)
                return;
            
            var opts = axis.options, i, ticks = axis.ticks || [], labels = [],
                l, w = opts.labelWidth, h = opts.labelHeight, dummyDiv;

            function makeDummyDiv(labels, width) {
                return $('<div style="position:absolute;top:-10000px;' + width + 'font-size:smaller">' +
                         '<div class="' + axis.direction + 'Axis ' + axis.direction + axis.n + 'Axis">'
                         + labels.join("") + '</div></div>')
                    .appendTo(placeholder);
            }
            
            if (axis.direction == "x") {
                // to avoid measuring the widths of the labels (it's slow), we
                // construct fixed-size boxes and put the labels inside
                // them, we don't need the exact figures and the
                // fixed-size box content is easy to center
                if (w == null)
                    w = Math.floor(canvasWidth / (ticks.length > 0 ? ticks.length : 1));

                // measure x label heights
                if (h == null) {
                    labels = [];
                    for (i = 0; i < ticks.length; ++i) {
                        l = ticks[i].label;
                        if (l)
                            labels.push('<div class="tickLabel" style="float:left;width:' + w + 'px">' + l + '</div>');
                    }

                    if (labels.length > 0) {
                        // stick them all in the same div and measure
                        // collective height
                        labels.push('<div style="clear:left"></div>');
                        dummyDiv = makeDummyDiv(labels, "width:10000px;");
                        h = dummyDiv.height();
                        dummyDiv.remove();
                    }
                }
            }
            else if (w == null || h == null) {
                // calculate y label dimensions
                for (i = 0; i < ticks.length; ++i) {
                    l = ticks[i].label;
                    if (l)
                        labels.push('<div class="tickLabel">' + l + '</div>');
                }
                
                if (labels.length > 0) {
                    dummyDiv = makeDummyDiv(labels, "");
                    if (w == null)
                        w = dummyDiv.children().width();
                    if (h == null)
                        h = dummyDiv.find("div.tickLabel").height();
                    dummyDiv.remove();
                }
            }

            if (w == null)
                w = 0;
            if (h == null)
                h = 0;
            
            axis.labelWidth = w;
            axis.labelHeight = h;
        }

        function computeAxisBox(axis) {
            if (!axis || (!axis.used && !(axis.labelWidth || axis.labelHeight)))
                return;

            // find the bounding box of the axis by looking at label
            // widths/heights and ticks, make room by diminishing the
            // plotOffset

            var lw = axis.labelWidth,
                lh = axis.labelHeight,
                pos = axis.options.position,
                tickLength = axis.options.tickLength,
                axismargin = options.grid.axisMargin,
                padding = options.grid.labelMargin,
                all = axis.direction == "x" ? xaxes : yaxes,
                index;

            // determine axis margin
            var samePosition = $.grep(all, function (a) {
                return a && a.options.position == pos && (a.labelHeight || a.labelWidth);
            });
            if ($.inArray(axis, samePosition) == samePosition.length - 1)
                axismargin = 0; // outermost

            // determine tick length - if we're innermost, we can use "full"
            if (tickLength == null)
                tickLength = "full";

            var sameDirection = $.grep(all, function (a) {
                return a && (a.labelHeight || a.labelWidth);
            });

            var innermost = $.inArray(axis, sameDirection) == 0;
            if (!innermost && tickLength == "full")
                tickLength = 5;
                
            if (!isNaN(+tickLength))
                padding += +tickLength;

            // compute box
            if (axis.direction == "x") {
                lh += padding;
                
                if (pos == "bottom") {
                    plotOffset.bottom += lh + axismargin;
                    axis.box = { top: canvasHeight - plotOffset.bottom, height: lh };
                }
                else {
                    axis.box = { top: plotOffset.top + axismargin, height: lh };
                    plotOffset.top += lh + axismargin;
                }
            }
            else {
                lw += padding;
                
                if (pos == "left") {
                    axis.box = { left: plotOffset.left + axismargin, width: lw };
                    plotOffset.left += lw + axismargin;
                }
                else {
                    plotOffset.right += lw + axismargin;
                    axis.box = { left: canvasWidth - plotOffset.right, width: lw };
                }
            }

             // save for future reference
            axis.position = pos;
            axis.tickLength = tickLength;
            axis.box.padding = padding;
            axis.innermost = innermost;
        }

        function fixupAxisBox(axis) {
            // set remaining bounding box coordinates
            if (axis.direction == "x") {
                axis.box.left = plotOffset.left;
                axis.box.width = plotWidth;
            }
            else {
                axis.box.top = plotOffset.top;
                axis.box.height = plotHeight;
            }
        }
        
        function setupGrid() {
            var axes = getUsedAxes(), j, k;

            // compute axis intervals
            for (k = 0; k < axes.length; ++k)
                setRange(axes[k]);

            
            plotOffset.left = plotOffset.right = plotOffset.top = plotOffset.bottom = 0;
            if (options.grid.show) {
                // make the ticks
                for (k = 0; k < axes.length; ++k) {
                    setupTickGeneration(axes[k]);
                    setTicks(axes[k]);
                    snapRangeToTicks(axes[k], axes[k].ticks);
                }

                // find labelWidth/Height, do this on all, not just
                // used as we might need to reserve space for unused
                // too if their labelWidth/Height is set
                for (j = 0; j < xaxes.length; ++j)
                    measureTickLabels(xaxes[j]);
                for (j = 0; j < yaxes.length; ++j)
                    measureTickLabels(yaxes[j]);
                    
                // compute the axis boxes, start from the outside (reverse order)
                for (j = xaxes.length - 1; j >= 0; --j)
                    computeAxisBox(xaxes[j]);
                for (j = yaxes.length - 1; j >= 0; --j)
                    computeAxisBox(yaxes[j]);

                // make sure we've got enough space for things that
                // might stick out
                var maxOutset = 0;
                for (var i = 0; i < series.length; ++i)
                    maxOutset = Math.max(maxOutset, 2 * (series[i].points.radius + series[i].points.lineWidth/2));

                for (var a in plotOffset) {
                    plotOffset[a] += options.grid.borderWidth;
                    plotOffset[a] = Math.max(maxOutset, plotOffset[a]);
                }
            }
            
            plotWidth = canvasWidth - plotOffset.left - plotOffset.right;
            plotHeight = canvasHeight - plotOffset.bottom - plotOffset.top;
            
            // now we got the proper plotWidth/Height, we can compute the scaling
            for (k = 0; k < axes.length; ++k)
                setTransformationHelpers(axes[k]);

            if (options.grid.show) {
                for (k = 0; k < axes.length; ++k)
                    fixupAxisBox(axes[k]);
                
                insertAxisLabels();
            }
            
            insertLegend();
        }
        
        function setRange(axis) {
            var opts = axis.options,
                min = +(opts.min != null ? opts.min : axis.datamin),
                max = +(opts.max != null ? opts.max : axis.datamax),
                delta = max - min;

            if (delta == 0.0) {
                // degenerate case
                var widen = max == 0 ? 1 : 0.01;

                if (opts.min == null)
                    min -= widen;
                // alway widen max if we couldn't widen min to ensure we
                // don't fall into min == max which doesn't work
                if (opts.max == null || opts.min != null)
                    max += widen;
            }
            else {
                // consider autoscaling
                var margin = opts.autoscaleMargin;
                if (margin != null) {
                    if (opts.min == null) {
                        min -= delta * margin;
                        // make sure we don't go below zero if all values
                        // are positive
                        if (min < 0 && axis.datamin != null && axis.datamin >= 0)
                            min = 0;
                    }
                    if (opts.max == null) {
                        max += delta * margin;
                        if (max > 0 && axis.datamax != null && axis.datamax <= 0)
                            max = 0;
                    }
                }
            }
            axis.min = min;
            axis.max = max;
        }

        function setupTickGeneration(axis) {
            var opts = axis.options;
                
            // estimate number of ticks
            var noTicks;
            if (typeof opts.ticks == "number" && opts.ticks > 0)
                noTicks = opts.ticks;
            else if (axis.direction == "x")
                 // heuristic based on the model a*sqrt(x) fitted to
                 // some reasonable data points
                noTicks = 0.3 * Math.sqrt(canvasWidth);
            else
                noTicks = 0.3 * Math.sqrt(canvasHeight);

            var delta = (axis.max - axis.min) / noTicks,
                size, generator, unit, formatter, i, magn, norm;

            if (opts.mode == "time") {
                // pretty handling of time
                
                // map of app. size of time units in milliseconds
                var timeUnitSize = {
                    "second": 1000,
                    "minute": 60 * 1000,
                    "hour": 60 * 60 * 1000,
                    "day": 24 * 60 * 60 * 1000,
                    "month": 30 * 24 * 60 * 60 * 1000,
                    "year": 365.2425 * 24 * 60 * 60 * 1000
                };


                // the allowed tick sizes, after 1 year we use
                // an integer algorithm
                var spec = [
                    [1, "second"], [2, "second"], [5, "second"], [10, "second"],
                    [30, "second"], 
                    [1, "minute"], [2, "minute"], [5, "minute"], [10, "minute"],
                    [30, "minute"], 
                    [1, "hour"], [2, "hour"], [4, "hour"],
                    [8, "hour"], [12, "hour"],
                    [1, "day"], [2, "day"], [3, "day"],
                    [0.25, "month"], [0.5, "month"], [1, "month"],
                    [2, "month"], [3, "month"], [6, "month"],
                    [1, "year"]
                ];

                var minSize = 0;
                if (opts.minTickSize != null) {
                    if (typeof opts.tickSize == "number")
                        minSize = opts.tickSize;
                    else
                        minSize = opts.minTickSize[0] * timeUnitSize[opts.minTickSize[1]];
                }

                for (var i = 0; i < spec.length - 1; ++i)
                    if (delta < (spec[i][0] * timeUnitSize[spec[i][1]]
                                 + spec[i + 1][0] * timeUnitSize[spec[i + 1][1]]) / 2
                       && spec[i][0] * timeUnitSize[spec[i][1]] >= minSize)
                        break;
                size = spec[i][0];
                unit = spec[i][1];
                
                // special-case the possibility of several years
                if (unit == "year") {
                    magn = Math.pow(10, Math.floor(Math.log(delta / timeUnitSize.year) / Math.LN10));
                    norm = (delta / timeUnitSize.year) / magn;
                    if (norm < 1.5)
                        size = 1;
                    else if (norm < 3)
                        size = 2;
                    else if (norm < 7.5)
                        size = 5;
                    else
                        size = 10;

                    size *= magn;
                }

                axis.tickSize = opts.tickSize || [size, unit];
                
                generator = function(axis) {
                    var ticks = [],
                        tickSize = axis.tickSize[0], unit = axis.tickSize[1],
                        d = new Date(axis.min);
                    
                    var step = tickSize * timeUnitSize[unit];

                    if (unit == "second")
                        d.setUTCSeconds(floorInBase(d.getUTCSeconds(), tickSize));
                    if (unit == "minute")
                        d.setUTCMinutes(floorInBase(d.getUTCMinutes(), tickSize));
                    if (unit == "hour")
                        d.setUTCHours(floorInBase(d.getUTCHours(), tickSize));
                    if (unit == "month")
                        d.setUTCMonth(floorInBase(d.getUTCMonth(), tickSize));
                    if (unit == "year")
                        d.setUTCFullYear(floorInBase(d.getUTCFullYear(), tickSize));
                    
                    // reset smaller components
                    d.setUTCMilliseconds(0);
                    if (step >= timeUnitSize.minute)
                        d.setUTCSeconds(0);
                    if (step >= timeUnitSize.hour)
                        d.setUTCMinutes(0);
                    if (step >= timeUnitSize.day)
                        d.setUTCHours(0);
                    if (step >= timeUnitSize.day * 4)
                        d.setUTCDate(1);
                    if (step >= timeUnitSize.year)
                        d.setUTCMonth(0);


                    var carry = 0, v = Number.NaN, prev;
                    do {
                        prev = v;
                        v = d.getTime();
                        ticks.push(v);
                        if (unit == "month") {
                            if (tickSize < 1) {
                                // a bit complicated - we'll divide the month
                                // up but we need to take care of fractions
                                // so we don't end up in the middle of a day
                                d.setUTCDate(1);
                                var start = d.getTime();
                                d.setUTCMonth(d.getUTCMonth() + 1);
                                var end = d.getTime();
                                d.setTime(v + carry * timeUnitSize.hour + (end - start) * tickSize);
                                carry = d.getUTCHours();
                                d.setUTCHours(0);
                            }
                            else
                                d.setUTCMonth(d.getUTCMonth() + tickSize);
                        }
                        else if (unit == "year") {
                            d.setUTCFullYear(d.getUTCFullYear() + tickSize);
                        }
                        else
                            d.setTime(v + step);
                    } while (v < axis.max && v != prev);

                    return ticks;
                };

                formatter = function (v, axis) {
                    var d = new Date(v);

                    // first check global format
                    if (opts.timeformat != null)
                        return $.plot.formatDate(d, opts.timeformat, opts.monthNames);
                    
                    var t = axis.tickSize[0] * timeUnitSize[axis.tickSize[1]];
                    var span = axis.max - axis.min;
                    var suffix = (opts.twelveHourClock) ? " %p" : "";
                    
                    if (t < timeUnitSize.minute)
                        fmt = "%h:%M:%S" + suffix;
                    else if (t < timeUnitSize.day) {
                        if (span < 2 * timeUnitSize.day)
                            fmt = "%h:%M" + suffix;
                        else
                            fmt = "%b %d %h:%M" + suffix;
                    }
                    else if (t < timeUnitSize.month)
                        fmt = "%b %d";
                    else if (t < timeUnitSize.year) {
                        if (span < timeUnitSize.year)
                            fmt = "%b";
                        else
                            fmt = "%b %y";
                    }
                    else
                        fmt = "%y";
                    
                    return $.plot.formatDate(d, fmt, opts.monthNames);
                };
            }
            else {
                // pretty rounding of base-10 numbers
                var maxDec = opts.tickDecimals;
                var dec = -Math.floor(Math.log(delta) / Math.LN10);
                if (maxDec != null && dec > maxDec)
                    dec = maxDec;

                magn = Math.pow(10, -dec);
                norm = delta / magn; // norm is between 1.0 and 10.0
                
                if (norm < 1.5)
                    size = 1;
                else if (norm < 3) {
                    size = 2;
                    // special case for 2.5, requires an extra decimal
                    if (norm > 2.25 && (maxDec == null || dec + 1 <= maxDec)) {
                        size = 2.5;
                        ++dec;
                    }
                }
                else if (norm < 7.5)
                    size = 5;
                else
                    size = 10;

                size *= magn;
                
                if (opts.minTickSize != null && size < opts.minTickSize)
                    size = opts.minTickSize;

                axis.tickDecimals = Math.max(0, maxDec != null ? maxDec : dec);
                axis.tickSize = opts.tickSize || size;

                generator = function (axis) {
                    var ticks = [];

                    // spew out all possible ticks
                    var start = floorInBase(axis.min, axis.tickSize),
                        i = 0, v = Number.NaN, prev;
                    do {
                        prev = v;
                        v = start + i * axis.tickSize;
                        ticks.push(v);
                        ++i;
                    } while (v < axis.max && v != prev);
                    return ticks;
                };

                formatter = function (v, axis) {
                    return v.toFixed(axis.tickDecimals);
                };
            }

            if (opts.alignTicksWithAxis != null) {
                var otherAxis = (axis.direction == "x" ? xaxes : yaxes)[opts.alignTicksWithAxis - 1];
                if (otherAxis && otherAxis.used && otherAxis != axis) {
                    // consider snapping min/max to outermost nice ticks
                    var niceTicks = generator(axis);
                    if (niceTicks.length > 0) {
                        if (opts.min == null)
                            axis.min = Math.min(axis.min, niceTicks[0]);
                        if (opts.max == null && niceTicks.length > 1)
                            axis.max = Math.max(axis.max, niceTicks[niceTicks.length - 1]);
                    }
                    
                    generator = function (axis) {
                        // copy ticks, scaled to this axis
                        var ticks = [], v, i;
                        for (i = 0; i < otherAxis.ticks.length; ++i) {
                            v = (otherAxis.ticks[i].v - otherAxis.min) / (otherAxis.max - otherAxis.min);
                            v = axis.min + v * (axis.max - axis.min);
                            ticks.push(v);
                        }
                        return ticks;
                    };
                    
                    // we might need an extra decimal since forced
                    // ticks don't necessarily fit naturally
                    if (axis.mode != "time" && opts.tickDecimals == null) {
                        var extraDec = Math.max(0, -Math.floor(Math.log(delta) / Math.LN10) + 1),
                            ts = generator(axis);

                        // only proceed if the tick interval rounded
                        // with an extra decimal doesn't give us a
                        // zero at end
                        if (!(ts.length > 1 && /\..*0$/.test((ts[1] - ts[0]).toFixed(extraDec))))
                            axis.tickDecimals = extraDec;
                    }
                }
            }

            axis.tickGenerator = generator;
            if ($.isFunction(opts.tickFormatter))
                axis.tickFormatter = function (v, axis) { return "" + opts.tickFormatter(v, axis); };
            else
                axis.tickFormatter = formatter;
        }
        
        function setTicks(axis) {
            axis.ticks = [];

            var oticks = axis.options.ticks, ticks = null;
            if (oticks == null || (typeof oticks == "number" && oticks > 0))
                ticks = axis.tickGenerator(axis);
            else if (oticks) {
                if ($.isFunction(oticks))
                    // generate the ticks
                    ticks = oticks({ min: axis.min, max: axis.max });
                else
                    ticks = oticks;
            }

            // clean up/labelify the supplied ticks, copy them over
            if (ticks) {
                var i, v;
                for (i = 0; i < ticks.length; ++i) {
                    var label = null;
                    var t = ticks[i];
                    if (typeof t == "object") {
                        v = t[0];
                        if (t.length > 1)
                            label = t[1];
                    }
                    else
                        v = t;
                    if (label == null)
                        label = axis.tickFormatter(v, axis);
                    axis.ticks[i] = { v: v, label: label };
                }
            }
        }

        function snapRangeToTicks(axis, ticks) {
            if (axis.options.autoscaleMargin != null && ticks.length > 0) {
                // snap to ticks
                if (axis.options.min == null)
                    axis.min = Math.min(axis.min, ticks[0].v);
                if (axis.options.max == null && ticks.length > 1)
                    axis.max = Math.max(axis.max, ticks[ticks.length - 1].v);
            }
        }
      
        function draw() {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);

            var grid = options.grid;
            
            if (grid.show && !grid.aboveData)
                drawGrid();

            for (var i = 0; i < series.length; ++i) {
                executeHooks(hooks.drawSeries, [ctx, series[i]]);
                drawSeries(series[i]);
            }

            executeHooks(hooks.draw, [ctx]);
            
            if (grid.show && grid.aboveData)
                drawGrid();
        }

        function extractRange(ranges, coord) {
            var axis, from, to, axes, key;

            axes = getUsedAxes();
            for (i = 0; i < axes.length; ++i) {
                axis = axes[i];
                if (axis.direction == coord) {
                    key = coord + axis.n + "axis";
                    if (!ranges[key] && axis.n == 1)
                        key = coord + "axis"; // support x1axis as xaxis
                    if (ranges[key]) {
                        from = ranges[key].from;
                        to = ranges[key].to;
                        break;
                    }
                }
            }

            // backwards-compat stuff - to be removed in future
            if (!ranges[key]) {
                axis = coord == "x" ? xaxes[0] : yaxes[0];
                from = ranges[coord + "1"];
                to = ranges[coord + "2"];
            }

            // auto-reverse as an added bonus
            if (from != null && to != null && from > to) {
                var tmp = from;
                from = to;
                to = tmp;
            }
            
            return { from: from, to: to, axis: axis };
        }
        
        function drawGrid() {
            var i;
            
            ctx.save();
            ctx.translate(plotOffset.left, plotOffset.top);

            // draw background, if any
            if (options.grid.backgroundColor) {
                ctx.fillStyle = getColorOrGradient(options.grid.backgroundColor, plotHeight, 0, "rgba(255, 255, 255, 0)");
                ctx.fillRect(0, 0, plotWidth, plotHeight);
            }

            // draw markings
            var markings = options.grid.markings;
            if (markings) {
                if ($.isFunction(markings)) {
                    var axes = plot.getAxes();
                    // xmin etc. is backwards compatibility, to be
                    // removed in the future
                    axes.xmin = axes.xaxis.min;
                    axes.xmax = axes.xaxis.max;
                    axes.ymin = axes.yaxis.min;
                    axes.ymax = axes.yaxis.max;
                    
                    markings = markings(axes);
                }

                for (i = 0; i < markings.length; ++i) {
                    var m = markings[i],
                        xrange = extractRange(m, "x"),
                        yrange = extractRange(m, "y");

                    // fill in missing
                    if (xrange.from == null)
                        xrange.from = xrange.axis.min;
                    if (xrange.to == null)
                        xrange.to = xrange.axis.max;
                    if (yrange.from == null)
                        yrange.from = yrange.axis.min;
                    if (yrange.to == null)
                        yrange.to = yrange.axis.max;

                    // clip
                    if (xrange.to < xrange.axis.min || xrange.from > xrange.axis.max ||
                        yrange.to < yrange.axis.min || yrange.from > yrange.axis.max)
                        continue;

                    xrange.from = Math.max(xrange.from, xrange.axis.min);
                    xrange.to = Math.min(xrange.to, xrange.axis.max);
                    yrange.from = Math.max(yrange.from, yrange.axis.min);
                    yrange.to = Math.min(yrange.to, yrange.axis.max);

                    if (xrange.from == xrange.to && yrange.from == yrange.to)
                        continue;

                    // then draw
                    xrange.from = xrange.axis.p2c(xrange.from);
                    xrange.to = xrange.axis.p2c(xrange.to);
                    yrange.from = yrange.axis.p2c(yrange.from);
                    yrange.to = yrange.axis.p2c(yrange.to);
                    
                    if (xrange.from == xrange.to || yrange.from == yrange.to) {
                        // draw line
                        ctx.beginPath();
                        ctx.strokeStyle = m.color || options.grid.markingsColor;
                        ctx.lineWidth = m.lineWidth || options.grid.markingsLineWidth;
                        ctx.moveTo(xrange.from, yrange.from);
                        ctx.lineTo(xrange.to, yrange.to);
                        ctx.stroke();
                    }
                    else {
                        // fill area
                        ctx.fillStyle = m.color || options.grid.markingsColor;
                        ctx.fillRect(xrange.from, yrange.to,
                                     xrange.to - xrange.from,
                                     yrange.from - yrange.to);
                    }
                }
            }
            
            // draw the ticks
            var axes = getUsedAxes(), bw = options.grid.borderWidth;

            for (var j = 0; j < axes.length; ++j) {
                var axis = axes[j], box = axis.box,
                    t = axis.tickLength, x, y, xoff, yoff;

                ctx.strokeStyle = axis.options.tickColor || $.color.parse(axis.options.color).scale('a', 0.22).toString();
                ctx.lineWidth = 1;
                
                // find the edges
                if (axis.direction == "x") {
                    x = 0;
                    if (t == "full")
                        y = (axis.position == "top" ? 0 : plotHeight);
                    else
                        y = box.top - plotOffset.top + (axis.position == "top" ? box.height : 0);
                }
                else {
                    y = 0;
                    if (t == "full")
                        x = (axis.position == "left" ? 0 : plotWidth);
                    else
                        x = box.left - plotOffset.left + (axis.position == "left" ? box.width : 0);
                }
                
                // draw tick bar (if 1 or more ticks)
                if ((!axis.innermost || options.grid.drawAllAxes) && 
                         (axis.ticks.length || (axis.innermost && options.grid.drawAllAxes))) {
                    ctx.beginPath();
                    xoff = yoff = 0;
                    if (axis.direction == "x")
                        xoff = plotWidth;
                    else
                        yoff = plotHeight;
                    
                    if (ctx.lineWidth == 1) {
                        x = Math.floor(x) + 0.5;
                        y = Math.floor(y) + 0.5;
                    }

                    ctx.moveTo(x, y);
                    ctx.lineTo(x + xoff, y + yoff);
                    ctx.stroke();
                }

                // draw ticks
                ctx.beginPath();
                for (i = 0; i < axis.ticks.length; ++i) {
                    var v = axis.ticks[i].v;
                    
                    xoff = yoff = 0;

                    if (v < axis.min || v > axis.max
                        // skip those lying on the axes if we got a border
                        || (t == "full" && bw > 0
                            && (v == axis.min || v == axis.max)))
                        continue;

                    if (axis.direction == "x") {
                        x = axis.p2c(v);
                        yoff = t == "full" ? -plotHeight : t;
                        
                        if (axis.position == "top")
                            yoff = -yoff;
                    }
                    else {
                        y = axis.p2c(v);
                        xoff = t == "full" ? -plotWidth : t;
                        
                        if (axis.position == "left")
                            xoff = -xoff;
                    }

                    if (ctx.lineWidth == 1) {
                        if (axis.direction == "x")
                            x = Math.floor(x) + 0.5;
                        else
                            y = Math.floor(y) + 0.5;
                    }

                    ctx.moveTo(x, y);
                    ctx.lineTo(x + xoff, y + yoff);
                }
                
                ctx.stroke();
            }
            
            
            // draw border
            if (bw) {
                ctx.lineWidth = bw;
                ctx.strokeStyle = options.grid.borderColor;
                
                ctx.strokeRect(-bw/2, -bw/2, plotWidth + bw, plotHeight + bw);
            }

            ctx.restore();
        }

        function insertAxisLabels() {
            placeholder.find(".tickLabels").remove();
            
            var html = ['<div class="tickLabels" style="font-size:smaller">'];

            var axes = getUsedAxes();
            for (var j = 0; j < axes.length; ++j) {
                var axis = axes[j], box = axis.box;
                //debug: html.push('<div style="position:absolute;opacity:0.10;background-color:red;left:' + box.left + 'px;top:' + box.top + 'px;width:' + box.width +  'px;height:' + box.height + 'px"></div>')
                html.push('<div class="' + axis.direction + 'Axis ' + axis.direction + axis.n + 'Axis" style="color:' + axis.options.color + '">');
                for (var i = 0; i < axis.ticks.length; ++i) {
                    var tick = axis.ticks[i];
                    if (!tick.label || tick.v < axis.min || tick.v > axis.max)
                        continue;

                    var pos = {}, align;
                    
                    if (axis.direction == "x") {
                        align = "center";
                        pos.left = Math.round(plotOffset.left + axis.p2c(tick.v) - axis.labelWidth/2);
                        if (axis.position == "bottom")
                            pos.top = box.top + box.padding;
                        else
                            pos.bottom = canvasHeight - (box.top + box.height - box.padding);
                    }
                    else {
                        pos.top = Math.round(plotOffset.top + axis.p2c(tick.v) - axis.labelHeight/2);
                        if (axis.position == "left") {
                            pos.right = canvasWidth - (box.left + box.width - box.padding)
                            align = "right";
                        }
                        else {
                            pos.left = box.left + box.padding;
                            align = "left";
                        }
                    }

                    pos.width = axis.labelWidth;

                    var style = ["position:absolute", "text-align:" + align ];
                    for (var a in pos)
                        style.push(a + ":" + pos[a] + "px")
                    
                    html.push('<div class="tickLabel" style="' + style.join(';') + '">' + tick.label + '</div>');
                }
                html.push('</div>');
            }

            html.push('</div>');

            placeholder.append(html.join(""));
        }

        function drawSeries(series) {
            if (series.lines.show)
                drawSeriesLines(series);
            if (series.bars.show)
                drawSeriesBars(series);
            if (series.points.show)
                drawSeriesPoints(series);
        }
        
        function drawSeriesLines(series) {
            function plotLine(datapoints, xoffset, yoffset, axisx, axisy) {
                var points = datapoints.points,
                    ps = datapoints.pointsize,
                    prevx = null, prevy = null;
                
                ctx.beginPath();
                for (var i = ps; i < points.length; i += ps) {
                    var x1 = points[i - ps], y1 = points[i - ps + 1],
                        x2 = points[i], y2 = points[i + 1];
                    
                    if (x1 == null || x2 == null)
                        continue;

                    // clip with ymin
                    if (y1 <= y2 && y1 < axisy.min) {
                        if (y2 < axisy.min)
                            continue;   // line segment is outside
                        // compute new intersection point
                        x1 = (axisy.min - y1) / (y2 - y1) * (x2 - x1) + x1;
                        y1 = axisy.min;
                    }
                    else if (y2 <= y1 && y2 < axisy.min) {
                        if (y1 < axisy.min)
                            continue;
                        x2 = (axisy.min - y1) / (y2 - y1) * (x2 - x1) + x1;
                        y2 = axisy.min;
                    }

                    // clip with ymax
                    if (y1 >= y2 && y1 > axisy.max) {
                        if (y2 > axisy.max)
                            continue;
                        x1 = (axisy.max - y1) / (y2 - y1) * (x2 - x1) + x1;
                        y1 = axisy.max;
                    }
                    else if (y2 >= y1 && y2 > axisy.max) {
                        if (y1 > axisy.max)
                            continue;
                        x2 = (axisy.max - y1) / (y2 - y1) * (x2 - x1) + x1;
                        y2 = axisy.max;
                    }

                    // clip with xmin
                    if (x1 <= x2 && x1 < axisx.min) {
                        if (x2 < axisx.min)
                            continue;
                        y1 = (axisx.min - x1) / (x2 - x1) * (y2 - y1) + y1;
                        x1 = axisx.min;
                    }
                    else if (x2 <= x1 && x2 < axisx.min) {
                        if (x1 < axisx.min)
                            continue;
                        y2 = (axisx.min - x1) / (x2 - x1) * (y2 - y1) + y1;
                        x2 = axisx.min;
                    }

                    // clip with xmax
                    if (x1 >= x2 && x1 > axisx.max) {
                        if (x2 > axisx.max)
                            continue;
                        y1 = (axisx.max - x1) / (x2 - x1) * (y2 - y1) + y1;
                        x1 = axisx.max;
                    }
                    else if (x2 >= x1 && x2 > axisx.max) {
                        if (x1 > axisx.max)
                            continue;
                        y2 = (axisx.max - x1) / (x2 - x1) * (y2 - y1) + y1;
                        x2 = axisx.max;
                    }

                    if (x1 != prevx || y1 != prevy)
                        ctx.moveTo(axisx.p2c(x1) + xoffset, axisy.p2c(y1) + yoffset);
                    
                    prevx = x2;
                    prevy = y2;
                    ctx.lineTo(axisx.p2c(x2) + xoffset, axisy.p2c(y2) + yoffset);
                }
                ctx.stroke();
            }

            function plotLineArea(datapoints, axisx, axisy) {
                var points = datapoints.points,
                    ps = datapoints.pointsize,
                    bottom = Math.min(Math.max(0, axisy.min), axisy.max),
                    i = 0, top, areaOpen = false,
                    ypos = 1, segmentStart = 0, segmentEnd = 0;

                // we process each segment in two turns, first forward
                // direction to sketch out top, then once we hit the
                // end we go backwards to sketch the bottom
                while (true) {
                    if (ps > 0 && i > points.length + ps)
                        break;

                    i += ps; // ps is negative if going backwards

                    var x1 = points[i - ps],
                        y1 = points[i - ps + ypos],
                        x2 = points[i], y2 = points[i + ypos];

                    if (areaOpen) {
                        if (ps > 0 && x1 != null && x2 == null) {
                            // at turning point
                            segmentEnd = i;
                            ps = -ps;
                            ypos = 2;
                            continue;
                        }

                        if (ps < 0 && i == segmentStart + ps) {
                            // done with the reverse sweep
                            ctx.fill();
                            areaOpen = false;
                            ps = -ps;
                            ypos = 1;
                            i = segmentStart = segmentEnd + ps;
                            continue;
                        }
                    }

                    if (x1 == null || x2 == null)
                        continue;

                    // clip x values
                    
                    // clip with xmin
                    if (x1 <= x2 && x1 < axisx.min) {
                        if (x2 < axisx.min)
                            continue;
                        y1 = (axisx.min - x1) / (x2 - x1) * (y2 - y1) + y1;
                        x1 = axisx.min;
                    }
                    else if (x2 <= x1 && x2 < axisx.min) {
                        if (x1 < axisx.min)
                            continue;
                        y2 = (axisx.min - x1) / (x2 - x1) * (y2 - y1) + y1;
                        x2 = axisx.min;
                    }

                    // clip with xmax
                    if (x1 >= x2 && x1 > axisx.max) {
                        if (x2 > axisx.max)
                            continue;
                        y1 = (axisx.max - x1) / (x2 - x1) * (y2 - y1) + y1;
                        x1 = axisx.max;
                    }
                    else if (x2 >= x1 && x2 > axisx.max) {
                        if (x1 > axisx.max)
                            continue;
                        y2 = (axisx.max - x1) / (x2 - x1) * (y2 - y1) + y1;
                        x2 = axisx.max;
                    }

                    if (!areaOpen) {
                        // open area
                        ctx.beginPath();
                        ctx.moveTo(axisx.p2c(x1), axisy.p2c(bottom));
                        areaOpen = true;
                    }
                    
                    // now first check the case where both is outside
                    if (y1 >= axisy.max && y2 >= axisy.max) {
                        ctx.lineTo(axisx.p2c(x1), axisy.p2c(axisy.max));
                        ctx.lineTo(axisx.p2c(x2), axisy.p2c(axisy.max));
                        continue;
                    }
                    else if (y1 <= axisy.min && y2 <= axisy.min) {
                        ctx.lineTo(axisx.p2c(x1), axisy.p2c(axisy.min));
                        ctx.lineTo(axisx.p2c(x2), axisy.p2c(axisy.min));
                        continue;
                    }
                    
                    // else it's a bit more complicated, there might
                    // be a flat maxed out rectangle first, then a
                    // triangular cutout or reverse; to find these
                    // keep track of the current x values
                    var x1old = x1, x2old = x2;

                    // clip the y values, without shortcutting, we
                    // go through all cases in turn
                    
                    // clip with ymin
                    if (y1 <= y2 && y1 < axisy.min && y2 >= axisy.min) {
                        x1 = (axisy.min - y1) / (y2 - y1) * (x2 - x1) + x1;
                        y1 = axisy.min;
                    }
                    else if (y2 <= y1 && y2 < axisy.min && y1 >= axisy.min) {
                        x2 = (axisy.min - y1) / (y2 - y1) * (x2 - x1) + x1;
                        y2 = axisy.min;
                    }

                    // clip with ymax
                    if (y1 >= y2 && y1 > axisy.max && y2 <= axisy.max) {
                        x1 = (axisy.max - y1) / (y2 - y1) * (x2 - x1) + x1;
                        y1 = axisy.max;
                    }
                    else if (y2 >= y1 && y2 > axisy.max && y1 <= axisy.max) {
                        x2 = (axisy.max - y1) / (y2 - y1) * (x2 - x1) + x1;
                        y2 = axisy.max;
                    }

                    // if the x value was changed we got a rectangle
                    // to fill
                    if (x1 != x1old) {
                        ctx.lineTo(axisx.p2c(x1old), axisy.p2c(y1));
                        // it goes to (x1, y1), but we fill that below
                    }
                    
                    // fill triangular section, this sometimes result
                    // in redundant points if (x1, y1) hasn't changed
                    // from previous line to, but we just ignore that
                    ctx.lineTo(axisx.p2c(x1), axisy.p2c(y1));
                    ctx.lineTo(axisx.p2c(x2), axisy.p2c(y2));

                    // fill the other rectangle if it's there
                    if (x2 != x2old) {
                        ctx.lineTo(axisx.p2c(x2), axisy.p2c(y2));
                        ctx.lineTo(axisx.p2c(x2old), axisy.p2c(y2));
                    }
                }
            }

            ctx.save();
            ctx.translate(plotOffset.left, plotOffset.top);
            ctx.lineJoin = "round";

            var lw = series.lines.lineWidth,
                sw = series.shadowSize;
            // FIXME: consider another form of shadow when filling is turned on
            if (lw > 0 && sw > 0) {
                // draw shadow as a thick and thin line with transparency
                ctx.lineWidth = sw;
                ctx.strokeStyle = "rgba(0,0,0,0.1)";
                // position shadow at angle from the mid of line
                var angle = Math.PI/18;
                plotLine(series.datapoints, Math.sin(angle) * (lw/2 + sw/2), Math.cos(angle) * (lw/2 + sw/2), series.xaxis, series.yaxis);
                ctx.lineWidth = sw/2;
                plotLine(series.datapoints, Math.sin(angle) * (lw/2 + sw/4), Math.cos(angle) * (lw/2 + sw/4), series.xaxis, series.yaxis);
            }

            ctx.lineWidth = lw;
            ctx.strokeStyle = series.color;
            var fillStyle = getFillStyle(series.lines, series.color, 0, plotHeight);
            if (fillStyle) {
                ctx.fillStyle = fillStyle;
                plotLineArea(series.datapoints, series.xaxis, series.yaxis);
            }

            if (lw > 0)
                plotLine(series.datapoints, 0, 0, series.xaxis, series.yaxis);
            ctx.restore();
        }

        function drawSeriesPoints(series) {
            function plotPoints(datapoints, radius, fillStyle, offset, circumference, axisx, axisy) {
                var points = datapoints.points, ps = datapoints.pointsize;
                
                for (var i = 0; i < points.length; i += ps) {
                    var x = points[i], y = points[i + 1];
                    if (x == null || x < axisx.min || x > axisx.max || y < axisy.min || y > axisy.max)
                        continue;
                    
                    ctx.beginPath();
                    ctx.arc(axisx.p2c(x), axisy.p2c(y) + offset, radius, 0, circumference, false);
                    if (fillStyle) {
                        ctx.fillStyle = fillStyle;
                        ctx.fill();
                    }
                    ctx.stroke();
                }
            }
            
            ctx.save();
            ctx.translate(plotOffset.left, plotOffset.top);

            var lw = series.lines.lineWidth,
                sw = series.shadowSize,
                radius = series.points.radius;
            if (lw > 0 && sw > 0) {
                // draw shadow in two steps
                var w = sw / 2;
                ctx.lineWidth = w;
                ctx.strokeStyle = "rgba(0,0,0,0.1)";
                plotPoints(series.datapoints, radius, null, w + w/2, Math.PI,
                           series.xaxis, series.yaxis);

                ctx.strokeStyle = "rgba(0,0,0,0.2)";
                plotPoints(series.datapoints, radius, null, w/2, Math.PI,
                           series.xaxis, series.yaxis);
            }

            ctx.lineWidth = lw;
            ctx.strokeStyle = series.color;
            plotPoints(series.datapoints, radius,
                       getFillStyle(series.points, series.color), 0, 2 * Math.PI,
                       series.xaxis, series.yaxis);
            ctx.restore();
        }

        function drawBar(x, y, b, barLeft, barRight, offset, fillStyleCallback, axisx, axisy, c, horizontal, lineWidth) {
            var left, right, bottom, top,
                drawLeft, drawRight, drawTop, drawBottom,
                tmp;

            // in horizontal mode, we start the bar from the left
            // instead of from the bottom so it appears to be
            // horizontal rather than vertical
            if (horizontal) {
                drawBottom = drawRight = drawTop = true;
                drawLeft = false;
                left = b;
                right = x;
                top = y + barLeft;
                bottom = y + barRight;

                // account for negative bars
                if (right < left) {
                    tmp = right;
                    right = left;
                    left = tmp;
                    drawLeft = true;
                    drawRight = false;
                }
            }
            else {
                drawLeft = drawRight = drawTop = true;
                drawBottom = false;
                left = x + barLeft;
                right = x + barRight;
                bottom = b;
                top = y;

                // account for negative bars
                if (top < bottom) {
                    tmp = top;
                    top = bottom;
                    bottom = tmp;
                    drawBottom = true;
                    drawTop = false;
                }
            }
           
            // clip
            if (right < axisx.min || left > axisx.max ||
                top < axisy.min || bottom > axisy.max)
                return;
            
            if (left < axisx.min) {
                left = axisx.min;
                drawLeft = false;
            }

            if (right > axisx.max) {
                right = axisx.max;
                drawRight = false;
            }

            if (bottom < axisy.min) {
                bottom = axisy.min;
                drawBottom = false;
            }
            
            if (top > axisy.max) {
                top = axisy.max;
                drawTop = false;
            }

            left = axisx.p2c(left);
            bottom = axisy.p2c(bottom);
            right = axisx.p2c(right);
            top = axisy.p2c(top);
            
            // fill the bar
            if (fillStyleCallback) {
                c.beginPath();
                c.moveTo(left, bottom);
                c.lineTo(left, top);
                c.lineTo(right, top);
                c.lineTo(right, bottom);
                c.fillStyle = fillStyleCallback(bottom, top);
                c.fill();
            }

            // draw outline
            if (lineWidth > 0 && (drawLeft || drawRight || drawTop || drawBottom)) {
                c.beginPath();

                // FIXME: inline moveTo is buggy with excanvas
                c.moveTo(left, bottom + offset);
                if (drawLeft)
                    c.lineTo(left, top + offset);
                else
                    c.moveTo(left, top + offset);
                if (drawTop)
                    c.lineTo(right, top + offset);
                else
                    c.moveTo(right, top + offset);
                if (drawRight)
                    c.lineTo(right, bottom + offset);
                else
                    c.moveTo(right, bottom + offset);
                if (drawBottom)
                    c.lineTo(left, bottom + offset);
                else
                    c.moveTo(left, bottom + offset);
                c.stroke();
            }
        }
        
        function drawSeriesBars(series) {
            function plotBars(datapoints, barLeft, barRight, offset, fillStyleCallback, axisx, axisy) {
                var points = datapoints.points, ps = datapoints.pointsize;
                
                for (var i = 0; i < points.length; i += ps) {
                    if (points[i] == null)
                        continue;
                    drawBar(points[i], points[i + 1], points[i + 2], barLeft, barRight, offset, fillStyleCallback, axisx, axisy, ctx, series.bars.horizontal, series.bars.lineWidth);
                }
            }

            ctx.save();
            ctx.translate(plotOffset.left, plotOffset.top);

            // FIXME: figure out a way to add shadows (for instance along the right edge)
            ctx.lineWidth = series.bars.lineWidth;
            ctx.strokeStyle = series.color;
            var barLeft = series.bars.align == "left" ? 0 : -series.bars.barWidth/2;
            var fillStyleCallback = series.bars.fill ? function (bottom, top) { return getFillStyle(series.bars, series.color, bottom, top); } : null;
            plotBars(series.datapoints, barLeft, barLeft + series.bars.barWidth, 0, fillStyleCallback, series.xaxis, series.yaxis);
            ctx.restore();
        }

        function getFillStyle(filloptions, seriesColor, bottom, top) {
            var fill = filloptions.fill;
            if (!fill)
                return null;

            if (filloptions.fillColor)
                return getColorOrGradient(filloptions.fillColor, bottom, top, seriesColor);
            
            var c = $.color.parse(seriesColor);
            c.a = typeof fill == "number" ? fill : 0.4;
            c.normalize();
            return c.toString();
        }
        
        function insertLegend() {
            placeholder.find(".legend").remove();

            if (!options.legend.show)
                return;
            
            var sortedSeries = series.slice().sort(function(a, b) { return a.legendOrder - b.legendOrder; });

            var fragments = [], rowStarted = false,
                lf = options.legend.labelFormatter, s, label;
            for (var i = 0; i < sortedSeries.length; ++i) {
                s = sortedSeries[i];
                label = s.label;
                if (!label)
                    continue;
                
                if (i % options.legend.noColumns == 0) {
                    if (rowStarted)
                        fragments.push('</tr>');
                    fragments.push('<tr>');
                    rowStarted = true;
                }

                if (lf)
                    label = lf(label, s);

                var idClass = (s.id) ? 'legend-'+s.id : '';
                
                if (options.legend.suppressStyles)
                    fragments.push(
                        '<td class="legendColorBox '+ idClass +'"><div class="legendColorBoxBorder"><div class="legendColorBoxSwatch" style="background-color: ' + s.color + ';"></div></div></td>' +
                        '<td class="legendLabel '+ idClass +'">' + label + '</td>');
                else
                    fragments.push(
                        '<td class="legendColorBox '+ idClass +'"><div style="border:1px solid ' + options.legend.labelBoxBorderColor + ';padding:1px"><div style="width:4px;height:0;border:5px solid ' + s.color + ';overflow:hidden"></div></div></td>' +
                        '<td class="legendLabel '+ idClass +'">' + label + '</td>');
            }
            if (rowStarted)
                fragments.push('</tr>');
            
            if (fragments.length == 0)
                return;
            
            if (options.legend.suppressStyles)
                var table = '<table>' + fragments.join("") + '</table>';
            else
                var table = '<table style="font-size:smaller;color:' + options.grid.color + '">' + fragments.join("") + '</table>';
            
            if (options.legend.container != null)
                $(options.legend.container).html(table);
            else {
                var pos = "",
                    p = options.legend.position,
                    m = options.legend.margin;
                if (m[0] == null)
                    m = [m, m];
                if (p.charAt(0) == "n")
                    pos += 'top:' + (m[1] + plotOffset.top) + 'px;';
                else if (p.charAt(0) == "s")
                    pos += 'bottom:' + (m[1] + plotOffset.bottom) + 'px;';
                if (p.charAt(1) == "e")
                    pos += 'right:' + (m[0] + plotOffset.right) + 'px;';
                else if (p.charAt(1) == "w")
                    pos += 'left:' + (m[0] + plotOffset.left) + 'px;';
                var legend = $('<div class="legend">' + table.replace('style="', 'style="position:absolute;' + pos +';') + '</div>').appendTo(placeholder);
                if (options.legend.backgroundOpacity != 0.0) {
                    // put in the transparent background
                    // separately to avoid blended labels and
                    // label boxes
                    var c = options.legend.backgroundColor;
                    if (c == null) {
                        c = options.grid.backgroundColor;
                        if (c && typeof c == "string")
                            c = $.color.parse(c);
                        else
                            c = $.color.extract(legend, 'background-color');
                        c.a = 1;
                        c = c.toString();
                    }
                    var div = legend.children();
                    $('<div style="position:absolute;width:' + div.width() + 'px;height:' + div.height() + 'px;' + pos +'background-color:' + c + ';"> </div>').prependTo(legend).css('opacity', options.legend.backgroundOpacity);
                }
            }
        }


        // interactive features
        
        var highlights = [],
            redrawTimeout = null;
        
        // returns the data item the mouse is over, or null if none is found
        function findNearbyItem(mouseX, mouseY, seriesFilter) {
            var maxDistance = options.grid.mouseActiveRadius,
                smallestDistance = maxDistance * maxDistance + 1,
                item = null, foundPoint = false, i, j;

            for (i = series.length - 1; i >= 0; --i) {
                if (!seriesFilter(series[i]))
                    continue;

                var s = series[i],
                    axisx = s.xaxis,
                    axisy = s.yaxis,
                    points = s.datapoints.points,
                    ps = s.datapoints.pointsize,
                    mx = axisx.c2p(mouseX), // precompute some stuff to make the loop faster
                    my = axisy.c2p(mouseY),
                    maxx = maxDistance / axisx.scale,
                    maxy = maxDistance / axisy.scale;

                if (s.lines.show || s.points.show) {
                    for (j = 0; j < points.length; j += ps) {
                        var x = points[j], y = points[j + 1];
                        if (x == null)
                            continue;
                        
                        // For points and lines, the cursor must be within a
                        // certain distance to the data point
                        if (x - mx > maxx || x - mx < -maxx ||
                            y - my > maxy || y - my < -maxy)
                            continue;

                        // We have to calculate distances in pixels, not in
                        // data units, because the scales of the axes may be different
                        var dx = Math.abs(axisx.p2c(x) - mouseX),
                            dy = Math.abs(axisy.p2c(y) - mouseY),
                            dist = dx * dx + dy * dy; // we save the sqrt

                        // use <= to ensure last point takes precedence
                        // (last generally means on top of)
                        if (dist < smallestDistance) {
                            smallestDistance = dist;
                            item = [i, j / ps];
                        }
                    }
                }
                    
                if (s.bars.show && !item) { // no other point can be nearby
                    var barLeft = s.bars.align == "left" ? 0 : -s.bars.barWidth/2,
                        barRight = barLeft + s.bars.barWidth;
                    
                    for (j = 0; j < points.length; j += ps) {
                        var x = points[j], y = points[j + 1], b = points[j + 2];
                        if (x == null)
                            continue;
  
                        // for a bar graph, the cursor must be inside the bar
                        if (series[i].bars.horizontal ? 
                            (mx <= Math.max(b, x) && mx >= Math.min(b, x) && 
                             my >= y + barLeft && my <= y + barRight) :
                            (mx >= x + barLeft && mx <= x + barRight &&
                             my >= Math.min(b, y) && my <= Math.max(b, y)))
                                item = [i, j / ps];
                    }
                }
            }

            if (item) {
                i = item[0];
                j = item[1];
                ps = series[i].datapoints.pointsize;
                
                return { datapoint: series[i].datapoints.points.slice(j * ps, (j + 1) * ps),
                         dataIndex: j,
                         series: series[i],
                         seriesIndex: i };
            }
            
            return null;
        }

        function onMouseMove(e) {
            if (options.grid.hoverable)
                triggerClickHoverEvent("plothover", e,
                                       function (s) { return s["hoverable"] != false; });
        }
        
        function onClick(e) {
            triggerClickHoverEvent("plotclick", e,
                                   function (s) { return s["clickable"] != false; });
        }

        // trigger click or hover event (they send the same parameters
        // so we share their code)
        function triggerClickHoverEvent(eventname, event, seriesFilter) {
            var offset = eventHolder.offset(),
                canvasX = event.pageX - offset.left - plotOffset.left,
                canvasY = event.pageY - offset.top - plotOffset.top,
            pos = canvasToAxisCoords({ left: canvasX, top: canvasY });

            pos.pageX = event.pageX;
            pos.pageY = event.pageY;

            var item = findNearbyItem(canvasX, canvasY, seriesFilter);

            if (item) {
                // fill in mouse pos for any listeners out there
                item.pageX = parseInt(item.series.xaxis.p2c(item.datapoint[0]) + offset.left + plotOffset.left);
                item.pageY = parseInt(item.series.yaxis.p2c(item.datapoint[1]) + offset.top + plotOffset.top);
            }

            if (options.grid.autoHighlight) {
                // clear auto-highlights
                for (var i = 0; i < highlights.length; ++i) {
                    var h = highlights[i];
                    if (h.auto == eventname &&
                        !(item && h.series == item.series && h.point == item.datapoint))
                        unhighlight(h.series, h.point);
                }
                
                if (item)
                    highlight(item.series, item.datapoint, eventname);
            }
            
            placeholder.trigger(eventname, [ pos, item ]);
        }

        function triggerRedrawOverlay() {
            if (!redrawTimeout)
                redrawTimeout = setTimeout(drawOverlay, 30);
        }

        function drawOverlay() {
            redrawTimeout = null;

            // draw highlights
            octx.save();
            octx.clearRect(0, 0, canvasWidth, canvasHeight);
            octx.translate(plotOffset.left, plotOffset.top);
            
            var i, hi;
            for (i = 0; i < highlights.length; ++i) {
                hi = highlights[i];

                if (hi.series.bars.show)
                    drawBarHighlight(hi.series, hi.point);
                else
                    drawPointHighlight(hi.series, hi.point);
            }
            octx.restore();
            
            executeHooks(hooks.drawOverlay, [octx]);
        }
        
        function highlight(s, point, auto) {
            if (typeof s == "number")
                s = series[s];

            if (typeof point == "number") {
                var ps = s.datapoints.pointsize;
                point = s.datapoints.points.slice(ps * point, ps * (point + 1));
            }

            var i = indexOfHighlight(s, point);
            if (i == -1) {
                highlights.push({ series: s, point: point, auto: auto });

                triggerRedrawOverlay();
            }
            else if (!auto)
                highlights[i].auto = false;
        }
            
        function unhighlight(s, point) {
            if (s == null && point == null) {
                highlights = [];
                triggerRedrawOverlay();
            }
            
            if (typeof s == "number")
                s = series[s];

            if (typeof point == "number")
                point = s.data[point];

            var i = indexOfHighlight(s, point);
            if (i != -1) {
                highlights.splice(i, 1);

                triggerRedrawOverlay();
            }
        }
        
        function indexOfHighlight(s, p) {
            for (var i = 0; i < highlights.length; ++i) {
                var h = highlights[i];
                if (h.series == s && h.point[0] == p[0]
                    && h.point[1] == p[1])
                    return i;
            }
            return -1;
        }
        
        function drawPointHighlight(series, point) {
            var x = point[0], y = point[1],
                axisx = series.xaxis, axisy = series.yaxis;
            
            if (x < axisx.min || x > axisx.max || y < axisy.min || y > axisy.max)
                return;
            
            var pointRadius = series.points.radius + series.points.lineWidth / 2;
            octx.lineWidth = pointRadius;
            octx.strokeStyle = $.color.parse(series.color).scale('a', 0.5).toString();
            var radius = 1.5 * pointRadius;
            octx.beginPath();
            octx.arc(axisx.p2c(x), axisy.p2c(y), radius, 0, 2 * Math.PI, false);
            octx.stroke();
        }

        function drawBarHighlight(series, point) {
            octx.lineWidth = series.bars.lineWidth;
            octx.strokeStyle = $.color.parse(series.color).scale('a', 0.5).toString();
            var fillStyle = $.color.parse(series.color).scale('a', 0.5).toString();
            var barLeft = series.bars.align == "left" ? 0 : -series.bars.barWidth/2;
            drawBar(point[0], point[1], point[2] || 0, barLeft, barLeft + series.bars.barWidth,
                    0, function () { return fillStyle; }, series.xaxis, series.yaxis, octx, series.bars.horizontal, series.bars.lineWidth);
        }

        function getColorOrGradient(spec, bottom, top, defaultColor) {
            if (typeof spec == "string")
                return spec;
            else {
                // assume this is a gradient spec; IE currently only
                // supports a simple vertical gradient properly, so that's
                // what we support too
                var gradient = ctx.createLinearGradient(0, top, 0, bottom);
                
                for (var i = 0, l = spec.colors.length; i < l; ++i) {
                    var c = spec.colors[i];
                    if (typeof c != "string") {
                        var co = $.color.parse(defaultColor);
                        if (c.brightness != null)
                            co = co.scale('rgb', c.brightness)
                        if (c.opacity != null)
                            co.a *= c.opacity;
                        c = co.toString();
                    }
                    gradient.addColorStop(i / (l - 1), c);
                }
                return gradient;
            }
        }
    }

    $.plot = function(placeholder, data, options) {
        //var t0 = new Date();
        var plot = new Plot($(placeholder), data, options, $.plot.plugins);
        //(window.console ? console.log : alert)("time used (msecs): " + ((new Date()).getTime() - t0.getTime()));
        return plot;
    };

    $.plot.plugins = [];

    // returns a string with the date d formatted according to fmt
    $.plot.formatDate = function(d, fmt, monthNames) {
        var leftPad = function(n) {
            n = "" + n;
            return n.length == 1 ? "0" + n : n;
        };
        
        var r = [];
        var escape = false, padNext = false;
        var hours = d.getUTCHours();
        var isAM = hours < 12;
        if (monthNames == null)
            monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        if (fmt.search(/%p|%P/) != -1) {
            if (hours > 12) {
                hours = hours - 12;
            } else if (hours == 0) {
                hours = 12;
            }
        }
        for (var i = 0; i < fmt.length; ++i) {
            var c = fmt.charAt(i);
            
            if (escape) {
                switch (c) {
                case 'h': c = "" + hours; break;
                case 'H': c = leftPad(hours); break;
                case 'M': c = leftPad(d.getUTCMinutes()); break;
                case 'S': c = leftPad(d.getUTCSeconds()); break;
                case 'd': c = "" + d.getUTCDate(); break;
                case 'm': c = "" + (d.getUTCMonth() + 1); break;
                case 'y': c = "" + d.getUTCFullYear(); break;
                case 'b': c = "" + monthNames[d.getUTCMonth()]; break;
                case 'p': c = (isAM) ? ("" + "am") : ("" + "pm"); break;
                case 'P': c = (isAM) ? ("" + "AM") : ("" + "PM"); break;
                case '0': c = ""; padNext = true; break;
                }
                if (c && padNext) {
                    c = leftPad(c);
                    padNext = false;
                }
                r.push(c);
                if (!padNext)
                    escape = false;
            }
            else {
                if (c == "%")
                    escape = true;
                else
                    r.push(c);
            }
        }
        return r.join("");
    };
    
    // round to nearby lower multiple of base
    function floorInBase(n, base) {
        return base * Math.floor(n / base);
    }
    
})(jQuery);
/*
 * Flot plugin for grouped bar charts. Only logical to use with auto barwidths
 * 
 * Borrowed general ideas from flot.stack.js. 
 * Hooks into drawSeries(), because auto barwidths are calculated after processDatapoints
 * 
 *
 * Known issues:
 * Currently doesn't work with horizontal charts
 *
 *
 * @author Andy Fowler
 *
 */

(function ($) {
	var options = {
		series: { group: false }
	};
	
	function init(plot) {
		// return an array of other series in this group
		function findOtherSeries(mySerie, allSeries) {
			var result = [];

			for (var i=0, serie; serie = allSeries[i]; i++) {
				if (mySerie == serie) continue;
				
				if (mySerie.group == serie.group) result.push(serie);
			}
			
			return result;
		}
		
		function findPlaceInSeries(mySerie, allSeries) {
			for (var i=0, serie; serie = allSeries[i]; i++) {
				if (mySerie == serie) return (i + 1);
			}
		}
		
		function groupData(plot, canvas, serie) {
			if (! serie.group) return;
			
			var otherSeries = findOtherSeries(serie, plot.getData()),
				numSeries = otherSeries.length + 1,
				placeInSeries = findPlaceInSeries(serie, plot.getData());
				
			if (numSeries == 1) return; // nothing to do with a group of 1
			
			// shrink bar widths to fit
			serie.bars.barWidth = serie.bars.barWidth / numSeries;
			
			if ( placeInSeries == 1) return; // first group is not offset
			
			var offset = serie.bars.barWidth * (placeInSeries - 1);

			for (var i=0; i < serie.datapoints.points.length; i += serie.datapoints.pointsize) {
				serie.datapoints.points[i] += offset;
			}
			
			return;
		}

		plot.hooks.drawSeries.push(groupData);
	}
	
	$.plot.plugins.push({
		init: init,
		options: options,
		name: 'grouped'
	});
})(jQuery);
/*
Flot plugin for stacking data sets, i.e. putting them on top of each
other, for accumulative graphs.

The plugin assumes the data is sorted on x (or y if stacking
horizontally). For line charts, it is assumed that if a line has an
undefined gap (from a null point), then the line above it should have
the same gap - insert zeros instead of "null" if you want another
behaviour. This also holds for the start and end of the chart. Note
that stacking a mix of positive and negative values in most instances
doesn't make sense (so it looks weird).

Two or more series are stacked when their "stack" attribute is set to
the same key (which can be any number or string or just "true"). To
specify the default stack, you can set

  series: {
    stack: null or true or key (number/string)
  }

or specify it for a specific series

  $.plot($("#placeholder"), [{ data: [ ... ], stack: true }])
  
The stacking order is determined by the order of the data series in
the array (later series end up on top of the previous).

Internally, the plugin modifies the datapoints in each series, adding
an offset to the y value. For line series, extra data points are
inserted through interpolation. If there's a second y value, it's also
adjusted (e.g for bar charts or filled areas).
*/

(function ($) {
    var options = {
        series: { stack: null } // or number/string
    };
    
    function init(plot) {
        function findMatchingSeries(s, allseries) {
            var res = null;
            for (var i = 0; i < allseries.length; ++i) {
                if (s == allseries[i])
                    break;
                
                if (allseries[i].stack == s.stack)
                    res = allseries[i];
            }
            
            return res;
        }
        
        function stackData(plot, s, datapoints) {
            if (s.stack == null)
                return;

            var other = findMatchingSeries(s, plot.getData());
            if (!other)
                return;

            var ps = datapoints.pointsize,
                points = datapoints.points,
                otherps = other.datapoints.pointsize,
                otherpoints = other.datapoints.points,
                newpoints = [],
                px, py, intery, qx, qy, bottom,
                withlines = s.lines.show,
                horizontal = s.bars.horizontal,
                withbottom = ps > 2 && (horizontal ? datapoints.format[2].x : datapoints.format[2].y),
                withsteps = withlines && s.lines.steps,
                fromgap = true,
                keyOffset = horizontal ? 1 : 0,
                accumulateOffset = horizontal ? 0 : 1,
                i = 0, j = 0, l;

            while (true) {
                if (i >= points.length)
                    break;

                l = newpoints.length;

                if (points[i] == null) {
                    // copy gaps
                    for (m = 0; m < ps; ++m)
                        newpoints.push(points[i + m]);
                    i += ps;
                }
                else if (j >= otherpoints.length) {
                    // for lines, we can't use the rest of the points
                    if (!withlines) {
                        for (m = 0; m < ps; ++m)
                            newpoints.push(points[i + m]);
                    }
                    i += ps;
                }
                else if (otherpoints[j] == null) {
                    // oops, got a gap
                    for (m = 0; m < ps; ++m)
                        newpoints.push(null);
                    fromgap = true;
                    j += otherps;
                }
                else {
                    // cases where we actually got two points
                    px = points[i + keyOffset];
                    py = points[i + accumulateOffset];
                    qx = otherpoints[j + keyOffset];
                    qy = otherpoints[j + accumulateOffset];
                    bottom = 0;

                    if (px == qx) {
                        for (m = 0; m < ps; ++m)
                            newpoints.push(points[i + m]);

                        newpoints[l + accumulateOffset] += qy;
                        bottom = qy;
                        
                        i += ps;
                        j += otherps;
                    }
                    else if (px > qx) {
                        // we got past point below, might need to
                        // insert interpolated extra point
                        if (withlines && i > 0 && points[i - ps] != null) {
                            intery = py + (points[i - ps + accumulateOffset] - py) * (qx - px) / (points[i - ps + keyOffset] - px);
                            newpoints.push(qx);
                            newpoints.push(intery + qy);
                            for (m = 2; m < ps; ++m)
                                newpoints.push(points[i + m]);
                            bottom = qy; 
                        }

                        j += otherps;
                    }
                    else { // px < qx
                        if (fromgap && withlines) {
                            // if we come from a gap, we just skip this point
                            i += ps;
                            continue;
                        }
                            
                        for (m = 0; m < ps; ++m)
                            newpoints.push(points[i + m]);
                        
                        // we might be able to interpolate a point below,
                        // this can give us a better y
                        if (withlines && j > 0 && otherpoints[j - otherps] != null)
                            bottom = qy + (otherpoints[j - otherps + accumulateOffset] - qy) * (px - qx) / (otherpoints[j - otherps + keyOffset] - qx);

                        newpoints[l + accumulateOffset] += bottom;
                        
                        i += ps;
                    }

                    fromgap = false;
                    
                    if (l != newpoints.length && withbottom)
                        newpoints[l + 2] += bottom;
                }

                // maintain the line steps invariant
                if (withsteps && l != newpoints.length && l > 0
                    && newpoints[l] != null
                    && newpoints[l] != newpoints[l - ps]
                    && newpoints[l + 1] != newpoints[l - ps + 1]) {
                    for (m = 0; m < ps; ++m)
                        newpoints[l + ps + m] = newpoints[l + m];
                    newpoints[l + 1] = newpoints[l - ps + 1];
                }
            }

            datapoints.points = newpoints;
        }
        
        plot.hooks.processDatapoints.push(stackData);
    }
    
    $.plot.plugins.push({
        init: init,
        options: options,
        name: 'stack',
        version: '1.2'
    });
})(jQuery);
/**!

 @license
 handlebars v4.7.7

Copyright (C) 2011-2019 by Yehuda Katz

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.

*/
(function webpackUniversalModuleDefinition(root, factory) {
	if(typeof exports === 'object' && typeof module === 'object')
		module.exports = factory();
	else if(typeof define === 'function' && define.amd)
		define([], factory);
	else if(typeof exports === 'object')
		exports["Handlebars"] = factory();
	else
		root["Handlebars"] = factory();
})(this, function() {
return /******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};

/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {

/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId])
/******/ 			return installedModules[moduleId].exports;

/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			exports: {},
/******/ 			id: moduleId,
/******/ 			loaded: false
/******/ 		};

/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);

/******/ 		// Flag the module as loaded
/******/ 		module.loaded = true;

/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}


/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;

/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;

/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";

/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(0);
/******/ })
/************************************************************************/
/******/ ([
/* 0 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;

	var _handlebarsRuntime = __webpack_require__(2);

	var _handlebarsRuntime2 = _interopRequireDefault(_handlebarsRuntime);

	// Compiler imports

	var _handlebarsCompilerAst = __webpack_require__(45);

	var _handlebarsCompilerAst2 = _interopRequireDefault(_handlebarsCompilerAst);

	var _handlebarsCompilerBase = __webpack_require__(46);

	var _handlebarsCompilerCompiler = __webpack_require__(51);

	var _handlebarsCompilerJavascriptCompiler = __webpack_require__(52);

	var _handlebarsCompilerJavascriptCompiler2 = _interopRequireDefault(_handlebarsCompilerJavascriptCompiler);

	var _handlebarsCompilerVisitor = __webpack_require__(49);

	var _handlebarsCompilerVisitor2 = _interopRequireDefault(_handlebarsCompilerVisitor);

	var _handlebarsNoConflict = __webpack_require__(44);

	var _handlebarsNoConflict2 = _interopRequireDefault(_handlebarsNoConflict);

	var _create = _handlebarsRuntime2['default'].create;
	function create() {
	  var hb = _create();

	  hb.compile = function (input, options) {
	    return _handlebarsCompilerCompiler.compile(input, options, hb);
	  };
	  hb.precompile = function (input, options) {
	    return _handlebarsCompilerCompiler.precompile(input, options, hb);
	  };

	  hb.AST = _handlebarsCompilerAst2['default'];
	  hb.Compiler = _handlebarsCompilerCompiler.Compiler;
	  hb.JavaScriptCompiler = _handlebarsCompilerJavascriptCompiler2['default'];
	  hb.Parser = _handlebarsCompilerBase.parser;
	  hb.parse = _handlebarsCompilerBase.parse;
	  hb.parseWithoutProcessing = _handlebarsCompilerBase.parseWithoutProcessing;

	  return hb;
	}

	var inst = create();
	inst.create = create;

	_handlebarsNoConflict2['default'](inst);

	inst.Visitor = _handlebarsCompilerVisitor2['default'];

	inst['default'] = inst;

	exports['default'] = inst;
	module.exports = exports['default'];

/***/ }),
/* 1 */
/***/ (function(module, exports) {

	"use strict";

	exports["default"] = function (obj) {
	  return obj && obj.__esModule ? obj : {
	    "default": obj
	  };
	};

	exports.__esModule = true;

/***/ }),
/* 2 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireWildcard = __webpack_require__(3)['default'];

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;

	var _handlebarsBase = __webpack_require__(4);

	var base = _interopRequireWildcard(_handlebarsBase);

	// Each of these augment the Handlebars object. No need to setup here.
	// (This is done to easily share code between commonjs and browse envs)

	var _handlebarsSafeString = __webpack_require__(37);

	var _handlebarsSafeString2 = _interopRequireDefault(_handlebarsSafeString);

	var _handlebarsException = __webpack_require__(6);

	var _handlebarsException2 = _interopRequireDefault(_handlebarsException);

	var _handlebarsUtils = __webpack_require__(5);

	var Utils = _interopRequireWildcard(_handlebarsUtils);

	var _handlebarsRuntime = __webpack_require__(38);

	var runtime = _interopRequireWildcard(_handlebarsRuntime);

	var _handlebarsNoConflict = __webpack_require__(44);

	var _handlebarsNoConflict2 = _interopRequireDefault(_handlebarsNoConflict);

	// For compatibility and usage outside of module systems, make the Handlebars object a namespace
	function create() {
	  var hb = new base.HandlebarsEnvironment();

	  Utils.extend(hb, base);
	  hb.SafeString = _handlebarsSafeString2['default'];
	  hb.Exception = _handlebarsException2['default'];
	  hb.Utils = Utils;
	  hb.escapeExpression = Utils.escapeExpression;

	  hb.VM = runtime;
	  hb.template = function (spec) {
	    return runtime.template(spec, hb);
	  };

	  return hb;
	}

	var inst = create();
	inst.create = create;

	_handlebarsNoConflict2['default'](inst);

	inst['default'] = inst;

	exports['default'] = inst;
	module.exports = exports['default'];

/***/ }),
/* 3 */
/***/ (function(module, exports) {

	"use strict";

	exports["default"] = function (obj) {
	  if (obj && obj.__esModule) {
	    return obj;
	  } else {
	    var newObj = {};

	    if (obj != null) {
	      for (var key in obj) {
	        if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key];
	      }
	    }

	    newObj["default"] = obj;
	    return newObj;
	  }
	};

	exports.__esModule = true;

/***/ }),
/* 4 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;
	exports.HandlebarsEnvironment = HandlebarsEnvironment;

	var _utils = __webpack_require__(5);

	var _exception = __webpack_require__(6);

	var _exception2 = _interopRequireDefault(_exception);

	var _helpers = __webpack_require__(10);

	var _decorators = __webpack_require__(30);

	var _logger = __webpack_require__(32);

	var _logger2 = _interopRequireDefault(_logger);

	var _internalProtoAccess = __webpack_require__(33);

	var VERSION = '4.7.7';
	exports.VERSION = VERSION;
	var COMPILER_REVISION = 8;
	exports.COMPILER_REVISION = COMPILER_REVISION;
	var LAST_COMPATIBLE_COMPILER_REVISION = 7;

	exports.LAST_COMPATIBLE_COMPILER_REVISION = LAST_COMPATIBLE_COMPILER_REVISION;
	var REVISION_CHANGES = {
	  1: '<= 1.0.rc.2', // 1.0.rc.2 is actually rev2 but doesn't report it
	  2: '== 1.0.0-rc.3',
	  3: '== 1.0.0-rc.4',
	  4: '== 1.x.x',
	  5: '== 2.0.0-alpha.x',
	  6: '>= 2.0.0-beta.1',
	  7: '>= 4.0.0 <4.3.0',
	  8: '>= 4.3.0'
	};

	exports.REVISION_CHANGES = REVISION_CHANGES;
	var objectType = '[object Object]';

	function HandlebarsEnvironment(helpers, partials, decorators) {
	  this.helpers = helpers || {};
	  this.partials = partials || {};
	  this.decorators = decorators || {};

	  _helpers.registerDefaultHelpers(this);
	  _decorators.registerDefaultDecorators(this);
	}

	HandlebarsEnvironment.prototype = {
	  constructor: HandlebarsEnvironment,

	  logger: _logger2['default'],
	  log: _logger2['default'].log,

	  registerHelper: function registerHelper(name, fn) {
	    if (_utils.toString.call(name) === objectType) {
	      if (fn) {
	        throw new _exception2['default']('Arg not supported with multiple helpers');
	      }
	      _utils.extend(this.helpers, name);
	    } else {
	      this.helpers[name] = fn;
	    }
	  },
	  unregisterHelper: function unregisterHelper(name) {
	    delete this.helpers[name];
	  },

	  registerPartial: function registerPartial(name, partial) {
	    if (_utils.toString.call(name) === objectType) {
	      _utils.extend(this.partials, name);
	    } else {
	      if (typeof partial === 'undefined') {
	        throw new _exception2['default']('Attempting to register a partial called "' + name + '" as undefined');
	      }
	      this.partials[name] = partial;
	    }
	  },
	  unregisterPartial: function unregisterPartial(name) {
	    delete this.partials[name];
	  },

	  registerDecorator: function registerDecorator(name, fn) {
	    if (_utils.toString.call(name) === objectType) {
	      if (fn) {
	        throw new _exception2['default']('Arg not supported with multiple decorators');
	      }
	      _utils.extend(this.decorators, name);
	    } else {
	      this.decorators[name] = fn;
	    }
	  },
	  unregisterDecorator: function unregisterDecorator(name) {
	    delete this.decorators[name];
	  },
	  /**
	   * Reset the memory of illegal property accesses that have already been logged.
	   * @deprecated should only be used in handlebars test-cases
	   */
	  resetLoggedPropertyAccesses: function resetLoggedPropertyAccesses() {
	    _internalProtoAccess.resetLoggedProperties();
	  }
	};

	var log = _logger2['default'].log;

	exports.log = log;
	exports.createFrame = _utils.createFrame;
	exports.logger = _logger2['default'];

/***/ }),
/* 5 */
/***/ (function(module, exports) {

	'use strict';

	exports.__esModule = true;
	exports.extend = extend;
	exports.indexOf = indexOf;
	exports.escapeExpression = escapeExpression;
	exports.isEmpty = isEmpty;
	exports.createFrame = createFrame;
	exports.blockParams = blockParams;
	exports.appendContextPath = appendContextPath;
	var escape = {
	  '&': '&amp;',
	  '<': '&lt;',
	  '>': '&gt;',
	  '"': '&quot;',
	  "'": '&#x27;',
	  '`': '&#x60;',
	  '=': '&#x3D;'
	};

	var badChars = /[&<>"'`=]/g,
	    possible = /[&<>"'`=]/;

	function escapeChar(chr) {
	  return escape[chr];
	}

	function extend(obj /* , ...source */) {
	  for (var i = 1; i < arguments.length; i++) {
	    for (var key in arguments[i]) {
	      if (Object.prototype.hasOwnProperty.call(arguments[i], key)) {
	        obj[key] = arguments[i][key];
	      }
	    }
	  }

	  return obj;
	}

	var toString = Object.prototype.toString;

	exports.toString = toString;
	// Sourced from lodash
	// https://github.com/bestiejs/lodash/blob/master/LICENSE.txt
	/* eslint-disable func-style */
	var isFunction = function isFunction(value) {
	  return typeof value === 'function';
	};
	// fallback for older versions of Chrome and Safari
	/* istanbul ignore next */
	if (isFunction(/x/)) {
	  exports.isFunction = isFunction = function (value) {
	    return typeof value === 'function' && toString.call(value) === '[object Function]';
	  };
	}
	exports.isFunction = isFunction;

	/* eslint-enable func-style */

	/* istanbul ignore next */
	var isArray = Array.isArray || function (value) {
	  return value && typeof value === 'object' ? toString.call(value) === '[object Array]' : false;
	};

	exports.isArray = isArray;
	// Older IE versions do not directly support indexOf so we must implement our own, sadly.

	function indexOf(array, value) {
	  for (var i = 0, len = array.length; i < len; i++) {
	    if (array[i] === value) {
	      return i;
	    }
	  }
	  return -1;
	}

	function escapeExpression(string) {
	  if (typeof string !== 'string') {
	    // don't escape SafeStrings, since they're already safe
	    if (string && string.toHTML) {
	      return string.toHTML();
	    } else if (string == null) {
	      return '';
	    } else if (!string) {
	      return string + '';
	    }

	    // Force a string conversion as this will be done by the append regardless and
	    // the regex test will do this transparently behind the scenes, causing issues if
	    // an object's to string has escaped characters in it.
	    string = '' + string;
	  }

	  if (!possible.test(string)) {
	    return string;
	  }
	  return string.replace(badChars, escapeChar);
	}

	function isEmpty(value) {
	  if (!value && value !== 0) {
	    return true;
	  } else if (isArray(value) && value.length === 0) {
	    return true;
	  } else {
	    return false;
	  }
	}

	function createFrame(object) {
	  var frame = extend({}, object);
	  frame._parent = object;
	  return frame;
	}

	function blockParams(params, ids) {
	  params.path = ids;
	  return params;
	}

	function appendContextPath(contextPath, id) {
	  return (contextPath ? contextPath + '.' : '') + id;
	}

/***/ }),
/* 6 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _Object$defineProperty = __webpack_require__(7)['default'];

	exports.__esModule = true;
	var errorProps = ['description', 'fileName', 'lineNumber', 'endLineNumber', 'message', 'name', 'number', 'stack'];

	function Exception(message, node) {
	  var loc = node && node.loc,
	      line = undefined,
	      endLineNumber = undefined,
	      column = undefined,
	      endColumn = undefined;

	  if (loc) {
	    line = loc.start.line;
	    endLineNumber = loc.end.line;
	    column = loc.start.column;
	    endColumn = loc.end.column;

	    message += ' - ' + line + ':' + column;
	  }

	  var tmp = Error.prototype.constructor.call(this, message);

	  // Unfortunately errors are not enumerable in Chrome (at least), so `for prop in tmp` doesn't work.
	  for (var idx = 0; idx < errorProps.length; idx++) {
	    this[errorProps[idx]] = tmp[errorProps[idx]];
	  }

	  /* istanbul ignore else */
	  if (Error.captureStackTrace) {
	    Error.captureStackTrace(this, Exception);
	  }

	  try {
	    if (loc) {
	      this.lineNumber = line;
	      this.endLineNumber = endLineNumber;

	      // Work around issue under safari where we can't directly set the column value
	      /* istanbul ignore next */
	      if (_Object$defineProperty) {
	        Object.defineProperty(this, 'column', {
	          value: column,
	          enumerable: true
	        });
	        Object.defineProperty(this, 'endColumn', {
	          value: endColumn,
	          enumerable: true
	        });
	      } else {
	        this.column = column;
	        this.endColumn = endColumn;
	      }
	    }
	  } catch (nop) {
	    /* Ignore if the browser is very particular */
	  }
	}

	Exception.prototype = new Error();

	exports['default'] = Exception;
	module.exports = exports['default'];

/***/ }),
/* 7 */
/***/ (function(module, exports, __webpack_require__) {

	module.exports = { "default": __webpack_require__(8), __esModule: true };

/***/ }),
/* 8 */
/***/ (function(module, exports, __webpack_require__) {

	var $ = __webpack_require__(9);
	module.exports = function defineProperty(it, key, desc){
	  return $.setDesc(it, key, desc);
	};

/***/ }),
/* 9 */
/***/ (function(module, exports) {

	var $Object = Object;
	module.exports = {
	  create:     $Object.create,
	  getProto:   $Object.getPrototypeOf,
	  isEnum:     {}.propertyIsEnumerable,
	  getDesc:    $Object.getOwnPropertyDescriptor,
	  setDesc:    $Object.defineProperty,
	  setDescs:   $Object.defineProperties,
	  getKeys:    $Object.keys,
	  getNames:   $Object.getOwnPropertyNames,
	  getSymbols: $Object.getOwnPropertySymbols,
	  each:       [].forEach
	};

/***/ }),
/* 10 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;
	exports.registerDefaultHelpers = registerDefaultHelpers;
	exports.moveHelperToHooks = moveHelperToHooks;

	var _helpersBlockHelperMissing = __webpack_require__(11);

	var _helpersBlockHelperMissing2 = _interopRequireDefault(_helpersBlockHelperMissing);

	var _helpersEach = __webpack_require__(12);

	var _helpersEach2 = _interopRequireDefault(_helpersEach);

	var _helpersHelperMissing = __webpack_require__(25);

	var _helpersHelperMissing2 = _interopRequireDefault(_helpersHelperMissing);

	var _helpersIf = __webpack_require__(26);

	var _helpersIf2 = _interopRequireDefault(_helpersIf);

	var _helpersLog = __webpack_require__(27);

	var _helpersLog2 = _interopRequireDefault(_helpersLog);

	var _helpersLookup = __webpack_require__(28);

	var _helpersLookup2 = _interopRequireDefault(_helpersLookup);

	var _helpersWith = __webpack_require__(29);

	var _helpersWith2 = _interopRequireDefault(_helpersWith);

	function registerDefaultHelpers(instance) {
	  _helpersBlockHelperMissing2['default'](instance);
	  _helpersEach2['default'](instance);
	  _helpersHelperMissing2['default'](instance);
	  _helpersIf2['default'](instance);
	  _helpersLog2['default'](instance);
	  _helpersLookup2['default'](instance);
	  _helpersWith2['default'](instance);
	}

	function moveHelperToHooks(instance, helperName, keepHelper) {
	  if (instance.helpers[helperName]) {
	    instance.hooks[helperName] = instance.helpers[helperName];
	    if (!keepHelper) {
	      delete instance.helpers[helperName];
	    }
	  }
	}

/***/ }),
/* 11 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	exports.__esModule = true;

	var _utils = __webpack_require__(5);

	exports['default'] = function (instance) {
	  instance.registerHelper('blockHelperMissing', function (context, options) {
	    var inverse = options.inverse,
	        fn = options.fn;

	    if (context === true) {
	      return fn(this);
	    } else if (context === false || context == null) {
	      return inverse(this);
	    } else if (_utils.isArray(context)) {
	      if (context.length > 0) {
	        if (options.ids) {
	          options.ids = [options.name];
	        }

	        return instance.helpers.each(context, options);
	      } else {
	        return inverse(this);
	      }
	    } else {
	      if (options.data && options.ids) {
	        var data = _utils.createFrame(options.data);
	        data.contextPath = _utils.appendContextPath(options.data.contextPath, options.name);
	        options = { data: data };
	      }

	      return fn(context, options);
	    }
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 12 */
/***/ (function(module, exports, __webpack_require__) {

	/* WEBPACK VAR INJECTION */(function(global) {'use strict';

	var _Object$keys = __webpack_require__(13)['default'];

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;

	var _utils = __webpack_require__(5);

	var _exception = __webpack_require__(6);

	var _exception2 = _interopRequireDefault(_exception);

	exports['default'] = function (instance) {
	  instance.registerHelper('each', function (context, options) {
	    if (!options) {
	      throw new _exception2['default']('Must pass iterator to #each');
	    }

	    var fn = options.fn,
	        inverse = options.inverse,
	        i = 0,
	        ret = '',
	        data = undefined,
	        contextPath = undefined;

	    if (options.data && options.ids) {
	      contextPath = _utils.appendContextPath(options.data.contextPath, options.ids[0]) + '.';
	    }

	    if (_utils.isFunction(context)) {
	      context = context.call(this);
	    }

	    if (options.data) {
	      data = _utils.createFrame(options.data);
	    }

	    function execIteration(field, index, last) {
	      if (data) {
	        data.key = field;
	        data.index = index;
	        data.first = index === 0;
	        data.last = !!last;

	        if (contextPath) {
	          data.contextPath = contextPath + field;
	        }
	      }

	      ret = ret + fn(context[field], {
	        data: data,
	        blockParams: _utils.blockParams([context[field], field], [contextPath + field, null])
	      });
	    }

	    if (context && typeof context === 'object') {
	      if (_utils.isArray(context)) {
	        for (var j = context.length; i < j; i++) {
	          if (i in context) {
	            execIteration(i, i, i === context.length - 1);
	          }
	        }
	      } else if (global.Symbol && context[global.Symbol.iterator]) {
	        var newContext = [];
	        var iterator = context[global.Symbol.iterator]();
	        for (var it = iterator.next(); !it.done; it = iterator.next()) {
	          newContext.push(it.value);
	        }
	        context = newContext;
	        for (var j = context.length; i < j; i++) {
	          execIteration(i, i, i === context.length - 1);
	        }
	      } else {
	        (function () {
	          var priorKey = undefined;

	          _Object$keys(context).forEach(function (key) {
	            // We're running the iterations one step out of sync so we can detect
	            // the last iteration without have to scan the object twice and create
	            // an itermediate keys array.
	            if (priorKey !== undefined) {
	              execIteration(priorKey, i - 1);
	            }
	            priorKey = key;
	            i++;
	          });
	          if (priorKey !== undefined) {
	            execIteration(priorKey, i - 1, true);
	          }
	        })();
	      }
	    }

	    if (i === 0) {
	      ret = inverse(this);
	    }

	    return ret;
	  });
	};

	module.exports = exports['default'];
	/* WEBPACK VAR INJECTION */}.call(exports, (function() { return this; }())))

/***/ }),
/* 13 */
/***/ (function(module, exports, __webpack_require__) {

	module.exports = { "default": __webpack_require__(14), __esModule: true };

/***/ }),
/* 14 */
/***/ (function(module, exports, __webpack_require__) {

	__webpack_require__(15);
	module.exports = __webpack_require__(21).Object.keys;

/***/ }),
/* 15 */
/***/ (function(module, exports, __webpack_require__) {

	// 19.1.2.14 Object.keys(O)
	var toObject = __webpack_require__(16);

	__webpack_require__(18)('keys', function($keys){
	  return function keys(it){
	    return $keys(toObject(it));
	  };
	});

/***/ }),
/* 16 */
/***/ (function(module, exports, __webpack_require__) {

	// 7.1.13 ToObject(argument)
	var defined = __webpack_require__(17);
	module.exports = function(it){
	  return Object(defined(it));
	};

/***/ }),
/* 17 */
/***/ (function(module, exports) {

	// 7.2.1 RequireObjectCoercible(argument)
	module.exports = function(it){
	  if(it == undefined)throw TypeError("Can't call method on  " + it);
	  return it;
	};

/***/ }),
/* 18 */
/***/ (function(module, exports, __webpack_require__) {

	// most Object methods by ES6 should accept primitives
	var $export = __webpack_require__(19)
	  , core    = __webpack_require__(21)
	  , fails   = __webpack_require__(24);
	module.exports = function(KEY, exec){
	  var fn  = (core.Object || {})[KEY] || Object[KEY]
	    , exp = {};
	  exp[KEY] = exec(fn);
	  $export($export.S + $export.F * fails(function(){ fn(1); }), 'Object', exp);
	};

/***/ }),
/* 19 */
/***/ (function(module, exports, __webpack_require__) {

	var global    = __webpack_require__(20)
	  , core      = __webpack_require__(21)
	  , ctx       = __webpack_require__(22)
	  , PROTOTYPE = 'prototype';

	var $export = function(type, name, source){
	  var IS_FORCED = type & $export.F
	    , IS_GLOBAL = type & $export.G
	    , IS_STATIC = type & $export.S
	    , IS_PROTO  = type & $export.P
	    , IS_BIND   = type & $export.B
	    , IS_WRAP   = type & $export.W
	    , exports   = IS_GLOBAL ? core : core[name] || (core[name] = {})
	    , target    = IS_GLOBAL ? global : IS_STATIC ? global[name] : (global[name] || {})[PROTOTYPE]
	    , key, own, out;
	  if(IS_GLOBAL)source = name;
	  for(key in source){
	    // contains in native
	    own = !IS_FORCED && target && key in target;
	    if(own && key in exports)continue;
	    // export native or passed
	    out = own ? target[key] : source[key];
	    // prevent global pollution for namespaces
	    exports[key] = IS_GLOBAL && typeof target[key] != 'function' ? source[key]
	    // bind timers to global for call from export context
	    : IS_BIND && own ? ctx(out, global)
	    // wrap global constructors for prevent change them in library
	    : IS_WRAP && target[key] == out ? (function(C){
	      var F = function(param){
	        return this instanceof C ? new C(param) : C(param);
	      };
	      F[PROTOTYPE] = C[PROTOTYPE];
	      return F;
	    // make static versions for prototype methods
	    })(out) : IS_PROTO && typeof out == 'function' ? ctx(Function.call, out) : out;
	    if(IS_PROTO)(exports[PROTOTYPE] || (exports[PROTOTYPE] = {}))[key] = out;
	  }
	};
	// type bitmap
	$export.F = 1;  // forced
	$export.G = 2;  // global
	$export.S = 4;  // static
	$export.P = 8;  // proto
	$export.B = 16; // bind
	$export.W = 32; // wrap
	module.exports = $export;

/***/ }),
/* 20 */
/***/ (function(module, exports) {

	// https://github.com/zloirock/core-js/issues/86#issuecomment-115759028
	var global = module.exports = typeof window != 'undefined' && window.Math == Math
	  ? window : typeof self != 'undefined' && self.Math == Math ? self : Function('return this')();
	if(typeof __g == 'number')__g = global; // eslint-disable-line no-undef

/***/ }),
/* 21 */
/***/ (function(module, exports) {

	var core = module.exports = {version: '1.2.6'};
	if(typeof __e == 'number')__e = core; // eslint-disable-line no-undef

/***/ }),
/* 22 */
/***/ (function(module, exports, __webpack_require__) {

	// optional / simple context binding
	var aFunction = __webpack_require__(23);
	module.exports = function(fn, that, length){
	  aFunction(fn);
	  if(that === undefined)return fn;
	  switch(length){
	    case 1: return function(a){
	      return fn.call(that, a);
	    };
	    case 2: return function(a, b){
	      return fn.call(that, a, b);
	    };
	    case 3: return function(a, b, c){
	      return fn.call(that, a, b, c);
	    };
	  }
	  return function(/* ...args */){
	    return fn.apply(that, arguments);
	  };
	};

/***/ }),
/* 23 */
/***/ (function(module, exports) {

	module.exports = function(it){
	  if(typeof it != 'function')throw TypeError(it + ' is not a function!');
	  return it;
	};

/***/ }),
/* 24 */
/***/ (function(module, exports) {

	module.exports = function(exec){
	  try {
	    return !!exec();
	  } catch(e){
	    return true;
	  }
	};

/***/ }),
/* 25 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;

	var _exception = __webpack_require__(6);

	var _exception2 = _interopRequireDefault(_exception);

	exports['default'] = function (instance) {
	  instance.registerHelper('helperMissing', function () /* [args, ]options */{
	    if (arguments.length === 1) {
	      // A missing field in a {{foo}} construct.
	      return undefined;
	    } else {
	      // Someone is actually trying to call something, blow up.
	      throw new _exception2['default']('Missing helper: "' + arguments[arguments.length - 1].name + '"');
	    }
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 26 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;

	var _utils = __webpack_require__(5);

	var _exception = __webpack_require__(6);

	var _exception2 = _interopRequireDefault(_exception);

	exports['default'] = function (instance) {
	  instance.registerHelper('if', function (conditional, options) {
	    if (arguments.length != 2) {
	      throw new _exception2['default']('#if requires exactly one argument');
	    }
	    if (_utils.isFunction(conditional)) {
	      conditional = conditional.call(this);
	    }

	    // Default behavior is to render the positive path if the value is truthy and not empty.
	    // The `includeZero` option may be set to treat the condtional as purely not empty based on the
	    // behavior of isEmpty. Effectively this determines if 0 is handled by the positive path or negative.
	    if (!options.hash.includeZero && !conditional || _utils.isEmpty(conditional)) {
	      return options.inverse(this);
	    } else {
	      return options.fn(this);
	    }
	  });

	  instance.registerHelper('unless', function (conditional, options) {
	    if (arguments.length != 2) {
	      throw new _exception2['default']('#unless requires exactly one argument');
	    }
	    return instance.helpers['if'].call(this, conditional, {
	      fn: options.inverse,
	      inverse: options.fn,
	      hash: options.hash
	    });
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 27 */
/***/ (function(module, exports) {

	'use strict';

	exports.__esModule = true;

	exports['default'] = function (instance) {
	  instance.registerHelper('log', function () /* message, options */{
	    var args = [undefined],
	        options = arguments[arguments.length - 1];
	    for (var i = 0; i < arguments.length - 1; i++) {
	      args.push(arguments[i]);
	    }

	    var level = 1;
	    if (options.hash.level != null) {
	      level = options.hash.level;
	    } else if (options.data && options.data.level != null) {
	      level = options.data.level;
	    }
	    args[0] = level;

	    instance.log.apply(instance, args);
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 28 */
/***/ (function(module, exports) {

	'use strict';

	exports.__esModule = true;

	exports['default'] = function (instance) {
	  instance.registerHelper('lookup', function (obj, field, options) {
	    if (!obj) {
	      // Note for 5.0: Change to "obj == null" in 5.0
	      return obj;
	    }
	    return options.lookupProperty(obj, field);
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 29 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;

	var _utils = __webpack_require__(5);

	var _exception = __webpack_require__(6);

	var _exception2 = _interopRequireDefault(_exception);

	exports['default'] = function (instance) {
	  instance.registerHelper('with', function (context, options) {
	    if (arguments.length != 2) {
	      throw new _exception2['default']('#with requires exactly one argument');
	    }
	    if (_utils.isFunction(context)) {
	      context = context.call(this);
	    }

	    var fn = options.fn;

	    if (!_utils.isEmpty(context)) {
	      var data = options.data;
	      if (options.data && options.ids) {
	        data = _utils.createFrame(options.data);
	        data.contextPath = _utils.appendContextPath(options.data.contextPath, options.ids[0]);
	      }

	      return fn(context, {
	        data: data,
	        blockParams: _utils.blockParams([context], [data && data.contextPath])
	      });
	    } else {
	      return options.inverse(this);
	    }
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 30 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;
	exports.registerDefaultDecorators = registerDefaultDecorators;

	var _decoratorsInline = __webpack_require__(31);

	var _decoratorsInline2 = _interopRequireDefault(_decoratorsInline);

	function registerDefaultDecorators(instance) {
	  _decoratorsInline2['default'](instance);
	}

/***/ }),
/* 31 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	exports.__esModule = true;

	var _utils = __webpack_require__(5);

	exports['default'] = function (instance) {
	  instance.registerDecorator('inline', function (fn, props, container, options) {
	    var ret = fn;
	    if (!props.partials) {
	      props.partials = {};
	      ret = function (context, options) {
	        // Create a new partials stack frame prior to exec.
	        var original = container.partials;
	        container.partials = _utils.extend({}, original, props.partials);
	        var ret = fn(context, options);
	        container.partials = original;
	        return ret;
	      };
	    }

	    props.partials[options.args[0]] = options.fn;

	    return ret;
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 32 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	exports.__esModule = true;

	var _utils = __webpack_require__(5);

	var logger = {
	  methodMap: ['debug', 'info', 'warn', 'error'],
	  level: 'info',

	  // Maps a given level value to the `methodMap` indexes above.
	  lookupLevel: function lookupLevel(level) {
	    if (typeof level === 'string') {
	      var levelMap = _utils.indexOf(logger.methodMap, level.toLowerCase());
	      if (levelMap >= 0) {
	        level = levelMap;
	      } else {
	        level = parseInt(level, 10);
	      }
	    }

	    return level;
	  },

	  // Can be overridden in the host environment
	  log: function log(level) {
	    level = logger.lookupLevel(level);

	    if (typeof console !== 'undefined' && logger.lookupLevel(logger.level) <= level) {
	      var method = logger.methodMap[level];
	      // eslint-disable-next-line no-console
	      if (!console[method]) {
	        method = 'log';
	      }

	      for (var _len = arguments.length, message = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
	        message[_key - 1] = arguments[_key];
	      }

	      console[method].apply(console, message); // eslint-disable-line no-console
	    }
	  }
	};

	exports['default'] = logger;
	module.exports = exports['default'];

/***/ }),
/* 33 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _Object$create = __webpack_require__(34)['default'];

	var _Object$keys = __webpack_require__(13)['default'];

	var _interopRequireWildcard = __webpack_require__(3)['default'];

	exports.__esModule = true;
	exports.createProtoAccessControl = createProtoAccessControl;
	exports.resultIsAllowed = resultIsAllowed;
	exports.resetLoggedProperties = resetLoggedProperties;

	var _createNewLookupObject = __webpack_require__(36);

	var _logger = __webpack_require__(32);

	var logger = _interopRequireWildcard(_logger);

	var loggedProperties = _Object$create(null);

	function createProtoAccessControl(runtimeOptions) {
	  var defaultMethodWhiteList = _Object$create(null);
	  defaultMethodWhiteList['constructor'] = false;
	  defaultMethodWhiteList['__defineGetter__'] = false;
	  defaultMethodWhiteList['__defineSetter__'] = false;
	  defaultMethodWhiteList['__lookupGetter__'] = false;

	  var defaultPropertyWhiteList = _Object$create(null);
	  // eslint-disable-next-line no-proto
	  defaultPropertyWhiteList['__proto__'] = false;

	  return {
	    properties: {
	      whitelist: _createNewLookupObject.createNewLookupObject(defaultPropertyWhiteList, runtimeOptions.allowedProtoProperties),
	      defaultValue: runtimeOptions.allowProtoPropertiesByDefault
	    },
	    methods: {
	      whitelist: _createNewLookupObject.createNewLookupObject(defaultMethodWhiteList, runtimeOptions.allowedProtoMethods),
	      defaultValue: runtimeOptions.allowProtoMethodsByDefault
	    }
	  };
	}

	function resultIsAllowed(result, protoAccessControl, propertyName) {
	  if (typeof result === 'function') {
	    return checkWhiteList(protoAccessControl.methods, propertyName);
	  } else {
	    return checkWhiteList(protoAccessControl.properties, propertyName);
	  }
	}

	function checkWhiteList(protoAccessControlForType, propertyName) {
	  if (protoAccessControlForType.whitelist[propertyName] !== undefined) {
	    return protoAccessControlForType.whitelist[propertyName] === true;
	  }
	  if (protoAccessControlForType.defaultValue !== undefined) {
	    return protoAccessControlForType.defaultValue;
	  }
	  logUnexpecedPropertyAccessOnce(propertyName);
	  return false;
	}

	function logUnexpecedPropertyAccessOnce(propertyName) {
	  if (loggedProperties[propertyName] !== true) {
	    loggedProperties[propertyName] = true;
	    logger.log('error', 'Handlebars: Access has been denied to resolve the property "' + propertyName + '" because it is not an "own property" of its parent.\n' + 'You can add a runtime option to disable the check or this warning:\n' + 'See https://handlebarsjs.com/api-reference/runtime-options.html#options-to-control-prototype-access for details');
	  }
	}

	function resetLoggedProperties() {
	  _Object$keys(loggedProperties).forEach(function (propertyName) {
	    delete loggedProperties[propertyName];
	  });
	}

/***/ }),
/* 34 */
/***/ (function(module, exports, __webpack_require__) {

	module.exports = { "default": __webpack_require__(35), __esModule: true };

/***/ }),
/* 35 */
/***/ (function(module, exports, __webpack_require__) {

	var $ = __webpack_require__(9);
	module.exports = function create(P, D){
	  return $.create(P, D);
	};

/***/ }),
/* 36 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _Object$create = __webpack_require__(34)['default'];

	exports.__esModule = true;
	exports.createNewLookupObject = createNewLookupObject;

	var _utils = __webpack_require__(5);

	/**
	 * Create a new object with "null"-prototype to avoid truthy results on prototype properties.
	 * The resulting object can be used with "object[property]" to check if a property exists
	 * @param {...object} sources a varargs parameter of source objects that will be merged
	 * @returns {object}
	 */

	function createNewLookupObject() {
	  for (var _len = arguments.length, sources = Array(_len), _key = 0; _key < _len; _key++) {
	    sources[_key] = arguments[_key];
	  }

	  return _utils.extend.apply(undefined, [_Object$create(null)].concat(sources));
	}

/***/ }),
/* 37 */
/***/ (function(module, exports) {

	// Build out our basic SafeString type
	'use strict';

	exports.__esModule = true;
	function SafeString(string) {
	  this.string = string;
	}

	SafeString.prototype.toString = SafeString.prototype.toHTML = function () {
	  return '' + this.string;
	};

	exports['default'] = SafeString;
	module.exports = exports['default'];

/***/ }),
/* 38 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _Object$seal = __webpack_require__(39)['default'];

	var _Object$keys = __webpack_require__(13)['default'];

	var _interopRequireWildcard = __webpack_require__(3)['default'];

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;
	exports.checkRevision = checkRevision;
	exports.template = template;
	exports.wrapProgram = wrapProgram;
	exports.resolvePartial = resolvePartial;
	exports.invokePartial = invokePartial;
	exports.noop = noop;

	var _utils = __webpack_require__(5);

	var Utils = _interopRequireWildcard(_utils);

	var _exception = __webpack_require__(6);

	var _exception2 = _interopRequireDefault(_exception);

	var _base = __webpack_require__(4);

	var _helpers = __webpack_require__(10);

	var _internalWrapHelper = __webpack_require__(43);

	var _internalProtoAccess = __webpack_require__(33);

	function checkRevision(compilerInfo) {
	  var compilerRevision = compilerInfo && compilerInfo[0] || 1,
	      currentRevision = _base.COMPILER_REVISION;

	  if (compilerRevision >= _base.LAST_COMPATIBLE_COMPILER_REVISION && compilerRevision <= _base.COMPILER_REVISION) {
	    return;
	  }

	  if (compilerRevision < _base.LAST_COMPATIBLE_COMPILER_REVISION) {
	    var runtimeVersions = _base.REVISION_CHANGES[currentRevision],
	        compilerVersions = _base.REVISION_CHANGES[compilerRevision];
	    throw new _exception2['default']('Template was precompiled with an older version of Handlebars than the current runtime. ' + 'Please update your precompiler to a newer version (' + runtimeVersions + ') or downgrade your runtime to an older version (' + compilerVersions + ').');
	  } else {
	    // Use the embedded version info since the runtime doesn't know about this revision yet
	    throw new _exception2['default']('Template was precompiled with a newer version of Handlebars than the current runtime. ' + 'Please update your runtime to a newer version (' + compilerInfo[1] + ').');
	  }
	}

	function template(templateSpec, env) {
	  /* istanbul ignore next */
	  if (!env) {
	    throw new _exception2['default']('No environment passed to template');
	  }
	  if (!templateSpec || !templateSpec.main) {
	    throw new _exception2['default']('Unknown template object: ' + typeof templateSpec);
	  }

	  templateSpec.main.decorator = templateSpec.main_d;

	  // Note: Using env.VM references rather than local var references throughout this section to allow
	  // for external users to override these as pseudo-supported APIs.
	  env.VM.checkRevision(templateSpec.compiler);

	  // backwards compatibility for precompiled templates with compiler-version 7 (<4.3.0)
	  var templateWasPrecompiledWithCompilerV7 = templateSpec.compiler && templateSpec.compiler[0] === 7;

	  function invokePartialWrapper(partial, context, options) {
	    if (options.hash) {
	      context = Utils.extend({}, context, options.hash);
	      if (options.ids) {
	        options.ids[0] = true;
	      }
	    }
	    partial = env.VM.resolvePartial.call(this, partial, context, options);

	    var extendedOptions = Utils.extend({}, options, {
	      hooks: this.hooks,
	      protoAccessControl: this.protoAccessControl
	    });

	    var result = env.VM.invokePartial.call(this, partial, context, extendedOptions);

	    if (result == null && env.compile) {
	      options.partials[options.name] = env.compile(partial, templateSpec.compilerOptions, env);
	      result = options.partials[options.name](context, extendedOptions);
	    }
	    if (result != null) {
	      if (options.indent) {
	        var lines = result.split('\n');
	        for (var i = 0, l = lines.length; i < l; i++) {
	          if (!lines[i] && i + 1 === l) {
	            break;
	          }

	          lines[i] = options.indent + lines[i];
	        }
	        result = lines.join('\n');
	      }
	      return result;
	    } else {
	      throw new _exception2['default']('The partial ' + options.name + ' could not be compiled when running in runtime-only mode');
	    }
	  }

	  // Just add water
	  var container = {
	    strict: function strict(obj, name, loc) {
	      if (!obj || !(name in obj)) {
	        throw new _exception2['default']('"' + name + '" not defined in ' + obj, {
	          loc: loc
	        });
	      }
	      return container.lookupProperty(obj, name);
	    },
	    lookupProperty: function lookupProperty(parent, propertyName) {
	      var result = parent[propertyName];
	      if (result == null) {
	        return result;
	      }
	      if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
	        return result;
	      }

	      if (_internalProtoAccess.resultIsAllowed(result, container.protoAccessControl, propertyName)) {
	        return result;
	      }
	      return undefined;
	    },
	    lookup: function lookup(depths, name) {
	      var len = depths.length;
	      for (var i = 0; i < len; i++) {
	        var result = depths[i] && container.lookupProperty(depths[i], name);
	        if (result != null) {
	          return depths[i][name];
	        }
	      }
	    },
	    lambda: function lambda(current, context) {
	      return typeof current === 'function' ? current.call(context) : current;
	    },

	    escapeExpression: Utils.escapeExpression,
	    invokePartial: invokePartialWrapper,

	    fn: function fn(i) {
	      var ret = templateSpec[i];
	      ret.decorator = templateSpec[i + '_d'];
	      return ret;
	    },

	    programs: [],
	    program: function program(i, data, declaredBlockParams, blockParams, depths) {
	      var programWrapper = this.programs[i],
	          fn = this.fn(i);
	      if (data || depths || blockParams || declaredBlockParams) {
	        programWrapper = wrapProgram(this, i, fn, data, declaredBlockParams, blockParams, depths);
	      } else if (!programWrapper) {
	        programWrapper = this.programs[i] = wrapProgram(this, i, fn);
	      }
	      return programWrapper;
	    },

	    data: function data(value, depth) {
	      while (value && depth--) {
	        value = value._parent;
	      }
	      return value;
	    },
	    mergeIfNeeded: function mergeIfNeeded(param, common) {
	      var obj = param || common;

	      if (param && common && param !== common) {
	        obj = Utils.extend({}, common, param);
	      }

	      return obj;
	    },
	    // An empty object to use as replacement for null-contexts
	    nullContext: _Object$seal({}),

	    noop: env.VM.noop,
	    compilerInfo: templateSpec.compiler
	  };

	  function ret(context) {
	    var options = arguments.length <= 1 || arguments[1] === undefined ? {} : arguments[1];

	    var data = options.data;

	    ret._setup(options);
	    if (!options.partial && templateSpec.useData) {
	      data = initData(context, data);
	    }
	    var depths = undefined,
	        blockParams = templateSpec.useBlockParams ? [] : undefined;
	    if (templateSpec.useDepths) {
	      if (options.depths) {
	        depths = context != options.depths[0] ? [context].concat(options.depths) : options.depths;
	      } else {
	        depths = [context];
	      }
	    }

	    function main(context /*, options*/) {
	      return '' + templateSpec.main(container, context, container.helpers, container.partials, data, blockParams, depths);
	    }

	    main = executeDecorators(templateSpec.main, main, container, options.depths || [], data, blockParams);
	    return main(context, options);
	  }

	  ret.isTop = true;

	  ret._setup = function (options) {
	    if (!options.partial) {
	      var mergedHelpers = Utils.extend({}, env.helpers, options.helpers);
	      wrapHelpersToPassLookupProperty(mergedHelpers, container);
	      container.helpers = mergedHelpers;

	      if (templateSpec.usePartial) {
	        // Use mergeIfNeeded here to prevent compiling global partials multiple times
	        container.partials = container.mergeIfNeeded(options.partials, env.partials);
	      }
	      if (templateSpec.usePartial || templateSpec.useDecorators) {
	        container.decorators = Utils.extend({}, env.decorators, options.decorators);
	      }

	      container.hooks = {};
	      container.protoAccessControl = _internalProtoAccess.createProtoAccessControl(options);

	      var keepHelperInHelpers = options.allowCallsToHelperMissing || templateWasPrecompiledWithCompilerV7;
	      _helpers.moveHelperToHooks(container, 'helperMissing', keepHelperInHelpers);
	      _helpers.moveHelperToHooks(container, 'blockHelperMissing', keepHelperInHelpers);
	    } else {
	      container.protoAccessControl = options.protoAccessControl; // internal option
	      container.helpers = options.helpers;
	      container.partials = options.partials;
	      container.decorators = options.decorators;
	      container.hooks = options.hooks;
	    }
	  };

	  ret._child = function (i, data, blockParams, depths) {
	    if (templateSpec.useBlockParams && !blockParams) {
	      throw new _exception2['default']('must pass block params');
	    }
	    if (templateSpec.useDepths && !depths) {
	      throw new _exception2['default']('must pass parent depths');
	    }

	    return wrapProgram(container, i, templateSpec[i], data, 0, blockParams, depths);
	  };
	  return ret;
	}

	function wrapProgram(container, i, fn, data, declaredBlockParams, blockParams, depths) {
	  function prog(context) {
	    var options = arguments.length <= 1 || arguments[1] === undefined ? {} : arguments[1];

	    var currentDepths = depths;
	    if (depths && context != depths[0] && !(context === container.nullContext && depths[0] === null)) {
	      currentDepths = [context].concat(depths);
	    }

	    return fn(container, context, container.helpers, container.partials, options.data || data, blockParams && [options.blockParams].concat(blockParams), currentDepths);
	  }

	  prog = executeDecorators(fn, prog, container, depths, data, blockParams);

	  prog.program = i;
	  prog.depth = depths ? depths.length : 0;
	  prog.blockParams = declaredBlockParams || 0;
	  return prog;
	}

	/**
	 * This is currently part of the official API, therefore implementation details should not be changed.
	 */

	function resolvePartial(partial, context, options) {
	  if (!partial) {
	    if (options.name === '@partial-block') {
	      partial = options.data['partial-block'];
	    } else {
	      partial = options.partials[options.name];
	    }
	  } else if (!partial.call && !options.name) {
	    // This is a dynamic partial that returned a string
	    options.name = partial;
	    partial = options.partials[partial];
	  }
	  return partial;
	}

	function invokePartial(partial, context, options) {
	  // Use the current closure context to save the partial-block if this partial
	  var currentPartialBlock = options.data && options.data['partial-block'];
	  options.partial = true;
	  if (options.ids) {
	    options.data.contextPath = options.ids[0] || options.data.contextPath;
	  }

	  var partialBlock = undefined;
	  if (options.fn && options.fn !== noop) {
	    (function () {
	      options.data = _base.createFrame(options.data);
	      // Wrapper function to get access to currentPartialBlock from the closure
	      var fn = options.fn;
	      partialBlock = options.data['partial-block'] = function partialBlockWrapper(context) {
	        var options = arguments.length <= 1 || arguments[1] === undefined ? {} : arguments[1];

	        // Restore the partial-block from the closure for the execution of the block
	        // i.e. the part inside the block of the partial call.
	        options.data = _base.createFrame(options.data);
	        options.data['partial-block'] = currentPartialBlock;
	        return fn(context, options);
	      };
	      if (fn.partials) {
	        options.partials = Utils.extend({}, options.partials, fn.partials);
	      }
	    })();
	  }

	  if (partial === undefined && partialBlock) {
	    partial = partialBlock;
	  }

	  if (partial === undefined) {
	    throw new _exception2['default']('The partial ' + options.name + ' could not be found');
	  } else if (partial instanceof Function) {
	    return partial(context, options);
	  }
	}

	function noop() {
	  return '';
	}

	function initData(context, data) {
	  if (!data || !('root' in data)) {
	    data = data ? _base.createFrame(data) : {};
	    data.root = context;
	  }
	  return data;
	}

	function executeDecorators(fn, prog, container, depths, data, blockParams) {
	  if (fn.decorator) {
	    var props = {};
	    prog = fn.decorator(prog, props, container, depths && depths[0], data, blockParams, depths);
	    Utils.extend(prog, props);
	  }
	  return prog;
	}

	function wrapHelpersToPassLookupProperty(mergedHelpers, container) {
	  _Object$keys(mergedHelpers).forEach(function (helperName) {
	    var helper = mergedHelpers[helperName];
	    mergedHelpers[helperName] = passLookupPropertyOption(helper, container);
	  });
	}

	function passLookupPropertyOption(helper, container) {
	  var lookupProperty = container.lookupProperty;
	  return _internalWrapHelper.wrapHelper(helper, function (options) {
	    return Utils.extend({ lookupProperty: lookupProperty }, options);
	  });
	}

/***/ }),
/* 39 */
/***/ (function(module, exports, __webpack_require__) {

	module.exports = { "default": __webpack_require__(40), __esModule: true };

/***/ }),
/* 40 */
/***/ (function(module, exports, __webpack_require__) {

	__webpack_require__(41);
	module.exports = __webpack_require__(21).Object.seal;

/***/ }),
/* 41 */
/***/ (function(module, exports, __webpack_require__) {

	// 19.1.2.17 Object.seal(O)
	var isObject = __webpack_require__(42);

	__webpack_require__(18)('seal', function($seal){
	  return function seal(it){
	    return $seal && isObject(it) ? $seal(it) : it;
	  };
	});

/***/ }),
/* 42 */
/***/ (function(module, exports) {

	module.exports = function(it){
	  return typeof it === 'object' ? it !== null : typeof it === 'function';
	};

/***/ }),
/* 43 */
/***/ (function(module, exports) {

	'use strict';

	exports.__esModule = true;
	exports.wrapHelper = wrapHelper;

	function wrapHelper(helper, transformOptionsFn) {
	  if (typeof helper !== 'function') {
	    // This should not happen, but apparently it does in https://github.com/wycats/handlebars.js/issues/1639
	    // We try to make the wrapper least-invasive by not wrapping it, if the helper is not a function.
	    return helper;
	  }
	  var wrapper = function wrapper() /* dynamic arguments */{
	    var options = arguments[arguments.length - 1];
	    arguments[arguments.length - 1] = transformOptionsFn(options);
	    return helper.apply(this, arguments);
	  };
	  return wrapper;
	}

/***/ }),
/* 44 */
/***/ (function(module, exports) {

	/* WEBPACK VAR INJECTION */(function(global) {'use strict';

	exports.__esModule = true;

	exports['default'] = function (Handlebars) {
	  /* istanbul ignore next */
	  var root = typeof global !== 'undefined' ? global : window,
	      $Handlebars = root.Handlebars;
	  /* istanbul ignore next */
	  Handlebars.noConflict = function () {
	    if (root.Handlebars === Handlebars) {
	      root.Handlebars = $Handlebars;
	    }
	    return Handlebars;
	  };
	};

	module.exports = exports['default'];
	/* WEBPACK VAR INJECTION */}.call(exports, (function() { return this; }())))

/***/ }),
/* 45 */
/***/ (function(module, exports) {

	'use strict';

	exports.__esModule = true;
	var AST = {
	  // Public API used to evaluate derived attributes regarding AST nodes
	  helpers: {
	    // a mustache is definitely a helper if:
	    // * it is an eligible helper, and
	    // * it has at least one parameter or hash segment
	    helperExpression: function helperExpression(node) {
	      return node.type === 'SubExpression' || (node.type === 'MustacheStatement' || node.type === 'BlockStatement') && !!(node.params && node.params.length || node.hash);
	    },

	    scopedId: function scopedId(path) {
	      return (/^\.|this\b/.test(path.original)
	      );
	    },

	    // an ID is simple if it only has one part, and that part is not
	    // `..` or `this`.
	    simpleId: function simpleId(path) {
	      return path.parts.length === 1 && !AST.helpers.scopedId(path) && !path.depth;
	    }
	  }
	};

	// Must be exported as an object rather than the root of the module as the jison lexer
	// must modify the object to operate properly.
	exports['default'] = AST;
	module.exports = exports['default'];

/***/ }),
/* 46 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(1)['default'];

	var _interopRequireWildcard = __webpack_require__(3)['default'];

	exports.__esModule = true;
	exports.parseWithoutProcessing = parseWithoutProcessing;
	exports.parse = parse;

	var _parser = __webpack_require__(47);

	var _parser2 = _interopRequireDefault(_parser);

	var _whitespaceControl = __webpack_require__(48);

	var _whitespaceControl2 = _interopRequireDefault(_whitespaceControl);

	var _helpers = __webpack_require__(50);

	var Helpers = _interopRequireWildcard(_helpers);

	var _utils = __webpack_require__(5);

	exports.parser = _parser2['default'];

	var yy = {};
	_utils.extend(yy, Helpers);

	function parseWithoutProcessing(input, options) {
	  // Just return if an already-compiled AST was passed in.
	  if (input.type === 'Program') {
	    return input;
	  }

	  _parser2['default'].yy = yy;

	  // Altering the shared object here, but this is ok as parser is a sync operation
	  yy.locInfo = function (locInfo) {
	    return new yy.SourceLocation(options && options.srcName, locInfo);
	  };

	  var ast = _parser2['default'].parse(input);

	  return ast;
	}

	function parse(input, options) {
	  var ast = parseWithoutProcessing(input, options);
	  var strip = new _whitespaceControl2['default'](options);

	  return strip.accept(ast);
	}

/***/ }),
/* 47 */
/***/ (function(module, exports) {

	// File ignored in coverage tests via setting in .istanbul.yml
	/* Jison generated parser */
	"use strict";

	exports.__esModule = true;
	var handlebars = (function () {
	    var parser = { trace: function trace() {},
	        yy: {},
	        symbols_: { "error": 2, "root": 3, "program": 4, "EOF": 5, "program_repetition0": 6, "statement": 7, "mustache": 8, "block": 9, "rawBlock": 10, "partial": 11, "partialBlock": 12, "content": 13, "COMMENT": 14, "CONTENT": 15, "openRawBlock": 16, "rawBlock_repetition0": 17, "END_RAW_BLOCK": 18, "OPEN_RAW_BLOCK": 19, "helperName": 20, "openRawBlock_repetition0": 21, "openRawBlock_option0": 22, "CLOSE_RAW_BLOCK": 23, "openBlock": 24, "block_option0": 25, "closeBlock": 26, "openInverse": 27, "block_option1": 28, "OPEN_BLOCK": 29, "openBlock_repetition0": 30, "openBlock_option0": 31, "openBlock_option1": 32, "CLOSE": 33, "OPEN_INVERSE": 34, "openInverse_repetition0": 35, "openInverse_option0": 36, "openInverse_option1": 37, "openInverseChain": 38, "OPEN_INVERSE_CHAIN": 39, "openInverseChain_repetition0": 40, "openInverseChain_option0": 41, "openInverseChain_option1": 42, "inverseAndProgram": 43, "INVERSE": 44, "inverseChain": 45, "inverseChain_option0": 46, "OPEN_ENDBLOCK": 47, "OPEN": 48, "mustache_repetition0": 49, "mustache_option0": 50, "OPEN_UNESCAPED": 51, "mustache_repetition1": 52, "mustache_option1": 53, "CLOSE_UNESCAPED": 54, "OPEN_PARTIAL": 55, "partialName": 56, "partial_repetition0": 57, "partial_option0": 58, "openPartialBlock": 59, "OPEN_PARTIAL_BLOCK": 60, "openPartialBlock_repetition0": 61, "openPartialBlock_option0": 62, "param": 63, "sexpr": 64, "OPEN_SEXPR": 65, "sexpr_repetition0": 66, "sexpr_option0": 67, "CLOSE_SEXPR": 68, "hash": 69, "hash_repetition_plus0": 70, "hashSegment": 71, "ID": 72, "EQUALS": 73, "blockParams": 74, "OPEN_BLOCK_PARAMS": 75, "blockParams_repetition_plus0": 76, "CLOSE_BLOCK_PARAMS": 77, "path": 78, "dataName": 79, "STRING": 80, "NUMBER": 81, "BOOLEAN": 82, "UNDEFINED": 83, "NULL": 84, "DATA": 85, "pathSegments": 86, "SEP": 87, "$accept": 0, "$end": 1 },
	        terminals_: { 2: "error", 5: "EOF", 14: "COMMENT", 15: "CONTENT", 18: "END_RAW_BLOCK", 19: "OPEN_RAW_BLOCK", 23: "CLOSE_RAW_BLOCK", 29: "OPEN_BLOCK", 33: "CLOSE", 34: "OPEN_INVERSE", 39: "OPEN_INVERSE_CHAIN", 44: "INVERSE", 47: "OPEN_ENDBLOCK", 48: "OPEN", 51: "OPEN_UNESCAPED", 54: "CLOSE_UNESCAPED", 55: "OPEN_PARTIAL", 60: "OPEN_PARTIAL_BLOCK", 65: "OPEN_SEXPR", 68: "CLOSE_SEXPR", 72: "ID", 73: "EQUALS", 75: "OPEN_BLOCK_PARAMS", 77: "CLOSE_BLOCK_PARAMS", 80: "STRING", 81: "NUMBER", 82: "BOOLEAN", 83: "UNDEFINED", 84: "NULL", 85: "DATA", 87: "SEP" },
	        productions_: [0, [3, 2], [4, 1], [7, 1], [7, 1], [7, 1], [7, 1], [7, 1], [7, 1], [7, 1], [13, 1], [10, 3], [16, 5], [9, 4], [9, 4], [24, 6], [27, 6], [38, 6], [43, 2], [45, 3], [45, 1], [26, 3], [8, 5], [8, 5], [11, 5], [12, 3], [59, 5], [63, 1], [63, 1], [64, 5], [69, 1], [71, 3], [74, 3], [20, 1], [20, 1], [20, 1], [20, 1], [20, 1], [20, 1], [20, 1], [56, 1], [56, 1], [79, 2], [78, 1], [86, 3], [86, 1], [6, 0], [6, 2], [17, 0], [17, 2], [21, 0], [21, 2], [22, 0], [22, 1], [25, 0], [25, 1], [28, 0], [28, 1], [30, 0], [30, 2], [31, 0], [31, 1], [32, 0], [32, 1], [35, 0], [35, 2], [36, 0], [36, 1], [37, 0], [37, 1], [40, 0], [40, 2], [41, 0], [41, 1], [42, 0], [42, 1], [46, 0], [46, 1], [49, 0], [49, 2], [50, 0], [50, 1], [52, 0], [52, 2], [53, 0], [53, 1], [57, 0], [57, 2], [58, 0], [58, 1], [61, 0], [61, 2], [62, 0], [62, 1], [66, 0], [66, 2], [67, 0], [67, 1], [70, 1], [70, 2], [76, 1], [76, 2]],
	        performAction: function anonymous(yytext, yyleng, yylineno, yy, yystate, $$, _$) {

	            var $0 = $$.length - 1;
	            switch (yystate) {
	                case 1:
	                    return $$[$0 - 1];
	                    break;
	                case 2:
	                    this.$ = yy.prepareProgram($$[$0]);
	                    break;
	                case 3:
	                    this.$ = $$[$0];
	                    break;
	                case 4:
	                    this.$ = $$[$0];
	                    break;
	                case 5:
	                    this.$ = $$[$0];
	                    break;
	                case 6:
	                    this.$ = $$[$0];
	                    break;
	                case 7:
	                    this.$ = $$[$0];
	                    break;
	                case 8:
	                    this.$ = $$[$0];
	                    break;
	                case 9:
	                    this.$ = {
	                        type: 'CommentStatement',
	                        value: yy.stripComment($$[$0]),
	                        strip: yy.stripFlags($$[$0], $$[$0]),
	                        loc: yy.locInfo(this._$)
	                    };

	                    break;
	                case 10:
	                    this.$ = {
	                        type: 'ContentStatement',
	                        original: $$[$0],
	                        value: $$[$0],
	                        loc: yy.locInfo(this._$)
	                    };

	                    break;
	                case 11:
	                    this.$ = yy.prepareRawBlock($$[$0 - 2], $$[$0 - 1], $$[$0], this._$);
	                    break;
	                case 12:
	                    this.$ = { path: $$[$0 - 3], params: $$[$0 - 2], hash: $$[$0 - 1] };
	                    break;
	                case 13:
	                    this.$ = yy.prepareBlock($$[$0 - 3], $$[$0 - 2], $$[$0 - 1], $$[$0], false, this._$);
	                    break;
	                case 14:
	                    this.$ = yy.prepareBlock($$[$0 - 3], $$[$0 - 2], $$[$0 - 1], $$[$0], true, this._$);
	                    break;
	                case 15:
	                    this.$ = { open: $$[$0 - 5], path: $$[$0 - 4], params: $$[$0 - 3], hash: $$[$0 - 2], blockParams: $$[$0 - 1], strip: yy.stripFlags($$[$0 - 5], $$[$0]) };
	                    break;
	                case 16:
	                    this.$ = { path: $$[$0 - 4], params: $$[$0 - 3], hash: $$[$0 - 2], blockParams: $$[$0 - 1], strip: yy.stripFlags($$[$0 - 5], $$[$0]) };
	                    break;
	                case 17:
	                    this.$ = { path: $$[$0 - 4], params: $$[$0 - 3], hash: $$[$0 - 2], blockParams: $$[$0 - 1], strip: yy.stripFlags($$[$0 - 5], $$[$0]) };
	                    break;
	                case 18:
	                    this.$ = { strip: yy.stripFlags($$[$0 - 1], $$[$0 - 1]), program: $$[$0] };
	                    break;
	                case 19:
	                    var inverse = yy.prepareBlock($$[$0 - 2], $$[$0 - 1], $$[$0], $$[$0], false, this._$),
	                        program = yy.prepareProgram([inverse], $$[$0 - 1].loc);
	                    program.chained = true;

	                    this.$ = { strip: $$[$0 - 2].strip, program: program, chain: true };

	                    break;
	                case 20:
	                    this.$ = $$[$0];
	                    break;
	                case 21:
	                    this.$ = { path: $$[$0 - 1], strip: yy.stripFlags($$[$0 - 2], $$[$0]) };
	                    break;
	                case 22:
	                    this.$ = yy.prepareMustache($$[$0 - 3], $$[$0 - 2], $$[$0 - 1], $$[$0 - 4], yy.stripFlags($$[$0 - 4], $$[$0]), this._$);
	                    break;
	                case 23:
	                    this.$ = yy.prepareMustache($$[$0 - 3], $$[$0 - 2], $$[$0 - 1], $$[$0 - 4], yy.stripFlags($$[$0 - 4], $$[$0]), this._$);
	                    break;
	                case 24:
	                    this.$ = {
	                        type: 'PartialStatement',
	                        name: $$[$0 - 3],
	                        params: $$[$0 - 2],
	                        hash: $$[$0 - 1],
	                        indent: '',
	                        strip: yy.stripFlags($$[$0 - 4], $$[$0]),
	                        loc: yy.locInfo(this._$)
	                    };

	                    break;
	                case 25:
	                    this.$ = yy.preparePartialBlock($$[$0 - 2], $$[$0 - 1], $$[$0], this._$);
	                    break;
	                case 26:
	                    this.$ = { path: $$[$0 - 3], params: $$[$0 - 2], hash: $$[$0 - 1], strip: yy.stripFlags($$[$0 - 4], $$[$0]) };
	                    break;
	                case 27:
	                    this.$ = $$[$0];
	                    break;
	                case 28:
	                    this.$ = $$[$0];
	                    break;
	                case 29:
	                    this.$ = {
	                        type: 'SubExpression',
	                        path: $$[$0 - 3],
	                        params: $$[$0 - 2],
	                        hash: $$[$0 - 1],
	                        loc: yy.locInfo(this._$)
	                    };

	                    break;
	                case 30:
	                    this.$ = { type: 'Hash', pairs: $$[$0], loc: yy.locInfo(this._$) };
	                    break;
	                case 31:
	                    this.$ = { type: 'HashPair', key: yy.id($$[$0 - 2]), value: $$[$0], loc: yy.locInfo(this._$) };
	                    break;
	                case 32:
	                    this.$ = yy.id($$[$0 - 1]);
	                    break;
	                case 33:
	                    this.$ = $$[$0];
	                    break;
	                case 34:
	                    this.$ = $$[$0];
	                    break;
	                case 35:
	                    this.$ = { type: 'StringLiteral', value: $$[$0], original: $$[$0], loc: yy.locInfo(this._$) };
	                    break;
	                case 36:
	                    this.$ = { type: 'NumberLiteral', value: Number($$[$0]), original: Number($$[$0]), loc: yy.locInfo(this._$) };
	                    break;
	                case 37:
	                    this.$ = { type: 'BooleanLiteral', value: $$[$0] === 'true', original: $$[$0] === 'true', loc: yy.locInfo(this._$) };
	                    break;
	                case 38:
	                    this.$ = { type: 'UndefinedLiteral', original: undefined, value: undefined, loc: yy.locInfo(this._$) };
	                    break;
	                case 39:
	                    this.$ = { type: 'NullLiteral', original: null, value: null, loc: yy.locInfo(this._$) };
	                    break;
	                case 40:
	                    this.$ = $$[$0];
	                    break;
	                case 41:
	                    this.$ = $$[$0];
	                    break;
	                case 42:
	                    this.$ = yy.preparePath(true, $$[$0], this._$);
	                    break;
	                case 43:
	                    this.$ = yy.preparePath(false, $$[$0], this._$);
	                    break;
	                case 44:
	                    $$[$0 - 2].push({ part: yy.id($$[$0]), original: $$[$0], separator: $$[$0 - 1] });this.$ = $$[$0 - 2];
	                    break;
	                case 45:
	                    this.$ = [{ part: yy.id($$[$0]), original: $$[$0] }];
	                    break;
	                case 46:
	                    this.$ = [];
	                    break;
	                case 47:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	                case 48:
	                    this.$ = [];
	                    break;
	                case 49:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	                case 50:
	                    this.$ = [];
	                    break;
	                case 51:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	                case 58:
	                    this.$ = [];
	                    break;
	                case 59:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	                case 64:
	                    this.$ = [];
	                    break;
	                case 65:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	                case 70:
	                    this.$ = [];
	                    break;
	                case 71:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	                case 78:
	                    this.$ = [];
	                    break;
	                case 79:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	                case 82:
	                    this.$ = [];
	                    break;
	                case 83:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	                case 86:
	                    this.$ = [];
	                    break;
	                case 87:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	                case 90:
	                    this.$ = [];
	                    break;
	                case 91:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	                case 94:
	                    this.$ = [];
	                    break;
	                case 95:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	                case 98:
	                    this.$ = [$$[$0]];
	                    break;
	                case 99:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	                case 100:
	                    this.$ = [$$[$0]];
	                    break;
	                case 101:
	                    $$[$0 - 1].push($$[$0]);
	                    break;
	            }
	        },
	        table: [{ 3: 1, 4: 2, 5: [2, 46], 6: 3, 14: [2, 46], 15: [2, 46], 19: [2, 46], 29: [2, 46], 34: [2, 46], 48: [2, 46], 51: [2, 46], 55: [2, 46], 60: [2, 46] }, { 1: [3] }, { 5: [1, 4] }, { 5: [2, 2], 7: 5, 8: 6, 9: 7, 10: 8, 11: 9, 12: 10, 13: 11, 14: [1, 12], 15: [1, 20], 16: 17, 19: [1, 23], 24: 15, 27: 16, 29: [1, 21], 34: [1, 22], 39: [2, 2], 44: [2, 2], 47: [2, 2], 48: [1, 13], 51: [1, 14], 55: [1, 18], 59: 19, 60: [1, 24] }, { 1: [2, 1] }, { 5: [2, 47], 14: [2, 47], 15: [2, 47], 19: [2, 47], 29: [2, 47], 34: [2, 47], 39: [2, 47], 44: [2, 47], 47: [2, 47], 48: [2, 47], 51: [2, 47], 55: [2, 47], 60: [2, 47] }, { 5: [2, 3], 14: [2, 3], 15: [2, 3], 19: [2, 3], 29: [2, 3], 34: [2, 3], 39: [2, 3], 44: [2, 3], 47: [2, 3], 48: [2, 3], 51: [2, 3], 55: [2, 3], 60: [2, 3] }, { 5: [2, 4], 14: [2, 4], 15: [2, 4], 19: [2, 4], 29: [2, 4], 34: [2, 4], 39: [2, 4], 44: [2, 4], 47: [2, 4], 48: [2, 4], 51: [2, 4], 55: [2, 4], 60: [2, 4] }, { 5: [2, 5], 14: [2, 5], 15: [2, 5], 19: [2, 5], 29: [2, 5], 34: [2, 5], 39: [2, 5], 44: [2, 5], 47: [2, 5], 48: [2, 5], 51: [2, 5], 55: [2, 5], 60: [2, 5] }, { 5: [2, 6], 14: [2, 6], 15: [2, 6], 19: [2, 6], 29: [2, 6], 34: [2, 6], 39: [2, 6], 44: [2, 6], 47: [2, 6], 48: [2, 6], 51: [2, 6], 55: [2, 6], 60: [2, 6] }, { 5: [2, 7], 14: [2, 7], 15: [2, 7], 19: [2, 7], 29: [2, 7], 34: [2, 7], 39: [2, 7], 44: [2, 7], 47: [2, 7], 48: [2, 7], 51: [2, 7], 55: [2, 7], 60: [2, 7] }, { 5: [2, 8], 14: [2, 8], 15: [2, 8], 19: [2, 8], 29: [2, 8], 34: [2, 8], 39: [2, 8], 44: [2, 8], 47: [2, 8], 48: [2, 8], 51: [2, 8], 55: [2, 8], 60: [2, 8] }, { 5: [2, 9], 14: [2, 9], 15: [2, 9], 19: [2, 9], 29: [2, 9], 34: [2, 9], 39: [2, 9], 44: [2, 9], 47: [2, 9], 48: [2, 9], 51: [2, 9], 55: [2, 9], 60: [2, 9] }, { 20: 25, 72: [1, 35], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 20: 36, 72: [1, 35], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 4: 37, 6: 3, 14: [2, 46], 15: [2, 46], 19: [2, 46], 29: [2, 46], 34: [2, 46], 39: [2, 46], 44: [2, 46], 47: [2, 46], 48: [2, 46], 51: [2, 46], 55: [2, 46], 60: [2, 46] }, { 4: 38, 6: 3, 14: [2, 46], 15: [2, 46], 19: [2, 46], 29: [2, 46], 34: [2, 46], 44: [2, 46], 47: [2, 46], 48: [2, 46], 51: [2, 46], 55: [2, 46], 60: [2, 46] }, { 15: [2, 48], 17: 39, 18: [2, 48] }, { 20: 41, 56: 40, 64: 42, 65: [1, 43], 72: [1, 35], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 4: 44, 6: 3, 14: [2, 46], 15: [2, 46], 19: [2, 46], 29: [2, 46], 34: [2, 46], 47: [2, 46], 48: [2, 46], 51: [2, 46], 55: [2, 46], 60: [2, 46] }, { 5: [2, 10], 14: [2, 10], 15: [2, 10], 18: [2, 10], 19: [2, 10], 29: [2, 10], 34: [2, 10], 39: [2, 10], 44: [2, 10], 47: [2, 10], 48: [2, 10], 51: [2, 10], 55: [2, 10], 60: [2, 10] }, { 20: 45, 72: [1, 35], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 20: 46, 72: [1, 35], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 20: 47, 72: [1, 35], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 20: 41, 56: 48, 64: 42, 65: [1, 43], 72: [1, 35], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 33: [2, 78], 49: 49, 65: [2, 78], 72: [2, 78], 80: [2, 78], 81: [2, 78], 82: [2, 78], 83: [2, 78], 84: [2, 78], 85: [2, 78] }, { 23: [2, 33], 33: [2, 33], 54: [2, 33], 65: [2, 33], 68: [2, 33], 72: [2, 33], 75: [2, 33], 80: [2, 33], 81: [2, 33], 82: [2, 33], 83: [2, 33], 84: [2, 33], 85: [2, 33] }, { 23: [2, 34], 33: [2, 34], 54: [2, 34], 65: [2, 34], 68: [2, 34], 72: [2, 34], 75: [2, 34], 80: [2, 34], 81: [2, 34], 82: [2, 34], 83: [2, 34], 84: [2, 34], 85: [2, 34] }, { 23: [2, 35], 33: [2, 35], 54: [2, 35], 65: [2, 35], 68: [2, 35], 72: [2, 35], 75: [2, 35], 80: [2, 35], 81: [2, 35], 82: [2, 35], 83: [2, 35], 84: [2, 35], 85: [2, 35] }, { 23: [2, 36], 33: [2, 36], 54: [2, 36], 65: [2, 36], 68: [2, 36], 72: [2, 36], 75: [2, 36], 80: [2, 36], 81: [2, 36], 82: [2, 36], 83: [2, 36], 84: [2, 36], 85: [2, 36] }, { 23: [2, 37], 33: [2, 37], 54: [2, 37], 65: [2, 37], 68: [2, 37], 72: [2, 37], 75: [2, 37], 80: [2, 37], 81: [2, 37], 82: [2, 37], 83: [2, 37], 84: [2, 37], 85: [2, 37] }, { 23: [2, 38], 33: [2, 38], 54: [2, 38], 65: [2, 38], 68: [2, 38], 72: [2, 38], 75: [2, 38], 80: [2, 38], 81: [2, 38], 82: [2, 38], 83: [2, 38], 84: [2, 38], 85: [2, 38] }, { 23: [2, 39], 33: [2, 39], 54: [2, 39], 65: [2, 39], 68: [2, 39], 72: [2, 39], 75: [2, 39], 80: [2, 39], 81: [2, 39], 82: [2, 39], 83: [2, 39], 84: [2, 39], 85: [2, 39] }, { 23: [2, 43], 33: [2, 43], 54: [2, 43], 65: [2, 43], 68: [2, 43], 72: [2, 43], 75: [2, 43], 80: [2, 43], 81: [2, 43], 82: [2, 43], 83: [2, 43], 84: [2, 43], 85: [2, 43], 87: [1, 50] }, { 72: [1, 35], 86: 51 }, { 23: [2, 45], 33: [2, 45], 54: [2, 45], 65: [2, 45], 68: [2, 45], 72: [2, 45], 75: [2, 45], 80: [2, 45], 81: [2, 45], 82: [2, 45], 83: [2, 45], 84: [2, 45], 85: [2, 45], 87: [2, 45] }, { 52: 52, 54: [2, 82], 65: [2, 82], 72: [2, 82], 80: [2, 82], 81: [2, 82], 82: [2, 82], 83: [2, 82], 84: [2, 82], 85: [2, 82] }, { 25: 53, 38: 55, 39: [1, 57], 43: 56, 44: [1, 58], 45: 54, 47: [2, 54] }, { 28: 59, 43: 60, 44: [1, 58], 47: [2, 56] }, { 13: 62, 15: [1, 20], 18: [1, 61] }, { 33: [2, 86], 57: 63, 65: [2, 86], 72: [2, 86], 80: [2, 86], 81: [2, 86], 82: [2, 86], 83: [2, 86], 84: [2, 86], 85: [2, 86] }, { 33: [2, 40], 65: [2, 40], 72: [2, 40], 80: [2, 40], 81: [2, 40], 82: [2, 40], 83: [2, 40], 84: [2, 40], 85: [2, 40] }, { 33: [2, 41], 65: [2, 41], 72: [2, 41], 80: [2, 41], 81: [2, 41], 82: [2, 41], 83: [2, 41], 84: [2, 41], 85: [2, 41] }, { 20: 64, 72: [1, 35], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 26: 65, 47: [1, 66] }, { 30: 67, 33: [2, 58], 65: [2, 58], 72: [2, 58], 75: [2, 58], 80: [2, 58], 81: [2, 58], 82: [2, 58], 83: [2, 58], 84: [2, 58], 85: [2, 58] }, { 33: [2, 64], 35: 68, 65: [2, 64], 72: [2, 64], 75: [2, 64], 80: [2, 64], 81: [2, 64], 82: [2, 64], 83: [2, 64], 84: [2, 64], 85: [2, 64] }, { 21: 69, 23: [2, 50], 65: [2, 50], 72: [2, 50], 80: [2, 50], 81: [2, 50], 82: [2, 50], 83: [2, 50], 84: [2, 50], 85: [2, 50] }, { 33: [2, 90], 61: 70, 65: [2, 90], 72: [2, 90], 80: [2, 90], 81: [2, 90], 82: [2, 90], 83: [2, 90], 84: [2, 90], 85: [2, 90] }, { 20: 74, 33: [2, 80], 50: 71, 63: 72, 64: 75, 65: [1, 43], 69: 73, 70: 76, 71: 77, 72: [1, 78], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 72: [1, 79] }, { 23: [2, 42], 33: [2, 42], 54: [2, 42], 65: [2, 42], 68: [2, 42], 72: [2, 42], 75: [2, 42], 80: [2, 42], 81: [2, 42], 82: [2, 42], 83: [2, 42], 84: [2, 42], 85: [2, 42], 87: [1, 50] }, { 20: 74, 53: 80, 54: [2, 84], 63: 81, 64: 75, 65: [1, 43], 69: 82, 70: 76, 71: 77, 72: [1, 78], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 26: 83, 47: [1, 66] }, { 47: [2, 55] }, { 4: 84, 6: 3, 14: [2, 46], 15: [2, 46], 19: [2, 46], 29: [2, 46], 34: [2, 46], 39: [2, 46], 44: [2, 46], 47: [2, 46], 48: [2, 46], 51: [2, 46], 55: [2, 46], 60: [2, 46] }, { 47: [2, 20] }, { 20: 85, 72: [1, 35], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 4: 86, 6: 3, 14: [2, 46], 15: [2, 46], 19: [2, 46], 29: [2, 46], 34: [2, 46], 47: [2, 46], 48: [2, 46], 51: [2, 46], 55: [2, 46], 60: [2, 46] }, { 26: 87, 47: [1, 66] }, { 47: [2, 57] }, { 5: [2, 11], 14: [2, 11], 15: [2, 11], 19: [2, 11], 29: [2, 11], 34: [2, 11], 39: [2, 11], 44: [2, 11], 47: [2, 11], 48: [2, 11], 51: [2, 11], 55: [2, 11], 60: [2, 11] }, { 15: [2, 49], 18: [2, 49] }, { 20: 74, 33: [2, 88], 58: 88, 63: 89, 64: 75, 65: [1, 43], 69: 90, 70: 76, 71: 77, 72: [1, 78], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 65: [2, 94], 66: 91, 68: [2, 94], 72: [2, 94], 80: [2, 94], 81: [2, 94], 82: [2, 94], 83: [2, 94], 84: [2, 94], 85: [2, 94] }, { 5: [2, 25], 14: [2, 25], 15: [2, 25], 19: [2, 25], 29: [2, 25], 34: [2, 25], 39: [2, 25], 44: [2, 25], 47: [2, 25], 48: [2, 25], 51: [2, 25], 55: [2, 25], 60: [2, 25] }, { 20: 92, 72: [1, 35], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 20: 74, 31: 93, 33: [2, 60], 63: 94, 64: 75, 65: [1, 43], 69: 95, 70: 76, 71: 77, 72: [1, 78], 75: [2, 60], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 20: 74, 33: [2, 66], 36: 96, 63: 97, 64: 75, 65: [1, 43], 69: 98, 70: 76, 71: 77, 72: [1, 78], 75: [2, 66], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 20: 74, 22: 99, 23: [2, 52], 63: 100, 64: 75, 65: [1, 43], 69: 101, 70: 76, 71: 77, 72: [1, 78], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 20: 74, 33: [2, 92], 62: 102, 63: 103, 64: 75, 65: [1, 43], 69: 104, 70: 76, 71: 77, 72: [1, 78], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 33: [1, 105] }, { 33: [2, 79], 65: [2, 79], 72: [2, 79], 80: [2, 79], 81: [2, 79], 82: [2, 79], 83: [2, 79], 84: [2, 79], 85: [2, 79] }, { 33: [2, 81] }, { 23: [2, 27], 33: [2, 27], 54: [2, 27], 65: [2, 27], 68: [2, 27], 72: [2, 27], 75: [2, 27], 80: [2, 27], 81: [2, 27], 82: [2, 27], 83: [2, 27], 84: [2, 27], 85: [2, 27] }, { 23: [2, 28], 33: [2, 28], 54: [2, 28], 65: [2, 28], 68: [2, 28], 72: [2, 28], 75: [2, 28], 80: [2, 28], 81: [2, 28], 82: [2, 28], 83: [2, 28], 84: [2, 28], 85: [2, 28] }, { 23: [2, 30], 33: [2, 30], 54: [2, 30], 68: [2, 30], 71: 106, 72: [1, 107], 75: [2, 30] }, { 23: [2, 98], 33: [2, 98], 54: [2, 98], 68: [2, 98], 72: [2, 98], 75: [2, 98] }, { 23: [2, 45], 33: [2, 45], 54: [2, 45], 65: [2, 45], 68: [2, 45], 72: [2, 45], 73: [1, 108], 75: [2, 45], 80: [2, 45], 81: [2, 45], 82: [2, 45], 83: [2, 45], 84: [2, 45], 85: [2, 45], 87: [2, 45] }, { 23: [2, 44], 33: [2, 44], 54: [2, 44], 65: [2, 44], 68: [2, 44], 72: [2, 44], 75: [2, 44], 80: [2, 44], 81: [2, 44], 82: [2, 44], 83: [2, 44], 84: [2, 44], 85: [2, 44], 87: [2, 44] }, { 54: [1, 109] }, { 54: [2, 83], 65: [2, 83], 72: [2, 83], 80: [2, 83], 81: [2, 83], 82: [2, 83], 83: [2, 83], 84: [2, 83], 85: [2, 83] }, { 54: [2, 85] }, { 5: [2, 13], 14: [2, 13], 15: [2, 13], 19: [2, 13], 29: [2, 13], 34: [2, 13], 39: [2, 13], 44: [2, 13], 47: [2, 13], 48: [2, 13], 51: [2, 13], 55: [2, 13], 60: [2, 13] }, { 38: 55, 39: [1, 57], 43: 56, 44: [1, 58], 45: 111, 46: 110, 47: [2, 76] }, { 33: [2, 70], 40: 112, 65: [2, 70], 72: [2, 70], 75: [2, 70], 80: [2, 70], 81: [2, 70], 82: [2, 70], 83: [2, 70], 84: [2, 70], 85: [2, 70] }, { 47: [2, 18] }, { 5: [2, 14], 14: [2, 14], 15: [2, 14], 19: [2, 14], 29: [2, 14], 34: [2, 14], 39: [2, 14], 44: [2, 14], 47: [2, 14], 48: [2, 14], 51: [2, 14], 55: [2, 14], 60: [2, 14] }, { 33: [1, 113] }, { 33: [2, 87], 65: [2, 87], 72: [2, 87], 80: [2, 87], 81: [2, 87], 82: [2, 87], 83: [2, 87], 84: [2, 87], 85: [2, 87] }, { 33: [2, 89] }, { 20: 74, 63: 115, 64: 75, 65: [1, 43], 67: 114, 68: [2, 96], 69: 116, 70: 76, 71: 77, 72: [1, 78], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 33: [1, 117] }, { 32: 118, 33: [2, 62], 74: 119, 75: [1, 120] }, { 33: [2, 59], 65: [2, 59], 72: [2, 59], 75: [2, 59], 80: [2, 59], 81: [2, 59], 82: [2, 59], 83: [2, 59], 84: [2, 59], 85: [2, 59] }, { 33: [2, 61], 75: [2, 61] }, { 33: [2, 68], 37: 121, 74: 122, 75: [1, 120] }, { 33: [2, 65], 65: [2, 65], 72: [2, 65], 75: [2, 65], 80: [2, 65], 81: [2, 65], 82: [2, 65], 83: [2, 65], 84: [2, 65], 85: [2, 65] }, { 33: [2, 67], 75: [2, 67] }, { 23: [1, 123] }, { 23: [2, 51], 65: [2, 51], 72: [2, 51], 80: [2, 51], 81: [2, 51], 82: [2, 51], 83: [2, 51], 84: [2, 51], 85: [2, 51] }, { 23: [2, 53] }, { 33: [1, 124] }, { 33: [2, 91], 65: [2, 91], 72: [2, 91], 80: [2, 91], 81: [2, 91], 82: [2, 91], 83: [2, 91], 84: [2, 91], 85: [2, 91] }, { 33: [2, 93] }, { 5: [2, 22], 14: [2, 22], 15: [2, 22], 19: [2, 22], 29: [2, 22], 34: [2, 22], 39: [2, 22], 44: [2, 22], 47: [2, 22], 48: [2, 22], 51: [2, 22], 55: [2, 22], 60: [2, 22] }, { 23: [2, 99], 33: [2, 99], 54: [2, 99], 68: [2, 99], 72: [2, 99], 75: [2, 99] }, { 73: [1, 108] }, { 20: 74, 63: 125, 64: 75, 65: [1, 43], 72: [1, 35], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 5: [2, 23], 14: [2, 23], 15: [2, 23], 19: [2, 23], 29: [2, 23], 34: [2, 23], 39: [2, 23], 44: [2, 23], 47: [2, 23], 48: [2, 23], 51: [2, 23], 55: [2, 23], 60: [2, 23] }, { 47: [2, 19] }, { 47: [2, 77] }, { 20: 74, 33: [2, 72], 41: 126, 63: 127, 64: 75, 65: [1, 43], 69: 128, 70: 76, 71: 77, 72: [1, 78], 75: [2, 72], 78: 26, 79: 27, 80: [1, 28], 81: [1, 29], 82: [1, 30], 83: [1, 31], 84: [1, 32], 85: [1, 34], 86: 33 }, { 5: [2, 24], 14: [2, 24], 15: [2, 24], 19: [2, 24], 29: [2, 24], 34: [2, 24], 39: [2, 24], 44: [2, 24], 47: [2, 24], 48: [2, 24], 51: [2, 24], 55: [2, 24], 60: [2, 24] }, { 68: [1, 129] }, { 65: [2, 95], 68: [2, 95], 72: [2, 95], 80: [2, 95], 81: [2, 95], 82: [2, 95], 83: [2, 95], 84: [2, 95], 85: [2, 95] }, { 68: [2, 97] }, { 5: [2, 21], 14: [2, 21], 15: [2, 21], 19: [2, 21], 29: [2, 21], 34: [2, 21], 39: [2, 21], 44: [2, 21], 47: [2, 21], 48: [2, 21], 51: [2, 21], 55: [2, 21], 60: [2, 21] }, { 33: [1, 130] }, { 33: [2, 63] }, { 72: [1, 132], 76: 131 }, { 33: [1, 133] }, { 33: [2, 69] }, { 15: [2, 12], 18: [2, 12] }, { 14: [2, 26], 15: [2, 26], 19: [2, 26], 29: [2, 26], 34: [2, 26], 47: [2, 26], 48: [2, 26], 51: [2, 26], 55: [2, 26], 60: [2, 26] }, { 23: [2, 31], 33: [2, 31], 54: [2, 31], 68: [2, 31], 72: [2, 31], 75: [2, 31] }, { 33: [2, 74], 42: 134, 74: 135, 75: [1, 120] }, { 33: [2, 71], 65: [2, 71], 72: [2, 71], 75: [2, 71], 80: [2, 71], 81: [2, 71], 82: [2, 71], 83: [2, 71], 84: [2, 71], 85: [2, 71] }, { 33: [2, 73], 75: [2, 73] }, { 23: [2, 29], 33: [2, 29], 54: [2, 29], 65: [2, 29], 68: [2, 29], 72: [2, 29], 75: [2, 29], 80: [2, 29], 81: [2, 29], 82: [2, 29], 83: [2, 29], 84: [2, 29], 85: [2, 29] }, { 14: [2, 15], 15: [2, 15], 19: [2, 15], 29: [2, 15], 34: [2, 15], 39: [2, 15], 44: [2, 15], 47: [2, 15], 48: [2, 15], 51: [2, 15], 55: [2, 15], 60: [2, 15] }, { 72: [1, 137], 77: [1, 136] }, { 72: [2, 100], 77: [2, 100] }, { 14: [2, 16], 15: [2, 16], 19: [2, 16], 29: [2, 16], 34: [2, 16], 44: [2, 16], 47: [2, 16], 48: [2, 16], 51: [2, 16], 55: [2, 16], 60: [2, 16] }, { 33: [1, 138] }, { 33: [2, 75] }, { 33: [2, 32] }, { 72: [2, 101], 77: [2, 101] }, { 14: [2, 17], 15: [2, 17], 19: [2, 17], 29: [2, 17], 34: [2, 17], 39: [2, 17], 44: [2, 17], 47: [2, 17], 48: [2, 17], 51: [2, 17], 55: [2, 17], 60: [2, 17] }],
	        defaultActions: { 4: [2, 1], 54: [2, 55], 56: [2, 20], 60: [2, 57], 73: [2, 81], 82: [2, 85], 86: [2, 18], 90: [2, 89], 101: [2, 53], 104: [2, 93], 110: [2, 19], 111: [2, 77], 116: [2, 97], 119: [2, 63], 122: [2, 69], 135: [2, 75], 136: [2, 32] },
	        parseError: function parseError(str, hash) {
	            throw new Error(str);
	        },
	        parse: function parse(input) {
	            var self = this,
	                stack = [0],
	                vstack = [null],
	                lstack = [],
	                table = this.table,
	                yytext = "",
	                yylineno = 0,
	                yyleng = 0,
	                recovering = 0,
	                TERROR = 2,
	                EOF = 1;
	            this.lexer.setInput(input);
	            this.lexer.yy = this.yy;
	            this.yy.lexer = this.lexer;
	            this.yy.parser = this;
	            if (typeof this.lexer.yylloc == "undefined") this.lexer.yylloc = {};
	            var yyloc = this.lexer.yylloc;
	            lstack.push(yyloc);
	            var ranges = this.lexer.options && this.lexer.options.ranges;
	            if (typeof this.yy.parseError === "function") this.parseError = this.yy.parseError;
	            function popStack(n) {
	                stack.length = stack.length - 2 * n;
	                vstack.length = vstack.length - n;
	                lstack.length = lstack.length - n;
	            }
	            function lex() {
	                var token;
	                token = self.lexer.lex() || 1;
	                if (typeof token !== "number") {
	                    token = self.symbols_[token] || token;
	                }
	                return token;
	            }
	            var symbol,
	                preErrorSymbol,
	                state,
	                action,
	                a,
	                r,
	                yyval = {},
	                p,
	                len,
	                newState,
	                expected;
	            while (true) {
	                state = stack[stack.length - 1];
	                if (this.defaultActions[state]) {
	                    action = this.defaultActions[state];
	                } else {
	                    if (symbol === null || typeof symbol == "undefined") {
	                        symbol = lex();
	                    }
	                    action = table[state] && table[state][symbol];
	                }
	                if (typeof action === "undefined" || !action.length || !action[0]) {
	                    var errStr = "";
	                    if (!recovering) {
	                        expected = [];
	                        for (p in table[state]) if (this.terminals_[p] && p > 2) {
	                            expected.push("'" + this.terminals_[p] + "'");
	                        }
	                        if (this.lexer.showPosition) {
	                            errStr = "Parse error on line " + (yylineno + 1) + ":\n" + this.lexer.showPosition() + "\nExpecting " + expected.join(", ") + ", got '" + (this.terminals_[symbol] || symbol) + "'";
	                        } else {
	                            errStr = "Parse error on line " + (yylineno + 1) + ": Unexpected " + (symbol == 1 ? "end of input" : "'" + (this.terminals_[symbol] || symbol) + "'");
	                        }
	                        this.parseError(errStr, { text: this.lexer.match, token: this.terminals_[symbol] || symbol, line: this.lexer.yylineno, loc: yyloc, expected: expected });
	                    }
	                }
	                if (action[0] instanceof Array && action.length > 1) {
	                    throw new Error("Parse Error: multiple actions possible at state: " + state + ", token: " + symbol);
	                }
	                switch (action[0]) {
	                    case 1:
	                        stack.push(symbol);
	                        vstack.push(this.lexer.yytext);
	                        lstack.push(this.lexer.yylloc);
	                        stack.push(action[1]);
	                        symbol = null;
	                        if (!preErrorSymbol) {
	                            yyleng = this.lexer.yyleng;
	                            yytext = this.lexer.yytext;
	                            yylineno = this.lexer.yylineno;
	                            yyloc = this.lexer.yylloc;
	                            if (recovering > 0) recovering--;
	                        } else {
	                            symbol = preErrorSymbol;
	                            preErrorSymbol = null;
	                        }
	                        break;
	                    case 2:
	                        len = this.productions_[action[1]][1];
	                        yyval.$ = vstack[vstack.length - len];
	                        yyval._$ = { first_line: lstack[lstack.length - (len || 1)].first_line, last_line: lstack[lstack.length - 1].last_line, first_column: lstack[lstack.length - (len || 1)].first_column, last_column: lstack[lstack.length - 1].last_column };
	                        if (ranges) {
	                            yyval._$.range = [lstack[lstack.length - (len || 1)].range[0], lstack[lstack.length - 1].range[1]];
	                        }
	                        r = this.performAction.call(yyval, yytext, yyleng, yylineno, this.yy, action[1], vstack, lstack);
	                        if (typeof r !== "undefined") {
	                            return r;
	                        }
	                        if (len) {
	                            stack = stack.slice(0, -1 * len * 2);
	                            vstack = vstack.slice(0, -1 * len);
	                            lstack = lstack.slice(0, -1 * len);
	                        }
	                        stack.push(this.productions_[action[1]][0]);
	                        vstack.push(yyval.$);
	                        lstack.push(yyval._$);
	                        newState = table[stack[stack.length - 2]][stack[stack.length - 1]];
	                        stack.push(newState);
	                        break;
	                    case 3:
	                        return true;
	                }
	            }
	            return true;
	        }
	    };
	    /* Jison generated lexer */
	    var lexer = (function () {
	        var lexer = { EOF: 1,
	            parseError: function parseError(str, hash) {
	                if (this.yy.parser) {
	                    this.yy.parser.parseError(str, hash);
	                } else {
	                    throw new Error(str);
	                }
	            },
	            setInput: function setInput(input) {
	                this._input = input;
	                this._more = this._less = this.done = false;
	                this.yylineno = this.yyleng = 0;
	                this.yytext = this.matched = this.match = '';
	                this.conditionStack = ['INITIAL'];
	                this.yylloc = { first_line: 1, first_column: 0, last_line: 1, last_column: 0 };
	                if (this.options.ranges) this.yylloc.range = [0, 0];
	                this.offset = 0;
	                return this;
	            },
	            input: function input() {
	                var ch = this._input[0];
	                this.yytext += ch;
	                this.yyleng++;
	                this.offset++;
	                this.match += ch;
	                this.matched += ch;
	                var lines = ch.match(/(?:\r\n?|\n).*/g);
	                if (lines) {
	                    this.yylineno++;
	                    this.yylloc.last_line++;
	                } else {
	                    this.yylloc.last_column++;
	                }
	                if (this.options.ranges) this.yylloc.range[1]++;

	                this._input = this._input.slice(1);
	                return ch;
	            },
	            unput: function unput(ch) {
	                var len = ch.length;
	                var lines = ch.split(/(?:\r\n?|\n)/g);

	                this._input = ch + this._input;
	                this.yytext = this.yytext.substr(0, this.yytext.length - len - 1);
	                //this.yyleng -= len;
	                this.offset -= len;
	                var oldLines = this.match.split(/(?:\r\n?|\n)/g);
	                this.match = this.match.substr(0, this.match.length - 1);
	                this.matched = this.matched.substr(0, this.matched.length - 1);

	                if (lines.length - 1) this.yylineno -= lines.length - 1;
	                var r = this.yylloc.range;

	                this.yylloc = { first_line: this.yylloc.first_line,
	                    last_line: this.yylineno + 1,
	                    first_column: this.yylloc.first_column,
	                    last_column: lines ? (lines.length === oldLines.length ? this.yylloc.first_column : 0) + oldLines[oldLines.length - lines.length].length - lines[0].length : this.yylloc.first_column - len
	                };

	                if (this.options.ranges) {
	                    this.yylloc.range = [r[0], r[0] + this.yyleng - len];
	                }
	                return this;
	            },
	            more: function more() {
	                this._more = true;
	                return this;
	            },
	            less: function less(n) {
	                this.unput(this.match.slice(n));
	            },
	            pastInput: function pastInput() {
	                var past = this.matched.substr(0, this.matched.length - this.match.length);
	                return (past.length > 20 ? '...' : '') + past.substr(-20).replace(/\n/g, "");
	            },
	            upcomingInput: function upcomingInput() {
	                var next = this.match;
	                if (next.length < 20) {
	                    next += this._input.substr(0, 20 - next.length);
	                }
	                return (next.substr(0, 20) + (next.length > 20 ? '...' : '')).replace(/\n/g, "");
	            },
	            showPosition: function showPosition() {
	                var pre = this.pastInput();
	                var c = new Array(pre.length + 1).join("-");
	                return pre + this.upcomingInput() + "\n" + c + "^";
	            },
	            next: function next() {
	                if (this.done) {
	                    return this.EOF;
	                }
	                if (!this._input) this.done = true;

	                var token, match, tempMatch, index, col, lines;
	                if (!this._more) {
	                    this.yytext = '';
	                    this.match = '';
	                }
	                var rules = this._currentRules();
	                for (var i = 0; i < rules.length; i++) {
	                    tempMatch = this._input.match(this.rules[rules[i]]);
	                    if (tempMatch && (!match || tempMatch[0].length > match[0].length)) {
	                        match = tempMatch;
	                        index = i;
	                        if (!this.options.flex) break;
	                    }
	                }
	                if (match) {
	                    lines = match[0].match(/(?:\r\n?|\n).*/g);
	                    if (lines) this.yylineno += lines.length;
	                    this.yylloc = { first_line: this.yylloc.last_line,
	                        last_line: this.yylineno + 1,
	                        first_column: this.yylloc.last_column,
	                        last_column: lines ? lines[lines.length - 1].length - lines[lines.length - 1].match(/\r?\n?/)[0].length : this.yylloc.last_column + match[0].length };
	                    this.yytext += match[0];
	                    this.match += match[0];
	                    this.matches = match;
	                    this.yyleng = this.yytext.length;
	                    if (this.options.ranges) {
	                        this.yylloc.range = [this.offset, this.offset += this.yyleng];
	                    }
	                    this._more = false;
	                    this._input = this._input.slice(match[0].length);
	                    this.matched += match[0];
	                    token = this.performAction.call(this, this.yy, this, rules[index], this.conditionStack[this.conditionStack.length - 1]);
	                    if (this.done && this._input) this.done = false;
	                    if (token) return token;else return;
	                }
	                if (this._input === "") {
	                    return this.EOF;
	                } else {
	                    return this.parseError('Lexical error on line ' + (this.yylineno + 1) + '. Unrecognized text.\n' + this.showPosition(), { text: "", token: null, line: this.yylineno });
	                }
	            },
	            lex: function lex() {
	                var r = this.next();
	                if (typeof r !== 'undefined') {
	                    return r;
	                } else {
	                    return this.lex();
	                }
	            },
	            begin: function begin(condition) {
	                this.conditionStack.push(condition);
	            },
	            popState: function popState() {
	                return this.conditionStack.pop();
	            },
	            _currentRules: function _currentRules() {
	                return this.conditions[this.conditionStack[this.conditionStack.length - 1]].rules;
	            },
	            topState: function topState() {
	                return this.conditionStack[this.conditionStack.length - 2];
	            },
	            pushState: function begin(condition) {
	                this.begin(condition);
	            } };
	        lexer.options = {};
	        lexer.performAction = function anonymous(yy, yy_, $avoiding_name_collisions, YY_START) {

	            function strip(start, end) {
	                return yy_.yytext = yy_.yytext.substring(start, yy_.yyleng - end + start);
	            }

	            var YYSTATE = YY_START;
	            switch ($avoiding_name_collisions) {
	                case 0:
	                    if (yy_.yytext.slice(-2) === "\\\\") {
	                        strip(0, 1);
	                        this.begin("mu");
	                    } else if (yy_.yytext.slice(-1) === "\\") {
	                        strip(0, 1);
	                        this.begin("emu");
	                    } else {
	                        this.begin("mu");
	                    }
	                    if (yy_.yytext) return 15;

	                    break;
	                case 1:
	                    return 15;
	                    break;
	                case 2:
	                    this.popState();
	                    return 15;

	                    break;
	                case 3:
	                    this.begin('raw');return 15;
	                    break;
	                case 4:
	                    this.popState();
	                    // Should be using `this.topState()` below, but it currently
	                    // returns the second top instead of the first top. Opened an
	                    // issue about it at https://github.com/zaach/jison/issues/291
	                    if (this.conditionStack[this.conditionStack.length - 1] === 'raw') {
	                        return 15;
	                    } else {
	                        strip(5, 9);
	                        return 'END_RAW_BLOCK';
	                    }

	                    break;
	                case 5:
	                    return 15;
	                    break;
	                case 6:
	                    this.popState();
	                    return 14;

	                    break;
	                case 7:
	                    return 65;
	                    break;
	                case 8:
	                    return 68;
	                    break;
	                case 9:
	                    return 19;
	                    break;
	                case 10:
	                    this.popState();
	                    this.begin('raw');
	                    return 23;

	                    break;
	                case 11:
	                    return 55;
	                    break;
	                case 12:
	                    return 60;
	                    break;
	                case 13:
	                    return 29;
	                    break;
	                case 14:
	                    return 47;
	                    break;
	                case 15:
	                    this.popState();return 44;
	                    break;
	                case 16:
	                    this.popState();return 44;
	                    break;
	                case 17:
	                    return 34;
	                    break;
	                case 18:
	                    return 39;
	                    break;
	                case 19:
	                    return 51;
	                    break;
	                case 20:
	                    return 48;
	                    break;
	                case 21:
	                    this.unput(yy_.yytext);
	                    this.popState();
	                    this.begin('com');

	                    break;
	                case 22:
	                    this.popState();
	                    return 14;

	                    break;
	                case 23:
	                    return 48;
	                    break;
	                case 24:
	                    return 73;
	                    break;
	                case 25:
	                    return 72;
	                    break;
	                case 26:
	                    return 72;
	                    break;
	                case 27:
	                    return 87;
	                    break;
	                case 28:
	                    // ignore whitespace
	                    break;
	                case 29:
	                    this.popState();return 54;
	                    break;
	                case 30:
	                    this.popState();return 33;
	                    break;
	                case 31:
	                    yy_.yytext = strip(1, 2).replace(/\\"/g, '"');return 80;
	                    break;
	                case 32:
	                    yy_.yytext = strip(1, 2).replace(/\\'/g, "'");return 80;
	                    break;
	                case 33:
	                    return 85;
	                    break;
	                case 34:
	                    return 82;
	                    break;
	                case 35:
	                    return 82;
	                    break;
	                case 36:
	                    return 83;
	                    break;
	                case 37:
	                    return 84;
	                    break;
	                case 38:
	                    return 81;
	                    break;
	                case 39:
	                    return 75;
	                    break;
	                case 40:
	                    return 77;
	                    break;
	                case 41:
	                    return 72;
	                    break;
	                case 42:
	                    yy_.yytext = yy_.yytext.replace(/\\([\\\]])/g, '$1');return 72;
	                    break;
	                case 43:
	                    return 'INVALID';
	                    break;
	                case 44:
	                    return 5;
	                    break;
	            }
	        };
	        lexer.rules = [/^(?:[^\x00]*?(?=(\{\{)))/, /^(?:[^\x00]+)/, /^(?:[^\x00]{2,}?(?=(\{\{|\\\{\{|\\\\\{\{|$)))/, /^(?:\{\{\{\{(?=[^\/]))/, /^(?:\{\{\{\{\/[^\s!"#%-,\.\/;->@\[-\^`\{-~]+(?=[=}\s\/.])\}\}\}\})/, /^(?:[^\x00]+?(?=(\{\{\{\{)))/, /^(?:[\s\S]*?--(~)?\}\})/, /^(?:\()/, /^(?:\))/, /^(?:\{\{\{\{)/, /^(?:\}\}\}\})/, /^(?:\{\{(~)?>)/, /^(?:\{\{(~)?#>)/, /^(?:\{\{(~)?#\*?)/, /^(?:\{\{(~)?\/)/, /^(?:\{\{(~)?\^\s*(~)?\}\})/, /^(?:\{\{(~)?\s*else\s*(~)?\}\})/, /^(?:\{\{(~)?\^)/, /^(?:\{\{(~)?\s*else\b)/, /^(?:\{\{(~)?\{)/, /^(?:\{\{(~)?&)/, /^(?:\{\{(~)?!--)/, /^(?:\{\{(~)?![\s\S]*?\}\})/, /^(?:\{\{(~)?\*?)/, /^(?:=)/, /^(?:\.\.)/, /^(?:\.(?=([=~}\s\/.)|])))/, /^(?:[\/.])/, /^(?:\s+)/, /^(?:\}(~)?\}\})/, /^(?:(~)?\}\})/, /^(?:"(\\["]|[^"])*")/, /^(?:'(\\[']|[^'])*')/, /^(?:@)/, /^(?:true(?=([~}\s)])))/, /^(?:false(?=([~}\s)])))/, /^(?:undefined(?=([~}\s)])))/, /^(?:null(?=([~}\s)])))/, /^(?:-?[0-9]+(?:\.[0-9]+)?(?=([~}\s)])))/, /^(?:as\s+\|)/, /^(?:\|)/, /^(?:([^\s!"#%-,\.\/;->@\[-\^`\{-~]+(?=([=~}\s\/.)|]))))/, /^(?:\[(\\\]|[^\]])*\])/, /^(?:.)/, /^(?:$)/];
	        lexer.conditions = { "mu": { "rules": [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44], "inclusive": false }, "emu": { "rules": [2], "inclusive": false }, "com": { "rules": [6], "inclusive": false }, "raw": { "rules": [3, 4, 5], "inclusive": false }, "INITIAL": { "rules": [0, 1, 44], "inclusive": true } };
	        return lexer;
	    })();
	    parser.lexer = lexer;
	    function Parser() {
	        this.yy = {};
	    }Parser.prototype = parser;parser.Parser = Parser;
	    return new Parser();
	})();exports["default"] = handlebars;
	module.exports = exports["default"];

/***/ }),
/* 48 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;

	var _visitor = __webpack_require__(49);

	var _visitor2 = _interopRequireDefault(_visitor);

	function WhitespaceControl() {
	  var options = arguments.length <= 0 || arguments[0] === undefined ? {} : arguments[0];

	  this.options = options;
	}
	WhitespaceControl.prototype = new _visitor2['default']();

	WhitespaceControl.prototype.Program = function (program) {
	  var doStandalone = !this.options.ignoreStandalone;

	  var isRoot = !this.isRootSeen;
	  this.isRootSeen = true;

	  var body = program.body;
	  for (var i = 0, l = body.length; i < l; i++) {
	    var current = body[i],
	        strip = this.accept(current);

	    if (!strip) {
	      continue;
	    }

	    var _isPrevWhitespace = isPrevWhitespace(body, i, isRoot),
	        _isNextWhitespace = isNextWhitespace(body, i, isRoot),
	        openStandalone = strip.openStandalone && _isPrevWhitespace,
	        closeStandalone = strip.closeStandalone && _isNextWhitespace,
	        inlineStandalone = strip.inlineStandalone && _isPrevWhitespace && _isNextWhitespace;

	    if (strip.close) {
	      omitRight(body, i, true);
	    }
	    if (strip.open) {
	      omitLeft(body, i, true);
	    }

	    if (doStandalone && inlineStandalone) {
	      omitRight(body, i);

	      if (omitLeft(body, i)) {
	        // If we are on a standalone node, save the indent info for partials
	        if (current.type === 'PartialStatement') {
	          // Pull out the whitespace from the final line
	          current.indent = /([ \t]+$)/.exec(body[i - 1].original)[1];
	        }
	      }
	    }
	    if (doStandalone && openStandalone) {
	      omitRight((current.program || current.inverse).body);

	      // Strip out the previous content node if it's whitespace only
	      omitLeft(body, i);
	    }
	    if (doStandalone && closeStandalone) {
	      // Always strip the next node
	      omitRight(body, i);

	      omitLeft((current.inverse || current.program).body);
	    }
	  }

	  return program;
	};

	WhitespaceControl.prototype.BlockStatement = WhitespaceControl.prototype.DecoratorBlock = WhitespaceControl.prototype.PartialBlockStatement = function (block) {
	  this.accept(block.program);
	  this.accept(block.inverse);

	  // Find the inverse program that is involed with whitespace stripping.
	  var program = block.program || block.inverse,
	      inverse = block.program && block.inverse,
	      firstInverse = inverse,
	      lastInverse = inverse;

	  if (inverse && inverse.chained) {
	    firstInverse = inverse.body[0].program;

	    // Walk the inverse chain to find the last inverse that is actually in the chain.
	    while (lastInverse.chained) {
	      lastInverse = lastInverse.body[lastInverse.body.length - 1].program;
	    }
	  }

	  var strip = {
	    open: block.openStrip.open,
	    close: block.closeStrip.close,

	    // Determine the standalone candiacy. Basically flag our content as being possibly standalone
	    // so our parent can determine if we actually are standalone
	    openStandalone: isNextWhitespace(program.body),
	    closeStandalone: isPrevWhitespace((firstInverse || program).body)
	  };

	  if (block.openStrip.close) {
	    omitRight(program.body, null, true);
	  }

	  if (inverse) {
	    var inverseStrip = block.inverseStrip;

	    if (inverseStrip.open) {
	      omitLeft(program.body, null, true);
	    }

	    if (inverseStrip.close) {
	      omitRight(firstInverse.body, null, true);
	    }
	    if (block.closeStrip.open) {
	      omitLeft(lastInverse.body, null, true);
	    }

	    // Find standalone else statments
	    if (!this.options.ignoreStandalone && isPrevWhitespace(program.body) && isNextWhitespace(firstInverse.body)) {
	      omitLeft(program.body);
	      omitRight(firstInverse.body);
	    }
	  } else if (block.closeStrip.open) {
	    omitLeft(program.body, null, true);
	  }

	  return strip;
	};

	WhitespaceControl.prototype.Decorator = WhitespaceControl.prototype.MustacheStatement = function (mustache) {
	  return mustache.strip;
	};

	WhitespaceControl.prototype.PartialStatement = WhitespaceControl.prototype.CommentStatement = function (node) {
	  /* istanbul ignore next */
	  var strip = node.strip || {};
	  return {
	    inlineStandalone: true,
	    open: strip.open,
	    close: strip.close
	  };
	};

	function isPrevWhitespace(body, i, isRoot) {
	  if (i === undefined) {
	    i = body.length;
	  }

	  // Nodes that end with newlines are considered whitespace (but are special
	  // cased for strip operations)
	  var prev = body[i - 1],
	      sibling = body[i - 2];
	  if (!prev) {
	    return isRoot;
	  }

	  if (prev.type === 'ContentStatement') {
	    return (sibling || !isRoot ? /\r?\n\s*?$/ : /(^|\r?\n)\s*?$/).test(prev.original);
	  }
	}
	function isNextWhitespace(body, i, isRoot) {
	  if (i === undefined) {
	    i = -1;
	  }

	  var next = body[i + 1],
	      sibling = body[i + 2];
	  if (!next) {
	    return isRoot;
	  }

	  if (next.type === 'ContentStatement') {
	    return (sibling || !isRoot ? /^\s*?\r?\n/ : /^\s*?(\r?\n|$)/).test(next.original);
	  }
	}

	// Marks the node to the right of the position as omitted.
	// I.e. {{foo}}' ' will mark the ' ' node as omitted.
	//
	// If i is undefined, then the first child will be marked as such.
	//
	// If mulitple is truthy then all whitespace will be stripped out until non-whitespace
	// content is met.
	function omitRight(body, i, multiple) {
	  var current = body[i == null ? 0 : i + 1];
	  if (!current || current.type !== 'ContentStatement' || !multiple && current.rightStripped) {
	    return;
	  }

	  var original = current.value;
	  current.value = current.value.replace(multiple ? /^\s+/ : /^[ \t]*\r?\n?/, '');
	  current.rightStripped = current.value !== original;
	}

	// Marks the node to the left of the position as omitted.
	// I.e. ' '{{foo}} will mark the ' ' node as omitted.
	//
	// If i is undefined then the last child will be marked as such.
	//
	// If mulitple is truthy then all whitespace will be stripped out until non-whitespace
	// content is met.
	function omitLeft(body, i, multiple) {
	  var current = body[i == null ? body.length - 1 : i - 1];
	  if (!current || current.type !== 'ContentStatement' || !multiple && current.leftStripped) {
	    return;
	  }

	  // We omit the last node if it's whitespace only and not preceded by a non-content node.
	  var original = current.value;
	  current.value = current.value.replace(multiple ? /\s+$/ : /[ \t]+$/, '');
	  current.leftStripped = current.value !== original;
	  return current.leftStripped;
	}

	exports['default'] = WhitespaceControl;
	module.exports = exports['default'];

/***/ }),
/* 49 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;

	var _exception = __webpack_require__(6);

	var _exception2 = _interopRequireDefault(_exception);

	function Visitor() {
	  this.parents = [];
	}

	Visitor.prototype = {
	  constructor: Visitor,
	  mutating: false,

	  // Visits a given value. If mutating, will replace the value if necessary.
	  acceptKey: function acceptKey(node, name) {
	    var value = this.accept(node[name]);
	    if (this.mutating) {
	      // Hacky sanity check: This may have a few false positives for type for the helper
	      // methods but will generally do the right thing without a lot of overhead.
	      if (value && !Visitor.prototype[value.type]) {
	        throw new _exception2['default']('Unexpected node type "' + value.type + '" found when accepting ' + name + ' on ' + node.type);
	      }
	      node[name] = value;
	    }
	  },

	  // Performs an accept operation with added sanity check to ensure
	  // required keys are not removed.
	  acceptRequired: function acceptRequired(node, name) {
	    this.acceptKey(node, name);

	    if (!node[name]) {
	      throw new _exception2['default'](node.type + ' requires ' + name);
	    }
	  },

	  // Traverses a given array. If mutating, empty respnses will be removed
	  // for child elements.
	  acceptArray: function acceptArray(array) {
	    for (var i = 0, l = array.length; i < l; i++) {
	      this.acceptKey(array, i);

	      if (!array[i]) {
	        array.splice(i, 1);
	        i--;
	        l--;
	      }
	    }
	  },

	  accept: function accept(object) {
	    if (!object) {
	      return;
	    }

	    /* istanbul ignore next: Sanity code */
	    if (!this[object.type]) {
	      throw new _exception2['default']('Unknown type: ' + object.type, object);
	    }

	    if (this.current) {
	      this.parents.unshift(this.current);
	    }
	    this.current = object;

	    var ret = this[object.type](object);

	    this.current = this.parents.shift();

	    if (!this.mutating || ret) {
	      return ret;
	    } else if (ret !== false) {
	      return object;
	    }
	  },

	  Program: function Program(program) {
	    this.acceptArray(program.body);
	  },

	  MustacheStatement: visitSubExpression,
	  Decorator: visitSubExpression,

	  BlockStatement: visitBlock,
	  DecoratorBlock: visitBlock,

	  PartialStatement: visitPartial,
	  PartialBlockStatement: function PartialBlockStatement(partial) {
	    visitPartial.call(this, partial);

	    this.acceptKey(partial, 'program');
	  },

	  ContentStatement: function ContentStatement() /* content */{},
	  CommentStatement: function CommentStatement() /* comment */{},

	  SubExpression: visitSubExpression,

	  PathExpression: function PathExpression() /* path */{},

	  StringLiteral: function StringLiteral() /* string */{},
	  NumberLiteral: function NumberLiteral() /* number */{},
	  BooleanLiteral: function BooleanLiteral() /* bool */{},
	  UndefinedLiteral: function UndefinedLiteral() /* literal */{},
	  NullLiteral: function NullLiteral() /* literal */{},

	  Hash: function Hash(hash) {
	    this.acceptArray(hash.pairs);
	  },
	  HashPair: function HashPair(pair) {
	    this.acceptRequired(pair, 'value');
	  }
	};

	function visitSubExpression(mustache) {
	  this.acceptRequired(mustache, 'path');
	  this.acceptArray(mustache.params);
	  this.acceptKey(mustache, 'hash');
	}
	function visitBlock(block) {
	  visitSubExpression.call(this, block);

	  this.acceptKey(block, 'program');
	  this.acceptKey(block, 'inverse');
	}
	function visitPartial(partial) {
	  this.acceptRequired(partial, 'name');
	  this.acceptArray(partial.params);
	  this.acceptKey(partial, 'hash');
	}

	exports['default'] = Visitor;
	module.exports = exports['default'];

/***/ }),
/* 50 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;
	exports.SourceLocation = SourceLocation;
	exports.id = id;
	exports.stripFlags = stripFlags;
	exports.stripComment = stripComment;
	exports.preparePath = preparePath;
	exports.prepareMustache = prepareMustache;
	exports.prepareRawBlock = prepareRawBlock;
	exports.prepareBlock = prepareBlock;
	exports.prepareProgram = prepareProgram;
	exports.preparePartialBlock = preparePartialBlock;

	var _exception = __webpack_require__(6);

	var _exception2 = _interopRequireDefault(_exception);

	function validateClose(open, close) {
	  close = close.path ? close.path.original : close;

	  if (open.path.original !== close) {
	    var errorNode = { loc: open.path.loc };

	    throw new _exception2['default'](open.path.original + " doesn't match " + close, errorNode);
	  }
	}

	function SourceLocation(source, locInfo) {
	  this.source = source;
	  this.start = {
	    line: locInfo.first_line,
	    column: locInfo.first_column
	  };
	  this.end = {
	    line: locInfo.last_line,
	    column: locInfo.last_column
	  };
	}

	function id(token) {
	  if (/^\[.*\]$/.test(token)) {
	    return token.substring(1, token.length - 1);
	  } else {
	    return token;
	  }
	}

	function stripFlags(open, close) {
	  return {
	    open: open.charAt(2) === '~',
	    close: close.charAt(close.length - 3) === '~'
	  };
	}

	function stripComment(comment) {
	  return comment.replace(/^\{\{~?!-?-?/, '').replace(/-?-?~?\}\}$/, '');
	}

	function preparePath(data, parts, loc) {
	  loc = this.locInfo(loc);

	  var original = data ? '@' : '',
	      dig = [],
	      depth = 0;

	  for (var i = 0, l = parts.length; i < l; i++) {
	    var part = parts[i].part,

	    // If we have [] syntax then we do not treat path references as operators,
	    // i.e. foo.[this] resolves to approximately context.foo['this']
	    isLiteral = parts[i].original !== part;
	    original += (parts[i].separator || '') + part;

	    if (!isLiteral && (part === '..' || part === '.' || part === 'this')) {
	      if (dig.length > 0) {
	        throw new _exception2['default']('Invalid path: ' + original, { loc: loc });
	      } else if (part === '..') {
	        depth++;
	      }
	    } else {
	      dig.push(part);
	    }
	  }

	  return {
	    type: 'PathExpression',
	    data: data,
	    depth: depth,
	    parts: dig,
	    original: original,
	    loc: loc
	  };
	}

	function prepareMustache(path, params, hash, open, strip, locInfo) {
	  // Must use charAt to support IE pre-10
	  var escapeFlag = open.charAt(3) || open.charAt(2),
	      escaped = escapeFlag !== '{' && escapeFlag !== '&';

	  var decorator = /\*/.test(open);
	  return {
	    type: decorator ? 'Decorator' : 'MustacheStatement',
	    path: path,
	    params: params,
	    hash: hash,
	    escaped: escaped,
	    strip: strip,
	    loc: this.locInfo(locInfo)
	  };
	}

	function prepareRawBlock(openRawBlock, contents, close, locInfo) {
	  validateClose(openRawBlock, close);

	  locInfo = this.locInfo(locInfo);
	  var program = {
	    type: 'Program',
	    body: contents,
	    strip: {},
	    loc: locInfo
	  };

	  return {
	    type: 'BlockStatement',
	    path: openRawBlock.path,
	    params: openRawBlock.params,
	    hash: openRawBlock.hash,
	    program: program,
	    openStrip: {},
	    inverseStrip: {},
	    closeStrip: {},
	    loc: locInfo
	  };
	}

	function prepareBlock(openBlock, program, inverseAndProgram, close, inverted, locInfo) {
	  if (close && close.path) {
	    validateClose(openBlock, close);
	  }

	  var decorator = /\*/.test(openBlock.open);

	  program.blockParams = openBlock.blockParams;

	  var inverse = undefined,
	      inverseStrip = undefined;

	  if (inverseAndProgram) {
	    if (decorator) {
	      throw new _exception2['default']('Unexpected inverse block on decorator', inverseAndProgram);
	    }

	    if (inverseAndProgram.chain) {
	      inverseAndProgram.program.body[0].closeStrip = close.strip;
	    }

	    inverseStrip = inverseAndProgram.strip;
	    inverse = inverseAndProgram.program;
	  }

	  if (inverted) {
	    inverted = inverse;
	    inverse = program;
	    program = inverted;
	  }

	  return {
	    type: decorator ? 'DecoratorBlock' : 'BlockStatement',
	    path: openBlock.path,
	    params: openBlock.params,
	    hash: openBlock.hash,
	    program: program,
	    inverse: inverse,
	    openStrip: openBlock.strip,
	    inverseStrip: inverseStrip,
	    closeStrip: close && close.strip,
	    loc: this.locInfo(locInfo)
	  };
	}

	function prepareProgram(statements, loc) {
	  if (!loc && statements.length) {
	    var firstLoc = statements[0].loc,
	        lastLoc = statements[statements.length - 1].loc;

	    /* istanbul ignore else */
	    if (firstLoc && lastLoc) {
	      loc = {
	        source: firstLoc.source,
	        start: {
	          line: firstLoc.start.line,
	          column: firstLoc.start.column
	        },
	        end: {
	          line: lastLoc.end.line,
	          column: lastLoc.end.column
	        }
	      };
	    }
	  }

	  return {
	    type: 'Program',
	    body: statements,
	    strip: {},
	    loc: loc
	  };
	}

	function preparePartialBlock(open, program, close, locInfo) {
	  validateClose(open, close);

	  return {
	    type: 'PartialBlockStatement',
	    name: open.path,
	    params: open.params,
	    hash: open.hash,
	    program: program,
	    openStrip: open.strip,
	    closeStrip: close && close.strip,
	    loc: this.locInfo(locInfo)
	  };
	}

/***/ }),
/* 51 */
/***/ (function(module, exports, __webpack_require__) {

	/* eslint-disable new-cap */

	'use strict';

	var _Object$create = __webpack_require__(34)['default'];

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;
	exports.Compiler = Compiler;
	exports.precompile = precompile;
	exports.compile = compile;

	var _exception = __webpack_require__(6);

	var _exception2 = _interopRequireDefault(_exception);

	var _utils = __webpack_require__(5);

	var _ast = __webpack_require__(45);

	var _ast2 = _interopRequireDefault(_ast);

	var slice = [].slice;

	function Compiler() {}

	// the foundHelper register will disambiguate helper lookup from finding a
	// function in a context. This is necessary for mustache compatibility, which
	// requires that context functions in blocks are evaluated by blockHelperMissing,
	// and then proceed as if the resulting value was provided to blockHelperMissing.

	Compiler.prototype = {
	  compiler: Compiler,

	  equals: function equals(other) {
	    var len = this.opcodes.length;
	    if (other.opcodes.length !== len) {
	      return false;
	    }

	    for (var i = 0; i < len; i++) {
	      var opcode = this.opcodes[i],
	          otherOpcode = other.opcodes[i];
	      if (opcode.opcode !== otherOpcode.opcode || !argEquals(opcode.args, otherOpcode.args)) {
	        return false;
	      }
	    }

	    // We know that length is the same between the two arrays because they are directly tied
	    // to the opcode behavior above.
	    len = this.children.length;
	    for (var i = 0; i < len; i++) {
	      if (!this.children[i].equals(other.children[i])) {
	        return false;
	      }
	    }

	    return true;
	  },

	  guid: 0,

	  compile: function compile(program, options) {
	    this.sourceNode = [];
	    this.opcodes = [];
	    this.children = [];
	    this.options = options;
	    this.stringParams = options.stringParams;
	    this.trackIds = options.trackIds;

	    options.blockParams = options.blockParams || [];

	    options.knownHelpers = _utils.extend(_Object$create(null), {
	      helperMissing: true,
	      blockHelperMissing: true,
	      each: true,
	      'if': true,
	      unless: true,
	      'with': true,
	      log: true,
	      lookup: true
	    }, options.knownHelpers);

	    return this.accept(program);
	  },

	  compileProgram: function compileProgram(program) {
	    var childCompiler = new this.compiler(),
	        // eslint-disable-line new-cap
	    result = childCompiler.compile(program, this.options),
	        guid = this.guid++;

	    this.usePartial = this.usePartial || result.usePartial;

	    this.children[guid] = result;
	    this.useDepths = this.useDepths || result.useDepths;

	    return guid;
	  },

	  accept: function accept(node) {
	    /* istanbul ignore next: Sanity code */
	    if (!this[node.type]) {
	      throw new _exception2['default']('Unknown type: ' + node.type, node);
	    }

	    this.sourceNode.unshift(node);
	    var ret = this[node.type](node);
	    this.sourceNode.shift();
	    return ret;
	  },

	  Program: function Program(program) {
	    this.options.blockParams.unshift(program.blockParams);

	    var body = program.body,
	        bodyLength = body.length;
	    for (var i = 0; i < bodyLength; i++) {
	      this.accept(body[i]);
	    }

	    this.options.blockParams.shift();

	    this.isSimple = bodyLength === 1;
	    this.blockParams = program.blockParams ? program.blockParams.length : 0;

	    return this;
	  },

	  BlockStatement: function BlockStatement(block) {
	    transformLiteralToPath(block);

	    var program = block.program,
	        inverse = block.inverse;

	    program = program && this.compileProgram(program);
	    inverse = inverse && this.compileProgram(inverse);

	    var type = this.classifySexpr(block);

	    if (type === 'helper') {
	      this.helperSexpr(block, program, inverse);
	    } else if (type === 'simple') {
	      this.simpleSexpr(block);

	      // now that the simple mustache is resolved, we need to
	      // evaluate it by executing `blockHelperMissing`
	      this.opcode('pushProgram', program);
	      this.opcode('pushProgram', inverse);
	      this.opcode('emptyHash');
	      this.opcode('blockValue', block.path.original);
	    } else {
	      this.ambiguousSexpr(block, program, inverse);

	      // now that the simple mustache is resolved, we need to
	      // evaluate it by executing `blockHelperMissing`
	      this.opcode('pushProgram', program);
	      this.opcode('pushProgram', inverse);
	      this.opcode('emptyHash');
	      this.opcode('ambiguousBlockValue');
	    }

	    this.opcode('append');
	  },

	  DecoratorBlock: function DecoratorBlock(decorator) {
	    var program = decorator.program && this.compileProgram(decorator.program);
	    var params = this.setupFullMustacheParams(decorator, program, undefined),
	        path = decorator.path;

	    this.useDecorators = true;
	    this.opcode('registerDecorator', params.length, path.original);
	  },

	  PartialStatement: function PartialStatement(partial) {
	    this.usePartial = true;

	    var program = partial.program;
	    if (program) {
	      program = this.compileProgram(partial.program);
	    }

	    var params = partial.params;
	    if (params.length > 1) {
	      throw new _exception2['default']('Unsupported number of partial arguments: ' + params.length, partial);
	    } else if (!params.length) {
	      if (this.options.explicitPartialContext) {
	        this.opcode('pushLiteral', 'undefined');
	      } else {
	        params.push({ type: 'PathExpression', parts: [], depth: 0 });
	      }
	    }

	    var partialName = partial.name.original,
	        isDynamic = partial.name.type === 'SubExpression';
	    if (isDynamic) {
	      this.accept(partial.name);
	    }

	    this.setupFullMustacheParams(partial, program, undefined, true);

	    var indent = partial.indent || '';
	    if (this.options.preventIndent && indent) {
	      this.opcode('appendContent', indent);
	      indent = '';
	    }

	    this.opcode('invokePartial', isDynamic, partialName, indent);
	    this.opcode('append');
	  },
	  PartialBlockStatement: function PartialBlockStatement(partialBlock) {
	    this.PartialStatement(partialBlock);
	  },

	  MustacheStatement: function MustacheStatement(mustache) {
	    this.SubExpression(mustache);

	    if (mustache.escaped && !this.options.noEscape) {
	      this.opcode('appendEscaped');
	    } else {
	      this.opcode('append');
	    }
	  },
	  Decorator: function Decorator(decorator) {
	    this.DecoratorBlock(decorator);
	  },

	  ContentStatement: function ContentStatement(content) {
	    if (content.value) {
	      this.opcode('appendContent', content.value);
	    }
	  },

	  CommentStatement: function CommentStatement() {},

	  SubExpression: function SubExpression(sexpr) {
	    transformLiteralToPath(sexpr);
	    var type = this.classifySexpr(sexpr);

	    if (type === 'simple') {
	      this.simpleSexpr(sexpr);
	    } else if (type === 'helper') {
	      this.helperSexpr(sexpr);
	    } else {
	      this.ambiguousSexpr(sexpr);
	    }
	  },
	  ambiguousSexpr: function ambiguousSexpr(sexpr, program, inverse) {
	    var path = sexpr.path,
	        name = path.parts[0],
	        isBlock = program != null || inverse != null;

	    this.opcode('getContext', path.depth);

	    this.opcode('pushProgram', program);
	    this.opcode('pushProgram', inverse);

	    path.strict = true;
	    this.accept(path);

	    this.opcode('invokeAmbiguous', name, isBlock);
	  },

	  simpleSexpr: function simpleSexpr(sexpr) {
	    var path = sexpr.path;
	    path.strict = true;
	    this.accept(path);
	    this.opcode('resolvePossibleLambda');
	  },

	  helperSexpr: function helperSexpr(sexpr, program, inverse) {
	    var params = this.setupFullMustacheParams(sexpr, program, inverse),
	        path = sexpr.path,
	        name = path.parts[0];

	    if (this.options.knownHelpers[name]) {
	      this.opcode('invokeKnownHelper', params.length, name);
	    } else if (this.options.knownHelpersOnly) {
	      throw new _exception2['default']('You specified knownHelpersOnly, but used the unknown helper ' + name, sexpr);
	    } else {
	      path.strict = true;
	      path.falsy = true;

	      this.accept(path);
	      this.opcode('invokeHelper', params.length, path.original, _ast2['default'].helpers.simpleId(path));
	    }
	  },

	  PathExpression: function PathExpression(path) {
	    this.addDepth(path.depth);
	    this.opcode('getContext', path.depth);

	    var name = path.parts[0],
	        scoped = _ast2['default'].helpers.scopedId(path),
	        blockParamId = !path.depth && !scoped && this.blockParamIndex(name);

	    if (blockParamId) {
	      this.opcode('lookupBlockParam', blockParamId, path.parts);
	    } else if (!name) {
	      // Context reference, i.e. `{{foo .}}` or `{{foo ..}}`
	      this.opcode('pushContext');
	    } else if (path.data) {
	      this.options.data = true;
	      this.opcode('lookupData', path.depth, path.parts, path.strict);
	    } else {
	      this.opcode('lookupOnContext', path.parts, path.falsy, path.strict, scoped);
	    }
	  },

	  StringLiteral: function StringLiteral(string) {
	    this.opcode('pushString', string.value);
	  },

	  NumberLiteral: function NumberLiteral(number) {
	    this.opcode('pushLiteral', number.value);
	  },

	  BooleanLiteral: function BooleanLiteral(bool) {
	    this.opcode('pushLiteral', bool.value);
	  },

	  UndefinedLiteral: function UndefinedLiteral() {
	    this.opcode('pushLiteral', 'undefined');
	  },

	  NullLiteral: function NullLiteral() {
	    this.opcode('pushLiteral', 'null');
	  },

	  Hash: function Hash(hash) {
	    var pairs = hash.pairs,
	        i = 0,
	        l = pairs.length;

	    this.opcode('pushHash');

	    for (; i < l; i++) {
	      this.pushParam(pairs[i].value);
	    }
	    while (i--) {
	      this.opcode('assignToHash', pairs[i].key);
	    }
	    this.opcode('popHash');
	  },

	  // HELPERS
	  opcode: function opcode(name) {
	    this.opcodes.push({
	      opcode: name,
	      args: slice.call(arguments, 1),
	      loc: this.sourceNode[0].loc
	    });
	  },

	  addDepth: function addDepth(depth) {
	    if (!depth) {
	      return;
	    }

	    this.useDepths = true;
	  },

	  classifySexpr: function classifySexpr(sexpr) {
	    var isSimple = _ast2['default'].helpers.simpleId(sexpr.path);

	    var isBlockParam = isSimple && !!this.blockParamIndex(sexpr.path.parts[0]);

	    // a mustache is an eligible helper if:
	    // * its id is simple (a single part, not `this` or `..`)
	    var isHelper = !isBlockParam && _ast2['default'].helpers.helperExpression(sexpr);

	    // if a mustache is an eligible helper but not a definite
	    // helper, it is ambiguous, and will be resolved in a later
	    // pass or at runtime.
	    var isEligible = !isBlockParam && (isHelper || isSimple);

	    // if ambiguous, we can possibly resolve the ambiguity now
	    // An eligible helper is one that does not have a complex path, i.e. `this.foo`, `../foo` etc.
	    if (isEligible && !isHelper) {
	      var _name = sexpr.path.parts[0],
	          options = this.options;
	      if (options.knownHelpers[_name]) {
	        isHelper = true;
	      } else if (options.knownHelpersOnly) {
	        isEligible = false;
	      }
	    }

	    if (isHelper) {
	      return 'helper';
	    } else if (isEligible) {
	      return 'ambiguous';
	    } else {
	      return 'simple';
	    }
	  },

	  pushParams: function pushParams(params) {
	    for (var i = 0, l = params.length; i < l; i++) {
	      this.pushParam(params[i]);
	    }
	  },

	  pushParam: function pushParam(val) {
	    var value = val.value != null ? val.value : val.original || '';

	    if (this.stringParams) {
	      if (value.replace) {
	        value = value.replace(/^(\.?\.\/)*/g, '').replace(/\//g, '.');
	      }

	      if (val.depth) {
	        this.addDepth(val.depth);
	      }
	      this.opcode('getContext', val.depth || 0);
	      this.opcode('pushStringParam', value, val.type);

	      if (val.type === 'SubExpression') {
	        // SubExpressions get evaluated and passed in
	        // in string params mode.
	        this.accept(val);
	      }
	    } else {
	      if (this.trackIds) {
	        var blockParamIndex = undefined;
	        if (val.parts && !_ast2['default'].helpers.scopedId(val) && !val.depth) {
	          blockParamIndex = this.blockParamIndex(val.parts[0]);
	        }
	        if (blockParamIndex) {
	          var blockParamChild = val.parts.slice(1).join('.');
	          this.opcode('pushId', 'BlockParam', blockParamIndex, blockParamChild);
	        } else {
	          value = val.original || value;
	          if (value.replace) {
	            value = value.replace(/^this(?:\.|$)/, '').replace(/^\.\//, '').replace(/^\.$/, '');
	          }

	          this.opcode('pushId', val.type, value);
	        }
	      }
	      this.accept(val);
	    }
	  },

	  setupFullMustacheParams: function setupFullMustacheParams(sexpr, program, inverse, omitEmpty) {
	    var params = sexpr.params;
	    this.pushParams(params);

	    this.opcode('pushProgram', program);
	    this.opcode('pushProgram', inverse);

	    if (sexpr.hash) {
	      this.accept(sexpr.hash);
	    } else {
	      this.opcode('emptyHash', omitEmpty);
	    }

	    return params;
	  },

	  blockParamIndex: function blockParamIndex(name) {
	    for (var depth = 0, len = this.options.blockParams.length; depth < len; depth++) {
	      var blockParams = this.options.blockParams[depth],
	          param = blockParams && _utils.indexOf(blockParams, name);
	      if (blockParams && param >= 0) {
	        return [depth, param];
	      }
	    }
	  }
	};

	function precompile(input, options, env) {
	  if (input == null || typeof input !== 'string' && input.type !== 'Program') {
	    throw new _exception2['default']('You must pass a string or Handlebars AST to Handlebars.precompile. You passed ' + input);
	  }

	  options = options || {};
	  if (!('data' in options)) {
	    options.data = true;
	  }
	  if (options.compat) {
	    options.useDepths = true;
	  }

	  var ast = env.parse(input, options),
	      environment = new env.Compiler().compile(ast, options);
	  return new env.JavaScriptCompiler().compile(environment, options);
	}

	function compile(input, options, env) {
	  if (options === undefined) options = {};

	  if (input == null || typeof input !== 'string' && input.type !== 'Program') {
	    throw new _exception2['default']('You must pass a string or Handlebars AST to Handlebars.compile. You passed ' + input);
	  }

	  options = _utils.extend({}, options);
	  if (!('data' in options)) {
	    options.data = true;
	  }
	  if (options.compat) {
	    options.useDepths = true;
	  }

	  var compiled = undefined;

	  function compileInput() {
	    var ast = env.parse(input, options),
	        environment = new env.Compiler().compile(ast, options),
	        templateSpec = new env.JavaScriptCompiler().compile(environment, options, undefined, true);
	    return env.template(templateSpec);
	  }

	  // Template is only compiled on first use and cached after that point.
	  function ret(context, execOptions) {
	    if (!compiled) {
	      compiled = compileInput();
	    }
	    return compiled.call(this, context, execOptions);
	  }
	  ret._setup = function (setupOptions) {
	    if (!compiled) {
	      compiled = compileInput();
	    }
	    return compiled._setup(setupOptions);
	  };
	  ret._child = function (i, data, blockParams, depths) {
	    if (!compiled) {
	      compiled = compileInput();
	    }
	    return compiled._child(i, data, blockParams, depths);
	  };
	  return ret;
	}

	function argEquals(a, b) {
	  if (a === b) {
	    return true;
	  }

	  if (_utils.isArray(a) && _utils.isArray(b) && a.length === b.length) {
	    for (var i = 0; i < a.length; i++) {
	      if (!argEquals(a[i], b[i])) {
	        return false;
	      }
	    }
	    return true;
	  }
	}

	function transformLiteralToPath(sexpr) {
	  if (!sexpr.path.parts) {
	    var literal = sexpr.path;
	    // Casting to string here to make false and 0 literal values play nicely with the rest
	    // of the system.
	    sexpr.path = {
	      type: 'PathExpression',
	      data: false,
	      depth: 0,
	      parts: [literal.original + ''],
	      original: literal.original + '',
	      loc: literal.loc
	    };
	  }
	}

/***/ }),
/* 52 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _Object$keys = __webpack_require__(13)['default'];

	var _interopRequireDefault = __webpack_require__(1)['default'];

	exports.__esModule = true;

	var _base = __webpack_require__(4);

	var _exception = __webpack_require__(6);

	var _exception2 = _interopRequireDefault(_exception);

	var _utils = __webpack_require__(5);

	var _codeGen = __webpack_require__(53);

	var _codeGen2 = _interopRequireDefault(_codeGen);

	function Literal(value) {
	  this.value = value;
	}

	function JavaScriptCompiler() {}

	JavaScriptCompiler.prototype = {
	  // PUBLIC API: You can override these methods in a subclass to provide
	  // alternative compiled forms for name lookup and buffering semantics
	  nameLookup: function nameLookup(parent, name /*,  type */) {
	    return this.internalNameLookup(parent, name);
	  },
	  depthedLookup: function depthedLookup(name) {
	    return [this.aliasable('container.lookup'), '(depths, ', JSON.stringify(name), ')'];
	  },

	  compilerInfo: function compilerInfo() {
	    var revision = _base.COMPILER_REVISION,
	        versions = _base.REVISION_CHANGES[revision];
	    return [revision, versions];
	  },

	  appendToBuffer: function appendToBuffer(source, location, explicit) {
	    // Force a source as this simplifies the merge logic.
	    if (!_utils.isArray(source)) {
	      source = [source];
	    }
	    source = this.source.wrap(source, location);

	    if (this.environment.isSimple) {
	      return ['return ', source, ';'];
	    } else if (explicit) {
	      // This is a case where the buffer operation occurs as a child of another
	      // construct, generally braces. We have to explicitly output these buffer
	      // operations to ensure that the emitted code goes in the correct location.
	      return ['buffer += ', source, ';'];
	    } else {
	      source.appendToBuffer = true;
	      return source;
	    }
	  },

	  initializeBuffer: function initializeBuffer() {
	    return this.quotedString('');
	  },
	  // END PUBLIC API
	  internalNameLookup: function internalNameLookup(parent, name) {
	    this.lookupPropertyFunctionIsUsed = true;
	    return ['lookupProperty(', parent, ',', JSON.stringify(name), ')'];
	  },

	  lookupPropertyFunctionIsUsed: false,

	  compile: function compile(environment, options, context, asObject) {
	    this.environment = environment;
	    this.options = options;
	    this.stringParams = this.options.stringParams;
	    this.trackIds = this.options.trackIds;
	    this.precompile = !asObject;

	    this.name = this.environment.name;
	    this.isChild = !!context;
	    this.context = context || {
	      decorators: [],
	      programs: [],
	      environments: []
	    };

	    this.preamble();

	    this.stackSlot = 0;
	    this.stackVars = [];
	    this.aliases = {};
	    this.registers = { list: [] };
	    this.hashes = [];
	    this.compileStack = [];
	    this.inlineStack = [];
	    this.blockParams = [];

	    this.compileChildren(environment, options);

	    this.useDepths = this.useDepths || environment.useDepths || environment.useDecorators || this.options.compat;
	    this.useBlockParams = this.useBlockParams || environment.useBlockParams;

	    var opcodes = environment.opcodes,
	        opcode = undefined,
	        firstLoc = undefined,
	        i = undefined,
	        l = undefined;

	    for (i = 0, l = opcodes.length; i < l; i++) {
	      opcode = opcodes[i];

	      this.source.currentLocation = opcode.loc;
	      firstLoc = firstLoc || opcode.loc;
	      this[opcode.opcode].apply(this, opcode.args);
	    }

	    // Flush any trailing content that might be pending.
	    this.source.currentLocation = firstLoc;
	    this.pushSource('');

	    /* istanbul ignore next */
	    if (this.stackSlot || this.inlineStack.length || this.compileStack.length) {
	      throw new _exception2['default']('Compile completed with content left on stack');
	    }

	    if (!this.decorators.isEmpty()) {
	      this.useDecorators = true;

	      this.decorators.prepend(['var decorators = container.decorators, ', this.lookupPropertyFunctionVarDeclaration(), ';\n']);
	      this.decorators.push('return fn;');

	      if (asObject) {
	        this.decorators = Function.apply(this, ['fn', 'props', 'container', 'depth0', 'data', 'blockParams', 'depths', this.decorators.merge()]);
	      } else {
	        this.decorators.prepend('function(fn, props, container, depth0, data, blockParams, depths) {\n');
	        this.decorators.push('}\n');
	        this.decorators = this.decorators.merge();
	      }
	    } else {
	      this.decorators = undefined;
	    }

	    var fn = this.createFunctionContext(asObject);
	    if (!this.isChild) {
	      var ret = {
	        compiler: this.compilerInfo(),
	        main: fn
	      };

	      if (this.decorators) {
	        ret.main_d = this.decorators; // eslint-disable-line camelcase
	        ret.useDecorators = true;
	      }

	      var _context = this.context;
	      var programs = _context.programs;
	      var decorators = _context.decorators;

	      for (i = 0, l = programs.length; i < l; i++) {
	        if (programs[i]) {
	          ret[i] = programs[i];
	          if (decorators[i]) {
	            ret[i + '_d'] = decorators[i];
	            ret.useDecorators = true;
	          }
	        }
	      }

	      if (this.environment.usePartial) {
	        ret.usePartial = true;
	      }
	      if (this.options.data) {
	        ret.useData = true;
	      }
	      if (this.useDepths) {
	        ret.useDepths = true;
	      }
	      if (this.useBlockParams) {
	        ret.useBlockParams = true;
	      }
	      if (this.options.compat) {
	        ret.compat = true;
	      }

	      if (!asObject) {
	        ret.compiler = JSON.stringify(ret.compiler);

	        this.source.currentLocation = { start: { line: 1, column: 0 } };
	        ret = this.objectLiteral(ret);

	        if (options.srcName) {
	          ret = ret.toStringWithSourceMap({ file: options.destName });
	          ret.map = ret.map && ret.map.toString();
	        } else {
	          ret = ret.toString();
	        }
	      } else {
	        ret.compilerOptions = this.options;
	      }

	      return ret;
	    } else {
	      return fn;
	    }
	  },

	  preamble: function preamble() {
	    // track the last context pushed into place to allow skipping the
	    // getContext opcode when it would be a noop
	    this.lastContext = 0;
	    this.source = new _codeGen2['default'](this.options.srcName);
	    this.decorators = new _codeGen2['default'](this.options.srcName);
	  },

	  createFunctionContext: function createFunctionContext(asObject) {
	    // istanbul ignore next

	    var _this = this;

	    var varDeclarations = '';

	    var locals = this.stackVars.concat(this.registers.list);
	    if (locals.length > 0) {
	      varDeclarations += ', ' + locals.join(', ');
	    }

	    // Generate minimizer alias mappings
	    //
	    // When using true SourceNodes, this will update all references to the given alias
	    // as the source nodes are reused in situ. For the non-source node compilation mode,
	    // aliases will not be used, but this case is already being run on the client and
	    // we aren't concern about minimizing the template size.
	    var aliasCount = 0;
	    _Object$keys(this.aliases).forEach(function (alias) {
	      var node = _this.aliases[alias];
	      if (node.children && node.referenceCount > 1) {
	        varDeclarations += ', alias' + ++aliasCount + '=' + alias;
	        node.children[0] = 'alias' + aliasCount;
	      }
	    });

	    if (this.lookupPropertyFunctionIsUsed) {
	      varDeclarations += ', ' + this.lookupPropertyFunctionVarDeclaration();
	    }

	    var params = ['container', 'depth0', 'helpers', 'partials', 'data'];

	    if (this.useBlockParams || this.useDepths) {
	      params.push('blockParams');
	    }
	    if (this.useDepths) {
	      params.push('depths');
	    }

	    // Perform a second pass over the output to merge content when possible
	    var source = this.mergeSource(varDeclarations);

	    if (asObject) {
	      params.push(source);

	      return Function.apply(this, params);
	    } else {
	      return this.source.wrap(['function(', params.join(','), ') {\n  ', source, '}']);
	    }
	  },
	  mergeSource: function mergeSource(varDeclarations) {
	    var isSimple = this.environment.isSimple,
	        appendOnly = !this.forceBuffer,
	        appendFirst = undefined,
	        sourceSeen = undefined,
	        bufferStart = undefined,
	        bufferEnd = undefined;
	    this.source.each(function (line) {
	      if (line.appendToBuffer) {
	        if (bufferStart) {
	          line.prepend('  + ');
	        } else {
	          bufferStart = line;
	        }
	        bufferEnd = line;
	      } else {
	        if (bufferStart) {
	          if (!sourceSeen) {
	            appendFirst = true;
	          } else {
	            bufferStart.prepend('buffer += ');
	          }
	          bufferEnd.add(';');
	          bufferStart = bufferEnd = undefined;
	        }

	        sourceSeen = true;
	        if (!isSimple) {
	          appendOnly = false;
	        }
	      }
	    });

	    if (appendOnly) {
	      if (bufferStart) {
	        bufferStart.prepend('return ');
	        bufferEnd.add(';');
	      } else if (!sourceSeen) {
	        this.source.push('return "";');
	      }
	    } else {
	      varDeclarations += ', buffer = ' + (appendFirst ? '' : this.initializeBuffer());

	      if (bufferStart) {
	        bufferStart.prepend('return buffer + ');
	        bufferEnd.add(';');
	      } else {
	        this.source.push('return buffer;');
	      }
	    }

	    if (varDeclarations) {
	      this.source.prepend('var ' + varDeclarations.substring(2) + (appendFirst ? '' : ';\n'));
	    }

	    return this.source.merge();
	  },

	  lookupPropertyFunctionVarDeclaration: function lookupPropertyFunctionVarDeclaration() {
	    return '\n      lookupProperty = container.lookupProperty || function(parent, propertyName) {\n        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {\n          return parent[propertyName];\n        }\n        return undefined\n    }\n    '.trim();
	  },

	  // [blockValue]
	  //
	  // On stack, before: hash, inverse, program, value
	  // On stack, after: return value of blockHelperMissing
	  //
	  // The purpose of this opcode is to take a block of the form
	  // `{{#this.foo}}...{{/this.foo}}`, resolve the value of `foo`, and
	  // replace it on the stack with the result of properly
	  // invoking blockHelperMissing.
	  blockValue: function blockValue(name) {
	    var blockHelperMissing = this.aliasable('container.hooks.blockHelperMissing'),
	        params = [this.contextName(0)];
	    this.setupHelperArgs(name, 0, params);

	    var blockName = this.popStack();
	    params.splice(1, 0, blockName);

	    this.push(this.source.functionCall(blockHelperMissing, 'call', params));
	  },

	  // [ambiguousBlockValue]
	  //
	  // On stack, before: hash, inverse, program, value
	  // Compiler value, before: lastHelper=value of last found helper, if any
	  // On stack, after, if no lastHelper: same as [blockValue]
	  // On stack, after, if lastHelper: value
	  ambiguousBlockValue: function ambiguousBlockValue() {
	    // We're being a bit cheeky and reusing the options value from the prior exec
	    var blockHelperMissing = this.aliasable('container.hooks.blockHelperMissing'),
	        params = [this.contextName(0)];
	    this.setupHelperArgs('', 0, params, true);

	    this.flushInline();

	    var current = this.topStack();
	    params.splice(1, 0, current);

	    this.pushSource(['if (!', this.lastHelper, ') { ', current, ' = ', this.source.functionCall(blockHelperMissing, 'call', params), '}']);
	  },

	  // [appendContent]
	  //
	  // On stack, before: ...
	  // On stack, after: ...
	  //
	  // Appends the string value of `content` to the current buffer
	  appendContent: function appendContent(content) {
	    if (this.pendingContent) {
	      content = this.pendingContent + content;
	    } else {
	      this.pendingLocation = this.source.currentLocation;
	    }

	    this.pendingContent = content;
	  },

	  // [append]
	  //
	  // On stack, before: value, ...
	  // On stack, after: ...
	  //
	  // Coerces `value` to a String and appends it to the current buffer.
	  //
	  // If `value` is truthy, or 0, it is coerced into a string and appended
	  // Otherwise, the empty string is appended
	  append: function append() {
	    if (this.isInline()) {
	      this.replaceStack(function (current) {
	        return [' != null ? ', current, ' : ""'];
	      });

	      this.pushSource(this.appendToBuffer(this.popStack()));
	    } else {
	      var local = this.popStack();
	      this.pushSource(['if (', local, ' != null) { ', this.appendToBuffer(local, undefined, true), ' }']);
	      if (this.environment.isSimple) {
	        this.pushSource(['else { ', this.appendToBuffer("''", undefined, true), ' }']);
	      }
	    }
	  },

	  // [appendEscaped]
	  //
	  // On stack, before: value, ...
	  // On stack, after: ...
	  //
	  // Escape `value` and append it to the buffer
	  appendEscaped: function appendEscaped() {
	    this.pushSource(this.appendToBuffer([this.aliasable('container.escapeExpression'), '(', this.popStack(), ')']));
	  },

	  // [getContext]
	  //
	  // On stack, before: ...
	  // On stack, after: ...
	  // Compiler value, after: lastContext=depth
	  //
	  // Set the value of the `lastContext` compiler value to the depth
	  getContext: function getContext(depth) {
	    this.lastContext = depth;
	  },

	  // [pushContext]
	  //
	  // On stack, before: ...
	  // On stack, after: currentContext, ...
	  //
	  // Pushes the value of the current context onto the stack.
	  pushContext: function pushContext() {
	    this.pushStackLiteral(this.contextName(this.lastContext));
	  },

	  // [lookupOnContext]
	  //
	  // On stack, before: ...
	  // On stack, after: currentContext[name], ...
	  //
	  // Looks up the value of `name` on the current context and pushes
	  // it onto the stack.
	  lookupOnContext: function lookupOnContext(parts, falsy, strict, scoped) {
	    var i = 0;

	    if (!scoped && this.options.compat && !this.lastContext) {
	      // The depthed query is expected to handle the undefined logic for the root level that
	      // is implemented below, so we evaluate that directly in compat mode
	      this.push(this.depthedLookup(parts[i++]));
	    } else {
	      this.pushContext();
	    }

	    this.resolvePath('context', parts, i, falsy, strict);
	  },

	  // [lookupBlockParam]
	  //
	  // On stack, before: ...
	  // On stack, after: blockParam[name], ...
	  //
	  // Looks up the value of `parts` on the given block param and pushes
	  // it onto the stack.
	  lookupBlockParam: function lookupBlockParam(blockParamId, parts) {
	    this.useBlockParams = true;

	    this.push(['blockParams[', blockParamId[0], '][', blockParamId[1], ']']);
	    this.resolvePath('context', parts, 1);
	  },

	  // [lookupData]
	  //
	  // On stack, before: ...
	  // On stack, after: data, ...
	  //
	  // Push the data lookup operator
	  lookupData: function lookupData(depth, parts, strict) {
	    if (!depth) {
	      this.pushStackLiteral('data');
	    } else {
	      this.pushStackLiteral('container.data(data, ' + depth + ')');
	    }

	    this.resolvePath('data', parts, 0, true, strict);
	  },

	  resolvePath: function resolvePath(type, parts, i, falsy, strict) {
	    // istanbul ignore next

	    var _this2 = this;

	    if (this.options.strict || this.options.assumeObjects) {
	      this.push(strictLookup(this.options.strict && strict, this, parts, type));
	      return;
	    }

	    var len = parts.length;
	    for (; i < len; i++) {
	      /* eslint-disable no-loop-func */
	      this.replaceStack(function (current) {
	        var lookup = _this2.nameLookup(current, parts[i], type);
	        // We want to ensure that zero and false are handled properly if the context (falsy flag)
	        // needs to have the special handling for these values.
	        if (!falsy) {
	          return [' != null ? ', lookup, ' : ', current];
	        } else {
	          // Otherwise we can use generic falsy handling
	          return [' && ', lookup];
	        }
	      });
	      /* eslint-enable no-loop-func */
	    }
	  },

	  // [resolvePossibleLambda]
	  //
	  // On stack, before: value, ...
	  // On stack, after: resolved value, ...
	  //
	  // If the `value` is a lambda, replace it on the stack by
	  // the return value of the lambda
	  resolvePossibleLambda: function resolvePossibleLambda() {
	    this.push([this.aliasable('container.lambda'), '(', this.popStack(), ', ', this.contextName(0), ')']);
	  },

	  // [pushStringParam]
	  //
	  // On stack, before: ...
	  // On stack, after: string, currentContext, ...
	  //
	  // This opcode is designed for use in string mode, which
	  // provides the string value of a parameter along with its
	  // depth rather than resolving it immediately.
	  pushStringParam: function pushStringParam(string, type) {
	    this.pushContext();
	    this.pushString(type);

	    // If it's a subexpression, the string result
	    // will be pushed after this opcode.
	    if (type !== 'SubExpression') {
	      if (typeof string === 'string') {
	        this.pushString(string);
	      } else {
	        this.pushStackLiteral(string);
	      }
	    }
	  },

	  emptyHash: function emptyHash(omitEmpty) {
	    if (this.trackIds) {
	      this.push('{}'); // hashIds
	    }
	    if (this.stringParams) {
	      this.push('{}'); // hashContexts
	      this.push('{}'); // hashTypes
	    }
	    this.pushStackLiteral(omitEmpty ? 'undefined' : '{}');
	  },
	  pushHash: function pushHash() {
	    if (this.hash) {
	      this.hashes.push(this.hash);
	    }
	    this.hash = { values: {}, types: [], contexts: [], ids: [] };
	  },
	  popHash: function popHash() {
	    var hash = this.hash;
	    this.hash = this.hashes.pop();

	    if (this.trackIds) {
	      this.push(this.objectLiteral(hash.ids));
	    }
	    if (this.stringParams) {
	      this.push(this.objectLiteral(hash.contexts));
	      this.push(this.objectLiteral(hash.types));
	    }

	    this.push(this.objectLiteral(hash.values));
	  },

	  // [pushString]
	  //
	  // On stack, before: ...
	  // On stack, after: quotedString(string), ...
	  //
	  // Push a quoted version of `string` onto the stack
	  pushString: function pushString(string) {
	    this.pushStackLiteral(this.quotedString(string));
	  },

	  // [pushLiteral]
	  //
	  // On stack, before: ...
	  // On stack, after: value, ...
	  //
	  // Pushes a value onto the stack. This operation prevents
	  // the compiler from creating a temporary variable to hold
	  // it.
	  pushLiteral: function pushLiteral(value) {
	    this.pushStackLiteral(value);
	  },

	  // [pushProgram]
	  //
	  // On stack, before: ...
	  // On stack, after: program(guid), ...
	  //
	  // Push a program expression onto the stack. This takes
	  // a compile-time guid and converts it into a runtime-accessible
	  // expression.
	  pushProgram: function pushProgram(guid) {
	    if (guid != null) {
	      this.pushStackLiteral(this.programExpression(guid));
	    } else {
	      this.pushStackLiteral(null);
	    }
	  },

	  // [registerDecorator]
	  //
	  // On stack, before: hash, program, params..., ...
	  // On stack, after: ...
	  //
	  // Pops off the decorator's parameters, invokes the decorator,
	  // and inserts the decorator into the decorators list.
	  registerDecorator: function registerDecorator(paramSize, name) {
	    var foundDecorator = this.nameLookup('decorators', name, 'decorator'),
	        options = this.setupHelperArgs(name, paramSize);

	    this.decorators.push(['fn = ', this.decorators.functionCall(foundDecorator, '', ['fn', 'props', 'container', options]), ' || fn;']);
	  },

	  // [invokeHelper]
	  //
	  // On stack, before: hash, inverse, program, params..., ...
	  // On stack, after: result of helper invocation
	  //
	  // Pops off the helper's parameters, invokes the helper,
	  // and pushes the helper's return value onto the stack.
	  //
	  // If the helper is not found, `helperMissing` is called.
	  invokeHelper: function invokeHelper(paramSize, name, isSimple) {
	    var nonHelper = this.popStack(),
	        helper = this.setupHelper(paramSize, name);

	    var possibleFunctionCalls = [];

	    if (isSimple) {
	      // direct call to helper
	      possibleFunctionCalls.push(helper.name);
	    }
	    // call a function from the input object
	    possibleFunctionCalls.push(nonHelper);
	    if (!this.options.strict) {
	      possibleFunctionCalls.push(this.aliasable('container.hooks.helperMissing'));
	    }

	    var functionLookupCode = ['(', this.itemsSeparatedBy(possibleFunctionCalls, '||'), ')'];
	    var functionCall = this.source.functionCall(functionLookupCode, 'call', helper.callParams);
	    this.push(functionCall);
	  },

	  itemsSeparatedBy: function itemsSeparatedBy(items, separator) {
	    var result = [];
	    result.push(items[0]);
	    for (var i = 1; i < items.length; i++) {
	      result.push(separator, items[i]);
	    }
	    return result;
	  },
	  // [invokeKnownHelper]
	  //
	  // On stack, before: hash, inverse, program, params..., ...
	  // On stack, after: result of helper invocation
	  //
	  // This operation is used when the helper is known to exist,
	  // so a `helperMissing` fallback is not required.
	  invokeKnownHelper: function invokeKnownHelper(paramSize, name) {
	    var helper = this.setupHelper(paramSize, name);
	    this.push(this.source.functionCall(helper.name, 'call', helper.callParams));
	  },

	  // [invokeAmbiguous]
	  //
	  // On stack, before: hash, inverse, program, params..., ...
	  // On stack, after: result of disambiguation
	  //
	  // This operation is used when an expression like `{{foo}}`
	  // is provided, but we don't know at compile-time whether it
	  // is a helper or a path.
	  //
	  // This operation emits more code than the other options,
	  // and can be avoided by passing the `knownHelpers` and
	  // `knownHelpersOnly` flags at compile-time.
	  invokeAmbiguous: function invokeAmbiguous(name, helperCall) {
	    this.useRegister('helper');

	    var nonHelper = this.popStack();

	    this.emptyHash();
	    var helper = this.setupHelper(0, name, helperCall);

	    var helperName = this.lastHelper = this.nameLookup('helpers', name, 'helper');

	    var lookup = ['(', '(helper = ', helperName, ' || ', nonHelper, ')'];
	    if (!this.options.strict) {
	      lookup[0] = '(helper = ';
	      lookup.push(' != null ? helper : ', this.aliasable('container.hooks.helperMissing'));
	    }

	    this.push(['(', lookup, helper.paramsInit ? ['),(', helper.paramsInit] : [], '),', '(typeof helper === ', this.aliasable('"function"'), ' ? ', this.source.functionCall('helper', 'call', helper.callParams), ' : helper))']);
	  },

	  // [invokePartial]
	  //
	  // On stack, before: context, ...
	  // On stack after: result of partial invocation
	  //
	  // This operation pops off a context, invokes a partial with that context,
	  // and pushes the result of the invocation back.
	  invokePartial: function invokePartial(isDynamic, name, indent) {
	    var params = [],
	        options = this.setupParams(name, 1, params);

	    if (isDynamic) {
	      name = this.popStack();
	      delete options.name;
	    }

	    if (indent) {
	      options.indent = JSON.stringify(indent);
	    }
	    options.helpers = 'helpers';
	    options.partials = 'partials';
	    options.decorators = 'container.decorators';

	    if (!isDynamic) {
	      params.unshift(this.nameLookup('partials', name, 'partial'));
	    } else {
	      params.unshift(name);
	    }

	    if (this.options.compat) {
	      options.depths = 'depths';
	    }
	    options = this.objectLiteral(options);
	    params.push(options);

	    this.push(this.source.functionCall('container.invokePartial', '', params));
	  },

	  // [assignToHash]
	  //
	  // On stack, before: value, ..., hash, ...
	  // On stack, after: ..., hash, ...
	  //
	  // Pops a value off the stack and assigns it to the current hash
	  assignToHash: function assignToHash(key) {
	    var value = this.popStack(),
	        context = undefined,
	        type = undefined,
	        id = undefined;

	    if (this.trackIds) {
	      id = this.popStack();
	    }
	    if (this.stringParams) {
	      type = this.popStack();
	      context = this.popStack();
	    }

	    var hash = this.hash;
	    if (context) {
	      hash.contexts[key] = context;
	    }
	    if (type) {
	      hash.types[key] = type;
	    }
	    if (id) {
	      hash.ids[key] = id;
	    }
	    hash.values[key] = value;
	  },

	  pushId: function pushId(type, name, child) {
	    if (type === 'BlockParam') {
	      this.pushStackLiteral('blockParams[' + name[0] + '].path[' + name[1] + ']' + (child ? ' + ' + JSON.stringify('.' + child) : ''));
	    } else if (type === 'PathExpression') {
	      this.pushString(name);
	    } else if (type === 'SubExpression') {
	      this.pushStackLiteral('true');
	    } else {
	      this.pushStackLiteral('null');
	    }
	  },

	  // HELPERS

	  compiler: JavaScriptCompiler,

	  compileChildren: function compileChildren(environment, options) {
	    var children = environment.children,
	        child = undefined,
	        compiler = undefined;

	    for (var i = 0, l = children.length; i < l; i++) {
	      child = children[i];
	      compiler = new this.compiler(); // eslint-disable-line new-cap

	      var existing = this.matchExistingProgram(child);

	      if (existing == null) {
	        this.context.programs.push(''); // Placeholder to prevent name conflicts for nested children
	        var index = this.context.programs.length;
	        child.index = index;
	        child.name = 'program' + index;
	        this.context.programs[index] = compiler.compile(child, options, this.context, !this.precompile);
	        this.context.decorators[index] = compiler.decorators;
	        this.context.environments[index] = child;

	        this.useDepths = this.useDepths || compiler.useDepths;
	        this.useBlockParams = this.useBlockParams || compiler.useBlockParams;
	        child.useDepths = this.useDepths;
	        child.useBlockParams = this.useBlockParams;
	      } else {
	        child.index = existing.index;
	        child.name = 'program' + existing.index;

	        this.useDepths = this.useDepths || existing.useDepths;
	        this.useBlockParams = this.useBlockParams || existing.useBlockParams;
	      }
	    }
	  },
	  matchExistingProgram: function matchExistingProgram(child) {
	    for (var i = 0, len = this.context.environments.length; i < len; i++) {
	      var environment = this.context.environments[i];
	      if (environment && environment.equals(child)) {
	        return environment;
	      }
	    }
	  },

	  programExpression: function programExpression(guid) {
	    var child = this.environment.children[guid],
	        programParams = [child.index, 'data', child.blockParams];

	    if (this.useBlockParams || this.useDepths) {
	      programParams.push('blockParams');
	    }
	    if (this.useDepths) {
	      programParams.push('depths');
	    }

	    return 'container.program(' + programParams.join(', ') + ')';
	  },

	  useRegister: function useRegister(name) {
	    if (!this.registers[name]) {
	      this.registers[name] = true;
	      this.registers.list.push(name);
	    }
	  },

	  push: function push(expr) {
	    if (!(expr instanceof Literal)) {
	      expr = this.source.wrap(expr);
	    }

	    this.inlineStack.push(expr);
	    return expr;
	  },

	  pushStackLiteral: function pushStackLiteral(item) {
	    this.push(new Literal(item));
	  },

	  pushSource: function pushSource(source) {
	    if (this.pendingContent) {
	      this.source.push(this.appendToBuffer(this.source.quotedString(this.pendingContent), this.pendingLocation));
	      this.pendingContent = undefined;
	    }

	    if (source) {
	      this.source.push(source);
	    }
	  },

	  replaceStack: function replaceStack(callback) {
	    var prefix = ['('],
	        stack = undefined,
	        createdStack = undefined,
	        usedLiteral = undefined;

	    /* istanbul ignore next */
	    if (!this.isInline()) {
	      throw new _exception2['default']('replaceStack on non-inline');
	    }

	    // We want to merge the inline statement into the replacement statement via ','
	    var top = this.popStack(true);

	    if (top instanceof Literal) {
	      // Literals do not need to be inlined
	      stack = [top.value];
	      prefix = ['(', stack];
	      usedLiteral = true;
	    } else {
	      // Get or create the current stack name for use by the inline
	      createdStack = true;
	      var _name = this.incrStack();

	      prefix = ['((', this.push(_name), ' = ', top, ')'];
	      stack = this.topStack();
	    }

	    var item = callback.call(this, stack);

	    if (!usedLiteral) {
	      this.popStack();
	    }
	    if (createdStack) {
	      this.stackSlot--;
	    }
	    this.push(prefix.concat(item, ')'));
	  },

	  incrStack: function incrStack() {
	    this.stackSlot++;
	    if (this.stackSlot > this.stackVars.length) {
	      this.stackVars.push('stack' + this.stackSlot);
	    }
	    return this.topStackName();
	  },
	  topStackName: function topStackName() {
	    return 'stack' + this.stackSlot;
	  },
	  flushInline: function flushInline() {
	    var inlineStack = this.inlineStack;
	    this.inlineStack = [];
	    for (var i = 0, len = inlineStack.length; i < len; i++) {
	      var entry = inlineStack[i];
	      /* istanbul ignore if */
	      if (entry instanceof Literal) {
	        this.compileStack.push(entry);
	      } else {
	        var stack = this.incrStack();
	        this.pushSource([stack, ' = ', entry, ';']);
	        this.compileStack.push(stack);
	      }
	    }
	  },
	  isInline: function isInline() {
	    return this.inlineStack.length;
	  },

	  popStack: function popStack(wrapped) {
	    var inline = this.isInline(),
	        item = (inline ? this.inlineStack : this.compileStack).pop();

	    if (!wrapped && item instanceof Literal) {
	      return item.value;
	    } else {
	      if (!inline) {
	        /* istanbul ignore next */
	        if (!this.stackSlot) {
	          throw new _exception2['default']('Invalid stack pop');
	        }
	        this.stackSlot--;
	      }
	      return item;
	    }
	  },

	  topStack: function topStack() {
	    var stack = this.isInline() ? this.inlineStack : this.compileStack,
	        item = stack[stack.length - 1];

	    /* istanbul ignore if */
	    if (item instanceof Literal) {
	      return item.value;
	    } else {
	      return item;
	    }
	  },

	  contextName: function contextName(context) {
	    if (this.useDepths && context) {
	      return 'depths[' + context + ']';
	    } else {
	      return 'depth' + context;
	    }
	  },

	  quotedString: function quotedString(str) {
	    return this.source.quotedString(str);
	  },

	  objectLiteral: function objectLiteral(obj) {
	    return this.source.objectLiteral(obj);
	  },

	  aliasable: function aliasable(name) {
	    var ret = this.aliases[name];
	    if (ret) {
	      ret.referenceCount++;
	      return ret;
	    }

	    ret = this.aliases[name] = this.source.wrap(name);
	    ret.aliasable = true;
	    ret.referenceCount = 1;

	    return ret;
	  },

	  setupHelper: function setupHelper(paramSize, name, blockHelper) {
	    var params = [],
	        paramsInit = this.setupHelperArgs(name, paramSize, params, blockHelper);
	    var foundHelper = this.nameLookup('helpers', name, 'helper'),
	        callContext = this.aliasable(this.contextName(0) + ' != null ? ' + this.contextName(0) + ' : (container.nullContext || {})');

	    return {
	      params: params,
	      paramsInit: paramsInit,
	      name: foundHelper,
	      callParams: [callContext].concat(params)
	    };
	  },

	  setupParams: function setupParams(helper, paramSize, params) {
	    var options = {},
	        contexts = [],
	        types = [],
	        ids = [],
	        objectArgs = !params,
	        param = undefined;

	    if (objectArgs) {
	      params = [];
	    }

	    options.name = this.quotedString(helper);
	    options.hash = this.popStack();

	    if (this.trackIds) {
	      options.hashIds = this.popStack();
	    }
	    if (this.stringParams) {
	      options.hashTypes = this.popStack();
	      options.hashContexts = this.popStack();
	    }

	    var inverse = this.popStack(),
	        program = this.popStack();

	    // Avoid setting fn and inverse if neither are set. This allows
	    // helpers to do a check for `if (options.fn)`
	    if (program || inverse) {
	      options.fn = program || 'container.noop';
	      options.inverse = inverse || 'container.noop';
	    }

	    // The parameters go on to the stack in order (making sure that they are evaluated in order)
	    // so we need to pop them off the stack in reverse order
	    var i = paramSize;
	    while (i--) {
	      param = this.popStack();
	      params[i] = param;

	      if (this.trackIds) {
	        ids[i] = this.popStack();
	      }
	      if (this.stringParams) {
	        types[i] = this.popStack();
	        contexts[i] = this.popStack();
	      }
	    }

	    if (objectArgs) {
	      options.args = this.source.generateArray(params);
	    }

	    if (this.trackIds) {
	      options.ids = this.source.generateArray(ids);
	    }
	    if (this.stringParams) {
	      options.types = this.source.generateArray(types);
	      options.contexts = this.source.generateArray(contexts);
	    }

	    if (this.options.data) {
	      options.data = 'data';
	    }
	    if (this.useBlockParams) {
	      options.blockParams = 'blockParams';
	    }
	    return options;
	  },

	  setupHelperArgs: function setupHelperArgs(helper, paramSize, params, useRegister) {
	    var options = this.setupParams(helper, paramSize, params);
	    options.loc = JSON.stringify(this.source.currentLocation);
	    options = this.objectLiteral(options);
	    if (useRegister) {
	      this.useRegister('options');
	      params.push('options');
	      return ['options=', options];
	    } else if (params) {
	      params.push(options);
	      return '';
	    } else {
	      return options;
	    }
	  }
	};

	(function () {
	  var reservedWords = ('break else new var' + ' case finally return void' + ' catch for switch while' + ' continue function this with' + ' default if throw' + ' delete in try' + ' do instanceof typeof' + ' abstract enum int short' + ' boolean export interface static' + ' byte extends long super' + ' char final native synchronized' + ' class float package throws' + ' const goto private transient' + ' debugger implements protected volatile' + ' double import public let yield await' + ' null true false').split(' ');

	  var compilerWords = JavaScriptCompiler.RESERVED_WORDS = {};

	  for (var i = 0, l = reservedWords.length; i < l; i++) {
	    compilerWords[reservedWords[i]] = true;
	  }
	})();

	/**
	 * @deprecated May be removed in the next major version
	 */
	JavaScriptCompiler.isValidJavaScriptVariableName = function (name) {
	  return !JavaScriptCompiler.RESERVED_WORDS[name] && /^[a-zA-Z_$][0-9a-zA-Z_$]*$/.test(name);
	};

	function strictLookup(requireTerminal, compiler, parts, type) {
	  var stack = compiler.popStack(),
	      i = 0,
	      len = parts.length;
	  if (requireTerminal) {
	    len--;
	  }

	  for (; i < len; i++) {
	    stack = compiler.nameLookup(stack, parts[i], type);
	  }

	  if (requireTerminal) {
	    return [compiler.aliasable('container.strict'), '(', stack, ', ', compiler.quotedString(parts[i]), ', ', JSON.stringify(compiler.source.currentLocation), ' )'];
	  } else {
	    return stack;
	  }
	}

	exports['default'] = JavaScriptCompiler;
	module.exports = exports['default'];

/***/ }),
/* 53 */
/***/ (function(module, exports, __webpack_require__) {

	/* global define */
	'use strict';

	var _Object$keys = __webpack_require__(13)['default'];

	exports.__esModule = true;

	var _utils = __webpack_require__(5);

	var SourceNode = undefined;

	try {
	  /* istanbul ignore next */
	  if (false) {
	    // We don't support this in AMD environments. For these environments, we asusme that
	    // they are running on the browser and thus have no need for the source-map library.
	    var SourceMap = require('source-map');
	    SourceNode = SourceMap.SourceNode;
	  }
	} catch (err) {}
	/* NOP */

	/* istanbul ignore if: tested but not covered in istanbul due to dist build  */
	if (!SourceNode) {
	  SourceNode = function (line, column, srcFile, chunks) {
	    this.src = '';
	    if (chunks) {
	      this.add(chunks);
	    }
	  };
	  /* istanbul ignore next */
	  SourceNode.prototype = {
	    add: function add(chunks) {
	      if (_utils.isArray(chunks)) {
	        chunks = chunks.join('');
	      }
	      this.src += chunks;
	    },
	    prepend: function prepend(chunks) {
	      if (_utils.isArray(chunks)) {
	        chunks = chunks.join('');
	      }
	      this.src = chunks + this.src;
	    },
	    toStringWithSourceMap: function toStringWithSourceMap() {
	      return { code: this.toString() };
	    },
	    toString: function toString() {
	      return this.src;
	    }
	  };
	}

	function castChunk(chunk, codeGen, loc) {
	  if (_utils.isArray(chunk)) {
	    var ret = [];

	    for (var i = 0, len = chunk.length; i < len; i++) {
	      ret.push(codeGen.wrap(chunk[i], loc));
	    }
	    return ret;
	  } else if (typeof chunk === 'boolean' || typeof chunk === 'number') {
	    // Handle primitives that the SourceNode will throw up on
	    return chunk + '';
	  }
	  return chunk;
	}

	function CodeGen(srcFile) {
	  this.srcFile = srcFile;
	  this.source = [];
	}

	CodeGen.prototype = {
	  isEmpty: function isEmpty() {
	    return !this.source.length;
	  },
	  prepend: function prepend(source, loc) {
	    this.source.unshift(this.wrap(source, loc));
	  },
	  push: function push(source, loc) {
	    this.source.push(this.wrap(source, loc));
	  },

	  merge: function merge() {
	    var source = this.empty();
	    this.each(function (line) {
	      source.add(['  ', line, '\n']);
	    });
	    return source;
	  },

	  each: function each(iter) {
	    for (var i = 0, len = this.source.length; i < len; i++) {
	      iter(this.source[i]);
	    }
	  },

	  empty: function empty() {
	    var loc = this.currentLocation || { start: {} };
	    return new SourceNode(loc.start.line, loc.start.column, this.srcFile);
	  },
	  wrap: function wrap(chunk) {
	    var loc = arguments.length <= 1 || arguments[1] === undefined ? this.currentLocation || { start: {} } : arguments[1];

	    if (chunk instanceof SourceNode) {
	      return chunk;
	    }

	    chunk = castChunk(chunk, this, loc);

	    return new SourceNode(loc.start.line, loc.start.column, this.srcFile, chunk);
	  },

	  functionCall: function functionCall(fn, type, params) {
	    params = this.generateList(params);
	    return this.wrap([fn, type ? '.' + type + '(' : '(', params, ')']);
	  },

	  quotedString: function quotedString(str) {
	    return '"' + (str + '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\u2028/g, '\\u2028') // Per Ecma-262 7.3 + 7.8.4
	    .replace(/\u2029/g, '\\u2029') + '"';
	  },

	  objectLiteral: function objectLiteral(obj) {
	    // istanbul ignore next

	    var _this = this;

	    var pairs = [];

	    _Object$keys(obj).forEach(function (key) {
	      var value = castChunk(obj[key], _this);
	      if (value !== 'undefined') {
	        pairs.push([_this.quotedString(key), ':', value]);
	      }
	    });

	    var ret = this.generateList(pairs);
	    ret.prepend('{');
	    ret.add('}');
	    return ret;
	  },

	  generateList: function generateList(entries) {
	    var ret = this.empty();

	    for (var i = 0, len = entries.length; i < len; i++) {
	      if (i) {
	        ret.add(',');
	      }

	      ret.add(castChunk(entries[i], this));
	    }

	    return ret;
	  },

	  generateArray: function generateArray(entries) {
	    var ret = this.generateList(entries);
	    ret.prepend('[');
	    ret.add(']');

	    return ret;
	  }
	};

	exports['default'] = CodeGen;
	module.exports = exports['default'];

/***/ })
/******/ ])
});
;var Nut = _.extend(
    Nut || {},
    (function($) {
        return {
            modules: {},
            opt: {
                blinkSuccessDelay: 2000,
                blinkSuccessFade: 900,
                blinkErrorDelay: 600,
                blinkErrorFade: 1400,
                loadingHud: {fadeIn: 150, fadeOut: 300},
                flashWait: 10000,
                flashFade: 700,
                flashSlide: 400,
            },
            errorCount: 0,
            cachedTemplates: {},

            // eventually i'd like to move all events here, instead of in modules. otherwise we have loading order dependencies
            events: {
                global: {
                    entityWasCreated: 'entityWasCreated',
                    entityWasEdited: 'entityWasEdited',
                    entityWasDeleted: 'entityWasDeleted',
                    pageWasChanged: 'pageWasChanged',
                    reactWasMountedAndInitialized: 'reactWasMountedAndInitialized',
                },
                capsule: {
                    willBeAdded: 'CapsuleWillBeAdded',
                    wasAdded: 'CapsuleWasAdded',
                    willBeDeleted: 'CapsuleWillBeDeleted', // can be preventDefault()'d
                    wasDeleted: 'CapsuleWasDeleted',
                },
                tag: {
                    wasChanged: 'tagWasChanged',
                },
                region: {
                    wasChanged: 'regionWasChanged',
                },
                card: {
                    willBeAdded: 'cardWillBeAdded',
                    wasAdded: 'cardWasAdded',
                    wasDeleted: 'cardWasDeleted',
                    transientWasAdded: 'cardTransientWasAdded', // cards that don't represent a real entity yet
                    transientWasDeleted: 'cardTransientWasDeleted',
                },
                table: {
                    pageWillBeChanged: 'tablePageWillBeChanged',
                    pageWasChanged: 'tablePageWasChanged',
                    orderByWasChanged: 'tableOrderByWasChanged',
                    headerWasReordered: 'tableHeaderWasReordered',
                    wasChanged: 'tableWasChanged',
                    shouldScroll: 'tableShouldScroll',
                },
                list: {
                    rowWasReordered: 'listRowWasReordered',
                    rowReceived: 'listReceived',
                },
                popover: {
                    wasOpened: 'popoverWasOpened',
                    wasClosed: 'popoverWasClosed',
                },
                limits: {
                    limitWasExceeded: 'limitWasExceeded',
                },
            },
            colors: {
                graphDarkBlue: '#3b9eD7',
                graphBlue: '#61B2DE',
                graphPlum: '#3d3b8a',
                graphOrange: '#ff7833',
                graphGreen: '#5ccf73',
                graphGrey: '#ebe8e8',
                graphLabelGrey: '#4d4540',
            },

            go: function() {
                Nut.time('nutGo');

                for (var fn in this.init) {
                    this.init[fn].apply(this);
                }

                Nut.timeEnd('nutGo');
            },

            updateAppearances: function() {
                Nut.time('updateAppearances');

                for (var mod in this.modules) {
                    if (this.modules[mod].init.updateAppearances)
                        this.modules[mod].init.updateAppearances.call(this);
                }

                Nut.timeEnd('updateAppearances');
            },

            preparePage: function() {
                Nut.time('preparePages');

                for (var mod in this.modules) {
                    if (this.modules[mod].init.preparePage) this.modules[mod].init.preparePage();
                }

                Nut.timeEnd('preparePages');
            },

            init: {
                // Kicks all the nasty old browsers to the old-browser page
                checkBrowserVersion: function() {
                    if (!location.href.match(/old-browser/)) {
                        var version = $.browser.version
                            .replace(/\./, '#')
                            .replace(/[^0-9#]/g, '')
                            .replace(/\#/, '.');
                        switch (true) {
                            case $.browser.msie && version < 11:
                            case $.browser.mozilla && version < 1.9: // firefox 3.6
                            case $.browser.opera && version < 11:
                            case $.browser.webkit && version < 14:
                                location.href = '/auth/old-browser';
                                break;
                        }
                    }
                },

                // we do this first for quickliness
                addBrowserClasses: function() {
                    // add more general browser classes for targeting all version of any one browser
                    $('html').addClass(
                        'browser-' +
                            (Nut.isUsingIe()
                                ? 'ie'
                                : $.browser.mozilla
                                ? 'ff'
                                : $.browser.webkit
                                ? 'wk'
                                : $.browser.opera
                                ? 'op'
                                : 'unknown')
                    );
                },
                setUpJqueryOverloads: function() {
                    $.fn.blinkSuccess = Nut.blinkSuccess;
                    $.fn.blinkError = Nut.blinkError;
                    $.fn.serializeArrayToObject = Nut.serializeArrayToObject;
                    $.fn.getEipData = Nut.getEipData;
                },
                setUpModules: function() {
                    for (var fn in Nut.modules) {
                        if (Nut.isLoggedIn() || Nut.modules[fn].runWhenLoggedOut) {
                            Nut.time('init' + fn);
                            Nut.modules[fn].go();
                            Nut.timeEnd('init' + fn);
                        }
                    }
                },
            },

            // added to jquery in setup
            blinkSuccess: function(skipFade) {
                var els = $(this).is('tr')
                    ? $(this)
                          .find('td')
                          .andSelf()
                    : $(this);

                var fadeSpeed = typeof skipFade !== 'undefined' ? 1 : Nut.opt.blinkSuccessFade;

                if (!els.hasClass('blink-success')) {
                    els.addClass('blink-success')
                        .delay(Nut.opt.blinkSuccessDelay)
                        .removeClass('blink-success', fadeSpeed);
                }
            },
            blinkError: function() {
                var els = $(this).is('tr')
                    ? $(this)
                          .find('td')
                          .andSelf()
                    : $(this);

                els.addClass('blink-error')
                    .delay(Nut.opt.blinkErrorDelay)
                    .removeClass('blink-error', Nut.opt.blinkErrorFade);
            },
            serializeArrayToObject: function() {
                return _(
                    $(this)
                        .find(':input:not(.eip-input)')
                        .serializeArray()
                ).reduce(function(s, i) {
                    if (i.name.slice(-2) === '[]') {
                        i.name = i.name.replace('[]', '');
                        if (!(i.name in s)) {
                            s[i.name] = [];
                        }
                        s[i.name].push(i.value);
                    } else {
                        s[i.name] = i.value;
                    }

                    return s;
                }, {});
            },
            getEipData: function() {
                var data = {};

                $(this)
                    .find('.eip-container')
                    .each(function() {
                        var eip = $(this).data('eip'),
                            eipData = eip.getDataToSave();
                        delete eip.id;
                        $.extend(data, eipData);
                    });

                return data;
            },

            getControllerForEntityName: function(entityName) {
                switch (entityName) {
                    case 'Accounts':
                        return 'account';
                    case 'Contacts':
                        return 'contact';
                    case 'Leads':
                        return 'lead';
                    case 'Activities':
                        return 'activity';
                    default:
                        Nut.error('No controller for ' + entityName);
                        return '';
                }
            },

            getConfig: function(setting) {
                if (typeof NutClientConfig === 'undefined') return false;

                return NutClientConfig[setting];
            },

            loadingHudStart: function() {
                $('#loading-hud').fadeIn(Nut.opt.loadingHud.fadeIn);
                Nut.animateLoadingSprite($('#loading-hud .loading-sprite'));
            },

            loadingHudStop: function() {
                $('#loading-hud').fadeOut(Nut.opt.loadingHud.fadeOut);
            },

            // should only be called with a $('.loading-sprite').
            // tries to stop spinning when hidden, but only checks parent visibility / opacity
            animateLoadingSprite: function(element) {
                if (element.hasClass('state-animated')) return;
                element.addClass('state-animated');

                var animateInterval = window.setInterval(function() {
                    // check if any element (or any parent) is hidden or if any parent is opacity: 0
                    if (
                        element.is(':hidden') ||
                        element.parents().filter(function() {
                            return $(this).css('opacity') === '0';
                        }).length
                    ) {
                        element.removeClass('state-animated');
                        window.clearInterval(animateInterval);
                        return;
                    }

                    var position = element.css('background-position');

                    // IE7 & 8
                    if (typeof position == 'undefined') {
                        position =
                            element.css('background-position-x') +
                            ' ' +
                            element.css('background-position-y');
                    }

                    var positionX = Math.abs($.nounit(position.split(' ')[0])),
                        positionY = position.split(' ')[1],
                        frameWidth = 66,
                        numFrames = 8,
                        currentFrame = Math.floor(positionX / frameWidth),
                        nextFrame,
                        nextFrameOffset;

                    nextFrame = currentFrame == numFrames - 1 ? 0 : currentFrame + 1;
                    nextFrameOffset = nextFrame * frameWidth * -1;

                    // oddly, this works as a setter for ie
                    element.css('background-position', nextFrameOffset + 'px ' + positionY);
                }, 100);
            },

            log: function(ie_arg) {
                if (typeof console !== 'undefined' && typeof console.log !== 'undefined') {
                    if (typeof console.log.apply !== 'undefined') {
                        console.log.apply(console, arguments);
                    } else {
                        console.log(ie_arg);
                    }
                }
            },
            error: function(ie_arg) {
                if (typeof console !== 'undefined' && typeof console.log !== 'undefined') {
                    if (typeof console.log.apply !== 'undefined') {
                        console.error.apply(console, arguments);
                    } else {
                        console.error(ie_arg);
                    }
                }

                Nut.errorCount++;
                if (Nut.errorCount > maxJsErrors) return;

                // Use global trackJs if available
                if (typeof window.trackJs !== 'undefined') {
                    trackJs.track(Array.prototype.join.call(arguments, ' '));
                }
                // don't use Nut.ajax.post because we don't care about the response, even if it's an error
                $.post('/error/javascript', {
                    errorString: Array.prototype.join.call(arguments, ' '),
                    location: window && window.location && window.location.pathname,
                });
            },
            time: function(name) {
                //if ( typeof console !== 'undefined' && !_.isUndefined(console.time) )
                //console.time(name);
            },
            timeEnd: function(name) {
                //if ( typeof console !== 'undefined' && !_.isUndefined(console.timeEnd) )
                //console.timeEnd(name);
            },
            getTemplate: function(templateId, variableName) {
                if (_.isUndefined(Nut.cachedTemplates[templateId])) {
                    Nut.cachedTemplates[templateId] = _.template($('#' + templateId).html(), null, {
                        variable: variableName,
                    });
                }
                return Nut.cachedTemplates[templateId];
            },

            // functions further out which to be fleshed
            ajax: {
                activeRequests: {},

                main: function(options) {
                    // we use our own success callback which calls the specified success function after add'l checking
                    var mySuccess = options.success,
                        myError = options.error;

                    delete options.success;
                    delete options.error;

                    options = $.extend(
                        {
                            // defaults
                            data: {},
                            success: function(result) {
                                if (result && result.isRedirectResult) {
                                    window.location = result.redirectUrl;
                                    return;
                                }

                                if (result && result.isReloadResult) {
                                    window.location.reload();
                                    return;
                                }

                                if (result && result.events) {
                                    _(result.events).each(function(data) {
                                        $('body').trigger(data[0], data[1]);
                                    });
                                }

                                if (result && result.isValidationResult) {
                                    if (options.validate) {
                                        options.validate(result);
                                        return; // don't call success
                                    } else {
                                        // global validation handling
                                        if (!result.isValid) {
                                            if (options.error) {
                                                options.error(result);
                                            } else {
                                                Nut.alert('Received validation exception');
                                            }
                                            return;
                                        }
                                    }
                                }

                                if (options.exclusive) {
                                    Nut.ajax.activeRequests[options.exclusive.tag] = null;
                                }

                                if (typeof mySuccess == 'function') mySuccess(result);
                            },
                            error: function(xhr, errorText, errorThrown) {
                                var response;
                                if (xhr.isValidationResult) {
                                    response = xhr;
                                } else {
                                    try {
                                        response = $.parseJSON(xhr.responseText);
                                    } catch (e) {
                                        response = null;
                                    }
                                }

                                // it seems that in jquery 1.7, this reflects an xhr request that was cancelled due to navigating away. do nothing.
                                if (xhr.readyState === 0) {
                                    return;
                                }

                                // first check for global error codes

                                // #3295 IE9 issue - sometimes when calling abort() it triggers
                                // the error handler.  Catch it and call the success with
                                // no arguments.
                                if (xhr.isNutshellAbort) {
                                    if (typeof mySuccess == 'function') mySuccess();
                                    return;
                                }

                                // @TODO potentially be nice to save state here :)
                                if (response && response.isAuthRequiredResult) {
                                    if (typeof window.trackJs !== 'undefined') {
                                        trackJs.track('isAuthRequiredResult redirecting');
                                    }
                                    window.location.pathname = '/auth/';
                                    return;
                                }

                                if (response && response.isSessionTimeoutResult) {
                                    window.location.href = '/auth?sessionTimeout=1';
                                    return;
                                }

                                // run provided error callback, or use default
                                if (typeof myError == 'function') {
                                    myError(response);
                                    return;
                                }

                                if (options.exclusive) {
                                    Nut.ajax.activeRequests[options.exclusive.tag] = null;
                                }

                                var message =
                                    response && response.message ? response.message : false;
                                if (message) {
                                    // Use global trackJs if available
                                    if (typeof window.trackJs !== 'undefined') {
                                        trackJs.track('Nut.ajax Error:' + message);
                                    }
                                    Nut.alert('Error: ' + message);
                                } else {
                                    // Use global trackJs if available
                                    if (typeof window.trackJs !== 'undefined') {
                                        trackJs.track(
                                            'Nut.ajax generic error with request options:' +
                                                JSON.stringify(options)
                                        );
                                    }
                                    Nut.alert(
                                        'Sorry, an unknown error occured while communicating with the server.'
                                    );
                                }
                            },
                        },
                        options
                    );

                    return $.ajax(options);
                },
                get: function(options) {
                    options = $.extend({type: 'GET'}, options);

                    // For mutually exclusive GET requests, include this option:
                    // exclusive: { tag='unique id', order='FIFO or LIFO' }
                    // note that this will still call the success object, but w/ an empty response.
                    // see jqxhr object docs
                    if (options.exclusive && Nut.ajax.activeRequests[options.exclusive.tag]) {
                        if (options.exclusive.order == 'FIFO') {
                            // FIFO: ignore this new request
                            return null;
                        } else {
                            // LIFO
                            // LIFO: abort currently executing request
                            if (Nut.ajax.activeRequests[options.exclusive.tag].abort) {
                                // #3295 IE9 issue - sometimes calls error handler
                                // this allows us to detect abort in error handler
                                Nut.ajax.activeRequests[options.exclusive.tag].isNutshellAbort = 1;
                                Nut.ajax.activeRequests[options.exclusive.tag].abort();
                            }
                        }
                    }

                    var xhr = Nut.ajax.main.call(Nut, options);

                    if (options.exclusive) {
                        Nut.ajax.activeRequests[options.exclusive.tag] = xhr;
                    }

                    return xhr;
                },
                post: function(options) {
                    options = $.extend({type: 'POST'}, options);
                    return Nut.ajax.main.call(Nut, options);
                },
            },
            alert: function(txt) {
                return alert(txt);
            },
            confirm: function(confirmMe) {
                var confirmText;

                if (typeof confirmMe === 'string') {
                    confirmText = confirmMe;
                } else {
                    confirmText = $(confirmMe)
                        .find('.js-confirm')
                        .text();
                }

                return confirm(confirmText);
            },
            flashSave: function(container, position) {
                var flash = "<div class='flash-mini'><span>Saved</span></div>";
                var style =
                    position === 'left' ? {top: 0, left: '-43px'} : {top: '20px', left: '-10px'};
                $(flash)
                    .appendTo(container)
                    .css(style)
                    .animate({opacity: 1}, 50)
                    .delay(500)
                    .animate({opacity: 0}, 50, function() {
                        this.remove();
                    });
            },
            flashSuccess: function(message) {
                Nut._flash(message, 'flash-message-standard');
            },
            flashError: function(message) {
                Nut._flash(message, 'flash-message-error');
            },
            _flash: function(message, classes) {
                var container = $('div.flash-messages:first'),
                    $message = $(document.createElement('div'))
                        .addClass('flash-message')
                        .addClass(classes)
                        .html(message);

                // we probably didn't already have any messages on this page, so we use the placeholder
                if (!container.length) {
                    container = $('div.flash-messages-placeholder:first')
                        .removeClass('.flash-messages-place')
                        .addClass('flash-messages');
                }

                container.append($message);

                // fade out all flashes after interval
                window.setTimeout(Nut.removeFlashes, Nut.opt.flashWait);
            },
            removeFlashes: function() {
                $('div.flash-messages .flash-message')
                    .fadeTo(Nut.opt.flashFade, 0.01)
                    .slideUp(Nut.opt.flashSlide, function() {
                        $(this).remove();
                    });
            },
            // shit that jqui should be doing to make the helper's columns match the original
            sortableWidthFixHelper: function(e, tr) {
                var $originals = tr.children(),
                    $helper = tr.clone();
                $helper.children().each(function(index) {
                    $(this).width($originals.eq(index).width());
                });
                return $helper;
            },
            isLoggedIn: function() {
                if (document.readyState === 'loading') {
                    Nut.error('Cannot check logged in before document ready');
                    return false;
                }
                return Boolean(
                    document.querySelector('body') &&
                        document.querySelector('body').hasAttribute('data-logged-in')
                );
            },
            getCsrfTokenFromDom: function() {
                return document.querySelector('body').getAttribute('data-csrf-token');
            },
            watchForNotifyUpdates: function() {
                if (!$('.has-queued-notifies').length) return;

                var currentState = $('.notify-messages').html();
                var pollNotifications = window.setTimeout(function() {
                    Nut.ajax.get({
                        url: '/notify.json',
                        success: function(result) {
                            if ($(result.html).html() != currentState) {
                                $('.notify-messages').replaceWith(result.html);
                            }
                            Nut.watchForNotifyUpdates();
                        },
                    });
                }, 5000);
            },
            isUsingIe: function() {
                var sAgent = window.navigator.userAgent;
                var Idx = sAgent.indexOf('MSIE');

                if (Idx > 0) {
                    return true;
                    // If IE 11 then look for Updated user agent string.
                } else if (!!navigator.userAgent.match(/Trident\/7\./)) {
                    return true;
                }
                return false;
            },
            copyToClipboard: function(str) {
                const dummyEl = document.createElement('input');
                document.body.appendChild(dummyEl);
                dummyEl.setAttribute('value', str);
                dummyEl.select();
                document.execCommand('copy');
                document.body.removeChild(dummyEl);
            },
        };
    })(jQuery)
);
//     Backbone.js 1.0.0

//     (c) 2010-2013 Jeremy Ashkenas, DocumentCloud Inc.
//     Backbone may be freely distributed under the MIT license.
//     For all details and documentation:
//     http://backbonejs.org

(function(){

  // Initial Setup
  // -------------

  // Save a reference to the global object (`window` in the browser, `exports`
  // on the server).
  var root = this;

  // Save the previous value of the `Backbone` variable, so that it can be
  // restored later on, if `noConflict` is used.
  var previousBackbone = root.Backbone;

  // Create local references to array methods we'll want to use later.
  var array = [];
  var push = array.push;
  var slice = array.slice;
  var splice = array.splice;

  // The top-level namespace. All public Backbone classes and modules will
  // be attached to this. Exported for both the browser and the server.
  var Backbone;
  if (typeof exports !== 'undefined') {
    Backbone = exports;
  } else {
    Backbone = root.Backbone = {};
  }

  // Current version of the library. Keep in sync with `package.json`.
  Backbone.VERSION = '1.0.0';

  // Require Underscore, if we're on the server, and it's not already present.
  var _ = root._;
  if (!_ && (typeof require !== 'undefined')) _ = require('underscore');

  // For Backbone's purposes, jQuery, Zepto, Ender, or My Library (kidding) owns
  // the `$` variable.
  Backbone.$ = root.jQuery || root.Zepto || root.ender || root.$;

  // Runs Backbone.js in *noConflict* mode, returning the `Backbone` variable
  // to its previous owner. Returns a reference to this Backbone object.
  Backbone.noConflict = function() {
    root.Backbone = previousBackbone;
    return this;
  };

  // Turn on `emulateHTTP` to support legacy HTTP servers. Setting this option
  // will fake `"PUT"` and `"DELETE"` requests via the `_method` parameter and
  // set a `X-Http-Method-Override` header.
  Backbone.emulateHTTP = false;

  // Turn on `emulateJSON` to support legacy servers that can't deal with direct
  // `application/json` requests ... will encode the body as
  // `application/x-www-form-urlencoded` instead and will send the model in a
  // form param named `model`.
  Backbone.emulateJSON = false;

  // Backbone.Events
  // ---------------

  // A module that can be mixed in to *any object* in order to provide it with
  // custom events. You may bind with `on` or remove with `off` callback
  // functions to an event; `trigger`-ing an event fires all callbacks in
  // succession.
  //
  //     var object = {};
  //     _.extend(object, Backbone.Events);
  //     object.on('expand', function(){ alert('expanded'); });
  //     object.trigger('expand');
  //
  var Events = Backbone.Events = {

    // Bind an event to a `callback` function. Passing `"all"` will bind
    // the callback to all events fired.
    on: function(name, callback, context) {
      if (!eventsApi(this, 'on', name, [callback, context]) || !callback) return this;
      this._events || (this._events = {});
      var events = this._events[name] || (this._events[name] = []);
      events.push({callback: callback, context: context, ctx: context || this});
      return this;
    },

    // Bind an event to only be triggered a single time. After the first time
    // the callback is invoked, it will be removed.
    once: function(name, callback, context) {
      if (!eventsApi(this, 'once', name, [callback, context]) || !callback) return this;
      var self = this;
      var once = _.once(function() {
        self.off(name, once);
        callback.apply(this, arguments);
      });
      once._callback = callback;
      return this.on(name, once, context);
    },

    // Remove one or many callbacks. If `context` is null, removes all
    // callbacks with that function. If `callback` is null, removes all
    // callbacks for the event. If `name` is null, removes all bound
    // callbacks for all events.
    off: function(name, callback, context) {
      var retain, ev, events, names, i, l, j, k;
      if (!this._events || !eventsApi(this, 'off', name, [callback, context])) return this;
      if (!name && !callback && !context) {
        this._events = {};
        return this;
      }

      names = name ? [name] : _.keys(this._events);
      for (i = 0, l = names.length; i < l; i++) {
        name = names[i];
        if (events = this._events[name]) {
          this._events[name] = retain = [];
          if (callback || context) {
            for (j = 0, k = events.length; j < k; j++) {
              ev = events[j];
              if ((callback && callback !== ev.callback && callback !== ev.callback._callback) ||
                  (context && context !== ev.context)) {
                retain.push(ev);
              }
            }
          }
          if (!retain.length) delete this._events[name];
        }
      }

      return this;
    },

    // Trigger one or many events, firing all bound callbacks. Callbacks are
    // passed the same arguments as `trigger` is, apart from the event name
    // (unless you're listening on `"all"`, which will cause your callback to
    // receive the true name of the event as the first argument).
    trigger: function(name) {
      if (!this._events) return this;
      var args = slice.call(arguments, 1);
      if (!eventsApi(this, 'trigger', name, args)) return this;
      var events = this._events[name];
      var allEvents = this._events.all;
      if (events) triggerEvents(events, args);
      if (allEvents) triggerEvents(allEvents, arguments);
      return this;
    },

    // Tell this object to stop listening to either specific events ... or
    // to every object it's currently listening to.
    stopListening: function(obj, name, callback) {
      var listeners = this._listeners;
      if (!listeners) return this;
      var deleteListener = !name && !callback;
      if (typeof name === 'object') callback = this;
      if (obj) (listeners = {})[obj._listenerId] = obj;
      for (var id in listeners) {
        listeners[id].off(name, callback, this);
        if (deleteListener) delete this._listeners[id];
      }
      return this;
    }

  };

  // Regular expression used to split event strings.
  var eventSplitter = /\s+/;

  // Implement fancy features of the Events API such as multiple event
  // names `"change blur"` and jQuery-style event maps `{change: action}`
  // in terms of the existing API.
  var eventsApi = function(obj, action, name, rest) {
    if (!name) return true;

    // Handle event maps.
    if (typeof name === 'object') {
      for (var key in name) {
        obj[action].apply(obj, [key, name[key]].concat(rest));
      }
      return false;
    }

    // Handle space separated event names.
    if (eventSplitter.test(name)) {
      var names = name.split(eventSplitter);
      for (var i = 0, l = names.length; i < l; i++) {
        obj[action].apply(obj, [names[i]].concat(rest));
      }
      return false;
    }

    return true;
  };

  // A difficult-to-believe, but optimized internal dispatch function for
  // triggering events. Tries to keep the usual cases speedy (most internal
  // Backbone events have 3 arguments).
  var triggerEvents = function(events, args) {
    var ev, i = -1, l = events.length, a1 = args[0], a2 = args[1], a3 = args[2];
    switch (args.length) {
      case 0: while (++i < l) (ev = events[i]).callback.call(ev.ctx); return;
      case 1: while (++i < l) (ev = events[i]).callback.call(ev.ctx, a1); return;
      case 2: while (++i < l) (ev = events[i]).callback.call(ev.ctx, a1, a2); return;
      case 3: while (++i < l) (ev = events[i]).callback.call(ev.ctx, a1, a2, a3); return;
      default: while (++i < l) (ev = events[i]).callback.apply(ev.ctx, args);
    }
  };

  var listenMethods = {listenTo: 'on', listenToOnce: 'once'};

  // Inversion-of-control versions of `on` and `once`. Tell *this* object to
  // listen to an event in another object ... keeping track of what it's
  // listening to.
  _.each(listenMethods, function(implementation, method) {
    Events[method] = function(obj, name, callback) {
      var listeners = this._listeners || (this._listeners = {});
      var id = obj._listenerId || (obj._listenerId = _.uniqueId('l'));
      listeners[id] = obj;
      if (typeof name === 'object') callback = this;
      obj[implementation](name, callback, this);
      return this;
    };
  });

  // Aliases for backwards compatibility.
  Events.bind   = Events.on;
  Events.unbind = Events.off;

  // Allow the `Backbone` object to serve as a global event bus, for folks who
  // want global "pubsub" in a convenient place.
  _.extend(Backbone, Events);

  // Backbone.Model
  // --------------

  // Backbone **Models** are the basic data object in the framework --
  // frequently representing a row in a table in a database on your server.
  // A discrete chunk of data and a bunch of useful, related methods for
  // performing computations and transformations on that data.

  // Create a new model with the specified attributes. A client id (`cid`)
  // is automatically generated and assigned for you.
  var Model = Backbone.Model = function(attributes, options) {
    var defaults;
    var attrs = attributes || {};
    options || (options = {});
    this.cid = _.uniqueId('c');
    this.attributes = {};
    _.extend(this, _.pick(options, modelOptions));
    if (options.parse) attrs = this.parse(attrs, options) || {};
    if (defaults = _.result(this, 'defaults')) {
      attrs = _.defaults({}, attrs, defaults);
    }
    this.set(attrs, options);
    this.changed = {};
    this.initialize.apply(this, arguments);
  };

  // A list of options to be attached directly to the model, if provided.
  var modelOptions = ['url', 'urlRoot', 'collection'];

  // Attach all inheritable methods to the Model prototype.
  _.extend(Model.prototype, Events, {

    // A hash of attributes whose current and previous value differ.
    changed: null,

    // The value returned during the last failed validation.
    validationError: null,

    // The default name for the JSON `id` attribute is `"id"`. MongoDB and
    // CouchDB users may want to set this to `"_id"`.
    idAttribute: 'id',

    // Initialize is an empty function by default. Override it with your own
    // initialization logic.
    initialize: function(){},

    // Return a copy of the model's `attributes` object.
    toJSON: function(options) {
      return _.clone(this.attributes);
    },

    // Proxy `Backbone.sync` by default -- but override this if you need
    // custom syncing semantics for *this* particular model.
    sync: function() {
      return Backbone.sync.apply(this, arguments);
    },

    // Get the value of an attribute.
    get: function(attr) {
      return this.attributes[attr];
    },

    // Get the HTML-escaped value of an attribute.
    escape: function(attr) {
      return _.escape(this.get(attr));
    },

    // Returns `true` if the attribute contains a value that is not null
    // or undefined.
    has: function(attr) {
      return this.get(attr) != null;
    },

    // Set a hash of model attributes on the object, firing `"change"`. This is
    // the core primitive operation of a model, updating the data and notifying
    // anyone who needs to know about the change in state. The heart of the beast.
    set: function(key, val, options) {
      var attr, attrs, unset, changes, silent, changing, prev, current;
      if (key == null) return this;

      // Handle both `"key", value` and `{key: value}` -style arguments.
      if (typeof key === 'object') {
        attrs = key;
        options = val;
      } else {
        (attrs = {})[key] = val;
      }

      options || (options = {});

      // Run validation.
      if (!this._validate(attrs, options)) return false;

      // Extract attributes and options.
      unset           = options.unset;
      silent          = options.silent;
      changes         = [];
      changing        = this._changing;
      this._changing  = true;

      if (!changing) {
        this._previousAttributes = _.clone(this.attributes);
        this.changed = {};
      }
      current = this.attributes, prev = this._previousAttributes;

      // Check for changes of `id`.
      if (this.idAttribute in attrs) this.id = attrs[this.idAttribute];

      // For each `set` attribute, update or delete the current value.
      for (attr in attrs) {
        val = attrs[attr];
        if (!_.isEqual(current[attr], val)) changes.push(attr);
        if (!_.isEqual(prev[attr], val)) {
          this.changed[attr] = val;
        } else {
          delete this.changed[attr];
        }
        unset ? delete current[attr] : current[attr] = val;
      }

      // Trigger all relevant attribute changes.
      if (!silent) {
        if (changes.length) this._pending = true;
        for (var i = 0, l = changes.length; i < l; i++) {
          this.trigger('change:' + changes[i], this, current[changes[i]], options);
        }
      }

      // You might be wondering why there's a `while` loop here. Changes can
      // be recursively nested within `"change"` events.
      if (changing) return this;
      if (!silent) {
        while (this._pending) {
          this._pending = false;
          this.trigger('change', this, options);
        }
      }
      this._pending = false;
      this._changing = false;
      return this;
    },

    // Remove an attribute from the model, firing `"change"`. `unset` is a noop
    // if the attribute doesn't exist.
    unset: function(attr, options) {
      return this.set(attr, void 0, _.extend({}, options, {unset: true}));
    },

    // Clear all attributes on the model, firing `"change"`.
    clear: function(options) {
      var attrs = {};
      for (var key in this.attributes) attrs[key] = void 0;
      return this.set(attrs, _.extend({}, options, {unset: true}));
    },

    // Determine if the model has changed since the last `"change"` event.
    // If you specify an attribute name, determine if that attribute has changed.
    hasChanged: function(attr) {
      if (attr == null) return !_.isEmpty(this.changed);
      return _.has(this.changed, attr);
    },

    // Return an object containing all the attributes that have changed, or
    // false if there are no changed attributes. Useful for determining what
    // parts of a view need to be updated and/or what attributes need to be
    // persisted to the server. Unset attributes will be set to undefined.
    // You can also pass an attributes object to diff against the model,
    // determining if there *would be* a change.
    changedAttributes: function(diff) {
      if (!diff) return this.hasChanged() ? _.clone(this.changed) : false;
      var val, changed = false;
      var old = this._changing ? this._previousAttributes : this.attributes;
      for (var attr in diff) {
        if (_.isEqual(old[attr], (val = diff[attr]))) continue;
        (changed || (changed = {}))[attr] = val;
      }
      return changed;
    },

    // Get the previous value of an attribute, recorded at the time the last
    // `"change"` event was fired.
    previous: function(attr) {
      if (attr == null || !this._previousAttributes) return null;
      return this._previousAttributes[attr];
    },

    // Get all of the attributes of the model at the time of the previous
    // `"change"` event.
    previousAttributes: function() {
      return _.clone(this._previousAttributes);
    },

    // Fetch the model from the server. If the server's representation of the
    // model differs from its current attributes, they will be overridden,
    // triggering a `"change"` event.
    fetch: function(options) {
      options = options ? _.clone(options) : {};
      if (options.parse === void 0) options.parse = true;
      var model = this;
      var success = options.success;
      options.success = function(resp) {
        if (!model.set(model.parse(resp, options), options)) return false;
        if (success) success(model, resp, options);
        model.trigger('sync', model, resp, options);
      };
      wrapError(this, options);
      return this.sync('read', this, options);
    },

    // Set a hash of model attributes, and sync the model to the server.
    // If the server returns an attributes hash that differs, the model's
    // state will be `set` again.
    save: function(key, val, options) {
      var attrs, method, xhr, attributes = this.attributes;

      // Handle both `"key", value` and `{key: value}` -style arguments.
      if (key == null || typeof key === 'object') {
        attrs = key;
        options = val;
      } else {
        (attrs = {})[key] = val;
      }

      // If we're not waiting and attributes exist, save acts as `set(attr).save(null, opts)`.
      if (attrs && (!options || !options.wait) && !this.set(attrs, options)) return false;

      options = _.extend({validate: true}, options);

      // Do not persist invalid models.
      if (!this._validate(attrs, options)) return false;

      // Set temporary attributes if `{wait: true}`.
      if (attrs && options.wait) {
        this.attributes = _.extend({}, attributes, attrs);
      }

      // After a successful server-side save, the client is (optionally)
      // updated with the server-side state.
      if (options.parse === void 0) options.parse = true;
      var model = this;
      var success = options.success;
      options.success = function(resp) {
        // Ensure attributes are restored during synchronous saves.
        model.attributes = attributes;
        var serverAttrs = model.parse(resp, options);
        if (options.wait) serverAttrs = _.extend(attrs || {}, serverAttrs);
        if (_.isObject(serverAttrs) && !model.set(serverAttrs, options)) {
          return false;
        }
        if (success) success(model, resp, options);
        model.trigger('sync', model, resp, options);
      };
      wrapError(this, options);

      method = this.isNew() ? 'create' : (options.patch ? 'patch' : 'update');
      if (method === 'patch') options.attrs = attrs;
      xhr = this.sync(method, this, options);

      // Restore attributes.
      if (attrs && options.wait) this.attributes = attributes;

      return xhr;
    },

    // Destroy this model on the server if it was already persisted.
    // Optimistically removes the model from its collection, if it has one.
    // If `wait: true` is passed, waits for the server to respond before removal.
    destroy: function(options) {
      options = options ? _.clone(options) : {};
      var model = this;
      var success = options.success;

      var destroy = function() {
        model.trigger('destroy', model, model.collection, options);
      };

      options.success = function(resp) {
        if (options.wait || model.isNew()) destroy();
        if (success) success(model, resp, options);
        if (!model.isNew()) model.trigger('sync', model, resp, options);
      };

      if (this.isNew()) {
        options.success();
        return false;
      }
      wrapError(this, options);

      var xhr = this.sync('delete', this, options);
      if (!options.wait) destroy();
      return xhr;
    },

    // Default URL for the model's representation on the server -- if you're
    // using Backbone's restful methods, override this to change the endpoint
    // that will be called.
    url: function() {
      var base = _.result(this, 'urlRoot') || _.result(this.collection, 'url') || urlError();
      if (this.isNew()) return base;
      return base + (base.charAt(base.length - 1) === '/' ? '' : '/') + encodeURIComponent(this.id);
    },

    // **parse** converts a response into the hash of attributes to be `set` on
    // the model. The default implementation is just to pass the response along.
    parse: function(resp, options) {
      return resp;
    },

    // Create a new model with identical attributes to this one.
    clone: function() {
      return new this.constructor(this.attributes);
    },

    // A model is new if it has never been saved to the server, and lacks an id.
    isNew: function() {
      return this.id == null;
    },

    // Check if the model is currently in a valid state.
    isValid: function(options) {
      return this._validate({}, _.extend(options || {}, { validate: true }));
    },

    // Run validation against the next complete set of model attributes,
    // returning `true` if all is well. Otherwise, fire an `"invalid"` event.
    _validate: function(attrs, options) {
      if (!options.validate || !this.validate) return true;
      attrs = _.extend({}, this.attributes, attrs);
      var error = this.validationError = this.validate(attrs, options) || null;
      if (!error) return true;
      this.trigger('invalid', this, error, _.extend(options || {}, {validationError: error}));
      return false;
    }

  });

  // Underscore methods that we want to implement on the Model.
  var modelMethods = ['keys', 'values', 'pairs', 'invert', 'pick', 'omit'];

  // Mix in each Underscore method as a proxy to `Model#attributes`.
  _.each(modelMethods, function(method) {
    Model.prototype[method] = function() {
      var args = slice.call(arguments);
      args.unshift(this.attributes);
      return _[method].apply(_, args);
    };
  });

  // Backbone.Collection
  // -------------------

  // If models tend to represent a single row of data, a Backbone Collection is
  // more analagous to a table full of data ... or a small slice or page of that
  // table, or a collection of rows that belong together for a particular reason
  // -- all of the messages in this particular folder, all of the documents
  // belonging to this particular author, and so on. Collections maintain
  // indexes of their models, both in order, and for lookup by `id`.

  // Create a new **Collection**, perhaps to contain a specific type of `model`.
  // If a `comparator` is specified, the Collection will maintain
  // its models in sort order, as they're added and removed.
  var Collection = Backbone.Collection = function(models, options) {
    options || (options = {});
    if (options.url) this.url = options.url;
    if (options.model) this.model = options.model;
    if (options.comparator !== void 0) this.comparator = options.comparator;
    this._reset();
    this.initialize.apply(this, arguments);
    if (models) this.reset(models, _.extend({silent: true}, options));
  };

  // Default options for `Collection#set`.
  var setOptions = {add: true, remove: true, merge: true};
  var addOptions = {add: true, merge: false, remove: false};

  // Define the Collection's inheritable methods.
  _.extend(Collection.prototype, Events, {

    // The default model for a collection is just a **Backbone.Model**.
    // This should be overridden in most cases.
    model: Model,

    // Initialize is an empty function by default. Override it with your own
    // initialization logic.
    initialize: function(){},

    // The JSON representation of a Collection is an array of the
    // models' attributes.
    toJSON: function(options) {
      return this.map(function(model){ return model.toJSON(options); });
    },

    // Proxy `Backbone.sync` by default.
    sync: function() {
      return Backbone.sync.apply(this, arguments);
    },

    // Add a model, or list of models to the set.
    add: function(models, options) {
      return this.set(models, _.defaults(options || {}, addOptions));
    },

    // Remove a model, or a list of models from the set.
    remove: function(models, options) {
      models = _.isArray(models) ? models.slice() : [models];
      options || (options = {});
      var i, l, index, model;
      for (i = 0, l = models.length; i < l; i++) {
        model = this.get(models[i]);
        if (!model) continue;
        delete this._byId[model.id];
        delete this._byId[model.cid];
        index = this.indexOf(model);
        this.models.splice(index, 1);
        this.length--;
        if (!options.silent) {
          options.index = index;
          model.trigger('remove', model, this, options);
        }
        this._removeReference(model);
      }
      return this;
    },

    // Update a collection by `set`-ing a new list of models, adding new ones,
    // removing models that are no longer present, and merging models that
    // already exist in the collection, as necessary. Similar to **Model#set**,
    // the core operation for updating the data contained by the collection.
    set: function(models, options) {
      options = _.defaults(options || {}, setOptions);
      if (options.parse) models = this.parse(models, options);
      if (!_.isArray(models)) models = models ? [models] : [];
      var i, l, model, attrs, existing, sort;
      var at = options.at;
      var sortable = this.comparator && (at == null) && options.sort !== false;
      var sortAttr = _.isString(this.comparator) ? this.comparator : null;
      var toAdd = [], toRemove = [], modelMap = {};

      // Turn bare objects into model references, and prevent invalid models
      // from being added.
      for (i = 0, l = models.length; i < l; i++) {
        if (!(model = this._prepareModel(models[i], options))) continue;

        // If a duplicate is found, prevent it from being added and
        // optionally merge it into the existing model.
        if (existing = this.get(model)) {
          if (options.remove) modelMap[existing.cid] = true;
          if (options.merge) {
            existing.set(model.attributes, options);
            if (sortable && !sort && existing.hasChanged(sortAttr)) sort = true;
          }

        // This is a new model, push it to the `toAdd` list.
        } else if (options.add) {
          toAdd.push(model);

          // Listen to added models' events, and index models for lookup by
          // `id` and by `cid`.
          model.on('all', this._onModelEvent, this);
          this._byId[model.cid] = model;
          if (model.id != null) this._byId[model.id] = model;
        }
      }

      // Remove nonexistent models if appropriate.
      if (options.remove) {
        for (i = 0, l = this.length; i < l; ++i) {
          if (!modelMap[(model = this.models[i]).cid]) toRemove.push(model);
        }
        if (toRemove.length) this.remove(toRemove, options);
      }

      // See if sorting is needed, update `length` and splice in new models.
      if (toAdd.length) {
        if (sortable) sort = true;
        this.length += toAdd.length;
        if (at != null) {
          splice.apply(this.models, [at, 0].concat(toAdd));
        } else {
          push.apply(this.models, toAdd);
        }
      }

      // Silently sort the collection if appropriate.
      if (sort) this.sort({silent: true});

      if (options.silent) return this;

      // Trigger `add` events.
      for (i = 0, l = toAdd.length; i < l; i++) {
        (model = toAdd[i]).trigger('add', model, this, options);
      }

      // Trigger `sort` if the collection was sorted.
      if (sort) this.trigger('sort', this, options);
      return this;
    },

    // When you have more items than you want to add or remove individually,
    // you can reset the entire set with a new list of models, without firing
    // any granular `add` or `remove` events. Fires `reset` when finished.
    // Useful for bulk operations and optimizations.
    reset: function(models, options) {
      options || (options = {});
      for (var i = 0, l = this.models.length; i < l; i++) {
        this._removeReference(this.models[i]);
      }
      options.previousModels = this.models;
      this._reset();
      this.add(models, _.extend({silent: true}, options));
      if (!options.silent) this.trigger('reset', this, options);
      return this;
    },

    // Add a model to the end of the collection.
    push: function(model, options) {
      model = this._prepareModel(model, options);
      this.add(model, _.extend({at: this.length}, options));
      return model;
    },

    // Remove a model from the end of the collection.
    pop: function(options) {
      var model = this.at(this.length - 1);
      this.remove(model, options);
      return model;
    },

    // Add a model to the beginning of the collection.
    unshift: function(model, options) {
      model = this._prepareModel(model, options);
      this.add(model, _.extend({at: 0}, options));
      return model;
    },

    // Remove a model from the beginning of the collection.
    shift: function(options) {
      var model = this.at(0);
      this.remove(model, options);
      return model;
    },

    // Slice out a sub-array of models from the collection.
    slice: function(begin, end) {
      return this.models.slice(begin, end);
    },

    // Get a model from the set by id.
    get: function(obj) {
      if (obj == null) return void 0;
      return this._byId[obj.id != null ? obj.id : obj.cid || obj];
    },

    // Get the model at the given index.
    at: function(index) {
      return this.models[index];
    },

    // Return models with matching attributes. Useful for simple cases of
    // `filter`.
    where: function(attrs, first) {
      if (_.isEmpty(attrs)) return first ? void 0 : [];
      return this[first ? 'find' : 'filter'](function(model) {
        for (var key in attrs) {
          if (attrs[key] !== model.get(key)) return false;
        }
        return true;
      });
    },

    // Return the first model with matching attributes. Useful for simple cases
    // of `find`.
    findWhere: function(attrs) {
      return this.where(attrs, true);
    },

    // Force the collection to re-sort itself. You don't need to call this under
    // normal circumstances, as the set will maintain sort order as each item
    // is added.
    sort: function(options) {
      if (!this.comparator) throw new Error('Cannot sort a set without a comparator');
      options || (options = {});

      // Run sort based on type of `comparator`.
      if (_.isString(this.comparator) || this.comparator.length === 1) {
        this.models = this.sortBy(this.comparator, this);
      } else {
        this.models.sort(_.bind(this.comparator, this));
      }

      if (!options.silent) this.trigger('sort', this, options);
      return this;
    },

    // Figure out the smallest index at which a model should be inserted so as
    // to maintain order.
    sortedIndex: function(model, value, context) {
      value || (value = this.comparator);
      var iterator = _.isFunction(value) ? value : function(model) {
        return model.get(value);
      };
      return _.sortedIndex(this.models, model, iterator, context);
    },

    // Pluck an attribute from each model in the collection.
    pluck: function(attr) {
      return _.invoke(this.models, 'get', attr);
    },

    // Fetch the default set of models for this collection, resetting the
    // collection when they arrive. If `reset: true` is passed, the response
    // data will be passed through the `reset` method instead of `set`.
    fetch: function(options) {
      options = options ? _.clone(options) : {};
      if (options.parse === void 0) options.parse = true;
      var success = options.success;
      var collection = this;
      options.success = function(resp) {
        var method = options.reset ? 'reset' : 'set';
        collection[method](resp, options);
        if (success) success(collection, resp, options);
        collection.trigger('sync', collection, resp, options);
      };
      wrapError(this, options);
      return this.sync('read', this, options);
    },

    // Create a new instance of a model in this collection. Add the model to the
    // collection immediately, unless `wait: true` is passed, in which case we
    // wait for the server to agree.
    create: function(model, options) {
      options = options ? _.clone(options) : {};
      if (!(model = this._prepareModel(model, options))) return false;
      if (!options.wait) this.add(model, options);
      var collection = this;
      var success = options.success;
      options.success = function(resp) {
        if (options.wait) collection.add(model, options);
        if (success) success(model, resp, options);
      };
      model.save(null, options);
      return model;
    },

    // **parse** converts a response into a list of models to be added to the
    // collection. The default implementation is just to pass it through.
    parse: function(resp, options) {
      return resp;
    },

    // Create a new collection with an identical list of models as this one.
    clone: function() {
      return new this.constructor(this.models);
    },

    // Private method to reset all internal state. Called when the collection
    // is first initialized or reset.
    _reset: function() {
      this.length = 0;
      this.models = [];
      this._byId  = {};
    },

    // Prepare a hash of attributes (or other model) to be added to this
    // collection.
    _prepareModel: function(attrs, options) {
      if (attrs instanceof Model) {
        if (!attrs.collection) attrs.collection = this;
        return attrs;
      }
      options || (options = {});
      options.collection = this;
      var model = new this.model(attrs, options);
      if (!model._validate(attrs, options)) {
        this.trigger('invalid', this, attrs, options);
        return false;
      }
      return model;
    },

    // Internal method to sever a model's ties to a collection.
    _removeReference: function(model) {
      if (this === model.collection) delete model.collection;
      model.off('all', this._onModelEvent, this);
    },

    // Internal method called every time a model in the set fires an event.
    // Sets need to update their indexes when models change ids. All other
    // events simply proxy through. "add" and "remove" events that originate
    // in other collections are ignored.
    _onModelEvent: function(event, model, collection, options) {
      if ((event === 'add' || event === 'remove') && collection !== this) return;
      if (event === 'destroy') this.remove(model, options);
      if (model && event === 'change:' + model.idAttribute) {
        delete this._byId[model.previous(model.idAttribute)];
        if (model.id != null) this._byId[model.id] = model;
      }
      this.trigger.apply(this, arguments);
    }

  });

  // Underscore methods that we want to implement on the Collection.
  // 90% of the core usefulness of Backbone Collections is actually implemented
  // right here:
  var methods = ['forEach', 'each', 'map', 'collect', 'reduce', 'foldl',
    'inject', 'reduceRight', 'foldr', 'find', 'detect', 'filter', 'select',
    'reject', 'every', 'all', 'some', 'any', 'include', 'contains', 'invoke',
    'max', 'min', 'toArray', 'size', 'first', 'head', 'take', 'initial', 'rest',
    'tail', 'drop', 'last', 'without', 'indexOf', 'shuffle', 'lastIndexOf',
    'isEmpty', 'chain'];

  // Mix in each Underscore method as a proxy to `Collection#models`.
  _.each(methods, function(method) {
    Collection.prototype[method] = function() {
      var args = slice.call(arguments);
      args.unshift(this.models);
      return _[method].apply(_, args);
    };
  });

  // Underscore methods that take a property name as an argument.
  var attributeMethods = ['groupBy', 'countBy', 'sortBy'];

  // Use attributes instead of properties.
  _.each(attributeMethods, function(method) {
    Collection.prototype[method] = function(value, context) {
      var iterator = _.isFunction(value) ? value : function(model) {
        return model.get(value);
      };
      return _[method](this.models, iterator, context);
    };
  });

  // Backbone.View
  // -------------

  // Backbone Views are almost more convention than they are actual code. A View
  // is simply a JavaScript object that represents a logical chunk of UI in the
  // DOM. This might be a single item, an entire list, a sidebar or panel, or
  // even the surrounding frame which wraps your whole app. Defining a chunk of
  // UI as a **View** allows you to define your DOM events declaratively, without
  // having to worry about render order ... and makes it easy for the view to
  // react to specific changes in the state of your models.

  // Creating a Backbone.View creates its initial element outside of the DOM,
  // if an existing element is not provided...
  var View = Backbone.View = function(options) {
    this.cid = _.uniqueId('view');
    this._configure(options || {});
    this._ensureElement();
    this.initialize.apply(this, arguments);
    this.delegateEvents();
  };

  // Cached regex to split keys for `delegate`.
  var delegateEventSplitter = /^(\S+)\s*(.*)$/;

  // List of view options to be merged as properties.
  var viewOptions = ['model', 'collection', 'el', 'id', 'attributes', 'className', 'tagName', 'events'];

  // Set up all inheritable **Backbone.View** properties and methods.
  _.extend(View.prototype, Events, {

    // The default `tagName` of a View's element is `"div"`.
    tagName: 'div',

    // jQuery delegate for element lookup, scoped to DOM elements within the
    // current view. This should be prefered to global lookups where possible.
    $: function(selector) {
      return this.$el.find(selector);
    },

    // Initialize is an empty function by default. Override it with your own
    // initialization logic.
    initialize: function(){},

    // **render** is the core function that your view should override, in order
    // to populate its element (`this.el`), with the appropriate HTML. The
    // convention is for **render** to always return `this`.
    render: function() {
      return this;
    },

    // Remove this view by taking the element out of the DOM, and removing any
    // applicable Backbone.Events listeners.
    remove: function() {
      this.$el.remove();
      this.stopListening();
      return this;
    },

    // Change the view's element (`this.el` property), including event
    // re-delegation.
    setElement: function(element, delegate) {
      if (this.$el) this.undelegateEvents();
      this.$el = element instanceof Backbone.$ ? element : Backbone.$(element);
      this.el = this.$el[0];
      if (delegate !== false) this.delegateEvents();
      return this;
    },

    // Set callbacks, where `this.events` is a hash of
    //
    // *{"event selector": "callback"}*
    //
    //     {
    //       'mousedown .title':  'edit',
    //       'click .button':     'save'
    //       'click .open':       function(e) { ... }
    //     }
    //
    // pairs. Callbacks will be bound to the view, with `this` set properly.
    // Uses event delegation for efficiency.
    // Omitting the selector binds the event to `this.el`.
    // This only works for delegate-able events: not `focus`, `blur`, and
    // not `change`, `submit`, and `reset` in Internet Explorer.
    delegateEvents: function(events) {
      if (!(events || (events = _.result(this, 'events')))) return this;
      this.undelegateEvents();
      for (var key in events) {
        var method = events[key];
        if (!_.isFunction(method)) method = this[events[key]];
        if (!method) continue;

        var match = key.match(delegateEventSplitter);
        var eventName = match[1], selector = match[2];
        method = _.bind(method, this);
        eventName += '.delegateEvents' + this.cid;
        if (selector === '') {
          this.$el.on(eventName, method);
        } else {
          this.$el.on(eventName, selector, method);
        }
      }
      return this;
    },

    // Clears all callbacks previously bound to the view with `delegateEvents`.
    // You usually don't need to use this, but may wish to if you have multiple
    // Backbone views attached to the same DOM element.
    undelegateEvents: function() {
      this.$el.off('.delegateEvents' + this.cid);
      return this;
    },

    // Performs the initial configuration of a View with a set of options.
    // Keys with special meaning *(e.g. model, collection, id, className)* are
    // attached directly to the view.  See `viewOptions` for an exhaustive
    // list.
    _configure: function(options) {
      if (this.options) options = _.extend({}, _.result(this, 'options'), options);
      _.extend(this, _.pick(options, viewOptions));
      this.options = options;
    },

    // Ensure that the View has a DOM element to render into.
    // If `this.el` is a string, pass it through `$()`, take the first
    // matching element, and re-assign it to `el`. Otherwise, create
    // an element from the `id`, `className` and `tagName` properties.
    _ensureElement: function() {
      if (!this.el) {
        var attrs = _.extend({}, _.result(this, 'attributes'));
        if (this.id) attrs.id = _.result(this, 'id');
        if (this.className) attrs['class'] = _.result(this, 'className');
        var $el = Backbone.$('<' + _.result(this, 'tagName') + '>').attr(attrs);
        this.setElement($el, false);
      } else {
        this.setElement(_.result(this, 'el'), false);
      }
    }

  });

  // Backbone.sync
  // -------------

  // Override this function to change the manner in which Backbone persists
  // models to the server. You will be passed the type of request, and the
  // model in question. By default, makes a RESTful Ajax request
  // to the model's `url()`. Some possible customizations could be:
  //
  // * Use `setTimeout` to batch rapid-fire updates into a single request.
  // * Send up the models as XML instead of JSON.
  // * Persist models via WebSockets instead of Ajax.
  //
  // Turn on `Backbone.emulateHTTP` in order to send `PUT` and `DELETE` requests
  // as `POST`, with a `_method` parameter containing the true HTTP method,
  // as well as all requests with the body as `application/x-www-form-urlencoded`
  // instead of `application/json` with the model in a param named `model`.
  // Useful when interfacing with server-side languages like **PHP** that make
  // it difficult to read the body of `PUT` requests.
  Backbone.sync = function(method, model, options) {
    var type = methodMap[method];

    // Default options, unless specified.
    _.defaults(options || (options = {}), {
      emulateHTTP: Backbone.emulateHTTP,
      emulateJSON: Backbone.emulateJSON
    });

    // Default JSON-request options.
    var params = {type: type, dataType: 'json'};

    // Ensure that we have a URL.
    if (!options.url) {
      params.url = _.result(model, 'url') || urlError();
    }

    // Ensure that we have the appropriate request data.
    if (options.data == null && model && (method === 'create' || method === 'update' || method === 'patch')) {
      params.contentType = 'application/json';
      params.data = JSON.stringify(options.attrs || model.toJSON(options));
    }

    // For older servers, emulate JSON by encoding the request into an HTML-form.
    if (options.emulateJSON) {
      params.contentType = 'application/x-www-form-urlencoded';
      params.data = params.data ? {model: params.data} : {};
    }

    // For older servers, emulate HTTP by mimicking the HTTP method with `_method`
    // And an `X-HTTP-Method-Override` header.
    if (options.emulateHTTP && (type === 'PUT' || type === 'DELETE' || type === 'PATCH')) {
      params.type = 'POST';
      if (options.emulateJSON) params.data._method = type;
      var beforeSend = options.beforeSend;
      options.beforeSend = function(xhr) {
        xhr.setRequestHeader('X-HTTP-Method-Override', type);
        if (beforeSend) return beforeSend.apply(this, arguments);
      };
    }

    // Don't process data on a non-GET request.
    if (params.type !== 'GET' && !options.emulateJSON) {
      params.processData = false;
    }

    // If we're sending a `PATCH` request, and we're in an old Internet Explorer
    // that still has ActiveX enabled by default, override jQuery to use that
    // for XHR instead. Remove this line when jQuery supports `PATCH` on IE8.
    if (params.type === 'PATCH' && window.ActiveXObject &&
          !(window.external && window.external.msActiveXFilteringEnabled)) {
      params.xhr = function() {
        return new ActiveXObject("Microsoft.XMLHTTP");
      };
    }

    // Make the request, allowing the user to override any Ajax options.
    var xhr = options.xhr = Backbone.ajax(_.extend(params, options));
    model.trigger('request', model, xhr, options);
    return xhr;
  };

  // Map from CRUD to HTTP for our default `Backbone.sync` implementation.
  var methodMap = {
    'create': 'POST',
    'update': 'PUT',
    'patch':  'PATCH',
    'delete': 'DELETE',
    'read':   'GET'
  };

  // Set the default implementation of `Backbone.ajax` to proxy through to `$`.
  // Override this if you'd like to use a different library.
  Backbone.ajax = function() {
    return Backbone.$.ajax.apply(Backbone.$, arguments);
  };

  // Backbone.Router
  // ---------------

  // Routers map faux-URLs to actions, and fire events when routes are
  // matched. Creating a new one sets its `routes` hash, if not set statically.
  var Router = Backbone.Router = function(options) {
    options || (options = {});
    if (options.routes) this.routes = options.routes;
    this._bindRoutes();
    this.initialize.apply(this, arguments);
  };

  // Cached regular expressions for matching named param parts and splatted
  // parts of route strings.
  var optionalParam = /\((.*?)\)/g;
  var namedParam    = /(\(\?)?:\w+/g;
  var splatParam    = /\*\w+/g;
  var escapeRegExp  = /[\-{}\[\]+?.,\\\^$|#\s]/g;

  // Set up all inheritable **Backbone.Router** properties and methods.
  _.extend(Router.prototype, Events, {

    // Initialize is an empty function by default. Override it with your own
    // initialization logic.
    initialize: function(){},

    // Manually bind a single named route to a callback. For example:
    //
    //     this.route('search/:query/p:num', 'search', function(query, num) {
    //       ...
    //     });
    //
    route: function(route, name, callback) {
      if (!_.isRegExp(route)) route = this._routeToRegExp(route);
      if (_.isFunction(name)) {
        callback = name;
        name = '';
      }
      if (!callback) callback = this[name];
      var router = this;
      Backbone.history.route(route, function(fragment) {
        var args = router._extractParameters(route, fragment);
        callback && callback.apply(router, args);
        router.trigger.apply(router, ['route:' + name].concat(args));
        router.trigger('route', name, args);
        Backbone.history.trigger('route', router, name, args);
      });
      return this;
    },

    // Simple proxy to `Backbone.history` to save a fragment into the history.
    navigate: function(fragment, options) {
      Backbone.history.navigate(fragment, options);
      return this;
    },

    // Bind all defined routes to `Backbone.history`. We have to reverse the
    // order of the routes here to support behavior where the most general
    // routes can be defined at the bottom of the route map.
    _bindRoutes: function() {
      if (!this.routes) return;
      this.routes = _.result(this, 'routes');
      var route, routes = _.keys(this.routes);
      while ((route = routes.pop()) != null) {
        this.route(route, this.routes[route]);
      }
    },

    // Convert a route string into a regular expression, suitable for matching
    // against the current location hash.
    _routeToRegExp: function(route) {
      route = route.replace(escapeRegExp, '\\$&')
                   .replace(optionalParam, '(?:$1)?')
                   .replace(namedParam, function(match, optional){
                     return optional ? match : '([^\/]+)';
                   })
                   .replace(splatParam, '(.*?)');
      return new RegExp('^' + route + '$');
    },

    // Given a route, and a URL fragment that it matches, return the array of
    // extracted decoded parameters. Empty or unmatched parameters will be
    // treated as `null` to normalize cross-browser behavior.
    _extractParameters: function(route, fragment) {
      var params = route.exec(fragment).slice(1);
      return _.map(params, function(param) {
        return param ? decodeURIComponent(param) : null;
      });
    }

  });

  // Backbone.History
  // ----------------

  // Handles cross-browser history management, based on either
  // [pushState](http://diveintohtml5.info/history.html) and real URLs, or
  // [onhashchange](https://developer.mozilla.org/en-US/docs/DOM/window.onhashchange)
  // and URL fragments. If the browser supports neither (old IE, natch),
  // falls back to polling.
  var History = Backbone.History = function() {
    this.handlers = [];
    _.bindAll(this, 'checkUrl');

    // Ensure that `History` can be used outside of the browser.
    if (typeof window !== 'undefined') {
      this.location = window.location;
      this.history = window.history;
    }
  };

  // Cached regex for stripping a leading hash/slash and trailing space.
  var routeStripper = /^[#\/]|\s+$/g;

  // Cached regex for stripping leading and trailing slashes.
  var rootStripper = /^\/+|\/+$/g;

  // Cached regex for detecting MSIE.
  var isExplorer = /msie [\w.]+/;

  // Cached regex for removing a trailing slash.
  var trailingSlash = /\/$/;

  // Has the history handling already been started?
  History.started = false;

  // Set up all inheritable **Backbone.History** properties and methods.
  _.extend(History.prototype, Events, {

    // The default interval to poll for hash changes, if necessary, is
    // twenty times a second.
    interval: 50,

    // Gets the true hash value. Cannot use location.hash directly due to bug
    // in Firefox where location.hash will always be decoded.
    getHash: function(window) {
      var match = (window || this).location.href.match(/#(.*)$/);
      return match ? match[1] : '';
    },

    // Get the cross-browser normalized URL fragment, either from the URL,
    // the hash, or the override.
    getFragment: function(fragment, forcePushState) {
      if (fragment == null) {
        if (this._hasPushState || !this._wantsHashChange || forcePushState) {
          fragment = this.location.pathname;
          var root = this.root.replace(trailingSlash, '');
          if (!fragment.indexOf(root)) fragment = fragment.substr(root.length);
        } else {
          fragment = this.getHash();
        }
      }
      return fragment.replace(routeStripper, '');
    },

    // Start the hash change handling, returning `true` if the current URL matches
    // an existing route, and `false` otherwise.
    start: function(options) {
      if (History.started) throw new Error("Backbone.history has already been started");
      History.started = true;

      // Figure out the initial configuration. Do we need an iframe?
      // Is pushState desired ... is it available?
      this.options          = _.extend({}, {root: '/'}, this.options, options);
      this.root             = this.options.root;
      this._wantsHashChange = this.options.hashChange !== false;
      this._wantsPushState  = !!this.options.pushState;
      this._hasPushState    = !!(this.options.pushState && this.history && this.history.pushState);
      var fragment          = this.getFragment();
      var docMode           = document.documentMode;
      var oldIE             = (isExplorer.exec(navigator.userAgent.toLowerCase()) && (!docMode || docMode <= 7));

      // Normalize root to always include a leading and trailing slash.
      this.root = ('/' + this.root + '/').replace(rootStripper, '/');

      if (oldIE && this._wantsHashChange) {
        this.iframe = Backbone.$('<iframe src="javascript:0" tabindex="-1" />').hide().appendTo('body')[0].contentWindow;
        this.navigate(fragment);
      }

      // Depending on whether we're using pushState or hashes, and whether
      // 'onhashchange' is supported, determine how we check the URL state.
      if (this._hasPushState) {
        Backbone.$(window).on('popstate', this.checkUrl);
      } else if (this._wantsHashChange && ('onhashchange' in window) && !oldIE) {
        Backbone.$(window).on('hashchange', this.checkUrl);
      } else if (this._wantsHashChange) {
        this._checkUrlInterval = setInterval(this.checkUrl, this.interval);
      }

      // Determine if we need to change the base url, for a pushState link
      // opened by a non-pushState browser.
      this.fragment = fragment;
      var loc = this.location;
      var atRoot = loc.pathname.replace(/[^\/]$/, '$&/') === this.root;

      // If we've started off with a route from a `pushState`-enabled browser,
      // but we're currently in a browser that doesn't support it...
      if (this._wantsHashChange && this._wantsPushState && !this._hasPushState && !atRoot) {
        this.fragment = this.getFragment(null, true);
        this.location.replace(this.root + this.location.search + '#' + this.fragment);
        // Return immediately as browser will do redirect to new url
        return true;

      // Or if we've started out with a hash-based route, but we're currently
      // in a browser where it could be `pushState`-based instead...
      } else if (this._wantsPushState && this._hasPushState && atRoot && loc.hash) {
        this.fragment = this.getHash().replace(routeStripper, '');
        this.history.replaceState({}, document.title, this.root + this.fragment + loc.search);
      }

      if (!this.options.silent) return this.loadUrl();
    },

    // Disable Backbone.history, perhaps temporarily. Not useful in a real app,
    // but possibly useful for unit testing Routers.
    stop: function() {
      Backbone.$(window).off('popstate', this.checkUrl).off('hashchange', this.checkUrl);
      clearInterval(this._checkUrlInterval);
      History.started = false;
    },

    // Add a route to be tested when the fragment changes. Routes added later
    // may override previous routes.
    route: function(route, callback) {
      this.handlers.unshift({route: route, callback: callback});
    },

    // Checks the current URL to see if it has changed, and if it has,
    // calls `loadUrl`, normalizing across the hidden iframe.
    checkUrl: function(e) {
      var current = this.getFragment();
      if (current === this.fragment && this.iframe) {
        current = this.getFragment(this.getHash(this.iframe));
      }
      if (current === this.fragment) return false;
      if (this.iframe) this.navigate(current);
      this.loadUrl() || this.loadUrl(this.getHash());
    },

    // Attempt to load the current URL fragment. If a route succeeds with a
    // match, returns `true`. If no defined routes matches the fragment,
    // returns `false`.
    loadUrl: function(fragmentOverride) {
      var fragment = this.fragment = this.getFragment(fragmentOverride);
      var matched = _.any(this.handlers, function(handler) {
        if (handler.route.test(fragment)) {
          handler.callback(fragment);
          return true;
        }
      });
      return matched;
    },

    // Save a fragment into the hash history, or replace the URL state if the
    // 'replace' option is passed. You are responsible for properly URL-encoding
    // the fragment in advance.
    //
    // The options object can contain `trigger: true` if you wish to have the
    // route callback be fired (not usually desirable), or `replace: true`, if
    // you wish to modify the current URL without adding an entry to the history.
    navigate: function(fragment, options) {
      if (!History.started) return false;
      if (!options || options === true) options = {trigger: options};
      fragment = this.getFragment(fragment || '');
      if (this.fragment === fragment) return;
      this.fragment = fragment;
      var url = this.root + fragment;

      // If pushState is available, we use it to set the fragment as a real URL.
      if (this._hasPushState) {
        this.history[options.replace ? 'replaceState' : 'pushState']({}, document.title, url);

      // If hash changes haven't been explicitly disabled, update the hash
      // fragment to store history.
      } else if (this._wantsHashChange) {
        this._updateHash(this.location, fragment, options.replace);
        if (this.iframe && (fragment !== this.getFragment(this.getHash(this.iframe)))) {
          // Opening and closing the iframe tricks IE7 and earlier to push a
          // history entry on hash-tag change.  When replace is true, we don't
          // want this.
          if(!options.replace) this.iframe.document.open().close();
          this._updateHash(this.iframe.location, fragment, options.replace);
        }

      // If you've told us that you explicitly don't want fallback hashchange-
      // based history, then `navigate` becomes a page refresh.
      } else {
        return this.location.assign(url);
      }
      if (options.trigger) this.loadUrl(fragment);
    },

    // Update the hash location, either replacing the current entry, or adding
    // a new one to the browser history.
    _updateHash: function(location, fragment, replace) {
      if (replace) {
        var href = location.href.replace(/(javascript:|#).*$/, '');
        location.replace(href + '#' + fragment);
      } else {
        // Some browsers require that `hash` contains a leading #.
        location.hash = '#' + fragment;
      }
    }

  });

  // Create the default Backbone.history.
  Backbone.history = new History;

  // Helpers
  // -------

  // Helper function to correctly set up the prototype chain, for subclasses.
  // Similar to `goog.inherits`, but uses a hash of prototype properties and
  // class properties to be extended.
  var extend = function(protoProps, staticProps) {
    var parent = this;
    var child;

    // The constructor function for the new subclass is either defined by you
    // (the "constructor" property in your `extend` definition), or defaulted
    // by us to simply call the parent's constructor.
    if (protoProps && _.has(protoProps, 'constructor')) {
      child = protoProps.constructor;
    } else {
      child = function(){ return parent.apply(this, arguments); };
    }

    // Add static properties to the constructor function, if supplied.
    _.extend(child, parent, staticProps);

    // Set the prototype chain to inherit from `parent`, without calling
    // `parent`'s constructor function.
    var Surrogate = function(){ this.constructor = child; };
    Surrogate.prototype = parent.prototype;
    child.prototype = new Surrogate;

    // Add prototype properties (instance properties) to the subclass,
    // if supplied.
    if (protoProps) _.extend(child.prototype, protoProps);

    // Set a convenience property in case the parent's prototype is needed
    // later.
    child.__super__ = parent.prototype;

    return child;
  };

  // Set up inheritance for the model, collection, router, view and history.
  Model.extend = Collection.extend = Router.extend = View.extend = History.extend = extend;

  // Throw an error when a URL is needed, and none is supplied.
  var urlError = function() {
    throw new Error('A "url" property or function must be specified');
  };

  // Wrap an optional error callback with a fallback error event.
  var wrapError = function (model, options) {
    var error = options.error;
    options.error = function(resp) {
      if (error) error(model, resp, options);
      model.trigger('error', model, resp, options);
    };
  };

}).call(this);
(function() {
  'use strict';  var S4, dualsync, localsync, onlineSync, parseRemoteResponse, result;
  Backbone.Collection.prototype.syncDirty = function() {
    var id, ids, model, store, _i, _len, _results;
    store = localStorage.getItem("" + this.url + "_dirty");
    ids = (store && store.split(',')) || [];
    _results = [];
    for (_i = 0, _len = ids.length; _i < _len; _i++) {
      id = ids[_i];
      model = id.length === 36 ? this.where({
        id: id
      })[0] : this.get(parseInt(id));
      _results.push(model.save());
    }
    return _results;
  };
  Backbone.Collection.prototype.syncDestroyed = function() {
    var id, ids, model, store, _i, _len, _results;
    store = localStorage.getItem("" + this.url + "_destroyed");
    ids = (store && store.split(',')) || [];
    _results = [];
    for (_i = 0, _len = ids.length; _i < _len; _i++) {
      id = ids[_i];
      model = new this.model({
        id: id
      });
      model.collection = this;
      _results.push(model.destroy());
    }
    return _results;
  };
  Backbone.Collection.prototype.syncDirtyAndDestroyed = function() {
    this.syncDirty();
    return this.syncDestroyed();
  };
  S4 = function() {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };
  window.Store = (function() {
    Store.prototype.sep = '';
    function Store(name) {
      this.name = name;
      this.records = this.recordsOn(this.name);
    }
    Store.prototype.generateId = function() {
      return S4() + S4() + '-' + S4() + '-' + S4() + '-' + S4() + '-' + S4() + S4() + S4();
    };
    Store.prototype.save = function() {
      return this.localStorageSet(this.name, this.records.join(','));
    };
    Store.prototype.localStorageSet = function(itemName, itemValue) {
      try {
        return localStorage.setItem(itemName, itemValue);
      } catch (error) {

      }
    };
    Store.prototype.recordsOn = function(key) {
      var store;
      store = localStorage.getItem(key);
      return (store && store.split(',')) || [];
    };
    Store.prototype.dirty = function(model) {
      var dirtyRecords;
      dirtyRecords = this.recordsOn(this.name + '_dirty');
      if (!_.include(dirtyRecords, model.id.toString())) {
        dirtyRecords.push(model.id);
        this.localStorageSet(this.name + '_dirty', dirtyRecords.join(','));
      }
      return model;
    };
    Store.prototype.clean = function(model, from) {
      var dirtyRecords, store;
      store = "" + this.name + "_" + from;
      dirtyRecords = this.recordsOn(store);
      if (_.include(dirtyRecords, model.id.toString())) {
        this.localStorageSet(store, _.without(dirtyRecords, model.id.toString()).join(','));
      }
      return model;
    };
    Store.prototype.destroyed = function(model) {
      var destroyedRecords;
      destroyedRecords = this.recordsOn(this.name + '_destroyed');
      if (!_.include(destroyedRecords, model.id.toString())) {
        destroyedRecords.push(model.id);
        this.localStorageSet(this.name + '_destroyed', destroyedRecords.join(','));
      }
      return model;
    };
    Store.prototype.create = function(model) {
      if (!_.isObject(model)) {
        return model;
      }
      if (model.attributes != null) {
        model = model.attributes;
      }
      if (!model.id) {
        model.id = this.generateId();
      }
      this.localStorageSet(this.name + this.sep + model.id, JSON.stringify(model));
      this.records.push(model.id.toString());
      this.save();
      return model;
    };
    Store.prototype.update = function(model) {
      this.localStorageSet(this.name + this.sep + model.id, JSON.stringify(model));
      if (!_.include(this.records, model.id.toString())) {
        this.records.push(model.id.toString());
      }
      this.save();
      return model;
    };
    Store.prototype.clear = function() {
      var id, _i, _len, _ref;
      _ref = this.records;
      for (_i = 0, _len = _ref.length; _i < _len; _i++) {
        id = _ref[_i];
        localStorage.setItem(this.name + this.sep + id, null);
      }
      this.records = [];
      return this.save();
    };
    Store.prototype.hasDirtyOrDestroyed = function() {
      return !_.isEmpty(localStorage.getItem(this.name + '_dirty')) || !_.isEmpty(localStorage.getItem(this.name + '_destroyed'));
    };
    Store.prototype.find = function(model) {
      return JSON.parse(localStorage.getItem(this.name + this.sep + model.id));
    };
    Store.prototype.findAll = function() {
      var id, _i, _len, _ref, _results;
      _ref = this.records;
      _results = [];
      for (_i = 0, _len = _ref.length; _i < _len; _i++) {
        id = _ref[_i];
        _results.push(JSON.parse(localStorage.getItem(this.name + this.sep + id)));
      }
      return _results;
    };
    Store.prototype.destroy = function(model) {
      localStorage.setItem(this.name + this.sep + model.id, null);
      this.records = _.reject(this.records, function(record_id) {
        return record_id === model.id.toString();
      });
      this.save();
      return model;
    };
    return Store;
  })();
  localsync = function(method, model, options) {
    var response, store;
    store = new Store(options.storeName);
    response = (function() {
      switch (method) {
        case 'read':
          if (model.id) {
            return store.find(model);
          } else {
            return store.findAll();
          }
          break;
        case 'hasDirtyOrDestroyed':
          return store.hasDirtyOrDestroyed();
        case 'clear':
          return store.clear();
        case 'create':
          model = store.create(model);
          if (options.dirty) {
            return store.dirty(model);
          }
          break;
        case 'update':
          store.update(model);
          if (options.dirty) {
            return store.dirty(model);
          } else {
            return store.clean(model, 'dirty');
          }
          break;
        case 'delete':
          store.destroy(model);
          if (options.dirty) {
            return store.destroyed(model);
          } else {
            if (model.id.toString().length === 36) {
              return store.clean(model, 'dirty');
            } else {
              return store.clean(model, 'destroyed');
            }
          }
      }
    })();
    if (!options.ignoreCallbacks) {
      if (response) {
        options.success(response);
      } else {
        options.error('Record not found');
      }
    }
    return response;
  };
  result = function(object, property) {
    var value;
    if (!object) {
      return null;
    }
    value = object[property];
    if (_.isFunction(value)) {
      return value.call(object);
    } else {
      return value;
    }
  };
  parseRemoteResponse = function(object, response) {
    if (!(object && object.parseBeforeLocalSave)) {
      return response;
    }
    if (_.isFunction(object.parseBeforeLocalSave)) {
      return object.parseBeforeLocalSave(response);
    }
  };
  onlineSync = Backbone.sync;
  dualsync = function(method, model, options) {
    var error, local, originalModel, remoteOptions, success;
    options.storeName = result(model.collection, 'url') || result(model, 'url');
    if (result(model, 'remote') || result(model.collection, 'remote')) {
      return onlineSync(method, model, options);
    }
    local = result(model, 'local') || result(model.collection, 'local');
    options.dirty = options.remote === false && !local;
    if (options.remote === false || local) {
      return localsync(method, model, options);
    }
    options.ignoreCallbacks = true;
    success = options.success;
    error = options.error;
    switch (method) {
      case 'read':
        if (localsync('hasDirtyOrDestroyed', model, options)) {
          return success(localsync(method, model, options));
        } else {
          remoteOptions = _(options).clone();
          remoteOptions.success = function(resp, status, xhr) {
            var i, _i, _len;
            resp = parseRemoteResponse(model, resp);
            localsync('clear', model, options);
            if (_.isArray(resp)) {
              for (_i = 0, _len = resp.length; _i < _len; _i++) {
                i = resp[_i];
                localsync('create', i, options);
              }
            } else {
              localsync('create', resp, options);
            }
            return success(resp, status, xhr);
          };
          options.ignoreCallbacks = false;
          options.success = function(resp) {
            success(resp);
            if (_.isUndefined(model.shouldRemoteSync) || (_.isFunction(model.shouldRemoteSync) && model.shouldRemoteSync())) {
              return onlineSync(method, model, remoteOptions);
            }
          };
          return localsync(method, model, options);
        }
        break;
      case 'create':
        options.success = function(resp, status, xhr) {
          localsync(method, resp, options);
          return success(resp, status, xhr);
        };
        options.error = function(resp) {
          options.dirty = true;
          return success(localsync(method, model, options));
        };
        return onlineSync(method, model, options);
      case 'update':
        if (_.isString(model.id) && model.id.length === 36) {
          originalModel = model.clone();
          options.success = function(resp, status, xhr) {
            localsync('delete', originalModel, options);
            localsync('create', resp, options);
            return success(resp, status, xhr);
          };
          options.error = function(resp) {
            options.dirty = true;
            return success(localsync(method, originalModel, options));
          };
          model.set({
            id: null
          });
          return onlineSync('create', model, options);
        } else {
          options.success = function(resp, status, xhr) {
            return success(localsync(method, model, options));
          };
          options.error = function(resp) {
            options.dirty = true;
            return success(localsync(method, model, options));
          };
          return onlineSync(method, model, options);
        }
        break;
      case 'delete':
        if (_.isString(model.id) && model.id.length === 36) {
          return localsync(method, model, options);
        } else {
          options.success = function(resp, status, xhr) {
            localsync(method, model, options);
            return success(resp, status, xhr);
          };
          options.error = function(resp) {
            options.dirty = true;
            return success(localsync(method, model, options));
          };
          return onlineSync(method, model, options);
        }
    }
  };
  Backbone.sync = dualsync;
}).call(this);
/* vim: set tabstop=4 softtabstop=4 shiftwidth=4 noexpandtab: */
/**
 * Backbone-relational.js 0.8.6
 * (c) 2011-2013 Paul Uithol and contributors (https://github.com/PaulUithol/Backbone-relational/graphs/contributors)
 *
 * Backbone-relational may be freely distributed under the MIT license; see the accompanying LICENSE.txt.
 * For details and documentation: https://github.com/PaulUithol/Backbone-relational.
 * Depends on Backbone (and thus on Underscore as well): https://github.com/documentcloud/backbone.
 */
( function( undefined ) {
	"use strict";

	/**
	 * CommonJS shim
	 **/
	var _, Backbone, exports;
	if ( typeof window === 'undefined' ) {
		_ = require( 'underscore' );
		Backbone = require( 'backbone' );
		exports = Backbone;
		typeof module === 'undefined' || ( module.exports = exports );
	}
	else {
		_ = window._;
		Backbone = window.Backbone;
		exports = window;
	}

	Backbone.Relational = {
		showWarnings: true
	};

	/**
	 * Semaphore mixin; can be used as both binary and counting.
	 **/
	Backbone.Semaphore = {
		_permitsAvailable: null,
		_permitsUsed: 0,

		acquire: function() {
			if ( this._permitsAvailable && this._permitsUsed >= this._permitsAvailable ) {
				throw new Error( 'Max permits acquired' );
			}
			else {
				this._permitsUsed++;
			}
		},

		release: function() {
			if ( this._permitsUsed === 0 ) {
				throw new Error( 'All permits released' );
			}
			else {
				this._permitsUsed--;
			}
		},

		isLocked: function() {
			return this._permitsUsed > 0;
		},

		setAvailablePermits: function( amount ) {
			if ( this._permitsUsed > amount ) {
				throw new Error( 'Available permits cannot be less than used permits' );
			}
			this._permitsAvailable = amount;
		}
	};

	/**
	 * A BlockingQueue that accumulates items while blocked (via 'block'),
	 * and processes them when unblocked (via 'unblock').
	 * Process can also be called manually (via 'process').
	 */
	Backbone.BlockingQueue = function() {
		this._queue = [];
	};
	_.extend( Backbone.BlockingQueue.prototype, Backbone.Semaphore, {
		_queue: null,

		add: function( func ) {
			if ( this.isBlocked() ) {
				this._queue.push( func );
			}
			else {
				func();
			}
		},

		// Some of the queued events may trigger other blocking events. By
		// copying the queue here it allows queued events to process closer to
		// the natural order.
		//
		// queue events [ 'A', 'B', 'C' ]
		// A handler of 'B' triggers 'D' and 'E'
		// By copying `this._queue` this executes:
		// [ 'A', 'B', 'D', 'E', 'C' ]
		// The same order the would have executed if they didn't have to be
		// delayed and queued.
		process: function() {
			var queue = this._queue;
			this._queue = [];
			while ( queue && queue.length ) {
				queue.shift()();
			}
		},

		block: function() {
			this.acquire();
		},

		unblock: function() {
			this.release();
			if ( !this.isBlocked() ) {
				this.process();
			}
		},

		isBlocked: function() {
			return this.isLocked();
		}
	});
	/**
	 * Global event queue. Accumulates external events ('add:<key>', 'remove:<key>' and 'change:<key>')
	 * until the top-level object is fully initialized (see 'Backbone.RelationalModel').
	 */
	Backbone.Relational.eventQueue = new Backbone.BlockingQueue();

	/**
	 * Backbone.Store keeps track of all created (and destruction of) Backbone.RelationalModel.
	 * Handles lookup for relations.
	 */
	Backbone.Store = function() {
		this._collections = [];
		this._reverseRelations = [];
		this._orphanRelations = [];
		this._subModels = [];
		this._modelScopes = [ exports ];
	};
	_.extend( Backbone.Store.prototype, Backbone.Events, {
		/**
		 * Create a new `Relation`.
		 * @param {Backbone.RelationalModel} [model]
		 * @param {Object} relation
		 * @param {Object} [options]
		 */
		initializeRelation: function( model, relation, options ) {
			var type = !_.isString( relation.type ) ? relation.type : Backbone[ relation.type ] || this.getObjectByName( relation.type );
			if ( type && type.prototype instanceof Backbone.Relation ) {
				new type( model, relation, options ); // Also pushes the new Relation into `model._relations`
			}
			else {
				Backbone.Relational.showWarnings && typeof console !== 'undefined' && console.warn( 'Relation=%o; missing or invalid relation type!', relation );
			}
		},

		/**
		 * Add a scope for `getObjectByName` to look for model types by name.
		 * @param {Object} scope
		 */
		addModelScope: function( scope ) {
			this._modelScopes.push( scope );
		},

		/**
		 * Remove a scope.
		 * @param {Object} scope
		 */
		removeModelScope: function( scope ) {
			this._modelScopes = _.without( this._modelScopes, scope );
		},

		/**
		 * Add a set of subModelTypes to the store, that can be used to resolve the '_superModel'
		 * for a model later in 'setupSuperModel'.
		 *
		 * @param {Backbone.RelationalModel} subModelTypes
		 * @param {Backbone.RelationalModel} superModelType
		 */
		addSubModels: function( subModelTypes, superModelType ) {
			this._subModels.push({
				'superModelType': superModelType,
				'subModels': subModelTypes
			});
		},

		/**
		 * Check if the given modelType is registered as another model's subModel. If so, add it to the super model's
		 * '_subModels', and set the modelType's '_superModel', '_subModelTypeName', and '_subModelTypeAttribute'.
		 *
		 * @param {Backbone.RelationalModel} modelType
		 */
		setupSuperModel: function( modelType ) {
			_.find( this._subModels, function( subModelDef ) {
				return _.find( subModelDef.subModels || [], function( subModelTypeName, typeValue ) {
					var subModelType = this.getObjectByName( subModelTypeName );

					if ( modelType === subModelType ) {
						// Set 'modelType' as a child of the found superModel
						subModelDef.superModelType._subModels[ typeValue ] = modelType;

						// Set '_superModel', '_subModelTypeValue', and '_subModelTypeAttribute' on 'modelType'.
						modelType._superModel = subModelDef.superModelType;
						modelType._subModelTypeValue = typeValue;
						modelType._subModelTypeAttribute = subModelDef.superModelType.prototype.subModelTypeAttribute;
						return true;
					}
				}, this );
			}, this );
		},

		/**
		 * Add a reverse relation. Is added to the 'relations' property on model's prototype, and to
		 * existing instances of 'model' in the store as well.
		 * @param {Object} relation
		 * @param {Backbone.RelationalModel} relation.model
		 * @param {String} relation.type
		 * @param {String} relation.key
		 * @param {String|Object} relation.relatedModel
		 */
		addReverseRelation: function( relation ) {
			var exists = _.any( this._reverseRelations, function( rel ) {
				return _.all( relation || [], function( val, key ) {
					return val === rel[ key ];
				});
			});

			if ( !exists && relation.model && relation.type ) {
				this._reverseRelations.push( relation );
				this._addRelation( relation.model, relation );
				this.retroFitRelation( relation );
			}
		},

		/**
		 * Deposit a `relation` for which the `relatedModel` can't be resolved at the moment.
		 *
		 * @param {Object} relation
		 */
		addOrphanRelation: function( relation ) {
			var exists = _.any( this._orphanRelations, function( rel ) {
				return _.all( relation || [], function( val, key ) {
					return val === rel[ key ];
				});
			});

			if ( !exists && relation.model && relation.type ) {
				this._orphanRelations.push( relation );
			}
		},

		/**
		 * Try to initialize any `_orphanRelation`s
		 */
		processOrphanRelations: function() {
			// Make sure to operate on a copy since we're removing while iterating
			_.each( this._orphanRelations.slice( 0 ), function( rel ) {
				var relatedModel = Backbone.Relational.store.getObjectByName( rel.relatedModel );
				if ( relatedModel ) {
					this.initializeRelation( null, rel );
					this._orphanRelations = _.without( this._orphanRelations, rel );
				}
			}, this );
		},

		/**
		 *
		 * @param {Backbone.RelationalModel.constructor} type
		 * @param {Object} relation
		 * @private
		 */
		_addRelation: function( type, relation ) {
			if ( !type.prototype.relations ) {
				type.prototype.relations = [];
			}
			type.prototype.relations.push( relation );

			_.each( type._subModels || [], function( subModel ) {
				this._addRelation( subModel, relation );
			}, this );
		},

		/**
		 * Add a 'relation' to all existing instances of 'relation.model' in the store
		 * @param {Object} relation
		 */
		retroFitRelation: function( relation ) {
			var coll = this.getCollection( relation.model, false );
			coll && coll.each( function( model ) {
				if ( !( model instanceof relation.model ) ) {
					return;
				}

				new relation.type( model, relation );
			}, this );
		},

		/**
		 * Find the Store's collection for a certain type of model.
		 * @param {Backbone.RelationalModel} type
		 * @param {Boolean} [create=true] Should a collection be created if none is found?
		 * @return {Backbone.Collection} A collection if found (or applicable for 'model'), or null
		 */
		getCollection: function( type, create ) {
			if ( type instanceof Backbone.RelationalModel ) {
				type = type.constructor;
			}

			var rootModel = type;
			while ( rootModel._superModel ) {
				rootModel = rootModel._superModel;
			}

			var coll = _.find( this._collections, function(item) {
				return item.model === rootModel;
			});

			if ( !coll && create !== false ) {
				coll = this._createCollection( rootModel );
			}

			return coll;
		},

		/**
		 * Find a model type on one of the modelScopes by name. Names are split on dots.
		 * @param {String} name
		 * @return {Object}
		 */
		getObjectByName: function( name ) {
			var parts = name.split( '.' ),
				type = null;

			_.find( this._modelScopes, function( scope ) {
				type = _.reduce( parts || [], function( memo, val ) {
					return memo ? memo[ val ] : undefined;
				}, scope );

				if ( type && type !== scope ) {
					return true;
				}
			}, this );

			return type;
		},

		_createCollection: function( type ) {
			var coll;

			// If 'type' is an instance, take its constructor
			if ( type instanceof Backbone.RelationalModel ) {
				type = type.constructor;
			}

			// Type should inherit from Backbone.RelationalModel.
			if ( type.prototype instanceof Backbone.RelationalModel ) {
				coll = new Backbone.Collection();
				coll.model = type;

				this._collections.push( coll );
			}

			return coll;
		},

		/**
		 * Find the attribute that is to be used as the `id` on a given object
		 * @param type
		 * @param {String|Number|Object|Backbone.RelationalModel} item
		 * @return {String|Number}
		 */
		resolveIdForItem: function( type, item ) {
			var id = _.isString( item ) || _.isNumber( item ) ? item : null;

			if ( id === null ) {
				if ( item instanceof Backbone.RelationalModel ) {
					id = item.id;
				}
				else if ( _.isObject( item ) ) {
					id = item[ type.prototype.idAttribute ];
				}
			}

			// Make all falsy values `null` (except for 0, which could be an id.. see '/issues/179')
			if ( !id && id !== 0 ) {
				id = null;
			}

			return id;
		},

		/**
		 * Find a specific model of a certain `type` in the store
		 * @param type
		 * @param {String|Number|Object|Backbone.RelationalModel} item
		 */
		find: function( type, item ) {
			var id = this.resolveIdForItem( type, item );
			var coll = this.getCollection( type );

			// Because the found object could be of any of the type's superModel
			// types, only return it if it's actually of the type asked for.
			if ( coll ) {
				var obj = coll.get( id );

				if ( obj instanceof type ) {
					return obj;
				}
			}

			return null;
		},

		/**
		 * Add a 'model' to its appropriate collection. Retain the original contents of 'model.collection'.
		 * @param {Backbone.RelationalModel} model
		 */
		register: function( model ) {
			var coll = this.getCollection( model );

			if ( coll ) {
				var modelColl = model.collection;
				coll.add( model );
				this.listenTo( model, 'destroy', this.unregister, this );
				this.listenTo( model, 'relational:unregister', this.unregister, this );
				model.collection = modelColl;
			}
		},

		/**
		 * Check if the given model may use the given `id`
		 * @param model
		 * @param [id]
		 */
		checkId: function( model, id ) {
			var coll = this.getCollection( model ),
				duplicate = coll && coll.get( id );

			if ( duplicate && model !== duplicate ) {
				if ( Backbone.Relational.showWarnings && typeof console !== 'undefined' ) {
					console.warn( 'Duplicate id! Old RelationalModel=%o, new RelationalModel=%o', duplicate, model );
				}

				throw new Error( "Cannot instantiate more than one Backbone.RelationalModel with the same id per type!" );
			}
		},

		/**
		 * Explicitly update a model's id in its store collection
		 * @param {Backbone.RelationalModel} model
		 */
		update: function( model ) {
			var coll = this.getCollection( model );
			// This triggers updating the lookup indices kept in a collection
			coll._onModelEvent( 'change:' + model.idAttribute, model, coll );

			// Trigger an event on model so related models (having the model's new id in their keyContents) can add it.
			model.trigger( 'relational:change:id', model, coll );
		},

		/**
		 * Remove a 'model' from the store.
		 * @param {Backbone.RelationalModel} model
		 */
		unregister: function( model, collection, options ) {
			this.stopListening( model );
			var coll = this.getCollection( model );
			coll && coll.remove( model, options );
		},

		/**
		 * Reset the `store` to it's original state. The `reverseRelations` are kept though, since attempting to
		 * re-initialize these on models would lead to a large amount of warnings.
		 */
		reset: function() {
			this.stopListening();
			this._collections = [];
			this._subModels = [];
			this._modelScopes = [ exports ];
		}
	});
	Backbone.Relational.store = new Backbone.Store();

	/**
	 * The main Relation class, from which 'HasOne' and 'HasMany' inherit. Internally, 'relational:<key>' events
	 * are used to regulate addition and removal of models from relations.
	 *
	 * @param {Backbone.RelationalModel} [instance] Model that this relation is created for. If no model is supplied,
	 *      Relation just tries to instantiate it's `reverseRelation` if specified, and bails out after that.
	 * @param {Object} options
	 * @param {string} options.key
	 * @param {Backbone.RelationalModel.constructor} options.relatedModel
	 * @param {Boolean|String} [options.includeInJSON=true] Serialize the given attribute for related model(s)' in toJSON, or just their ids.
	 * @param {Boolean} [options.createModels=true] Create objects from the contents of keys if the object is not found in Backbone.store.
	 * @param {Object} [options.reverseRelation] Specify a bi-directional relation. If provided, Relation will reciprocate
	 *    the relation to the 'relatedModel'. Required and optional properties match 'options', except that it also needs
	 *    {Backbone.Relation|String} type ('HasOne' or 'HasMany').
	 * @param {Object} opts
	 */
	Backbone.Relation = function( instance, options, opts ) {
		this.instance = instance;
		// Make sure 'options' is sane, and fill with defaults from subclasses and this object's prototype
		options = _.isObject( options ) ? options : {};
		this.reverseRelation = _.defaults( options.reverseRelation || {}, this.options.reverseRelation );
		this.options = _.defaults( options, this.options, Backbone.Relation.prototype.options );

		this.reverseRelation.type = !_.isString( this.reverseRelation.type ) ? this.reverseRelation.type :
			Backbone[ this.reverseRelation.type ] || Backbone.Relational.store.getObjectByName( this.reverseRelation.type );

		this.key = this.options.key;
		this.keySource = this.options.keySource || this.key;
		this.keyDestination = this.options.keyDestination || this.keySource || this.key;

		this.model = this.options.model || this.instance.constructor;

		this.relatedModel = this.options.relatedModel;

		if ( _.isFunction( this.relatedModel ) && !( this.relatedModel.prototype instanceof Backbone.RelationalModel ) ) {
			this.relatedModel = _.result( this, 'relatedModel' );
		}
		if ( _.isString( this.relatedModel ) ) {
			this.relatedModel = Backbone.Relational.store.getObjectByName( this.relatedModel );
		}

		if ( !this.checkPreconditions() ) {
			return;
		}

		// Add the reverse relation on 'relatedModel' to the store's reverseRelations
		if ( !this.options.isAutoRelation && this.reverseRelation.type && this.reverseRelation.key ) {
			Backbone.Relational.store.addReverseRelation( _.defaults( {
					isAutoRelation: true,
					model: this.relatedModel,
					relatedModel: this.model,
					reverseRelation: this.options // current relation is the 'reverseRelation' for its own reverseRelation
				},
				this.reverseRelation // Take further properties from this.reverseRelation (type, key, etc.)
			) );
		}

		if ( instance ) {
			var contentKey = this.keySource;
			if ( contentKey !== this.key && typeof this.instance.get( this.key ) === 'object' ) {
				contentKey = this.key;
			}

			this.setKeyContents( this.instance.get( contentKey ) );
			this.relatedCollection = Backbone.Relational.store.getCollection( this.relatedModel );

			// Explicitly clear 'keySource', to prevent a leaky abstraction if 'keySource' differs from 'key'.
			if ( this.keySource !== this.key ) {
				delete this.instance.attributes[ this.keySource ];
			}

			// Add this Relation to instance._relations
			this.instance._relations[ this.key ] = this;

			this.initialize( opts );

			if ( this.options.autoFetch ) {
				this.instance.fetchRelated( this.key, _.isObject( this.options.autoFetch ) ? this.options.autoFetch : {} );
			}

			// When 'relatedModel' are created or destroyed, check if it affects this relation.
			this.listenTo( this.instance, 'destroy', this.destroy )
				.listenTo( this.relatedCollection, 'relational:add relational:change:id', this.tryAddRelated )
				.listenTo( this.relatedCollection, 'relational:remove', this.removeRelated )
		}
	};
	// Fix inheritance :\
	Backbone.Relation.extend = Backbone.Model.extend;
	// Set up all inheritable **Backbone.Relation** properties and methods.
	_.extend( Backbone.Relation.prototype, Backbone.Events, Backbone.Semaphore, {
		options: {
			createModels: true,
			includeInJSON: true,
			isAutoRelation: false,
			autoFetch: false,
			parse: false
		},

		instance: null,
		key: null,
		keyContents: null,
		relatedModel: null,
		relatedCollection: null,
		reverseRelation: null,
		related: null,

		/**
		 * Check several pre-conditions.
		 * @return {Boolean} True if pre-conditions are satisfied, false if they're not.
		 */
		checkPreconditions: function() {
			var i = this.instance,
				k = this.key,
				m = this.model,
				rm = this.relatedModel,
				warn = Backbone.Relational.showWarnings && typeof console !== 'undefined';

			if ( !m || !k || !rm ) {
				warn && console.warn( 'Relation=%o: missing model, key or relatedModel (%o, %o, %o).', this, m, k, rm );
				return false;
			}
			// Check if the type in 'model' inherits from Backbone.RelationalModel
			if ( !( m.prototype instanceof Backbone.RelationalModel ) ) {
				warn && console.warn( 'Relation=%o: model does not inherit from Backbone.RelationalModel (%o).', this, i );
				return false;
			}
			// Check if the type in 'relatedModel' inherits from Backbone.RelationalModel
			if ( !( rm.prototype instanceof Backbone.RelationalModel ) ) {
				warn && console.warn( 'Relation=%o: relatedModel does not inherit from Backbone.RelationalModel (%o).', this, rm );
				return false;
			}
			// Check if this is not a HasMany, and the reverse relation is HasMany as well
			if ( this instanceof Backbone.HasMany && this.reverseRelation.type === Backbone.HasMany ) {
				warn && console.warn( 'Relation=%o: relation is a HasMany, and the reverseRelation is HasMany as well.', this );
				return false;
			}
			// Check if we're not attempting to create a relationship on a `key` that's already used.
			if ( i && _.keys( i._relations ).length ) {
				var existing = _.find( i._relations, function( rel ) {
					return rel.key === k;
				}, this );

				if ( existing ) {
					warn && console.warn( 'Cannot create relation=%o on %o for model=%o: already taken by relation=%o.',
						this, k, i, existing );
					return false;
				}
			}

			return true;
		},

		/**
		 * Set the related model(s) for this relation
		 * @param {Backbone.Model|Backbone.Collection} related
		 */
		setRelated: function( related ) {
			this.related = related;

			this.instance.acquire();
			this.instance.attributes[ this.key ] = related;
			this.instance.release();
		},

		/**
		 * Determine if a relation (on a different RelationalModel) is the reverse
		 * relation of the current one.
		 * @param {Backbone.Relation} relation
		 * @return {Boolean}
		 */
		_isReverseRelation: function( relation ) {
			return relation.instance instanceof this.relatedModel && this.reverseRelation.key === relation.key &&
				this.key === relation.reverseRelation.key;
		},

		/**
		 * Get the reverse relations (pointing back to 'this.key' on 'this.instance') for the currently related model(s).
		 * @param {Backbone.RelationalModel} [model] Get the reverse relations for a specific model.
		 *    If not specified, 'this.related' is used.
		 * @return {Backbone.Relation[]}
		 */
		getReverseRelations: function( model ) {
			var reverseRelations = [];
			// Iterate over 'model', 'this.related.models' (if this.related is a Backbone.Collection), or wrap 'this.related' in an array.
			var models = !_.isUndefined( model ) ? [ model ] : this.related && ( this.related.models || [ this.related ] );
			_.each( models || [], function( related ) {
				_.each( related.getRelations() || [], function( relation ) {
						if ( this._isReverseRelation( relation ) ) {
							reverseRelations.push( relation );
						}
					}, this );
			}, this );

			return reverseRelations;
		},

		/**
		 * When `this.instance` is destroyed, cleanup our relations.
		 * Get reverse relation, call removeRelated on each.
		 */
		destroy: function() {
			this.stopListening();

			if ( this instanceof Backbone.HasOne ) {
				this.setRelated( null );
			}
			else if ( this instanceof Backbone.HasMany ) {
				this.setRelated( this._prepareCollection() );
			}

			_.each( this.getReverseRelations(), function( relation ) {
				relation.removeRelated( this.instance );
			}, this );
		}
	});

	Backbone.HasOne = Backbone.Relation.extend({
		options: {
			reverseRelation: { type: 'HasMany' }
		},

		initialize: function( opts ) {
			this.listenTo( this.instance, 'relational:change:' + this.key, this.onChange );

			var related = this.findRelated( opts );
			this.setRelated( related );

			// Notify new 'related' object of the new relation.
			_.each( this.getReverseRelations(), function( relation ) {
				relation.addRelated( this.instance, opts );
			}, this );
		},

		/**
		 * Find related Models.
		 * @param {Object} [options]
		 * @return {Backbone.Model}
		 */
		findRelated: function( options ) {
			var related = null;

			options = _.defaults( { parse: this.options.parse }, options );

			if ( this.keyContents instanceof this.relatedModel ) {
				related = this.keyContents;
			}
			else if ( this.keyContents || this.keyContents === 0 ) { // since 0 can be a valid `id` as well
				var opts = _.defaults( { create: this.options.createModels }, options );
				related = this.relatedModel.findOrCreate( this.keyContents, opts );
			}

			// Nullify `keyId` if we have a related model; in case it was already part of the relation
			if ( this.related ) {
				this.keyId = null;
			}

			return related;
		},

		/**
		 * Normalize and reduce `keyContents` to an `id`, for easier comparison
		 * @param {String|Number|Backbone.Model} keyContents
		 */
		setKeyContents: function( keyContents ) {
			this.keyContents = keyContents;
			this.keyId = Backbone.Relational.store.resolveIdForItem( this.relatedModel, this.keyContents );
		},

		/**
		 * Event handler for `change:<key>`.
		 * If the key is changed, notify old & new reverse relations and initialize the new relation.
		 */
		onChange: function( model, attr, options ) {
			// Don't accept recursive calls to onChange (like onChange->findRelated->findOrCreate->initializeRelations->addRelated->onChange)
			if ( this.isLocked() ) {
				return;
			}
			this.acquire();
			options = options ? _.clone( options ) : {};

			// 'options.__related' is set by 'addRelated'/'removeRelated'. If it is set, the change
			// is the result of a call from a relation. If it's not, the change is the result of
			// a 'set' call on this.instance.
			var changed = _.isUndefined( options.__related ),
				oldRelated = changed ? this.related : options.__related;

			if ( changed ) {
				this.setKeyContents( attr );
				var related = this.findRelated( options );
				this.setRelated( related );
			}

			// Notify old 'related' object of the terminated relation
			if ( oldRelated && this.related !== oldRelated ) {
				_.each( this.getReverseRelations( oldRelated ), function( relation ) {
					relation.removeRelated( this.instance, null, options );
				}, this );
			}

			// Notify new 'related' object of the new relation. Note we do re-apply even if this.related is oldRelated;
			// that can be necessary for bi-directional relations if 'this.instance' was created after 'this.related'.
			// In that case, 'this.instance' will already know 'this.related', but the reverse might not exist yet.
			_.each( this.getReverseRelations(), function( relation ) {
				relation.addRelated( this.instance, options );
			}, this );

			// Fire the 'change:<key>' event if 'related' was updated
			if ( !options.silent && this.related !== oldRelated ) {
				var dit = this;
				this.changed = true;
				Backbone.Relational.eventQueue.add( function() {
					dit.instance.trigger( 'change:' + dit.key, dit.instance, dit.related, options, true );
					dit.changed = false;
				});
			}
			this.release();
		},

		/**
		 * If a new 'this.relatedModel' appears in the 'store', try to match it to the last set 'keyContents'
		 */
		tryAddRelated: function( model, coll, options ) {
			if ( ( this.keyId || this.keyId === 0 ) && model.id === this.keyId ) { // since 0 can be a valid `id` as well
				this.addRelated( model, options );
				this.keyId = null;
			}
		},

		addRelated: function( model, options ) {
			// Allow 'model' to set up its relations before proceeding.
			// (which can result in a call to 'addRelated' from a relation of 'model')
			var dit = this;
			model.queue( function() {
				if ( model !== dit.related ) {
					var oldRelated = dit.related || null;
					dit.setRelated( model );
					dit.onChange( dit.instance, model, _.defaults( { __related: oldRelated }, options ) );
				}
			});
		},

		removeRelated: function( model, coll, options ) {
			if ( !this.related ) {
				return;
			}

			if ( model === this.related ) {
				var oldRelated = this.related || null;
				this.setRelated( null );
				this.onChange( this.instance, model, _.defaults( { __related: oldRelated }, options ) );
			}
		}
	});

	Backbone.HasMany = Backbone.Relation.extend({
		collectionType: null,

		options: {
			reverseRelation: { type: 'HasOne' },
			collectionType: Backbone.Collection,
			collectionKey: true,
			collectionOptions: {}
		},

		initialize: function( opts ) {
			this.listenTo( this.instance, 'relational:change:' + this.key, this.onChange );

			// Handle a custom 'collectionType'
			this.collectionType = this.options.collectionType;
			if ( _.isFunction( this.collectionType ) && this.collectionType !== Backbone.Collection && !( this.collectionType.prototype instanceof Backbone.Collection ) ) {
				this.collectionType = _.result( this, 'collectionType' );
			}
			if ( _.isString( this.collectionType ) ) {
				this.collectionType = Backbone.Relational.store.getObjectByName( this.collectionType );
			}
			if ( this.collectionType !== Backbone.Collection && !( this.collectionType.prototype instanceof Backbone.Collection ) ) {
				throw new Error( '`collectionType` must inherit from Backbone.Collection' );
			}

			var related = this.findRelated( opts );
			this.setRelated( related );
		},

		/**
		 * Bind events and setup collectionKeys for a collection that is to be used as the backing store for a HasMany.
		 * If no 'collection' is supplied, a new collection will be created of the specified 'collectionType' option.
		 * @param {Backbone.Collection} [collection]
		 * @return {Backbone.Collection}
		 */
		_prepareCollection: function( collection ) {
			if ( this.related ) {
				this.stopListening( this.related );
			}

			if ( !collection || !( collection instanceof Backbone.Collection ) ) {
				var options = _.isFunction( this.options.collectionOptions ) ?
					this.options.collectionOptions( this.instance ) : this.options.collectionOptions;

				collection = new this.collectionType( null, options );
			}

			collection.model = this.relatedModel;

			if ( this.options.collectionKey ) {
				var key = this.options.collectionKey === true ? this.options.reverseRelation.key : this.options.collectionKey;

				if ( collection[ key ] && collection[ key ] !== this.instance ) {
					if ( Backbone.Relational.showWarnings && typeof console !== 'undefined' ) {
						console.warn( 'Relation=%o; collectionKey=%s already exists on collection=%o', this, key, this.options.collectionKey );
					}
				}
				else if ( key ) {
					collection[ key ] = this.instance;
				}
			}

			this.listenTo( collection, 'relational:add', this.handleAddition )
				.listenTo( collection, 'relational:remove', this.handleRemoval )
				.listenTo( collection, 'relational:reset', this.handleReset );

			return collection;
		},

		/**
		 * Find related Models.
		 * @param {Object} [options]
		 * @return {Backbone.Collection}
		 */
		findRelated: function( options ) {
			var related = null;

			options = _.defaults( { parse: this.options.parse }, options );

			// Replace 'this.related' by 'this.keyContents' if it is a Backbone.Collection
			if ( this.keyContents instanceof Backbone.Collection ) {
				this._prepareCollection( this.keyContents );
				related = this.keyContents;
			}
			// Otherwise, 'this.keyContents' should be an array of related object ids.
			// Re-use the current 'this.related' if it is a Backbone.Collection; otherwise, create a new collection.
			else {
				var toAdd = [];

				_.each( this.keyContents, function( attributes ) {
					if ( attributes instanceof this.relatedModel ) {
						var model = attributes;
					}
					else {
						// If `merge` is true, update models here, instead of during update.
						model = this.relatedModel.findOrCreate( attributes,
							_.extend( { merge: true }, options, { create: this.options.createModels } )
						);
					}

					model && toAdd.push( model );
				}, this );

				if ( this.related instanceof Backbone.Collection ) {
					related = this.related;
				}
				else {
					related = this._prepareCollection();
				}

				// By now, both `merge` and `parse` will already have been executed for models if they were specified.
				// Disable them to prevent additional calls.
				related.set( toAdd, _.defaults( { merge: false, parse: false }, options ) );
			}

			// Remove entries from `keyIds` that were already part of the relation (and are thus 'unchanged')
			this.keyIds = _.difference( this.keyIds, _.pluck( related.models, 'id' ) );

			return related;
		},

		/**
		 * Normalize and reduce `keyContents` to a list of `ids`, for easier comparison
		 * @param {String|Number|String[]|Number[]|Backbone.Collection} keyContents
		 */
		setKeyContents: function( keyContents ) {
			this.keyContents = keyContents instanceof Backbone.Collection ? keyContents : null;
			this.keyIds = [];

			if ( !this.keyContents && ( keyContents || keyContents === 0 ) ) { // since 0 can be a valid `id` as well
				// Handle cases the an API/user supplies just an Object/id instead of an Array
				this.keyContents = _.isArray( keyContents ) ? keyContents : [ keyContents ];

				_.each( this.keyContents, function( item ) {
					var itemId = Backbone.Relational.store.resolveIdForItem( this.relatedModel, item );
					if ( itemId || itemId === 0 ) {
						this.keyIds.push( itemId );
					}
				}, this );
			}
		},

		/**
		 * Event handler for `change:<key>`.
		 * If the contents of the key are changed, notify old & new reverse relations and initialize the new relation.
		 */
		onChange: function( model, attr, options ) {
			options = options ? _.clone( options ) : {};
			this.setKeyContents( attr );
			this.changed = false;

			var related = this.findRelated( options );
			this.setRelated( related );

			if ( !options.silent ) {
				var dit = this;
				Backbone.Relational.eventQueue.add( function() {
					// The `changed` flag can be set in `handleAddition` or `handleRemoval`
					if ( dit.changed ) {
						dit.instance.trigger( 'change:' + dit.key, dit.instance, dit.related, options, true );
						dit.changed = false;
					}
				});
			}
		},

		/**
		 * When a model is added to a 'HasMany', trigger 'add' on 'this.instance' and notify reverse relations.
		 * (should be 'HasOne', must set 'this.instance' as their related).
		*/
		handleAddition: function( model, coll, options ) {
			//console.debug('handleAddition called; args=%o', arguments);
			options = options ? _.clone( options ) : {};
			this.changed = true;

			_.each( this.getReverseRelations( model ), function( relation ) {
				relation.addRelated( this.instance, options );
			}, this );

			// Only trigger 'add' once the newly added model is initialized (so, has its relations set up)
			var dit = this;
			!options.silent && Backbone.Relational.eventQueue.add( function() {
				dit.instance.trigger( 'add:' + dit.key, model, dit.related, options );
			});
		},

		/**
		 * When a model is removed from a 'HasMany', trigger 'remove' on 'this.instance' and notify reverse relations.
		 * (should be 'HasOne', which should be nullified)
		 */
		handleRemoval: function( model, coll, options ) {
			//console.debug('handleRemoval called; args=%o', arguments);
			options = options ? _.clone( options ) : {};
			this.changed = true;

			_.each( this.getReverseRelations( model ), function( relation ) {
				relation.removeRelated( this.instance, null, options );
			}, this );

			var dit = this;
			!options.silent && Backbone.Relational.eventQueue.add( function() {
				dit.instance.trigger( 'remove:' + dit.key, model, dit.related, options );
			});
		},

		handleReset: function( coll, options ) {
			var dit = this;
			options = options ? _.clone( options ) : {};
			!options.silent && Backbone.Relational.eventQueue.add( function() {
				dit.instance.trigger( 'reset:' + dit.key, dit.related, options );
			});
		},

		tryAddRelated: function( model, coll, options ) {
			var item = _.contains( this.keyIds, model.id );

			if ( item ) {
				this.addRelated( model, options );
				this.keyIds = _.without( this.keyIds, model.id );
			}
		},

		addRelated: function( model, options ) {
			// Allow 'model' to set up its relations before proceeding.
			// (which can result in a call to 'addRelated' from a relation of 'model')
			var dit = this;
			model.queue( function() {
				if ( dit.related && !dit.related.get( model ) ) {
					dit.related.add( model, _.defaults( { parse: false }, options ) );
				}
			});
		},

		removeRelated: function( model, coll, options ) {
			if ( this.related.get( model ) ) {
				this.related.remove( model, options );
			}
		}
	});

	/**
	 * A type of Backbone.Model that also maintains relations to other models and collections.
	 * New events when compared to the original:
	 *  - 'add:<key>' (model, related collection, options)
	 *  - 'remove:<key>' (model, related collection, options)
	 *  - 'change:<key>' (model, related model or collection, options)
	 */
	Backbone.RelationalModel = Backbone.Model.extend({
		relations: null, // Relation descriptions on the prototype
		_relations: null, // Relation instances
		_isInitialized: false,
		_deferProcessing: false,
		_queue: null,
		_attributeChangeFired: false, // Keeps track of `change` event firing under some conditions (like nested `set`s)

		subModelTypeAttribute: 'type',
		subModelTypes: null,

		constructor: function( attributes, options ) {
			// Nasty hack, for cases like 'model.get( <HasMany key> ).add( item )'.
			// Defer 'processQueue', so that when 'Relation.createModels' is used we trigger 'HasMany'
			// collection events only after the model is really fully set up.
			// Example: event for "p.on( 'add:jobs' )" -> "p.get('jobs').add( { company: c.id, person: p.id } )".
			if ( options && options.collection ) {
				var dit = this,
					collection = this.collection =  options.collection;

				// Prevent `collection` from cascading down to nested models; they shouldn't go into this `if` clause.
				delete options.collection;

				this._deferProcessing = true;

				var processQueue = function( model ) {
					if ( model === dit ) {
						dit._deferProcessing = false;
						dit.processQueue();
						collection.off( 'relational:add', processQueue );
					}
				};
				collection.on( 'relational:add', processQueue );

				// So we do process the queue eventually, regardless of whether this model actually gets added to 'options.collection'.
				_.defer( function() {
					processQueue( dit );
				});
			}

			Backbone.Relational.store.processOrphanRelations();

			this._queue = new Backbone.BlockingQueue();
			this._queue.block();
			Backbone.Relational.eventQueue.block();

			try {
				Backbone.Model.apply( this, arguments );
			}
			finally {
				// Try to run the global queue holding external events
				Backbone.Relational.eventQueue.unblock();
			}
		},

		/**
		 * Override 'trigger' to queue 'change' and 'change:*' events
		 */
		trigger: function( eventName ) {
			if ( eventName.length > 5 && eventName.indexOf( 'change' ) === 0 ) {
				var dit = this,
					args = arguments;

				Backbone.Relational.eventQueue.add( function() {
					if ( !dit._isInitialized ) {
						return;
					}

					// Determine if the `change` event is still valid, now that all relations are populated
					var changed = true;
					if ( eventName === 'change' ) {
						// `hasChanged` may have gotten reset by nested calls to `set`.
						changed = dit.hasChanged() || dit._attributeChangeFired;
						dit._attributeChangeFired = false;
					}
					else {
						var attr = eventName.slice( 7 ),
							rel = dit.getRelation( attr );

						if ( rel ) {
							// If `attr` is a relation, `change:attr` get triggered from `Relation.onChange`.
							// These take precedence over `change:attr` events triggered by `Model.set`.
							// The relation sets a fourth attribute to `true`. If this attribute is present,
							// continue triggering this event; otherwise, it's from `Model.set` and should be stopped.
							changed = ( args[ 4 ] === true );

							// If this event was triggered by a relation, set the right value in `this.changed`
							// (a Collection or Model instead of raw data).
							if ( changed ) {
								dit.changed[ attr ] = args[ 2 ];
							}
							// Otherwise, this event is from `Model.set`. If the relation doesn't report a change,
							// remove attr from `dit.changed` so `hasChanged` doesn't take it into account.
							else if ( !rel.changed ) {
								delete dit.changed[ attr ];
							}
						}
						else if ( changed ) {
							dit._attributeChangeFired = true;
						}
					}

					changed && Backbone.Model.prototype.trigger.apply( dit, args );
				});
			}
			else {
				Backbone.Model.prototype.trigger.apply( this, arguments );
			}

			return this;
		},

		/**
		 * Initialize Relations present in this.relations; determine the type (HasOne/HasMany), then creates a new instance.
		 * Invoked in the first call so 'set' (which is made from the Backbone.Model constructor).
		 */
		initializeRelations: function( options ) {
			this.acquire(); // Setting up relations often also involve calls to 'set', and we only want to enter this function once
			this._relations = {};

			_.each( _.result( this, 'relations' ) || [], function( rel ) {
				Backbone.Relational.store.initializeRelation( this, rel, options );
			}, this );

			this._isInitialized = true;
			this.release();
			this.processQueue();
		},

		/**
		 * When new values are set, notify this model's relations (also if options.silent is set).
		 * (Relation.setRelated locks this model before calling 'set' on it to prevent loops)
		 */
		updateRelations: function( options ) {
			if ( this._isInitialized && !this.isLocked() ) {
				_.each( this._relations, function( rel ) {
					// Update from data in `rel.keySource` if data got set in there, or `rel.key` otherwise
					var val = this.attributes[ rel.keySource ] || this.attributes[ rel.key ];
					if ( rel.related !== val ) {
						this.trigger( 'relational:change:' + rel.key, this, val, options || {} );
					}

					// Explicitly clear 'keySource', to prevent a leaky abstraction if 'keySource' differs from 'key'.
					if ( rel.keySource !== rel.key ) {
						delete rel.instance.attributes[ rel.keySource ];
					}
				}, this );
			}
		},

		/**
		 * Either add to the queue (if we're not initialized yet), or execute right away.
		 */
		queue: function( func ) {
			this._queue.add( func );
		},

		/**
		 * Process _queue
		 */
		processQueue: function() {
			if ( this._isInitialized && !this._deferProcessing && this._queue.isBlocked() ) {
				this._queue.unblock();
			}
		},

		/**
		 * Get a specific relation.
		 * @param key {string} The relation key to look for.
		 * @return {Backbone.Relation} An instance of 'Backbone.Relation', if a relation was found for 'key', or null.
		 */
		getRelation: function( key ) {
			return this._relations[ key ];
		},

		/**
		 * Get all of the created relations.
		 * @return {Backbone.Relation[]}
		 */
		getRelations: function() {
			return _.values( this._relations );
		},

		/**
		 * Retrieve related objects.
		 * @param key {string} The relation key to fetch models for.
		 * @param [options] {Object} Options for 'Backbone.Model.fetch' and 'Backbone.sync'.
		 * @param [refresh=false] {boolean} Fetch existing models from the server as well (in order to update them).
		 * @return {jQuery.when[]} An array of request objects
		 */
		fetchRelated: function( key, options, refresh ) {
			// Set default `options` for fetch
			options = _.extend( { update: true, remove: false }, options );

			var setUrl,
				requests = [],
				rel = this.getRelation( key ),
				idsToFetch = rel && ( ( rel.keyIds && rel.keyIds.slice( 0 ) ) || ( ( rel.keyId || rel.keyId === 0 ) ? [ rel.keyId ] : [] ) );

			// On `refresh`, add the ids for current models in the relation to `idsToFetch`
			if ( refresh ) {
				var models = rel.related instanceof Backbone.Collection ? rel.related.models : [ rel.related ];
				_.each( models, function( model ) {
					if ( model.id || model.id === 0 ) {
						idsToFetch.push( model.id );
					}
				});
			}

			if ( idsToFetch && idsToFetch.length ) {
				// Find (or create) a model for each one that is to be fetched
				var created = [],
					models = _.map( idsToFetch, function( id ) {
						var model = Backbone.Relational.store.find( rel.relatedModel, id );

						if ( !model ) {
							var attrs = {};
							attrs[ rel.relatedModel.prototype.idAttribute ] = id;
							model = rel.relatedModel.findOrCreate( attrs, options );
							created.push( model );
						}

						return model;
					}, this );

				// Try if the 'collection' can provide a url to fetch a set of models in one request.
				if ( rel.related instanceof Backbone.Collection && _.isFunction( rel.related.url ) ) {
					setUrl = rel.related.url( models );
				}

				// An assumption is that when 'Backbone.Collection.url' is a function, it can handle building of set urls.
				// To make sure it can, test if the url we got by supplying a list of models to fetch is different from
				// the one supplied for the default fetch action (without args to 'url').
				if ( setUrl && setUrl !== rel.related.url() ) {
					var opts = _.defaults(
						{
							error: function() {
								var args = arguments;
								_.each( created, function( model ) {
									model.trigger( 'destroy', model, model.collection, options );
									options.error && options.error.apply( model, args );
								});
							},
							url: setUrl
						},
						options
					);

					requests = [ rel.related.fetch( opts ) ];
				}
				else {
					requests = _.map( models, function( model ) {
						var opts = _.defaults(
							{
								error: function() {
									if ( _.contains( created, model ) ) {
										model.trigger( 'destroy', model, model.collection, options );
										options.error && options.error.apply( model, arguments );
									}
								}
							},
							options
						);
						return model.fetch( opts );
					}, this );
				}
			}

			return requests;
		},

		get: function( attr ) {
			var originalResult = Backbone.Model.prototype.get.call( this, attr );

			// Use `originalResult` get if dotNotation not enabled or not required because no dot is in `attr`
			if ( !this.dotNotation || attr.indexOf( '.' ) === -1 ) {
				return originalResult;
			}

			// Go through all splits and return the final result
			var splits = attr.split( '.' );
			var result = _.reduce(splits, function( model, split ) {
				if ( _.isNull(model) || _.isUndefined( model ) ) {
					// Return undefined if the path cannot be expanded
					return undefined;
				}
				else if ( model instanceof Backbone.Model ) {
					return Backbone.Model.prototype.get.call( model, split );
				}
				else if ( model instanceof Backbone.Collection ) {
					return Backbone.Collection.prototype.at.call( model, split )
				}
				else {
					throw new Error( 'Attribute must be an instanceof Backbone.Model or Backbone.Collection. Is: ' + model + ', currentSplit: ' + split );
				}
			}, this );

			if ( originalResult !== undefined && result !== undefined ) {
				throw new Error( "Ambiguous result for '" + attr + "'. direct result: " + originalResult + ", dotNotation: " + result );
			}

			return originalResult || result;
		},

		set: function( key, value, options ) {
			Backbone.Relational.eventQueue.block();

			// Duplicate backbone's behavior to allow separate key/value parameters, instead of a single 'attributes' object
			var attributes;
			if ( _.isObject( key ) || key == null ) {
				attributes = key;
				options = value;
			}
			else {
				attributes = {};
				attributes[ key ] = value;
			}

			try {
				var id = this.id,
					newId = attributes && this.idAttribute in attributes && attributes[ this.idAttribute ];

				// Check if we're not setting a duplicate id before actually calling `set`.
				Backbone.Relational.store.checkId( this, newId );

				var result = Backbone.Model.prototype.set.apply( this, arguments );

				// Ideal place to set up relations, if this is the first time we're here for this model
				if ( !this._isInitialized && !this.isLocked() ) {
					this.constructor.initializeModelHierarchy();
					Backbone.Relational.store.register( this );
					this.initializeRelations( options );
				}
				// The store should know about an `id` update asap
				else if ( newId && newId !== id ) {
					Backbone.Relational.store.update( this );
				}

				if ( attributes ) {
					this.updateRelations( options );
				}
			}
			finally {
				// Try to run the global queue holding external events
				Backbone.Relational.eventQueue.unblock();
			}

			return result;
		},

		clone: function() {
			var attributes = _.clone( this.attributes );
			if ( !_.isUndefined( attributes[ this.idAttribute ] ) ) {
				attributes[ this.idAttribute ] = null;
			}

			_.each( this.getRelations(), function( rel ) {
				delete attributes[ rel.key ];
			});

			return new this.constructor( attributes );
		},

		/**
		 * Convert relations to JSON, omits them when required
		 */
		toJSON: function( options ) {
			// If this Model has already been fully serialized in this branch once, return to avoid loops
			if ( this.isLocked() ) {
				return this.id;
			}

			this.acquire();
			var json = Backbone.Model.prototype.toJSON.call( this, options );

			if ( this.constructor._superModel && !( this.constructor._subModelTypeAttribute in json ) ) {
				json[ this.constructor._subModelTypeAttribute ] = this.constructor._subModelTypeValue;
			}

			_.each( this._relations, function( rel ) {
				var related = json[ rel.key ],
					includeInJSON = rel.options.includeInJSON,
					value = null;

				if ( includeInJSON === true ) {
					if ( related && _.isFunction( related.toJSON ) ) {
						value = related.toJSON( options );
					}
				}
				else if ( _.isString( includeInJSON ) ) {
					if ( related instanceof Backbone.Collection ) {
						value = related.pluck( includeInJSON );
					}
					else if ( related instanceof Backbone.Model ) {
						value = related.get( includeInJSON );
					}

					// Add ids for 'unfound' models if includeInJSON is equal to (only) the relatedModel's `idAttribute`
					if ( includeInJSON === rel.relatedModel.prototype.idAttribute ) {
						if ( rel instanceof Backbone.HasMany ) {
							value = value.concat( rel.keyIds );
						}
						else if  ( rel instanceof Backbone.HasOne ) {
							value = value || rel.keyId;
						}
					}
				}
				else if ( _.isArray( includeInJSON ) ) {
					if ( related instanceof Backbone.Collection ) {
						value = [];
						related.each( function( model ) {
							var curJson = {};
							_.each( includeInJSON, function( key ) {
								curJson[ key ] = model.get( key );
							});
							value.push( curJson );
						});
					}
					else if ( related instanceof Backbone.Model ) {
						value = {};
						_.each( includeInJSON, function( key ) {
							value[ key ] = related.get( key );
						});
					}
				}
				else {
					delete json[ rel.key ];
				}

				if ( includeInJSON ) {
					json[ rel.keyDestination ] = value;
				}

				if ( rel.keyDestination !== rel.key ) {
					delete json[ rel.key ];
				}
			});

			this.release();
			return json;
		}
	},
	{
		/**
		 *
		 * @param superModel
		 * @returns {Backbone.RelationalModel.constructor}
		 */
		setup: function( superModel ) {
			// We don't want to share a relations array with a parent, as this will cause problems with
			// reverse relations.
			this.prototype.relations = ( this.prototype.relations || [] ).slice( 0 );

			this._subModels = {};
			this._superModel = null;

			// If this model has 'subModelTypes' itself, remember them in the store
			if ( this.prototype.hasOwnProperty( 'subModelTypes' ) ) {
				Backbone.Relational.store.addSubModels( this.prototype.subModelTypes, this );
			}
			// The 'subModelTypes' property should not be inherited, so reset it.
			else {
				this.prototype.subModelTypes = null;
			}

			// Initialize all reverseRelations that belong to this new model.
			_.each( this.prototype.relations || [], function( rel ) {
				if ( !rel.model ) {
					rel.model = this;
				}

				if ( rel.reverseRelation && rel.model === this ) {
					var preInitialize = true;
					if ( _.isString( rel.relatedModel ) ) {
						/**
						 * The related model might not be defined for two reasons
						 *  1. it is related to itself
						 *  2. it never gets defined, e.g. a typo
						 *  3. the model hasn't been defined yet, but will be later
						 * In neither of these cases do we need to pre-initialize reverse relations.
						 * However, for 3. (which is, to us, indistinguishable from 2.), we do need to attempt
						 * setting up this relation again later, in case the related model is defined later.
						 */
						var relatedModel = Backbone.Relational.store.getObjectByName( rel.relatedModel );
						preInitialize = relatedModel && ( relatedModel.prototype instanceof Backbone.RelationalModel );
					}

					if ( preInitialize ) {
						Backbone.Relational.store.initializeRelation( null, rel );
					}
					else if ( _.isString( rel.relatedModel ) ) {
						Backbone.Relational.store.addOrphanRelation( rel );
					}
				}
			}, this );

			return this;
		},

		/**
		 * Create a 'Backbone.Model' instance based on 'attributes'.
		 * @param {Object} attributes
		 * @param {Object} [options]
		 * @return {Backbone.Model}
		 */
		build: function( attributes, options ) {
			// 'build' is a possible entrypoint; it's possible no model hierarchy has been determined yet.
			this.initializeModelHierarchy();

			// Determine what type of (sub)model should be built if applicable.
			var model = this._findSubModelType(this, attributes) || this;

			return new model( attributes, options );
		},

		/**
		 * Determines what type of (sub)model should be built if applicable.
		 * Looks up the proper subModelType in 'this._subModels', recursing into
		 * types until a match is found.  Returns the applicable 'Backbone.Model'
		 * or null if no match is found.
		 * @param {Backbone.Model} type
		 * @param {Object} attributes
		 * @return {Backbone.Model}
		 */
		_findSubModelType: function (type, attributes) {
			if ( type._subModels && type.prototype.subModelTypeAttribute in attributes ) {
				var subModelTypeAttribute = attributes[type.prototype.subModelTypeAttribute];
				var subModelType = type._subModels[subModelTypeAttribute];
				if ( subModelType ) {
					return subModelType;
				} else {
					// Recurse into subModelTypes to find a match
					for ( subModelTypeAttribute in type._subModels ) {
						subModelType = this._findSubModelType(type._subModels[subModelTypeAttribute], attributes);
						if ( subModelType ) {
							return subModelType;
						}
					}
				}
			}
			return null;
		},

		/**
		 *
		 */
		initializeModelHierarchy: function() {
			// Inherit any relations that have been defined in the parent model.
			this.inheritRelations();

			// If we came here through 'build' for a model that has 'subModelTypes' then try to initialize the ones that
			// haven't been resolved yet.
			if ( this.prototype.subModelTypes ) {
				var resolvedSubModels = _.keys(this._subModels);
				var unresolvedSubModels = _.omit(this.prototype.subModelTypes, resolvedSubModels);
				_.each( unresolvedSubModels, function( subModelTypeName ) {
					var subModelType = Backbone.Relational.store.getObjectByName( subModelTypeName );
					subModelType && subModelType.initializeModelHierarchy();
				});
			}
		},

		inheritRelations: function() {
			// Bail out if we've been here before.
			if (!_.isUndefined( this._superModel ) && !_.isNull( this._superModel )) {
				return;
			}
			// Try to initialize the _superModel.
			Backbone.Relational.store.setupSuperModel( this );

			// If a superModel has been found, copy relations from the _superModel if they haven't been inherited automatically
			// (due to a redefinition of 'relations').
			if ( this._superModel ) {
				// The _superModel needs a chance to initialize its own inherited relations before we attempt to inherit relations
				// from the _superModel. You don't want to call 'initializeModelHierarchy' because that could cause sub-models of
				// this class to inherit their relations before this class has had chance to inherit it's relations.
				this._superModel.inheritRelations();
				if ( this._superModel.prototype.relations ) {
					// Find relations that exist on the '_superModel', but not yet on this model.
					var inheritedRelations = _.select( this._superModel.prototype.relations || [], function( superRel ) {
						return !_.any( this.prototype.relations || [], function( rel ) {
							return superRel.relatedModel === rel.relatedModel && superRel.key === rel.key;
						}, this );
					}, this );

					this.prototype.relations = inheritedRelations.concat( this.prototype.relations );
				}
			}
			// Otherwise, make sure we don't get here again for this type by making '_superModel' false so we fail the
			// isUndefined/isNull check next time.
			else {
				this._superModel = false;
			}
		},

		/**
		 * Find an instance of `this` type in 'Backbone.Relational.store'.
		 * - If `attributes` is a string or a number, `findOrCreate` will just query the `store` and return a model if found.
		 * - If `attributes` is an object and is found in the store, the model will be updated with `attributes` unless `options.update` is `false`.
		 *   Otherwise, a new model is created with `attributes` (unless `options.create` is explicitly set to `false`).
		 * @param {Object|String|Number} attributes Either a model's id, or the attributes used to create or update a model.
		 * @param {Object} [options]
		 * @param {Boolean} [options.create=true]
		 * @param {Boolean} [options.merge=true]
		 * @param {Boolean} [options.parse=false]
		 * @return {Backbone.RelationalModel}
		 */
		findOrCreate: function( attributes, options ) {
			options || ( options = {} );
			var parsedAttributes = ( _.isObject( attributes ) && options.parse && this.prototype.parse ) ?
				this.prototype.parse( _.clone( attributes ) ) : attributes;

			// Try to find an instance of 'this' model type in the store
			var model = Backbone.Relational.store.find( this, parsedAttributes );

			// If we found an instance, update it with the data in 'item' (unless 'options.merge' is false).
			// If not, create an instance (unless 'options.create' is false).
			if ( _.isObject( attributes ) ) {
				if ( model && options.merge !== false ) {
					// Make sure `options.collection` and `options.url` doesn't cascade to nested models
					delete options.collection;
					delete options.url;

					model.set( parsedAttributes, options );
				}
				else if ( !model && options.create !== false ) {
					model = this.build( attributes, options );
				}
			}

			return model;
		},

		/**
		 * Find an instance of `this` type in 'Backbone.Relational.store'.
		 * - If `attributes` is a string or a number, `find` will just query the `store` and return a model if found.
		 * - If `attributes` is an object and is found in the store, the model will be updated with `attributes` unless `options.update` is `false`.
		 * @param {Object|String|Number} attributes Either a model's id, or the attributes used to create or update a model.
		 * @param {Object} [options]
		 * @param {Boolean} [options.merge=true]
		 * @param {Boolean} [options.parse=false]
		 * @return {Backbone.RelationalModel}
		 */
		find: function( attributes, options ) {
			options || ( options = {} );
			options.create = false;
			return this.findOrCreate( attributes, options );
		}
	});
	_.extend( Backbone.RelationalModel.prototype, Backbone.Semaphore );

	/**
	 * Override Backbone.Collection._prepareModel, so objects will be built using the correct type
	 * if the collection.model has subModels.
	 * Attempts to find a model for `attrs` in Backbone.store through `findOrCreate`
	 * (which sets the new properties on it if found), or instantiates a new model.
	 */
	Backbone.Collection.prototype.__prepareModel = Backbone.Collection.prototype._prepareModel;
	Backbone.Collection.prototype._prepareModel = function ( attrs, options ) {
		var model;

		if ( attrs instanceof Backbone.Model ) {
			if ( !attrs.collection ) {
				attrs.collection = this;
			}
			model = attrs;
		}
		else {
			options || ( options = {} );
			options.collection = this;

			if ( typeof this.model.findOrCreate !== 'undefined' ) {
				model = this.model.findOrCreate( attrs, options );
			}
			else {
				model = new this.model( attrs, options );
			}

			if ( model && model.isNew() && !model._validate( attrs, options ) ) {
				this.trigger( 'invalid', this, attrs, options );
				model = false;
			}
		}

		return model;
	};


	/**
	 * Override Backbone.Collection.set, so we'll create objects from attributes where required,
	 * and update the existing models. Also, trigger 'relational:add'.
	 */
	var set = Backbone.Collection.prototype.__set = Backbone.Collection.prototype.set;
	Backbone.Collection.prototype.set = function( models, options ) {
		// Short-circuit if this Collection doesn't hold RelationalModels
		if ( !( this.model.prototype instanceof Backbone.RelationalModel ) ) {
			return set.apply( this, arguments );
		}

		if ( options && options.parse ) {
			models = this.parse( models, options );
		}

		if ( !_.isArray( models ) ) {
			models = models ? [ models ] : [];
		}

		var newModels = [],
			toAdd = [];

		//console.debug( 'calling add on coll=%o; model=%o, options=%o', this, models, options );
		_.each( models, function( model ) {
			if ( !( model instanceof Backbone.Model ) ) {
				model = Backbone.Collection.prototype._prepareModel.call( this, model, options );
			}

			if ( model ) {
				toAdd.push( model );

				if ( !( this.get( model ) || this.get( model.cid ) ) ) {
					newModels.push( model );
				}
				// If we arrive in `add` while performing a `set` (after a create, so the model gains an `id`),
				// we may get here before `_onModelEvent` has had the chance to update `_byId`.
				else if ( model.id != null ) {
					this._byId[ model.id ] = model;
				}
			}
		}, this );

		// Add 'models' in a single batch, so the original add will only be called once (and thus 'sort', etc).
		// If `parse` was specified, the collection and contained models have been parsed now.
		set.call( this, toAdd, _.defaults( { parse: false }, options ) );

		_.each( newModels, function( model ) {
			// Fire a `relational:add` event for any model in `newModels` that has actually been added to the collection.
			if ( this.get( model ) || this.get( model.cid ) ) {
				this.trigger( 'relational:add', model, this, options );
			}
		}, this );

		return this;
	};

	/**
	 * Override 'Backbone.Collection.remove' to trigger 'relational:remove'.
	 */
	var remove = Backbone.Collection.prototype.__remove = Backbone.Collection.prototype.remove;
	Backbone.Collection.prototype.remove = function( models, options ) {
		// Short-circuit if this Collection doesn't hold RelationalModels
		if ( !( this.model.prototype instanceof Backbone.RelationalModel ) ) {
			return remove.apply( this, arguments );
		}

		models = _.isArray( models ) ? models.slice( 0 ) : [ models ];
		options || ( options = {} );

		var toRemove = [];

		//console.debug('calling remove on coll=%o; models=%o, options=%o', this, models, options );
		_.each( models, function( model ) {
			model = this.get( model ) || ( model && this.get( model.cid ) );
			model && toRemove.push( model );
		}, this );

		if ( toRemove.length ) {
			remove.call( this, toRemove, options );

			_.each( toRemove, function( model ) {
				this.trigger('relational:remove', model, this, options);
			}, this );
		}

		return this;
	};

	/**
	 * Override 'Backbone.Collection.reset' to trigger 'relational:reset'.
	 */
	var reset = Backbone.Collection.prototype.__reset = Backbone.Collection.prototype.reset;
	Backbone.Collection.prototype.reset = function( models, options ) {
		options = _.extend( { merge: true }, options );
		reset.call( this, models, options );

		if ( this.model.prototype instanceof Backbone.RelationalModel ) {
			this.trigger( 'relational:reset', this, options );
		}

		return this;
	};

	/**
	 * Override 'Backbone.Collection.sort' to trigger 'relational:reset'.
	 */
	var sort = Backbone.Collection.prototype.__sort = Backbone.Collection.prototype.sort;
	Backbone.Collection.prototype.sort = function( options ) {
		sort.call( this, options );

		if ( this.model.prototype instanceof Backbone.RelationalModel ) {
			this.trigger( 'relational:reset', this, options );
		}

		return this;
	};

	/**
	 * Override 'Backbone.Collection.trigger' so 'add', 'remove' and 'reset' events are queued until relations
	 * are ready.
	 */
	var trigger = Backbone.Collection.prototype.__trigger = Backbone.Collection.prototype.trigger;
	Backbone.Collection.prototype.trigger = function( eventName ) {
		// Short-circuit if this Collection doesn't hold RelationalModels
		if ( !( this.model.prototype instanceof Backbone.RelationalModel ) ) {
			return trigger.apply( this, arguments );
		}

		if ( eventName === 'add' || eventName === 'remove' || eventName === 'reset' || eventName === 'sort' ) {
			var dit = this,
				args = arguments;

			if ( _.isObject( args[ 3 ] ) ) {
				args = _.toArray( args );
				// the fourth argument is the option object.
				// we need to clone it, as it could be modified while we wait on the eventQueue to be unblocked
				args[ 3 ] = _.clone( args[ 3 ] );
			}

			Backbone.Relational.eventQueue.add( function() {
				trigger.apply( dit, args );
			});
		}
		else {
			trigger.apply( this, arguments );
		}

		return this;
	};

	// Override .extend() to automatically call .setup()
	Backbone.RelationalModel.extend = function( protoProps, classProps ) {
		var child = Backbone.Model.extend.apply( this, arguments );

		child.setup( this );

		return child;
	};
})();
/*
Copyright 2012 Igor Vaynberg

Version: 3.5.3 Timestamp: Wed Aug 19 21:55:46 EDT 2015

This software is licensed under the Apache License, Version 2.0 (the "Apache License") or the GNU
General Public License version 2 (the "GPL License"). You may choose either license to govern your
use of this software only upon the condition that you accept all of the terms of either the Apache
License or the GPL License.

You may obtain a copy of the Apache License and the GPL License at:

    http://www.apache.org/licenses/LICENSE-2.0
    http://www.gnu.org/licenses/gpl-2.0.html

Unless required by applicable law or agreed to in writing, software distributed under the
Apache License or the GPL License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
CONDITIONS OF ANY KIND, either express or implied. See the Apache License and the GPL License for
the specific language governing permissions and limitations under the Apache License and the GPL License.
*/
(function ($) {
    if(typeof $.fn.each2 == "undefined") {
        $.extend($.fn, {
            /*
            * 4-10 times faster .each replacement
            * use it carefully, as it overrides jQuery context of element on each iteration
            */
            each2 : function (c) {
                var j = $([0]), i = -1, l = this.length;
                while (
                    ++i < l
                    && (j.context = j[0] = this[i])
                    && c.call(j[0], i, j) !== false //"this"=DOM, i=index, j=jQuery object
                );
                return this;
            }
        });
    }
})(jQuery);

(function ($, undefined) {
    "use strict";
    /*global document, window, jQuery, console */

    if (window.Select2 !== undefined) {
        return;
    }

    var AbstractSelect2, SingleSelect2, MultiSelect2, nextUid, sizer,
        lastMousePosition={x:0,y:0}, $document, scrollBarDimensions,

    KEY = {
        TAB: 9,
        ENTER: 13,
        ESC: 27,
        SPACE: 32,
        LEFT: 37,
        UP: 38,
        RIGHT: 39,
        DOWN: 40,
        SHIFT: 16,
        CTRL: 17,
        ALT: 18,
        PAGE_UP: 33,
        PAGE_DOWN: 34,
        HOME: 36,
        END: 35,
        BACKSPACE: 8,
        DELETE: 46,
        isArrow: function (k) {
            k = k.which ? k.which : k;
            switch (k) {
            case KEY.LEFT:
            case KEY.RIGHT:
            case KEY.UP:
            case KEY.DOWN:
                return true;
            }
            return false;
        },
        isControl: function (e) {
            var k = e.which;
            switch (k) {
            case KEY.SHIFT:
            case KEY.CTRL:
            case KEY.ALT:
                return true;
            }

            if (e.metaKey) return true;

            return false;
        },
        isFunctionKey: function (k) {
            k = k.which ? k.which : k;
            return k >= 112 && k <= 123;
        }
    },
    MEASURE_SCROLLBAR_TEMPLATE = "<div class='select2-measure-scrollbar'></div>",

    DIACRITICS = {"\u24B6":"A","\uFF21":"A","\u00C0":"A","\u00C1":"A","\u00C2":"A","\u1EA6":"A","\u1EA4":"A","\u1EAA":"A","\u1EA8":"A","\u00C3":"A","\u0100":"A","\u0102":"A","\u1EB0":"A","\u1EAE":"A","\u1EB4":"A","\u1EB2":"A","\u0226":"A","\u01E0":"A","\u00C4":"A","\u01DE":"A","\u1EA2":"A","\u00C5":"A","\u01FA":"A","\u01CD":"A","\u0200":"A","\u0202":"A","\u1EA0":"A","\u1EAC":"A","\u1EB6":"A","\u1E00":"A","\u0104":"A","\u023A":"A","\u2C6F":"A","\uA732":"AA","\u00C6":"AE","\u01FC":"AE","\u01E2":"AE","\uA734":"AO","\uA736":"AU","\uA738":"AV","\uA73A":"AV","\uA73C":"AY","\u24B7":"B","\uFF22":"B","\u1E02":"B","\u1E04":"B","\u1E06":"B","\u0243":"B","\u0182":"B","\u0181":"B","\u24B8":"C","\uFF23":"C","\u0106":"C","\u0108":"C","\u010A":"C","\u010C":"C","\u00C7":"C","\u1E08":"C","\u0187":"C","\u023B":"C","\uA73E":"C","\u24B9":"D","\uFF24":"D","\u1E0A":"D","\u010E":"D","\u1E0C":"D","\u1E10":"D","\u1E12":"D","\u1E0E":"D","\u0110":"D","\u018B":"D","\u018A":"D","\u0189":"D","\uA779":"D","\u01F1":"DZ","\u01C4":"DZ","\u01F2":"Dz","\u01C5":"Dz","\u24BA":"E","\uFF25":"E","\u00C8":"E","\u00C9":"E","\u00CA":"E","\u1EC0":"E","\u1EBE":"E","\u1EC4":"E","\u1EC2":"E","\u1EBC":"E","\u0112":"E","\u1E14":"E","\u1E16":"E","\u0114":"E","\u0116":"E","\u00CB":"E","\u1EBA":"E","\u011A":"E","\u0204":"E","\u0206":"E","\u1EB8":"E","\u1EC6":"E","\u0228":"E","\u1E1C":"E","\u0118":"E","\u1E18":"E","\u1E1A":"E","\u0190":"E","\u018E":"E","\u24BB":"F","\uFF26":"F","\u1E1E":"F","\u0191":"F","\uA77B":"F","\u24BC":"G","\uFF27":"G","\u01F4":"G","\u011C":"G","\u1E20":"G","\u011E":"G","\u0120":"G","\u01E6":"G","\u0122":"G","\u01E4":"G","\u0193":"G","\uA7A0":"G","\uA77D":"G","\uA77E":"G","\u24BD":"H","\uFF28":"H","\u0124":"H","\u1E22":"H","\u1E26":"H","\u021E":"H","\u1E24":"H","\u1E28":"H","\u1E2A":"H","\u0126":"H","\u2C67":"H","\u2C75":"H","\uA78D":"H","\u24BE":"I","\uFF29":"I","\u00CC":"I","\u00CD":"I","\u00CE":"I","\u0128":"I","\u012A":"I","\u012C":"I","\u0130":"I","\u00CF":"I","\u1E2E":"I","\u1EC8":"I","\u01CF":"I","\u0208":"I","\u020A":"I","\u1ECA":"I","\u012E":"I","\u1E2C":"I","\u0197":"I","\u24BF":"J","\uFF2A":"J","\u0134":"J","\u0248":"J","\u24C0":"K","\uFF2B":"K","\u1E30":"K","\u01E8":"K","\u1E32":"K","\u0136":"K","\u1E34":"K","\u0198":"K","\u2C69":"K","\uA740":"K","\uA742":"K","\uA744":"K","\uA7A2":"K","\u24C1":"L","\uFF2C":"L","\u013F":"L","\u0139":"L","\u013D":"L","\u1E36":"L","\u1E38":"L","\u013B":"L","\u1E3C":"L","\u1E3A":"L","\u0141":"L","\u023D":"L","\u2C62":"L","\u2C60":"L","\uA748":"L","\uA746":"L","\uA780":"L","\u01C7":"LJ","\u01C8":"Lj","\u24C2":"M","\uFF2D":"M","\u1E3E":"M","\u1E40":"M","\u1E42":"M","\u2C6E":"M","\u019C":"M","\u24C3":"N","\uFF2E":"N","\u01F8":"N","\u0143":"N","\u00D1":"N","\u1E44":"N","\u0147":"N","\u1E46":"N","\u0145":"N","\u1E4A":"N","\u1E48":"N","\u0220":"N","\u019D":"N","\uA790":"N","\uA7A4":"N","\u01CA":"NJ","\u01CB":"Nj","\u24C4":"O","\uFF2F":"O","\u00D2":"O","\u00D3":"O","\u00D4":"O","\u1ED2":"O","\u1ED0":"O","\u1ED6":"O","\u1ED4":"O","\u00D5":"O","\u1E4C":"O","\u022C":"O","\u1E4E":"O","\u014C":"O","\u1E50":"O","\u1E52":"O","\u014E":"O","\u022E":"O","\u0230":"O","\u00D6":"O","\u022A":"O","\u1ECE":"O","\u0150":"O","\u01D1":"O","\u020C":"O","\u020E":"O","\u01A0":"O","\u1EDC":"O","\u1EDA":"O","\u1EE0":"O","\u1EDE":"O","\u1EE2":"O","\u1ECC":"O","\u1ED8":"O","\u01EA":"O","\u01EC":"O","\u00D8":"O","\u01FE":"O","\u0186":"O","\u019F":"O","\uA74A":"O","\uA74C":"O","\u01A2":"OI","\uA74E":"OO","\u0222":"OU","\u24C5":"P","\uFF30":"P","\u1E54":"P","\u1E56":"P","\u01A4":"P","\u2C63":"P","\uA750":"P","\uA752":"P","\uA754":"P","\u24C6":"Q","\uFF31":"Q","\uA756":"Q","\uA758":"Q","\u024A":"Q","\u24C7":"R","\uFF32":"R","\u0154":"R","\u1E58":"R","\u0158":"R","\u0210":"R","\u0212":"R","\u1E5A":"R","\u1E5C":"R","\u0156":"R","\u1E5E":"R","\u024C":"R","\u2C64":"R","\uA75A":"R","\uA7A6":"R","\uA782":"R","\u24C8":"S","\uFF33":"S","\u1E9E":"S","\u015A":"S","\u1E64":"S","\u015C":"S","\u1E60":"S","\u0160":"S","\u1E66":"S","\u1E62":"S","\u1E68":"S","\u0218":"S","\u015E":"S","\u2C7E":"S","\uA7A8":"S","\uA784":"S","\u24C9":"T","\uFF34":"T","\u1E6A":"T","\u0164":"T","\u1E6C":"T","\u021A":"T","\u0162":"T","\u1E70":"T","\u1E6E":"T","\u0166":"T","\u01AC":"T","\u01AE":"T","\u023E":"T","\uA786":"T","\uA728":"TZ","\u24CA":"U","\uFF35":"U","\u00D9":"U","\u00DA":"U","\u00DB":"U","\u0168":"U","\u1E78":"U","\u016A":"U","\u1E7A":"U","\u016C":"U","\u00DC":"U","\u01DB":"U","\u01D7":"U","\u01D5":"U","\u01D9":"U","\u1EE6":"U","\u016E":"U","\u0170":"U","\u01D3":"U","\u0214":"U","\u0216":"U","\u01AF":"U","\u1EEA":"U","\u1EE8":"U","\u1EEE":"U","\u1EEC":"U","\u1EF0":"U","\u1EE4":"U","\u1E72":"U","\u0172":"U","\u1E76":"U","\u1E74":"U","\u0244":"U","\u24CB":"V","\uFF36":"V","\u1E7C":"V","\u1E7E":"V","\u01B2":"V","\uA75E":"V","\u0245":"V","\uA760":"VY","\u24CC":"W","\uFF37":"W","\u1E80":"W","\u1E82":"W","\u0174":"W","\u1E86":"W","\u1E84":"W","\u1E88":"W","\u2C72":"W","\u24CD":"X","\uFF38":"X","\u1E8A":"X","\u1E8C":"X","\u24CE":"Y","\uFF39":"Y","\u1EF2":"Y","\u00DD":"Y","\u0176":"Y","\u1EF8":"Y","\u0232":"Y","\u1E8E":"Y","\u0178":"Y","\u1EF6":"Y","\u1EF4":"Y","\u01B3":"Y","\u024E":"Y","\u1EFE":"Y","\u24CF":"Z","\uFF3A":"Z","\u0179":"Z","\u1E90":"Z","\u017B":"Z","\u017D":"Z","\u1E92":"Z","\u1E94":"Z","\u01B5":"Z","\u0224":"Z","\u2C7F":"Z","\u2C6B":"Z","\uA762":"Z","\u24D0":"a","\uFF41":"a","\u1E9A":"a","\u00E0":"a","\u00E1":"a","\u00E2":"a","\u1EA7":"a","\u1EA5":"a","\u1EAB":"a","\u1EA9":"a","\u00E3":"a","\u0101":"a","\u0103":"a","\u1EB1":"a","\u1EAF":"a","\u1EB5":"a","\u1EB3":"a","\u0227":"a","\u01E1":"a","\u00E4":"a","\u01DF":"a","\u1EA3":"a","\u00E5":"a","\u01FB":"a","\u01CE":"a","\u0201":"a","\u0203":"a","\u1EA1":"a","\u1EAD":"a","\u1EB7":"a","\u1E01":"a","\u0105":"a","\u2C65":"a","\u0250":"a","\uA733":"aa","\u00E6":"ae","\u01FD":"ae","\u01E3":"ae","\uA735":"ao","\uA737":"au","\uA739":"av","\uA73B":"av","\uA73D":"ay","\u24D1":"b","\uFF42":"b","\u1E03":"b","\u1E05":"b","\u1E07":"b","\u0180":"b","\u0183":"b","\u0253":"b","\u24D2":"c","\uFF43":"c","\u0107":"c","\u0109":"c","\u010B":"c","\u010D":"c","\u00E7":"c","\u1E09":"c","\u0188":"c","\u023C":"c","\uA73F":"c","\u2184":"c","\u24D3":"d","\uFF44":"d","\u1E0B":"d","\u010F":"d","\u1E0D":"d","\u1E11":"d","\u1E13":"d","\u1E0F":"d","\u0111":"d","\u018C":"d","\u0256":"d","\u0257":"d","\uA77A":"d","\u01F3":"dz","\u01C6":"dz","\u24D4":"e","\uFF45":"e","\u00E8":"e","\u00E9":"e","\u00EA":"e","\u1EC1":"e","\u1EBF":"e","\u1EC5":"e","\u1EC3":"e","\u1EBD":"e","\u0113":"e","\u1E15":"e","\u1E17":"e","\u0115":"e","\u0117":"e","\u00EB":"e","\u1EBB":"e","\u011B":"e","\u0205":"e","\u0207":"e","\u1EB9":"e","\u1EC7":"e","\u0229":"e","\u1E1D":"e","\u0119":"e","\u1E19":"e","\u1E1B":"e","\u0247":"e","\u025B":"e","\u01DD":"e","\u24D5":"f","\uFF46":"f","\u1E1F":"f","\u0192":"f","\uA77C":"f","\u24D6":"g","\uFF47":"g","\u01F5":"g","\u011D":"g","\u1E21":"g","\u011F":"g","\u0121":"g","\u01E7":"g","\u0123":"g","\u01E5":"g","\u0260":"g","\uA7A1":"g","\u1D79":"g","\uA77F":"g","\u24D7":"h","\uFF48":"h","\u0125":"h","\u1E23":"h","\u1E27":"h","\u021F":"h","\u1E25":"h","\u1E29":"h","\u1E2B":"h","\u1E96":"h","\u0127":"h","\u2C68":"h","\u2C76":"h","\u0265":"h","\u0195":"hv","\u24D8":"i","\uFF49":"i","\u00EC":"i","\u00ED":"i","\u00EE":"i","\u0129":"i","\u012B":"i","\u012D":"i","\u00EF":"i","\u1E2F":"i","\u1EC9":"i","\u01D0":"i","\u0209":"i","\u020B":"i","\u1ECB":"i","\u012F":"i","\u1E2D":"i","\u0268":"i","\u0131":"i","\u24D9":"j","\uFF4A":"j","\u0135":"j","\u01F0":"j","\u0249":"j","\u24DA":"k","\uFF4B":"k","\u1E31":"k","\u01E9":"k","\u1E33":"k","\u0137":"k","\u1E35":"k","\u0199":"k","\u2C6A":"k","\uA741":"k","\uA743":"k","\uA745":"k","\uA7A3":"k","\u24DB":"l","\uFF4C":"l","\u0140":"l","\u013A":"l","\u013E":"l","\u1E37":"l","\u1E39":"l","\u013C":"l","\u1E3D":"l","\u1E3B":"l","\u017F":"l","\u0142":"l","\u019A":"l","\u026B":"l","\u2C61":"l","\uA749":"l","\uA781":"l","\uA747":"l","\u01C9":"lj","\u24DC":"m","\uFF4D":"m","\u1E3F":"m","\u1E41":"m","\u1E43":"m","\u0271":"m","\u026F":"m","\u24DD":"n","\uFF4E":"n","\u01F9":"n","\u0144":"n","\u00F1":"n","\u1E45":"n","\u0148":"n","\u1E47":"n","\u0146":"n","\u1E4B":"n","\u1E49":"n","\u019E":"n","\u0272":"n","\u0149":"n","\uA791":"n","\uA7A5":"n","\u01CC":"nj","\u24DE":"o","\uFF4F":"o","\u00F2":"o","\u00F3":"o","\u00F4":"o","\u1ED3":"o","\u1ED1":"o","\u1ED7":"o","\u1ED5":"o","\u00F5":"o","\u1E4D":"o","\u022D":"o","\u1E4F":"o","\u014D":"o","\u1E51":"o","\u1E53":"o","\u014F":"o","\u022F":"o","\u0231":"o","\u00F6":"o","\u022B":"o","\u1ECF":"o","\u0151":"o","\u01D2":"o","\u020D":"o","\u020F":"o","\u01A1":"o","\u1EDD":"o","\u1EDB":"o","\u1EE1":"o","\u1EDF":"o","\u1EE3":"o","\u1ECD":"o","\u1ED9":"o","\u01EB":"o","\u01ED":"o","\u00F8":"o","\u01FF":"o","\u0254":"o","\uA74B":"o","\uA74D":"o","\u0275":"o","\u01A3":"oi","\u0223":"ou","\uA74F":"oo","\u24DF":"p","\uFF50":"p","\u1E55":"p","\u1E57":"p","\u01A5":"p","\u1D7D":"p","\uA751":"p","\uA753":"p","\uA755":"p","\u24E0":"q","\uFF51":"q","\u024B":"q","\uA757":"q","\uA759":"q","\u24E1":"r","\uFF52":"r","\u0155":"r","\u1E59":"r","\u0159":"r","\u0211":"r","\u0213":"r","\u1E5B":"r","\u1E5D":"r","\u0157":"r","\u1E5F":"r","\u024D":"r","\u027D":"r","\uA75B":"r","\uA7A7":"r","\uA783":"r","\u24E2":"s","\uFF53":"s","\u00DF":"s","\u015B":"s","\u1E65":"s","\u015D":"s","\u1E61":"s","\u0161":"s","\u1E67":"s","\u1E63":"s","\u1E69":"s","\u0219":"s","\u015F":"s","\u023F":"s","\uA7A9":"s","\uA785":"s","\u1E9B":"s","\u24E3":"t","\uFF54":"t","\u1E6B":"t","\u1E97":"t","\u0165":"t","\u1E6D":"t","\u021B":"t","\u0163":"t","\u1E71":"t","\u1E6F":"t","\u0167":"t","\u01AD":"t","\u0288":"t","\u2C66":"t","\uA787":"t","\uA729":"tz","\u24E4":"u","\uFF55":"u","\u00F9":"u","\u00FA":"u","\u00FB":"u","\u0169":"u","\u1E79":"u","\u016B":"u","\u1E7B":"u","\u016D":"u","\u00FC":"u","\u01DC":"u","\u01D8":"u","\u01D6":"u","\u01DA":"u","\u1EE7":"u","\u016F":"u","\u0171":"u","\u01D4":"u","\u0215":"u","\u0217":"u","\u01B0":"u","\u1EEB":"u","\u1EE9":"u","\u1EEF":"u","\u1EED":"u","\u1EF1":"u","\u1EE5":"u","\u1E73":"u","\u0173":"u","\u1E77":"u","\u1E75":"u","\u0289":"u","\u24E5":"v","\uFF56":"v","\u1E7D":"v","\u1E7F":"v","\u028B":"v","\uA75F":"v","\u028C":"v","\uA761":"vy","\u24E6":"w","\uFF57":"w","\u1E81":"w","\u1E83":"w","\u0175":"w","\u1E87":"w","\u1E85":"w","\u1E98":"w","\u1E89":"w","\u2C73":"w","\u24E7":"x","\uFF58":"x","\u1E8B":"x","\u1E8D":"x","\u24E8":"y","\uFF59":"y","\u1EF3":"y","\u00FD":"y","\u0177":"y","\u1EF9":"y","\u0233":"y","\u1E8F":"y","\u00FF":"y","\u1EF7":"y","\u1E99":"y","\u1EF5":"y","\u01B4":"y","\u024F":"y","\u1EFF":"y","\u24E9":"z","\uFF5A":"z","\u017A":"z","\u1E91":"z","\u017C":"z","\u017E":"z","\u1E93":"z","\u1E95":"z","\u01B6":"z","\u0225":"z","\u0240":"z","\u2C6C":"z","\uA763":"z","\u0386":"\u0391","\u0388":"\u0395","\u0389":"\u0397","\u038A":"\u0399","\u03AA":"\u0399","\u038C":"\u039F","\u038E":"\u03A5","\u03AB":"\u03A5","\u038F":"\u03A9","\u03AC":"\u03B1","\u03AD":"\u03B5","\u03AE":"\u03B7","\u03AF":"\u03B9","\u03CA":"\u03B9","\u0390":"\u03B9","\u03CC":"\u03BF","\u03CD":"\u03C5","\u03CB":"\u03C5","\u03B0":"\u03C5","\u03C9":"\u03C9","\u03C2":"\u03C3"};

    $document = $(document);

    nextUid=(function() { var counter=1; return function() { return counter++; }; }());


    function reinsertElement(element) {
        var placeholder = $(document.createTextNode(''));

        element.before(placeholder);
        placeholder.before(element);
        placeholder.remove();
    }

    function stripDiacritics(str) {
        // Used 'uni range + named function' from http://jsperf.com/diacritics/18
        function match(a) {
            return DIACRITICS[a] || a;
        }

        return str.replace(/[^\u0000-\u007E]/g, match);
    }

    function indexOf(value, array) {
        var i = 0, l = array.length;
        for (; i < l; i = i + 1) {
            if (equal(value, array[i])) return i;
        }
        return -1;
    }

    function measureScrollbar () {
        var $template = $( MEASURE_SCROLLBAR_TEMPLATE );
        $template.appendTo(document.body);

        var dim = {
            width: $template.width() - $template[0].clientWidth,
            height: $template.height() - $template[0].clientHeight
        };
        $template.remove();

        return dim;
    }

    /**
     * Compares equality of a and b
     * @param a
     * @param b
     */
    function equal(a, b) {
        if (a === b) return true;
        if (a === undefined || b === undefined) return false;
        if (a === null || b === null) return false;
        // Check whether 'a' or 'b' is a string (primitive or object).
        // The concatenation of an empty string (+'') converts its argument to a string's primitive.
        if (a.constructor === String) return a+'' === b+''; // a+'' - in case 'a' is a String object
        if (b.constructor === String) return b+'' === a+''; // b+'' - in case 'b' is a String object
        return false;
    }

    /**
     * Splits the string into an array of values, transforming each value. An empty array is returned for nulls or empty
     * strings
     * @param string
     * @param separator
     */
    function splitVal(string, separator, transform) {
        var val, i, l;
        if (string === null || string.length < 1) return [];
        val = string.split(separator);
        for (i = 0, l = val.length; i < l; i = i + 1) val[i] = transform(val[i]);
        return val;
    }

    function getSideBorderPadding(element) {
        return element.outerWidth(false) - element.width();
    }

    function installKeyUpChangeEvent(element) {
        var key="keyup-change-value";
        element.on("keydown", function () {
            if ($.data(element, key) === undefined) {
                $.data(element, key, element.val());
            }
        });
        element.on("keyup", function () {
            var val= $.data(element, key);
            if (val !== undefined && element.val() !== val) {
                $.removeData(element, key);
                element.trigger("keyup-change");
            }
        });
    }


    /**
     * filters mouse events so an event is fired only if the mouse moved.
     *
     * filters out mouse events that occur when mouse is stationary but
     * the elements under the pointer are scrolled.
     */
    function installFilteredMouseMove(element) {
        element.on("mousemove", function (e) {
            var lastpos = lastMousePosition;
            if (lastpos === undefined || lastpos.x !== e.pageX || lastpos.y !== e.pageY) {
                $(e.target).trigger("mousemove-filtered", e);
            }
        });
    }

    /**
     * Debounces a function. Returns a function that calls the original fn function only if no invocations have been made
     * within the last quietMillis milliseconds.
     *
     * @param quietMillis number of milliseconds to wait before invoking fn
     * @param fn function to be debounced
     * @param ctx object to be used as this reference within fn
     * @return debounced version of fn
     */
    function debounce(quietMillis, fn, ctx) {
        ctx = ctx || undefined;
        var timeout;
        return function () {
            var args = arguments;
            window.clearTimeout(timeout);
            timeout = window.setTimeout(function() {
                fn.apply(ctx, args);
            }, quietMillis);
        };
    }

    function installDebouncedScroll(threshold, element) {
        var notify = debounce(threshold, function (e) { element.trigger("scroll-debounced", e);});
        element.on("scroll", function (e) {
            if (indexOf(e.target, element.get()) >= 0) notify(e);
        });
    }

    function focus($el) {
        if ($el[0] === document.activeElement) return;

        /* set the focus in a 0 timeout - that way the focus is set after the processing
            of the current event has finished - which seems like the only reliable way
            to set focus */
        window.setTimeout(function() {
            var el=$el[0], pos=$el.val().length, range;

            $el.focus();

            /* make sure el received focus so we do not error out when trying to manipulate the caret.
                sometimes modals or others listeners may steal it after its set */
            var isVisible = (el.offsetWidth > 0 || el.offsetHeight > 0);
            if (isVisible && el === document.activeElement) {

                /* after the focus is set move the caret to the end, necessary when we val()
                    just before setting focus */
                if(el.setSelectionRange)
                {
                    el.setSelectionRange(pos, pos);
                }
                else if (el.createTextRange) {
                    range = el.createTextRange();
                    range.collapse(false);
                    range.select();
                }
            }
        }, 0);
    }

    function getCursorInfo(el) {
        el = $(el)[0];
        var offset = 0;
        var length = 0;
        if ('selectionStart' in el) {
            offset = el.selectionStart;
            length = el.selectionEnd - offset;
        } else if ('selection' in document) {
            el.focus();
            var sel = document.selection.createRange();
            length = document.selection.createRange().text.length;
            sel.moveStart('character', -el.value.length);
            offset = sel.text.length - length;
        }
        return { offset: offset, length: length };
    }

    function killEvent(event) {
        event.preventDefault();
        event.stopPropagation();
    }
    function killEventImmediately(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
    }

    function measureTextWidth(e) {
        if (!sizer){
            var style = e[0].currentStyle || window.getComputedStyle(e[0], null);
            sizer = $(document.createElement("div")).css({
                position: "absolute",
                left: "-10000px",
                top: "-10000px",
                display: "none",
                fontSize: style.fontSize,
                fontFamily: style.fontFamily,
                fontStyle: style.fontStyle,
                fontWeight: style.fontWeight,
                letterSpacing: style.letterSpacing,
                textTransform: style.textTransform,
                whiteSpace: "nowrap"
            });
            sizer.attr("class","select2-sizer");
            $(document.body).append(sizer);
        }
        sizer.text(e.val());
        return sizer.width();
    }

    function syncCssClasses(dest, src, adapter) {
        var classes, replacements = [], adapted;

        classes = $.trim(dest.attr("class"));

        if (classes) {
            classes = '' + classes; // for IE which returns object

            $(classes.split(/\s+/)).each2(function() {
                if (this.indexOf("select2-") === 0) {
                    replacements.push(this);
                }
            });
        }

        classes = $.trim(src.attr("class"));

        if (classes) {
            classes = '' + classes; // for IE which returns object

            $(classes.split(/\s+/)).each2(function() {
                if (this.indexOf("select2-") !== 0) {
                    adapted = adapter(this);

                    if (adapted) {
                        replacements.push(adapted);
                    }
                }
            });
        }

        dest.attr("class", replacements.join(" "));
    }


    function markMatch(text, term, markup, escapeMarkup) {
        var match=stripDiacritics(text.toUpperCase()).indexOf(stripDiacritics(term.toUpperCase())),
            tl=term.length;

        if (match<0) {
            markup.push(escapeMarkup(text));
            return;
        }

        markup.push(escapeMarkup(text.substring(0, match)));
        markup.push("<span class='select2-match'>");
        markup.push(escapeMarkup(text.substring(match, match + tl)));
        markup.push("</span>");
        markup.push(escapeMarkup(text.substring(match + tl, text.length)));
    }

    function defaultEscapeMarkup(markup) {
        var replace_map = {
            '\\': '&#92;',
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
            "/": '&#47;'
        };

        return String(markup).replace(/[&<>"'\/\\]/g, function (match) {
            return replace_map[match];
        });
    }

    /**
     * Produces an ajax-based query function
     *
     * @param options object containing configuration parameters
     * @param options.params parameter map for the transport ajax call, can contain such options as cache, jsonpCallback, etc. see $.ajax
     * @param options.transport function that will be used to execute the ajax request. must be compatible with parameters supported by $.ajax
     * @param options.url url for the data
     * @param options.data a function(searchTerm, pageNumber, context) that should return an object containing query string parameters for the above url.
     * @param options.dataType request data type: ajax, jsonp, other datatypes supported by jQuery's $.ajax function or the transport function if specified
     * @param options.quietMillis (optional) milliseconds to wait before making the ajaxRequest, helps debounce the ajax function if invoked too often
     * @param options.results a function(remoteData, pageNumber, query) that converts data returned form the remote request to the format expected by Select2.
     *      The expected format is an object containing the following keys:
     *      results array of objects that will be used as choices
     *      more (optional) boolean indicating whether there are more results available
     *      Example: {results:[{id:1, text:'Red'},{id:2, text:'Blue'}], more:true}
     */
    function ajax(options) {
        var timeout, // current scheduled but not yet executed request
            handler = null,
            quietMillis = options.quietMillis || 100,
            ajaxUrl = options.url,
            self = this;

        return function (query) {
            window.clearTimeout(timeout);
            timeout = window.setTimeout(function () {
                var data = options.data, // ajax data function
                    url = ajaxUrl, // ajax url string or function
                    transport = options.transport || $.fn.select2.ajaxDefaults.transport,
                    // deprecated - to be removed in 4.0  - use params instead
                    deprecated = {
                        type: options.type || 'GET', // set type of request (GET or POST)
                        cache: options.cache || false,
                        jsonpCallback: options.jsonpCallback||undefined,
                        dataType: options.dataType||"json"
                    },
                    params = $.extend({}, $.fn.select2.ajaxDefaults.params, deprecated);

                data = data ? data.call(self, query.term, query.page, query.context) : null;
                url = (typeof url === 'function') ? url.call(self, query.term, query.page, query.context) : url;

                if (handler && typeof handler.abort === "function") { handler.abort(); }

                if (options.params) {
                    if ($.isFunction(options.params)) {
                        $.extend(params, options.params.call(self));
                    } else {
                        $.extend(params, options.params);
                    }
                }

                $.extend(params, {
                    url: url,
                    dataType: options.dataType,
                    data: data,
                    success: function (data) {
                        // TODO - replace query.page with query so users have access to term, page, etc.
                        // added query as third paramter to keep backwards compatibility
                        var results = options.results(data, query.page, query);
                        query.callback(results);
                    },
                    error: function(jqXHR, textStatus, errorThrown){
                        var results = {
                            hasError: true,
                            jqXHR: jqXHR,
                            textStatus: textStatus,
                            errorThrown: errorThrown
                        };

                        query.callback(results);
                    }
                });
                handler = transport.call(self, params);
            }, quietMillis);
        };
    }

    /**
     * Produces a query function that works with a local array
     *
     * @param options object containing configuration parameters. The options parameter can either be an array or an
     * object.
     *
     * If the array form is used it is assumed that it contains objects with 'id' and 'text' keys.
     *
     * If the object form is used it is assumed that it contains 'data' and 'text' keys. The 'data' key should contain
     * an array of objects that will be used as choices. These objects must contain at least an 'id' key. The 'text'
     * key can either be a String in which case it is expected that each element in the 'data' array has a key with the
     * value of 'text' which will be used to match choices. Alternatively, text can be a function(item) that can extract
     * the text.
     */
    function local(options) {
        var data = options, // data elements
            dataText,
            tmp,
            text = function (item) { return ""+item.text; }; // function used to retrieve the text portion of a data item that is matched against the search

         if ($.isArray(data)) {
            tmp = data;
            data = { results: tmp };
        }

         if ($.isFunction(data) === false) {
            tmp = data;
            data = function() { return tmp; };
        }

        var dataItem = data();
        if (dataItem.text) {
            text = dataItem.text;
            // if text is not a function we assume it to be a key name
            if (!$.isFunction(text)) {
                dataText = dataItem.text; // we need to store this in a separate variable because in the next step data gets reset and data.text is no longer available
                text = function (item) { return item[dataText]; };
            }
        }

        return function (query) {
            var t = query.term, filtered = { results: [] }, process;
            if (t === "") {
                query.callback(data());
                return;
            }

            process = function(datum, collection) {
                var group, attr;
                datum = datum[0];
                if (datum.children) {
                    group = {};
                    for (attr in datum) {
                        if (datum.hasOwnProperty(attr)) group[attr]=datum[attr];
                    }
                    group.children=[];
                    $(datum.children).each2(function(i, childDatum) { process(childDatum, group.children); });
                    if (group.children.length || query.matcher(t, text(group), datum)) {
                        collection.push(group);
                    }
                } else {
                    if (query.matcher(t, text(datum), datum)) {
                        collection.push(datum);
                    }
                }
            };

            $(data().results).each2(function(i, datum) { process(datum, filtered.results); });
            query.callback(filtered);
        };
    }

    // TODO javadoc
    function tags(data) {
        var isFunc = $.isFunction(data);
        return function (query) {
            var t = query.term, filtered = {results: []};
            var result = isFunc ? data(query) : data;
            if ($.isArray(result)) {
                $(result).each(function () {
                    var isObject = this.text !== undefined,
                        text = isObject ? this.text : this;
                    if (t === "" || query.matcher(t, text)) {
                        filtered.results.push(isObject ? this : {id: this, text: this});
                    }
                });
                query.callback(filtered);
            }
        };
    }

    /**
     * Checks if the formatter function should be used.
     *
     * Throws an error if it is not a function. Returns true if it should be used,
     * false if no formatting should be performed.
     *
     * @param formatter
     */
    function checkFormatter(formatter, formatterName) {
        if ($.isFunction(formatter)) return true;
        if (!formatter) return false;
        if (typeof(formatter) === 'string') return true;
        throw new Error(formatterName +" must be a string, function, or falsy value");
    }

  /**
   * Returns a given value
   * If given a function, returns its output
   *
   * @param val string|function
   * @param context value of "this" to be passed to function
   * @returns {*}
   */
    function evaluate(val, context) {
        if ($.isFunction(val)) {
            var args = Array.prototype.slice.call(arguments, 2);
            return val.apply(context, args);
        }
        return val;
    }

    function countResults(results) {
        var count = 0;
        $.each(results, function(i, item) {
            if (item.children) {
                count += countResults(item.children);
            } else {
                count++;
            }
        });
        return count;
    }

    /**
     * Default tokenizer. This function uses breaks the input on substring match of any string from the
     * opts.tokenSeparators array and uses opts.createSearchChoice to create the choice object. Both of those
     * two options have to be defined in order for the tokenizer to work.
     *
     * @param input text user has typed so far or pasted into the search field
     * @param selection currently selected choices
     * @param selectCallback function(choice) callback tho add the choice to selection
     * @param opts select2's opts
     * @return undefined/null to leave the current input unchanged, or a string to change the input to the returned value
     */
    function defaultTokenizer(input, selection, selectCallback, opts) {
        var original = input, // store the original so we can compare and know if we need to tell the search to update its text
            dupe = false, // check for whether a token we extracted represents a duplicate selected choice
            token, // token
            index, // position at which the separator was found
            i, l, // looping variables
            separator; // the matched separator

        if (!opts.createSearchChoice || !opts.tokenSeparators || opts.tokenSeparators.length < 1) return undefined;

        while (true) {
            index = -1;

            for (i = 0, l = opts.tokenSeparators.length; i < l; i++) {
                separator = opts.tokenSeparators[i];
                index = input.indexOf(separator);
                if (index >= 0) break;
            }

            if (index < 0) break; // did not find any token separator in the input string, bail

            token = input.substring(0, index);
            input = input.substring(index + separator.length);

            if (token.length > 0) {
                token = opts.createSearchChoice.call(this, token, selection);
                if (token !== undefined && token !== null && opts.id(token) !== undefined && opts.id(token) !== null) {
                    dupe = false;
                    for (i = 0, l = selection.length; i < l; i++) {
                        if (equal(opts.id(token), opts.id(selection[i]))) {
                            dupe = true; break;
                        }
                    }

                    if (!dupe) selectCallback(token);
                }
            }
        }

        if (original!==input) return input;
    }

    function cleanupJQueryElements() {
        var self = this;

        $.each(arguments, function (i, element) {
            self[element].remove();
            self[element] = null;
        });
    }

    /**
     * Creates a new class
     *
     * @param superClass
     * @param methods
     */
    function clazz(SuperClass, methods) {
        var constructor = function () {};
        constructor.prototype = new SuperClass;
        constructor.prototype.constructor = constructor;
        constructor.prototype.parent = SuperClass.prototype;
        constructor.prototype = $.extend(constructor.prototype, methods);
        return constructor;
    }

    AbstractSelect2 = clazz(Object, {

        // abstract
        bind: function (func) {
            var self = this;
            return function () {
                func.apply(self, arguments);
            };
        },

        // abstract
        init: function (opts) {
            var results, search, resultsSelector = ".select2-results";

            // prepare options
            this.opts = opts = this.prepareOpts(opts);

            this.id=opts.id;

            // destroy if called on an existing component
            if (opts.element.data("select2") !== undefined &&
                opts.element.data("select2") !== null) {
                opts.element.data("select2").destroy();
            }

            this.container = this.createContainer();

            this.liveRegion = $('.select2-hidden-accessible');
            if (this.liveRegion.length == 0) {
                this.liveRegion = $("<span>", {
                        role: "status",
                        "aria-live": "polite"
                    })
                    .addClass("select2-hidden-accessible")
                    .appendTo(document.body);
            }

            this.containerId="s2id_"+(opts.element.attr("id") || "autogen"+nextUid());
            this.containerEventName= this.containerId
                .replace(/([.])/g, '_')
                .replace(/([;&,\-\.\+\*\~':"\!\^#$%@\[\]\(\)=>\|])/g, '\\$1');
            this.container.attr("id", this.containerId);

            this.container.attr("title", opts.element.attr("title"));

            this.body = $(document.body);

            syncCssClasses(this.container, this.opts.element, this.opts.adaptContainerCssClass);

            this.container.attr("style", opts.element.attr("style"));
            this.container.css(evaluate(opts.containerCss, this.opts.element));
            this.container.addClass(evaluate(opts.containerCssClass, this.opts.element));

            this.elementTabIndex = this.opts.element.attr("tabindex");

            // swap container for the element
            this.opts.element
                .data("select2", this)
                .attr("tabindex", "-1")
                .before(this.container)
                .on("click.select2", killEvent); // do not leak click events

            this.container.data("select2", this);

            this.dropdown = this.container.find(".select2-drop");

            syncCssClasses(this.dropdown, this.opts.element, this.opts.adaptDropdownCssClass);

            this.dropdown.addClass(evaluate(opts.dropdownCssClass, this.opts.element));
            this.dropdown.data("select2", this);
            this.dropdown.on("click", killEvent);

            this.results = results = this.container.find(resultsSelector);
            this.search = search = this.container.find("input.select2-input");

            this.queryCount = 0;
            this.resultsPage = 0;
            this.context = null;

            // initialize the container
            this.initContainer();

            this.container.on("click", killEvent);

            installFilteredMouseMove(this.results);

            this.dropdown.on("mousemove-filtered", resultsSelector, this.bind(this.highlightUnderEvent));
            this.dropdown.on("touchstart touchmove touchend", resultsSelector, this.bind(function (event) {
                this._touchEvent = true;
                this.highlightUnderEvent(event);
            }));
            this.dropdown.on("touchmove", resultsSelector, this.bind(this.touchMoved));
            this.dropdown.on("touchstart touchend", resultsSelector, this.bind(this.clearTouchMoved));

            // Waiting for a click event on touch devices to select option and hide dropdown
            // otherwise click will be triggered on an underlying element
            this.dropdown.on('click', this.bind(function (event) {
                if (this._touchEvent) {
                    this._touchEvent = false;
                    this.selectHighlighted();
                }
            }));

            installDebouncedScroll(80, this.results);
            this.dropdown.on("scroll-debounced", resultsSelector, this.bind(this.loadMoreIfNeeded));

            // do not propagate change event from the search field out of the component
            $(this.container).on("change", ".select2-input", function(e) {e.stopPropagation();});
            $(this.dropdown).on("change", ".select2-input", function(e) {e.stopPropagation();});

            // if jquery.mousewheel plugin is installed we can prevent out-of-bounds scrolling of results via mousewheel
            if ($.fn.mousewheel) {
                results.mousewheel(function (e, delta, deltaX, deltaY) {
                    var top = results.scrollTop();
                    if (deltaY > 0 && top - deltaY <= 0) {
                        results.scrollTop(0);
                        killEvent(e);
                    } else if (deltaY < 0 && results.get(0).scrollHeight - results.scrollTop() + deltaY <= results.height()) {
                        results.scrollTop(results.get(0).scrollHeight - results.height());
                        killEvent(e);
                    }
                });
            }

            installKeyUpChangeEvent(search);
            search.on("keyup-change input paste", this.bind(this.updateResults));
            search.on("focus", function () { search.addClass("select2-focused"); });
            search.on("blur", function () { search.removeClass("select2-focused");});

            this.dropdown.on("mouseup", resultsSelector, this.bind(function (e) {
                if ($(e.target).closest(".select2-result-selectable").length > 0) {
                    this.highlightUnderEvent(e);
                    this.selectHighlighted(e);
                }
            }));

            // trap all mouse events from leaving the dropdown. sometimes there may be a modal that is listening
            // for mouse events outside of itself so it can close itself. since the dropdown is now outside the select2's
            // dom it will trigger the popup close, which is not what we want
            // focusin can cause focus wars between modals and select2 since the dropdown is outside the modal.
            this.dropdown.on("click mouseup mousedown touchstart touchend focusin", function (e) { e.stopPropagation(); });

            this.lastSearchTerm = undefined;

            if ($.isFunction(this.opts.initSelection)) {
                // initialize selection based on the current value of the source element
                this.initSelection();

                // if the user has provided a function that can set selection based on the value of the source element
                // we monitor the change event on the element and trigger it, allowing for two way synchronization
                this.monitorSource();
            }

            if (opts.maximumInputLength !== null) {
                this.search.attr("maxlength", opts.maximumInputLength);
            }

            var disabled = opts.element.prop("disabled");
            if (disabled === undefined) disabled = false;
            this.enable(!disabled);

            var readonly = opts.element.prop("readonly");
            if (readonly === undefined) readonly = false;
            this.readonly(readonly);

            // Calculate size of scrollbar
            scrollBarDimensions = scrollBarDimensions || measureScrollbar();

            this.autofocus = opts.element.prop("autofocus");
            opts.element.prop("autofocus", false);
            if (this.autofocus) this.focus();

            this.search.attr("placeholder", opts.searchInputPlaceholder);
        },

        // abstract
        destroy: function () {
            var element=this.opts.element, select2 = element.data("select2"), self = this;

            this.close();

            if (element.length && element[0].detachEvent && self._sync) {
                element.each(function () {
                    if (self._sync) {
                        this.detachEvent("onpropertychange", self._sync);
                    }
                });
            }
            if (this.propertyObserver) {
                this.propertyObserver.disconnect();
                this.propertyObserver = null;
            }
            this._sync = null;

            if (select2 !== undefined) {
                select2.container.remove();
                select2.liveRegion.remove();
                select2.dropdown.remove();
                element.removeData("select2")
                    .off(".select2");
                if (!element.is("input[type='hidden']")) {
                    element
                        .show()
                        .prop("autofocus", this.autofocus || false);
                    if (this.elementTabIndex) {
                        element.attr({tabindex: this.elementTabIndex});
                    } else {
                        element.removeAttr("tabindex");
                    }
                    element.show();
                } else {
                    element.css("display", "");
                }
            }

            cleanupJQueryElements.call(this,
                "container",
                "liveRegion",
                "dropdown",
                "results",
                "search"
            );
        },

        // abstract
        optionToData: function(element) {
            if (element.is("option")) {
                return {
                    id:element.prop("value"),
                    text:element.text(),
                    element: element.get(),
                    css: element.attr("class"),
                    disabled: element.prop("disabled"),
                    locked: equal(element.attr("locked"), "locked") || equal(element.data("locked"), true)
                };
            } else if (element.is("optgroup")) {
                return {
                    text:element.attr("label"),
                    children:[],
                    element: element.get(),
                    css: element.attr("class")
                };
            }
        },

        // abstract
        prepareOpts: function (opts) {
            var element, select, idKey, ajaxUrl, self = this;

            element = opts.element;

            if (element.get(0).tagName.toLowerCase() === "select") {
                this.select = select = opts.element;
            }

            if (select) {
                // these options are not allowed when attached to a select because they are picked up off the element itself
                $.each(["id", "multiple", "ajax", "query", "createSearchChoice", "initSelection", "data", "tags"], function () {
                    if (this in opts) {
                        throw new Error("Option '" + this + "' is not allowed for Select2 when attached to a <select> element.");
                    }
                });
            }

            opts.debug = opts.debug || $.fn.select2.defaults.debug;

            // Warnings for options renamed/removed in Select2 4.0.0
            // Only when it's enabled through debug mode
            if (opts.debug && console && console.warn) {
                // id was removed
                if (opts.id != null) {
                    console.warn(
                        'Select2: The `id` option has been removed in Select2 4.0.0, ' +
                        'consider renaming your `id` property or mapping the property before your data makes it to Select2. ' +
                        'You can read more at https://select2.github.io/announcements-4.0.html#changed-id'
                    );
                }

                // text was removed
                if (opts.text != null) {
                    console.warn(
                        'Select2: The `text` option has been removed in Select2 4.0.0, ' +
                        'consider renaming your `text` property or mapping the property before your data makes it to Select2. ' +
                        'You can read more at https://select2.github.io/announcements-4.0.html#changed-id'
                    );
                }

                // sortResults was renamed to results
                if (opts.sortResults != null) {
                    console.warn(
                        'Select2: the `sortResults` option has been renamed to `sorter` in Select2 4.0.0. '
                    );
                }

                // selectOnBlur was renamed to selectOnClose
                if (opts.selectOnBlur != null) {
                    console.warn(
                        'Select2: The `selectOnBlur` option has been renamed to `selectOnClose` in Select2 4.0.0.'
                    );
                }

                // ajax.results was renamed to ajax.processResults
                if (opts.ajax != null && opts.ajax.results != null) {
                    console.warn(
                        'Select2: The `ajax.results` option has been renamed to `ajax.processResults` in Select2 4.0.0.'
                    );
                }

                // format* options were renamed to language.*
                if (opts.formatNoResults != null) {
                    console.warn(
                        'Select2: The `formatNoResults` option has been renamed to `language.noResults` in Select2 4.0.0.'
                    );
                }
                if (opts.formatSearching != null) {
                    console.warn(
                        'Select2: The `formatSearching` option has been renamed to `language.searching` in Select2 4.0.0.'
                    );
                }
                if (opts.formatInputTooShort != null) {
                    console.warn(
                        'Select2: The `formatInputTooShort` option has been renamed to `language.inputTooShort` in Select2 4.0.0.'
                    );
                }
                if (opts.formatInputTooLong != null) {
                    console.warn(
                        'Select2: The `formatInputTooLong` option has been renamed to `language.inputTooLong` in Select2 4.0.0.'
                    );
                }
                if (opts.formatLoading != null) {
                    console.warn(
                        'Select2: The `formatLoading` option has been renamed to `language.loadingMore` in Select2 4.0.0.'
                    );
                }
                if (opts.formatSelectionTooBig != null) {
                    console.warn(
                        'Select2: The `formatSelectionTooBig` option has been renamed to `language.maximumSelected` in Select2 4.0.0.'
                    );
                }

                if (opts.element.data('select2Tags')) {
                    console.warn(
                        'Select2: The `data-select2-tags` attribute has been renamed to `data-tags` in Select2 4.0.0.'
                    );
                }
            }

            // Aliasing options renamed in Select2 4.0.0

            // data-select2-tags -> data-tags
            if (opts.element.data('tags') != null) {
                var tags = opts.element.data('tags');

                // data-tags should actually be a boolean
                if (!$.isArray(tags)) {
                    tags = [];
                }

                opts.element.data('select2Tags', tags);
            }

            // sortResults -> sorter
            if (opts.sorter != null) {
                opts.sortResults = opts.sorter;
            }

            // selectOnBlur -> selectOnClose
            if (opts.selectOnClose != null) {
                opts.selectOnBlur = opts.selectOnClose;
            }

            // ajax.results -> ajax.processResults
            if (opts.ajax != null) {
                if ($.isFunction(opts.ajax.processResults)) {
                    opts.ajax.results = opts.ajax.processResults;
                }
            }

            // Formatters/language options
            if (opts.language != null) {
                var lang = opts.language;

                // formatNoMatches -> language.noMatches
                if ($.isFunction(lang.noMatches)) {
                    opts.formatNoMatches = lang.noMatches;
                }

                // formatSearching -> language.searching
                if ($.isFunction(lang.searching)) {
                    opts.formatSearching = lang.searching;
                }

                // formatInputTooShort -> language.inputTooShort
                if ($.isFunction(lang.inputTooShort)) {
                    opts.formatInputTooShort = lang.inputTooShort;
                }

                // formatInputTooLong -> language.inputTooLong
                if ($.isFunction(lang.inputTooLong)) {
                    opts.formatInputTooLong = lang.inputTooLong;
                }

                // formatLoading -> language.loadingMore
                if ($.isFunction(lang.loadingMore)) {
                    opts.formatLoading = lang.loadingMore;
                }

                // formatSelectionTooBig -> language.maximumSelected
                if ($.isFunction(lang.maximumSelected)) {
                    opts.formatSelectionTooBig = lang.maximumSelected;
                }
            }

            opts = $.extend({}, {
                populateResults: function(container, results, query) {
                    var populate, id=this.opts.id, liveRegion=this.liveRegion;

                    populate=function(results, container, depth) {

                        var i, l, result, selectable, disabled, compound, node, label, innerContainer, formatted;

                        results = opts.sortResults(results, container, query);

                        // collect the created nodes for bulk append
                        var nodes = [];
                        for (i = 0, l = results.length; i < l; i = i + 1) {

                            result=results[i];

                            disabled = (result.disabled === true);
                            selectable = (!disabled) && (id(result) !== undefined);

                            compound=result.children && result.children.length > 0;

                            node=$("<li></li>");
                            node.addClass("select2-results-dept-"+depth);
                            node.addClass("select2-result");
                            node.addClass(selectable ? "select2-result-selectable" : "select2-result-unselectable");
                            if (disabled) { node.addClass("select2-disabled"); }
                            if (compound) { node.addClass("select2-result-with-children"); }
                            node.addClass(self.opts.formatResultCssClass(result));
                            node.attr("role", "presentation");

                            label=$(document.createElement("div"));
                            label.addClass("select2-result-label");
                            label.attr("id", "select2-result-label-" + nextUid());
                            label.attr("role", "option");

                            formatted=opts.formatResult(result, label, query, self.opts.escapeMarkup);
                            if (formatted!==undefined) {
                                label.html(formatted);
                                node.append(label);
                            }


                            if (compound) {
                                innerContainer=$("<ul></ul>");
                                innerContainer.addClass("select2-result-sub");
                                populate(result.children, innerContainer, depth+1);
                                node.append(innerContainer);
                            }

                            node.data("select2-data", result);
                            nodes.push(node[0]);
                        }

                        // bulk append the created nodes
                        container.append(nodes);
                        liveRegion.text(opts.formatMatches(results.length));
                    };

                    populate(results, container, 0);
                }
            }, $.fn.select2.defaults, opts);

            if (typeof(opts.id) !== "function") {
                idKey = opts.id;
                opts.id = function (e) { return e[idKey]; };
            }

            if ($.isArray(opts.element.data("select2Tags"))) {
                if ("tags" in opts) {
                    throw "tags specified as both an attribute 'data-select2-tags' and in options of Select2 " + opts.element.attr("id");
                }
                opts.tags=opts.element.data("select2Tags");
            }

            if (select) {
                opts.query = this.bind(function (query) {
                    var data = { results: [], more: false },
                        term = query.term,
                        children, placeholderOption, process;

                    process=function(element, collection) {
                        var group;
                        if (element.is("option")) {
                            if (query.matcher(term, element.text(), element)) {
                                collection.push(self.optionToData(element));
                            }
                        } else if (element.is("optgroup")) {
                            group=self.optionToData(element);
                            element.children().each2(function(i, elm) { process(elm, group.children); });
                            if (group.children.length>0) {
                                collection.push(group);
                            }
                        }
                    };

                    children=element.children();

                    // ignore the placeholder option if there is one
                    if (this.getPlaceholder() !== undefined && children.length > 0) {
                        placeholderOption = this.getPlaceholderOption();
                        if (placeholderOption) {
                            children=children.not(placeholderOption);
                        }
                    }

                    children.each2(function(i, elm) { process(elm, data.results); });

                    query.callback(data);
                });
                // this is needed because inside val() we construct choices from options and their id is hardcoded
                opts.id=function(e) { return e.id; };
            } else {
                if (!("query" in opts)) {
                    if ("ajax" in opts) {
                        ajaxUrl = opts.element.data("ajax-url");
                        if (ajaxUrl && ajaxUrl.length > 0) {
                            opts.ajax.url = ajaxUrl;
                        }
                        opts.query = ajax.call(opts.element, opts.ajax);
                    } else if ("data" in opts) {
                        opts.query = local(opts.data);
                    } else if ("tags" in opts) {
                        opts.query = tags(opts.tags);
                        if (opts.createSearchChoice === undefined) {
                            opts.createSearchChoice = function (term) { return {id: $.trim(term), text: $.trim(term)}; };
                        }
                        if (opts.initSelection === undefined) {
                            opts.initSelection = function (element, callback) {
                                var data = [];
                                $(splitVal(element.val(), opts.separator, opts.transformVal)).each(function () {
                                    var obj = { id: this, text: this },
                                        tags = opts.tags;
                                    if ($.isFunction(tags)) tags=tags();
                                    $(tags).each(function() { if (equal(this.id, obj.id)) { obj = this; return false; } });
                                    data.push(obj);
                                });

                                callback(data);
                            };
                        }
                    }
                }
            }
            if (typeof(opts.query) !== "function") {
                throw "query function not defined for Select2 " + opts.element.attr("id");
            }

            if (opts.createSearchChoicePosition === 'top') {
                opts.createSearchChoicePosition = function(list, item) { list.unshift(item); };
            }
            else if (opts.createSearchChoicePosition === 'bottom') {
                opts.createSearchChoicePosition = function(list, item) { list.push(item); };
            }
            else if (typeof(opts.createSearchChoicePosition) !== "function")  {
                throw "invalid createSearchChoicePosition option must be 'top', 'bottom' or a custom function";
            }

            return opts;
        },

        /**
         * Monitor the original element for changes and update select2 accordingly
         */
        // abstract
        monitorSource: function () {
            var el = this.opts.element, observer, self = this;

            el.on("change.select2", this.bind(function (e) {
                if (this.opts.element.data("select2-change-triggered") !== true) {
                    this.initSelection();
                }
            }));

            this._sync = this.bind(function () {

                // sync enabled state
                var disabled = el.prop("disabled");
                if (disabled === undefined) disabled = false;
                this.enable(!disabled);

                var readonly = el.prop("readonly");
                if (readonly === undefined) readonly = false;
                this.readonly(readonly);

                if (this.container) {
                    syncCssClasses(this.container, this.opts.element, this.opts.adaptContainerCssClass);
                    this.container.addClass(evaluate(this.opts.containerCssClass, this.opts.element));
                }

                if (this.dropdown) {
                    syncCssClasses(this.dropdown, this.opts.element, this.opts.adaptDropdownCssClass);
                    this.dropdown.addClass(evaluate(this.opts.dropdownCssClass, this.opts.element));
                }

            });

            // IE8-10 (IE9/10 won't fire propertyChange via attachEventListener)
            if (el.length && el[0].attachEvent) {
                el.each(function() {
                    this.attachEvent("onpropertychange", self._sync);
                });
            }

            // safari, chrome, firefox, IE11
            observer = window.MutationObserver || window.WebKitMutationObserver|| window.MozMutationObserver;
            if (observer !== undefined) {
                if (this.propertyObserver) { delete this.propertyObserver; this.propertyObserver = null; }
                this.propertyObserver = new observer(function (mutations) {
                    $.each(mutations, self._sync);
                });
                this.propertyObserver.observe(el.get(0), { attributes:true, subtree:false });
            }
        },

        // abstract
        triggerSelect: function(data) {
            var evt = $.Event("select2-selecting", { val: this.id(data), object: data, choice: data });
            this.opts.element.trigger(evt);
            return !evt.isDefaultPrevented();
        },

        /**
         * Triggers the change event on the source element
         */
        // abstract
        triggerChange: function (details) {

            details = details || {};
            details= $.extend({}, details, { type: "change", val: this.val() });
            // prevents recursive triggering
            this.opts.element.data("select2-change-triggered", true);
            this.opts.element.trigger(details);
            this.opts.element.data("select2-change-triggered", false);

            // some validation frameworks ignore the change event and listen instead to keyup, click for selects
            // so here we trigger the click event manually
            this.opts.element.click();

            // ValidationEngine ignores the change event and listens instead to blur
            // so here we trigger the blur event manually if so desired
            if (this.opts.blurOnChange)
                this.opts.element.blur();
        },

        //abstract
        isInterfaceEnabled: function()
        {
            return this.enabledInterface === true;
        },

        // abstract
        enableInterface: function() {
            var enabled = this._enabled && !this._readonly,
                disabled = !enabled;

            if (enabled === this.enabledInterface) return false;

            this.container.toggleClass("select2-container-disabled", disabled);
            this.close();
            this.enabledInterface = enabled;

            return true;
        },

        // abstract
        enable: function(enabled) {
            if (enabled === undefined) enabled = true;
            if (this._enabled === enabled) return;
            this._enabled = enabled;

            this.opts.element.prop("disabled", !enabled);
            this.enableInterface();
        },

        // abstract
        disable: function() {
            this.enable(false);
        },

        // abstract
        readonly: function(enabled) {
            if (enabled === undefined) enabled = false;
            if (this._readonly === enabled) return;
            this._readonly = enabled;

            this.opts.element.prop("readonly", enabled);
            this.enableInterface();
        },

        // abstract
        opened: function () {
            return (this.container) ? this.container.hasClass("select2-dropdown-open") : false;
        },

        // abstract
        positionDropdown: function() {
            var $dropdown = this.dropdown,
                container = this.container,
                offset = container.offset(),
                height = container.outerHeight(false),
                width = container.outerWidth(false),
                dropHeight = $dropdown.outerHeight(false),
                $window = $(window),
                windowWidth = $window.width(),
                windowHeight = $window.height(),
                viewPortRight = $window.scrollLeft() + windowWidth,
                viewportBottom = $window.scrollTop() + windowHeight,
                dropTop = offset.top + height,
                dropLeft = offset.left,
                enoughRoomBelow = dropTop + dropHeight <= viewportBottom,
                enoughRoomAbove = (offset.top - dropHeight) >= $window.scrollTop(),
                dropWidth = $dropdown.outerWidth(false),
                enoughRoomOnRight = function() {
                    return dropLeft + dropWidth <= viewPortRight;
                },
                enoughRoomOnLeft = function() {
                    return offset.left + viewPortRight + container.outerWidth(false)  > dropWidth;
                },
                aboveNow = $dropdown.hasClass("select2-drop-above"),
                bodyOffset,
                above,
                changeDirection,
                css,
                resultsListNode;

            // always prefer the current above/below alignment, unless there is not enough room
            if (aboveNow) {
                above = true;
                if (!enoughRoomAbove && enoughRoomBelow) {
                    changeDirection = true;
                    above = false;
                }
            } else {
                above = false;
                if (!enoughRoomBelow && enoughRoomAbove) {
                    changeDirection = true;
                    above = true;
                }
            }

            //if we are changing direction we need to get positions when dropdown is hidden;
            if (changeDirection) {
                $dropdown.hide();
                offset = this.container.offset();
                height = this.container.outerHeight(false);
                width = this.container.outerWidth(false);
                dropHeight = $dropdown.outerHeight(false);
                viewPortRight = $window.scrollLeft() + windowWidth;
                viewportBottom = $window.scrollTop() + windowHeight;
                dropTop = offset.top + height;
                dropLeft = offset.left;
                dropWidth = $dropdown.outerWidth(false);
                $dropdown.show();

                // fix so the cursor does not move to the left within the search-textbox in IE
                this.focusSearch();
            }

            if (this.opts.dropdownAutoWidth) {
                resultsListNode = $('.select2-results', $dropdown)[0];
                $dropdown.addClass('select2-drop-auto-width');
                $dropdown.css('width', '');
                // Add scrollbar width to dropdown if vertical scrollbar is present
                dropWidth = $dropdown.outerWidth(false) + (resultsListNode.scrollHeight === resultsListNode.clientHeight ? 0 : scrollBarDimensions.width);
                dropWidth > width ? width = dropWidth : dropWidth = width;
                dropHeight = $dropdown.outerHeight(false);
            }
            else {
                this.container.removeClass('select2-drop-auto-width');
            }

            //console.log("below/ droptop:", dropTop, "dropHeight", dropHeight, "sum", (dropTop+dropHeight)+" viewport bottom", viewportBottom, "enough?", enoughRoomBelow);
            //console.log("above/ offset.top", offset.top, "dropHeight", dropHeight, "top", (offset.top-dropHeight), "scrollTop", this.body.scrollTop(), "enough?", enoughRoomAbove);

            // fix positioning when body has an offset and is not position: static
            if (this.body.css('position') !== 'static') {
                bodyOffset = this.body.offset();
                dropTop -= bodyOffset.top;
                dropLeft -= bodyOffset.left;
            }

            if (!enoughRoomOnRight() && enoughRoomOnLeft()) {
                dropLeft = offset.left + this.container.outerWidth(false) - dropWidth;
            }

            css =  {
                left: dropLeft,
                width: width
            };

            if (above) {
                this.container.addClass("select2-drop-above");
                $dropdown.addClass("select2-drop-above");
                dropHeight = $dropdown.outerHeight(false);
                css.top = offset.top - dropHeight;
                css.bottom = 'auto';
            }
            else {
                css.top = dropTop;
                css.bottom = 'auto';
                this.container.removeClass("select2-drop-above");
                $dropdown.removeClass("select2-drop-above");
            }
            css = $.extend(css, evaluate(this.opts.dropdownCss, this.opts.element));

            $dropdown.css(css);
        },

        // abstract
        shouldOpen: function() {
            var event;

            if (this.opened()) return false;

            if (this._enabled === false || this._readonly === true) return false;

            event = $.Event("select2-opening");
            this.opts.element.trigger(event);
            return !event.isDefaultPrevented();
        },

        // abstract
        clearDropdownAlignmentPreference: function() {
            // clear the classes used to figure out the preference of where the dropdown should be opened
            this.container.removeClass("select2-drop-above");
            this.dropdown.removeClass("select2-drop-above");
        },

        /**
         * Opens the dropdown
         *
         * @return {Boolean} whether or not dropdown was opened. This method will return false if, for example,
         * the dropdown is already open, or if the 'open' event listener on the element called preventDefault().
         */
        // abstract
        open: function () {

            if (!this.shouldOpen()) return false;

            this.opening();

            // Only bind the document mousemove when the dropdown is visible
            $document.on("mousemove.select2Event", function (e) {
                lastMousePosition.x = e.pageX;
                lastMousePosition.y = e.pageY;
            });

            return true;
        },

        /**
         * Performs the opening of the dropdown
         */
        // abstract
        opening: function() {
            var cid = this.containerEventName,
                scroll = "scroll." + cid,
                resize = "resize."+cid,
                orient = "orientationchange."+cid,
                mask;

            this.container.addClass("select2-dropdown-open").addClass("select2-container-active");

            this.clearDropdownAlignmentPreference();

            if(this.dropdown[0] !== this.body.children().last()[0]) {
                this.dropdown.detach().appendTo(this.body);
            }

            // create the dropdown mask if doesn't already exist
            mask = $("#select2-drop-mask");
            if (mask.length === 0) {
                mask = $(document.createElement("div"));
                mask.attr("id","select2-drop-mask").attr("class","select2-drop-mask");
                mask.hide();
                mask.appendTo(this.body);
                mask.on("mousedown touchstart click", function (e) {
                    // Prevent IE from generating a click event on the body
                    reinsertElement(mask);

                    var dropdown = $("#select2-drop"), self;
                    if (dropdown.length > 0) {
                        self=dropdown.data("select2");
                        if (self.opts.selectOnBlur) {
                            self.selectHighlighted({noFocus: true});
                        }
                        self.close();
                        e.preventDefault();
                        e.stopPropagation();
                    }
                });
            }

            // ensure the mask is always right before the dropdown
            if (this.dropdown.prev()[0] !== mask[0]) {
                this.dropdown.before(mask);
            }

            // move the global id to the correct dropdown
            $("#select2-drop").removeAttr("id");
            this.dropdown.attr("id", "select2-drop");

            // show the elements
            mask.show();

            this.positionDropdown();
            this.dropdown.show();
            this.positionDropdown();

            this.dropdown.addClass("select2-drop-active");

            // attach listeners to events that can change the position of the container and thus require
            // the position of the dropdown to be updated as well so it does not come unglued from the container
            var that = this;
            this.container.parents().add(window).each(function () {
                $(this).on(resize+" "+scroll+" "+orient, function (e) {
                    if (that.opened()) that.positionDropdown();
                });
            });


        },

        // abstract
        close: function () {
            if (!this.opened()) return;

            var cid = this.containerEventName,
                scroll = "scroll." + cid,
                resize = "resize."+cid,
                orient = "orientationchange."+cid;

            // unbind event listeners
            this.container.parents().add(window).each(function () { $(this).off(scroll).off(resize).off(orient); });

            this.clearDropdownAlignmentPreference();

            $("#select2-drop-mask").hide();
            this.dropdown.removeAttr("id"); // only the active dropdown has the select2-drop id
            this.dropdown.hide();
            this.container.removeClass("select2-dropdown-open").removeClass("select2-container-active");
            this.results.empty();

            // Now that the dropdown is closed, unbind the global document mousemove event
            $document.off("mousemove.select2Event");

            this.clearSearch();
            this.search.removeClass("select2-active");

            // Remove the aria active descendant for highlighted element
            this.search.removeAttr("aria-activedescendant");
            this.opts.element.trigger($.Event("select2-close"));
        },

        /**
         * Opens control, sets input value, and updates results.
         */
        // abstract
        externalSearch: function (term) {
            this.open();
            this.search.val(term);
            this.updateResults(false);
        },

        // abstract
        clearSearch: function () {

        },

        /**
         * @return {Boolean} Whether or not search value was changed.
         * @private
         */
        prefillNextSearchTerm: function () {
            // initializes search's value with nextSearchTerm (if defined by user)
            // ignore nextSearchTerm if the dropdown is opened by the user pressing a letter
            if(this.search.val() !== "") {
                return false;
            }

            var nextSearchTerm = this.opts.nextSearchTerm(this.data(), this.lastSearchTerm);
            if(nextSearchTerm !== undefined){
                this.search.val(nextSearchTerm);
                this.search.select();
                return true;
            }

            return false;
        },

        //abstract
        getMaximumSelectionSize: function() {
            return evaluate(this.opts.maximumSelectionSize, this.opts.element);
        },

        // abstract
        ensureHighlightVisible: function () {
            var results = this.results, children, index, child, hb, rb, y, more, topOffset;

            index = this.highlight();

            if (index < 0) return;

            if (index == 0) {

                // if the first element is highlighted scroll all the way to the top,
                // that way any unselectable headers above it will also be scrolled
                // into view

                results.scrollTop(0);
                return;
            }

            children = this.findHighlightableChoices().find('.select2-result-label');

            child = $(children[index]);

            topOffset = (child.offset() || {}).top || 0;

            hb = topOffset + child.outerHeight(true);

            // if this is the last child lets also make sure select2-more-results is visible
            if (index === children.length - 1) {
                more = results.find("li.select2-more-results");
                if (more.length > 0) {
                    hb = more.offset().top + more.outerHeight(true);
                }
            }

            rb = results.offset().top + results.outerHeight(false);
            if (hb > rb) {
                results.scrollTop(results.scrollTop() + (hb - rb));
            }
            y = topOffset - results.offset().top;

            // make sure the top of the element is visible
            if (y < 0 && child.css('display') != 'none' ) {
                results.scrollTop(results.scrollTop() + y); // y is negative
            }
        },

        // abstract
        findHighlightableChoices: function() {
            return this.results.find(".select2-result-selectable:not(.select2-disabled):not(.select2-selected)");
        },

        // abstract
        moveHighlight: function (delta) {
            var choices = this.findHighlightableChoices(),
                index = this.highlight();

            while (index > -1 && index < choices.length) {
                index += delta;
                var choice = $(choices[index]);
                if (choice.hasClass("select2-result-selectable") && !choice.hasClass("select2-disabled") && !choice.hasClass("select2-selected")) {
                    this.highlight(index);
                    break;
                }
            }
        },

        // abstract
        highlight: function (index) {
            var choices = this.findHighlightableChoices(),
                choice,
                data;

            if (arguments.length === 0) {
                return indexOf(choices.filter(".select2-highlighted")[0], choices.get());
            }

            if (index >= choices.length) index = choices.length - 1;
            if (index < 0) index = 0;

            this.removeHighlight();

            choice = $(choices[index]);
            choice.addClass("select2-highlighted");

            // ensure assistive technology can determine the active choice
            this.search.attr("aria-activedescendant", choice.find(".select2-result-label").attr("id"));

            this.ensureHighlightVisible();

            this.liveRegion.text(choice.text());

            data = choice.data("select2-data");
            if (data) {
                this.opts.element.trigger({ type: "select2-highlight", val: this.id(data), choice: data });
            }
        },

        removeHighlight: function() {
            this.results.find(".select2-highlighted").removeClass("select2-highlighted");
        },

        touchMoved: function() {
            this._touchMoved = true;
        },

        clearTouchMoved: function() {
          this._touchMoved = false;
        },

        // abstract
        countSelectableResults: function() {
            return this.findHighlightableChoices().length;
        },

        // abstract
        highlightUnderEvent: function (event) {
            var el = $(event.target).closest(".select2-result-selectable");
            if (el.length > 0 && !el.is(".select2-highlighted")) {
                var choices = this.findHighlightableChoices();
                this.highlight(choices.index(el));
            } else if (el.length == 0) {
                // if we are over an unselectable item remove all highlights
                this.removeHighlight();
            }
        },

        // abstract
        loadMoreIfNeeded: function () {
            var results = this.results,
                more = results.find("li.select2-more-results"),
                below, // pixels the element is below the scroll fold, below==0 is when the element is starting to be visible
                page = this.resultsPage + 1,
                self=this,
                term=this.search.val(),
                context=this.context;

            if (more.length === 0) return;
            below = more.offset().top - results.offset().top - results.height();

            if (below <= this.opts.loadMorePadding) {
                more.addClass("select2-active");
                this.opts.query({
                        element: this.opts.element,
                        term: term,
                        page: page,
                        context: context,
                        matcher: this.opts.matcher,
                        callback: this.bind(function (data) {

                    // ignore a response if the select2 has been closed before it was received
                    if (!self.opened()) return;


                    self.opts.populateResults.call(this, results, data.results, {term: term, page: page, context:context});
                    self.postprocessResults(data, false, false);

                    if (data.more===true) {
                        more.detach().appendTo(results).html(self.opts.escapeMarkup(evaluate(self.opts.formatLoadMore, self.opts.element, page+1)));
                        window.setTimeout(function() { self.loadMoreIfNeeded(); }, 10);
                    } else {
                        more.remove();
                    }
                    self.positionDropdown();
                    self.resultsPage = page;
                    self.context = data.context;
                    this.opts.element.trigger({ type: "select2-loaded", items: data });
                })});
            }
        },

        /**
         * Default tokenizer function which does nothing
         */
        tokenize: function() {

        },

        /**
         * @param initial whether or not this is the call to this method right after the dropdown has been opened
         */
        // abstract
        updateResults: function (initial) {
            var search = this.search,
                results = this.results,
                opts = this.opts,
                data,
                self = this,
                input,
                term = search.val(),
                lastTerm = $.data(this.container, "select2-last-term"),
                // sequence number used to drop out-of-order responses
                queryNumber;

            // prevent duplicate queries against the same term
            if (initial !== true && lastTerm && equal(term, lastTerm)) return;

            $.data(this.container, "select2-last-term", term);

            // if the search is currently hidden we do not alter the results
            if (initial !== true && (this.showSearchInput === false || !this.opened())) {
                return;
            }

            function postRender() {
                search.removeClass("select2-active");
                self.positionDropdown();
                if (results.find('.select2-no-results,.select2-selection-limit,.select2-searching').length) {
                    self.liveRegion.text(results.text());
                }
                else {
                    self.liveRegion.text(self.opts.formatMatches(results.find('.select2-result-selectable:not(".select2-selected")').length));
                }
            }

            function render(html) {
                results.html(html);
                postRender();
            }

            queryNumber = ++this.queryCount;

            var maxSelSize = this.getMaximumSelectionSize();
            if (maxSelSize >=1) {
                data = this.data();
                if ($.isArray(data) && data.length >= maxSelSize && checkFormatter(opts.formatSelectionTooBig, "formatSelectionTooBig")) {
                    render("<li class='select2-selection-limit'>" + evaluate(opts.formatSelectionTooBig, opts.element, maxSelSize) + "</li>");
                    return;
                }
            }

            if (search.val().length < opts.minimumInputLength) {
                if (checkFormatter(opts.formatInputTooShort, "formatInputTooShort")) {
                    render("<li class='select2-no-results'>" + evaluate(opts.formatInputTooShort, opts.element, search.val(), opts.minimumInputLength) + "</li>");
                } else {
                    render("");
                }
                if (initial && this.showSearch) this.showSearch(true);
                return;
            }

            if (opts.maximumInputLength && search.val().length > opts.maximumInputLength) {
                if (checkFormatter(opts.formatInputTooLong, "formatInputTooLong")) {
                    render("<li class='select2-no-results'>" + evaluate(opts.formatInputTooLong, opts.element, search.val(), opts.maximumInputLength) + "</li>");
                } else {
                    render("");
                }
                return;
            }

            if (opts.formatSearching && this.findHighlightableChoices().length === 0) {
                render("<li class='select2-searching'>" + evaluate(opts.formatSearching, opts.element) + "</li>");
            }

            search.addClass("select2-active");

            this.removeHighlight();

            // give the tokenizer a chance to pre-process the input
            input = this.tokenize();
            if (input != undefined && input != null) {
                search.val(input);
            }

            this.resultsPage = 1;

            opts.query({
                element: opts.element,
                    term: search.val(),
                    page: this.resultsPage,
                    context: null,
                    matcher: opts.matcher,
                    callback: this.bind(function (data) {
                var def; // default choice

                // ignore old responses
                if (queryNumber != this.queryCount) {
                  return;
                }

                // ignore a response if the select2 has been closed before it was received
                if (!this.opened()) {
                    this.search.removeClass("select2-active");
                    return;
                }

                // handle ajax error
                if(data.hasError !== undefined && checkFormatter(opts.formatAjaxError, "formatAjaxError")) {
                    render("<li class='select2-ajax-error'>" + evaluate(opts.formatAjaxError, opts.element, data.jqXHR, data.textStatus, data.errorThrown) + "</li>");
                    return;
                }

                // save context, if any
                this.context = (data.context===undefined) ? null : data.context;
                // create a default choice and prepend it to the list
                if (this.opts.createSearchChoice && search.val() !== "") {
                    def = this.opts.createSearchChoice.call(self, search.val(), data.results);
                    if (def !== undefined && def !== null && self.id(def) !== undefined && self.id(def) !== null) {
                        if ($(data.results).filter(
                            function () {
                                return equal(self.id(this), self.id(def));
                            }).length === 0) {
                            this.opts.createSearchChoicePosition(data.results, def);
                        }
                    }
                }

                if (data.results.length === 0 && checkFormatter(opts.formatNoMatches, "formatNoMatches")) {
                    render("<li class='select2-no-results'>" + evaluate(opts.formatNoMatches, opts.element, search.val()) + "</li>");
                    if(this.showSearch){
                        this.showSearch(search.val());
                    }
                    return;
                }

                results.empty();
                self.opts.populateResults.call(this, results, data.results, {term: search.val(), page: this.resultsPage, context:null});

                if (data.more === true && checkFormatter(opts.formatLoadMore, "formatLoadMore")) {
                    results.append("<li class='select2-more-results'>" + opts.escapeMarkup(evaluate(opts.formatLoadMore, opts.element, this.resultsPage)) + "</li>");
                    window.setTimeout(function() { self.loadMoreIfNeeded(); }, 10);
                }

                this.postprocessResults(data, initial);

                postRender();

                this.opts.element.trigger({ type: "select2-loaded", items: data });
            })});
        },

        // abstract
        cancel: function () {
            this.close();
        },

        // abstract
        blur: function () {
            // if selectOnBlur == true, select the currently highlighted option
            if (this.opts.selectOnBlur)
                this.selectHighlighted({noFocus: true});

            this.close();
            this.container.removeClass("select2-container-active");
            // synonymous to .is(':focus'), which is available in jquery >= 1.6
            if (this.search[0] === document.activeElement) { this.search.blur(); }
            this.clearSearch();
            this.selection.find(".select2-search-choice-focus").removeClass("select2-search-choice-focus");
        },

        // abstract
        focusSearch: function () {
            focus(this.search);
        },

        // abstract
        selectHighlighted: function (options) {
            if (this._touchMoved) {
              this.clearTouchMoved();
              return;
            }
            var index=this.highlight(),
                highlighted=this.results.find(".select2-highlighted"),
                data = highlighted.closest('.select2-result').data("select2-data");

            if (data) {
                this.highlight(index);
                this.onSelect(data, options);
            } else if (options && options.noFocus) {
                this.close();
            }
        },

        // abstract
        getPlaceholder: function () {
            var placeholderOption;
            return this.opts.element.attr("placeholder") ||
                this.opts.element.attr("data-placeholder") || // jquery 1.4 compat
                this.opts.element.data("placeholder") ||
                this.opts.placeholder ||
                ((placeholderOption = this.getPlaceholderOption()) !== undefined ? placeholderOption.text() : undefined);
        },

        // abstract
        getPlaceholderOption: function() {
            if (this.select) {
                var firstOption = this.select.children('option').first();
                if (this.opts.placeholderOption !== undefined ) {
                    //Determine the placeholder option based on the specified placeholderOption setting
                    return (this.opts.placeholderOption === "first" && firstOption) ||
                           (typeof this.opts.placeholderOption === "function" && this.opts.placeholderOption(this.select));
                } else if ($.trim(firstOption.text()) === "" && firstOption.val() === "") {
                    //No explicit placeholder option specified, use the first if it's blank
                    return firstOption;
                }
            }
        },

        /**
         * Get the desired width for the container element.  This is
         * derived first from option `width` passed to select2, then
         * the inline 'style' on the original element, and finally
         * falls back to the jQuery calculated element width.
         */
        // abstract
        initContainerWidth: function () {
            function resolveContainerWidth() {
                var style, attrs, matches, i, l, attr;

                if (this.opts.width === "off") {
                    return null;
                } else if (this.opts.width === "element"){
                    return this.opts.element.outerWidth(false) === 0 ? 'auto' : this.opts.element.outerWidth(false) + 'px';
                } else if (this.opts.width === "copy" || this.opts.width === "resolve") {
                    // check if there is inline style on the element that contains width
                    style = this.opts.element.attr('style');
                    if (typeof(style) === "string") {
                        attrs = style.split(';');
                        for (i = 0, l = attrs.length; i < l; i = i + 1) {
                            attr = attrs[i].replace(/\s/g, '');
                            matches = attr.match(/^width:(([-+]?([0-9]*\.)?[0-9]+)(px|em|ex|%|in|cm|mm|pt|pc))/i);
                            if (matches !== null && matches.length >= 1)
                                return matches[1];
                        }
                    }

                    if (this.opts.width === "resolve") {
                        // next check if css('width') can resolve a width that is percent based, this is sometimes possible
                        // when attached to input type=hidden or elements hidden via css
                        style = this.opts.element.css('width');
                        if (style.indexOf("%") > 0) return style;

                        // finally, fallback on the calculated width of the element
                        return (this.opts.element.outerWidth(false) === 0 ? 'auto' : this.opts.element.outerWidth(false) + 'px');
                    }

                    return null;
                } else if ($.isFunction(this.opts.width)) {
                    return this.opts.width();
                } else {
                    return this.opts.width;
               }
            };

            var width = resolveContainerWidth.call(this);
            if (width !== null) {
                this.container.css("width", width);
            }
        }
    });

    SingleSelect2 = clazz(AbstractSelect2, {

        // single

        createContainer: function () {
            var container = $(document.createElement("div")).attr({
                "class": "select2-container"
            }).html([
                "<a href='javascript:void(0)' class='select2-choice' tabindex='-1'>",
                "   <span class='select2-chosen'>&#160;</span><abbr class='select2-search-choice-close'></abbr>",
                "   <span class='select2-arrow' role='presentation'><b role='presentation'></b></span>",
                "</a>",
                "<label for='' class='select2-offscreen'></label>",
                "<input class='select2-focusser select2-offscreen' type='text' aria-haspopup='true' role='button' />",
                "<div class='select2-drop select2-display-none'>",
                "   <div class='select2-search'>",
                "       <label for='' class='select2-offscreen'></label>",
                "       <input type='text' autocomplete='off' autocorrect='off' autocapitalize='off' spellcheck='false' class='select2-input' role='combobox' aria-expanded='true'",
                "       aria-autocomplete='list' />",
                "   </div>",
                "   <ul class='select2-results' role='listbox'>",
                "   </ul>",
                "</div>"].join(""));
            return container;
        },

        // single
        enableInterface: function() {
            if (this.parent.enableInterface.apply(this, arguments)) {
                this.focusser.prop("disabled", !this.isInterfaceEnabled());
            }
        },

        // single
        opening: function () {
            var el, range, len;

            if (this.opts.minimumResultsForSearch >= 0) {
                this.showSearch(true);
            }

            this.parent.opening.apply(this, arguments);

            if (this.showSearchInput !== false) {
                // IE appends focusser.val() at the end of field :/ so we manually insert it at the beginning using a range
                // all other browsers handle this just fine

                this.search.val(this.focusser.val());
            }
            if (this.opts.shouldFocusInput(this)) {
                this.search.focus();
                // move the cursor to the end after focussing, otherwise it will be at the beginning and
                // new text will appear *before* focusser.val()
                el = this.search.get(0);
                if (el.createTextRange) {
                    range = el.createTextRange();
                    range.collapse(false);
                    range.select();
                } else if (el.setSelectionRange) {
                    len = this.search.val().length;
                    el.setSelectionRange(len, len);
                }
            }

            this.prefillNextSearchTerm();

            this.focusser.prop("disabled", true).val("");
            this.updateResults(true);
            this.opts.element.trigger($.Event("select2-open"));
        },

        // single
        close: function () {
            if (!this.opened()) return;
            this.parent.close.apply(this, arguments);

            this.focusser.prop("disabled", false);

            if (this.opts.shouldFocusInput(this)) {
                this.focusser.focus();
            }
        },

        // single
        focus: function () {
            if (this.opened()) {
                this.close();
            } else {
                this.focusser.prop("disabled", false);
                if (this.opts.shouldFocusInput(this)) {
                    this.focusser.focus();
                }
            }
        },

        // single
        isFocused: function () {
            return this.container.hasClass("select2-container-active");
        },

        // single
        cancel: function () {
            this.parent.cancel.apply(this, arguments);
            this.focusser.prop("disabled", false);

            if (this.opts.shouldFocusInput(this)) {
                this.focusser.focus();
            }
        },

        // single
        destroy: function() {
            $("label[for='" + this.focusser.attr('id') + "']")
                .attr('for', this.opts.element.attr("id"));
            this.parent.destroy.apply(this, arguments);

            cleanupJQueryElements.call(this,
                "selection",
                "focusser"
            );
        },

        // single
        initContainer: function () {

            var selection,
                container = this.container,
                dropdown = this.dropdown,
                idSuffix = nextUid(),
                elementLabel;

            if (this.opts.minimumResultsForSearch < 0) {
                this.showSearch(false);
            } else {
                this.showSearch(true);
            }

            this.selection = selection = container.find(".select2-choice");

            this.focusser = container.find(".select2-focusser");

            // add aria associations
            selection.find(".select2-chosen").attr("id", "select2-chosen-"+idSuffix);
            this.focusser.attr("aria-labelledby", "select2-chosen-"+idSuffix);
            this.results.attr("id", "select2-results-"+idSuffix);
            this.search.attr("aria-owns", "select2-results-"+idSuffix);

            // rewrite labels from original element to focusser
            this.focusser.attr("id", "s2id_autogen"+idSuffix);

            elementLabel = $("label[for='" + this.opts.element.attr("id") + "']");
            this.opts.element.on('focus.select2', this.bind(function () { this.focus(); }));

            this.focusser.prev()
                .text(elementLabel.text())
                .attr('for', this.focusser.attr('id'));

            // Ensure the original element retains an accessible name
            var originalTitle = this.opts.element.attr("title");
            this.opts.element.attr("title", (originalTitle || elementLabel.text()));

            this.focusser.attr("tabindex", this.elementTabIndex);

            // write label for search field using the label from the focusser element
            this.search.attr("id", this.focusser.attr('id') + '_search');

            this.search.prev()
                .text($("label[for='" + this.focusser.attr('id') + "']").text())
                .attr('for', this.search.attr('id'));

            this.search.on("keydown", this.bind(function (e) {
                if (!this.isInterfaceEnabled()) return;

                // filter 229 keyCodes (input method editor is processing key input)
                if (229 == e.keyCode) return;

                if (e.which === KEY.PAGE_UP || e.which === KEY.PAGE_DOWN) {
                    // prevent the page from scrolling
                    killEvent(e);
                    return;
                }

                switch (e.which) {
                    case KEY.UP:
                    case KEY.DOWN:
                        this.moveHighlight((e.which === KEY.UP) ? -1 : 1);
                        killEvent(e);
                        return;
                    case KEY.ENTER:
                        this.selectHighlighted();
                        killEvent(e);
                        return;
                    case KEY.TAB:
                        this.selectHighlighted({noFocus: true});
                        return;
                    case KEY.ESC:
                        this.cancel(e);
                        killEvent(e);
                        return;
                }
            }));

            this.search.on("blur", this.bind(function(e) {
                // a workaround for chrome to keep the search field focussed when the scroll bar is used to scroll the dropdown.
                // without this the search field loses focus which is annoying
                if (document.activeElement === this.body.get(0)) {
                    window.setTimeout(this.bind(function() {
                        if (this.opened() && this.results && this.results.length > 1) {
                            this.search.focus();
                        }
                    }), 0);
                }
            }));

            this.focusser.on("keydown", this.bind(function (e) {
                if (!this.isInterfaceEnabled()) return;

                if (e.which === KEY.TAB || KEY.isControl(e) || KEY.isFunctionKey(e) || e.which === KEY.ESC) {
                    return;
                }

                if (this.opts.openOnEnter === false && e.which === KEY.ENTER) {
                    killEvent(e);
                    return;
                }

                if (e.which == KEY.DOWN || e.which == KEY.UP
                    || (e.which == KEY.ENTER && this.opts.openOnEnter)) {

                    if (e.altKey || e.ctrlKey || e.shiftKey || e.metaKey) return;

                    this.open();
                    killEvent(e);
                    return;
                }

                if (e.which == KEY.DELETE || e.which == KEY.BACKSPACE) {
                    if (this.opts.allowClear) {
                        this.clear();
                    }
                    killEvent(e);
                    return;
                }
            }));


            installKeyUpChangeEvent(this.focusser);
            this.focusser.on("keyup-change input", this.bind(function(e) {
                if (this.opts.minimumResultsForSearch >= 0) {
                    e.stopPropagation();
                    if (this.opened()) return;
                    this.open();
                }
            }));

            selection.on("mousedown touchstart", "abbr", this.bind(function (e) {
                if (!this.isInterfaceEnabled()) {
                    return;
                }

                this.clear();
                killEventImmediately(e);
                this.close();

                if (this.selection) {
                    this.selection.focus();
                }
            }));

            selection.on("mousedown touchstart", this.bind(function (e) {
                // Prevent IE from generating a click event on the body
                reinsertElement(selection);

                if (!this.container.hasClass("select2-container-active")) {
                    this.opts.element.trigger($.Event("select2-focus"));
                }

                if (this.opened()) {
                    this.close();
                } else if (this.isInterfaceEnabled()) {
                    this.open();
                }

                killEvent(e);
            }));

            dropdown.on("mousedown touchstart", this.bind(function() {
                if (this.opts.shouldFocusInput(this)) {
                    this.search.focus();
                }
            }));

            selection.on("focus", this.bind(function(e) {
                killEvent(e);
            }));

            this.focusser.on("focus", this.bind(function(){
                if (!this.container.hasClass("select2-container-active")) {
                    this.opts.element.trigger($.Event("select2-focus"));
                }
                this.container.addClass("select2-container-active");
            })).on("blur", this.bind(function() {
                if (!this.opened()) {
                    this.container.removeClass("select2-container-active");
                    this.opts.element.trigger($.Event("select2-blur"));
                }
            }));
            this.search.on("focus", this.bind(function(){
                if (!this.container.hasClass("select2-container-active")) {
                    this.opts.element.trigger($.Event("select2-focus"));
                }
                this.container.addClass("select2-container-active");
            }));

            this.initContainerWidth();
            this.opts.element.hide();
            this.setPlaceholder();

        },

        // single
        clear: function(triggerChange) {
            var data=this.selection.data("select2-data");
            if (data) { // guard against queued quick consecutive clicks
                var evt = $.Event("select2-clearing");
                this.opts.element.trigger(evt);
                if (evt.isDefaultPrevented()) {
                    return;
                }
                var placeholderOption = this.getPlaceholderOption();
                this.opts.element.val(placeholderOption ? placeholderOption.val() : "");
                this.selection.find(".select2-chosen").empty();
                this.selection.removeData("select2-data");
                this.setPlaceholder();

                if (triggerChange !== false){
                    this.opts.element.trigger({ type: "select2-removed", val: this.id(data), choice: data });
                    this.triggerChange({removed:data});
                }
            }
        },

        /**
         * Sets selection based on source element's value
         */
        // single
        initSelection: function () {
            var selected;
            if (this.isPlaceholderOptionSelected()) {
                this.updateSelection(null);
                this.close();
                this.setPlaceholder();
            } else {
                var self = this;
                this.opts.initSelection.call(null, this.opts.element, function(selected){
                    if (selected !== undefined && selected !== null) {
                        self.updateSelection(selected);
                        self.close();
                        self.setPlaceholder();
                        self.lastSearchTerm = self.search.val();
                    }
                });
            }
        },

        isPlaceholderOptionSelected: function() {
            var placeholderOption;
            if (this.getPlaceholder() === undefined) return false; // no placeholder specified so no option should be considered
            return ((placeholderOption = this.getPlaceholderOption()) !== undefined && placeholderOption.prop("selected"))
                || (this.opts.element.val() === "")
                || (this.opts.element.val() === undefined)
                || (this.opts.element.val() === null);
        },

        // single
        prepareOpts: function () {
            var opts = this.parent.prepareOpts.apply(this, arguments),
                self=this;

            if (opts.element.get(0).tagName.toLowerCase() === "select") {
                // install the selection initializer
                opts.initSelection = function (element, callback) {
                    var selected = element.find("option").filter(function() { return this.selected && !this.disabled });
                    // a single select box always has a value, no need to null check 'selected'
                    callback(self.optionToData(selected));
                };
            } else if ("data" in opts) {
                // install default initSelection when applied to hidden input and data is local
                opts.initSelection = opts.initSelection || function (element, callback) {
                    var id = element.val();
                    //search in data by id, storing the actual matching item
                    var match = null;
                    opts.query({
                        matcher: function(term, text, el){
                            var is_match = equal(id, opts.id(el));
                            if (is_match) {
                                match = el;
                            }
                            return is_match;
                        },
                        callback: !$.isFunction(callback) ? $.noop : function() {
                            callback(match);
                        }
                    });
                };
            }

            return opts;
        },

        // single
        getPlaceholder: function() {
            // if a placeholder is specified on a single select without a valid placeholder option ignore it
            if (this.select) {
                if (this.getPlaceholderOption() === undefined) {
                    return undefined;
                }
            }

            return this.parent.getPlaceholder.apply(this, arguments);
        },

        // single
        setPlaceholder: function () {
            var placeholder = this.getPlaceholder();

            if (this.isPlaceholderOptionSelected() && placeholder !== undefined) {

                // check for a placeholder option if attached to a select
                if (this.select && this.getPlaceholderOption() === undefined) return;

                this.selection.find(".select2-chosen").html(this.opts.escapeMarkup(placeholder));

                this.selection.addClass("select2-default");

                this.container.removeClass("select2-allowclear");
            }
        },

        // single
        postprocessResults: function (data, initial, noHighlightUpdate) {
            var selected = 0, self = this, showSearchInput = true;

            // find the selected element in the result list

            this.findHighlightableChoices().each2(function (i, elm) {
                if (equal(self.id(elm.data("select2-data")), self.opts.element.val())) {
                    selected = i;
                    return false;
                }
            });

            // and highlight it
            if (noHighlightUpdate !== false) {
                if (initial === true && selected >= 0) {
                    this.highlight(selected);
                } else {
                    this.highlight(0);
                }
            }

            // hide the search box if this is the first we got the results and there are enough of them for search

            if (initial === true) {
                var min = this.opts.minimumResultsForSearch;
                if (min >= 0) {
                    this.showSearch(countResults(data.results) >= min);
                }
            }
        },

        // single
        showSearch: function(showSearchInput) {
            if (this.showSearchInput === showSearchInput) return;

            this.showSearchInput = showSearchInput;

            this.dropdown.find(".select2-search").toggleClass("select2-search-hidden", !showSearchInput);
            this.dropdown.find(".select2-search").toggleClass("select2-offscreen", !showSearchInput);
            //add "select2-with-searchbox" to the container if search box is shown
            $(this.dropdown, this.container).toggleClass("select2-with-searchbox", showSearchInput);
        },

        // single
        onSelect: function (data, options) {

            if (!this.triggerSelect(data)) { return; }

            var old = this.opts.element.val(),
                oldData = this.data();

            this.opts.element.val(this.id(data));
            this.updateSelection(data);

            this.opts.element.trigger({ type: "select2-selected", val: this.id(data), choice: data });

            this.lastSearchTerm = this.search.val();
            this.close();

            if ((!options || !options.noFocus) && this.opts.shouldFocusInput(this)) {
                this.focusser.focus();
            }

            if (!equal(old, this.id(data))) {
                this.triggerChange({ added: data, removed: oldData });
            }
        },

        // single
        updateSelection: function (data) {

            var container=this.selection.find(".select2-chosen"), formatted, cssClass;

            this.selection.data("select2-data", data);

            container.empty();
            if (data !== null) {
                formatted=this.opts.formatSelection(data, container, this.opts.escapeMarkup);
            }
            if (formatted !== undefined) {
                container.append(formatted);
            }
            cssClass=this.opts.formatSelectionCssClass(data, container);
            if (cssClass !== undefined) {
                container.addClass(cssClass);
            }

            this.selection.removeClass("select2-default");

            if (this.opts.allowClear && this.getPlaceholder() !== undefined) {
                this.container.addClass("select2-allowclear");
            }
        },

        // single
        val: function () {
            var val,
                triggerChange = false,
                data = null,
                self = this,
                oldData = this.data();

            if (arguments.length === 0) {
                return this.opts.element.val();
            }

            val = arguments[0];

            if (arguments.length > 1) {
                triggerChange = arguments[1];

                if (this.opts.debug && console && console.warn) {
                    console.warn(
                        'Select2: The second option to `select2("val")` is not supported in Select2 4.0.0. ' +
                        'The `change` event will always be triggered in 4.0.0.'
                    );
                }
            }

            if (this.select) {
                if (this.opts.debug && console && console.warn) {
                    console.warn(
                        'Select2: Setting the value on a <select> using `select2("val")` is no longer supported in 4.0.0. ' +
                        'You can use the `.val(newValue).trigger("change")` method provided by jQuery instead.'
                    );
                }

                this.select
                    .val(val)
                    .find("option").filter(function() { return this.selected }).each2(function (i, elm) {
                        data = self.optionToData(elm);
                        return false;
                    });
                this.updateSelection(data);
                this.setPlaceholder();
                if (triggerChange) {
                    this.triggerChange({added: data, removed:oldData});
                }
            } else {
                // val is an id. !val is true for [undefined,null,'',0] - 0 is legal
                if (!val && val !== 0) {
                    this.clear(triggerChange);
                    return;
                }
                if (this.opts.initSelection === undefined) {
                    throw new Error("cannot call val() if initSelection() is not defined");
                }
                this.opts.element.val(val);
                this.opts.initSelection(this.opts.element, function(data){
                    self.opts.element.val(!data ? "" : self.id(data));
                    self.updateSelection(data);
                    self.setPlaceholder();
                    if (triggerChange) {
                        self.triggerChange({added: data, removed:oldData});
                    }
                });
            }
        },

        // single
        clearSearch: function () {
            this.search.val("");
            this.focusser.val("");
        },

        // single
        data: function(value) {
            var data,
                triggerChange = false;

            if (arguments.length === 0) {
                data = this.selection.data("select2-data");
                if (data == undefined) data = null;
                return data;
            } else {
                if (opts.debug && console && console.warn) {
                    console.warn(
                        'Select2: The `select2("data")` method can no longer set selected values in 4.0.0, ' +
                        'consider using the `.val()` method instead.'
                    );
                }

                if (arguments.length > 1) {
                    triggerChange = arguments[1];
                }
                if (!value) {
                    this.clear(triggerChange);
                } else {
                    data = this.data();
                    this.opts.element.val(!value ? "" : this.id(value));
                    this.updateSelection(value);
                    if (triggerChange) {
                        this.triggerChange({added: value, removed:data});
                    }
                }
            }
        }
    });

    MultiSelect2 = clazz(AbstractSelect2, {

        // multi
        createContainer: function () {
            var container = $(document.createElement("div")).attr({
                "class": "select2-container select2-container-multi"
            }).html([
                "<ul class='select2-choices'>",
                "  <li class='select2-search-field'>",
                "    <label for='' class='select2-offscreen'></label>",
                "    <input type='text' autocomplete='off' autocorrect='off' autocapitalize='off' spellcheck='false' class='select2-input'>",
                "  </li>",
                "</ul>",
                "<div class='select2-drop select2-drop-multi select2-display-none'>",
                "   <ul class='select2-results'>",
                "   </ul>",
                "</div>"].join(""));
            return container;
        },

        // multi
        prepareOpts: function () {
            var opts = this.parent.prepareOpts.apply(this, arguments),
                self=this;

            // TODO validate placeholder is a string if specified
            if (opts.element.get(0).tagName.toLowerCase() === "select") {
                // install the selection initializer
                opts.initSelection = function (element, callback) {

                    var data = [];

                    element.find("option").filter(function() { return this.selected && !this.disabled }).each2(function (i, elm) {
                        data.push(self.optionToData(elm));
                    });
                    callback(data);
                };
            } else if ("data" in opts) {
                // install default initSelection when applied to hidden input and data is local
                opts.initSelection = opts.initSelection || function (element, callback) {
                    var ids = splitVal(element.val(), opts.separator, opts.transformVal);
                    //search in data by array of ids, storing matching items in a list
                    var matches = [];
                    opts.query({
                        matcher: function(term, text, el){
                            var is_match = $.grep(ids, function(id) {
                                return equal(id, opts.id(el));
                            }).length;
                            if (is_match) {
                                matches.push(el);
                            }
                            return is_match;
                        },
                        callback: !$.isFunction(callback) ? $.noop : function() {
                            // reorder matches based on the order they appear in the ids array because right now
                            // they are in the order in which they appear in data array
                            var ordered = [];
                            for (var i = 0; i < ids.length; i++) {
                                var id = ids[i];
                                for (var j = 0; j < matches.length; j++) {
                                    var match = matches[j];
                                    if (equal(id, opts.id(match))) {
                                        ordered.push(match);
                                        matches.splice(j, 1);
                                        break;
                                    }
                                }
                            }
                            callback(ordered);
                        }
                    });
                };
            }

            return opts;
        },

        // multi
        selectChoice: function (choice) {

            var selected = this.container.find(".select2-search-choice-focus");
            if (selected.length && choice && choice[0] == selected[0]) {

            } else {
                if (selected.length) {
                    this.opts.element.trigger("choice-deselected", selected);
                }
                selected.removeClass("select2-search-choice-focus");
                if (choice && choice.length) {
                    this.close();
                    choice.addClass("select2-search-choice-focus");
                    this.opts.element.trigger("choice-selected", choice);
                }
            }
        },

        // multi
        destroy: function() {
            $("label[for='" + this.search.attr('id') + "']")
                .attr('for', this.opts.element.attr("id"));
            this.parent.destroy.apply(this, arguments);

            cleanupJQueryElements.call(this,
                "searchContainer",
                "selection"
            );
        },

        // multi
        initContainer: function () {

            var selector = ".select2-choices", selection;

            this.searchContainer = this.container.find(".select2-search-field");
            this.selection = selection = this.container.find(selector);

            var _this = this;
            this.selection.on("click", ".select2-container:not(.select2-container-disabled) .select2-search-choice:not(.select2-locked)", function (e) {
                _this.search[0].focus();
                _this.selectChoice($(this));
            });

            // rewrite labels from original element to focusser
            this.search.attr("id", "s2id_autogen"+nextUid());

            this.search.prev()
                .text($("label[for='" + this.opts.element.attr("id") + "']").text())
                .attr('for', this.search.attr('id'));
            this.opts.element.on('focus.select2', this.bind(function () { this.focus(); }));

            this.search.on("input paste", this.bind(function() {
                if (this.search.attr('placeholder') && this.search.val().length == 0) return;
                if (!this.isInterfaceEnabled()) return;
                if (!this.opened()) {
                    this.open();
                }
            }));

            this.search.attr("tabindex", this.elementTabIndex);

            this.keydowns = 0;
            this.search.on("keydown", this.bind(function (e) {
                if (!this.isInterfaceEnabled()) return;

                ++this.keydowns;
                var selected = selection.find(".select2-search-choice-focus");
                var prev = selected.prev(".select2-search-choice:not(.select2-locked)");
                var next = selected.next(".select2-search-choice:not(.select2-locked)");
                var pos = getCursorInfo(this.search);

                if (selected.length &&
                    (e.which == KEY.LEFT || e.which == KEY.RIGHT || e.which == KEY.BACKSPACE || e.which == KEY.DELETE || e.which == KEY.ENTER)) {
                    var selectedChoice = selected;
                    if (e.which == KEY.LEFT && prev.length) {
                        selectedChoice = prev;
                    }
                    else if (e.which == KEY.RIGHT) {
                        selectedChoice = next.length ? next : null;
                    }
                    else if (e.which === KEY.BACKSPACE) {
                        if (this.unselect(selected.first())) {
                            this.search.width(10);
                            selectedChoice = prev.length ? prev : next;
                        }
                    } else if (e.which == KEY.DELETE) {
                        if (this.unselect(selected.first())) {
                            this.search.width(10);
                            selectedChoice = next.length ? next : null;
                        }
                    } else if (e.which == KEY.ENTER) {
                        selectedChoice = null;
                    }

                    this.selectChoice(selectedChoice);
                    killEvent(e);
                    if (!selectedChoice || !selectedChoice.length) {
                        this.open();
                    }
                    return;
                } else if (((e.which === KEY.BACKSPACE && this.keydowns == 1)
                    || e.which == KEY.LEFT) && (pos.offset == 0 && !pos.length)) {

                    this.selectChoice(selection.find(".select2-search-choice:not(.select2-locked)").last());
                    killEvent(e);
                    return;
                } else {
                    this.selectChoice(null);
                }

                if (this.opened()) {
                    switch (e.which) {
                    case KEY.UP:
                    case KEY.DOWN:
                        this.moveHighlight((e.which === KEY.UP) ? -1 : 1);
                        killEvent(e);
                        return;
                    case KEY.ENTER:
                        this.selectHighlighted();
                        killEvent(e);
                        return;
                    case KEY.TAB:
                        this.selectHighlighted({noFocus:true});
                        this.close();
                        return;
                    case KEY.ESC:
                        this.cancel(e);
                        killEvent(e);
                        return;
                    }
                }

                if (e.which === KEY.TAB || KEY.isControl(e) || KEY.isFunctionKey(e)
                 || e.which === KEY.BACKSPACE || e.which === KEY.ESC) {
                    return;
                }

                if (e.which === KEY.ENTER) {
                    if (this.opts.openOnEnter === false) {
                        return;
                    } else if (e.altKey || e.ctrlKey || e.shiftKey || e.metaKey) {
                        return;
                    }
                }

                this.open();

                if (e.which === KEY.PAGE_UP || e.which === KEY.PAGE_DOWN) {
                    // prevent the page from scrolling
                    killEvent(e);
                }

                if (e.which === KEY.ENTER) {
                    // prevent form from being submitted
                    killEvent(e);
                }

            }));

            this.search.on("keyup", this.bind(function (e) {
                this.keydowns = 0;
                this.resizeSearch();
            })
            );

            this.search.on("blur", this.bind(function(e) {
                this.container.removeClass("select2-container-active");
                this.search.removeClass("select2-focused");
                this.selectChoice(null);
                if (!this.opened()) this.clearSearch();
                e.stopImmediatePropagation();
                this.opts.element.trigger($.Event("select2-blur"));
            }));

            this.container.on("click", selector, this.bind(function (e) {
                if (!this.isInterfaceEnabled()) return;
                if ($(e.target).closest(".select2-search-choice").length > 0) {
                    // clicked inside a select2 search choice, do not open
                    return;
                }
                this.selectChoice(null);
                this.clearPlaceholder();
                if (!this.container.hasClass("select2-container-active")) {
                    this.opts.element.trigger($.Event("select2-focus"));
                }
                this.open();
                this.focusSearch();
                e.preventDefault();
            }));

            this.container.on("focus", selector, this.bind(function () {
                if (!this.isInterfaceEnabled()) return;
                if (!this.container.hasClass("select2-container-active")) {
                    this.opts.element.trigger($.Event("select2-focus"));
                }
                this.container.addClass("select2-container-active");
                this.dropdown.addClass("select2-drop-active");
                this.clearPlaceholder();
            }));

            this.initContainerWidth();
            this.opts.element.hide();

            // set the placeholder if necessary
            this.clearSearch();
        },

        // multi
        enableInterface: function() {
            if (this.parent.enableInterface.apply(this, arguments)) {
                this.search.prop("disabled", !this.isInterfaceEnabled());
            }
        },

        // multi
        initSelection: function () {
            var data;
            if (this.opts.element.val() === "" && this.opts.element.text() === "") {
                this.updateSelection([]);
                this.close();
                // set the placeholder if necessary
                this.clearSearch();
            }
            if (this.select || this.opts.element.val() !== "") {
                var self = this;
                this.opts.initSelection.call(null, this.opts.element, function(data){
                    if (data !== undefined && data !== null) {
                        self.updateSelection(data);
                        self.close();
                        // set the placeholder if necessary
                        self.clearSearch();
                    }
                });
            }
        },

        // multi
        clearSearch: function () {
            var placeholder = this.getPlaceholder(),
                maxWidth = this.getMaxSearchWidth();

            if (placeholder !== undefined  && this.getVal().length === 0 && this.search.hasClass("select2-focused") === false) {
                this.search.val(placeholder).addClass("select2-default");
                // stretch the search box to full width of the container so as much of the placeholder is visible as possible
                // we could call this.resizeSearch(), but we do not because that requires a sizer and we do not want to create one so early because of a firefox bug, see #944
                this.search.width(maxWidth > 0 ? maxWidth : this.container.css("width"));
            } else {
                this.search.val("").width(10);
            }
        },

        // multi
        clearPlaceholder: function () {
            if (this.search.hasClass("select2-default")) {
                this.search.val("").removeClass("select2-default");
            }
        },

        // multi
        opening: function () {
            this.clearPlaceholder(); // should be done before super so placeholder is not used to search
            this.resizeSearch();

            this.parent.opening.apply(this, arguments);

            this.focusSearch();

            this.prefillNextSearchTerm();
            this.updateResults(true);

            if (this.opts.shouldFocusInput(this)) {
                this.search.focus();
            }
            this.opts.element.trigger($.Event("select2-open"));
        },

        // multi
        close: function () {
            if (!this.opened()) return;
            this.parent.close.apply(this, arguments);
        },

        // multi
        focus: function () {
            this.close();
            this.search.focus();
        },

        // multi
        isFocused: function () {
            return this.search.hasClass("select2-focused");
        },

        // multi
        updateSelection: function (data) {
            var ids = {}, filtered = [], self = this;

            // filter out duplicates
            $(data).each(function () {
                if (!(self.id(this) in ids)) {
                    ids[self.id(this)] = 0;
                    filtered.push(this);
                }
            });

            this.selection.find(".select2-search-choice").remove();
            this.addSelectedChoice(filtered);
            self.postprocessResults();
        },

        // multi
        tokenize: function() {
            var input = this.search.val();
            input = this.opts.tokenizer.call(this, input, this.data(), this.bind(this.onSelect), this.opts);
            if (input != null && input != undefined) {
                this.search.val(input);
                if (input.length > 0) {
                    this.open();
                }
            }

        },

        // multi
        onSelect: function (data, options) {

            if (!this.triggerSelect(data) || data.text === "") { return; }

            this.addSelectedChoice(data);

            this.opts.element.trigger({ type: "selected", val: this.id(data), choice: data });

            // keep track of the search's value before it gets cleared
            this.lastSearchTerm = this.search.val();

            this.clearSearch();
            this.updateResults();

            if (this.select || !this.opts.closeOnSelect) this.postprocessResults(data, false, this.opts.closeOnSelect===true);

            if (this.opts.closeOnSelect) {
                this.close();
                this.search.width(10);
            } else {
                if (this.countSelectableResults()>0) {
                    this.search.width(10);
                    this.resizeSearch();
                    if (this.getMaximumSelectionSize() > 0 && this.val().length >= this.getMaximumSelectionSize()) {
                        // if we reached max selection size repaint the results so choices
                        // are replaced with the max selection reached message
                        this.updateResults(true);
                    } else {
                        // initializes search's value with nextSearchTerm and update search result
                        if (this.prefillNextSearchTerm()) {
                            this.updateResults();
                        }
                    }
                    this.positionDropdown();
                } else {
                    // if nothing left to select close
                    this.close();
                    this.search.width(10);
                }
            }

            // since its not possible to select an element that has already been
            // added we do not need to check if this is a new element before firing change
            this.triggerChange({ added: data });

            if (!options || !options.noFocus)
                this.focusSearch();
        },

        // multi
        cancel: function () {
            this.close();
            this.focusSearch();
        },

        addSelectedChoice: function (data) {
            var val = this.getVal(), self = this;
            $(data).each(function () {
                val.push(self.createChoice(this));
            });
            this.setVal(val);
        },

        createChoice: function (data) {
            var enableChoice = !data.locked,
                enabledItem = $(
                    "<li class='select2-search-choice'>" +
                    "    <div></div>" +
                    "    <a href='#' class='select2-search-choice-close' tabindex='-1'></a>" +
                    "</li>"),
                disabledItem = $(
                    "<li class='select2-search-choice select2-locked'>" +
                    "<div></div>" +
                    "</li>");
            var choice = enableChoice ? enabledItem : disabledItem,
                id = this.id(data),
                formatted,
                cssClass;

            formatted=this.opts.formatSelection(data, choice.find("div"), this.opts.escapeMarkup);
            if (formatted != undefined) {
                choice.find("div").replaceWith($("<div></div>").html(formatted));
            }
            cssClass=this.opts.formatSelectionCssClass(data, choice.find("div"));
            if (cssClass != undefined) {
                choice.addClass(cssClass);
            }

            if(enableChoice){
              choice.find(".select2-search-choice-close")
                  .on("mousedown", killEvent)
                  .on("click dblclick", this.bind(function (e) {
                  if (!this.isInterfaceEnabled()) return;

                  this.unselect($(e.target));
                  this.selection.find(".select2-search-choice-focus").removeClass("select2-search-choice-focus");
                  killEvent(e);
                  this.close();
                  this.focusSearch();
              })).on("focus", this.bind(function () {
                  if (!this.isInterfaceEnabled()) return;
                  this.container.addClass("select2-container-active");
                  this.dropdown.addClass("select2-drop-active");
              }));
            }

            choice.data("select2-data", data);
            choice.insertBefore(this.searchContainer);

            return id;
        },

        // multi
        unselect: function (selected) {
            var val = this.getVal(),
                data,
                index;
            selected = selected.closest(".select2-search-choice");

            if (selected.length === 0) {
                throw "Invalid argument: " + selected + ". Must be .select2-search-choice";
            }

            data = selected.data("select2-data");

            if (!data) {
                // prevent a race condition when the 'x' is clicked really fast repeatedly the event can be queued
                // and invoked on an element already removed
                return;
            }

            var evt = $.Event("select2-removing");
            evt.val = this.id(data);
            evt.choice = data;
            this.opts.element.trigger(evt);

            if (evt.isDefaultPrevented()) {
                return false;
            }

            while((index = indexOf(this.id(data), val)) >= 0) {
                val.splice(index, 1);
                this.setVal(val);
                if (this.select) this.postprocessResults();
            }

            selected.remove();

            this.opts.element.trigger({ type: "select2-removed", val: this.id(data), choice: data });
            this.triggerChange({ removed: data });

            return true;
        },

        // multi
        postprocessResults: function (data, initial, noHighlightUpdate) {
            var val = this.getVal(),
                choices = this.results.find(".select2-result"),
                compound = this.results.find(".select2-result-with-children"),
                self = this;

            choices.each2(function (i, choice) {
                var id = self.id(choice.data("select2-data"));
                if (indexOf(id, val) >= 0) {
                    choice.addClass("select2-selected");
                    // mark all children of the selected parent as selected
                    choice.find(".select2-result-selectable").addClass("select2-selected");
                }
            });

            compound.each2(function(i, choice) {
                // hide an optgroup if it doesn't have any selectable children
                if (!choice.is('.select2-result-selectable')
                    && choice.find(".select2-result-selectable:not(.select2-selected)").length === 0) {
                    choice.addClass("select2-selected");
                }
            });

            if (this.highlight() == -1 && noHighlightUpdate !== false && this.opts.closeOnSelect === true){
                self.highlight(0);
            }

            //If all results are chosen render formatNoMatches
            if(!this.opts.createSearchChoice && !choices.filter('.select2-result:not(.select2-selected)').length > 0){
                if(!data || data && !data.more && this.results.find(".select2-no-results").length === 0) {
                    if (checkFormatter(self.opts.formatNoMatches, "formatNoMatches")) {
                        this.results.append("<li class='select2-no-results'>" + evaluate(self.opts.formatNoMatches, self.opts.element, self.search.val()) + "</li>");
                    }
                }
            }

        },

        // multi
        getMaxSearchWidth: function() {
            return this.selection.width() - getSideBorderPadding(this.search);
        },

        // multi
        resizeSearch: function () {
            var minimumWidth, left, maxWidth, containerLeft, searchWidth,
                sideBorderPadding = getSideBorderPadding(this.search);

            minimumWidth = measureTextWidth(this.search) + 10;

            left = this.search.offset().left;

            maxWidth = this.selection.width();
            containerLeft = this.selection.offset().left;

            searchWidth = maxWidth - (left - containerLeft) - sideBorderPadding;

            if (searchWidth < minimumWidth) {
                searchWidth = maxWidth - sideBorderPadding;
            }

            if (searchWidth < 40) {
                searchWidth = maxWidth - sideBorderPadding;
            }

            if (searchWidth <= 0) {
              searchWidth = minimumWidth;
            }

            this.search.width(Math.floor(searchWidth));
        },

        // multi
        getVal: function () {
            var val;
            if (this.select) {
                val = this.select.val();
                return val === null ? [] : val;
            } else {
                val = this.opts.element.val();
                return splitVal(val, this.opts.separator, this.opts.transformVal);
            }
        },

        // multi
        setVal: function (val) {
            if (this.select) {
                this.select.val(val);
            } else {
                var unique = [], valMap = {};
                // filter out duplicates
                $(val).each(function () {
                    if (!(this in valMap)) {
                        unique.push(this);
                        valMap[this] = 0;
                    }
                });
                this.opts.element.val(unique.length === 0 ? "" : unique.join(this.opts.separator));
            }
        },

        // multi
        buildChangeDetails: function (old, current) {
            var current = current.slice(0),
                old = old.slice(0);

            // remove intersection from each array
            for (var i = 0; i < current.length; i++) {
                for (var j = 0; j < old.length; j++) {
                    if (equal(this.opts.id(current[i]), this.opts.id(old[j]))) {
                        current.splice(i, 1);
                        i--;
                        old.splice(j, 1);
                        break;
                    }
                }
            }

            return {added: current, removed: old};
        },


        // multi
        val: function (val, triggerChange) {
            var oldData, self=this;

            if (arguments.length === 0) {
                return this.getVal();
            }

            oldData=this.data();
            if (!oldData.length) oldData=[];

            // val is an id. !val is true for [undefined,null,'',0] - 0 is legal
            if (!val && val !== 0) {
                this.opts.element.val("");
                this.updateSelection([]);
                this.clearSearch();
                if (triggerChange) {
                    this.triggerChange({added: this.data(), removed: oldData});
                }
                return;
            }

            // val is a list of ids
            this.setVal(val);

            if (this.select) {
                this.opts.initSelection(this.select, this.bind(this.updateSelection));
                if (triggerChange) {
                    this.triggerChange(this.buildChangeDetails(oldData, this.data()));
                }
            } else {
                if (this.opts.initSelection === undefined) {
                    throw new Error("val() cannot be called if initSelection() is not defined");
                }

                this.opts.initSelection(this.opts.element, function(data){
                    var ids=$.map(data, self.id);
                    self.setVal(ids);
                    self.updateSelection(data);
                    self.clearSearch();
                    if (triggerChange) {
                        self.triggerChange(self.buildChangeDetails(oldData, self.data()));
                    }
                });
            }
            this.clearSearch();
        },

        // multi
        onSortStart: function() {
            if (this.select) {
                throw new Error("Sorting of elements is not supported when attached to <select>. Attach to <input type='hidden'/> instead.");
            }

            // collapse search field into 0 width so its container can be collapsed as well
            this.search.width(0);
            // hide the container
            this.searchContainer.hide();
        },

        // multi
        onSortEnd:function() {

            var val=[], self=this;

            // show search and move it to the end of the list
            this.searchContainer.show();
            // make sure the search container is the last item in the list
            this.searchContainer.appendTo(this.searchContainer.parent());
            // since we collapsed the width in dragStarted, we resize it here
            this.resizeSearch();

            // update selection
            this.selection.find(".select2-search-choice").each(function() {
                val.push(self.opts.id($(this).data("select2-data")));
            });
            this.setVal(val);
            this.triggerChange();
        },

        // multi
        data: function(values, triggerChange) {
            var self=this, ids, old;
            if (arguments.length === 0) {
                 return this.selection
                     .children(".select2-search-choice")
                     .map(function() { return $(this).data("select2-data"); })
                     .get();
            } else {
                old = this.data();
                if (!values) { values = []; }
                ids = $.map(values, function(e) { return self.opts.id(e); });
                this.setVal(ids);
                this.updateSelection(values);
                this.clearSearch();
                if (triggerChange) {
                    this.triggerChange(this.buildChangeDetails(old, this.data()));
                }
            }
        }
    });

    $.fn.select2 = function () {

        var args = Array.prototype.slice.call(arguments, 0),
            opts,
            select2,
            method, value, multiple,
            allowedMethods = ["val", "destroy", "opened", "open", "close", "focus", "isFocused", "container", "dropdown", "onSortStart", "onSortEnd", "enable", "disable", "readonly", "positionDropdown", "data", "search"],
            valueMethods = ["opened", "isFocused", "container", "dropdown"],
            propertyMethods = ["val", "data"],
            methodsMap = { search: "externalSearch" };

        this.each(function () {
            if (args.length === 0 || typeof(args[0]) === "object") {
                opts = args.length === 0 ? {} : $.extend({}, args[0]);
                opts.element = $(this);

                if (opts.element.get(0).tagName.toLowerCase() === "select") {
                    multiple = opts.element.prop("multiple");
                } else {
                    multiple = opts.multiple || false;
                    if ("tags" in opts) {opts.multiple = multiple = true;}
                }

                select2 = multiple ? new window.Select2["class"].multi() : new window.Select2["class"].single();
                select2.init(opts);
            } else if (typeof(args[0]) === "string") {

                if (indexOf(args[0], allowedMethods) < 0) {
                    throw "Unknown method: " + args[0];
                }

                value = undefined;
                select2 = $(this).data("select2");
                if (select2 === undefined) return;

                method=args[0];

                if (method === "container") {
                    value = select2.container;
                } else if (method === "dropdown") {
                    value = select2.dropdown;
                } else {
                    if (methodsMap[method]) method = methodsMap[method];

                    value = select2[method].apply(select2, args.slice(1));
                }
                if (indexOf(args[0], valueMethods) >= 0
                    || (indexOf(args[0], propertyMethods) >= 0 && args.length == 1)) {
                    return false; // abort the iteration, ready to return first matched value
                }
            } else {
                throw "Invalid arguments to select2 plugin: " + args;
            }
        });
        return (value === undefined) ? this : value;
    };

    // plugin defaults, accessible to users
    $.fn.select2.defaults = {
        debug: false,
        width: "copy",
        loadMorePadding: 0,
        closeOnSelect: true,
        openOnEnter: true,
        containerCss: {},
        dropdownCss: {},
        containerCssClass: "",
        dropdownCssClass: "",
        formatResult: function(result, container, query, escapeMarkup) {
            var markup=[];
            markMatch(this.text(result), query.term, markup, escapeMarkup);
            return markup.join("");
        },
        transformVal: function(val) {
            return $.trim(val);
        },
        formatSelection: function (data, container, escapeMarkup) {
            return data ? escapeMarkup(this.text(data)) : undefined;
        },
        sortResults: function (results, container, query) {
            return results;
        },
        formatResultCssClass: function(data) {return data.css;},
        formatSelectionCssClass: function(data, container) {return undefined;},
        minimumResultsForSearch: 0,
        minimumInputLength: 0,
        maximumInputLength: null,
        maximumSelectionSize: 0,
        id: function (e) { return e == undefined ? null : e.id; },
        text: function (e) {
          if (e && this.data && this.data.text) {
            if ($.isFunction(this.data.text)) {
              return this.data.text(e);
            } else {
              return e[this.data.text];
            }
          } else {
            return e.text;
          }
        },
        matcher: function(term, text) {
            return stripDiacritics(''+text).toUpperCase().indexOf(stripDiacritics(''+term).toUpperCase()) >= 0;
        },
        separator: ",",
        tokenSeparators: [],
        tokenizer: defaultTokenizer,
        escapeMarkup: defaultEscapeMarkup,
        blurOnChange: false,
        selectOnBlur: false,
        adaptContainerCssClass: function(c) { return c; },
        adaptDropdownCssClass: function(c) { return null; },
        nextSearchTerm: function(selectedObject, currentSearchTerm) { return undefined; },
        searchInputPlaceholder: '',
        createSearchChoicePosition: 'top',
        shouldFocusInput: function (instance) {
            // Attempt to detect touch devices
            var supportsTouchEvents = (('ontouchstart' in window) ||
                                       (navigator.msMaxTouchPoints > 0));

            // Only devices which support touch events should be special cased
            if (!supportsTouchEvents) {
                return true;
            }

            // Never focus the input if search is disabled
            if (instance.opts.minimumResultsForSearch < 0) {
                return false;
            }

            return true;
        }
    };

    $.fn.select2.locales = [];

    $.fn.select2.locales['en'] = {
         formatMatches: function (matches) { if (matches === 1) { return "One result is available, press enter to select it."; } return matches + " results are available, use up and down arrow keys to navigate."; },
         formatNoMatches: function () { return "No matches found"; },
         formatAjaxError: function (jqXHR, textStatus, errorThrown) { return "Loading failed"; },
         formatInputTooShort: function (input, min) { var n = min - input.length; return "Please enter " + n + " or more character" + (n == 1 ? "" : "s"); },
         formatInputTooLong: function (input, max) { var n = input.length - max; return "Please delete " + n + " character" + (n == 1 ? "" : "s"); },
         formatSelectionTooBig: function (limit) { return "You can only select " + limit + " item" + (limit == 1 ? "" : "s"); },
         formatLoadMore: function (pageNumber) { return "Loading more results…"; },
         formatSearching: function () { return "Searching…"; }
    };

    $.extend($.fn.select2.defaults, $.fn.select2.locales['en']);

    $.fn.select2.ajaxDefaults = {
        transport: $.ajax,
        params: {
            type: "GET",
            cache: false,
            dataType: "json"
        }
    };

    // exports
    window.Select2 = {
        query: {
            ajax: ajax,
            local: local,
            tags: tags
        }, util: {
            debounce: debounce,
            markMatch: markMatch,
            escapeMarkup: defaultEscapeMarkup,
            stripDiacritics: stripDiacritics
        }, "class": {
            "abstract": AbstractSelect2,
            "single": SingleSelect2,
            "multi": MultiSelect2
        }
    };

}(jQuery));
$.ajaxSetup({
    accepts: {
        xml: 'application/xml, text/xml',
        html: 'text/html',
        text: 'text/plain',
        json: 'application/vnd.api+json, application/json, text/javascript',
        '*': '*/*',
    },
});

Nut.RestService = {
    parseTopLevelResponse: function(response, responseModel) {
        _(response).each(function(models, type) {
            if (type == 'links' || type == 'meta') return;
            // we're just parsing these to get them into the local stores
            if (responseModel) {
                models = _(models).reject(function(m) {
                    return m.type == responseModel.type && m.id == responseModel.id;
                });
            }
            models = _(models).map(function(m) {
                m.topLevelResponse = response;
                m = Nut.baseRestResourceModel.extractLinks(m);
                return Nut.Model.getModel(m);
            });
            new Nut.Model.GenericCollection(models);
        });
    },
};

Nut.Model = {
    standardRelations: {
        comments: {
            type: Backbone.HasMany,
            key: 'comments',
            keySource: 'links_comments',
            relatedModel: 'Nut.Model.Comments',
            collectionType: 'Nut.Model.CommentsCollection',
            includeInJSON: false,
            reverseRelation: {
                key: 'parent',
                includeInJSON: false,
                relatedModel: 'Nut.Model.baseRestResourceModel',
            },
        },
        leads: {
            type: Backbone.HasMany,
            key: 'leads',
            keySource: 'links_leads',
            relatedModel: 'Nut.Model.Leads',
            collectionType: 'Nut.Model.LeadsCollection',
        },
        followup: {
            type: Backbone.HasOne,
            key: 'followup',
            keySource: 'links_followup',
            relatedModel: 'Nut.Model.Followups',
            collectionType: 'Nut.Model.FollowupsCollection',
            includeInJSON: false,
            // reverseRelation: {
            //  key: 'entity',
            //  includeInJSON: false,
            //  relatedModel: 'Nut.Model.baseRestResourceModel'
            // },
        },
        recurringTask: {
            type: Backbone.HasOne,
            key: 'recurringTask',
            keySource: 'links_recurringTask',
            relatedModel: 'Nut.Model.Tasks',
            collectionType: 'Nut.Model.TasksCollection',
            includeInJSON: false,
        },
        files: {
            type: Backbone.HasMany,
            key: 'files',
            keySource: 'links_files',
            relatedModel: 'Nut.Model.Files',
            collectionType: 'Nut.Model.FilesCollection',
            collectionOptions: {source: 'files'},
            includeInJSON: false,
            reverseRelation: {
                key: 'parent',
                includeInJSON: false,
                relatedModel: 'Nut.Model.baseRestResourceModel',
            },
        },
        relatedFiles: {
            type: Backbone.HasMany,
            key: 'relatedFiles',
            keySource: 'links_relatedFiles',
            relatedModel: 'Nut.Model.RelatedFiles',
            collectionType: 'Nut.Model.FilesCollection',
            collectionOptions: {source: 'relatedfiles'},
            includeInJSON: false,
            reverseRelation: {
                key: 'parent',
                includeInJSON: false,
                relatedModel: 'Nut.Model.baseRestResourceModel',
            },
        },
        stagesets: {
            type: Backbone.HasOne,
            key: 'stagesets',
            keySource: 'links_stagesets',
            relatedModel: 'Nut.Model.Stagesets',
            collectionType: 'Nut.Model.StagesetsCollection',
        },
        teams: {
            type: Backbone.HasMany,
            key: 'teams',
            keySource: 'links_teams',
            relatedModel: 'Nut.Model.Teams',
            collectionType: 'Nut.Model.TeamsCollection',
        },
        users: {
            type: Backbone.HasMany,
            key: 'users',
            keySource: 'links_users',
            relatedModel: 'Nut.Model.Users',
            collectionType: 'Nut.Model.UsersCollection',
        },
    },
    getClassFromType: function(modelName) {
        if (!modelName) return null;
        modelName = modelName.charAt(0).toUpperCase() + modelName.substr(1);
        return Nut.Model[modelName];
    },
    getModel: function(attrs, options) {
        var modelClass = Nut.Model.getClassFromType(attrs.type);

        if (!modelClass) {
            Nut.error('Could not find model for ' + attrs.type);
            return null;
        }

        if (modelClass.findOrCreate) {
            return modelClass.findOrCreate(attrs, options);
        }
        return new modelClass(attrs, options);
    },

    /**
     * Convenience method that takes a canonical API id, parses out the type,
     * and tries to look it up.
     * @param  {String} id Id in the canonical form of {id}-{type}
     * @param  {Object} options
     * @return {Nut.Model}
     */
    getModelById: function(id, options) {
        if (!_.isString(id)) {
            Nut.error('Id passed to retrieve model not a string: ' + id);
            return null;
        }
        var idAndType = id.split('-');
        if (idAndType.length != 2) {
            Nut.error('Could not find model using id ' + id);
            return null;
        }

        var attrs = {
            id: id,
            type: idAndType[1],
        };
        return Nut.Model.getModel(attrs, options);
    },
};

Nut.baseModelAccessors = {
    getString: function(name, defaultValue) {
        var value = this.get(name);
        if (_.isObject(value)) {
            if (value['--primary']) {
                return value['--primary'];
            }
            return _.first(value);
        }
        defaultValue = typeof defaultValue == 'undefined' ? '' : defaultValue;
        return typeof value == 'undefined' || $.trim(value).length == 0 ? defaultValue : value;
    },

    /**
     * Convenience method that looks up a sanitized list of email models for
     * this model. First are the emails for this model, followed by the emails
     * for each contact, followed by the emails for each account. Within each
     * group the primary emails for the model are sorted to the front.
     *
     * @return {array} Sanitized array of email models
     */
    getEmailModels: function() {
        var emailCollections = [this.get('emails')];
        var secondaryEmailCollections = _.chain([this.get('contacts'), this.get('accounts')])
            .compact()
            .invoke('pluck', 'emails')
            .flatten()
            .value();

        emailCollections = _.compact(emailCollections.concat(secondaryEmailCollections));

        // Convert the array of emailCollection objects to an array of emailModel arrays
        var emailModelArrays = _.chain(emailCollections)
            .pluck('models')
            .invoke('sort', function(a, b) {
                // Sort the arrays to place primary emails first
                if (a.attributes.isPrimary === b.attributes.isPrimary) {
                    return 0;
                } else {
                    return a.attributes.isPrimary ? -1 : 1;
                }
            })
            .value();

        // flatten the array of emailModel arrays to one array of email models
        return _.flatten(emailModelArrays);
    },
};

Nut.baseModel = Backbone.Model.extend(Nut.baseModelAccessors);
Nut.baseRelationalModel = Backbone.RelationalModel.extend(Nut.baseModelAccessors);
Nut.baseRestResourceModel = Nut.baseRelationalModel.extend(
    {
        // this disables dualstorage stuff
        remote: true,
        relations: [
            // keySource/keyDestination will treat links_X as a list of Nut.Model.X ids,
            // and on fetchRelated populate `key` with the models
            {
                type: Backbone.HasMany,
                key: 'accounts',
                keySource: 'links_accounts',
                relatedModel: 'Nut.Model.Accounts',
                collectionType: 'Nut.Model.AccountsCollection',
                includeInJSON: false,
            },
            {
                type: Backbone.HasMany,
                key: 'contacts',
                keySource: 'links_contacts',
                relatedModel: 'Nut.Model.Contacts',
                collectionType: 'Nut.Model.ContactsCollection',
                includeInJSON: false,
            },
            {
                type: Backbone.HasOne,
                key: 'creator',
                keySource: 'links_creator',
                relatedModel: 'Nut.Model.Users',
            },
            {
                type: Backbone.HasOne,
                key: 'origin',
                keySource: 'links_origin',
                relatedModel: 'Nut.Model.Origins',
            },
        ],
        parse: function(response) {
            if (!response) return; // this would happen during a 204, since backbone core still calls us

            // if this is a json-api style response, save the whole daggum payload so
            // we can access the 'links' lookup dictionary, as well as any compound
            // documents
            if (!_.isUndefined(response[this.type])) {
                this.topLevelResponse = response;
                var model = response[this.type][0];
                Nut.RestService.parseTopLevelResponse(this.topLevelResponse, model);
                return Nut.baseRestResourceModel.extractLinks(model);
            } else if (!_.isUndefined(response.type) && response.type == this.type) {
                response = Nut.baseRestResourceModel.extractLinks(response);
                return response;
            }
            return null;
        },
        getLinks: function(linkName) {
            var linkedIds = this.get('links_' + linkName), // depends on ID-only responses for now
                linkConfig = this.getLinkConfig(linkName);

            if (linkConfig === null || linkedIds === null)
                return new Nut.Model.GenericCollection([]);

            // another hacky workaround for not knowing whether we have a to-many or to-one rel
            if (!_.isArray(linkedIds)) linkedIds = [linkedIds];

            var filteredDocuments = _(this.topLevelResponse[linkConfig.type]).filter(function(
                compoundDocument
            ) {
                return _.contains(linkedIds, compoundDocument.id);
            });

            var collection = new Nut.Model.GenericCollection(filteredDocuments);

            return collection;
        },
        // note, until we decide otherwise, this might return a string for a single ID
        // or an array of multiple IDs
        // to this end, we probably need servers to differentiate between null and [] based
        // on relationship type
        getLinkIds: function(linkName) {
            var linkedIds = this.get('links_' + linkName);

            return linkedIds; // maybe this should always be an empty array instead of null?
        },
        saveLinkIds: function(linkName, newLinkIds) {
            var toSave = {};
            toSave['links_' + linkName] = newLinkIds;

            return this.save(toSave, {patch: true}); // returns a jqxhr, to use as a promise
        },
        getLinkConfig: function(linkName) {
            var linkConfigKey = this.type + '.' + linkName;
            if (
                _.isUndefined(this.topLevelResponse) ||
                _.isUndefined(this.topLevelResponse.links[linkConfigKey])
            )
                return null;
            return this.topLevelResponse.links[linkConfigKey];
        },
        getPatchPathPrefix: function() {
            return '/' + this.type + '/0'; // hack for now
        },
        sync: function(method, model, options) {
            if (method == 'patch') {
                var patches = [];

                _(options.attrs).each(function(attributeValue, attributeName) {
                    var pathSuffix =
                        attributeName.indexOf('links_') === 0
                            ? '/links/' + attributeName.substr('links_'.length)
                            : '/' + attributeName;

                    patches.push({
                        op: 'replace',
                        path: model.getPatchPathPrefix() + pathSuffix,
                        value: attributeValue,
                    });
                });

                return this.sendPatches(patches, options);
            } else {
                return Backbone.sync.call(this, method, model, options);
            }
        },
        sendPatches: function(patchArray, options) {
            var jqxhr = $.ajax({
                url: _.result(this, 'url'),
                type: 'PATCH',
                contentType: 'application/json-patch+json',
                data: JSON.stringify(patchArray),
            });

            if (!_.isUndefined(options.success)) jqxhr.done(options.success);
            if (!_.isUndefined(options.error)) jqxhr.fail(options.error);
            if (!_.isUndefined(this.handleResponse)) jqxhr.done(_.bind(this.handleResponse, this));

            return jqxhr;
        },
        /**
         * Make a PUT request for this model, and on success, parse the response using
         * `Nut.RestService.parseTopLevelResponse`.
         *
         * @param  {Object} attributes The attributes to modify
         * @param  {Object} xhrOptions Additional options to pass to the XHR, such as data and content type.
         * @return {jQuery.Deferred} A promise that resolves upon completion of the xhr request.
         */
        updateWithPut: function(attributes, xhrOptions) {
            // for JSON API updates, all attributes live under a top-level `data` key
            var data = {
                data: attributes,
            };
            xhrOptions = xhrOptions || {};
            xhrOptions = _.extend(
                {
                    url: this.url(),
                    type: 'PUT',
                    dataType: 'json',
                    data: data,
                },
                xhrOptions
            );

            return $.ajax(xhrOptions).done(function(response, status) {
                if (status == 'success') {
                    Nut.RestService.parseTopLevelResponse(response);
                }
            });
        },
        addRelationFromCard: function($card) {
            var id = $card.data('id'),
                type = $card.data('type');
            if (this.has(type)) {
                var model = Nut.Model.getModel({id: id, type: type});
                this.get(type).add(model);

                // TODO: when we stop rendering the card server-side, this
                // will have to actually check for success/errors
                model.fetch();
            }
        },

        removeRelationFromCard: function($card) {
            var id = $card.data('id'),
                type = $card.data('type');
            if (this.has(type)) {
                this.get(type).remove({id: id});
            }
        },

        // takes an array of models to delete
        destroyModels: function(models) {
            var modelIds = _.pluck(models, 'id'),
                jqxhr = $.ajax({
                    url: this.urlRoot + '/' + modelIds.join(','),
                    type: 'DELETE',
                });

            return jqxhr;
        },
    },
    {
        extractLinks: function(model) {
            if (!_.isUndefined(model.links)) {
                _(model.links).each(function(links, linkName) {
                    if (_.isObject(links) && links.id) {
                        links = links.id;
                    }
                    model['links_' + linkName] = links;
                });
            }
            return model;
        },
    }
);

Nut.Model.Activities = Nut.baseRestResourceModel.extend({
    relations: _.union(Nut.baseRestResourceModel.prototype.relations, [
        Nut.Model.standardRelations.comments,
        Nut.Model.standardRelations.leads,
        Nut.Model.standardRelations.users,
        {
            type: Backbone.HasOne,
            key: 'activityType',
            keySource: 'links_activityType',
            relatedModel: 'Nut.Model.ActivityTypes',
        },
        {
            type: Backbone.HasOne,
            key: 'followUpActivity',
            keySource: 'links_followUpActivity',
            relatedModel: 'Nut.Model.Activities',
        },
        {
            type: Backbone.HasOne,
            key: 'logger',
            keySource: 'links_logger',
            relatedModel: 'Nut.Model.Users',
        },
        {
            type: Backbone.HasOne,
            key: 'logNote',
            keySource: 'links_logNote',
            relatedModel: 'Nut.Model.Notes',
        },
        {
            type: Backbone.HasOne,
            key: 'precedingActivity',
            keySource: 'links_precedingActivity',
            relatedModel: 'Nut.Model.Activities',
        },
    ]),
    singularLocalizedDescription: 'activity',
    urlRoot: '/rest/activities',
    type: 'activities',
    parse: function(response) {
        var model = Nut.Model.Activities.__super__.parse.call(this, response);
        return model;
    },
    getActivityType: function() {
        return Nut.Model.ActivityTypes.findOrCreate({
            id: this.get('links').activityType,
        });
    },
    getLogNote: function() {
        if (!this.get('links').logNote) return null;
        return Nut.Model.Notes.findOrCreate({
            id: this.get('links').logNote,
        });
    },
    getParticipants: function() {
        var participants = new Nut.Model.GenericCollection();
        _(['users', 'contacts', 'accounts', 'leads']).each(function(type) {
            _(this.get('links')[type]).each(function(id) {
                participants.add({id: id, type: type});
            });
        }, this);
        return participants;
    },
    getLinkable: function() {
        var linkable = {
            name: this.get('name'),
            type: 'activities',
            htmlUrl: '#',
        };
        return linkable;
    },
});

Nut.Model.ActivityTypes = Nut.baseRestResourceModel.extend({
    relations: [],
    urlRoot: '/rest/activitytypes',
    type: 'activityTypes',
});

Nut.Model.Chats = Nut.baseRestResourceModel.extend({
    relations: _.union(Nut.baseRestResourceModel.prototype.relations, [
        Nut.Model.standardRelations.comments,
        {
            type: Backbone.HasMany,
            key: 'users',
            keySource: 'links_users',
            relatedModel: 'Nut.Model.Users',
            collectionType: 'Nut.Model.UsersCollection',
        },
    ]),
    urlRoot: '/rest/chats',
    singularLocalizedDescription: 'chat',
    type: 'chats',

    getContact: function() {
        var contactId = _(this.get('links').contacts).first();
        return contactId ? Nut.Model.Contacts.findOrCreate({id: contactId}) : null;
    },

    getUser: function() {
        var contactId = _(this.get('links').users).first();
        return contactId ? Nut.Model.Users.findOrCreate({id: contactId}) : null;
    },

    toTemplateContext: function(action, attributes) {
        var contact = this.getContact();
        return _.extend(
            {
                actor: contact ? contact.toJSON() : null,
                excerpt: this.get('bodyHtml'),
                body: this.get('bodyHtml'),
                model: this.toJSON(),
                participants: [],
                subject: this.getString('subject'),
                timestamp: this.getString('createdTime'),
            },
            attributes
        );
    },

    getParticipants: function() {
        var participants = new Nut.Model.GenericCollection();
        var user = this.getUser() ? this.getUser().toJSON() : {name: 'Someone', type: 'users'};
        var contact = this.getContact()
            ? this.getContact().toJSON()
            : {name: 'Someone', type: 'contacts'};
        participants.add([user, contact]);
        return participants;
    },

    //chats are linked via getParticipants
    getLinkable: function() {
        return [];
    },
});

Nut.Model.EmailTemplates = Nut.baseRestResourceModel.extend({
    urlRoot: '/rest/emailtemplates',
    type: 'emailTemplates',
});

Nut.Model.Emails = Nut.baseRestResourceModel.extend({
    relations: [
        Nut.Model.standardRelations.comments,
        Nut.Model.standardRelations.files,
        {
            type: Backbone.HasMany,
            key: 'from',
            keySource: 'links_from',
            relatedModel: 'Nut.Model.EmailAddresses',
            collectionType: 'Nut.Model.EmailAddressesCollection',
        },
        {
            type: Backbone.HasMany,
            key: 'to',
            keySource: 'links_to',
            relatedModel: 'Nut.Model.EmailAddresses',
            collectionType: 'Nut.Model.EmailAddressesCollection',
        },
        {
            type: Backbone.HasMany,
            key: 'cc',
            keySource: 'links_cc',
            relatedModel: 'Nut.Model.EmailAddresses',
            collectionType: 'Nut.Model.EmailAddressesCollection',
        },
    ],
    singularLocalizedDescription: 'email',
    urlRoot: '/rest/emails',
    type: 'emails',
    parse: function(response) {
        if (!_.isUndefined(response.emailAddresses)) {
            _(response.emailAddresses).each(function(emailAddress) {
                var eaModel = Nut.Model.EmailAddresses.findOrCreate(emailAddress, {parse: true});
            });
        }
        var model = Nut.Model.Emails.__super__.parse.call(this, response);
        return model;
    },
    getSender: function() {
        return this.get('from').first();
    },
    getRecipients: function() {
        var recipients = new Nut.Model.GenericCollection();
        _(['to', 'cc']).each(function(type) {
            (this.get(type) || _([])).each(function(model) {
                recipients.add(model);
            });
        }, this);
        return recipients;
    },
    getLinkable: function() {
        return {
            name: this.get('subject'),
            type: 'emails',
            htmlUrl: this.get('htmlUrl'),
        };
    },
    getFiles: function() {
        return this.get('files');
    },
    isShareable: function() {
        return this.get('shared') === false && this.get('shareable') === true;
    },
    isUnshareable: function() {
        return this.get('shared') === true && this.get('shareable') === true;
    },
    isOpenable: function() {
        return true;
    },
    isReplyable: function() {
        return true;
    },
    share: function() {
        return this.updateWithPut({shared: true});
    },
    unshare: function() {
        return this.updateWithPut({shared: false});
    },
});

// This is only used as a temporary timeline entry while we process the queued email
Nut.Model.EmailsQueued = Nut.baseRestResourceModel.extend({
    relations: [
        Nut.Model.standardRelations.comments,
        Nut.Model.standardRelations.files,
        {
            type: Backbone.HasMany,
            key: 'from',
            keySource: 'links_from',
            relatedModel: 'Nut.Model.EmailAddresses',
            collectionType: 'Nut.Model.EmailAddressesCollection',
        },
        {
            type: Backbone.HasMany,
            key: 'to',
            keySource: 'links_to',
            relatedModel: 'Nut.Model.EmailAddresses',
            collectionType: 'Nut.Model.EmailAddressesCollection',
        },
        {
            type: Backbone.HasMany,
            key: 'cc',
            keySource: 'links_cc',
            relatedModel: 'Nut.Model.EmailAddresses',
            collectionType: 'Nut.Model.EmailAddressesCollection',
        },
    ],
    singularLocalizedDescription: 'email',
    urlRoot: '/rest/emailsqueued',
    type: 'emailsQueued',
    parse: function(response) {
        if (!_.isUndefined(response.emailAddresses)) {
            _(response.emailAddresses).each(function(emailAddress) {
                var eaModel = Nut.Model.EmailAddresses.findOrCreate(emailAddress, {parse: true});
            });
        }
        var model = Nut.Model.Emails.__super__.parse.call(this, response);
        return model;
    },
    getSender: function() {
        return this.get('from').first();
    },
    getRecipients: function() {
        var recipients = new Nut.Model.GenericCollection();
        _(['to', 'cc']).each(function(type) {
            (this.get(type) || _([])).each(function(model) {
                recipients.add(model);
            });
        }, this);
        return recipients;
    },
    getLinkable: function() {
        return {
            name: this.get('subject'),
            type: 'emailsQueued',
            htmlUrl: this.get('htmlUrl'),
        };
    },
    getFiles: function() {
        return this.get('files');
    },
    isReplyable: function() {
        return false;
    },
    // Not shareable or unshareable but email uses this and this makes our life easier
    isShareable: function() {
        return false;
    },
    isUnshareable: function() {
        return false;
    },
});

Nut.Model.SearchResults = Nut.baseRestResourceModel.extend({
    relations: [],
    type: 'searchResults',
    urlRoot: '/rest/searchresults',

    /**
     * Returns a single entity to use for links, styles, etc.
     * Uses precedence of user, contact, account, or returns itself if none
     * are found.
     * @return {Nut.Model}
     */
    getEntity: function() {
        if (this.has('entity')) return this.get('entity');

        var links = this.get('links');
        if (!links || !links.entity || !links.entity.id || !links.entity.type) {
            return null;
        }
        return Nut.Model.getModel({
            id: links.entity.id,
            type: links.entity.type,
        });
    },

    getEscapedInfo: function() {
        var unsafeAttrs = [
                'primaryName',
                'primaryInfo',
                'relatedName',
                'relatedInfo',
                'description',
            ],
            safeAttrs = {};

        _.each(
            unsafeAttrs,
            _.bind(function(str) {
                if (!this.get(str)) return;

                safeAttrs[str] = _.escape(this.get(str))
                    .replace(/&lt;em&gt;/g, '<em>')
                    .replace(/&lt;\/em&gt;/g, '</em>');
            }, this)
        );

        return safeAttrs;
    },
});

Nut.Model.EmailAddresses = Nut.baseRestResourceModel.extend({
    relations: _.union(Nut.baseRestResourceModel.prototype.relations, [
        {
            type: Backbone.HasMany,
            key: 'users',
            keySource: 'links_users',
            relatedModel: 'Nut.Model.Users',
            collectionType: 'Nut.Model.UsersCollection',
        },
    ]),
    type: 'emailAddresses',

    /**
     * Returns a single entity to use for links, styles, etc.
     * Uses precedence of user, contact, account, or returns itself if none
     * are found.
     * @return {Nut.Model}
     */
    getEntity: function() {
        var links = this.get('links'),
            type = _(['users', 'contacts', 'accounts']).find(function(type) {
                return links && links[type].length > 0;
            });
        if (type) {
            return Nut.Model.getModel({
                id: links[type][0],
                type: type,
            });
        }
        return this;
    },
});

Nut.Model.Leads = Nut.baseRestResourceModel.extend({
    relations: _.union(Nut.baseRestResourceModel.prototype.relations, [
        Nut.Model.standardRelations.files,
        Nut.Model.standardRelations.relatedFiles,
    ]),
    urlRoot: '/rest/leads',
    type: 'leads',
    singularLocalizedDescription: 'lead',
    sync: function() {
        return Nut.baseRestResourceModel.prototype.sync.apply(this, arguments);
    },

    getOwner: function() {
        var id = this.get('links_owner'),
            type = this.get('ownerType');

        return id
            ? Nut.Model.getModel({
                  id: id,
                  type: type,
              })
            : null;
    },

    getFiles: function() {
        return this.get('files');
    },

    getRelatedFiles: function() {
        return this.get('relatedFiles');
    },
    getActiveStageId: function() {
        return this.get('links_stage');
    },
    getStagesetId: function() {
        return this.get('links_stageset');
    },
    getStages: function() {
        var lead = this;
        var stagesRequest = Nut.ajax.get({
            url: '/rest/leads/' + this.get('id') + '/stages',
            error: function(response) {
                Nut.error('Failed fetching lead stages', response);
            },
        });

        stagesRequest.success(function(data) {
            lead.trigger('Stages received', data);
        });

        return stagesRequest;
    },
    activateStage: function(selectedStage) {
        var data = [
            {
                op: 'replace',
                path: '/leads/0/links/stage',
                value: selectedStage,
            },
        ];

        Nut.ajax
            .post({
                url: this.urlRoot + '/' + this.get('id'),
                type: 'PATCH',
                data: JSON.stringify(data),
                headers: {'Content-Type': 'application/json-patch+json'},
            })
            .success(
                function() {
                    this.fetch(); // reload the whole lead, in case automations happened
                    this.set({links_stage: selectedStage});
                }.bind(this)
            );
    },
    hasStageChanged: function() {
        return this.hasChanged('links_stage');
    },
});

Nut.Model.Markets = Nut.baseRestResourceModel.extend({
    singularLocalizedDescription: 'market',
    type: 'markets',
    relations: [],
    urlRoot: '/rest/markets',
});

Nut.Model.Notes = Nut.baseRestResourceModel.extend({
    relations: _.union(Nut.baseRestResourceModel.prototype.relations, [
        Nut.Model.standardRelations.comments,
        Nut.Model.standardRelations.leads,
        Nut.Model.standardRelations.teams,
        Nut.Model.standardRelations.users,

        {
            type: Backbone.HasOne,
            key: 'parent',
            keySource: 'links_parent',
            relatedModel: 'Nut.baseRelationalModel',
        },
    ]),
    singularLocalizedDescription: 'note',
    urlRoot: '/rest/notes',
    type: 'notes',
    parse: function(response) {
        var model = Nut.Model.Activities.__super__.parse.call(this, response);
        if (!_.isNull(model['parentType'])) {
            // set up the real related model
            this.getRelation('parent').relatedModel = Nut.Model.getClassFromType(
                model['parentType']
            );
        }
        return model;
    },
    getParent: function() {
        return Nut.Model.getModel({
            id: this.get('links')['parent'],
            type: this.get('parentType'),
        });
    },
    getRenderedBody: function() {
        return this.get('bodyHtml');
    },
    getMentionedEntities: function() {
        return _.chain(['accounts', 'contacts', 'teams', 'users', 'leads'])
            .map(function(type) {
                return this.get(type).map(function(entity) {
                    return {
                        entityName: entity.type,
                        entityId: entity.id,
                        name: entity.getString('name'),
                    };
                });
            }, this)
            .flatten()
            .compact()
            .value();
    },
    getLinkable: function() {
        var parent = this.getParent();
        return {
            name: parent.get('name'),
            type: parent.type,
            htmlUrl: parent.get('htmlUrl'),
        };
    },
});

Nut.Model.Sources = Nut.baseModel.extend({
    popoverUrl: function() {
        return '/popover/source/' + this.get('id') + '.json';
    },
    type: 'sources',
    getDescription: function() {
        const getDisplayableSourceType = (type) => {
            switch (type) {
                case 1:
                    return 'utm_campaign';
                case 2:
                    return 'utm_source';
                case 3:
                    return 'utm_medium';
                case 4:
                    return 'utm_content';
                case 5:
                    return 'utm_term';
                case 0:
                default:
                    return null;
            }
        };

        const channel = this.get('channel');
        const sourceType = this.get('sourceType');

        const type = sourceType ? getDisplayableSourceType(sourceType) : null;

        if (channel && type) {
            return `${channel.display} • ${type}`;
        } else if (channel) {
            return channel.display;
        } else {
            return type;
        }
    },
});

Nut.Model.Channels = Nut.baseModel.extend({
    type: 'channels',
});

Nut.Model.Milestones = Nut.baseRestResourceModel.extend({
    singularLocalizedDescription: 'milestone',
    type: 'milestones',
    urlRoot: '/rest/milestones',
});

Nut.Model.Stages = Nut.baseRestResourceModel.extend({
    singularLocalizedDescription: 'stage',
    type: 'stages',
    urlRoot: '/rest/stages',
});

Nut.Model.Users = Nut.baseRestResourceModel.extend({
    relations: [],
    urlRoot: '/rest/users',
    type: 'users',
    getInitials: function() {
        var name = this.get('name');

        name = typeof name == 'string' ? name : '';
        return _(name.split(' ').slice(0, 2)).reduce(function(memo, piece) {
            return memo + (piece[0] || '');
        }, '');
    },
});
Nut.Model.Teams = Nut.baseRestResourceModel.extend({
    relations: [],
    urlRoot: '/rest/teams',
    type: 'teams',
});
Nut.Model.Tickets = Nut.baseRestResourceModel.extend({
    type: 'tickets',
    urlRoot: '/rest/tickets',
});

Nut.Model.CtctContacts = Nut.baseRestResourceModel.extend({
    type: 'ctctContacts',
    urlRoot: '/rest/ctctContacts',
});

Nut.Model.CtctLists = Nut.baseRestResourceModel.extend({
    type: 'ctctLists',
    urlRoot: '/rest/ctctLists',
});

Nut.Model.CtctContactTrackingActivities = Nut.baseRestResourceModel.extend({
    type: 'ctctContactTrackingActivities',
    urlRoot: '/rest/ctctContactTrackingActivities',
    getCtctContact: function() {
        return Nut.Model.getModel({
            id: this.get('links')['ctctContact'],
            type: 'ctctContacts',
        });
    },
    getContact: function() {
        var ctctContact = Nut.Model.getModel({
            id: this.get('links')['ctctContact'],
            type: 'ctctContacts',
        });

        if (ctctContact.get('links')['contacts'].length === 1) {
            return Nut.Model.getModel({
                id: ctctContact.get('links')['contacts'][0],
                type: 'contacts',
            });
        }

        return null;
    },
});

Nut.Model.EmailInteractions = Nut.baseRestResourceModel.extend({
    type: 'emailInteractions',
    urlRoot: '/rest/emailInteractions',
    getFirstRecipientContact: function() {
        var email = Nut.Model.getModel({
            id: this.get('links')['email'],
            type: 'emails',
        });

        if (email) {
            var emailAddress = Nut.Model.getModel({
                id: email.get('links')['to'],
                type: 'emailAddresses',
            });

            if (emailAddress) {
                return emailAddress.getEntity();
            }
        }

        return null;
    },
    getEmail: function() {
        var email = Nut.Model.getModel({
            id: this.get('links')['email'],
            type: 'emails',
        });

        return email;
    },
});

Nut.Model.TicketMessages = Nut.Model.Emails.extend({
    relations: [
        Nut.Model.standardRelations.comments,
        Nut.Model.standardRelations.files,
        {
            type: Backbone.HasMany,
            key: 'from',
            keySource: 'links_from',
            relatedModel: 'Nut.Model.EmailAddresses',
            collectionType: 'Nut.Model.EmailAddressesCollection',
        },
        {
            type: Backbone.HasMany,
            key: 'to',
            keySource: 'links_to',
            relatedModel: 'Nut.Model.EmailAddresses',
            collectionType: 'Nut.Model.EmailAddressesCollection',
        },
        {
            type: Backbone.HasOne,
            key: 'ticket',
            keySource: 'links_ticket',
            relatedModel: 'Nut.Model.Tickets',
        },
    ],
    singularLocalizedDescription: 'ticket',
    type: 'ticketMessages',
    urlRoot: '/rest/ticketmessages',
    isReplyable: function() {
        return false;
    },
});
Nut.Model.Competitors = Nut.baseModel.extend({
    type: 'competitors',
});
Nut.Model.EmailSequenceTemplates = Nut.baseModel.extend({
    type: 'emailSequenceTemplates',
});
Nut.Model.EmailSequenceTemplateGroups = Nut.baseModel.extend({
    type: 'emailSequenceTemplateGroups',
});
Nut.Model.CompetitorMaps = Nut.baseModel.extend({
    type: 'competitorMaps',
});
Nut.Model.Tags = Nut.baseModel.extend({
    type: 'tags',
});
Nut.Model.Products = Nut.baseModel.extend({
    type: 'products',
});
Nut.Model.Stagesets = Nut.baseRestResourceModel.extend({
    type: 'stagesets',
    singularLocalizedDescription: 'stageset',
    urlRoot: '/rest/stagesets',
});
Nut.Model.ProductMaps = Nut.baseModel.extend({
    type: 'productMaps',
});
Nut.Model.Territories = Nut.baseModel.extend({
    type: 'territories',
});
Nut.Model.AccountTypes = Nut.baseModel.extend({
    type: 'accountTypes',
});
Nut.Model.Assignments = Nut.baseRestResourceModel.extend({
    type: 'assignments',
    urlRoot: '/rest/assignments',
    getParent: function() {
        return Nut.Model.getModel({
            id: this.get('links')['parent'],
            type: this.get('parentType'),
        });
    },
    getAssignmentEntity: function() {
        var assignmentEntityApiId = this.get('links_assignmentEntity');

        return Nut.Model.getModel({
            id: assignmentEntityApiId,
            type: assignmentEntityApiId.split('-')[1],
        });
    },
    getAssigneeEntity: function() {
        var assigneeEntityApiId = this.get('links_assigneeEntity');

        return Nut.Model.getModel({
            id: assigneeEntityApiId,
            type: assigneeEntityApiId.split('-')[1],
        });
    },
    getReplacedEntityAssignment: function() {
        var replacedEntityAssignmentApiId = this.get('links_replacedEntityAssignment');

        if (replacedEntityAssignmentApiId) {
            return Nut.Model.getModel({
                id: replacedEntityAssignmentApiId,
                type: replacedEntityAssignmentApiId.split('-')[1],
            });
        }

        return null;
    },
});
Nut.Model.Industries = Nut.baseModel.extend({
    type: 'industries',
});
Nut.Model.Origins = Nut.baseRestResourceModel.extend({
    relations: [],
    urlRoot: '/rest/origins',
    type: 'origins',
    isInteresting: function() {
        if (this.get('name') == 'Web') return false;
        if (this.get('name') == 'API') return false;
        return true;
    },
});
Nut.Model.Outcomes = Nut.baseRestResourceModel.extend({
    relations: [],
    urlRoot: '/rest/outcomes',
    type: 'outcomes',
});
Nut.Model.Dashboard = Nut.baseModel.extend({
    type: 'dashboards',
    sync: function() {},
});

Nut.Model.Audiences = Nut.baseModel.extend({
    type: 'emAudiences',
    isWebFX: function() {
        return this.get('isWebFX');
    },
});

Nut.Model.EmEditions = Nut.baseModel.extend({
    type: 'emEditions',
});

Nut.Model.EmCampaigns = Nut.baseModel.extend({
    type: 'emCampaigns',
});

Nut.Model.EmEditions = Nut.baseModel.extend({
    type: 'emEditions',
});

Nut.Model.WfForms = Nut.baseRestResourceModel.extend({
    type: 'wfForms',
});

Nut.Model.WfSubmissions = Nut.baseRestResourceModel.extend({
    type: 'wfSubmissions',
    getSubmitter: function() {
        var submitterApiId = this.get('links')['submitter'];

        return Nut.Model.getModel({
            id: submitterApiId,
            type: submitterApiId.split('-')[1],
        });
    },
    getForm: function() {
        var formApiId = this.get('links')['form'];

        return Nut.Model.getModel({
            id: formApiId,
            type: formApiId.split('-')[1],
        });
    },
});

// collections

Nut.baseCollection = Backbone.Collection.extend({
    shouldRemoteSync: function() {
        var remoteMaxModifiedTime = collectionTimes[this.type],
            localMaxModel = 0;

        if (this.length === 0 && remoteMaxModifiedTime === 0) return false; // never resync if empty and remote zero

        if (this.length)
            localMaxModel = this.max(function(model) {
                return model.get('modifiedTime') * 1;
            });

        if (!localMaxModel || _.isUndefined(localMaxModel.get)) return true; // corrupted local store (i.e. models without modifiedTime property)

        var shouldRemoteSync = localMaxModel.get('modifiedTime') < remoteMaxModifiedTime;
        return shouldRemoteSync;
    },
    filterActive: function(filter) {
        return _(
            this.filter(function(model) {
                return !model.get('deletedTime');
            })
        );
    },
    // handle json-api-style deeper responses
    parseBeforeLocalSave: function(response) {
        if (typeof response[this.type] != 'undefined') return response[this.type];
        return null;
    },
});

Nut.pagedCollection = Nut.baseCollection.extend({
    initialize: function() {
        this.on('request', this.fetchNextPage.bind(this));
    },
    fetch: function(options) {
        // If options were specifically provided use those
        if (options) return Nut.baseCollection.prototype.fetch.call(this, options);

        var options = {url: this.url, data: {page: {limit: 200, page: 0}}};
        try {
            var metaString = Nut.readLocalStorage(this.type + ':meta');
            if (metaString) {
                var meta = JSON.parse(metaString);
                this.total = meta.total;
                if (meta.next) {
                    options = {url: meta.next};
                }
            }
        } catch (e) {
            // Have a reasonable default in place knowingly swalling this
        }

        return Nut.baseCollection.prototype.fetch.call(this, options);
    },
    fetchNextPage: function(collection, xhr) {
        var self = this;
        xhr.then(function(response) {
            Nut.writeLocalStorage(self.type + ':meta', JSON.stringify(response.meta));
            if (response.meta && response.meta.next) self.fetch({url: response.meta.next});
        });
    },
    shouldRemoteSync: function() {
        if (this.total && this.total > this.length) return true; // if we haven't gotten all of the items yet keep going

        return Nut.baseCollection.prototype.shouldRemoteSync.apply(this, arguments);
    },
    parseBeforeLocalSave: function(response) {
        if (
            typeof response[this.type] != 'undefined' &&
            response.meta.total > response[this.type].length
        ) {
            this.total = response.meta.total;
            return this.models
                .map(function(model) {
                    var rawModel = Object.assign({}, model.attributes);
                    delete rawModel.topLevelResponse;
                    return rawModel;
                })
                .concat(response[this.type]);
        }

        return Nut.baseCollection.prototype.parseBeforeLocalSave.apply(this, arguments);
    },
});

Nut.baseRestResourceCollection = Nut.baseCollection.extend({
    parse: function(response) {
        if (typeof response.meta != 'undefined') {
            Nut.RestService.parseTopLevelResponse(response);
            return response[this.type];
        } else {
            return response;
        }
    },
    query: function(string, options) {
        if (!this.queryable) {
            if (options.error) {
                options.error.call(this, this, null, options);
            }
            return false;
        }
        options = _.extend(
            {
                reset: true,
                url: this.url + '?q=' + string,
            },
            options
        );
        return this.fetch(options);
    },
    queryable: false,
    remote: true,
});

Nut.Model.GenericCollection = Nut.baseCollection.extend({
    model: Nut.Model.getModel,
});

Nut.Model.CollectionsCollection = Nut.Model.GenericCollection.extend({
    addCollection: function(collection) {
        if (_.isFunction(collection.filterActive)) {
            collection.filterActive().each(function(cmodel) {
                this.add(cmodel);
            }, this);
        } else {
            this.add(collection);
        }
        collection
            .on('remove', this.remove, this)
            .on('reset', this.reset, this)
            // remove=false keeps it from removing all other models
            .on(
                'add',
                function(model, collection, options) {
                    options.remove = false;
                    this.add(model, options);
                },
                this
            )
            .on(
                'change',
                function(model, options) {
                    options.remove = false;
                    this.set(model, options);
                },
                this
            );
    },
});

Nut.Model.ActivitiesCollection = Nut.baseRestResourceCollection.extend({
    type: 'activities',
    model: Nut.Model.Activities,
    initialize: function(options) {
        if (options) {
            this.queryFilters = options.queryFilters;
            this.limit = options.limit;
            this.fields = options.fields;
        }
    },
    url: function() {
        var limit = this.limit ? this.limit : 40;
        baseUrl = '/rest/activities?limit=' + limit + this.parseFields();

        return this.queryFilters ? baseUrl + this.parseFilters() : baseUrl;
    },
    comparator: function(activity) {
        return activity.get('startTime');
    },

    //currently duplicate code with eventsCollection, but can't
    //refactor since we're still using filter[] && filters[]
    parseFilters: function() {
        var filterString = '';

        _.each(this.queryFilters, function(filterList, filterType) {
            if (filterList instanceof Array) {
                _.each(filterList, function(id) {
                    filterString += '&filter[' + filterType + '][]=' + id;
                });
            } else {
                filterString += '&filter[' + filterType + ']=' + filterList;
            }
        });

        return filterString;
    },

    parseFields: function() {
        var fieldString = '';

        if (this.fields) {
            fieldString = '&include=activities&fields[activities]=' + this.fields.join(',');
        }

        return fieldString;
    },
});

Nut.Model.SearchResultsCollection = Nut.baseRestResourceCollection.extend({
    type: 'searchResults',
    model: Nut.Model.SearchResults,

    initialize: function(options) {
        this.query = {};

        if (options.q) {
            this.query.q = options.q;
        }

        if (options.entityTypes) {
            var entityTypes = options.entityTypes.map(function(entityType) {
                return entityType.charAt(0).toUpperCase() + entityType.slice(1);
            });

            this.query.filter = {entityType: entityTypes};
        }
    },

    url: function() {
        var baseUrl = '/rest/searchresults';

        return Object.keys(this.query).length ? baseUrl + '?' + $.param(this.query) : baseUrl;
    },
});

Nut.Model.MarketsCollection = Nut.baseRestResourceCollection.extend({
    type: 'markets',
    model: Nut.Model.Markets,

    initialize: function(options) {
        if (options) {
            this.q = options.q;
        }
    },

    url: function() {
        var baseUrl = '/rest/markets';

        return this.q ? baseUrl + '?q=' + encodeURIComponent(this.q) : baseUrl;
    },
    remote: null,
});

Nut.Model.MilestonesCollection = Nut.baseRestResourceCollection.extend({
    type: 'milestones',
    model: Nut.Model.Milestones,

    initialize: function(options) {
        if (options) {
            this.q = options.q;
        }
    },

    url: function() {
        var baseUrl = '/rest/milestones';

        return this.q ? baseUrl + '?q=' + encodeURIComponent(this.q) : baseUrl;
    },
});

Nut.Model.SourcesCollection = Nut.baseCollection.extend({
    type: 'sources',
    model: Nut.Model.Sources,
    comparator: function(source) {
        return source.get('name') ? source.get('name').toLowerCase() : source.get('name');
    },
    url: '/rest/sources',
});

Nut.Model.ChannelsCollection = Nut.baseCollection.extend({
    type: 'channels',
    model: Nut.Model.Channels,
    comparator: function(channel) {
        return channel.get('name') ? channel.get('name').toLowerCase() : channel.get('name');
    },
    url: '/rest/channels',
});

Nut.Model.UsersCollection = Nut.pagedCollection.extend({
    type: 'users',
    model: Nut.Model.Users,
    url: '/rest/users',
    filterActive: function(filter) {
        return _(
            this.filter(function(model) {
                return (
                    !model.get('deletedTime') &&
                    (model.get('isEnabled') || NutshellFrontendSettings.use_disabled_users)
                );
            })
        );
    },
    comparator: function(user) {
        return user.get('name') ? user.get('name').toLowerCase() : user.get('name');
    },
    getLoggedInUser: function() {
        var id = $('body').data('loggedInUser');
        if (_.isEmpty(id)) return null;

        var results = this.where({id: id});
        return results.length ? results[0] : null;
    },
});

Nut.Model.StagesetsCollection = Nut.baseCollection.extend({
    type: 'stagesets',
    model: Nut.Model.Stagesets,
    url: '/rest/stagesets',
    remote: null,
});

Nut.Model.TeamsCollection = Nut.baseCollection.extend({
    type: 'teams',
    model: Nut.Model.Teams,
    url: '/rest/teams',
});

Nut.Model.CompetitorsCollection = Nut.baseCollection.extend({
    type: 'competitors',
    model: Nut.Model.Competitors,
    comparator: function(competitor) {
        return competitor.get('name')
            ? competitor.get('name').toLowerCase()
            : competitor.get('name');
    },
    url: '/rest/competitors',
});

Nut.Model.CtctListsCollection = Nut.baseCollection.extend({
    type: 'ctctLists',
    model: Nut.Model.CtctLists,
    url: '/rest/ctctlists',
});

Nut.Model.EmailSequenceTemplatesCollection = Nut.baseCollection.extend({
    type: 'emailSequenceTemplates',
    model: Nut.Model.EmailSequenceTemplates,
    comparator: function(template) {
        return template.get('name') ? template.get('name').toLowerCase() : template.get('name');
    },
    url: '/rest/emailsequencetemplates',
});

Nut.Model.EmailSequenceTemplateGroupsCollection = Nut.baseCollection.extend({
    type: 'emailSequenceTemplateGroups',
    model: Nut.Model.EmailSequenceTemplateGroups,
    remote: null,
    comparator: function(template) {
        return template.get('name') ? template.get('name').toLowerCase() : template.get('name');
    },
    url: '/rest/emailsequencetemplategroups',
});

Nut.Model.TagsCollection = Nut.baseCollection.extend({
    type: 'tags',
    model: Nut.Model.Tags,
    url: '/rest/tags',
});

Nut.Model.ProductsCollection = Nut.baseRestResourceCollection.extend({
    type: 'products',
    model: Nut.Model.Products,
    url: '/rest/products',
    queryable: true,
    remote: null,
});

Nut.Model.TerritoriesCollection = Nut.baseCollection.extend({
    type: 'territories',
    model: Nut.Model.Territories,
    url: '/rest/territories',
    singularLocalizedDescription: 'territory',
    shouldShowManageOptionToAdmins: true,
});

Nut.Model.AccountTypesCollection = Nut.baseCollection.extend({
    type: 'accountTypes',
    model: Nut.Model.AccountTypes,
    url: '/rest/accounttypes',
    singularLocalizedDescription: 'company type',
    shouldShowManageOptionToAdmins: true,
});

Nut.Model.ActivityTypesCollection = Nut.baseRestResourceCollection.extend({
    type: 'activityTypes',
    model: Nut.Model.ActivityTypes,
    url: '/rest/activitytypes',
});

Nut.Model.AssignmentsCollection = Nut.baseCollection.extend({
    type: 'assignments',
    model: Nut.Model.Assignments,
    url: '/rest/assignments',
    singularLocalizedDescription: 'assignment',
});

Nut.Model.IndustriesCollection = Nut.baseCollection.extend({
    type: 'industries',
    model: Nut.Model.Industries,
    url: '/rest/industries',
    singularLocalizedDescription: 'industry',
    shouldShowManageOptionToAdmins: true,
});

Nut.Model.OriginsCollection = Nut.baseCollection.extend({
    type: 'origins',
    model: Nut.Model.Origins,
    url: '/rest/origins',
});

Nut.Model.LeadsCollection = Nut.baseCollection.extend({
    type: 'leads',
    model: Nut.Model.Leads,
    url: '/rest/leads',
});

Nut.Model.EmailAddressesCollection = Nut.baseCollection.extend({
    singularLocalizedDescription: 'email',
    type: 'emailAddresses',
    model: Nut.Model.EmailAddresses,
});

Nut.Model.AudiencesCollection = Nut.baseCollection.extend({
    type: 'emAudiences',
    model: Nut.Model.Audiences,
    comparator: function(audience) {
        return audience.get('name') ? audience.get('name').toLowerCase() : audience.get('name');
    },
    url: '/rest/audiences',
});

Nut.Model.EditionsCollection = Nut.baseCollection.extend({
    type: 'emEditions',
    model: Nut.Model.EmEditions,
    comparator: function(edition) {
        return edition.get('name') ? edition.get('name').toLowerCase() : edition.get('name');
    },
    url: '/rest/editions',
});

Nut.Model.CampaignsCollection = Nut.baseCollection.extend({
    type: 'emCampaigns',
    model: Nut.Model.EmCampaigns,
    comparator: function(campaign) {
        return campaign.get('name') ? campaign.get('name').toLowerCase() : campaign.get('name');
    },
    url: '/rest/campaigns',
});

Nut.Model.FormsCollection = Nut.baseCollection.extend({
    type: 'wfForms',
    model: Nut.Model.WfForms,
    comparator: function(form) {
        return form.get('name') ? form.get('name').toLowerCase() : form.get('name');
    },
    url: '/rest/forms',
});

Nut.Model.SubmissionsCollection = Nut.baseCollection.extend({
    type: 'wfSubmissions',
    model: Nut.Model.WfSubmissions,
    url: '/rest/submissions',
});

var collectionsToLoad = [
    'Sources',
    'Channels',
    'Users',
    'Origins',
    'Teams',
    'Competitors',
    'Tags',
    'Products',
    'Territories',
    'AccountTypes',
    'ActivityTypes',
    'Industries',
    'Markets',
    'Stagesets',
    'EmailSequenceTemplates',
    'EmailSequenceTemplateGroups',
    'CtctLists',
    'Audiences',
    'Editions',
    'Campaigns',
    'Forms',
];
Nut.managedCollections = {};

// Called when we request a managedCollection, so that our redux store knows
// to set that collections' slice of state to isLoading
function dispatchRequestManagedCollection(collectionType) {
    var requestManagedCollection = Nut.actions.requestCollection;
    Nut.dispatch(requestManagedCollection(collectionType));
}

// Called when a managedCollection is updated with new models,
// we'll dispatch a `updateCollection` action, which is set in nut.js,
// and initially comes from our shared `nutshell-client-shared` repo.
//
// This is done because we always fetch collections on each page load for
// our backbone code, so we're just hooking into that API response here
// to seed our redux store with all of our different models
function dispatchUpdateManagedCollection(collectionType, collection) {
    var updateManagedCollection = Nut.actions.updateCollection;
    var rawCollection = collection.map(function(item) {
        return item.attributes;
    });
    var transformedCollection = transformBackboneCollectionForRedux(rawCollection);

    var response = {};
    response[collectionType] = transformedCollection;

    Nut.dispatch(updateManagedCollection(collectionType, response));
}

/**
 * Takes a backbone collection of models and removes the topLevelResponse key
 * from each object in the collection. We need to do this because the
 * topLevelResponse contains a reference to the entire collection, which is
 * recursive, and that doens't work when you're attempting to turn that kind
 * of object into JSON - no circular references.
 *
 * @param  {model[]} collection – Array of backbone model objects
 * @return {object[]}             "sanitized" array of model objects
 */
function transformBackboneCollectionForRedux(collection) {
    return collection.map(function(model) {
        var newModel = _.extend({}, model);
        delete newModel.topLevelResponse;

        return newModel;
    });
}

// wait until the page has loaded so we get all seeded vars from script tags
$(document).ready(function() {
    if (Nut.isLoggedIn()) {
        _(collectionsToLoad).each(function(modelName) {
            var collection = new Nut.Model[modelName + 'Collection']();
            Nut.managedCollections[modelName] = collection;

            collection.on({
                // Using collection.type here instead of modelName, as the collection's
                // type is the api-type of the model, versus the modelName which is
                // capitalized _and_ camelCased, which won't match on the respones's
                // type key. (i.e. using activityTypes vs. `ActivityTypes` || `activitytypes`)
                request: function() {
                    dispatchRequestManagedCollection(collection.type);
                },
                sync: function() {
                    dispatchUpdateManagedCollection(collection.type, collection);
                },
            });
        });

        try {
            var existingSchema = localStorage.getItem('collectionSchemaHash');
            if (wasHardRefreshed || existingSchema != collectionSchemaHash) {
                Nut.log('Clearing local storage from hard reload');
                localStorage.clear();
            }
            localStorage.setItem('collectionSchemaHash', collectionSchemaHash);
        } catch (e) {
            Nut.log('Failed to set collectionSchemaHash: ' + e.toString());
        }

        // wait until a different event from ui/app.js to indicate that the react
        // router has loaded and begun to fetch the current page's webchunk.
        // this way the webchunk request isn't last in line behind a dozen XHRs
        $(document).on(Nut.events.global.reactWasMountedAndInitialized, function() {
            collectionsToLoad.forEach(function(modelName) {
                Nut.managedCollections[modelName].fetch();
            });
        });
    }
});
(function(Nut, Backbone, $) {
    'use strict';

    var timeoutSeconds = 120;

    var pickDefaults = {
        maxSize: 52428800,
        uploadInBackground: false,
        fromSources: [
            'local_file_system',
            'box',
            'dropbox',
            'googledrive',
            'imagesearch',
            'webcam',
        ],
    };

    var collectionPickDefaults = {
        maxSize: 52428800,
        maxFiles: 50,
        uploadInBackground: false,
        fromSources: [
            'local_file_system',
            'box',
            'dropbox',
            'googledrive',
            'imagesearch',
            'webcam',
        ],
    };
    var storeDefaults = {};

    Nut.Model.Files = Nut.baseRestResourceModel.extend({
        remote: true,
        type: 'files',

        url: function() {
            return this.getParent().url() + '/files/' + this.id;
        },

        initialize: function(attributes, options) {
            var options = options || {};
            _.bindAll(this, 'url', 'hasTimedOut', 'reprocessFile');
            this.description = options.description;
            this.icon = options.icon || 'file';
            this.pickOptions = _.defaults(options.pickOptions || {}, pickDefaults);
            this.storeOptions = _.defaults(options.storeOptions || {}, storeDefaults);
        },

        getIconForFileType: function() {
            var filetype = this.get('filetype');
            return (
                {
                    doc: 'file-word-o',
                    pdf: 'file-pdf-o',
                    spreadsheet: 'file-excel-o',
                    archive: 'file-zip-o',
                    video: 'file-video-o',
                    text: 'file-text-o',
                    keynote: 'file-video-o',
                    audio: 'file-audio-o',
                    image: 'file-image-o',
                    powerpoint: 'file-powerpoint-o',
                    unknown: 'file-o',
                }[filetype] || filetype
            );
        },

        getParent: function() {
            if (this.has('parent')) return this.get('parent');

            var links = this.get('links');
            if (!links || !links.parent || !links.parent.id || !links.parent.type) {
                return null;
            }
            return Nut.Model.getModel({
                id: links.parent.id,
                type: links.parent.type,
            });
        },

        formatTimestamp: function() {
            var timestamp = this.get('createdTime');
            return timestamp ? moment.unix(timestamp).format('MMM DD YYYY') : false;
        },

        hasTimedOut: function() {
            var elapsedTime = moment().unix() - this.get('createdTime');
            return elapsedTime > timeoutSeconds && this.get('status') === 'pending';
        },

        reprocessFile: function() {
            return $.ajax({
                url: this.url() + '/reprocess',
                type: 'PATCH',
                statusCode: {
                    200: function(response) {
                        Nut.RestService.parseTopLevelResponse(response);
                    }.bind(this),
                    202: function() {
                        // 202 Accepted, so check again in 5 seconds
                        setTimeout(this.fetch.bind(this), 5000);
                    }.bind(this),
                },
            });
        },
    });

    Nut.Model.RelatedFiles = Nut.Model.Files.extend({});

    Nut.Model.Avatar = Nut.Model.Files.extend({
        url: function() {
            return this.baseUrl + 'avatar';
        },

        initialize: function initializeAvatar(attributes, options) {
            _.bindAll(this, 'url');
            this.description = 'Change photo…';
            this.icon = 'camera';
            this.baseUrl = options.baseUrl;
            this.pickOptions = _.defaults(
                {
                    accept: 'image/*',
                    maxSize: 10485760,
                    transformations: {force: true, circle: true, crop: false},
                },
                pickDefaults
            );
            this.storeOptions = _.defaults(options.storeOptions || {}, storeDefaults);
            this.id = 'avatar';
        },
    });

    Nut.Model.FilesCollection = Nut.baseRestResourceCollection.extend({
        model: Nut.Model.Files,
        comparator: function(file) {
            return -this.get('createdTime');
        },

        url: function filesCollectionUrl() {
            return this.parent.url() + '/' + this.source;
        },

        initialize: function initialize(items, options) {
            var options = options || {};
            _.bindAll(this, 'url');
            this.description = 'Attach files…';
            this.icon = 'files-o';
            this.type = 'files';
            this.source = options.source;
            this.pickOptions = _.defaults(options.pickOptions || {}, collectionPickDefaults);
            this.storeOptions = _.defaults(options.storeOptions || {}, storeDefaults);
        },

        saveNew: function saveNew() {
            var self = this;
            var newFiles = this.filter(function(file) {
                return file.isNew();
            });
            return $.ajax({
                type: 'POST',
                url: _.result(self, 'url'),
                contentType: 'application/json',
                data: JSON.stringify(newFiles),
            }).then(function() {
                self.postFileAnalytics(newFiles);
            });
        },

        postFileAnalytics: function postFileAnalytics(newFiles) {
            try {
                if (_.any(newFiles)) {
                    var analyticsProperties = {
                        parentType: newFiles[0].get('parent').get('type'),
                        mime: newFiles[0].get('mimetype'),
                        fileCount: newFiles.length,
                        googleValue: newFiles.length,
                        totalSize: newFiles.reduce(function(memo, value) {
                            return memo + value.get('size');
                        }, 0),
                    };

                    Analytics.trackEvent(
                        Analytics.categories.usage,
                        Analytics.actions.usage.uploadFile,
                        analyticsProperties
                    );
                }
            } catch (e) {
                Nut.log('Error tracking upload ', e);
            }
        },

        isRelatedFileCollection: function() {
            return this.source === 'relatedFiles';
        },
    });
})(Nut, Backbone, $);
(function(Nut, Backbone, $) {
    'use strict';

    Nut.Model.Followups = Nut.baseRestResourceModel.extend({
        relations: [],
        type: 'followups',
        urlRoot: '/rest/followups',

        /**
         * Returns a single entity to use for links, styles, etc.
         * Uses precedence of user, contact, account, or returns itself if none
         * are found.
         * @return {Nut.Model}
         */
        getEntity: function() {
            if (this.has('entity')) return this.get('entity');

            var links = this.get('links');
            if (!links || !links.entity || !links.entity.id || !links.entity.type) {
                return null;
            }
            return Nut.Model.getModel({
                id: links.entity.id,
                type: links.entity.type,
            });
        },
    });

    Nut.Model.FollowupsCollection = Nut.baseRestResourceCollection.extend({
        type: 'followups',
        model: Nut.Model.Followups,
        url: '/rest/followups',
    });
})(Nut, Backbone, $);
(function(Nut, Backbone, $) {
    'use strict';

    Nut.Model.Tasks = Nut.baseRestResourceModel.extend({
        remote: true,
        type: 'tasks',
        urlRoot: '/rest/tasks',

        parse: function(data) {
            return data && _.any(data.tasks) ? data.tasks[0] : {};
        },

        toJSON: function() {
            return {
                data: this.pick(
                    'title',
                    'dueDateSelector',
                    'dueDate',
                    'assigneeEntityId',
                    'description',
                    'entityId'
                ),
            };
        },
    });

    Nut.Model.TasksCollection = Nut.baseRestResourceCollection.extend({
        type: 'tasks',
        model: Nut.Model.Tasks,
        url: '/rest/tasks',
    });
})(Nut, Backbone, $);
Nut.Model.Peep = Nut.baseRestResourceModel.extend({
    relations: _.union(Nut.baseRestResourceModel.prototype.relations, [
        Nut.Model.standardRelations.files,
        Nut.Model.standardRelations.relatedFiles,
        Nut.Model.standardRelations.followup,
        Nut.Model.standardRelations.recurringTask,
        {
            type: Backbone.HasMany,
            key: 'addresses',
            relatedModel: 'Nut.Model.PeepAttributesAddresses',
            collectionType: 'Nut.Model.PeepAttributesCollection',
            reverseRelation: {
                key: 'parent',
                includeInJSON: false,
            },
            createModels: true,
            parse: true,
        },
        {
            type: Backbone.HasMany,
            key: 'emails',
            relatedModel: 'Nut.Model.PeepAttributesEmails',
            collectionType: 'Nut.Model.PeepAttributesCollection',
            reverseRelation: {
                key: 'parent',
                includeInJSON: false,
            },
            createModels: true,
            parse: true,
        },
        {
            type: Backbone.HasMany,
            key: 'phones',
            relatedModel: 'Nut.Model.PeepAttributesPhones',
            collectionType: 'Nut.Model.PeepAttributesCollection',
            reverseRelation: {
                key: 'parent',
                includeInJSON: false,
            },
            createModels: true,
            parse: true,
        },
        {
            type: Backbone.HasMany,
            key: 'urls',
            relatedModel: 'Nut.Model.PeepAttributesUrls',
            collectionType: 'Nut.Model.PeepAttributesCollection',
            reverseRelation: {
                key: 'parent',
                includeInJSON: false,
            },
            createModels: true,
            parse: true,
        },
    ]),

    getFollowup: function getFollowup() {
        return this.get('followup');
    },

    getRecurringTask: function getRecurringTask() {
        return this.get('recurringTask');
    },

    getFiles: function getFiles() {
        return this.get('files');
    },

    getRelatedFiles: function getRelatedFiles() {
        return this.get('relatedFiles');
    },

    getInitials: function() {
        var name = this.get('name');

        name = typeof name == 'string' ? name : '';
        return _(name.split(' ').slice(0, 2)).reduce(function(memo, piece) {
            return memo + (piece[0] || '');
        }, '');
    },

    saveOmnifield: function(value) {
        var jqxhr = $.ajax({
            url: _.result(this, 'url') + '/omnifield',
            type: 'POST',
            contentType: 'application/json',
            data: JSON.stringify({value: value}),
        });

        jqxhr.done(_.bind(this.handleResponse, this));

        return jqxhr;
    },

    handleResponse: function(responseContent, status, jqxhr) {
        if (
            jqxhr.status === 200 &&
            jqxhr.getResponseHeader('Content-Type') == 'application/json-patch+json'
        ) {
            return this.handlePatchResponse(responseContent, status, jqxhr);
        }
    },
    handlePatchResponse: function(patchResponseArray, status, jqxhr) {
        if (!_.isArray(patchResponseArray)) {
            Nut.error('Empty PATCH response', patchResponseArray);
            return;
        }
        _(patchResponseArray).map(this.handlePatchResponseItem, this);
    },
    handlePatchResponseItem: function(patch) {
        if (!patch.op || !patch.path) {
            Nut.error('Invalid patch received', patch);
        }

        // ignore the first part of the path, since we only support styles like '/accounts/0/'
        var explodedPath = patch.path.split('/').slice(3),
            attributeName = explodedPath[0]; // we'll need to support subvalues in the future

        switch (patch.op) {
            case 'add':
                var attributeCollection = this.get(attributeName);

                // right now we assume that attributes added via patch are supposed to be animated
                // as opposed to attribtues removed/added via full 200 responses
                attributeCollection.add(patch.value, {animate: true, parse: true});
                break;
            default:
                Nut.error('Unknown patch op', patch);
        }
    },

    // takes an array of models to merge into this entity
    merge: function(models) {
        (data = {}), (modelIds = _.pluck(models, 'id')), (data[this.get('type')] = modelIds);
        jqxhr = $.ajax({
            url: this.urlRoot + '/' + this.get('id') + '/merge',
            type: 'POST',
            data: data,
        });

        return jqxhr;
    },
});

Nut.Model.Accounts = Nut.Model.Peep.extend({
    urlRoot: '/rest/accounts',
    type: 'accounts',
    description: 'companies',
    singularLocalizedDescription: 'company', // don't you dare use this for anything but user-facing words!
});

Nut.Model.AccountsCollection = Nut.baseCollection.extend({
    model: Nut.Model.Accounts,
});

Nut.Model.Contacts = Nut.Model.Peep.extend({
    urlRoot: '/rest/contacts',
    type: 'contacts',
    description: 'people',
    singularLocalizedDescription: 'person', // don't you dare use this for anything but user-facing words!

    initialize: function() {
        _.bindAll(this, 'set', 'updateFromFullContact');
    },

    /**
     * Finds the first associated account with an address.
     * @return {promise} a promise that returns the first account with an address
     */
    getRelatedAddressModel: function() {
        var account = _.find(this.get('accounts').models, function(a) {
            return a.has('addresses');
        });
        return $.Deferred().resolve(account);
    },

    pollForFullcontactChanges: function() {
        var self = this;
        var intervalCount = 0;

        this.on('change:lastFullcontactTime', this.updateFromFullContact);

        this.fullContactPoll = setInterval(function() {
            intervalCount++;

            if (intervalCount >= 5) {
                clearInterval(self.fullContactPoll);
                return;
            }

            $.ajax({
                method: 'GET',
                dataType: 'json',
                url: self.url() + '/fullcontact',
            }).then(_.partial(self.set, 'lastFullcontactTime'));
        }, 2000);

        return this.fullContactPoll;
    },

    updateFromFullContact: function() {
        this.fetch();
        Peep.reloadCards();
        clearInterval(this.fullContactPoll);
        this.off('change:lastFullcontactTime');
    },
});

Nut.Model.ContactsCollection = Nut.baseRestResourceCollection.extend({
    model: Nut.Model.Contacts,
    type: 'contacts',
    url: '/rest/contacts',
});
Nut.Model.PeepAttributes = Nut.baseRelationalModel.extend({
    remote: true,

    // relational models need GUIDs on client, for relational to be happy :\
    parse: function(attributes) {
        if (attributes === null) return null;

        attributes.id = Math.random();
        return attributes;
    },

    // e.g. 'phones'
    getAttributeName: function() {
        return this.getRelation('parent').reverseRelation.key;
    },

    getAttributeIndex: function() {
        return this.isNew() ? '-' : this.collection.indexOf(this);
    },

    // since we're not a real remote representation, we're just going to issue a patch
    // can't decide if this should actually call back to a sync() on the parent?
    sync: function(method, model, options) {
        var parent = this.get('parent'),
            patches = [],
            pathPrefix = [
                parent.getPatchPathPrefix(), // maybe this should be done in sendPatch() preproc
                this.getAttributeName(),
                this.getAttributeIndex(),
            ].join('/');

        // we're being deleted
        if (method == 'delete') {
            patches.push({
                op: 'remove',
                path: pathPrefix,
            });
        } else if (method == 'create') {
            patches.push({
                op: 'add',
                path: pathPrefix,
                value: this.toJSON(),
            });
        } else {
            _(options.attrs).each(function(value, name) {
                // this would indicate the parent relationship changed, we only care about internals
                // basically this shouldn't happen, but it'd be bad if it did
                if (name == 'parent') return;

                patches.push({
                    op: 'replace',
                    path: [pathPrefix, name].join('/'),
                    value: value,
                });
            }, this);
        }

        var defaultBackboneSuccessFunction = options.success;
        options.success = function(response, status, jqxhr) {
            if (jqxhr.status == 200) {
                // by default, backbone is going to assume it gets back a set of new attrs for ourselves, but
                // they're actually for the parent model. we need to apply them there.

                var serverAttrs = parent.parse(response, options);
                parent.set(serverAttrs);
            } else if (jqxhr.status == 204) {
                // we've actually saved stuff, but no response to parse.
                // the built-in backbone success event will suffice for
                // things like finishing up the destroy() on delete

                if (defaultBackboneSuccessFunction) defaultBackboneSuccessFunction(response);
            }
        };

        return parent.sendPatches(patches, options);
    },
});

Nut.Model.PeepAttributesAddresses = Nut.Model.PeepAttributes.extend(
    {
        getLocation: function() {
            if (!_.isUndefined(this.get('value').location)) {
                return this.get('value').location;
            }

            return null;
        },
        getAccuracy: function() {
            if (!_.isUndefined(this.get('value').locationAccuracy)) {
                return this.get('value').locationAccuracy;
            }

            return null;
        },
        isMoreAccurateThan: function(accuracy) {
            return this.getAccuracy() > accuracy;
        },
        getGoogleMapsUrl: function() {
            var addressString = this.getSingleAddressString();
            if (addressString.length === 0) return null;

            return 'https://maps.google.com?q=' + encodeURIComponent(addressString);
        },

        // always return a string, something like "212 south fifth ave ann arbor mi 48103"
        getSingleAddressString: function() {
            var addressAttrs = this.get('value'),
                addressComponents = [];
            if (!addressAttrs) return '';

            _.each(
                ['address_1', 'address_2', 'address_3', 'city', 'state', 'postalCode', 'country'],
                function(keyName) {
                    if (!_.isString(addressAttrs[keyName]) || addressAttrs[keyName].length == 0)
                        return;
                    addressComponents.push(addressAttrs[keyName]);
                }
            );

            return addressComponents.join(' ');
        },

        // sledgehammer override of save() to preprocess `street` value into `address_[123]`
        // originally this was done as an override, but that is not called when wait: true
        // feels like there's a better backboney way of doing this
        save: function(key, val, options) {
            var attrs;

            if (key === null || typeof key === 'object') {
                attrs = key;
                options = val;
            } else {
                (attrs = {})[key] = val;
            }

            if (
                typeof attrs === 'object' &&
                !_.isUndefined(attrs['value']) &&
                !_.isUndefined(attrs['value']['street'])
            ) {
                var streets = attrs['value']['street'].split('\n');

                if (streets.length > 3) throw new Error('Street must be 3 lines or less');

                // iterate 3 street lines, and set as `address_[1-3]`
                // doing this for all three, so that empty lines are patched as well
                for (var i = 1; i <= 3; i++) {
                    var value = _.isUndefined(streets[i - 1]) ? '' : $.trim(streets[i - 1]);
                    attrs['value']['address_' + i] = value;
                }
                delete attrs['value']['street'];
            }

            return Nut.Model.PeepAttributesAddresses.__super__.save.call(this, attrs, options);
        },
    },
    {
        accuracy: {
            // these are defined in Nut_Geocoder
            unknown: 0,
            country: 1,
            region: 2, // Region (state, province, prefecture, etc.)
            subregion: 3, // Sub-region (county, municipality, etc.)
            city: 4, // Town (city, village)
            postcode: 5,
            street: 6,
            intersection: 7,
            address: 8,
        },
    }
);
Nut.Model.PeepAttributesEmails = Nut.Model.PeepAttributes.extend({});
Nut.Model.PeepAttributesPhones = Nut.Model.PeepAttributes.extend({});
Nut.Model.PeepAttributesUrls = Nut.Model.PeepAttributes.extend({
    parseUrl: function() {
        if (!_.isUndefined(this.get('value'))) {
            var regex = new RegExp('^https?://(?:www.)?(facebook|twitter|linkedin).com/(.*)');
            return regex.exec(this.get('value'));
        }
    },
    //Specifically for urls like http://facebook.com/chriscain since we truncate them to just 'chriscain' but when
    //we edit them, the text should show 'http://facebook.com/chriscain', not just 'chriscain' - used in EIP.js/prepareInputs
    getEditInPlaceText: function() {
        return !_.isUndefined(this.get('value')) ? this.get('value') : null;
    },
});

Nut.Model.PeepAttributesCollection = Nut.baseCollection.extend({
    model: Nut.Model.PeepAttributes,
    getPrimary: function() {
        return this.findWhere({isPrimary: true});
    },
});
Nut.Model.CommentsCollection = Nut.baseRestResourceCollection.extend({
    type: 'comments',
    model: Nut.Model.Comments,
    comparator: function(comment) {
        return comment.get('createdTime');
    },
    url: function(models) {
        var parentModel,
            parentIds = _.chain(models)
                .map(function(m) {
                    var parent = m.getParent();
                    if (parent) {
                        parentModel = parent;
                        return parent.id;
                    }
                    return null;
                })
                .compact() // remove null values and assume they belong to the same parent
                .unique()
                .value();

        if (
            parentIds.length == 1 &&
            models.length == parentModel.getRelation('comments').related.models.length
        ) {
            return Nut.Model.Comments.prototype.urlRoot + '?parent_id=' + parentIds[0];
        }

        // If for some reason we're requesting comments for multiple parents, we'll just request
        // them all. There's a possibility that this will break if the URL is too long, but
        // I don't see another way around it at the moment.
        return Nut.Model.Comments.prototype.urlRoot + '/' + _.pluck(models, 'id').join(',');
    },
});

Nut.Model.Comments = Nut.baseRestResourceModel.extend({
    relations: _.union(Nut.baseRestResourceModel.prototype.relations, [
        Nut.Model.standardRelations.leads,
        Nut.Model.standardRelations.teams,
        Nut.Model.standardRelations.users,
    ]),
    singularLocalizedDescription: 'comment',
    type: 'comments',
    urlRoot: '/rest/comments',

    /**
     * Overridden to format the body of "create" requests to comply with
     * JSON API. We should eventually override this in `baseRestResourceModel`
     * and implement a serialization method on the models specifically for this
     * purpose.
     * @param  {String} method
     * @param  {Nut.Model.Comments} model
     * @param  {Object} options
     * @return {Object} The XHR object returned from Backbone.sync.
     */
    sync: function(method, model, options) {
        if (method === 'create') {
            var parent = model.getParent();
            var data = {
                comments: [
                    {
                        links: {
                            parent: {
                                id: parent.id,
                                type: parent.get('type'),
                            },
                        },
                        bodyMarkup: model.get('bodyMarkup'),
                    },
                ],
            };

            options.contentType = 'application/json';
            options.data = JSON.stringify(data);
            return Backbone.sync.call(this, method, model, options);
        }
        return Backbone.sync.apply(this, arguments);
    },

    validate: function(attributes, options) {
        var parent = attributes.parent || (attributes.links && attributes.links.parent);
        if (!parent || !parent.id || !parent.type) {
            return 'Cannot create a comment without a parent';
        }
        if (!attributes.bodyMarkup) {
            return 'Cannot create an empty comment';
        }
    },

    getMentionedEntities: function() {
        return _.chain(['accounts', 'contacts', 'teams', 'users', 'leads'])
            .map(function(type) {
                return this.get(type).map(function(entity) {
                    return {
                        entityName: entity.type,
                        entityId: entity.id,
                        name: entity.getString('name'),
                    };
                });
            }, this)
            .flatten()
            .compact()
            .value();
    },

    getParent: function() {
        if (this.has('parent')) return this.get('parent');

        var links = this.get('links');
        if (!links || !links.parent || !links.parent.id || !links.parent.type) {
            return null;
        }
        return Nut.Model.getModel({
            id: links.parent.id,
            type: links.parent.type,
        });
    },
});
window.Nut = window.Nut || [];
Nut.Model = Nut.Model || [];

(function(model, $, Backbone, location) {
    'use strict';

    var Notifications = Backbone.Model.extend({
        remote: true,
        parse: function(data) {
            data.id = parseInt(data.id);
            data.created_time = parseInt(data.created_time);
            return data;
        },

        markAsRead: function() {
            return $.ajax('/notification/read.json', {
                type: 'POST',
                dataType: 'json',
                data: {
                    id: this.id,
                },
            });
        },
    });

    // this collection starts a poll for notification counts when instantiated, and if provided the option
    // in its constructor of autoFetch: true, will update the actual data on init and count change
    model.NotificationsCollection = Backbone.Collection.extend({
        model: Notifications,
        remote: true,
        initialize: function(options) {
            var pollOptions = {
                pollTimeout: NutshellFrontendSettings.notificationPollMs || 5000,
                pollBackgroundTimeout: 60000,
            };

            // allow an autoFetch option that decides whether we re-fetch notifications whenever the unread count changes
            // we do this in full-screen view, but not on the web
            this.autoFetch = options && options.autoFetch ? options.autoFetch : false;

            this.total = 0;
            this.unseen = null; // null == we don't know yet
            _.bindAll(this, 'unreadPoll', 'updateCount');
            Visibility.every(
                pollOptions.pollTimeout,
                pollOptions.pollBackgroundTimeout,
                this.unreadPoll
            );
            if (this.autoFetch) this.fetch();
        },

        markAllAsRead: function() {
            return $.ajax('/notification/read.json', {
                type: 'POST',
                dataType: 'json',
                data: {},
            });
        },

        markAllAsSeen: function() {
            return $.ajax('/notification/glance.json', {
                type: 'POST',
                dataType: 'json',
                data: {},
            });
        },

        parse: function(response, options) {
            return response.notifications || [];
        },

        url: function() {
            return '/notification.json';
        },

        unreadPoll: function() {
            $.ajax({
                accepts: 'application/json',
                url: '/notification/count.json',
            }).done(this.updateCount);
        },

        updateCount: function(counts, status, xhr) {
            if (this.unseen != counts.unseen) {
                this.unseen = counts.unseen;
                this.trigger('change:unread'); // we're a collection, not a model, so we're faking this property
            }
            if (this.total !== counts.unseen + counts.seen + counts.read) {
                this.total = counts.unseen + counts.seen + counts.read;
                if (this.autoFetch) this.reload();
            }
            var nutshellVersion = xhr.getResponseHeader('x-nutshell-app-version');
            // nutshellVersion is reported as "0" in development, but never in production
            if (nutshellVersion && nutshellVersion !== '0') {
                var event = new CustomEvent('Nutshell version checked', {
                    detail: 'st-' + nutshellVersion,
                });
                document.dispatchEvent(event);
            }
        },

        reload: function() {
            this.fetch({reset: true});
        },
    });
})(Nut.Model, jQuery, Backbone, window.location);
(function() {
    var twitterHandleRegex = /^@[a-zA-Z0-9_]{1,20}/;

    function linkifyTwitterHandle(twitterHandle) {
        return 'http://twitter.com/' + twitterHandle.split('@')[1];
    }

    var supportedIconsMap = {
        'facebook.com': 'facebook',
        'twitter.com': 'twitter',
        'linkedin.com': 'linkedin',
        'foursquare.com': 'foursquare',
        'pinterest.com': 'pinterest',
        'youtube.com': 'youtube',
        'flickr.com': 'flickr',
        'vimeo.com': 'vimeo-square',
        'plus.google.com': 'google-plus',
        'tumblr.com': 'tumblr',
        'dribble.com': 'dribble',
        'angellist.com': 'angellist',
        'behance.com': 'behance',
        'del.icio.us': 'delicious',
        'delicious.com': 'delicious',
        'digg.com': 'digg',
        'github.com': 'github',
        'ycombinator.com': 'hacker-news',
        'instagram.com': 'instagram',
        'last.fm': 'lastfm',
        'reddit.com': 'reddit',
        'twitch.tv': 'twitch',
        'vine.co': 'vine',
    };

    var networkRegex = new RegExp(
        '^https?://(?:www.)?(' + _.keys(supportedIconsMap).join('|') + ')'
    );

    Nut.View = {
        /**
         * Helper to return the font awesome icon classname.
         * For example, "plus.google.com" => "google-plus".
         *
         * @param  {string}
         * @return {string}
         */
        fontAwesomeSocialIcon: function(url) {
            var isTwitter = twitterHandleRegex.test(url);
            url = isTwitter ? linkifyTwitterHandle(url) : url;

            var result = networkRegex.exec(url);
            return result ? supportedIconsMap[result[1]] : null;
        },

        nl2br: function(str, is_xhtml) {
            // source: http://phpjs.org/functions/nl2br/
            var breakTag = is_xhtml || typeof is_xhtml === 'undefined' ? '<br ' + '/>' : '<br>'; // Adjust comment to avoid issue on phpjs.org display

            return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2');
        },

        /**
         * Helper to format a model type, prepending the "a" or "an" article.
         * For example, "accounts" => "an account", "leads" => "a lead".
         *
         * @param  {string|Object} If an object, will try to use `singularLocalizedDescription`
         *   or `type` attributes, falling back to 'object'.  If using `type`, it will strip
         *   a trailing 's', which will be incorrect for i.e. 'addresses'.
         * @return {string}
         */
        formatSingle: function(str) {
            if (_.isObject(str)) {
                if (str.singularLocalizedDescription) {
                    str = str.singularLocalizedDescription;
                } else if (!str.singularType && str.type) {
                    str = str.type.replace(/s$/, '');
                } else {
                    str = str.singularLocalizedDescription;
                }
            } else {
                str = str || 'object';
            }
            var article = _(['a', 'e', 'i', 'o', 'u']).contains(str[0]) ? 'an' : 'a';
            if (str == 'user') article = 'a';
            return article + ' ' + str;
        },

        /**
         * Takes a string created by numeral.js's format() function and enclose currency + alpha suffix in appropriate <span>s
         *
         * @param  {string} str
         * @return {string} HTML
         */
        spanifyNumeralFormatted: function(str) {
            str = str.replace(/(.*)([A-Za-z])/, '$1<span class="number-unit">$2</span>');

            // currently assuming that the string consts of an optional non-number currency symbol part prefixing the number part
            str = str.replace(/(^[^0-9]+)([0-9].*)/, '<span class="currency-symbol">$1</span>$2');

            return str;
        },

        formatCurrencyAsHtmlOutputFormat: '00.[0]a',
        formatCurrencyAsHtmlTitleFormat: '0,0.[0]',
        formatCurrencyAsHtml: function(currency, spanClass, format, useInstanceDefault) {
            // beware, assuming that title will be escaped
            var wrapInSpan = function(output, title) {
                return (
                    '<span class="' + spanClass + '" title="' + title + '">' + output + '</span>'
                );
            };

            var currencySymbol = useInstanceDefault
                ? NutshellFrontendSettings.defaultInstanceCurrencySymbol
                : localeData.currencySymbol;

            if (_.isUndefined(currency) || _.isUndefined(currency.amount)) {
                return wrapInSpan(Nut.View.spanifyNumeralFormatted(currencySymbol + '0'), 0);
            }

            // @TODO this is terribly janky and completly ignores the currency's type ('USD')
            // but numeral.js doesn't support using 3-letter ISO 4217 code, even though it knows
            // about them.
            // also monitoring https://github.com/adamwdraper/Numeral-js/pull/108
            var formatStyle = format ? format : this.formatCurrencyAsHtmlOutputFormat;
            var content = Nut.View.spanifyNumeralFormatted(
                currencySymbol + numeral(currency.amount).format(formatStyle)
            );
            var title =
                currency.currency +
                ' ' +
                numeral(currency.amount).format(this.formatCurrencyAsHtmlTitleFormat);

            return wrapInSpan(content, title);
        },

        formatLeadValueAsHtml: function(currency, spanClass, format) {
            // beware, assuming that title will be escaped
            var wrapInSpan = function(output, title) {
                return (
                    '<span class="' + spanClass + '" title="' + title + '">' + output + '</span>'
                );
            };

            if (_.isUndefined(currency) || _.isUndefined(currency.amount)) {
                return wrapInSpan(
                    Nut.View.spanifyNumeralFormatted(localeData.currencySymbol + '0'),
                    0
                );
            }

            var content = Nut.View.spanifyNumeralFormatted(
                this.naivelyFormatCurrencyWithPrefixAndSuffix(currency)
            );
            var title = Number(currency.amount).toLocaleString(localeData.language, {
                style: 'currency',
                currency: currency.currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });

            return wrapInSpan(content, title);
        },

        /**
         * Takes a currency object and attempts to format it by both
         * suffixing the value (i.e. 51200 => 51.2k)
         *
         * This is naive because we'll always suffix the "k", "m", or "b",
         * but I imagine there are some locales where that doesn't make
         * sense to go at the end. Better than nothing, however!
         *
         * @param  {Object} currency    Currency object with ISO currency code, amount, and formatted value
         * @return {string}
         */
        naivelyFormatCurrencyWithPrefixAndSuffix: function(currency) {
            if (currency.amount < 1000) {
                // The localeDate.language comes directly from Zend, and lives
                // on the `window`
                return Number(currency.amount).toLocaleString(localeData.language, {
                    style: 'currency',
                    currency: currency.currency,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                });
            }

            var num = currency.amount;
            var suffix;
            var roundedNumber;

            if (num >= 1000000000) {
                roundedNumber = (num / 1000000000).toFixed(1).replace(/\.0$/, '');
                suffix = 'b';
            } else if (num >= 1000000) {
                roundedNumber = (num / 1000000).toFixed(1).replace(/\.0$/, '');
                suffix = 'm';
            } else if (num >= 1000) {
                roundedNumber = (num / 1000).toFixed(1).replace(/\.0$/, '');
                suffix = 'k';
            }

            var formattedNumberWithoutPrefix = Number(roundedNumber).toLocaleString(
                localeData.language,
                {
                    style: 'currency',
                    currency: currency.currency,
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                }
            );

            // Remove any trailing 0s (i.e. 124.0k should become 124k)
            var formattedNumberRemoveZeroes = formattedNumberWithoutPrefix.replace(/\.0$/, '');

            return formattedNumberRemoveZeroes + suffix;
        },

        formatNumberAsHtml: function(number, optionalNumeralFormatString) {
            optionalNumeralFormatString = optionalNumeralFormatString || '0[.]0a';
            return Nut.View.spanifyNumeralFormatted(
                numeral(number).format(optionalNumeralFormatString)
            );
        },

        formatMomentAsHumanizedDate: function(timestamp) {
            return moment.unix(timestamp).format('MMMM Do, YYYY');
        },

        // Define global handlebar template helpers and partials here.
        // Any per-view helpers can then be defined within that file
        // and should be namespaced by using
        // 		Handlebars.registerHelper('Namespace_'+key, fn);
        helpers: {
            initials: function(name) {
                name = typeof name == 'string' ? name : '';
                return _(name.split(' ').slice(0, 2)).reduce(function(memo, piece) {
                    return memo + (piece[0] || '');
                }, '');
            },
            formatTimestamp: function(timestamp, format) {
                if (!timestamp) return '';
                return moment.unix(timestamp).format(format);
            },
            linkEntity: function(model, className) {
                model = model || {};
                className = typeof className == 'string' ? className : '';
                if (model.type == 'emailAddresses') {
                    var backboneModel = Nut.Model.EmailAddresses.findOrCreate({id: model.id}),
                        entity = backboneModel.getEntity();
                    model = entity ? entity.toJSON() : model;
                }

                var unnamedTypeMapping = {
                    contacts: 'person',
                    accounts: 'company',
                    leads: 'lead',
                };

                var text = Handlebars.Utils.escapeExpression(
                        model.name || 'An unnamed ' + unnamedTypeMapping[model.type]
                    ),
                    url,
                    clientRoutable,
                    result;

                if (model.htmlUrl) {
                    url = Handlebars.Utils.escapeExpression(model.htmlUrl);
                    clientRoutable =
                        model.type === 'contacts' ||
                        model.type === 'accounts' ||
                        model.type === 'leads'
                            ? 'data-client-routable="1"'
                            : 'data-client-routable="0"';
                    result =
                        '<a href="' +
                        url +
                        '" class="' +
                        className +
                        ' type-' +
                        model.type +
                        '" ' +
                        clientRoutable +
                        '>' +
                        text +
                        '</a>';
                } else {
                    result =
                        '<span class="' +
                        className +
                        ' type-' +
                        model.type +
                        '">' +
                        text +
                        '</span>';
                }

                return new Handlebars.SafeString(result);
            },
            is_Equal: function(value, test, options) {
                if (value && value === test) {
                    return options.fn(this);
                } else {
                    return options.inverse(this);
                }
            },
            // cruft from emojify
            htmlify: function(inputString) {
                return new Handlebars.SafeString(inputString);
            },
        },

        partials: {
            // nothing here yet
        },

        events: {
            MailToWasClicked: 'MailToWasClicked',
            ReplyWasClicked: 'ReplyWasClicked',
            OpenWasClicked: 'OpenWasClicked',
        },
    };

    // register global handlebars helpers and partials
    _.each(Nut.View.helpers, function(fn, key) {
        Handlebars.registerHelper(key, fn);
    });

    _.each(Nut.View.partials, function(partial, key) {
        Handlebars.registerPartial(key, Handlebars.compile(partial));
    });

    Nut.View.select2 = Backbone.View.extend({
        initialize: function(options) {
            var view = this;

            var createNewOptionId = 'create-new';

            view.originalInput = this.$el;
            view.isMultiple = this.originalInput.attr('data-single') ? false : true;
            view.collection = new Nut.Model.CollectionsCollection();
            view.defaultToFirstItem = this.originalInput.attr('data-default-first') ? true : false;

            var createOptions = [];

            _(view.originalInput.attr('data-collection').split(',')).each(function(collection) {
                var managedCollection = Nut.managedCollections[collection];

                var isAdmin = typeof document.querySelector('body').dataset.isAdmin === 'string';

                if (managedCollection.shouldShowManageOptionToAdmins && isAdmin) {
                    var manageMessage =
                        managedCollection.type === 'accountTypes'
                            ? 'company types'
                            : managedCollection.type;

                    createOptions.push({
                        id: createNewOptionId,
                        text: '- Manage ' + manageMessage + ' -',
                        // can just reuse the "create-new" string as the type
                        // here as well to be distinct from standard options
                        type: createNewOptionId,
                        setupUrl:
                            '/setup/' +
                            managedCollection.singularLocalizedDescription.replace(' ', '-'),
                    });
                }

                view.collection.addCollection(managedCollection);
            });

            // try to infer the model from data atrs
            if (view.originalInput.attr('data-model') && typeof this.model == 'undefined') {
                this.model = window[view.originalInput.attr('data-model')];
                if (this.model instanceof Backbone.View) {
                    this.model = this.model.model;
                }
            }
            if (view.originalInput.attr('data-modelattribute')) {
                this.modelAttribute = this.originalInput.attr('data-modelattribute');
            } else if (options && options.modelAttribute) {
                this.modelAttribute = options.modelAttribute;
            }
            view.setElement(view.originalInput.parent());

            var allowClear = this.originalInput.attr('data-allowclear')
                ? this.originalInput.data('allowclear')
                : true;
            view.originalInput.select2({
                multiple: view.isMultiple,
                query: function(query) {
                    var unfilteredResults = _(
                        view.collection.filterActive().filter(function(cmodel) {
                            return cmodel.get('name');
                        })
                    ).map(function(cmodel) {
                        return {
                            id: cmodel.id,
                            text: cmodel.get('name'),
                            type: cmodel.get('type'),
                        };
                    });

                    unfilteredResults = createOptions.concat(unfilteredResults);

                    // doing string matching unless it's the create new option, but we will
                    // remove the create new if there is any string being searched for
                    results = unfilteredResults.filter(function(option) {
                        var matchesSearch = option.text
                            .toUpperCase()
                            .includes(query.term.toUpperCase());

                        var isSpecialManageOption = option.id === createNewOptionId;
                        var noSearch = query.term.length === 0;

                        return (
                            (matchesSearch && !isSpecialManageOption) ||
                            (noSearch && isSpecialManageOption)
                        );
                    });

                    query.callback({
                        results: results,
                    });
                },
                // note that this is only called if <input value=""> isn't empty, so if seeding from a model, we need to do it elsewhere
                initSelection: function(input, callback) {
                    var ids = input.val().split(','),
                        data = _(ids).map(function(id) {
                            var cmodel = view.defaultToFirstItem
                                    ? view.collection.at(0)
                                    : view.collection.get(id),
                                name = cmodel ? cmodel.get('name') : '…',
                                return_id = cmodel ? cmodel.get('id') : id;
                            input.val(return_id);
                            return {id: return_id, text: name};
                        });

                    data = view.isMultiple ? data : data.pop();
                    callback(data);
                },
                width: this.originalInput.attr('data-width') || '100%',
                nativePlaceholder: true,
                allowClear: allowClear,
                formatResultCssClass: function(result) {
                    return 'type-' + result.type;
                },
                formatResult: function(result) {
                    return '<span class="icon"></span>' + _.escape(result.text);
                },
            });

            // save a reference to the select2 object
            view.select2 = view.originalInput.data('select2');

            // trigger select2 to reload if the collection changes
            view.collection.on('add remove change reset', function() {
                // directly call select2 instead of firing an event, since we don't want to trigger a real model change
                view.select2.initSelection();
            });

            // trigger select2 to reload if the model changes
            if (view.model) {
                view.model.on('change', view.modelWasChanged, this);

                // initialize view from model
                this.modelWasChanged(true);
            }
            //Hook into the select2 event lifecycle before value is changed.
            // See: https://select2.github.io/select2/#events
            this.originalInput.on('select2-selecting', function(e) {
                if (e.val === createNewOptionId) {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = e.object.setupUrl;
                }
            });

            this.originalInput
                .on('change', this.valueWasChanged.bind(this))
                .addClass('select2-ready');
        },

        events: {
            'click .select2-search-choice': 'capsuleWasClicked',
        },

        capsuleWasClicked: function(event) {
            var capsule = $(event.currentTarget),
                cmodel = this.collection.get(capsule.data('select2-data').id);

            // it's possible that we don't have a model, if the managedcollection is missing it
            if (this.originalInput.attr('data-click') == 'popover' && cmodel) {
                //this.select2.blur(); // this looks glitchy so just accepting the focus for now. to be decided

                Popover.load({
                    url: cmodel.popoverUrl(),
                    appendTo: capsule,
                });
            }
        },

        valueWasChanged: function(event) {
            if (this.model) {
                var inputVal = this.originalInput.val(),
                    existingIds = this._getIdsFromModel(),
                    ids = '',
                    newValue = {};
                existingIds = _.isArray(existingIds) ? existingIds.join(',') : existingIds;

                if (inputVal != existingIds) {
                    // for now, i'm sending an empty string to indicate no value
                    // seems to make sense at this point. model.saveLinkIds could
                    // see this and send a PATCH remove op instead of the ''
                    ids = inputVal ? inputVal : '';

                    // until we know whether we're a multi/single relationship,
                    // send an empty string if only one item is selected, send
                    // an array if more than one. essentially binding to whether
                    // the UI allowed multiple items
                    if (inputVal.indexOf(',') !== -1) ids = ids.split(',');

                    if (_.isFunction(this.model.saveLinkIds)) {
                        // awkward code split for JsonApi models, should be able to eliminate other case quickly
                        if (this.isMultiple && !_.isArray(ids)) {
                            ids = ids.length ? ids.split(',') : [];
                        }
                        var jqxhr = this.model.saveLinkIds(this.modelAttribute, ids);

                        jqxhr.done($.proxy(this.didSaveSuccessfully, this));
                        jqxhr.fail($.proxy(this.didFailSaving, this));
                    } else {
                        if (!_.isArray(ids)) {
                            // otherwise, map will map every character in the string
                            ids = ids.length ? [ids] : [];
                        }
                        newValue[this.modelAttribute] = _(ids).map(function(id) {
                            return {id: id};
                        });
                        this.model.save(newValue);
                    }
                }
            }
        },

        modelWasChanged: function(force) {
            var ids = this._getIdsFromModel(),
                existingValue = this.originalInput.val();

            // internally select2 deals w/ multiple ids as comma-separated
            if (_.isArray(ids)) ids = ids.join(',');

            if (ids != existingValue || (typeof force != 'undefined' && force === true)) {
                this.originalInput.val(ids);
                this.select2.initSelection();
            }
        },

        didSaveSuccessfully: function() {
            this.$el.find('.select2-container').blinkSuccess();
            if (
                this.model &&
                this.model.id &&
                this.model.attributes &&
                this.model.attributes.entityType
            ) {
                $('body').trigger(Nut.events.global.entityWasEdited, {
                    entityName: this.model.attributes.entityType,
                    entityId: this.model.id,
                });
            }
        },

        didFailSaving: function(response) {
            this.$el.find('.select2-container').blinkError();
            if (response.status !== 200 && response.status !== 204) {
                var message = response.responseText;
                if (response.status == 500 || !message.length) {
                    message = 'Unknown error, please try again.';
                }
                this.displayError(message);

                // fetch should update this with the correct value, whether reverting or otherwise
                this.model.fetch();
            }
        },

        displayError: function(errorText, defaultMessage) {
            var response = null,
                $container = this.originalInput.siblings('.select2-container').first();
            defaultMessage = _.isUndefined(defaultMessage) ? 'Unknown error' : defaultMessage;
            try {
                response = $.parseJSON(errorText);
            } finally {
                if (response && response.isValidationResult && !response.isValid) {
                    Form.handleValidationResult(response.messages, $container);
                } else {
                    response = _.isString(response) ? response : defaultMessage;
                    Popmini.error(response, {attachTo: $container});
                }
            }
        },

        _getIdsFromModel: function() {
            return _.isFunction(this.model.getLinkIds)
                ? this.model.getLinkIds(this.modelAttribute)
                : _(this.model.get(this.modelAttribute)).pluck('id');
        },
    });

    Nut.View.eip = Backbone.View.extend({
        initialize: function() {
            var view = this;
            view.originalContainer = this.$el;

            var eip = EditInPlace.factory(view.originalContainer, {model: this.model});
        },
    });

    Nut.View.eipBlock = Backbone.View.extend({
        initialize: function() {
            this.modelAttribute = this.options.modelAttribute;

            // @TODO maybe memoize this somehow, since we create a new view for every attribute
            this.template = _.template($('#template-eip-' + this.modelAttribute).html(), null, {
                variable: 'attribute',
            });
        },
        render: function() {
            this.model.get(this.modelAttribute).each(function(attribute) {
                this.addAttributeBlock(attribute);
            }, this);

            // start listening to new adds
            this.listenTo(
                this.model.get(this.modelAttribute),
                'add',
                _.bind(function(newAttribute, collection, options) {
                    this.addAttributeBlock(newAttribute, options.animate);
                }, this)
            ).listenTo(
                this.model.get(this.modelAttribute),
                'change',
                _.bind(function(newAttribute) {
                    this.editAttributeBlock(newAttribute);
                }, this)
            );
        },
        tearDown: function() {
            this.undelegateEvents();
            this.remove();
        },
        editAttributeBlock: function(attribute) {
            //store old value before element was edited
            oldValue = attribute.previousAttributes().value;

            //generate new block
            var newBlock = $(this.template(attribute));
            this.setupLabelSelect(newBlock);
            var eip = EditInPlace.factory(newBlock, {
                model: attribute,
                valueElementSelector: '.subvalue',
            });

            //find which block to replace with new block
            newBlockValue = newBlock.find('span.subvalue').text();
            var $match = this.$el.find('span.subvalue').filter(function() {
                return $(this).text() == oldValue;
            });

            //replace old block with new one
            $match.closest('div.eip-field').replaceWith(newBlock);

            //success blink and attach remove listeners since the DOM element has technically changed
            newBlock.blinkSuccess();
            this.listenTo(attribute, 'remove', function() {
                newBlock.remove();
                this.updateCountClasses();
                $('body').trigger(Nut.events.global.entityWasEdited, {
                    entityName: this.model.type === 'contacts' ? 'Contacts' : 'Accounts',
                    entityId: this.model.id,
                });
            });
        },
        addAttributeBlock: function(attribute, animateIn) {
            var newBlock = $(this.template(attribute));
            this.setupLabelSelect(newBlock);
            newBlock.appendTo(this.$el);

            var eip = EditInPlace.factory(newBlock, {
                model: attribute,
                valueElementSelector: '.subvalue',
            });

            if (typeof animateIn != 'undefined' && animateIn) {
                newBlock.blinkSuccess();
            }

            this.updateCountClasses();

            this.listenTo(attribute, 'remove', function() {
                newBlock.remove();
                this.updateCountClasses();
                $('body').trigger(Nut.events.global.entityWasEdited, {
                    entityName: this.model.type === 'contacts' ? 'Contacts' : 'Accounts',
                    entityId: this.model.id,
                });
                // @TODO EIP doesn't have any sort of destroy / cleanup
            });
        },
        updateCountClasses: function() {
            var newCount = this.$el.find('.eip-field').length;

            this.$el.toggleClass('state-empty', newCount === 0);

            // unfortunately, we need to use JS to keep a first-visible-child for css
            // iterate through all sibling blocks, and give the first one w/ eip fields the class first-visible-child
            this.$el
                .siblings()
                .andSelf()
                .removeClass('first-visible-child')
                .find('.eip-field')
                .first()
                .parent()
                .addClass('first-visible-child');

            this.$el.removeClassesStartingWith('eip-count-').addClass('eip-count-' + newCount);
        },
        setupLabelSelect: function(newBlock) {
            var select = newBlock.find('select'),
                initialValue = select.data('value'),
                initialOption = select.find('option').filter(function() {
                    return $(this).val() == initialValue;
                });

            // create a custom option if it doesn't exist
            if (!initialOption.length) {
                initialOption = $(document.createElement('option'))
                    .attr('value', initialValue)
                    .text(initialValue)
                    .appendTo(select);
            }

            initialOption.attr('selected', 'selected');

            select.uniform();
        },
    });

    Nut.View.timelineCreatorRecipients = Nut.View.select2.extend({
        initialize: function() {
            _.bindAll(this, 'mailtoWasClicked');

            if (
                document.querySelector('body').hasAttribute('data-setting-mailto-nutshell') &&
                !$('div.timeline-creator').is('.state-email-not-configured')
            ) {
                $('body').on('click', 'a.action-mailto', this.mailtoWasClicked);
            }
        },

        getChoice: function(peepModel, emailAddress) {
            var id = peepModel.get('id');
            var name = peepModel.get('name');
            if (name && name.length) {
                return {
                    id: emailAddress,
                    entityId: id,
                    text: name + ' <' + emailAddress + '>',
                    address: emailAddress,
                    display: name,
                };
            } else {
                return {
                    id: emailAddress,
                    entityId: id,
                    text: emailAddress,
                    address: emailAddress,
                    display: emailAddress,
                };
            }
        },

        mailtoWasClicked: function(event) {
            event.preventDefault();
            event.stopPropagation();

            var $el = $(event.target),
                model = Nut.Model.getModel({
                    id: $el.data('parentId'),
                    type: $el.data('parentType'),
                }),
                emailAddress = $el.data('emailAddress');

            $(window).trigger(
                Nut.View.events.MailToWasClicked,
                this.getChoice(model, emailAddress)
            );

            return false;
        },
    });

    Nut.View.Peep = Backbone.View.extend({
        el: '.peep-container',

        initialize: function() {
            this.listenTo(this.model, 'initialLoad', this.render);
            this.listenTo(this.model, 'change:name', this.updateTitleFromName);
            this.listenTo(this.model, 'change:name change:description', this.updateFixedSidebar);

            var avatar = new Nut.Model.Avatar({}, {baseUrl: this.model.url() + '/'});

            // subviews
            this.subviews = {
                peepHeader: new PeepHeader({model: this.model}),
                timelineCreatorRecipients: new Nut.View.timelineCreatorRecipients({
                    model: this.model,
                    el: $('div.timeline-form'),
                }),
                peepSidebar: new PeepSidebar({model: this.model}),
                subheader: new PeepSubheader({model: this.model, avatar: avatar}),
            };

            if (typeof filepickerClient !== 'undefined') {
                var dropTargets = [
                    new Nut.View.DropTarget({model: avatar}),
                    new Nut.View.DropTarget({collection: this.model.getFiles()}),
                ];
                this.subviews.dragDropOverlay = new Nut.View.DragDropOverlay({
                    dropTargets: dropTargets,
                });
            }

            if (this.subviews.dragDropOverlay) {
                this.listenTo(
                    this.subviews.dragDropOverlay,
                    'upload-complete',
                    this.subviews.subheader.applyNewAvatar
                );
            }
        },

        tearDown: function() {
            _.each(this.subviews, function(subview) {
                if (subview.tearDown) {
                    subview.tearDown();
                }
            });
            this.undelegateEvents();
            this.remove();
        },

        render: function() {
            _.each(this.subviews, function(subview) {
                subview.render();
            });
        },

        updateTitleFromName: function() {
            document.title = this.model.getString('name') + ' · Nutshell';
        },

        // hack to bind the fixed sidebar title with name changes
        updateFixedSidebar: function() {
            $('.peep-sidebar-top .name').text(this.model.getString('name'));
            $('.peep-sidebar-top .description').text(this.model.getString('description'));
        },
    });

    var PeepHeader = Backbone.View.extend({
        el: '.peep-header',
        events: {
            'mouseover .map-zoom-target': 'hoverMapZoom',
            'mouseout .map-zoom-target': 'hoverMapZoom',
        },

        initialize: function() {
            _.bindAll(this, 'render', 'renderMap');
            this.listenTo(this.model, 'initialLoad', this.onInitialLoad);
        },

        tearDown: function() {
            $(document).off(Nut.events.card.wasAdded, '.tile-container', this.render);
            $(document).off(
                Nut.events.card.wasDeleted,
                'div.peep-cards .accounts-list',
                this.render
            );
            this.undelegateEvents();
            this.remove();
        },

        onInitialLoad: function() {
            // we don't need to listen for change, because changes cause remove+add anyway
            this.listenTo(this.model, 'add:addresses remove:addresses', this.render);

            this.listenTo(
                this.model,
                'add:urls change:urls remove:urls',
                this.debouncedGetTwitterStats
            );

            // these events are for rendering account addresses on contacts, and will eventually be replaced
            // once we figure out how we're modeling the account-contact relationships via REST
            $(document).on(Nut.events.card.wasAdded, '.tile-container', this.render);
            $(document).on(
                Nut.events.card.wasDeleted,
                'div.peep-cards .accounts-list',
                this.render
            );
        },

        render: function() {
            var address = this.model.get('addresses').getPrimary();
            if (!_.isUndefined(address)) {
                this.renderMap(this.model);
            } else if (!_.isUndefined(this.model.getRelatedAddressModel)) {
                this.model.getRelatedAddressModel().always(this.renderMap);
            } else {
                // this will clear the map
                this.renderMap(this.model);
            }
        },

        debouncedGetTwitterStats: _.debounce(
            function() {
                this.getTwitterStats(this.model);
            },
            3000,
            false // trigger on trailing edge
        ),

        renderMap: function(model) {
            var address = _.isUndefined(model) ? undefined : model.get('addresses').getPrimary(),
                location = _.isUndefined(address) ? null : address.getLocation();
            if (_.isEqual(this.currentMapLocation, location)) return;

            this.currentMapLocation = location;

            // Remove old map if present
            _(['map1', 'map2', 'map3']).each(function(map, i) {
                if (this[map]) {
                    this[map].remove();
                    this[map] = null;
                }
            }, this);

            if (!location) {
                // no location; clear weather and do nothing
                this.renderWeather();
                this.setGoogleMapsUrl(null);
                return;
            }

            var defaults = {
                lat: location.latitude,
                lon: location.longitude,
                showMarker: address.isMoreAccurateThan(
                    Nut.Model.PeepAttributesAddresses.accuracy.country
                ),
                markerType: model.get('type') == 'accounts' ? 'accounts' : 'contacts',
            };

            this.map1 = this.constructStaticMap(
                _.defaults({zoom: 5, mapId: 'header-map-zoom-1'}, defaults)
            );
            if (address.isMoreAccurateThan(Nut.Model.PeepAttributesAddresses.accuracy.subregion)) {
                this.map2 = this.constructStaticMap(
                    _.defaults({zoom: 8, mapId: 'header-map-zoom-2'}, defaults)
                );
            }
            if (address.isMoreAccurateThan(Nut.Model.PeepAttributesAddresses.accuracy.street)) {
                this.map3 = this.constructStaticMap(
                    _.defaults({zoom: 14, mapId: 'header-map-zoom-3'}, defaults)
                );
            }

            // update the google maps URL (no URL if a country-only URL)
            var url = address.isMoreAccurateThan(
                Nut.Model.PeepAttributesAddresses.accuracy.subregion
            )
                ? address.getGoogleMapsUrl()
                : null;
            this.setGoogleMapsUrl(url);

            this.getWeather(model);

            return this;
        },

        // null to disable link
        setGoogleMapsUrl: function(url) {
            this.$('.map-zoom-target')
                .closest('a')
                .attr('href', url);
        },

        getWeather: function(model) {
            // prevent 400s on models that don't have an address
            model = model ? model : this.model;
            if (!model.get('addresses').length) {
                return;
            }
            Nut.ajax.get({
                url: '/weather/for-entity',
                data: {
                    entityName: model.get('type'),
                    entityId: model.get('id'),
                },
                success: _.bind(this.renderWeather, this),
                error: $.noop,
            });
        },

        renderWeather: function(response) {
            var $weatherElement = this.$('.weather'),
                weather = response && response.weather;

            if (!weather) {
                $weatherElement.removeClass('state-hasweather');
                return;
            }

            $weatherElement.find('.temperature').text(weather.temperature || '');
            $weatherElement.find('.location').text(weather.location || '');
            $weatherElement.find('.time').text(weather.localtime || '');
            // remove any pre-existing state-weather-condition/code classes
            $weatherElement.removeClassesStartingWith('state-weather-');

            if (
                (weather.temperature && weather.temperature.length) ||
                (weather.condition && weather.condition.length)
            ) {
                $weatherElement.addClass('state-hasweather');
                if (weather.condition.length)
                    $weatherElement.addClass('state-weather-' + weather.condition);
            }
        },

        getTwitterStats: function(model) {
            // prevent 400s on models that don't have an address
            model = model ? model : this.model;
            if (!model.get('urls').length) {
                return this.renderTwitterStats();
            }
            Nut.ajax.get({
                url: '/twitter/info-for-entity',
                data: {
                    entityName: model.get('type'),
                    entityId: model.get('id'),
                },
                success: _.bind(this.renderTwitterStats, this),
                error: $.noop,
            });
        },

        renderTwitterStats: function(response) {
            var $twitterElement = this.$('.twitter'),
                twitter = response && response.twitter,
                screenName = twitter && twitter.screen_name;

            if (twitter) {
                $twitterElement
                    .find('a.followers')
                    .attr('href', 'https://twitter.com/' + screenName + '/followers')
                    .children('.number')
                    .html(Nut.View.formatNumberAsHtml(twitter.followers_count) || '');

                $twitterElement
                    .find('a.following')
                    .attr('href', 'https://twitter.com/' + screenName + '/following')
                    .children('.number')
                    .html(Nut.View.formatNumberAsHtml(twitter.friends_count) || '');

                $twitterElement
                    .find('a.tweets')
                    .attr('href', 'https://twitter.com/' + screenName)
                    .children('.number')
                    .html(Nut.View.formatNumberAsHtml(twitter.statuses_count) || '');

                $twitterElement
                    .find('a.handle')
                    .attr('href', 'https://twitter.com/' + screenName)
                    .html('@' + screenName);
                $twitterElement.addClass('state-hastwitter');
            } else {
                $twitterElement.removeClass('state-hastwitter');
            }

            $('.eip-field.type-urls.type-twitter').each(function(i, e) {
                var description = '';
                if (
                    screenName &&
                    $(e)
                        .find('span.subvalue')
                        .text()
                        .toUpperCase() ==
                        '@' + screenName.toUpperCase()
                ) {
                    description = twitter.description || '';
                }

                $(e)
                    .find('div.twitter-description')
                    .text(description);
            });
        },

        constructStaticMap: function(options) {
            var target = $('#' + options.mapId),
                mapUrlParams =
                    '?access_token=' + MapboxConfig.apiKey + '&attribution=false&logo=false',
                mapUrl =
                    'https://api.mapbox.com/styles/v1/' +
                    MapboxConfig.newMapId +
                    '/static/' +
                    options.lon +
                    ',' +
                    options.lat +
                    ',' +
                    options.zoom +
                    '/' +
                    Math.min(target.width(), 1280) +
                    'x' +
                    target.height(),
                retinaMapUrl = mapUrl + '@2x';

            mapUrl += mapUrlParams;
            retinaMapUrl += mapUrlParams;

            var mapDom = $(
                '<div class="map-loaded"><div class="shadow-inset"></div><div class="marker-icon type-' +
                    options.markerType +
                    '"></div><img class="map-image" src="' +
                    mapUrl +
                    '" srcset="' +
                    retinaMapUrl +
                    ' 2x"></div>'
            );

            target.empty(); // we pre-seed the map elements with a shadow (for during load). make sure they're pristine
            return mapDom.appendTo(target);
        },

        hoverMapZoom: function(event) {
            this.$el.toggleClass('state-superzoom', event.type == 'mouseover');
        },
    });

    var PeepSidebar = Backbone.View.extend({
        el: 'div.peep-sidebar',

        events: {
            'click .peep-sidebar-add .action-cancel': 'omnifieldClose',
            'click .peep-sidebar-add .action-save': 'omnifieldSave',
            'paste .peep-sidebar-add input': 'omnifieldPaste',
        },

        initialize: function() {
            this.eipBlocks = {};
            _(['addresses', 'emails', 'phones', 'urls']).each(function(modelAttribute, i) {
                this.eipBlocks[modelAttribute] = new Nut.View.eipBlock({
                    model: this.model,
                    modelAttribute: modelAttribute,
                    el: $('div.peep-sidebar-block.type-' + modelAttribute),
                });
            }, this);

            // Unbind previous click events, if they exist (react might've replaced this view)
            $('.peep-sidebar .action-new-information').unbind('click');

            // Now bind a click event to action-new-information
            $('.peep-sidebar .action-new-information').click(_.bind(this.omnifieldToggle, this));

            // can't make this work in the declarative events setup.
            this.$('.peep-sidebar-add input').keydown($.proxy(this.omnifieldKeydown, this));

            this.boundScrollHandler = _.bind(function() {
                this.affixSidebar(this.$el);
            }, this);

            $(window).on('scroll', this.boundScrollHandler);
        },
        tearDown: function() {
            $(window).off('scroll', this.boundScrollHandler);
            _.each(this.eipBlocks, function(eipBlock) {
                eipBlock.tearDown();
            });
            this.undelegateEvents();
            this.remove();
        },
        render: function() {
            _.each(this.eipBlocks, function(eipBlock) {
                eipBlock.render();
            });
        },
        affixSidebar: function(sidebar) {
            if (typeof sidebar.offset() === 'undefined') {
                window.trackJs.track('No offset on sidebar element for: ' + sidebar);
            } else {
                var windowPosition = sidebar.offset().top - $(window).scrollTop();

                if (!document.querySelector('body').hasAttribute('data-is-demo-silo')) {
                    // 382 looks decent – let's roll with it for now.
                    sidebar.toggleClass('state-fixed', windowPosition < -382);
                }
            }
        },
        omnifieldToggle: function() {
            this.$el.toggleClass('state-new-information');
            if (this.$el.is('.state-new-information')) {
                this.$('input[name=omnifield]').focus();
            }
        },
        omnifieldKeydown: function(event) {
            switch (event.keyCode) {
                case Nut.keys.ESC:
                    this.omnifieldClose();
                    break;
                case Nut.keys.RETURN:
                    this.omnifieldSave();
                    break;
                default:
                    break;
            }
        },
        omnifieldPaste: function(event) {
            var input = this.$('input[name=omnifield]'),
                pastedText;

            try {
                var originalEvent = event.originalEvent;
                if (originalEvent && !_.isUndefined(originalEvent.clipboardData)) {
                    pastedText = originalEvent.clipboardData.getData('text/plain');
                }

                // no luck getting data from the event, try window.clipboardData (IE)
                if (!pastedText && !_.isUndefined(window.clipboardData)) {
                    pastedText = window.clipboardData.getData('Text');
                }

                if (!pastedText || !pastedText.length) return;

                // we can't immediately put the munged string into the input, since paste hasn't happened
                // and i don't trust event.preventDefault to work cross-browser/OS
                if (pastedText && pastedText.length) {
                    var mungedText = this.mungePastedTextToAddress(pastedText);

                    // this would be more accurate if weset an interval to monitor for the value change,
                    // and inject after we see the paste happened but this is easier
                    setTimeout(function() {
                        input.val(mungedText);
                    }, 50);
                }
            } catch (e) {}
        },
        mungePastedTextToAddress: function(pastedString) {
            return pastedString.replace(/\n/g, ', ');
        },
        omnifieldClose: function() {
            this.$el.removeClass('state-new-information state-submitting');
            this.$('input[name=omnifield]').val('');
        },
        omnifieldFailed: function(xhr, textStatus, errorThrown) {
            this.$el.removeClass('state-submitting');
            var response,
                omnifield = $('input[name=omnifield]');
            // same parsing as that in Nut.ajax
            try {
                response = $.parseJSON(xhr.responseText);
            } catch (e) {
                response = null;
            }

            if (response && response.isValidationResult && !response.isValid) {
                Form.handleValidationResult(response.messages, omnifield);
            } else if (_.isString(response)) {
                Popmini.error(response, {attachTo: omnifield});
            }
        },
        omnifieldSave: function() {
            var newValue = this.$('input[name=omnifield]').val();
            newValue = $.trim(newValue);

            if (this.$el.hasClass('state-submitting')) return;
            if (!newValue.length) return;

            this.$el.addClass('state-submitting');

            this.model
                .saveOmnifield(newValue)
                .done(
                    _.bind(function(response) {
                        this.omnifieldClose();
                        $('body').trigger(Nut.events.global.entityWasEdited, {
                            entityName: this.model.type === 'contacts' ? 'Contacts' : 'Accounts',
                            entityId: this.model.id,
                        });
                    }, this)
                )
                .fail(_.bind(this.omnifieldFailed, this));
        },
    });

    var PeepSubheader = Backbone.View.extend({
        el: '.peep-subheader-container',
        events: {
            'click .action-delete, .action-undelete, .action-hard-delete': 'actionWasClicked',
        },
        initialize: function initializePeepSubheader() {
            _.bindAll(this, 'applyNewAvatar');

            this.template = _.template($('#template-peep-subheader').html(), null, {
                variable: 'peep',
            });
        },

        tearDown: function() {
            if (this.avatar) {
                this.avatar.stopListening();
            }
            if (this.peepTags) {
                this.peepTags.remove();
            }
            this.undelegateEvents();
            this.remove();
        },

        render: function() {
            var html = this.template(this.model),
                model = this.model;
            this.$el.empty().html(html);

            this.$('input.collection-select:not(.select2-ready)').each(function() {
                var view = new Nut.View.select2({
                    el: $(this),
                    model: model,
                });
            });

            this.$('.eip-field').each(function() {
                var view = new Nut.View.eip({
                    el: $(this),
                    model: model,
                });
            });

            if (typeof filepickerClient === 'undefined') {
                this.$('.avatar .overlay').remove();
            } else {
                this.avatar = new Nut.View.Avatar({
                    el: this.$('.avatar'),
                    model: this.options.avatar,
                });
            }

            // render peepTags after subheader html has been loaded
            this.peepTags = new PeepTags({model: this.model});
            this.peepTags.render();

            this.renderMergedWith();
            this.renderCreatedTime();
            // update this time every 30s (again, this should happen for all times)
            setInterval(this.renderCreatedTime.bind(this), 15000);

            return this;
        },
        applyNewAvatar: function applyNewAvatar(file) {
            if (this.avatar) this.avatar.applyNewAvatar(file);
        },
        // this is all uber-temporary, ideally there will be some date config via markup, etc
        renderCreatedTime: function() {
            var createdTime = moment.unix(this.model.get('createdTime'));

            this.$('.created-time')
                .attr('title', createdTime.format('dddd, MMMM Do YYYY, h:mm:ss a'))
                .text(createdTime.fromNow());
        },
        renderMergedWith: function() {
            var mergedWithId = this.model.getLinkIds('mergedWith'),
                deletedTime = moment.unix(Peep.currentPeep.get('deletedTime'));
            if (mergedWithId) {
                this.mergedWith = Nut.Model[Peep.getPeepType()].findOrCreate({id: mergedWithId});
                this.mergedWith.fetch({
                    success: function(model, response) {
                        var type = model.type.replace(/s$/, ''),
                            text =
                                'This ' +
                                type +
                                ' was merged with ' +
                                model.getString('name') +
                                (deletedTime != 0 ? ' ' + deletedTime.fromNow() : '.');
                        $('.peep-subheader .info-left .deleted-notices .msg-merged').html(
                            '<a href="' + model.get('htmlUrl') + '">' + text + '</a>'
                        );
                    },
                });
            }
        },
        actionWasClicked: function(event) {
            event.stopPropagation();
            var $el = $(event.target),
                url = $el.data('action'),
                confirmText = $el.data('confirmText') || 'Are you sure?';
            if (!url.length) {
                return;
            }
            Popmini.question(
                confirmText,
                {
                    attachTo: $el,
                    orientation: 'north-east',
                },
                function(yesOrNo) {
                    if (!yesOrNo) return;

                    Nut.ajax.post({
                        data: {csrfToken: Nut.getCsrfTokenFromDom()},
                        url: url,
                        error: function(response) {
                            if (
                                !_.isUndefined(response) &&
                                response.message &&
                                response.message.length
                            ) {
                                Popmini.error(response.message, {attachTo: $el});
                            } else {
                                Popmini.error('Deletion failed, please try again.', {
                                    attachTo: $el,
                                });
                            }
                        },
                    });
                }
            );
        },
    });

    var PeepTags = Backbone.View.extend({
        el: '.peep-subheader .tags-container .tags-bar',

        initialize: function() {
            this.$el.on(Nut.events.tag.wasChanged, Tags.entityTagsWereChanged);
        },

        render: function() {
            Tags.setUpTagAreas.call(this.$el);
        },
    });
})();
$(function() {
    // not sure we should be wrapping these files in dom readys, but handlebar templates aren't ready from dom yet
    // safety around these templates not being included in the dom
    var commentsListTemplate = $('#template-comments-list').html();
    var commentsCommentTemplate = $('#template-comments-comment').html();

    if (commentsListTemplate && commentsCommentTemplate) {
        Nut.View.commentsList = Backbone.View.extend({
            template: Handlebars.compile(commentsListTemplate),
            events: {
                'keypress textarea': 'textareaKeyWasPressed',
                'focus textarea': 'toggleFocusState',
                'blur textarea': 'toggleFocusState',
            },

            initialize: function(options) {
                options = _.extend(
                    {pollTimeout: 5000, pollBackgroundTimeout: 120000, scrollSpeed: 500},
                    options
                );
                this.didCreateMentionInput = false;

                this.detailView = this.options.detailView;
                this.modal = this.options.modal;
                this.hasRendered = false;

                this.listenTo(
                    this.model,
                    'add:comments remove:comments change:comments',
                    function(newmodel) {
                        this.renderList();
                    },
                    this
                );

                // if we're being displayed in a detail view, we should poll for updates, since we don't have a timeline backing us
                // with update events
                if (this.detailView) {
                    Visibility.every(
                        options.pollTimeout,
                        options.pollBackgroundTimeout,
                        this.poll.bind(this)
                    );
                }
            },

            // setInterval'd when we're in the context of a detail view
            poll: function() {
                this.model.fetch();
            },

            render: function() {
                var currentUser = Nut.managedCollections.Users.getLoggedInUser();
                var templateVars = {
                    actor: currentUser ? currentUser.toJSON() : {},
                    detailView: this.detailView,
                };
                var $newEl = $(this.template(templateVars));
                this.$el.replaceWith($newEl);
                this.setElement($newEl);
                this.delegateEvents();

                this.renderList();
                this.hasRendered = true;
            },

            renderList: function() {
                var comments = this.model.get('comments'),
                    $list = this.$('.comments-list');
                $list.empty();

                comments.each(function(commentModel) {
                    //when we add a comment, we get an early 'add' listener that fires, before the model is fully-hydrated. We don't
                    //want to append this to the DOM yet, so we wait for a following 'change' events (and another add event, actually) to
                    //trigger, which lets us know the model is now fully hydrated and we can add it to the DOM. This is a smell, since we're
                    //getting 3 events for each add (add > change > add), but this is currently a stop-gap.
                    if (commentModel.isNew()) return;
                    var newView = new Nut.View.commentsComment({
                        model: commentModel,
                        detailView: this.detailView,
                    });
                    $list.append(newView.$el);
                }, this);

                if (!this.hasRendered && (this.detailView || this.modal)) {
                    this.scrollToComment(this.options);
                }
            },

            // submit-on-enter
            textareaKeyWasPressed: function(event) {
                if (event.keyCode != Nut.keys.RETURN) return;
                if (event.keyCode == Nut.keys.RETURN && event.shiftKey) return;
                event.preventDefault();

                this.submitComment();
            },

            scrollToComment: function(options) {
                if (!window.location.hash) return;

                var commentId = window.location.hash.split('#')[1];
                var scrollContainer = options.modal ? $('.modal-card-body') : $('body');
                var scrollSpeed = options.scrollSpeed;
                var commentTarget = this.$(
                    ".timeline-comment[data-comment-id='" + commentId + "']"
                );

                if (commentTarget && commentTarget.offset()) {
                    _.defer(function() {
                        scrollContainer.animate(
                            {scrollTop: commentTarget.offset().top},
                            scrollSpeed,
                            function() {
                                commentTarget.blinkSuccess();
                            }
                        );
                    });
                }
            },

            toggleFocusState: function(event) {
                var isFocusing = event.type == 'focusin';

                // lazy-initialize mentions + elastic until this focus event, since they're slow and dom-manip-y
                if (isFocusing && !this.didCreateMentionInput) {
                    var textarea = this.$('textarea.mention');
                    this.didCreateMentionInput = true;

                    textarea.elastic({breathingRoom: false, oneLine: true});
                    Mentions.MentionsInput(textarea);
                    textarea.focus(); // initialization causes a blur, since the textarea is reattached
                }

                var comment = $(event.target).closest('.comments');
                comment.toggleClass('state-focused', isFocusing);
            },

            submitComment: function() {
                var text = this.$('textarea').data('messageText');

                // Only save if there is some text
                if (!text.trim().length) return;

                this.$el.addClass('state-saving');
                var newComment = new Nut.Model.Comments({parent: this.model, bodyMarkup: text});
                newComment
                    .save({wait: true})
                    .always(
                        function() {
                            this.$el.removeClass('state-saving');
                        }.bind(this)
                    )
                    .done(this.resetForm.bind(this))
                    .done(
                        function() {
                            this.postAnalytics(newComment);
                        }.bind(this)
                    );
            },

            resetForm: function() {
                // the css height reset is to quickly reset elastic() height
                this.$('textarea')
                    .val('')
                    .mentionsInput('reset')
                    .css('height', '')
                    .focus();
            },

            postAnalytics: function(newComment) {
                try {
                    Analytics.trackEvent(
                        Analytics.categories.usage,
                        Analytics.actions.usage.addComment,
                        {parentType: newComment.get('parent').get('type')}
                    );
                } catch (e) {
                    Nut.log('Error tracking comment ', e);
                }
            },
        });

        Nut.View.commentsComment = Backbone.View.extend({
            template: Handlebars.compile(commentsCommentTemplate),
            events: {
                'click .action-delete': function(event) {
                    // stop propagation so it doesn't fetch the email body
                    event.preventDefault();
                    event.stopPropagation();
                    var $target = $(event.target);
                    Popmini.question(
                        'Are you sure you want to delete this comment?',
                        {attachTo: $target},
                        _.bind(function(didClickYes) {
                            if (!didClickYes) return;
                            this.model.destroy({
                                success: _.bind(function(model, response) {
                                    $target.remove();
                                }, this),
                            });
                        }, this)
                    );
                },
            },

            initialize: function(options) {
                this.detailView = this.options.detailView;
                this.render();
            },

            render: function() {
                var templateVars = {
                        actor: this.actor(),
                        body: this.body(),
                        bodyMarkup: this.model.getString('bodyMarkup'),
                        detailView: this.detailView,
                        id: this.model.id,
                        mentionedEntities: this.mentionedEntities(),
                    },
                    html = this.template(templateVars);
                this.setElement($(html));

                this.timestamp = new Nut.View.timestamp({
                    model: this.model,
                    modelAttribute: 'createdTime',
                    el: this.$('.timestamp'),
                    format: 'relative',
                });
            },

            actor: function() {
                var actor = this.model.get('creator');
                return actor ? actor.toJSON() : {name: 'Somebody'};
            },

            body: function() {
                return this.model.getString('bodyHtml');
            },

            mentionedEntities: function() {
                return JSON.stringify(this.model.getMentionedEntities());
            },
        });
    }
}); // file wrapped in a jquery domready
Nut.View.timestamp = Backbone.View.extend({
    initialize: function(options) {
        this.listenTo(this.model, 'change', this.render);
        this.timestampFormat = options.format;
        this.modelAttribute = options.modelAttribute;
        this.pollInterval = window.setInterval(_.bind(this.render, this), 30000);
        this.render();
    },

    render: function() {
        var formattedTimestamp = this.formatTimestamp(this.timestampFormat);
        this.$el.html(formattedTimestamp);
        this.$el.attr('title', this.formatTimestamp('standard'));
    },

    formatTimestamp: function(format) {
        var timestamp = this.model.get(this.modelAttribute);
        if (format == 'relative') {
            return this.formatRelativeTimestamp(timestamp);
        } else {
            return this.formatStandardTimestamp(timestamp);
        }
    },

    formatRelativeTimestamp: function(timestamp) {
        return moment.unix(timestamp).fromNow();
    },

    formatStandardTimestamp: function(timestamp) {
        return moment.unix(timestamp).format('MMMM D, YYYY, h:mm a');
    },
});
// prettier-ignore
$(function(){
	var Notifications = Backbone.View.extend({
		events: {
			'click .action-mark-as-read'           : 'markAsRead',
			'click .unread .link-notification'     : 'markAsReadAndFollow'
		},

		initialize: function(){
			_.bindAll(this, 'render', 'renderCount', 'markAllAsSeen');
			this.listenTo(this.collection, 'sync', this.render);
			this.listenTo(this.collection, 'change:unread', this.renderCount);

			this.favicon=new Favico({
				bgColor: '#DD0000',
				textColor: '#FFFFFF',
				animation: 'none',
				position: 'up'
			});
		},

		render: function(){
			var notificationString = this.collection.reduce(function(list, notification){
				return list += notification.get('html');
			}, '');

			var $notificationsBlock = $(notificationString);
	
			this.$el.removeClass('state-unloaded');

			if (notificationString.length) {
				this.$el.empty().append($notificationsBlock);
				if (navigator && navigator.userAgent
					&& (navigator.userAgent.indexOf('iPhone') !== -1 || navigator.userAgent.indexOf('Android')) !== -1) {
					return;
				}
				_.each(this.$el.find('.type-notification span.web-link'), function(span) {
					var $span = $(span),
						linkText = $span.text(),
						linkHref = $span.data('href');
					if (linkText && linkHref) {
						$span.parent().closest('a').attr('href', linkHref)
						$span.replaceWith('<a href="' + linkHref + '">' + linkText + '</a>');
					}
				});
			} else {
				this.$el.addClass('state-empty');
			}
		},

		renderCount: function() {
			var unreadCount = this.collection.unseen;

			// null unreadCount means we haven't really loaded it yet. don't bother updating UI
			if (unreadCount === null) return;

			var headerNotificationButton = $('#notifications-button');
			if (headerNotificationButton.length) { // the web app uses this change to update count indicator
				// note that any changes here need to be replicated in our DOM pre-population in header markup
				var unreadCountString = (unreadCount > 99) ? '99+' : unreadCount;

				headerNotificationButton.attr('data-count', unreadCount);
				headerNotificationButton.find('.count').attr(unreadCountString);
			}

			// Sidebar notifications
			var sidebarNotificationButton = $('#sidebar-notifications-badge');
			if (sidebarNotificationButton.length) {
				var unreadCountString = (unreadCount > 99) ? '99+' : unreadCount;

				sidebarNotificationButton.attr('data-count', unreadCount);
				sidebarNotificationButton.text(unreadCountString);
			}

			this.updateExternalCountViews(unreadCount);
		},

		markAsRead: function(event) {
			var notification = $(event.target).closest('li');
			var notificationId = notification.data('notificationId');

			var promise = this.collection.get(notificationId).markAsRead();

			promise.done(function(value) {
				if (value && value.success) {
					notification.removeClass('unread');
				}
			});
			return promise;
		},

		markAsReadAndFollow: function(event) {
			var linkToFollow = $(event.target).closest('a'),
				markAsReadPromise = this.markAsRead(event);

			if (event.ctrlKey || event.metaKey) {
				// allow default event behavior to propagate and open popup
			} else {
				event.preventDefault();
				// after we're sure that the event has been marked as read, then nav away
				markAsReadPromise.always(function() {
					// directly trigger a click event on the original link, no jquery / backbone fiddling.
					linkToFollow[0].click();
				})
			}

		},

		markAllAsRead: function() {
			var notificationList = this.$el,
			markAllAsReadPromise = this.collection.markAllAsRead();

			markAllAsReadPromise.done(function(value) {
				if (value && value.success) {
					notificationList.children().removeClass('unread');
					$('#notifications-button').attr('data-count', 0);
					$('#sidebar-notifications-badge').attr('data-count', 0);

					this.updateExternalCountViews(0);
				}
			}.bind(this));

			return markAllAsReadPromise;
		},

		markAllAsSeen: function() {
			var markAllAsSeenPromise = this.collection.markAllAsSeen();

			markAllAsSeenPromise.done(function(value) {
				if (value && value.success) {
					this.collection.unseen = 0;
					this.renderCount();
				}
			}.bind(this));

			return markAllAsSeenPromise;
		},

		updateExternalCountViews: function(count) {
			if (typeof count == 'undefined') count = 0;
			if (typeof NutWebApp !== 'undefined') NutWebApp._receiveNotificationCountChange(count);

			this.favicon.badge(count);
		},
	})

	if (typeof window !== 'undefined') {
		window.NutNotifications = Notifications;
	}

	var notificationList = $('.notifications-list');
	if (notificationList.length) {
		var autoFetch = notificationList.is(':visible'),
			notificationsCollection = new Nut.Model.NotificationsCollection({ autoFetch: autoFetch });

		window.notificationView = new Notifications({
			collection: notificationsCollection,
			el: '.notifications-list',
		});
	}
});
$(function() {
    function clearDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    Nut.View.DragDropOverlay = Backbone.View.extend({
        el: '.drag-drop-overlay',
        // Keeps track of the number of time dragenter/leave
        // have been fired, to properly close overlay
        counter: 0,

        initialize: function initializeDragDropOverlay(options) {
            options = options || {dropTargets: []};
            this.registerDropTargets(options.dropTargets);
            _.bindAll(
                this,
                'uploadComplete',
                'hideOverlay',
                'bodyDragEnter',
                'bodyDragLeave',
                'bodyDrop'
            );
            this.registerBodyDragEvents();
        },

        registerDropTargets: function(dropTargets) {
            var self = this;
            this.dropTargets = _.map(dropTargets || [], function(target) {
                self.listenTo(target, 'upload-complete', self.uploadComplete);
                self.listenTo(target, 'upload-error', self.hideOverlay);
                self.listenTo(target, 'dragenter', function incrementCounter() {
                    self.counter++;
                });
                self.listenTo(target, 'dragleave', function decrementCounter() {
                    self.counter--;
                });
                return target;
            });
        },

        render: function renderDragDropOverlay() {
            var self = this;
            this.$('.drag-drop-target-container')
                .empty()
                .append(
                    _.map(self.dropTargets, function(target) {
                        return target.render();
                    })
                );
        },

        registerBodyDragEvents: function registerBodyDragEvents() {
            var self = this;
            var dropSelector =
                '#lead:not(.no-drag-overlay), #peep:not(.no-drag-overlay), #myaccount:not(.no-drag-overlay)';
            $(document)
                .on('dragenter', dropSelector, self.bodyDragEnter)
                .on('dragover', dropSelector, clearDefaults)
                .on('dragleave', dropSelector, self.bodyDragLeave)
                .on('drop', dropSelector, self.bodyDrop);
        },

        bodyDragEnter: function bodyDragEnter(e) {
            clearDefaults(e);
            if (_.any($('#__filestack-picker'))) return;
            this.counter++;
            if (_.any(this.dropTargets)) {
                $('body').addClass('noScroll');
                this.$el.addClass('active');
            }
        },

        bodyDragLeave: function bodyDragLeave(e) {
            var self = this;
            clearDefaults(e);
            _.delay(function() {
                self.counter--;
                if (self.counter === 0) self.hideOverlay();
            }, 100);
        },

        bodyDrop: function bodyDrop(e) {
            clearDefaults(e);
            this.hideOverlay();
        },

        uploadComplete: function uploadComplete() {
            this.hideOverlay();
            this.render();
        },

        hideOverlay: function hideOverlay() {
            this.counter = 0;
            $('body').removeClass('noScroll');
            this.$el.removeClass('active');
        },
    });
});
$(function() {
    var dropTargetTemplate = $('#template-drop-target').html();

    if (dropTargetTemplate) {
        Nut.View.DropTarget = Backbone.View.extend({
            template: Handlebars.compile(dropTargetTemplate),
            className: 'drop-target',

            initialize: function initializeDropTarget(options) {
                var self = this;
                this.options = options || {};
                this.target = this.model || this.collection;
                _.bindAll(this, 'render');
            },

            render: function render() {
                this.$el.empty();
                this.$el.append(this.template(this.target));

                // Ensure filepicker has been configured before we use it
                var filepickerClient = Nut.modules.Global.init.configureFilePicker();

                var self = this;
                var filepickerOptions = {
                    container: self.$('.drop-overlay')[0],
                    displayMode: 'dropPane',
                    dropPane: {
                        overlay: false, // if this is true then overlays will conflict between instances
                        // Turn off as much UI as possible, override the rest with css in filepicker-modal.less
                        customText: ' ',
                        showIcon: false,
                        disableClick: true,
                        // can't set showProgress: false, or we don't get onProgress https://github.com/filestack/filestack-js/issues/313
                        // showProgress: false,
                        // can't allow cropping files during drag/drop, currently broken.  Monitor https://github.com/filestack/filestack-js/issues/350
                        // cropFiles: Boolean(this.target.pickOptions.transformations),
                        onDrop: function startUpload(e) {
                            // Prevent multi-file uploads to model
                            if (
                                self.model &&
                                e.dataTransfer &&
                                e.dataTransfer.files &&
                                e.dataTransfer.files.length !== 1
                            ) {
                                self.trigger('upload-error');
                                return;
                            }
                            // Make sure this is an image, if needed
                            // Until https://github.com/filestack/filestack-js/issues/315 is answered/fixed
                            if (self.target.pickOptions && self.target.pickOptions.accept) {
                                var accepts = new RegExp(self.target.pickOptions.accept);
                                if (!accepts.test(e.dataTransfer.files[0].type)) {
                                    self.trigger('upload-error');
                                    return;
                                }
                            }

                            e.preventDefault();
                            e.stopPropagation();
                            self.$('progress')
                                .attr('value', 0)
                                .removeClass('hidden');
                        },
                        onProgress: function updateProgress(percentComplete) {
                            self.$('progress').attr('value', percentComplete);
                        },
                        onDragEnter: function onDragEnter() {
                            self.$el.addClass('drop-ready');
                            self.trigger('dragenter');
                        },
                        onDragLeave: function onDragLeave() {
                            self.$el.removeClass('drop-ready');
                            self.trigger('dragleave');
                        },
                        onSuccess: function onSuccess(files) {
                            self.$el.removeClass('drop-ready');
                            if (self.model) {
                                self.model.save(files[0]);
                            } else {
                                self.collection.add(files);
                                self.collection.saveNew();
                            }
                            self.trigger('upload-complete');
                            self.$('progress').addClass('hidden');
                        },
                        onError: function onError() {
                            self.trigger('upload-error');
                        },
                    },
                };
                _.defaults(filepickerOptions, this.target.pickOptions);
                filepickerClient.picker(filepickerOptions).open();
                return this.$el;
            },
        });
    }
});
$(function() {
    Nut.View.Avatar = Backbone.View.extend({
        events: {
            click: 'uploadAvatar',
            'click .remove': 'removeAvatar',
        },

        initialize: function initializeAvatar(options) {
            options = options || {};
            _.bindAll(this, 'uploadAvatar', 'updateAvatar', 'removeAvatar');
            this.listenTo(this.model, 'change', this.updateAvatar);
        },

        uploadAvatar: function uploadAvatar() {
            var self = this;
            $('body').addClass('no-scroll');

            // Ensure filepicker has been configured before we use it
            var filepickerClient = Nut.modules.Global.init.configureFilePicker();

            var filestackOptions = {
                storeTo: this.model.storeOptions,
                onUploadDone: function onUploadDone(newFiles) {
                    // There should only be one, but filestack gives us an array
                    newFiles.filesUploaded.forEach(function(file) {
                        self.model.save(file);
                    });
                },
                onFileUploadFailed: function onFileUploadFailed(file, error) {
                    console.error('FilePicker error', error, 'for file', file);
                    if (typeof window.trackJs !== 'undefined') {
                        trackJs.track(
                            'FilePicker error: ' +
                                error.message +
                                ' -- ' +
                                error.details +
                                ' -- ' +
                                error.stack
                        );
                    }
                },
                onClose: function onClose() {
                    $('body').removeClass('no-scroll');
                },
            };

            _.defaults(filestackOptions, this.model.pickOptions);

            try {
                filepickerClient.picker(filestackOptions).open();
            } catch (error) {
                console.error(error.message, error.details);
                if (typeof window.trackJs !== 'undefined') {
                    trackJs.track(error.message + ': ' + JSON.stringify(error.details));
                }
            }
        },

        removeAvatar: function removeAvatar(e) {
            e.stopPropagation();
            var self = this;
            this.model.destroy();
            this.$('img').addClass('hidden');
        },

        updateAvatar: function updateAvatar() {
            var self = this;
            this.$('img')
                .attr('src', self.model.get('url'))
                .removeClass('hidden');
            this.$('.success.overlay')
                .fadeIn('fast')
                .delay(3000)
                .fadeOut('slow');
        },
    });
});
// this is an early draft of a public JS API that applications wrapping the nutshell api
// can use to interact with the inner web app.

NutWebApp = (function() {
    'use strict';

    var notificationCountCallbacks = [];

    return {
        // return a promise for success / error
        markAllNotificationsAsRead: function() {
            // currently we rely on our global notification view, which is janky, but this is meant to isolate that.
            if (!window.notificationView) {
                return $.Deferred.reject();
            }

            return window.notificationView.markAllAsRead();
        },

        // same behavior and caveats as markAllNotificationsAsRead
        markAllNotificationsAsSeen: function() {
            if (!window.notificationView) {
                return $.Deferred.reject();
            }

            return window.notificationView.markAllAsSeen();
        },

        // pass in a callback which will be called with a single integer argument of notification count
        subscribeToNotificationCountChanges: function(callback) {
            if (!_.isFunction(callback)) {
                throw new Error('Must send callback to subscribeToNotificationCountChanges');
            }

            notificationCountCallbacks.push(callback);
        },

        // calls from internal code to proxy to external listeners
        _receiveNotificationCountChange: function(count) {
            _.each(notificationCountCallbacks, function(callback) {
                callback(count);
            });
        },

        // keyboard shortcut hooks
        // not my favorite way to handle this but the event methods remain DRY at least for now..
        showCreateNew: function() {},
        showCreateNewContact: function() {},
        showCreateNewAccount: function() {},
        showCreateNewActivity: function() {},
        showCreateNewLead: function() {},
        openNutshellSupportWidget: function() {
            $(
                '#nav-top .action-open-support-widget, #header-nav .action-open-support-widget'
            ).trigger('click');

            var event = new CustomEvent('openHelp');
            window.dispatchEvent(event);
        },

        // update Mac app back/forward button state:
        setHistoryState: function(canGoBack, canGoForward) {
            var event = new CustomEvent('updateNav', {
                detail: {
                    canGoBack: canGoBack,
                    canGoForward: canGoForward,
                },
            });
            window.dispatchEvent(event);
        },
    };
})();

// temporary shim that should be moved to mac app
if (
    typeof MacGap !== 'undefined' &&
    // and this should be impossible given it is defined earlier in this file
    typeof NutWebApp !== 'undefined'
) {
    NutWebApp.subscribeToNotificationCountChanges(function(count) {
        if (count == 0) {
            MacGap.Dock.removeBadge();
        } else {
            MacGap.Dock.addBadge(count);
        }
    });
}
// not strictly jQuery, but hey.

// returns a total from an array of values
Math.sum = function(arr) {
	var total = 0;
	for (x in arr) { total += arr[x]*1; }
	return total;
};

// strips the px or % off of something returned by $().css() (because safari returns 0 as 0%, oddly)
// of course, you can just use width() or height() for a pixelless value most times
$.nounit = function(val) { return Number(val.replace(/%|px/, '')); };

jQuery.fn.removeClassesStartingWith = function(classNamePrefix) {
	return this.each(function() {
		// not compatible with IE8 (filter())
		var classes = $(this).attr('class').split(' ').filter(function(className) {
			return (className.indexOf(classNamePrefix) !== 0);
		});

		$(this).attr('class', classes.join(' '));
	});
};
/**
* hoverIntent is similar to jQuery's built-in "hover" function except that
* instead of firing the onMouseOver event immediately, hoverIntent checks
* to see if the user's mouse has slowed down (beneath the sensitivity
* threshold) before firing the onMouseOver event.
*
* hoverIntent r5 // 2007.03.27 // jQuery 1.1.2+
* <http://cherne.net/brian/resources/jquery.hoverIntent.html>
*
* hoverIntent is currently available for use in all personal or commercial
* projects under both MIT and GPL licenses. This means that you can choose
* the license that best suits your project, and use it accordingly.
*
* // basic usage (just like .hover) receives onMouseOver and onMouseOut functions
* $("ul li").hoverIntent( showNav , hideNav );
*
* // advanced usage receives configuration object only
* $("ul li").hoverIntent({
*	sensitivity: 7, // number = sensitivity threshold (must be 1 or higher)
*	interval: 100,   // number = milliseconds of polling interval
*	over: showNav,  // function = onMouseOver callback (required)
*	timeout: 0,   // number = milliseconds delay before onMouseOut function call
*	out: hideNav    // function = onMouseOut callback (required)
* });
*
* @param  f  onMouseOver function || An object with configuration options
* @param  g  onMouseOut function  || Nothing (use configuration options object)
* @author    Brian Cherne <brian@cherne.net>
*/
(function($) {
	$.fn.hoverIntent = function(f,g) {
		// default configuration options
		var cfg = {
			sensitivity: 7,
			interval: 100,
			timeout: 0
		};
		// override configuration options with user supplied object
		cfg = $.extend(cfg, g ? { over: f, out: g } : f );

		// instantiate variables
		// cX, cY = current X and Y position of mouse, updated by mousemove event
		// pX, pY = previous X and Y position of mouse, set by mouseover and polling interval
		var cX, cY, pX, pY;

		// A private function for getting mouse position
		var track = function(ev) {
			cX = ev.pageX;
			cY = ev.pageY;
		};

		// A private function for comparing current and previous mouse position
		var compare = function(ev,ob) {
			ob.hoverIntent_t = clearTimeout(ob.hoverIntent_t);
			// compare mouse positions to see if they've crossed the threshold
			if ( ( Math.abs(pX-cX) + Math.abs(pY-cY) ) < cfg.sensitivity ) {
				$(ob).unbind("mousemove",track);
				// set hoverIntent state to true (so mouseOut can be called)
				ob.hoverIntent_s = 1;
				return cfg.over.apply(ob,[ev]);
			} else {
				// set previous coordinates for next time
				pX = cX; pY = cY;
				// use self-calling timeout, guarantees intervals are spaced out properly (avoids JavaScript timer bugs)
				ob.hoverIntent_t = setTimeout( function(){compare(ev, ob);} , cfg.interval );
			}
		};

		// A private function for delaying the mouseOut function
		var delay = function(ev,ob) {
			ob.hoverIntent_t = clearTimeout(ob.hoverIntent_t);
			ob.hoverIntent_s = 0;
			return cfg.out.apply(ob,[ev]);
		};

		// A private function for handling mouse 'hovering'
		var handleHover = function(e) {
			// next three lines copied from jQuery.hover, ignore children onMouseOver/onMouseOut
			var p = (e.type == "mouseover" ? e.fromElement : e.toElement) || e.relatedTarget;
			while ( p && p != this ) { try { p = p.parentNode; } catch(e) { p = this; } }
			if ( p == this ) { return true; } // original hoverintent returned false, broke live() on inner els.

			// copy objects to be passed into t (required for event object to be passed in IE)
			var ev = $.extend({},e);
			var ob = this;

			// cancel hoverIntent timer if it exists
			if (ob.hoverIntent_t) { ob.hoverIntent_t = clearTimeout(ob.hoverIntent_t); }

			// else e.type == "onmouseover"
			if (e.type == "mouseover") {
				// set "previous" X and Y position based on initial entry point
				pX = ev.pageX; pY = ev.pageY;
				// update "current" X and Y position based on mousemove
				$(ob).bind("mousemove",track);
				// start polling interval (self-calling timeout) to compare mouse coordinates over time
				if (ob.hoverIntent_s != 1) { ob.hoverIntent_t = setTimeout( function(){compare(ev,ob);} , cfg.interval );}

			// else e.type == "onmouseout"
			} else {
				// unbind expensive mousemove event
				$(ob).unbind("mousemove",track);
				// if hoverIntent state is true, then call the mouseOut function after the specified delay
				if (ob.hoverIntent_s == 1) { ob.hoverIntent_t = setTimeout( function(){delay(ev,ob);} , cfg.timeout );}
			}
		};

		// bind the function to the two event listeners
		return this.mouseover(handleHover).mouseout(handleHover);
	};
})(jQuery);
/*! Copyright (c) 2011 Brandon Aaron (http://brandonaaron.net)
 * Licensed under the MIT License (LICENSE.txt).
 *
 * Thanks to: http://adomas.org/javascript-mouse-wheel/ for some pointers.
 * Thanks to: Mathias Bank(http://www.mathias-bank.de) for a scope bug fix.
 * Thanks to: Seamus Leahy for adding deltaX and deltaY
 *
 * Version: 3.0.6
 *
 * Requires: 1.2.2+
 */

(function($) {

var types = ['DOMMouseScroll', 'mousewheel'];

if ($.event.fixHooks) {
		for ( var i=types.length; i; ) {
				$.event.fixHooks[ types[--i] ] = $.event.mouseHooks;
		}
}

$.event.special.mousewheel = {
		setup: function() {
				if ( this.addEventListener ) {
						for ( var i=types.length; i; ) {
								this.addEventListener( types[--i], handler, false );
						}
				} else {
						this.onmousewheel = handler;
				}
		},

		teardown: function() {
				if ( this.removeEventListener ) {
						for ( var i=types.length; i; ) {
								this.removeEventListener( types[--i], handler, false );
						}
				} else {
						this.onmousewheel = null;
				}
		}
};

$.fn.extend({
		mousewheel: function(fn) {
				return fn ? this.bind("mousewheel", fn) : this.trigger("mousewheel");
		},

		unmousewheel: function(fn) {
				return this.unbind("mousewheel", fn);
		}
});


function handler(event) {
		var orgEvent = event || window.event, args = [].slice.call( arguments, 1 ), delta = 0, returnValue = true, deltaX = 0, deltaY = 0;
		event = $.event.fix(orgEvent);
		event.type = "mousewheel";

		// Old school scrollwheel delta
		if ( orgEvent.wheelDelta ) { delta = orgEvent.wheelDelta/120; }
		if ( orgEvent.detail     ) { delta = -orgEvent.detail/3; }

		// New school multidimensional scroll (touchpads) deltas
		deltaY = delta;

		// Gecko
		if ( orgEvent.axis !== undefined && orgEvent.axis === orgEvent.HORIZONTAL_AXIS ) {
				deltaY = 0;
				deltaX = -1*delta;
		}

		// Webkit
		if ( orgEvent.wheelDeltaY !== undefined ) { deltaY = orgEvent.wheelDeltaY/120; }
		if ( orgEvent.wheelDeltaX !== undefined ) { deltaX = -1*orgEvent.wheelDeltaX/120; }

		// Add event and delta to the front of the arguments
		args.unshift(event, delta, deltaX, deltaY);

		return ($.event.dispatch || $.event.handle).apply(this, args);
}

})(jQuery);
/**
 * jquery selection utilities
 * will expand for cross-browser trickiness. can't believe this doesn't exist.
 *
 * afowler
 *
 */

(function($){
$.extend({
	// a DOMSelection or whatever craziness IE will decide to give us...
	getSelectionObject: function() {
		if (window.getSelection) {
			return window.getSelection();
		} else if (document.getSelection) {
			return document.getSelection();
		} else if (document.selection && document.selection.createRange) { // IE
			return document.selection.createRange();
		}

		return false;
	},

	// boolean if a selection range exists
	hasSelection: function() {
		var selection = $.getSelectionObject();

		if (!selection) return false;
		if (selection.isCollapsed) return false;
		if (typeof selection.text !== 'undefined' && !selection.text) return false; // IE

		return true;
	},

	clearAllSelections: function() {
		var selection = $.getSelectionObject();

		if (selection.removeAllRanges) selection.removeAllRanges();
		if (selection.empty) selection.empty();
	}
});

// boolean if anything inside of the current collection is selected.
$.fn.containsSelection = function() {
	if (! $.hasSelection() ) return false;
	var selectionObject = $.getSelectionObject(),
		selectedElement;

	if (selectionObject.anchorNode) selectedElement = selectionObject.anchorNode.parentElement;
	if (selectionObject.parentElement) selectedElement = selectionObject.parentElement;

	if (!selectedElement) return false;

	return $(this).is(selectedElement) || $(this).has(selectedElement);
};

})(jQuery);
/*!
 * jQuery BBQ: Back Button & Query Library - v1.2.1 - 2/17/2010
 * http://benalman.com/projects/jquery-bbq-plugin/
 *
 * Copyright (c) 2010 "Cowboy" Ben Alman
 * Dual licensed under the MIT and GPL licenses.
 * http://benalman.com/about/license/
 */

// Script: jQuery BBQ: Back Button & Query Library
//
// *Version: 1.2.1, Last updated: 2/17/2010*
//
// Project Home - http://benalman.com/projects/jquery-bbq-plugin/
// GitHub       - http://github.com/cowboy/jquery-bbq/
// Source       - http://github.com/cowboy/jquery-bbq/raw/master/jquery.ba-bbq.js
// (Minified)   - http://github.com/cowboy/jquery-bbq/raw/master/jquery.ba-bbq.min.js (4.0kb)
//
// About: License
//
// Copyright (c) 2010 "Cowboy" Ben Alman,
// Dual licensed under the MIT and GPL licenses.
// http://benalman.com/about/license/
//
// About: Examples
//
// These working examples, complete with fully commented code, illustrate a few
// ways in which this plugin can be used.
//
// Basic AJAX     - http://benalman.com/code/projects/jquery-bbq/examples/fragment-basic/
// Advanced AJAX  - http://benalman.com/code/projects/jquery-bbq/examples/fragment-advanced/
// jQuery UI Tabs - http://benalman.com/code/projects/jquery-bbq/examples/fragment-jquery-ui-tabs/
// Deparam        - http://benalman.com/code/projects/jquery-bbq/examples/deparam/
//
// About: Support and Testing
//
// Information about what version or versions of jQuery this plugin has been
// tested with, what browsers it has been tested in, and where the unit tests
// reside (so you can test it yourself).
//
// jQuery Versions - 1.3.2, 1.4.1, 1.4.2
// Browsers Tested - Internet Explorer 6-8, Firefox 2-3.7, Safari 3-4,
//                   Chrome 4-5, Opera 9.6-10.1.
// Unit Tests      - http://benalman.com/code/projects/jquery-bbq/unit/
//
// About: Release History
//
// 1.2.1 - (2/17/2010) Actually fixed the stale window.location Safari bug from
//         <jQuery hashchange event> in BBQ, which was the main reason for the
//         previous release!
// 1.2   - (2/16/2010) Integrated <jQuery hashchange event> v1.2, which fixes a
//         Safari bug, the event can now be bound before DOM ready, and IE6/7
//         page should no longer scroll when the event is first bound. Also
//         added the <jQuery.param.fragment.noEscape> method, and reworked the
//         <hashchange event (BBQ)> internal "add" method to be compatible with
//         changes made to the jQuery 1.4.2 special events API.
// 1.1.1 - (1/22/2010) Integrated <jQuery hashchange event> v1.1, which fixes an
//         obscure IE8 EmulateIE7 meta tag compatibility mode bug.
// 1.1   - (1/9/2010) Broke out the jQuery BBQ event.special <hashchange event>
//         functionality into a separate plugin for users who want just the
//         basic event & back button support, without all the extra awesomeness
//         that BBQ provides. This plugin will be included as part of jQuery BBQ,
//         but also be available separately. See <jQuery hashchange event>
//         plugin for more information. Also added the <jQuery.bbq.removeState>
//         method and added additional <jQuery.deparam> examples.
// 1.0.3 - (12/2/2009) Fixed an issue in IE 6 where location.search and
//         location.hash would report incorrectly if the hash contained the ?
//         character. Also <jQuery.param.querystring> and <jQuery.param.fragment>
//         will no longer parse params out of a URL that doesn't contain ? or #,
//         respectively.
// 1.0.2 - (10/10/2009) Fixed an issue in IE 6/7 where the hidden IFRAME caused
//         a "This page contains both secure and nonsecure items." warning when
//         used on an https:// page.
// 1.0.1 - (10/7/2009) Fixed an issue in IE 8. Since both "IE7" and "IE8
//         Compatibility View" modes erroneously report that the browser
//         supports the native window.onhashchange event, a slightly more
//         robust test needed to be added.
// 1.0   - (10/2/2009) Initial release

(function($,window){
	'$:nomunge'; // Used by YUI compressor.

	// Some convenient shortcuts.
	var undefined,
		aps = Array.prototype.slice,
		decode = decodeURIComponent,

		// Method / object references.
		jq_param = $.param,
		jq_param_fragment,
		jq_deparam,
		jq_deparam_fragment,
		jq_bbq = $.bbq = $.bbq || {},
		jq_bbq_pushState,
		jq_bbq_getState,
		jq_elemUrlAttr,
		jq_event_special = $.event.special,

		// Reused strings.
		str_hashchange = 'hashchange',
		str_querystring = 'querystring',
		str_fragment = 'fragment',
		str_elemUrlAttr = 'elemUrlAttr',
		str_location = 'location',
		str_href = 'href',
		str_src = 'src',

		// Reused RegExp.
		re_trim_querystring = /^.*\?|#.*$/g,
		re_trim_fragment = /^.*\#/,
		re_no_escape,

		// Used by jQuery.elemUrlAttr.
		elemUrlAttr_cache = {};

	// A few commonly used bits, broken out to help reduce minified file size.

	function is_string( arg ) {
		return typeof arg === 'string';
	};

	// Why write the same function twice? Let's curry! Mmmm, curry..

	function curry( func ) {
		var args = aps.call( arguments, 1 );

		return function() {
			return func.apply( this, args.concat( aps.call( arguments ) ) );
		};
	};

	// Get location.hash (or what you'd expect location.hash to be) sans any
	// leading #. Thanks for making this necessary, Firefox!
	function get_fragment( url ) {
		return url.replace( /^[^#]*#?(.*)$/, '$1' );
	};

	// Get location.search (or what you'd expect location.search to be) sans any
	// leading #. Thanks for making this necessary, IE6!
	function get_querystring( url ) {
		return url.replace( /(?:^[^?#]*\?([^#]*).*$)?.*/, '$1' );
	};

	// Section: Param (to string)
	//
	// Method: jQuery.param.querystring
	//
	// Retrieve the query string from a URL or if no arguments are passed, the
	// current window.location.
	//
	// Usage:
	//
	// > jQuery.param.querystring( [ url ] );
	//
	// Arguments:
	//
	//  url - (String) A URL containing query string params to be parsed. If url
	//    is not passed, the current window.location is used.
	//
	// Returns:
	//
	//  (String) The parsed query string, with any leading "?" removed.
	//

	// Method: jQuery.param.querystring (build url)
	//
	// Merge a URL, with or without pre-existing query string params, plus any
	// object, params string or URL containing query string params into a new URL.
	//
	// Usage:
	//
	// > jQuery.param.querystring( url, params [, merge_mode ] );
	//
	// Arguments:
	//
	//  url - (String) A valid URL for params to be merged into. This URL may
	//    contain a query string and/or fragment (hash).
	//  params - (String) A params string or URL containing query string params to
	//    be merged into url.
	//  params - (Object) A params object to be merged into url.
	//  merge_mode - (Number) Merge behavior defaults to 0 if merge_mode is not
	//    specified, and is as-follows:
	//
	//    * 0: params in the params argument will override any query string
	//         params in url.
	//    * 1: any query string params in url will override params in the params
	//         argument.
	//    * 2: params argument will completely replace any query string in url.
	//
	// Returns:
	//
	//  (String) Either a params string with urlencoded data or a URL with a
	//    urlencoded query string in the format 'a=b&c=d&e=f'.

	// Method: jQuery.param.fragment
	//
	// Retrieve the fragment (hash) from a URL or if no arguments are passed, the
	// current window.location.
	//
	// Usage:
	//
	// > jQuery.param.fragment( [ url ] );
	//
	// Arguments:
	//
	//  url - (String) A URL containing fragment (hash) params to be parsed. If
	//    url is not passed, the current window.location is used.
	//
	// Returns:
	//
	//  (String) The parsed fragment (hash) string, with any leading "#" removed.

	// Method: jQuery.param.fragment (build url)
	//
	// Merge a URL, with or without pre-existing fragment (hash) params, plus any
	// object, params string or URL containing fragment (hash) params into a new
	// URL.
	//
	// Usage:
	//
	// > jQuery.param.fragment( url, params [, merge_mode ] );
	//
	// Arguments:
	//
	//  url - (String) A valid URL for params to be merged into. This URL may
	//    contain a query string and/or fragment (hash).
	//  params - (String) A params string or URL containing fragment (hash) params
	//    to be merged into url.
	//  params - (Object) A params object to be merged into url.
	//  merge_mode - (Number) Merge behavior defaults to 0 if merge_mode is not
	//    specified, and is as-follows:
	//
	//    * 0: params in the params argument will override any fragment (hash)
	//         params in url.
	//    * 1: any fragment (hash) params in url will override params in the
	//         params argument.
	//    * 2: params argument will completely replace any query string in url.
	//
	// Returns:
	//
	//  (String) Either a params string with urlencoded data or a URL with a
	//    urlencoded fragment (hash) in the format 'a=b&c=d&e=f'.

	function jq_param_sub( is_fragment, get_func, url, params, merge_mode ) {
		var result,
			qs,
			matches,
			url_params,
			hash;

		if ( params !== undefined ) {
			// Build URL by merging params into url string.

			// matches[1] = url part that precedes params, not including trailing ?/#
			// matches[2] = params, not including leading ?/#
			// matches[3] = if in 'querystring' mode, hash including leading #, otherwise ''
			matches = url.match( is_fragment ? /^([^#]*)\#?(.*)$/ : /^([^#?]*)\??([^#]*)(#?.*)/ );

			// Get the hash if in 'querystring' mode, and it exists.
			hash = matches[3] || '';

			if ( merge_mode === 2 && is_string( params ) ) {
				// If merge_mode is 2 and params is a string, merge the fragment / query
				// string into the URL wholesale, without converting it into an object.
				qs = params.replace( is_fragment ? re_trim_fragment : re_trim_querystring, '' );

			} else {
				// Convert relevant params in url to object.
				url_params = jq_deparam( matches[2] );

				params = is_string( params )

					// Convert passed params string into object.
					? jq_deparam[ is_fragment ? str_fragment : str_querystring ]( params )

					// Passed params object.
					: params;

				qs = merge_mode === 2 ? params                              // passed params replace url params
					: merge_mode === 1  ? $.extend( {}, params, url_params )  // url params override passed params
					: $.extend( {}, url_params, params );                     // passed params override url params

				// Convert params object to a string.
				qs = jq_param( qs );

				// Unescape characters specified via $.param.noEscape. Since only hash-
				// history users have requested this feature, it's only enabled for
				// fragment-related params strings.
				if ( is_fragment ) {
					qs = qs.replace( re_no_escape, decode );
				}
			}

			// Build URL from the base url, querystring and hash. In 'querystring'
			// mode, ? is only added if a query string exists. In 'fragment' mode, #
			// is always added.
			result = matches[1] + ( is_fragment ? '#' : qs || !matches[1] ? '?' : '' ) + qs + hash;

		} else {
			// If URL was passed in, parse params from URL string, otherwise parse
			// params from window.location.
			result = get_func( url !== undefined ? url : window[ str_location ][ str_href ] );
		}

		return result;
	};

	jq_param[ str_querystring ]                  = curry( jq_param_sub, 0, get_querystring );
	jq_param[ str_fragment ] = jq_param_fragment = curry( jq_param_sub, 1, get_fragment );

	// Method: jQuery.param.fragment.noEscape
	//
	// Specify characters that will be left unescaped when fragments are created
	// or merged using <jQuery.param.fragment>, or when the fragment is modified
	// using <jQuery.bbq.pushState>. This option only applies to serialized data
	// object fragments, and not set-as-string fragments. Does not affect the
	// query string. Defaults to ",/" (comma, forward slash).
	//
	// Note that this is considered a purely aesthetic option, and will help to
	// create URLs that "look pretty" in the address bar or bookmarks, without
	// affecting functionality in any way. That being said, be careful to not
	// unescape characters that are used as delimiters or serve a special
	// purpose, such as the "#?&=+" (octothorpe, question mark, ampersand,
	// equals, plus) characters.
	//
	// Usage:
	//
	// > jQuery.param.fragment.noEscape( [ chars ] );
	//
	// Arguments:
	//
	//  chars - (String) The characters to not escape in the fragment. If
	//    unspecified, defaults to empty string (escape all characters).
	//
	// Returns:
	//
	//  Nothing.

	jq_param_fragment.noEscape = function( chars ) {
		chars = chars || '';
		var arr = $.map( chars.split(''), encodeURIComponent );
		re_no_escape = new RegExp( arr.join('|'), 'g' );
	};

	// A sensible default. These are the characters people seem to complain about
	// "uglifying up the URL" the most.
	jq_param_fragment.noEscape( ',/' );

	// Section: Deparam (from string)
	//
	// Method: jQuery.deparam
	//
	// Deserialize a params string into an object, optionally coercing numbers,
	// booleans, null and undefined values; this method is the counterpart to the
	// internal jQuery.param method.
	//
	// Usage:
	//
	// > jQuery.deparam( params [, coerce ] );
	//
	// Arguments:
	//
	//  params - (String) A params string to be parsed.
	//  coerce - (Boolean) If true, coerces any numbers or true, false, null, and
	//    undefined to their actual value. Defaults to false if omitted.
	//
	// Returns:
	//
	//  (Object) An object representing the deserialized params string.

	$.deparam = jq_deparam = function( params, coerce ) {
		var obj = {},
			coerce_types = { 'true': !0, 'false': !1, 'null': null };

		// Iterate over all name=value pairs.
		$.each( params.replace( /\+/g, ' ' ).split( '&' ), function(j,v){
			var param = v.split( '=' ),
				key = decode( param[0] ),
				val,
				cur = obj,
				i = 0,

				// If key is more complex than 'foo', like 'a[]' or 'a[b][c]', split it
				// into its component parts.
				keys = key.split( '][' ),
				keys_last = keys.length - 1;

			// If the first keys part contains [ and the last ends with ], then []
			// are correctly balanced.
			if ( /\[/.test( keys[0] ) && /\]$/.test( keys[ keys_last ] ) ) {
				// Remove the trailing ] from the last keys part.
				keys[ keys_last ] = keys[ keys_last ].replace( /\]$/, '' );

				// Split first keys part into two parts on the [ and add them back onto
				// the beginning of the keys array.
				keys = keys.shift().split('[').concat( keys );

				keys_last = keys.length - 1;
			} else {
				// Basic 'foo' style key.
				keys_last = 0;
			}

			// Are we dealing with a name=value pair, or just a name?
			if ( param.length === 2 ) {
				val = decode( param[1] );

				// Coerce values.
				if ( coerce ) {
					val = val && !isNaN(val)            ? +val              // number
						: val === 'undefined'             ? undefined         // undefined
						: coerce_types[val] !== undefined ? coerce_types[val] // true, false, null
						: val;                                                // string
				}

				if ( keys_last ) {
					// Complex key, build deep object structure based on a few rules:
					// * The 'cur' pointer starts at the object top-level.
					// * [] = array push (n is set to array length), [n] = array if n is
					//   numeric, otherwise object.
					// * If at the last keys part, set the value.
					// * For each keys part, if the current level is undefined create an
					//   object or array based on the type of the next keys part.
					// * Move the 'cur' pointer to the next level.
					// * Rinse & repeat.
					for ( ; i <= keys_last; i++ ) {
						key = keys[i] === '' ? cur.length : keys[i];
						cur = cur[key] = i < keys_last
							? cur[key] || ( keys[i+1] && isNaN( keys[i+1] ) ? {} : [] )
							: val;
					}

				} else {
					// Simple key, even simpler rules, since only scalars and shallow
					// arrays are allowed.

					if ( $.isArray( obj[key] ) ) {
						// val is already an array, so push on the next value.
						obj[key].push( val );

					} else if ( obj[key] !== undefined ) {
						// val isn't an array, but since a second value has been specified,
						// convert val into an array.
						obj[key] = [ obj[key], val ];

					} else {
						// val is a scalar.
						obj[key] = val;
					}
				}

			} else if ( key ) {
				// No value was defined, so set something meaningful.
				obj[key] = coerce
					? undefined
					: '';
			}
		});

		return obj;
	};

	// Method: jQuery.deparam.querystring
	//
	// Parse the query string from a URL or the current window.location,
	// deserializing it into an object, optionally coercing numbers, booleans,
	// null and undefined values.
	//
	// Usage:
	//
	// > jQuery.deparam.querystring( [ url ] [, coerce ] );
	//
	// Arguments:
	//
	//  url - (String) An optional params string or URL containing query string
	//    params to be parsed. If url is omitted, the current window.location
	//    is used.
	//  coerce - (Boolean) If true, coerces any numbers or true, false, null, and
	//    undefined to their actual value. Defaults to false if omitted.
	//
	// Returns:
	//
	//  (Object) An object representing the deserialized params string.

	// Method: jQuery.deparam.fragment
	//
	// Parse the fragment (hash) from a URL or the current window.location,
	// deserializing it into an object, optionally coercing numbers, booleans,
	// null and undefined values.
	//
	// Usage:
	//
	// > jQuery.deparam.fragment( [ url ] [, coerce ] );
	//
	// Arguments:
	//
	//  url - (String) An optional params string or URL containing fragment (hash)
	//    params to be parsed. If url is omitted, the current window.location
	//    is used.
	//  coerce - (Boolean) If true, coerces any numbers or true, false, null, and
	//    undefined to their actual value. Defaults to false if omitted.
	//
	// Returns:
	//
	//  (Object) An object representing the deserialized params string.

	function jq_deparam_sub( is_fragment, url_or_params, coerce ) {
		if ( url_or_params === undefined || typeof url_or_params === 'boolean' ) {
			// url_or_params not specified.
			coerce = url_or_params;
			url_or_params = jq_param[ is_fragment ? str_fragment : str_querystring ]();
		} else {
			url_or_params = is_string( url_or_params )
				? url_or_params.replace( is_fragment ? re_trim_fragment : re_trim_querystring, '' )
				: url_or_params;
		}

		return jq_deparam( url_or_params, coerce );
	};

	jq_deparam[ str_querystring ]                    = curry( jq_deparam_sub, 0 );
	jq_deparam[ str_fragment ] = jq_deparam_fragment = curry( jq_deparam_sub, 1 );

	// Section: Element manipulation
	//
	// Method: jQuery.elemUrlAttr
	//
	// Get the internal "Default URL attribute per tag" list, or augment the list
	// with additional tag-attribute pairs, in case the defaults are insufficient.
	//
	// In the <jQuery.fn.querystring> and <jQuery.fn.fragment> methods, this list
	// is used to determine which attribute contains the URL to be modified, if
	// an "attr" param is not specified.
	//
	// Default Tag-Attribute List:
	//
	//  a      - href
	//  base   - href
	//  iframe - src
	//  img    - src
	//  input  - src
	//  form   - action
	//  link   - href
	//  script - src
	//
	// Usage:
	//
	// > jQuery.elemUrlAttr( [ tag_attr ] );
	//
	// Arguments:
	//
	//  tag_attr - (Object) An object containing a list of tag names and their
	//    associated default attribute names in the format { tag: 'attr', ... } to
	//    be merged into the internal tag-attribute list.
	//
	// Returns:
	//
	//  (Object) An object containing all stored tag-attribute values.

	// Only define function and set defaults if function doesn't already exist, as
	// the urlInternal plugin will provide this method as well.
	$[ str_elemUrlAttr ] || ($[ str_elemUrlAttr ] = function( obj ) {
		return $.extend( elemUrlAttr_cache, obj );
	})({
		a: str_href,
		base: str_href,
		iframe: str_src,
		img: str_src,
		input: str_src,
		form: 'action',
		link: str_href,
		script: str_src
	});

	jq_elemUrlAttr = $[ str_elemUrlAttr ];

	// Method: jQuery.fn.querystring
	//
	// Update URL attribute in one or more elements, merging the current URL (with
	// or without pre-existing query string params) plus any params object or
	// string into a new URL, which is then set into that attribute. Like
	// <jQuery.param.querystring (build url)>, but for all elements in a jQuery
	// collection.
	//
	// Usage:
	//
	// > jQuery('selector').querystring( [ attr, ] params [, merge_mode ] );
	//
	// Arguments:
	//
	//  attr - (String) Optional name of an attribute that will contain a URL to
	//    merge params or url into. See <jQuery.elemUrlAttr> for a list of default
	//    attributes.
	//  params - (Object) A params object to be merged into the URL attribute.
	//  params - (String) A URL containing query string params, or params string
	//    to be merged into the URL attribute.
	//  merge_mode - (Number) Merge behavior defaults to 0 if merge_mode is not
	//    specified, and is as-follows:
	//
	//    * 0: params in the params argument will override any params in attr URL.
	//    * 1: any params in attr URL will override params in the params argument.
	//    * 2: params argument will completely replace any query string in attr
	//         URL.
	//
	// Returns:
	//
	//  (jQuery) The initial jQuery collection of elements, but with modified URL
	//  attribute values.

	// Method: jQuery.fn.fragment
	//
	// Update URL attribute in one or more elements, merging the current URL (with
	// or without pre-existing fragment/hash params) plus any params object or
	// string into a new URL, which is then set into that attribute. Like
	// <jQuery.param.fragment (build url)>, but for all elements in a jQuery
	// collection.
	//
	// Usage:
	//
	// > jQuery('selector').fragment( [ attr, ] params [, merge_mode ] );
	//
	// Arguments:
	//
	//  attr - (String) Optional name of an attribute that will contain a URL to
	//    merge params into. See <jQuery.elemUrlAttr> for a list of default
	//    attributes.
	//  params - (Object) A params object to be merged into the URL attribute.
	//  params - (String) A URL containing fragment (hash) params, or params
	//    string to be merged into the URL attribute.
	//  merge_mode - (Number) Merge behavior defaults to 0 if merge_mode is not
	//    specified, and is as-follows:
	//
	//    * 0: params in the params argument will override any params in attr URL.
	//    * 1: any params in attr URL will override params in the params argument.
	//    * 2: params argument will completely replace any fragment (hash) in attr
	//         URL.
	//
	// Returns:
	//
	//  (jQuery) The initial jQuery collection of elements, but with modified URL
	//  attribute values.

	function jq_fn_sub( mode, force_attr, params, merge_mode ) {
		if ( !is_string( params ) && typeof params !== 'object' ) {
			// force_attr not specified.
			merge_mode = params;
			params = force_attr;
			force_attr = undefined;
		}

		return this.each(function(){
			var that = $(this),

				// Get attribute specified, or default specified via $.elemUrlAttr.
				attr = force_attr || jq_elemUrlAttr()[ ( this.nodeName || '' ).toLowerCase() ] || '',

				// Get URL value.
				url = attr && that.attr( attr ) || '';

			// Update attribute with new URL.
			that.attr( attr, jq_param[ mode ]( url, params, merge_mode ) );
		});

	};

	$.fn[ str_querystring ] = curry( jq_fn_sub, str_querystring );
	$.fn[ str_fragment ]    = curry( jq_fn_sub, str_fragment );

	// Section: History, hashchange event
	//
	// Method: jQuery.bbq.pushState
	//
	// Adds a 'state' into the browser history at the current position, setting
	// location.hash and triggering any bound <hashchange event> callbacks
	// (provided the new state is different than the previous state).
	//
	// If no arguments are passed, an empty state is created, which is just a
	// shortcut for jQuery.bbq.pushState( {}, 2 ).
	//
	// Usage:
	//
	// > jQuery.bbq.pushState( [ params [, merge_mode ] ] );
	//
	// Arguments:
	//
	//  params - (String) A serialized params string or a hash string beginning
	//    with # to merge into location.hash.
	//  params - (Object) A params object to merge into location.hash.
	//  merge_mode - (Number) Merge behavior defaults to 0 if merge_mode is not
	//    specified (unless a hash string beginning with # is specified, in which
	//    case merge behavior defaults to 2), and is as-follows:
	//
	//    * 0: params in the params argument will override any params in the
	//         current state.
	//    * 1: any params in the current state will override params in the params
	//         argument.
	//    * 2: params argument will completely replace current state.
	//
	// Returns:
	//
	//  Nothing.
	//
	// Additional Notes:
	//
	//  * Setting an empty state may cause the browser to scroll.
	//  * Unlike the fragment and querystring methods, if a hash string beginning
	//    with # is specified as the params agrument, merge_mode defaults to 2.

	jq_bbq.pushState = jq_bbq_pushState = function( params, merge_mode ) {
		if ( is_string( params ) && /^#/.test( params ) && merge_mode === undefined ) {
			// Params string begins with # and merge_mode not specified, so completely
			// overwrite window.location.hash.
			merge_mode = 2;
		}

		var has_args = params !== undefined,
			// Merge params into window.location using $.param.fragment.
			url = jq_param_fragment( window[ str_location ][ str_href ],
				has_args ? params : {}, has_args ? merge_mode : 2 );

		// Set new window.location.href. If hash is empty, use just # to prevent
		// browser from reloading the page. Note that Safari 3 & Chrome barf on
		// location.hash = '#'.
		window[ str_location ][ str_href ] = url + ( /#/.test( url ) ? '' : '#' );
	};

	// Method: jQuery.bbq.getState
	//
	// Retrieves the current 'state' from the browser history, parsing
	// location.hash for a specific key or returning an object containing the
	// entire state, optionally coercing numbers, booleans, null and undefined
	// values.
	//
	// Usage:
	//
	// > jQuery.bbq.getState( [ key ] [, coerce ] );
	//
	// Arguments:
	//
	//  key - (String) An optional state key for which to return a value.
	//  coerce - (Boolean) If true, coerces any numbers or true, false, null, and
	//    undefined to their actual value. Defaults to false.
	//
	// Returns:
	//
	//  (Anything) If key is passed, returns the value corresponding with that key
	//    in the location.hash 'state', or undefined. If not, an object
	//    representing the entire 'state' is returned.

	jq_bbq.getState = jq_bbq_getState = function( key, coerce ) {
		return key === undefined || typeof key === 'boolean'
			? jq_deparam_fragment( key ) // 'key' really means 'coerce' here
			: jq_deparam_fragment( coerce )[ key ];
	};

	// Method: jQuery.bbq.removeState
	//
	// Remove one or more keys from the current browser history 'state', creating
	// a new state, setting location.hash and triggering any bound
	// <hashchange event> callbacks (provided the new state is different than
	// the previous state).
	//
	// If no arguments are passed, an empty state is created, which is just a
	// shortcut for jQuery.bbq.pushState( {}, 2 ).
	//
	// Usage:
	//
	// > jQuery.bbq.removeState( [ key [, key ... ] ] );
	//
	// Arguments:
	//
	//  key - (String) One or more key values to remove from the current state,
	//    passed as individual arguments.
	//  key - (Array) A single array argument that contains a list of key values
	//    to remove from the current state.
	//
	// Returns:
	//
	//  Nothing.
	//
	// Additional Notes:
	//
	//  * Setting an empty state may cause the browser to scroll.

	jq_bbq.removeState = function( arr ) {
		var state = {};

		// If one or more arguments is passed..
		if ( arr !== undefined ) {

			// Get the current state.
			state = jq_bbq_getState();

			// For each passed key, delete the corresponding property from the current
			// state.
			$.each( $.isArray( arr ) ? arr : arguments, function(i,v){
				delete state[ v ];
			});
		}

		// Set the state, completely overriding any existing state.
		jq_bbq_pushState( state, 2 );
	};

	// Event: hashchange event (BBQ)
	//
	// Usage in jQuery 1.4 and newer:
	//
	// In jQuery 1.4 and newer, the event object passed into any hashchange event
	// callback is augmented with a copy of the location.hash fragment at the time
	// the event was triggered as its event.fragment property. In addition, the
	// event.getState method operates on this property (instead of location.hash)
	// which allows this fragment-as-a-state to be referenced later, even after
	// window.location may have changed.
	//
	// Note that event.fragment and event.getState are not defined according to
	// W3C (or any other) specification, but will still be available whether or
	// not the hashchange event exists natively in the browser, because of the
	// utility they provide.
	//
	// The event.fragment property contains the output of <jQuery.param.fragment>
	// and the event.getState method is equivalent to the <jQuery.bbq.getState>
	// method.
	//
	// > $(window).bind( 'hashchange', function( event ) {
	// >   var hash_str = event.fragment,
	// >     param_obj = event.getState(),
	// >     param_val = event.getState( 'param_name' ),
	// >     param_val_coerced = event.getState( 'param_name', true );
	// >   ...
	// > });
	//
	// Usage in jQuery 1.3.2:
	//
	// In jQuery 1.3.2, the event object cannot to be augmented as in jQuery 1.4+,
	// so the fragment state isn't bound to the event object and must instead be
	// parsed using the <jQuery.param.fragment> and <jQuery.bbq.getState> methods.
	//
	// > $(window).bind( 'hashchange', function( event ) {
	// >   var hash_str = $.param.fragment(),
	// >     param_obj = $.bbq.getState(),
	// >     param_val = $.bbq.getState( 'param_name' ),
	// >     param_val_coerced = $.bbq.getState( 'param_name', true );
	// >   ...
	// > });
	//
	// Additional Notes:
	//
	// * Due to changes in the special events API, jQuery BBQ v1.2 or newer is
	//   required to enable the augmented event object in jQuery 1.4.2 and newer.
	// * See <jQuery hashchange event> for more detailed information.

	jq_event_special[ str_hashchange ] = $.extend( jq_event_special[ str_hashchange ], {

		// Augmenting the event object with the .fragment property and .getState
		// method requires jQuery 1.4 or newer. Note: with 1.3.2, everything will
		// work, but the event won't be augmented)
		add: function( handleObj ) {
			var old_handler;

			function new_handler(e) {
				// e.fragment is set to the value of location.hash (with any leading #
				// removed) at the time the event is triggered.
				var hash = e[ str_fragment ] = jq_param_fragment();

				// e.getState() works just like $.bbq.getState(), but uses the
				// e.fragment property stored on the event object.
				e.getState = function( key, coerce ) {
					return key === undefined || typeof key === 'boolean'
						? jq_deparam( hash, key ) // 'key' really means 'coerce' here
						: jq_deparam( hash, coerce )[ key ];
				};

				old_handler.apply( this, arguments );
			};

			// This may seem a little complicated, but it normalizes the special event
			// .add method between jQuery 1.4/1.4.1 and 1.4.2+
			if ( $.isFunction( handleObj ) ) {
				// 1.4, 1.4.1
				old_handler = handleObj;
				return new_handler;
			} else {
				// 1.4.2+
				old_handler = handleObj.handler;
				handleObj.handler = new_handler;
			}
		}

	});

})(jQuery,this);

/*!
 * jQuery hashchange event - v1.2 - 2/11/2010
 * http://benalman.com/projects/jquery-hashchange-plugin/
 *
 * Copyright (c) 2010 "Cowboy" Ben Alman
 * Dual licensed under the MIT and GPL licenses.
 * http://benalman.com/about/license/
 */

// Script: jQuery hashchange event
//
// *Version: 1.2, Last updated: 2/11/2010*
//
// Project Home - http://benalman.com/projects/jquery-hashchange-plugin/
// GitHub       - http://github.com/cowboy/jquery-hashchange/
// Source       - http://github.com/cowboy/jquery-hashchange/raw/master/jquery.ba-hashchange.js
// (Minified)   - http://github.com/cowboy/jquery-hashchange/raw/master/jquery.ba-hashchange.min.js (1.1kb)
//
// About: License
//
// Copyright (c) 2010 "Cowboy" Ben Alman,
// Dual licensed under the MIT and GPL licenses.
// http://benalman.com/about/license/
//
// About: Examples
//
// This working example, complete with fully commented code, illustrate one way
// in which this plugin can be used.
//
// hashchange event - http://benalman.com/code/projects/jquery-hashchange/examples/hashchange/
//
// About: Support and Testing
//
// Information about what version or versions of jQuery this plugin has been
// tested with, what browsers it has been tested in, and where the unit tests
// reside (so you can test it yourself).
//
// jQuery Versions - 1.3.2, 1.4.1, 1.4.2
// Browsers Tested - Internet Explorer 6-8, Firefox 2-3.7, Safari 3-4, Chrome, Opera 9.6-10.1.
// Unit Tests      - http://benalman.com/code/projects/jquery-hashchange/unit/
//
// About: Known issues
//
// While this jQuery hashchange event implementation is quite stable and robust,
// there are a few unfortunate browser bugs surrounding expected hashchange
// event-based behaviors, independent of any JavaScript window.onhashchange
// abstraction. See the following examples for more information:
//
// Chrome: Back Button - http://benalman.com/code/projects/jquery-hashchange/examples/bug-chrome-back-button/
// Firefox: Remote XMLHttpRequest - http://benalman.com/code/projects/jquery-hashchange/examples/bug-firefox-remote-xhr/
// WebKit: Back Button in an Iframe - http://benalman.com/code/projects/jquery-hashchange/examples/bug-webkit-hash-iframe/
// Safari: Back Button from a different domain - http://benalman.com/code/projects/jquery-hashchange/examples/bug-safari-back-from-diff-domain/
//
// About: Release History
//
// 1.2   - (2/11/2010) Fixed a bug where coming back to a page using this plugin
//         from a page on another domain would cause an error in Safari 4. Also,
//         IE6/7 Iframe is now inserted after the body (this actually works),
//         which prevents the page from scrolling when the event is first bound.
//         Event can also now be bound before DOM ready, but it won't be usable
//         before then in IE6/7.
// 1.1   - (1/21/2010) Incorporated document.documentMode test to fix IE8 bug
//         where browser version is incorrectly reported as 8.0, despite
//         inclusion of the X-UA-Compatible IE=EmulateIE7 meta tag.
// 1.0   - (1/9/2010) Initial Release. Broke out the jQuery BBQ event.special
//         window.onhashchange functionality into a separate plugin for users
//         who want just the basic event & back button support, without all the
//         extra awesomeness that BBQ provides. This plugin will be included as
//         part of jQuery BBQ, but also be available separately.

(function($,window,undefined){
	'$:nomunge'; // Used by YUI compressor.

	// Method / object references.
	var fake_onhashchange,
		jq_event_special = $.event.special,

		// Reused strings.
		str_location = 'location',
		str_hashchange = 'hashchange',
		str_href = 'href',

		// IE6/7 specifically need some special love when it comes to back-button
		// support, so let's do a little browser sniffing..
		browser = $.browser,
		mode = document.documentMode,
		is_old_ie = browser.msie && ( mode === undefined || mode < 8 ),

		// Does the browser support window.onhashchange? Test for IE version, since
		// IE8 incorrectly reports this when in "IE7" or "IE8 Compatibility View"!
		supports_onhashchange = 'on' + str_hashchange in window && !is_old_ie;

	// Get location.hash (or what you'd expect location.hash to be) sans any
	// leading #. Thanks for making this necessary, Firefox!
	function get_fragment( url ) {
		url = url || window[ str_location ][ str_href ];
		return url.replace( /^[^#]*#?(.*)$/, '$1' );
	};

	// Property: jQuery.hashchangeDelay
	//
	// The numeric interval (in milliseconds) at which the <hashchange event>
	// polling loop executes. Defaults to 100.

	$[ str_hashchange + 'Delay' ] = 100;

	// Event: hashchange event
	//
	// Fired when location.hash changes. In browsers that support it, the native
	// window.onhashchange event is used (IE8, FF3.6), otherwise a polling loop is
	// initialized, running every <jQuery.hashchangeDelay> milliseconds to see if
	// the hash has changed. In IE 6 and 7, a hidden Iframe is created to allow
	// the back button and hash-based history to work.
	//
	// Usage:
	//
	// > $(window).bind( 'hashchange', function(e) {
	// >   var hash = location.hash;
	// >   ...
	// > });
	//
	// Additional Notes:
	//
	// * The polling loop and Iframe are not created until at least one callback
	//   is actually bound to 'hashchange'.
	// * If you need the bound callback(s) to execute immediately, in cases where
	//   the page 'state' exists on page load (via bookmark or page refresh, for
	//   example) use $(window).trigger( 'hashchange' );
	// * The event can be bound before DOM ready, but since it won't be usable
	//   before then in IE6/7 (due to the necessary Iframe), recommended usage is
	//   to bind it inside a $(document).ready() callback.

	jq_event_special[ str_hashchange ] = $.extend( jq_event_special[ str_hashchange ], {

		// Called only when the first 'hashchange' event is bound to window.
		setup: function() {
			// If window.onhashchange is supported natively, there's nothing to do..
			if ( supports_onhashchange ) { return false; }

			// Otherwise, we need to create our own. And we don't want to call this
			// until the user binds to the event, just in case they never do, since it
			// will create a polling loop and possibly even a hidden Iframe.
			$( fake_onhashchange.start );
		},

		// Called only when the last 'hashchange' event is unbound from window.
		teardown: function() {
			// If window.onhashchange is supported natively, there's nothing to do..
			if ( supports_onhashchange ) { return false; }

			// Otherwise, we need to stop ours (if possible).
			$( fake_onhashchange.stop );
		}

	});

	// fake_onhashchange does all the work of triggering the window.onhashchange
	// event for browsers that don't natively support it, including creating a
	// polling loop to watch for hash changes and in IE 6/7 creating a hidden
	// Iframe to enable back and forward.
	fake_onhashchange = (function(){
		var self = {},
			timeout_id,
			iframe,
			set_history,
			get_history;

		// Initialize. In IE 6/7, creates a hidden Iframe for history handling.
		function init(){
			// Most browsers don't need special methods here..
			set_history = get_history = function(val){ return val; };

			// But IE6/7 do!
			if ( is_old_ie ) {

				// Create hidden Iframe after the end of the body to prevent initial
				// page load from scrolling unnecessarily.
				iframe = $('<iframe src="javascript:0"/>').hide().insertAfter( 'body' )[0].contentWindow;

				// Get history by looking at the hidden Iframe's location.hash.
				get_history = function() {
					return get_fragment( iframe.document[ str_location ][ str_href ] );
				};

				// Set a new history item by opening and then closing the Iframe
				// document, *then* setting its location.hash.
				set_history = function( hash, history_hash ) {
					if ( hash !== history_hash ) {
						var doc = iframe.document;
						doc.open().close();
						doc[ str_location ].hash = '#' + hash;
					}
				};

				// Set initial history.
				set_history( get_fragment() );
			}
		};

		// Start the polling loop.
		self.start = function() {
			// Polling loop is already running!
			if ( timeout_id ) { return; }

			// Remember the initial hash so it doesn't get triggered immediately.
			var last_hash = get_fragment();

			// Initialize if not yet initialized.
			set_history || init();

			// This polling loop checks every $.hashchangeDelay milliseconds to see if
			// location.hash has changed, and triggers the 'hashchange' event on
			// window when necessary.
			(function loopy(){
				var hash = get_fragment(),
					history_hash = get_history( last_hash );

				if ( hash !== last_hash ) {
					set_history( last_hash = hash, history_hash );

					$(window).trigger( str_hashchange );

				} else if ( history_hash !== last_hash ) {
					window[ str_location ][ str_href ] = window[ str_location ][ str_href ].replace( /#.*/, '' ) + '#' + history_hash;
				}

				timeout_id = setTimeout( loopy, $[ str_hashchange + 'Delay' ] );
			})();
		};

		// Stop the polling loop, but only if an IE6/7 Iframe wasn't created. In
		// that case, even if there are no longer any bound event handlers, the
		// polling loop is still necessary for back/next to work at all!
		self.stop = function() {
			if ( !iframe ) {
				timeout_id && clearTimeout( timeout_id );
				timeout_id = 0;
			}
		};

		return self;
	})();

})(jQuery,this);
/**
*	@name							Elastic
*	@descripton						Elastic is Jquery plugin that grow and shrink your textareas automaticliy
*	@version						1.6.3
*	@requires						Jquery 1.2.6+
*
*	@author							Jan Jarfalk
*	@author-email					jan.jarfalk@unwrongest.com
*	@author-website					http://www.unwrongest.com
*
*	@licens							MIT License - http://www.opensource.org/licenses/mit-license.php
*/

(function(jQuery){
	jQuery.fn.extend({
		elastic: function(options) {

			//	We will create a div clone of the textarea
			//	by copying these attributes from the textarea to the div.
			var mimics = [
				'paddingTop',
				'paddingRight',
				'paddingBottom',
				'paddingLeft',
				'fontSize',
				'lineHeight',
				'fontFamily',
				'width',
				'fontWeight'];

			var opts = jQuery.extend({}, {breathingRoom: true}, options);

			return this.each( function(breathingRoom) {
				// Elastic only works on textareas
				if ( this.type != 'textarea' ) {
					return false;
				}

				var $textarea	=	jQuery(this),
					$twin		=	jQuery('<div />').css({'position': 'absolute','display':'none','word-wrap':'break-word'}),
					lineHeight	=	parseInt($textarea.css('line-height'),10) || parseInt($textarea.css('font-size'),'10'),
					minheight	=	parseInt($textarea.css('height'),10) || lineHeight*3,
					maxheight	=	parseInt($textarea.css('max-height'),10) || Number.MAX_VALUE,
					goalheight	=	0,
					i 			=	0;

				// Opera returns max-height of -1 if not set
				if (maxheight < 0) { maxheight = Number.MAX_VALUE; }

				// Append the twin to the DOM
				// We are going to meassure the height of this, not the textarea.
				$twin.appendTo($textarea.parent());

				// Copy the essential styles (mimics) from the textarea to the twin
				var i = mimics.length;
				while(i--){
					$twin.css(mimics[i].toString(),$textarea.css(mimics[i].toString()));
				}


				// Sets a given height and overflow state on the textarea
				function setHeightAndOverflow(height, overflow){
					curratedHeight = Math.floor(parseInt(height,10));
					if($.browser.safari) curratedHeight += 8;
					if($textarea.height() != curratedHeight){
						$textarea.css({'height': curratedHeight + 'px','overflow':overflow});

					}
				}


				// This function will update the height of the textarea if necessary
				function update() {
					// Get curated content from the textarea.
					var textareaContent = $textarea.val().replace(/&/g,'&amp;').replace(/  /g, '&nbsp;').replace(/<|>/g, '&gt;').replace(/\n/g, '<br />');

					var twinContent = $twin.html();

					if(textareaContent+'&nbsp;' != twinContent){

						// Add an extra white space so new rows are added when you are at the end of a row.
						$twin.html(textareaContent+'&nbsp;');

						// Change textarea height if twin plus the height of one line differs more than 3 pixel from textarea height
						var goalheight = $twin.height();
						if (opts.breathingRoom) goalheight += lineHeight;
						if(Math.abs(goalheight - $textarea.height()) > 3){

							if(goalheight >= maxheight) {
								setHeightAndOverflow(maxheight,'auto');
							} else if(goalheight <= minheight && !opts.oneLine) {
								setHeightAndOverflow(minheight,'hidden');
							} else {
								setHeightAndOverflow(goalheight,'hidden');
							}

						}

					}

				}

				// Hide scrollbars
				$textarea.css({'overflow':'hidden'});

				// Update textarea size on keyup
				$textarea.keyup(function(){ update(); });

				// And this line is to catch the browser paste event
				$textarea.live('input paste',function(e){ setTimeout( update, 250); });

				// Run update once when elastic is initialized
				update();

			});

				}
		});
})(jQuery);
/**
 * @author Guy Fraser, Adaptavist.com Limited
 *
 * Dual licensed under the MIT (http://www.opensource.org/licenses/mit-license.php)
 * and GPL (http://www.opensource.org/licenses/gpl-license.php) licenses.
 *
 * Version: 1.1
 *
 * Docs: http://www.adaptavist.com/display/free/jquery.eventdefer.js
 *
 */

;(function( $ ) {

	// save some electrons
	var CT = clearTimeout;
	var ST = setTimeout;
	var AP = Array.prototype;

	$.each(['trigger','triggerHandler'],function(i,method){
		$.fn[method+'Defer'] = function(){
			var args = arguments;
			// event id
			var id = method+'Defer.'+args[0];
			// clear any untriggered event with this id
			CT(this.data(id));
			// set timer for our function
			var delay = (typeof(args[args.length-1])=='number') ? AP.pop.call(args) : 150;
			var self = this;
			this.data(id,ST(function() {
				self[method].apply(self,args);
			}, delay));
			return this;
		};
	});

	$.fn.bindDefer = function() { // event, data, handler, delay
		// make local arguments visible to action function
		var args = arguments;
		// work out delay
		var delay = (typeof(args[args.length-1])=="number") ? AP.pop.call(args) : 150;
		// store original handler
		var handler = AP.pop.call(args);
		// following function is what actually calls the event listener (after delay)
		var self = this;
		// add new delayed handler
		var timer;
		AP.push.call(args,function(){
			// clear any unhandled event
			CT(timer);
			// store args for event handler action
			args = arguments;
			// start action timer
			timer = ST(function() {
				handler.apply(self,args);
			},delay);
		});
		// bind our delayed action
		this.bind.apply(this,args);
		return this;
	};

})(jQuery);
/**
 * jQuery.ScrollTo
 * Copyright (c) 2007-2009 Ariel Flesler - aflesler(at)gmail(dot)com | http://flesler.blogspot.com
 * Dual licensed under MIT and GPL.
 * Date: 5/25/2009
 *
 * @projectDescription Easy element scrolling using jQuery.
 * http://flesler.blogspot.com/2007/10/jqueryscrollto.html
 * Works with jQuery +1.2.6. Tested on FF 2/3, IE 6/7/8, Opera 9.5/6, Safari 3, Chrome 1 on WinXP.
 *
 * @author Ariel Flesler
 * @version 1.4.2
 *
 * @id jQuery.scrollTo
 * @id jQuery.fn.scrollTo
 * @param {String, Number, DOMElement, jQuery, Object} target Where to scroll the matched elements.
 *	  The different options for target are:
 *		- A number position (will be applied to all axes).
 *		- A string position ('44', '100px', '+=90', etc ) will be applied to all axes
 *		- A jQuery/DOM element ( logically, child of the element to scroll )
 *		- A string selector, that will be relative to the element to scroll ( 'li:eq(2)', etc )
 *		- A hash { top:x, left:y }, x and y can be any kind of number/string like above.
*		- A percentage of the container's dimension/s, for example: 50% to go to the middle.
 *		- The string 'max' for go-to-end. 
 * @param {Number} duration The OVERALL length of the animation, this argument can be the settings object instead.
 * @param {Object,Function} settings Optional set of settings or the onAfter callback.
 *	 @option {String} axis Which axis must be scrolled, use 'x', 'y', 'xy' or 'yx'.
 *	 @option {Number} duration The OVERALL length of the animation.
 *	 @option {String} easing The easing method for the animation.
 *	 @option {Boolean} margin If true, the margin of the target element will be deducted from the final position.
 *	 @option {Object, Number} offset Add/deduct from the end position. One number for both axes or { top:x, left:y }.
 *	 @option {Object, Number} over Add/deduct the height/width multiplied by 'over', can be { top:x, left:y } when using both axes.
 *	 @option {Boolean} queue If true, and both axis are given, the 2nd axis will only be animated after the first one ends.
 *	 @option {Function} onAfter Function to be called after the scrolling ends. 
 *	 @option {Function} onAfterFirst If queuing is activated, this function will be called after the first scrolling ends.
 * @return {jQuery} Returns the same jQuery object, for chaining.
 *
 * @desc Scroll to a fixed position
 * @example $('div').scrollTo( 340 );
 *
 * @desc Scroll relatively to the actual position
 * @example $('div').scrollTo( '+=340px', { axis:'y' } );
 *
 * @dec Scroll using a selector (relative to the scrolled element)
 * @example $('div').scrollTo( 'p.paragraph:eq(2)', 500, { easing:'swing', queue:true, axis:'xy' } );
 *
 * @ Scroll to a DOM element (same for jQuery object)
 * @example var second_child = document.getElementById('container').firstChild.nextSibling;
 *			$('#container').scrollTo( second_child, { duration:500, axis:'x', onAfter:function(){
 *				alert('scrolled!!');																   
 *			}});
 *
 * @desc Scroll on both axes, to different values
 * @example $('div').scrollTo( { top: 300, left:'+=200' }, { axis:'xy', offset:-20 } );
 */
;(function( $ ){
	
	var $scrollTo = $.scrollTo = function( target, duration, settings ){
		$(window).scrollTo( target, duration, settings );
	};

	$scrollTo.defaults = {
		axis:'xy',
		duration: parseFloat($.fn.jquery) >= 1.3 ? 0 : 1
	};

	// Returns the element that needs to be animated to scroll the window.
	// Kept for backwards compatibility (specially for localScroll & serialScroll)
	$scrollTo.window = function( scope ){
		return $(window)._scrollable();
	};

	// Hack, hack, hack :)
	// Returns the real elements to scroll (supports window/iframes, documents and regular nodes)
	$.fn._scrollable = function(){
		return this.map(function(){
			var elem = this,
				isWin = !elem.nodeName || $.inArray( elem.nodeName.toLowerCase(), ['iframe','#document','html','body'] ) != -1;

				if( !isWin )
					return elem;

			var doc = (elem.contentWindow || elem).document || elem.ownerDocument || elem;
			
			return $.browser.safari || doc.compatMode == 'BackCompat' ?
				doc.body : 
				doc.documentElement;
		});
	};

	$.fn.scrollTo = function( target, duration, settings ){
		if( typeof duration == 'object' ){
			settings = duration;
			duration = 0;
		}
		if( typeof settings == 'function' )
			settings = { onAfter:settings };
			
		if( target == 'max' )
			target = 9e9;
			
		settings = $.extend( {}, $scrollTo.defaults, settings );
		// Speed is still recognized for backwards compatibility
		duration = duration || settings.speed || settings.duration;
		// Make sure the settings are given right
		settings.queue = settings.queue && settings.axis.length > 1;
		
		if( settings.queue )
			// Let's keep the overall duration
			duration /= 2;
		settings.offset = both( settings.offset );
		settings.over = both( settings.over );

		return this._scrollable().each(function(){
			var elem = this,
				$elem = $(elem),
				targ = target, toff, attr = {},
				win = $elem.is('html,body');

			switch( typeof targ ){
				// A number will pass the regex
				case 'number':
				case 'string':
					if( /^([+-]=)?\d+(\.\d+)?(px|%)?$/.test(targ) ){
						targ = both( targ );
						// We are done
						break;
					}
					// Relative selector, no break!
					targ = $(targ,this);
				case 'object':
					// DOMElement / jQuery
					if( targ.is || targ.style )
						// Get the real position of the target 
						toff = (targ = $(targ)).offset();
			}
			$.each( settings.axis.split(''), function( i, axis ){
				var Pos	= axis == 'x' ? 'Left' : 'Top',
					pos = Pos.toLowerCase(),
					key = 'scroll' + Pos,
					old = elem[key],
					max = $scrollTo.max(elem, axis);

				if( toff ){// jQuery / DOMElement
					attr[key] = toff[pos] + ( win ? 0 : old - $elem.offset()[pos] );

					// If it's a dom element, reduce the margin
					if( settings.margin ){
						attr[key] -= parseInt(targ.css('margin'+Pos)) || 0;
						attr[key] -= parseInt(targ.css('border'+Pos+'Width')) || 0;
					}
					
					attr[key] += settings.offset[pos] || 0;
					
					if( settings.over[pos] )
						// Scroll to a fraction of its width/height
						attr[key] += targ[axis=='x'?'width':'height']() * settings.over[pos];
				}else{ 
					var val = targ[pos];
					// Handle percentage values
					attr[key] = val.slice && val.slice(-1) == '%' ? 
						parseFloat(val) / 100 * max
						: val;
				}

				// Number or 'number'
				if( /^\d+$/.test(attr[key]) )
					// Check the limits
					attr[key] = attr[key] <= 0 ? 0 : Math.min( attr[key], max );

				// Queueing axes
				if( !i && settings.queue ){
					// Don't waste time animating, if there's no need.
					if( old != attr[key] )
						// Intermediate animation
						animate( settings.onAfterFirst );
					// Don't animate this axis again in the next iteration.
					delete attr[key];
				}
			});

			animate( settings.onAfter );			

			function animate( callback ){
				$elem.animate( attr, duration, settings.easing, callback && function(){
					callback.call(this, target, settings);
				});
			};

		}).end();
	};
	
	// Max scrolling position, works on quirks mode
	// It only fails (not too badly) on IE, quirks mode.
	$scrollTo.max = function( elem, axis ){
		var Dim = axis == 'x' ? 'Width' : 'Height',
			scroll = 'scroll'+Dim;
		
		if( !$(elem).is('html,body') )
			return elem[scroll] - $(elem)[Dim.toLowerCase()]();
		
		var size = 'client' + Dim,
			html = elem.ownerDocument.documentElement,
			body = elem.ownerDocument.body;

		return Math.max( html[scroll], body[scroll] ) 
			- Math.min( html[size]  , body[size]   );
			
	};

	function both( val ){
		return typeof val == 'object' ? val : { top:val, left:val };
	};

})( jQuery );
/*
 * smartscroll: debounced scroll event for jQuery *
 * https://github.com/lukeshumard/smartscroll
 * Based on smartresize by @louis_remi: https://github.com/lrbabe/jquery.smartresize.js *
 * Copyright 2011 Louis-Remi & Luke Shumard * Licensed under the MIT license. *
 */

(function($) {
    var event = $.event,
        scrollTimeout;

    event.special.smartscroll = {
        setup: function() {
            $(this).bind('scroll', event.special.smartscroll.handler);
        },
        teardown: function() {
            $(this).unbind('scroll', event.special.smartscroll.handler);
        },
        handler: function(event, execAsap) {
            // Save the context
            var context = this,
                args = arguments;

            // set correct event type
            event.type = 'smartscroll';

            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
            scrollTimeout = setTimeout(
                function() {
                    $(context).trigger('smartscroll', args);
                },
                execAsap === 'execAsap' ? 0 : 100
            );
        },
    };

    $.fn.smartscroll = function(fn) {
        return fn ? this.bind('smartscroll', fn) : this.trigger('smartscroll', ['execAsap']);
    };

    $.almostNeedsNextPage = function(thresholdPixelsBelowViewport) {
        var pixelsBelowViewport =
            0 + $(document).height() - $(window).scrollTop() - $(window).height();

        var loadingContainerThreshold =
            $('.loading-container').offset().top + thresholdPixelsBelowViewport;
        var bottomOfScreen = $(window).scrollTop() + $(window).innerHeight();

        return (
            pixelsBelowViewport < thresholdPixelsBelowViewport ||
            loadingContainerThreshold < bottomOfScreen
        );
    };
})(jQuery);
/**
 * http://github.com/valums/file-uploader
 *
 * Multiple file upload component with progress-bar, drag-and-drop.
 * © 2010 Andrew Valums ( andrew(at)valums.com )
 *
 * Licensed under GNU GPL 2 or later, see license.txt.
 */

//
// Helper functions
//

var qq = qq || {};

/**
 * Adds all missing properties from second obj to first obj
 */
qq.extend = function(first, second){
		for (var prop in second){
				first[prop] = second[prop];
		}
};

/**
 * Searches for a given element in the array, returns -1 if it is not present.
 * @param {Number} [from] The index at which to begin the search
 */
qq.indexOf = function(arr, elt, from){
		if (arr.indexOf) return arr.indexOf(elt, from);

		from = from || 0;
		var len = arr.length;

		if (from < 0) from += len;

		for (; from < len; from++){
				if (from in arr && arr[from] === elt){
						return from;
				}
		}
		return -1;
};

qq.getUniqueId = (function(){
		var id = 0;
		return function(){ return id++; };
})();

//
// Events

qq.attach = function(element, type, fn){
		if (element.addEventListener){
				element.addEventListener(type, fn, false);
		} else if (element.attachEvent){
				element.attachEvent('on' + type, fn);
		}
};
qq.detach = function(element, type, fn){
		if (element.removeEventListener){
				element.removeEventListener(type, fn, false);
		} else if (element.attachEvent){
				element.detachEvent('on' + type, fn);
		}
};

qq.preventDefault = function(e){
		if (e.preventDefault){
				e.preventDefault();
		} else{
				e.returnValue = false;
		}
};

//
// Node manipulations

/**
 * Insert node a before node b.
 */
qq.insertBefore = function(a, b){
		b.parentNode.insertBefore(a, b);
};
qq.remove = function(element){
		element.parentNode.removeChild(element);
};

qq.contains = function(parent, descendant){
		// compareposition returns false in this case
		if (parent == descendant) return true;

		if (parent.contains){
				return parent.contains(descendant);
		} else {
				return !!(descendant.compareDocumentPosition(parent) & 8);
		}
};

/**
 * Creates and returns element from html string
 * Uses innerHTML to create an element
 */
qq.toElement = (function(){
		var div = document.createElement('div');
		return function(html){
				div.innerHTML = html;
				var element = div.firstChild;
				div.removeChild(element);
				return element;
		};
})();

//
// Node properties and attributes

/**
 * Sets styles for an element.
 * Fixes opacity in IE6-8.
 */
qq.css = function(element, styles){
		if (styles.opacity != null){
				if (typeof element.style.opacity != 'string' && typeof(element.filters) != 'undefined'){
						styles.filter = 'alpha(opacity=' + Math.round(100 * styles.opacity) + ')';
				}
		}
		qq.extend(element.style, styles);
};
qq.hasClass = function(element, name){
		var re = new RegExp('(^| )' + name + '( |$)');
		return re.test(element.className);
};
qq.addClass = function(element, name){
		if (!qq.hasClass(element, name)){
				element.className += ' ' + name;
		}
};
qq.removeClass = function(element, name){
		var re = new RegExp('(^| )' + name + '( |$)');
		element.className = element.className.replace(re, ' ').replace(/^\s+|\s+$/g, "");
};
qq.setText = function(element, text){
		element.innerText = text;
		element.textContent = text;
};

//
// Selecting elements

qq.children = function(element){
		var children = [],
		child = element.firstChild;

		while (child){
				if (child.nodeType == 1){
						children.push(child);
				}
				child = child.nextSibling;
		}

		return children;
};

qq.getByClass = function(element, className){
		if (element.querySelectorAll){
				return element.querySelectorAll('.' + className);
		}

		var result = [];
		var candidates = element.getElementsByTagName("*");
		var len = candidates.length;

		for (var i = 0; i < len; i++){
				if (qq.hasClass(candidates[i], className)){
						result.push(candidates[i]);
				}
		}
		return result;
};

/**
 * obj2url() takes a json-object as argument and generates
 * a querystring. pretty much like jQuery.param()
 *
 * how to use:
 *
 *    `qq.obj2url({a:'b',c:'d'},'http://any.url/upload?otherParam=value');`
 *
 * will result in:
 *
 *    `http://any.url/upload?otherParam=value&a=b&c=d`
 *
 * @param  Object JSON-Object
 * @param  String current querystring-part
 * @return String encoded querystring
 */
qq.obj2url = function(obj, temp, prefixDone){
		var uristrings = [],
				prefix = '&',
				add = function(nextObj, i){
						var nextTemp = temp
								? (/\[\]$/.test(temp)) // prevent double-encoding
									? temp
									: temp+'['+i+']'
								: i;
						if ((nextTemp != 'undefined') && (i != 'undefined')) {
								uristrings.push(
										(typeof nextObj === 'object')
												? qq.obj2url(nextObj, nextTemp, true)
												: (Object.prototype.toString.call(nextObj) === '[object Function]')
														? encodeURIComponent(nextTemp) + '=' + encodeURIComponent(nextObj())
														: encodeURIComponent(nextTemp) + '=' + encodeURIComponent(nextObj)
								);
						}
				};

		if (!prefixDone && temp) {
			prefix = (/\?/.test(temp)) ? (/\?$/.test(temp)) ? '' : '&' : '?';
			uristrings.push(temp);
			uristrings.push(qq.obj2url(obj));
		} else if ((Object.prototype.toString.call(obj) === '[object Array]') && (typeof obj != 'undefined') ) {
				// we wont use a for-in-loop on an array (performance)
				for (var i = 0, len = obj.length; i < len; ++i){
						add(obj[i], i);
				}
		} else if ((typeof obj != 'undefined') && (obj !== null) && (typeof obj === "object")){
				// for anything else but a scalar, we will use for-in-loop
				for (var i in obj){
						add(obj[i], i);
				}
		} else {
				uristrings.push(encodeURIComponent(temp) + '=' + encodeURIComponent(obj));
		}

		return uristrings.join(prefix)
										.replace(/^&/, '')
										.replace(/%20/g, '+');
};

//
//
// Uploader Classes
//
//

var qq = qq || {};

/**
 * Creates upload button, validates upload, but doesn't create file list or dd.
 */
qq.FileUploaderBasic = function(o){
		this._options = {
				// set to true to see the server response
				debug: false,
				action: '/server/upload',
				params: {},
				button: null,
				multiple: true,
				maxConnections: 3,
				// validation
				allowedExtensions: [],
				sizeLimit: 0,
				minSizeLimit: 0,
				// events
				// return false to cancel submit
				onSubmit: function(id, fileName){},
				onProgress: function(id, fileName, loaded, total){},
				onComplete: function(id, fileName, responseJSON){},
				onCancel: function(id, fileName){},
				// messages
				messages: {
						typeError: "{file} has invalid extension. Only {extensions} are allowed.",
						sizeError: "{file} is too large, maximum file size is {sizeLimit}.",
						minSizeError: "{file} is too small, minimum file size is {minSizeLimit}.",
						emptyError: "{file} is empty, please select files again without it.",
						onLeave: "The files are being uploaded, if you leave now the upload will be cancelled."
				},
				showMessage: function(message){
						alert(message);
				}
		};
		qq.extend(this._options, o);

		// number of files being uploaded
		this._filesInProgress = 0;
		this._handler = this._createUploadHandler();

		if (this._options.button){
				this._button = this._createUploadButton(this._options.button);
		}

		this._preventLeaveInProgress();
};

qq.FileUploaderBasic.prototype = {
		setParams: function(params){
				this._options.params = params;
		},
		getInProgress: function(){
				return this._filesInProgress;
		},
		_createUploadButton: function(element){
				var self = this;

				return new qq.UploadButton({
						element: element,
						multiple: this._options.multiple && qq.UploadHandlerXhr.isSupported(),
						onChange: function(input){
								self._onInputChange(input);
						}
				});
		},
		_createUploadHandler: function(){
				var self = this,
						handlerClass;

				if(qq.UploadHandlerXhr.isSupported()){
						handlerClass = 'UploadHandlerXhr';
				} else {
						handlerClass = 'UploadHandlerForm';
				}

				var handler = new qq[handlerClass]({
						debug: this._options.debug,
						action: this._options.action,
						maxConnections: this._options.maxConnections,
						onProgress: function(id, fileName, loaded, total){
								self._onProgress(id, fileName, loaded, total);
								self._options.onProgress(id, fileName, loaded, total);
						},
						onComplete: function(id, fileName, result){
								self._onComplete(id, fileName, result);
								self._options.onComplete(id, fileName, result);
						},
						onCancel: function(id, fileName){
								self._onCancel(id, fileName);
								self._options.onCancel(id, fileName);
						}
				});

				return handler;
		},
		_preventLeaveInProgress: function(){
				var self = this;

				qq.attach(window, 'beforeunload', function(e){
						if (!self._filesInProgress){return;}

						var e = e || window.event;
						// for ie, ff
						e.returnValue = self._options.messages.onLeave;
						// for webkit
						return self._options.messages.onLeave;
				});
		},
		_onSubmit: function(id, fileName){
				this._filesInProgress++;
		},
		_onProgress: function(id, fileName, loaded, total){
		},
		_onComplete: function(id, fileName, result){
				this._filesInProgress--;
				if (result.error){
						this._options.showMessage(result.error);
				}
		},
		_onCancel: function(id, fileName){
				this._filesInProgress--;
		},
		_onInputChange: function(input){
				if (this._handler instanceof qq.UploadHandlerXhr){
						this._uploadFileList(input.files);
				} else {
						if (this._validateFile(input)){
								this._uploadFile(input);
						}
				}
				this._button.reset();
		},
		_uploadFileList: function(files){
				for (var i=0; i<files.length; i++){
						if ( !this._validateFile(files[i])){
								return;
						}
				}

				for (var i=0; i<files.length; i++){
						this._uploadFile(files[i]);
				}
		},
		_uploadFile: function(fileContainer){
				var id = this._handler.add(fileContainer);
				var fileName = this._handler.getName(id);

				if (this._options.onSubmit(id, fileName) !== false){
						this._onSubmit(id, fileName);
						this._handler.upload(id, this._options.params);
				}
		},
		_validateFile: function(file){
				var name, size;

				if (file.value){
						// it is a file input
						// get input value and remove path to normalize
						name = file.value.replace(/.*(\/|\\)/, "");
				} else {
						// fix missing properties in Safari
						name = file.fileName != null ? file.fileName : file.name;
						size = file.fileSize != null ? file.fileSize : file.size;
				}

				if (! this._isAllowedExtension(name)){
						this._error('typeError', name);
						return false;

				} else if (size === 0){
						this._error('emptyError', name);
						return false;

				} else if (size && this._options.sizeLimit && size > this._options.sizeLimit){
						this._error('sizeError', name);
						return false;

				} else if (size && size < this._options.minSizeLimit){
						this._error('minSizeError', name);
						return false;
				}

				return true;
		},
		_error: function(code, fileName){
				var message = this._options.messages[code];
				function r(name, replacement){ message = message.replace(name, replacement); }

				r('{file}', this._formatFileName(fileName));
				r('{extensions}', this._options.allowedExtensions.join(', '));
				r('{sizeLimit}', this._formatSize(this._options.sizeLimit));
				r('{minSizeLimit}', this._formatSize(this._options.minSizeLimit));

				this._options.showMessage(message);
		},
		_formatFileName: function(name){
				if (name.length > 33){
						name = name.slice(0, 19) + '...' + name.slice(-13);
				}
				return name;
		},
		_isAllowedExtension: function(fileName){
				var ext = (-1 !== fileName.indexOf('.')) ? fileName.replace(/.*[.]/, '').toLowerCase() : '';
				var allowed = this._options.allowedExtensions;

				if (!allowed.length){return true;}

				for (var i=0; i<allowed.length; i++){
						if (allowed[i].toLowerCase() == ext){ return true;}
				}

				return false;
		},
		_formatSize: function(bytes){
				var i = -1;
				do {
						bytes = bytes / 1024;
						i++;
				} while (bytes > 99);

				return Math.max(bytes, 0.1).toFixed(1) + ['kB', 'MB', 'GB', 'TB', 'PB', 'EB'][i];
		}
};


/**
 * Class that creates upload widget with drag-and-drop and file list
 * @inherits qq.FileUploaderBasic
 */
qq.FileUploader = function(o){
		// call parent constructor
		qq.FileUploaderBasic.apply(this, arguments);

		// additional options
		qq.extend(this._options, {
				element: null,
				// if set, will be used instead of qq-upload-list in template
				listElement: null,

				template: '<div class="qq-uploader">' +
								'<div class="qq-upload-drop-area"><span>Drop files here to upload</span></div>' +
								'<div class="qq-upload-button">Upload a file</div>' +
								'<ul class="qq-upload-list"></ul>' +
						'</div>',

				// template for one item in file list
				fileTemplate: '<li>' +
								'<span class="qq-upload-file"></span>' +
								'<span class="qq-upload-spinner"></span>' +
								'<span class="qq-upload-size"></span>' +
								'<a class="qq-upload-cancel" href="#">Cancel</a>' +
								'<span class="qq-upload-failed-text">Failed</span>' +
						'</li>',

				classes: {
						// used to get elements from templates
						button: 'qq-upload-button',
						drop: 'qq-upload-drop-area',
						dropActive: 'qq-upload-drop-area-active',
						list: 'qq-upload-list',

						file: 'qq-upload-file',
						spinner: 'qq-upload-spinner',
						size: 'qq-upload-size',
						cancel: 'qq-upload-cancel',

						// added to list item when upload completes
						// used in css to hide progress spinner
						success: 'qq-upload-success',
						fail: 'qq-upload-fail'
				}
		});
		// overwrite options with user supplied
		qq.extend(this._options, o);

		this._element = this._options.element;
		this._element.innerHTML = this._options.template;
		this._listElement = this._options.listElement || this._find(this._element, 'list');

		this._classes = this._options.classes;

		this._button = this._createUploadButton(this._find(this._element, 'button'));

		this._bindCancelEvent();
		this._setupDragDrop();
};

// inherit from Basic Uploader
qq.extend(qq.FileUploader.prototype, qq.FileUploaderBasic.prototype);

qq.extend(qq.FileUploader.prototype, {
		/**
		 * Gets one of the elements listed in this._options.classes
		 **/
		_find: function(parent, type){
				var element = qq.getByClass(parent, this._options.classes[type])[0];
				if (!element){
						throw new Error('element not found ' + type);
				}

				return element;
		},
		_setupDragDrop: function(){
				var self = this,
						dropArea = this._find(this._element, 'drop');

				var dz = new qq.UploadDropZone({
						element: dropArea,
						onEnter: function(e){
								qq.addClass(dropArea, self._classes.dropActive);
								e.stopPropagation();
						},
						onLeave: function(e){
								e.stopPropagation();
						},
						onLeaveNotDescendants: function(e){
								qq.removeClass(dropArea, self._classes.dropActive);
						},
						onDrop: function(e){
								dropArea.style.display = 'none';
								qq.removeClass(dropArea, self._classes.dropActive);
								self._uploadFileList(e.dataTransfer.files);
						}
				});

				dropArea.style.display = 'none';

				qq.attach(document, 'dragenter', function(e){
						if (!dz._isValidFileDrag(e)) return;

						dropArea.style.display = 'block';
				});
				qq.attach(document, 'dragleave', function(e){
						if (!dz._isValidFileDrag(e)) return;

						var relatedTarget = document.elementFromPoint(e.clientX, e.clientY);
						// only fire when leaving document out
						if ( ! relatedTarget || relatedTarget.nodeName == "HTML"){
								dropArea.style.display = 'none';
						}
				});
		},
		_onSubmit: function(id, fileName){
				qq.FileUploaderBasic.prototype._onSubmit.apply(this, arguments);
				this._addToList(id, fileName);
		},
		_onProgress: function(id, fileName, loaded, total){
				qq.FileUploaderBasic.prototype._onProgress.apply(this, arguments);

				var item = this._getItemByFileId(id);
				var size = this._find(item, 'size');
				size.style.display = 'inline';

				var text;
				if (loaded != total){
						text = Math.round(loaded / total * 100) + '% from ' + this._formatSize(total);
				} else {
						text = this._formatSize(total);
				}

				qq.setText(size, text);
		},
		_onComplete: function(id, fileName, result){
				qq.FileUploaderBasic.prototype._onComplete.apply(this, arguments);

				// mark completed
				var item = this._getItemByFileId(id);
				qq.remove(this._find(item, 'cancel'));
				qq.remove(this._find(item, 'spinner'));

				if (result.success){
						qq.addClass(item, this._classes.success);
				} else {
						qq.addClass(item, this._classes.fail);
				}
		},
		_addToList: function(id, fileName){
				var item = qq.toElement(this._options.fileTemplate);
				item.qqFileId = id;

				var fileElement = this._find(item, 'file');
				qq.setText(fileElement, this._formatFileName(fileName));
				this._find(item, 'size').style.display = 'none';

				this._listElement.appendChild(item);
		},
		_getItemByFileId: function(id){
				var item = this._listElement.firstChild;

				// there can't be txt nodes in dynamically created list
				// and we can  use nextSibling
				while (item){
						if (item.qqFileId == id) return item;
						item = item.nextSibling;
				}
		},
		/**
		 * delegate click event for cancel link
		 **/
		_bindCancelEvent: function(){
				var self = this,
						list = this._listElement;

				qq.attach(list, 'click', function(e){
						e = e || window.event;
						var target = e.target || e.srcElement;

						if (qq.hasClass(target, self._classes.cancel)){
								qq.preventDefault(e);

								var item = target.parentNode;
								self._handler.cancel(item.qqFileId);
								qq.remove(item);
						}
				});
		}
});

qq.UploadDropZone = function(o){
		this._options = {
				element: null,
				onEnter: function(e){},
				onLeave: function(e){},
				// is not fired when leaving element by hovering descendants
				onLeaveNotDescendants: function(e){},
				onDrop: function(e){}
		};
		qq.extend(this._options, o);

		this._element = this._options.element;

		this._disableDropOutside();
		this._attachEvents();
};

qq.UploadDropZone.prototype = {
		_disableDropOutside: function(e){
				// run only once for all instances
				if (!qq.UploadDropZone.dropOutsideDisabled ){

						qq.attach(document, 'dragover', function(e){
								if (e.dataTransfer){
										e.dataTransfer.dropEffect = 'none';
										e.preventDefault();
								}
						});

						qq.UploadDropZone.dropOutsideDisabled = true;
				}
		},
		_attachEvents: function(){
				var self = this;

				qq.attach(self._element, 'dragover', function(e){
						if (!self._isValidFileDrag(e)) return;

						var effect = e.dataTransfer.effectAllowed;
						if (effect == 'move' || effect == 'linkMove'){
								e.dataTransfer.dropEffect = 'move'; // for FF (only move allowed)
						} else {
								e.dataTransfer.dropEffect = 'copy'; // for Chrome
						}

						e.stopPropagation();
						e.preventDefault();
				});

				qq.attach(self._element, 'dragenter', function(e){
						if (!self._isValidFileDrag(e)) return;

						self._options.onEnter(e);
				});

				qq.attach(self._element, 'dragleave', function(e){
						if (!self._isValidFileDrag(e)) return;

						self._options.onLeave(e);

						var relatedTarget = document.elementFromPoint(e.clientX, e.clientY);
						// do not fire when moving a mouse over a descendant
						if (qq.contains(this, relatedTarget)) return;

						self._options.onLeaveNotDescendants(e);
				});

				qq.attach(self._element, 'drop', function(e){
						if (!self._isValidFileDrag(e)) return;

						e.preventDefault();
						self._options.onDrop(e);
				});
		},
		_isValidFileDrag: function(e){
				var dt = e.dataTransfer,
						// do not check dt.types.contains in webkit, because it crashes safari 4
						isWebkit = navigator.userAgent.indexOf("AppleWebKit") > -1;

				// dt.effectAllowed is none in Safari 5
				// dt.types.contains check is for firefox
				return dt && dt.effectAllowed != 'none' &&
						(dt.files || (!isWebkit && dt.types.contains && dt.types.contains('Files')));

		}
};

qq.UploadButton = function(o){
		this._options = {
				element: null,
				// if set to true adds multiple attribute to file input
				multiple: false,
				// name attribute of file input
				name: 'file',
				onChange: function(input){},
				hoverClass: 'qq-upload-button-hover',
				focusClass: 'qq-upload-button-focus'
		};

		qq.extend(this._options, o);

		this._element = this._options.element;

		// make button suitable container for input
		qq.css(this._element, {
				// position: 'relative',
				overflow: 'hidden',
				// Make sure browse button is in the right side
				// in Internet Explorer
				direction: 'ltr'
		});

		this._input = this._createInput();
};

qq.UploadButton.prototype = {
		/* returns file input element */
		getInput: function(){
				return this._input;
		},
		/* cleans/recreates the file input */
		reset: function(){
				if (this._input.parentNode){
						qq.remove(this._input);
				}

				qq.removeClass(this._element, this._options.focusClass);
				this._input = this._createInput();
		},
		_createInput: function(){
				var input = document.createElement("input");

				if (this._options.multiple){
						input.setAttribute("multiple", "multiple");
				}

				input.setAttribute("type", "file");
				input.setAttribute("name", this._options.name);

				qq.css(input, {
						position: 'absolute',
						// in Opera only 'browse' button
						// is clickable and it is located at
						// the right side of the input
						right: 0,
						top: 0,
						fontFamily: 'Arial',
						// 4 persons reported this, the max values that worked for them were 243, 236, 236, 118
						fontSize: '118px',
						margin: 0,
						padding: 0,
						cursor: 'pointer',
						opacity: 0
				});

				this._element.appendChild(input);

				var self = this;
				qq.attach(input, 'change', function(){
						self._options.onChange(input);
				});

				qq.attach(input, 'mouseover', function(){
						qq.addClass(self._element, self._options.hoverClass);
				});
				qq.attach(input, 'mouseout', function(){
						qq.removeClass(self._element, self._options.hoverClass);
				});
				qq.attach(input, 'focus', function(){
						qq.addClass(self._element, self._options.focusClass);
				});
				qq.attach(input, 'blur', function(){
						qq.removeClass(self._element, self._options.focusClass);
				});

				// IE and Opera, unfortunately have 2 tab stops on file input
				// which is unacceptable in our case, disable keyboard access
				if (window.attachEvent){
						// it is IE or Opera
						input.setAttribute('tabIndex', "-1");
				}

				return input;
		}
};

/**
 * Class for uploading files, uploading itself is handled by child classes
 */
qq.UploadHandlerAbstract = function(o){
		this._options = {
				debug: false,
				action: '/upload.php',
				// maximum number of concurrent uploads
				maxConnections: 999,
				onProgress: function(id, fileName, loaded, total){},
				onComplete: function(id, fileName, response){},
				onCancel: function(id, fileName){}
		};
		qq.extend(this._options, o);

		this._queue = [];
		// params for files in queue
		this._params = [];
};
qq.UploadHandlerAbstract.prototype = {
		log: function(str){
				if (this._options.debug && window.console) console.log('[uploader] ' + str);
		},
		/**
		 * Adds file or file input to the queue
		 * @returns id
		 **/
		add: function(file){},
		/**
		 * Sends the file identified by id and additional query params to the server
		 */
		upload: function(id, params){
				var len = this._queue.push(id);

				var copy = {};
				qq.extend(copy, params);
				this._params[id] = copy;

				// if too many active uploads, wait...
				if (len <= this._options.maxConnections){
						this._upload(id, this._params[id]);
				}
		},
		/**
		 * Cancels file upload by id
		 */
		cancel: function(id){
				this._cancel(id);
				this._dequeue(id);
		},
		/**
		 * Cancells all uploads
		 */
		cancelAll: function(){
				for (var i=0; i<this._queue.length; i++){
						this._cancel(this._queue[i]);
				}
				this._queue = [];
		},
		/**
		 * Returns name of the file identified by id
		 */
		getName: function(id){},
		/**
		 * Returns size of the file identified by id
		 */
		getSize: function(id){},
		/**
		 * Returns id of files being uploaded or
		 * waiting for their turn
		 */
		getQueue: function(){
				return this._queue;
		},
		/**
		 * Actual upload method
		 */
		_upload: function(id){},
		/**
		 * Actual cancel method
		 */
		_cancel: function(id){},
		/**
		 * Removes element from queue, starts upload of next
		 */
		_dequeue: function(id){
				var i = qq.indexOf(this._queue, id);
				this._queue.splice(i, 1);

				var max = this._options.maxConnections;

				if (this._queue.length >= max){
						var nextId = this._queue[max-1];
						this._upload(nextId, this._params[nextId]);
				}
		}
};

/**
 * Class for uploading files using form and iframe
 * @inherits qq.UploadHandlerAbstract
 */
qq.UploadHandlerForm = function(o){
		qq.UploadHandlerAbstract.apply(this, arguments);

		this._inputs = {};
};
// @inherits qq.UploadHandlerAbstract
qq.extend(qq.UploadHandlerForm.prototype, qq.UploadHandlerAbstract.prototype);

qq.extend(qq.UploadHandlerForm.prototype, {
		add: function(fileInput){
				fileInput.setAttribute('name', 'qqfile');
				var id = 'qq-upload-handler-iframe' + qq.getUniqueId();

				this._inputs[id] = fileInput;

				// remove file input from DOM
				if (fileInput.parentNode){
						qq.remove(fileInput);
				}

				return id;
		},
		getName: function(id){
				// get input value and remove path to normalize
				return this._inputs[id].value.replace(/.*(\/|\\)/, "");
		},
		_cancel: function(id){
				this._options.onCancel(id, this.getName(id));

				delete this._inputs[id];

				var iframe = document.getElementById(id);
				if (iframe){
						// to cancel request set src to something else
						// we use src="javascript:false;" because it doesn't
						// trigger ie6 prompt on https
						iframe.setAttribute('src', 'javascript:false;');

						qq.remove(iframe);
				}
		},
		_upload: function(id, params){
				var input = this._inputs[id];

				if (!input){
						throw new Error('file with passed id was not added, or already uploaded or cancelled');
				}

				var fileName = this.getName(id);

				var iframe = this._createIframe(id);
				var form = this._createForm(iframe, params);
				form.appendChild(input);

				var self = this;
				this._attachLoadEvent(iframe, function(){
						self.log('iframe loaded');

						var response = self._getIframeContentJSON(iframe);

						self._options.onComplete(id, fileName, response);
						self._dequeue(id);

						delete self._inputs[id];
						// timeout added to fix busy state in FF3.6
						setTimeout(function(){
								qq.remove(iframe);
						}, 1);
				});

				form.submit();
				qq.remove(form);

				return id;
		},
		_attachLoadEvent: function(iframe, callback){
				qq.attach(iframe, 'load', function(){
						// when we remove iframe from dom
						// the request stops, but in IE load
						// event fires
						if (!iframe.parentNode){
								return;
						}

						// fixing Opera 10.53
						if (iframe.contentDocument &&
								iframe.contentDocument.body &&
								iframe.contentDocument.body.innerHTML == "false"){
								// In Opera event is fired second time
								// when body.innerHTML changed from false
								// to server response approx. after 1 sec
								// when we upload file with iframe
								return;
						}

						callback();
				});
		},
		/**
		 * Returns json object received by iframe from server.
		 */
		_getIframeContentJSON: function(iframe){
				// iframe.contentWindow.document - for IE<7
				var doc = iframe.contentDocument ? iframe.contentDocument: iframe.contentWindow.document,
						response;

				this.log("converting iframe's innerText to JSON");
				this.log("innerText = " + (doc.body.innerText || doc.body.textContent));

				try {
						response = eval("(" + (doc.body.innerText || doc.body.textContent) + ")");
				} catch(err){
						response = {};
				}

				return response;
		},
		/**
		 * Creates iframe with unique name
		 */
		_createIframe: function(id){
				// We can't use following code as the name attribute
				// won't be properly registered in IE6, and new window
				// on form submit will open
				// var iframe = document.createElement('iframe');
				// iframe.setAttribute('name', id);

				var iframe = qq.toElement('<iframe src="javascript:false;" name="' + id + '" />');
				// src="javascript:false;" removes ie6 prompt on https

				iframe.setAttribute('id', id);

				iframe.style.display = 'none';
				document.body.appendChild(iframe);

				return iframe;
		},
		/**
		 * Creates form, that will be submitted to iframe
		 */
		_createForm: function(iframe, params){
				// We can't use the following code in IE6
				// var form = document.createElement('form');
				// form.setAttribute('method', 'post');
				// form.setAttribute('enctype', 'multipart/form-data');
				// Because in this case file won't be attached to request
				var form = qq.toElement('<form method="post" enctype="multipart/form-data"></form>');

				var queryString = qq.obj2url(params, this._options.action);

				form.setAttribute('action', queryString);
				form.setAttribute('target', iframe.name);
				form.style.display = 'none';
				document.body.appendChild(form);

				return form;
		}
});

/**
 * Class for uploading files using xhr
 * @inherits qq.UploadHandlerAbstract
 */
qq.UploadHandlerXhr = function(o){
		qq.UploadHandlerAbstract.apply(this, arguments);

		this._files = [];
		this._xhrs = [];

		// current loaded size in bytes for each file
		this._loaded = [];
};

// static method
qq.UploadHandlerXhr.isSupported = function(){
		var input = document.createElement('input');
		input.type = 'file';

		return (
				'multiple' in input &&
				typeof File != "undefined" &&
				typeof (new XMLHttpRequest()).upload != "undefined" );
};

// @inherits qq.UploadHandlerAbstract
qq.extend(qq.UploadHandlerXhr.prototype, qq.UploadHandlerAbstract.prototype);

qq.extend(qq.UploadHandlerXhr.prototype, {
		/**
		 * Adds file to the queue
		 * Returns id to use with upload, cancel
		 **/
		add: function(file){
				if (!(file instanceof File)){
						throw new Error('Passed obj in not a File (in qq.UploadHandlerXhr)');
				}

				return this._files.push(file) - 1;
		},
		getName: function(id){
				var file = this._files[id];
				// fix missing name in Safari 4
				return file.fileName != null ? file.fileName : file.name;
		},
		getSize: function(id){
				var file = this._files[id];
				return file.fileSize != null ? file.fileSize : file.size;
		},
		/**
		 * Returns uploaded bytes for file identified by id
		 */
		getLoaded: function(id){
				return this._loaded[id] || 0;
		},
		/**
		 * Sends the file identified by id and additional query params to the server
		 * @param {Object} params name-value string pairs
		 */
		_upload: function(id, params){
				var file = this._files[id],
						name = this.getName(id),
						size = this.getSize(id);

				this._loaded[id] = 0;

				var xhr = this._xhrs[id] = new XMLHttpRequest();
				var self = this;

				xhr.upload.onprogress = function(e){
						if (e.lengthComputable){
								self._loaded[id] = e.loaded;
								self._options.onProgress(id, name, e.loaded, e.total);
						}
				};

				xhr.onreadystatechange = function(){
						if (xhr.readyState == 4){
								self._onComplete(id, xhr);
						}
				};

				// build query string
				params = params || {};
				params['qqfile'] = name;
				var queryString = qq.obj2url(params, this._options.action);

				xhr.open("POST", queryString, true);
				xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
				xhr.setRequestHeader("X-File-Name", encodeURIComponent(name));
				xhr.setRequestHeader("Content-Type", "application/octet-stream");
				xhr.send(file);
		},
		_onComplete: function(id, xhr){
				// the request was aborted/cancelled
				if (!this._files[id]) return;

				var name = this.getName(id);
				var size = this.getSize(id);

				this._options.onProgress(id, name, size, size);

				if (xhr.status == 200){
						this.log("xhr - server response received");
						this.log("responseText = " + xhr.responseText);

						var response;

						try {
								response = eval("(" + xhr.responseText + ")");
						} catch(err){
								response = {};
						}

						this._options.onComplete(id, name, response);

				} else {
						this._options.onComplete(id, name, { status: xhr.status });
				}

				this._files[id] = null;
				this._xhrs[id] = null;
				this._dequeue(id);
		},
		_cancel: function(id){
				this._options.onCancel(id, this.getName(id));

				this._files[id] = null;

				if (this._xhrs[id]){
						this._xhrs[id].abort();
						this._xhrs[id] = null;
				}
		}
});
/*jslint browser: true*/
/*jslint jquery: true*/

/*
 * jQuery Hotkeys Plugin
 * Copyright 2010, John Resig
 * Dual licensed under the MIT or GPL Version 2 licenses.
 *
 * Based upon the plugin by Tzury Bar Yochay:
 * http://github.com/tzuryby/hotkeys
 *
 * Original idea by:
 * Binny V A, http://www.openjs.com/scripts/events/keyboard_shortcuts/
 */

/*
 * One small change is: now keys are passed by object { keys: '...' }
 * Might be useful, when you want to pass some other data to your handler
 */

(function(jQuery) {

  jQuery.hotkeys = {
    version: "0.8",

    specialKeys: {
      8: "backspace",
      9: "tab",
      10: "return",
      13: "return",
      16: "shift",
      17: "ctrl",
      18: "alt",
      19: "pause",
      20: "capslock",
      27: "esc",
      32: "space",
      33: "pageup",
      34: "pagedown",
      35: "end",
      36: "home",
      37: "left",
      38: "up",
      39: "right",
      40: "down",
      45: "insert",
      46: "del",
      59: ";",
      61: "=",
      96: "0",
      97: "1",
      98: "2",
      99: "3",
      100: "4",
      101: "5",
      102: "6",
      103: "7",
      104: "8",
      105: "9",
      106: "*",
      107: "+",
      109: "-",
      110: ".",
      111: "/",
      112: "f1",
      113: "f2",
      114: "f3",
      115: "f4",
      116: "f5",
      117: "f6",
      118: "f7",
      119: "f8",
      120: "f9",
      121: "f10",
      122: "f11",
      123: "f12",
      144: "numlock",
      145: "scroll",
      173: "-",
      186: ";",
      187: "=",
      188: ",",
      189: "-",
      190: ".",
      191: "/",
      192: "`",
      219: "[",
      220: "\\",
      221: "]",
      222: "'"
    },

    shiftNums: {
      "`": "~",
      "1": "!",
      "2": "@",
      "3": "#",
      "4": "$",
      "5": "%",
      "6": "^",
      "7": "&",
      "8": "*",
      "9": "(",
      "0": ")",
      "-": "_",
      "=": "+",
      ";": ": ",
      "'": "\"",
      ",": "<",
      ".": ">",
      "/": "?",
      "\\": "|"
    },

    // excludes: button, checkbox, file, hidden, image, password, radio, reset, search, submit, url
    textAcceptingInputTypes: [
      "text", "password", "number", "email", "url", "range", "date", "month", "week", "time", "datetime",
      "datetime-local", "search", "color", "tel"],

    options: {
      filterTextInputs: true
    }
  };

  function keyHandler(handleObj) {
    if (typeof handleObj.data === "string") {
      handleObj.data = {
        keys: handleObj.data
      };
    }

    // Only care when a possible input has been specified
    if (!handleObj.data || !handleObj.data.keys || typeof handleObj.data.keys !== "string") {
      return;
    }

    var origHandler = handleObj.handler,
      keys = handleObj.data.keys.toLowerCase().split(" ");

    handleObj.handler = function(event) {
      //      Don't fire in text-accepting inputs that we didn't directly bind to
      if (this !== event.target && (/textarea|select/i.test(event.target.nodeName) ||
        (jQuery.hotkeys.options.filterTextInputs &&
          jQuery.inArray(event.target.type, jQuery.hotkeys.textAcceptingInputTypes) > -1))) {
        return;
      }

      var special = event.type !== "keypress" && jQuery.hotkeys.specialKeys[event.which],
        character = String.fromCharCode(event.which).toLowerCase(),
        modif = "",
        possible = {};

      jQuery.each(["alt", "ctrl", "shift"], function(index, specialKey) {

        if (event[specialKey + 'Key'] && special !== specialKey) {
          modif += specialKey + '+';
        }
      });

      // metaKey is triggered off ctrlKey erronously
      if (event.metaKey && !event.ctrlKey && special !== "meta") {
        modif += "meta+";
      }

      if (event.metaKey && special !== "meta" && modif.indexOf("alt+ctrl+shift+") > -1) {
        modif = modif.replace("alt+ctrl+shift+", "hyper+");
      }

      if (special) {
        possible[modif + special] = true;
      }
      else {
        possible[modif + character] = true;
        possible[modif + jQuery.hotkeys.shiftNums[character]] = true;

        // "$" can be triggered as "Shift+4" or "Shift+$" or just "$"
        if (modif === "shift+") {
          possible[jQuery.hotkeys.shiftNums[character]] = true;
        }
      }

      for (var i = 0, l = keys.length; i < l; i++) {
        if (possible[keys[i]]) {
          return origHandler.apply(this, arguments);
        }
      }
    };
  }

  jQuery.each(["keydown", "keyup", "keypress"], function() {
    jQuery.event.special[this] = {
      add: keyHandler
    };
  });

})(jQuery || this.jQuery || window.jQuery);
RegExp.escape = function(text) {
    return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
};
// should always be defined in <head>, but default to en_US here
var defaultLocaleData = {
    number: {
        decimal: '.',
        group: ',',
    },
};

/**
 * Convert a localized string into a floating-point number, using current localization data
 * to get the proper decimal separator.
 */
String.prototype.parseLocalFloat = function() {
    var l = localeData || defaultLocaleData,
        trimRegex = new RegExp('[^\\d' + RegExp.escape(l.number.decimal) + '\\-]', 'g');

    var output = this.replace(trimRegex, '');
    if (l.number.decimal !== '.') output = output.replace(l.number.decimal, '.');

    return parseFloat(output);
};

/**
 * Convert a localized string into an integer
 */
String.prototype.parseLocalInt = function() {
    var l = localeData || defaultLocaleData,
        output = this.split(l.number.decimal)[0].replace(/[^\d\-]/g, '');
    return parseInt(output, 10);
};

String.prototype.toLocalString = function() {
    return parseFloat(this).toLocalString();
};

/**
 * Convert a number into a localized string, with proper decimal separator and grouping
 * Accepts an optional decimal precision parameter
 * Some inspiration from YUI.DataType.Number.Format()
 */
Number.prototype.toLocalString = function(precision) {
    var l = localeData || defaultLocaleData,
        isNegative = this < 0,
        decIndex,
        tempOutput,
        count,
        i,
        output = this.toString();

    if (typeof precision !== 'undefined') output = this.toFixed(precision).toString();

    // decimal separator
    if (l.number.decimal !== '.') output = output.replace('.', l.number.decimal);

    // find the dot or where it would be
    decIndex = output.lastIndexOf(l.number.decimal);
    decIndex = decIndex > -1 ? decIndex : output.length;

    // start with the dot and everything to the right
    tempOutput = output.substring(decIndex);

    // working to the left, every third time add a separator, every time add a digit
    for (count = 0, i = decIndex; i > 0; i--) {
        if (count % 3 === 0 && i !== decIndex && (!isNegative || i > 1)) {
            tempOutput = l.number.group + tempOutput;
        }
        tempOutput = output.charAt(i - 1) + tempOutput;
        count++;
    }
    output = tempOutput;

    return output;
};

Number.prototype.truncate = function() {
    return this < 0 ? Math.ceil(this) : Math.floor(this);
};
// Class.extend(), @author jeresig, http://ejohn.org/blog/simple-javascript-inheritance/
// Inspired by base2 and Prototype
(function() {
    var initializing = false,
        fnTest = /xyz/.test(function() {
            xyz;
        })
            ? /\b_super\b/
            : /.*/;
    // The base Class implementation (does nothing)
    this.Class = function() {};

    // Create a new Class that inherits from this class
    Class.extend = function(prop) {
        var _super = this.prototype;

        // Instantiate a base class (but only create the instance,
        // don't run the init constructor)
        initializing = true;
        var prototype = new this();
        initializing = false;

        // Copy the properties over onto the new prototype
        for (var name in prop) {
            // Check if we're overwriting an existing function
            prototype[name] =
                typeof prop[name] == 'function' &&
                typeof _super[name] == 'function' &&
                fnTest.test(prop[name])
                    ? (function(name, fn) {
                          return function() {
                              var tmp = this._super;

                              // Add a new ._super() method that is the same method
                              // but on the super-class
                              this._super = _super[name];

                              // The method only need to be bound temporarily, so we
                              // remove it when we're done executing
                              var ret = fn.apply(this, arguments);
                              this._super = tmp;

                              return ret;
                          };
                      })(name, prop[name])
                    : prop[name];
        }

        // The dummy class constructor
        function Class() {
            // All construction is actually done in the init method
            if (!initializing && this.init) this.init.apply(this, arguments);
        }

        // Populate our constructed prototype object
        Class.prototype = prototype;

        // Enforce the constructor to be what we expect
        Class.constructor = Class;

        // And make this class extendable
        Class.extend = arguments.callee;

        return Class;
    };
})();
/*
 * EditInPlace.js
 *
 * @author Andy Fowler
 */
var EditInPlace = (function($) {
    return Class.extend({
        init: function(container, options) {
            this.container = $(container);
            this.options = $.extend(true, {}, this.defaults, options);
            this.distillOptions();
            this.hasFocus = 0; // used as an internal focus counter
            this.isSaving = false; // used to prevent double-clicks from saving multiple times

            this.valueElements = this.container.find(this.options.valueElementSelector);
            if (!this.valueElements.length) {
                this.valueElements = this.container.find('.value:first');
            }

            if (!this.valueElements.length)
                Nut.error('No value elements found for edit-in-place', this.container);

            this.inputElements = $(''); // collection built in createInputs
            this.buttons = $('');

            this.bindEditElements();
            this.bindSubmitAndCancel();
            this.bindDelete();
            this.bindExistingInputs();
            this.container.addClass(this.classes.ready);
            this.container.toggleClass(this.classes.empty, this.isValueEmpty());

            this.container.data('eip', this);
        },

        keys: {
            TAB: 9,
            RETURN: 13,
            ESC: 27,
        },

        classes: {
            ready: 'eip-ready',
            empty: 'state-empty',
            editing: 'state-editing',
            dirty: 'state-dirty',
            saving: 'state-saving',
            transient: 'state-transient', // for edit-in-place fields that don't yet exist remotely (delete w/o callback)
            invalid: 'state-invalid',
            warning: 'state-warning',
            input: 'eip-input',
            hover: 'state-hover',
        },

        // custom events triggered on the container
        events: {
            didSave: 'EipDidSave',
            didFailSaving: 'EipDidFailSaving',
            didClose: 'EipDidClose',
            didStartEditing: 'EipDidStartEditing',
            didDelete: 'EipDidDelete',
        },

        defaults: {
            version: 1,
            addButtons: true,
            buttonStyle: 'interaction', // or 'action' or 'icons'
            valueElementSelector: '.value span:not(.empty), .value div:not(.empty)',

            editOnContainerClick: false,
            editOnValueClick: true,
            editElementSelector: '.action-edit',

            submitElementSelector: '.action-submit',
            cancelElementSelector: '.eip-button.action-cancel', // any matching elements in the container will cancel the edit-in-place
            deleteElementSelector: '.action-delete',

            inputType: 'input', // input, textarea
            inputIsElastic: false, // use elastic plugin to auto-grow the field (only valid for textareas)
            hideValuesOnEdit: true,

            selectOnEdit: true, // select the contents of the input when it is focused
            saveOnBlur: true,
            saveOnBlurDelay: 500, // enough time to allow visual mousedowns on save buttons
            saveOnReturn: true,

            url: null,
            deleteUrl: null,
            saveKey: 'value', // root key for callback data
            saveCallback: null, // provide an AJAX saving function here, instead of the automatic AJAX request.

            filterSavedValueCallback: null, // a function which filters the returned saved value before it is reinserted into the DOM

            model: null, // for backbone-backed EIPs, passed in by BB view
        },

        version2Defaults: {
            buttonStyle: 'icons',
            hideValuesOnEdit: false,
        },

        // called after initial options are set, uses fields, classes to infer other options
        // the preferred way is to set a data- attribute like `data-button-style="icons"` on the container
        distillOptions: function() {
            if (this.container.find('input[name=action]').length)
                this.options.url = this.container.find('input[name=action]').val();

            if (this.container.find('.editor').length) this.options.version = 2;

            if (this.options.version == 2) {
                this.options = $.extend(true, {}, this.options, this.version2Defaults);
            }

            _(this.container.data()).each(function(value, attributeName) {
                if (attributeName.indexOf('eip') !== 0) return;

                attributeName = attributeName.substr('eip'.length);
                attributeName = attributeName.charAt(0).toLowerCase() + attributeName.substr(1);
                this.options[attributeName] = value;
            }, this);

            // for compatibility with new BB style attribute
            if (this.container.data('modelattribute'))
                this.options.saveKey = this.container.data('modelattribute');
        },

        startEditing: function(event) {
            var target = typeof event !== 'undefined' ? $(event.target) : $();

            // maybe handled by some other EIP thing (makePrimary)
            if (event.isDefaultPrevented()) return true;

            // most browsers don't trigger a click on select-minis, but IE9 does. ignore it.
            if (target.closest('.label select').length) return true;

            // do nothing if they clicked the delete button
            if (target.hasClass('action-delete') || target.hasClass('fa-times')) {
                event.preventDefault();
                return true;
            }

            // do nothing if they clicked an actual link
            var targetHref = target.is('a') ? target.attr('href') : null;
            if (targetHref && targetHref.length > 1) return true;

            // if this is a phone number link stop
            var isPhoneNumberLink =
                _.any(target.closest('.action-call-number')) ||
                _.any(target.find('.action-call-number'));
            if (isPhoneNumberLink) return true;

            // do nothing if they have a selection in here and they're not explicitly clicking the edit button
            if (target.containsSelection() && !target.is(this.options.editElementSelector))
                return true;

            event.stopPropagation();

            if (this.container.hasClass(this.classes.editing)) return false;

            this.container.addClass(this.classes.editing);
            this.container.removeClass(this.classes.saving); // prevent race condition

            this.prepareInputs();

            if (this.options.hideValuesOnEdit) this.valueElements.hide();
            this.inputElements.show();

            var inputToSelect = this.container.is('.type-currency')
                ? // currency custom field elements have 2 input elements, and
                  // want to focus the last one, which is the amount field.
                  this.inputElements.last()
                : this.inputElements.first();

            if (this.options.inputIsElastic) {
                inputToSelect.trigger('keyup'); // easiest way to force elastic recalc
            }

            if (this.options.addButtons) this.showButtons();

            inputToSelect.trigger(this.events.didStartEditing, this);

            inputToSelect.focus().trigger('focusin');

            if (this.options.selectOnEdit) inputToSelect.select();

            if (
                $(event.target).closest('.tile-account-top.tile-link, .tile-contact-top.tile-link')
                    .length
            ) {
                event.preventDefault();
            }

            return true;
        },

        // bind the appropriate elements to this.startEditing(), and add hovers to the container
        bindEditElements: function() {
            var editTriggers = this.container.find(this.options.editElementSelector);
            if (this.options.editOnValueClick) editTriggers = editTriggers.add(this.valueElements);
            if (this.options.editOnContainerClick) editTriggers = editTriggers.add(this.container);

            editTriggers.on('click', $.proxy(this, 'startEditing'));

            var container = this.container,
                hoverClass = this.classes.hover;
            editTriggers.hover(
                function() {
                    container.addClass(hoverClass);
                },
                function() {
                    container.removeClass(hoverClass);
                }
            );
        },

        bindDelete: function() {
            this.container
                .find(this.options.deleteElementSelector)
                .filter(':not(.eip-ignore)')
                .on('click', $.proxy(this.deleteElement, this));
        },

        bindSubmitAndCancel: function() {
            this.container
                .find(this.options.submitElementSelector)
                .on('click', $.proxy(this.saveAndClose, this));

            // Track whether we are over cancel
            this.container
                .find(this.options.cancelElementSelector)
                .on('mouseenter', $.proxy(this.overCancel, this));
            this.container
                .find(this.options.cancelElementSelector)
                .on('mouseleave', $.proxy(this.exitCancel, this));
            this.container
                .find(this.options.cancelElementSelector)
                .on('click', $.proxy(this.cancel, this));
            // @todo [esc] capture

            this.container.find('.action-primary').on('click', $.proxy(this.makePrimary, this));
            this.container.find('.action-warning').on('click', $.proxy(this.showWarning, this));

            if (!this.container.is('.type-currency')) {
                this.container
                    .find('.mode-edit select, .label select')
                    .on('change', $.proxy(this.selectChanged, this));
            }

            this.container
                .find('.label .mode-read span')
                .on('click', $.proxy(this, 'startEditing'));
        },

        // bind all existing focuses (not just our own inputs, but *any* input in the EIP container, to track focus)
        bindExistingInputs: function() {
            this.container
                .find('input[type!=hidden], select')
                .on('focus', $.proxy(this, 'inputWillFocus'))
                .on('blur', $.proxy(this, 'inputWillBlur'));

            this.container
                .find('select[name="currencyShortname"]')
                .on('change', $.proxy(this, 'saveCurrencyType'));
        },

        createInputs: function(options) {
            this.valueElements.each(
                $.proxy(function(index, valueElement) {
                    this.createInput(valueElement, options);
                }, this)
            );
        },

        // adds the appropriate <input> or <textarea> to the dom, saves as object attribute
        createInput: function(valueElement) {
            var newInputType =
                    $(valueElement).is('.eip-textarea') || this.options.inputType == 'textarea'
                        ? 'textarea'
                        : 'input',
                rawNewInput = document.createElement(newInputType),
                className = valueElement.className.length
                    ? valueElement.className
                    : valueElement.parentNode.className;

            valueElement = $(valueElement);

            if (newInputType == 'input') rawNewInput.type = 'text'; // this can't be set via jquery after creation, due to some IE compat

            var name = valueElement.data('name') || className;

            var newInput = $(rawNewInput)
                .addClass(this.classes.input)
                .addClass(className)
                .addClass('type-' + (newInputType == 'textarea' ? 'textarea' : 'text'))
                .attr('name', name);

            if (valueElement.data('disableSpellcheck')) {
                newInput.attr('spellcheck', false);
            }

            if (this.options.version == 2) {
                newInput.appendTo(this.container.find('.editor'));
            } else {
                newInput.insertAfter(valueElement).hide();
            }

            newInput
                .on('focus', $.proxy(this, 'inputWillFocus'))
                .on('blur', $.proxy(this, 'inputWillBlur'))
                .on('change', $.proxy(this, 'inputDidChange'))
                .on('keydown', $.proxy(this, 'inputWillKeydown'));

            if (valueElement.data('placeholder'))
                newInput.attr('placeholder', valueElement.data('placeholder'));
            if (this.options.inputIsElastic) newInput.elastic({breathingRoom: false});

            // tie input and value together. jquery will keep the circular reference sane (hopefully)
            newInput.data('eip-twin', valueElement);
            valueElement.data('eip-twin', newInput);

            // register new input with object
            this.inputElements = this.inputElements.add(newInput);
            return newInput;
        },

        // called every time the inputs are displayed
        prepareInputs: function(options) {
            var options = _.extend({createInputs: true}, options || {});

            if (!this.inputElements.length) {
                this.createInputs(options);
            }

            this.valueElements.each(
                $.proxy(function(index, valueElement) {
                    this.prepareInput(valueElement);
                }, this)
            );
        },

        prepareInput: function(valueElement) {
            var $valueElement = $(valueElement);
            if (this.options.model && this.options.model.getEditInPlaceText) {
                var fullUrl = this.options.model.getEditInPlaceText();
            }
            var inputElement = $valueElement.data('eip-twin'),
                defaultValue = $valueElement.data('default'),
                value = fullUrl ? fullUrl : $.trim($valueElement.text());
            if (!value && defaultValue) value = defaultValue;

            inputElement.val(value).data('eip-original-value', value);
        },

        inputWillFocus: function(event) {
            // no more than one element should have focus at a time, so only increment if focus isn't already set
            if (this.hasFocus < 1) this.hasFocus++;

            // refocusing on an element should remove the error state (otherwise you can get stuck never saving on blur)
            this.container.removeClass(this.classes.invalid);
        },

        inputWillBlur: function(event) {
            // don't decrement below 0
            if (this.hasFocus > 0) this.hasFocus--;

            // If the mouse cursor is over cancel we just bail
            if (this.overCancel) return true;

            // delay a bit to make save clicks feel authentic :active. cancel clicks happen on mousedown, so this
            // can be dialed back to ~20 without problem
            window.setTimeout(
                $.proxy(function() {
                    if (!this.hasFocus) this.inputDidBlur();
                }, this),
                this.options.saveOnBlurDelay
            );
        },

        inputDidBlur: function() {
            // check to make sure we didn't cancel in the meantime
            if (
                this.options.saveOnBlur &&
                this.isOpen() &&
                // if container is in invalid state, then there was probably a Popmini error message.
                // We don't want to save if user focuses on the Popmini (e.g. by clicking the "Dismiss" button)
                !this.container.hasClass(this.classes.invalid)
            ) {
                this.saveAndClose();
            }
        },

        // right now we only care if it changed (from other scripting) while not in edit mode
        // perhaps this should also trigger the ajax action. only used in activity modal time tether right now
        inputDidChange: function() {
            if (!this.isOpen()) {
                var eip = this;
                this.inputElements.each(function() {
                    eip.updateSavedValue(
                        $(this).attr('name'),
                        eip.getValueFromInputElement($(this))
                    );
                });
            }
        },

        inputWillKeydown: function(event) {
            switch (event.keyCode) {
                case this.keys.ESC:
                    this.cancel();
                    return false; // capture this escape from bubbling to any other listeners
                case this.keys.RETURN:
                    if (this.options.saveOnReturn && !$(event.target).is('textarea')) {
                        event.preventDefault();
                        this.saveAndClose();
                        return true;
                    }
                    break;
                default:
                    break;
            }

            window.setTimeout($.proxy(this.inputDidKeydown, this), 20);
            return true;
        },

        inputDidKeydown: function() {
            if (this.isOpen()) {
                // this happens on a different thread, so don't do anything if we're no longer working
                this.container.toggleClass(this.classes.dirty, this.isDirty());
            }
        },

        hideButtons: function() {
            this.buttons.hide();
        },

        showButtons: function() {
            if (!this.buttons.length) this.createButtons();

            this.buttons.show();
        },

        createButtons: function() {
            var saveText = this.options.url ? 'Save' : 'OK';

            if (this.options.buttonStyle == 'icons') {
                this.buttons = $(
                    '<div class="eip-buttons"> ' +
                        '<div class="button eip-button action-save">&#xf00c;</div> ' +
                        '<div class="button eip-button action-cancel">&#xf00d;</div> ' +
                        '</div>'
                );
            } else {
                this.buttons = $(
                    '<div class="eip-buttons"><a href="#" class="button variant-grey size-small button-' +
                        this.options.buttonStyle +
                        ' eip-button-cancel action-cancel"><span>Cancel</span></a><a href="#" class="button variant-green size-small button-' +
                        this.options.buttonStyle +
                        ' action-submit eip-button-save"><span>' +
                        saveText +
                        '</span></a></div>'
                );
            }

            this.buttons.appendTo(this.container);
            this.buttons
                .find(this.options.cancelElementSelector)
                .on('mouseenter', $.proxy(this, 'enterCancel'));
            this.buttons
                .find(this.options.cancelElementSelector)
                .on('mouseleave', $.proxy(this, 'exitCancel'));
            this.buttons
                .find(this.options.cancelElementSelector)
                .on('click', $.proxy(this, 'cancel'));
            this.buttons.find('.action-submit').on('click', $.proxy(this, 'saveAndClose'));
        },

        didSaveSuccessfully: function(response) {
            if (response && response.isWarning && response.message) {
                // check for warnings
                this.container.toggleClass(this.classes.warning, true);
                this.warningMessage = response.message;

                // attach to the container here, but showWarning() will attach to the warning icon
                Popmini.error(this.warningMessage, {attachTo: this.container});
            }
            this.isSaving = false;
            this.handleSaveResponse(response);

            // we clean up in close(), too, but we want these gone before didSave() event
            // because listeners may be confused that we still have the editing class
            this.container.removeClass(this.classes.dirty).removeClass(this.classes.editing);

            this.container.trigger(this.events.didSave, [response]);

            this.container.removeClass(this.classes['transient']).removeClass(this.classes.invalid);
            this.close();
            this.blinkSuccess();
        },

        handleSaveResponse: function(response) {
            if (response && response.alreadyExists) {
                this.clear();
                this.didDeleteSuccessfully();
                return;
            } else if (response && typeof response[this.options.saveKey] === 'string') {
                // escape response
                response.value = $('<div/>')
                    .text(response.value)
                    .html();
                this.updateSavedValue('value', response.value);
            } else if (response && $.isPlainObject(response[this.options.saveKey])) {
                $.each(response[this.options.saveKey], $.proxy(this, 'updateSavedValue'));
            } else {
                // we didn't get a confirmation on the new value, but save was still successful?
                var eip = this;
                this.inputElements.each(function() {
                    eip.updateSavedValue(
                        $(this).attr('name'),
                        eip.getValueFromInputElement($(this))
                    );
                });
            }

            // if this was a new entry, update any hidden fields that happen to coincide w/ the response
            this.container.find('input[type=hidden]').each(function() {
                if (response && typeof response[$(this).attr('name')] === 'string')
                    $(this).val(response[$(this).attr('name')]);
            });
        },

        didFailSaving: function(response) {
            this.isSaving = false;
            this.container.addClass(this.classes.invalid);
            this.blinkError();
            if (response && response.isValidationResult && !response.isValid) {
                Form.handleValidationResult(response.messages, this.container);
            } else if (_.isString(response)) {
                Popmini.error(response, {attachTo: this.container});
            }

            this.container.trigger(this.events.didFailSaving);
        },

        updateSavedValue: function(key, value, isHtml) {
            var escapedKey = key.replace(/([\[\]])/g, '\\$1'),
                inputElement = this.inputElements.filter(':input[name=' + escapedKey + ']');

            if ($.isFunction(this.options.filterSavedValueCallback))
                value = this.options.filterSavedValueCallback.apply(this, [key, value]);

            if (inputElement.length && inputElement.data('eip-twin')) {
                if (isHtml) {
                    inputElement
                        .val(value)
                        .data('eip-twin')
                        .html(value);
                } else if (this.container.hasClass('type-url')) {
                    // wrap the custom field EIP-url value in an anchor
                    // this allows a custom field url value to be a clickable link w/out refreshing page
                    var anchorNode = $('<a/>');
                    anchorNode
                        .attr('href', value)
                        .attr('target', '_blank')
                        .text(value);

                    inputElement
                        .val(value)
                        .data('eip-twin')
                        .empty()
                        .append(anchorNode);
                } else {
                    inputElement
                        .val(value)
                        .data('eip-twin')
                        .text(value);
                }
            }
        },

        // use to set values externally, fires didsave event, not sure if this is the right
        // long-term solution, but just using for activity modal names right now
        publicSetValue: function(key, value) {
            var escapedKey = key.replace(/([\[\]])/g, '\\$1'),
                inputElement = this.inputElements.filter(':input[name=' + escapedKey + ']');

            inputElement.val(value);

            this.updateSavedValue(key, value);
            this.container.trigger(this.events.didSave, []);
        },

        isDirty: function() {
            var isDirty = false;
            this.inputElements.each(
                $.proxy(function(index, inputElement) {
                    if (
                        this.getValueFromInputElement(inputElement) !=
                        $(inputElement).data('eip-original-value')
                    )
                        isDirty = true;
                }, this)
            );

            return isDirty;
        },

        isValueEmpty: function() {
            var isEmpty = true;
            this.valueElements.each(function() {
                if ($.trim($(this).text()).length) isEmpty = false;
            });

            return isEmpty;
        },

        isInputEmpty: function() {
            var isEmpty = true;

            this.inputElements.each(function() {
                var eipTwin = $(this).data('eip-twin');

                if (!eipTwin) {
                    isEmpty = false;
                    return true;
                }

                if (
                    $.trim($(this).val()).length &&
                    $.trim($(this).val()) != eipTwin.data('default')
                )
                    isEmpty = false;
            });

            return isEmpty;
        },

        isOpen: function() {
            return this.container.hasClass(this.classes.editing);
        },

        // merges all hidden inputs with all EIP inputs
        // if only one EIP input, it will be set as the value key, otherwise the value key will contain a dict of all values
        getDataToSave: function(options) {
            var options = _.extend(
                {
                    delete: false,
                    isReactDatePicker: false,
                },
                options || {}
            );

            var isDeletingReactDatePickerValue = options.delete && options.isReactDatePicker;

            if (!this.inputElements.length) {
                this.prepareInputs({createInputs: !isDeletingReactDatePickerValue});
            }

            var rawHiddenInputs = this.container
                .find('input[type=hidden]:not(input[name=action])')
                .serializeArray();
            var rawInputs = this.inputElements.serializeArray();
            var data = {};

            if (this.container.hasClass(this.classes['transient'])) data.isTransient = true;

            for (var i = rawHiddenInputs.length - 1; i >= 0; i--) {
                data[rawHiddenInputs[i].name] = rawHiddenInputs[i].value;
            }

            if (rawInputs.length == 1) {
                data[this.options.saveKey] = rawInputs.pop().value;
            } else {
                data[this.options.saveKey] = {};
                for (i = rawInputs.length - 1; i >= 0; i--) {
                    // Ignore Google Voice extension elements
                    if (rawInputs[i].name == 'gc-cs-link') {
                        continue;
                    }
                    data[this.options.saveKey][rawInputs[i].name] = rawInputs[i].value;
                }
            }

            return data;
        },

        // this is abstracted mainly for subclasses (i.e. date picker returns a Date() object instead)
        // not currently used by getDataToSave()
        getValueFromInputElement: function(inputElement) {
            return $(inputElement).val();
        },

        saveCurrencyType: function(event) {
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }
            // use built-in save method
            if (this.options.url) {
                this.isSaving = true;
                Nut.ajax.post({
                    url: this.options.url,
                    data: this.getDataToSave(),
                    success: $.proxy(this.didSaveSuccessfully, this),
                    error: $.proxy(this.didFailSaving, this),
                });
            } else {
                // we have no save callback, but this is probably intended.
                this.didSaveSuccessfully();
            }

            this.close();
        },

        saveAndClose: function(event) {
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }

            if (this.isInputEmpty() && !this.isDirty()) {
                if (this.container.hasClass(this.classes['transient'])) {
                    this.removeContainer();
                } else {
                    this.close();
                }
            } else if (this.isDirty() && !this.isSaving) {
                // only save if dirty and not already in the process of saving
                if ($.isFunction(this.options.saveCallback)) {
                    this.options.saveCallback.call(this);
                } else if (this.options.model) {
                    this.saveToModel();
                } else {
                    // use built-in save method
                    if (this.options.url) {
                        this.isSaving = true;
                        Nut.ajax.post({
                            url: this.options.url,
                            data: this.getDataToSave(),
                            success: $.proxy(this.didSaveSuccessfully, this),
                            error: $.proxy(this.didFailSaving, this),
                        });
                    } else {
                        // we have no save callback, but this is probably intended.
                        this.didSaveSuccessfully();
                    }
                }
            } else {
                // still close
                this.close();
            }
        },

        saveToModel: function() {
            var data = this.getDataToSave(),
                eip = this;

            try {
                var savePromise = this.options.model.save(data, {patch: true, wait: true});
            } catch (e) {
                this.didFailSaving(e.message);
                return;
            }

            savePromise.done($.proxy(this.didSaveSuccessfully, this)).fail(function(jqxhr) {
                var errorResponse = null;
                try {
                    errorResponse = $.parseJSON(jqxhr.responseText);
                } finally {
                    eip.didFailSaving(errorResponse);
                }
            });
        },

        makePrimary: function(event) {
            var eip = this;

            event.preventDefault();

            if (this.options.model) {
                this.options.model.save({isPrimary: true}, {patch: true, wait: true});
            } else {
                Nut.ajax.post({
                    url:
                        this.container.find('input[name=baseUrl]').val() +
                        '/make-multifield-primary.json',
                    data: {
                        fieldName: this.container.find('input[name=fieldName]').val(),
                        fieldKey: this.container.find('input[name=fieldKey]').val(),
                    },
                    success: function() {
                        eip.container
                            .addClass('type-primary')
                            .siblings()
                            .removeClass('type-primary');
                        eip.container.trigger(eip.events.didSave, []);
                    },
                });
            }
        },

        showWarning: function() {
            warningMessage = this.warningMessage || this.container.find('span.warning').text();
            if (warningMessage) {
                Popmini.error(warningMessage, {attachTo: this.container.find('.action-warning')});
            }
        },

        selectChanged: function(event) {
            var container = this.container,
                data = {},
                select = this.container.find('select');
            data.fieldName = container.find('input[name=fieldName]').val();
            data.originalKey = container.find('input[name=fieldKey]').val();
            data.newKey = $(event.target).val();

            if (data.newKey != data.originalKey) {
                if (data.newKey == 'custom') {
                    data.newKey = prompt('', 'custom...');

                    // The user might have cancelled the prompt
                    if (!data.newKey) {
                        // Can't figure out how to reset to original value.
                        // Just stop doing anything further.
                        return false;
                    }

                    data.newKey = data.newKey.toLowerCase();

                    // deselect custom label
                    select.find('option').attr('selected', false);

                    // insert new concrete custom label and select it
                    var newOption = $(document.createElement('option'))
                        .attr('value', data.newKey)
                        .text(data.newKey)
                        .insertBefore($(event.target).find('option[value=custom]'));

                    newOption.attr('selected', true);
                }

                $.uniform.update(select);

                var afterUpdate = function(response) {
                    if (response) {
                        // we may have a different key than we indicated, update.
                        container.find('input[name=fieldKey]').val(response.fieldKey);
                        container.find('select option:selected').attr('value', response.fieldKey);
                    } else {
                        // not issuing ajax yet, so keep using transient key
                        container.find('input[name=fieldKey]').val(data.newKey);
                    }

                    container.find('.mode-read span').text(data.newKey);
                    container.find('select').trigger('blur'); // forcibly blur, since EIP needs to track focus

                    // if a brand new field, jump to first field
                    if (container.is('.state-transient')) {
                        container
                            .find('.eip-input:first')
                            .select()
                            .focus();
                    } else {
                        // we're an existing field, and the user probably just wanted to change the select
                    }
                };

                if (container.is('.state-transient')) {
                    afterUpdate.call(this);
                } else if (this.options.model) {
                    this.options.model.save({name: data.newKey}, {patch: true, wait: true});
                } else {
                    Nut.ajax.post({
                        url:
                            container.find('input[name=baseUrl]').val() +
                            '/edit-multifield-key.json',
                        data: data,
                        success: afterUpdate,
                    });
                }
            }
        },

        removeContainer: function() {
            var p = this.container.parent();
            // need to remove this.classes.editing or inputDidBlur will fire and try to save an empty input
            this.container.removeClass(this.classes.editing).remove();
            p.trigger(this.events.didClose, this); // have to do this, otherwise it won't bubble
        },

        enterCancel: function() {
            this.overCancel = true;
        },

        exitCancel: function() {
            this.overCancel = false;
        },

        cancel: function(event) {
            if (event) event.stopPropagation();

            if (
                this.isInputEmpty() &&
                !this.isDirty() &&
                this.container.hasClass(this.classes['transient'])
            ) {
                this.removeContainer();
                return;
            }

            this.inputElements.each(function() {
                $(this).val($(this).data('eip-original-value'));
            });

            this.close();
        },

        deleteElement: function() {
            if (this.container.hasClass(this.classes['transient'])) {
                this.didDeleteSuccessfully();
            } else {
                var deleteElement = this.container.find(this.options.deleteElementSelector),
                    question = deleteElement.find('.js-confirm').text(),
                    popminiOptions = {
                        attachTo: this.container,
                        insertAfter: this.container,
                    };
                if (!_.isEmpty(this.container.data('popminiInsertWithin')))
                    popminiOptions.within = this.container.data('popminiInsertWithin');
                if (deleteElement.find('.js-confirm').length === 0) {
                    // If we don't have a .js-confirm element, we have nothing to show the
                    // user within the Popmini, so we're going to infer the intent of _not_
                    // showing a Popmini with confirmation.
                    //
                    // refs https://nutshell.atlassian.net/browse/NUT-5662
                    return;
                }
                Popmini.question(
                    question,
                    popminiOptions,
                    $.proxy(function(response) {
                        if (!response) return;
                        if (this.options.deleteUrl) {
                            Nut.ajax.post({
                                url: this.options.deleteUrl,
                                data: this.getDataToSave({delete: true}),
                                success: $.proxy(this.didDeleteSuccessfully, this),
                                error: $.proxy(this.didFailSaving, this),
                            });
                        } else if (this.options.model) {
                            this.options.model.destroy({wait: true});
                        } else {
                            this.didDeleteSuccessfully();
                        }
                    }, this)
                );
            }
        },

        didDeleteSuccessfully: function(response) {
            var didDelete = $.Event(this.events.didDelete);
            this.container.trigger(didDelete);
            if (!didDelete.isDefaultPrevented()) this.clear();
        },

        clear: function() {
            this.inputElements.val('').data('eip-original-value', '');
            this.valueElements.html('');
            if (this.isOpen()) this.close();
            this.container.addClass(this.classes.empty);
        },

        close: function() {
            this.inputElements.blur().hide(); // this will trigger the blur event, but we'll be closing, which prevents actions
            this.valueElements.show();
            this.hideButtons();
            this.container
                .removeClass(this.classes.dirty)
                .removeClass(this.classes.editing)
                .removeClass(this.classes.invalid);
            this.container.toggleClass(this.classes.empty, this.isValueEmpty());

            this.container.trigger(this.events.didClose, this);
        },

        blinkSuccess: function() {
            this.container.blinkSuccess();
        },

        blinkError: function() {
            this.container.blinkError();
        },
    });
})(jQuery);

EditInPlace.factory = (function($) {
    return function(container, options) {
        var $container = $(container);
        var isLegacyDatePicker = $container.hasClass('type-date type-legacy-date');
        var isReactDatePicker = $container.is('.type-date:not(.type-legacy-date)');

        // The legacy activity modal is the only instance where we are still
        // using the legacy date picker.
        if (isLegacyDatePicker) return new EditInPlaceDate(container, options);
        else if (isReactDatePicker) {
            options = _.extend(options, {
                shouldOmitTime: $container.hasClass('type-date-without-time'),
            });
            return new EditInPlaceReactDate(container, options);
        } else if ($container.hasClass('type-time')) return new EditInPlaceTime(container, options);
        else if ($container.hasClass('type-currency'))
            return new EditInPlaceCurrency(container, options);
        else if ($container.hasClass('type-text')) return new EditInPlaceText(container, options);
        else if ($container.hasClass('type-mentions'))
            return new EditInPlaceMentions(container, options);
        else return new EditInPlace(container, options);
    };
})(jQuery);
var EditInPlaceDate = (function($) {
    return EditInPlace.extend({
        init: function(container, options) {
            this.defaults = $.extend(true, {}, this.defaults, {
                hideValuesOnEdit: false, // don't hide value elements for dates
                addButtons: false,
                dateFormat: 'M d, yy',
            });

            this._super(container, options);
            if (_(this.options.minDate).isString() || _(this.options.minDate).isNumber())
                this.options.minDate = new Date(this.options.minDate * 1000);
            if (_(this.options.maxDate).isString() || _(this.options.maxDate).isNumber())
                this.options.maxDate = new Date(this.options.maxDate * 1000);
        },

        createInput: function(valueElement) {
            valueElement = $(valueElement);

            var newDatepicker = $(document.createElement('input'));

            var datepickerOptions = {
                showOtherMonths: true,
                showButtonPanel: false,
                beforeShow: $.proxy(this, 'willDisplayDatepicker'),
                onChangeMonthYear: $.proxy(this, 'didChangeMonthYear'),
                onSelect: $.proxy(this, 'didSelect'),
                onClose: $.proxy(this, 'inputWillBlur'),
                dateFormat: this.options.dateFormat,
                minDate: this.options.minDate,
                maxDate: this.options.maxDate,
            };

            newDatepicker
                .attr('type', 'hidden')
                .addClass(this.classes.input)
                .insertBefore(valueElement);

            newDatepicker.datepicker(datepickerOptions);
            newDatepicker.datepicker(
                'setDate',
                this.getDateFromValueElement(this.valueElements.first())
            );

            newDatepicker.data('eip-twin', valueElement);
            valueElement.data('eip-twin', newDatepicker);

            this.inputElements = this.inputElements.add(newDatepicker);
        },

        setDate: function(date) {
            this.inputElements.datepicker('setDate', date);
        },

        setMinDate: function(minDate, enforceImmediately) {
            this.setMinMaxDate(minDate, 'minDate', enforceImmediately);
        },

        setMaxDate: function(maxDate, enforceImmediately) {
            this.setMinMaxDate(maxDate, 'maxDate', enforceImmediately);
        },

        setMinMaxDate: function(date, minOrMax, enforceImmediately) {
            if (!this.inputElements.length) this.prepareInputs();
            this.options[minOrMax] = date;

            var formattedDate = $.datepicker.formatDate(this.options.dateFormat, date),
                valueElement = this.inputElements.data('eip-twin');

            // adjust current date if before the min
            if (minOrMax == 'minDate' && this.inputElements.datepicker('getDate') < date) {
                valueElement.text(formattedDate);
            } else if (minOrMax == 'minDate' && this.inputElements.datepicker('getDate') > date) {
                valueElement.text(formattedDate);
            }

            this.inputElements.datepicker('option', minOrMax, date);

            // if the user doesn't open the EIP, the minimum won't be forced. this forces it if the EIP is queried
            // without ever being opened
            if (typeof enforceImmediately != 'undefined' && enforceImmediately) {
                this.inputElements.addClass('state-was-changed');
            }
        },

        prepareInput: function(valueElement) {
            var inputElement = $(valueElement).data('eip-twin');

            inputElement.data('eip-original-value', this.getDateFromValueElement(valueElement));
        },

        willDisplayDatepicker: function(input, datePicker) {
            DatePicker.prettify(this.container, datePicker);
        },
        didChangeMonthYear: function(month, year, datePicker) {
            DatePicker.prettify(this.container, datePicker);
        },

        didSelect: function(dateText, datePicker) {
            // since we can't get the formatted string any other time,
            // stash it for use in handleSaveResponse if we don't get
            // a formatted response from a callback
            var valueElement = datePicker.input.data('eip-twin');
            valueElement.data('datepicker-formatted-string', dateText);

            // track whether a user explicitly chose a date, otherwise, we're going to use default date
            // since datepicker clamps it min/max
            datePicker.input.addClass('state-was-changed');

            // hiding before the saveAndClose logic; the order of events in close()
            // cause the styles and/or positioning to go out of whack, so just a bandaid to hide
            // the visual weirdness
            datePicker.dpDiv.hide();
            this.saveAndClose();
        },

        isDirty: function() {
            var isDirty = false;
            this.inputElements.each(
                $.proxy(function(index, inputElement) {
                    // prevent race condition and exception if the eip is completely yanked (e.g. custom report date range filters)
                    if (!$(inputElement).data('eip-original-value')) return;

                    if (!$(inputElement).hasClass('state-was-changed')) return;

                    if (
                        this.getValueFromInputElement(inputElement).getTime() !=
                        $(inputElement)
                            .data('eip-original-value')
                            .getTime()
                    )
                        isDirty = true;
                }, this)
            );

            return isDirty;
        },

        getDataToSave: function() {
            var data = this._super(),
                sel = this.inputElements.datepicker('getDate'),
                timestamp;

            if (
                sel &&
                (this.isDirty() || _.isUndefined(this.valueElements.first().data('timestamp')))
            ) {
                // Get the time to the local OS settings, subtract the local OS
                // timezone offset to get GMT, then add the user settings (localeData)
                // to get their configured time.
                var osTimezoneOffset = new Date().getTimezoneOffset() * 60;
                timestamp = sel.getTime() / 1000 - osTimezoneOffset + localeData.timezoneOffset;
                if (osTimezoneOffset != localeData.timezoneOffset) {
                    // @hack if the os timezone offset is different than the server, assume we're in DST and add an hour.
                    timestamp += 3600;
                }
            } else {
                // use the existing value
                timestamp = this.valueElements.first().data('timestamp');
            }

            if (timestamp) data[this.options.saveKey] = timestamp;

            // For some reason, the datepicker appears when you call getDate on it before showing it at all
            $('#ui-datepicker-div').hide();
            return data;
        },

        getDateFromValueElement: function(valueElement) {
            if ($(valueElement).data('timestamp')) {
                return new Date($(valueElement).data('timestamp') * 1000);
            }

            return new Date();
        },

        getValueFromInputElement: function(inputElement) {
            return $(inputElement).datepicker('getDate');
        },

        handleSaveResponse: function(response) {
            // current dependency on one date per eip container
            var valueElement = this.inputElements.first().data('eip-twin');

            if (response && $.isPlainObject(response[this.options.saveKey])) {
                var newValue = response[this.options.saveKey];

                valueElement.data('timestamp', newValue.timestamp);
                valueElement.html(newValue.humanDate);
                this.container.attr('title', newValue.humanDate);
            } else {
                // saved, but no immediate server save response, generate our own local one.
                // if we're not saving immediately, show the picked date in the value element
                // (less accurate than the date returned by the server)
                var selectedDate = this.inputElements.datepicker('getDate'),
                    newDateString = valueElement.data('datepicker-formatted-string'),
                    today = new Date(),
                    tomorrow = new Date(),
                    yesterday = new Date();
                tomorrow.setDate(today.getDate() + 1);
                yesterday.setDate(today.getDate() - 1);

                if (selectedDate.toDateString() == today.toDateString()) {
                    newDateString = 'Today';
                } else if (selectedDate.toDateString() == yesterday.toDateString()) {
                    newDateString = 'Yesterday';
                } else if (selectedDate.toDateString() == tomorrow.toDateString()) {
                    newDateString = 'Tomorrow';
                }

                valueElement.text(newDateString);
            }
        },

        getDateString: function() {
            var selectedDate = this.inputElements.datepicker('getDate'),
                valueElement = this.inputElements.first().data('eip-twin'),
                newDateString = valueElement.data('datepicker-formatted-string'),
                today = new Date(),
                tomorrow = new Date(),
                yesterday = new Date();
            tomorrow.setDate(today.getDate() + 1);
            yesterday.setDate(today.getDate() - 1);

            if (selectedDate.toDateString() == today.toDateString()) {
                newDateString = 'Today';
            } else if (selectedDate.toDateString() == yesterday.toDateString()) {
                newDateString = 'Yesterday';
            } else if (selectedDate.toDateString() == tomorrow.toDateString()) {
                newDateString = 'Tomorrow';
            }
            return newDateString;
        },
    });
})(jQuery);
var EditInPlaceTime = (function($) {
    return EditInPlace.extend({
        init: function(container, options) {
            this.defaults = $.extend(true, {}, this.defaults, {
                addButtons: false,
            });

            this._super(container, options);
        },

        createInput: function(valueElement) {
            var newTimepicker = this._super(valueElement);
            return newTimepicker.timePicker({
                step: 15,
                show24Hours: false,
                blurOnSelect: true,
            });
        },

        // overrides parent by always changing read-mode value on change
        // i think all classes should do this, but probably a needless performance hit
        inputDidChange: function() {
            var eip = this;
            this.inputElements.each(function() {
                eip.updateSavedValue($(this).attr('name'), eip.getValueFromInputElement($(this)));
            });
        },

        inputWillKeydown: function(event) {
            switch (event.keyCode) {
                //overrides EditInPlace's inputWillKeydown enter event since timepicker
                //will save and deal with the enter event itself.
                case this.keys.RETURN:
                    return true;
                    break;
                //otherwise, we'll let the parent function handle any other event
                default:
                    return this._super(event);
            }
        },

        prepareInput: function(valueElement) {
            this._super(valueElement);

            var inputElement = $(valueElement).data('eip-twin'),
                timePicker = $.timePicker(inputElement);

            // also save the original date object (for duration modified calculations)
            inputElement.data('eip-original-value-time', timePicker.getTime());
        },

        startEditing: function(event) {
            this._super(event);

            if (!event.isDefaultPrevented()) {
                //added :visible so it wasn't always selecting the left most timepicker
                var timePicker = $('.time-picker:visible');
                var firstInput = this.inputElements.filter(this.options.inputType).first();

                if (!timePicker.is('.popover')) {
                    _.defer(function() {
                        if (!timePicker) return;
                        var nub = $('<div class="popover-nub" />');

                        timePicker.addClass('popover orientation-south state-persist');
                        timePicker.append(nub);

                        nub.position({
                            my: 'center bottom',
                            at: 'center top',
                            of: timePicker,
                            offset: '0 1',
                            collision: 'none',
                        });
                    });
                }

                timePicker.mousewheel(function(e, delta, deltaX, deltaY) {
                    var results = $(this).find('.time-picker-container');
                    if (!results.length) return;
                    var top = results.scrollTop();

                    if (deltaY > 0 && top - deltaY <= 0) {
                        results.scrollTop(0);
                        e.preventDefault();
                        e.stopPropagation();
                    } else if (
                        deltaY < 0 &&
                        results[0].scrollHeight - top + deltaY <= results.innerHeight()
                    ) {
                        results.scrollTop(results[0].scrollHeight - results.height());
                        e.preventDefault();
                        e.stopPropagation();
                    }
                });
            }
        },
    });
})(jQuery);
var EditInPlaceCurrency = (function($) {
    return EditInPlace.extend({
        init: function(container, options) {
            this.defaults = $.extend(true, {}, this.defaults, {
                addButtons: false,
                editOnContainerClick: true,
                valueElementSelector: '.value .amount',
            });
            this._super(container, options);
        },

        createInputs: function() {
            this._super();
            var select = this.container.find('select');
            select.data('eip-original-value', select.val());
            select.data('eip-twin', select.closest('.select-mini').data('eip-twin', select));
            this.inputElements = this.inputElements.add(select);
        },

        bindEditElements: function() {
            this._super();
            this.container
                .find('select')
                .on('mousedown', $.proxy(this, 'startEditing'))
                .on('change', this.handleCurrencyChange);
        },

        handleCurrencyChange: function() {
            if ($(this).val() === 'other') {
                var newCurrency = prompt('Enter currency code', '');

                // deselect custom label
                $(this)
                    .find('option')
                    .attr('selected', false);

                // insert new concrete custom label and select it
                var newOption = $(document.createElement('option'))
                    .attr('value', newCurrency)
                    .text(newCurrency)
                    .insertBefore($(this).find('option[value=other]'));

                newOption.attr('selected', true);
                $.uniform.update(this);
            }
        },

        handleSaveResponse: function(response) {
            if (response && $.isPlainObject(response['value'])) {
                this.updateSavedValue('amount', response['value'].formatted);
            }
        },

        clear: function() {
            //keep the currency-shortname selector as-is
            this.inputElements
                .filter('.amount')
                .val('')
                .data('eip-original-value', '');
            this.valueElements.html('');
            if (this.isOpen()) this.close();
            this.container.addClass(this.classes.empty);
        },
    });
})(jQuery);
var EditInPlaceText = (function($) {
    return EditInPlace.extend({
        init: function(container, options) {
            this.defaults = $.extend(true, {}, this.defaults, {
                inputType: 'textarea',
                inputIsElastic: true,
                editOnContainerClick: true,
            });

            this._super(container, options);
        },

        //override to stop saving when enter is pressed, because we want to allow line breaks
        inputWillKeydown: function(event) {
            if (event.keyCode === this.keys.ESC) {
                this.cancel();
                return false;
            }
            window.setTimeout($.proxy(this.inputDidKeydown, this), 20);
            return true;
        },
    });
})(jQuery);
var EditInPlaceMentions = EditInPlaceText.extend({
    init: function(container, options) {
        this.defaults = $.extend(true, {}, this.defaults, {
            mentionCollections: 'accounts,contacts,Users,Teams',
        });

        this._super(container, options);
    },

    // adds the appropriate <input> or <textarea> to the dom, saves as object attribute
    createInput: function(valueElement) {
        var newInput = this._super(valueElement),
            mentionInputText = this.container.data('mentions-text'),
            mentions = this.container.data('mentions');
        // convert mentions to use mentionsInput nomenclature
        mentions = _.map(mentions, function(item) {
            return {
                type: item.entityName,
                id: item.entityId,
                value: item.name,
            };
        });

        // initialize mentions lib and render existing note using renderNote method
        newInput.data('collections', this.options.mentionCollections);
        Mentions.MentionsInput(newInput);
        newInput.mentionsInput('renderNote', mentionInputText, mentions);

        return newInput;
    },

    prepareInput: function(valueElement) {
        var inputElement = $(valueElement).data('eip-twin'),
            defaultValue = $(valueElement).data('default'),
            value = inputElement.data('messageText') || defaultValue;
        inputElement.mentionsInput('renderNote', value);
        inputElement.data('eip-original-value', value);
    },

    getValueFromInputElement: function(inputElement) {
        return this.container.find('div.mentions > div').html();
    },

    getDataToSave: function() {
        if (!this.inputElements.length) this.prepareInputs();
        var data = {};

        // this is the syntax version of the input from jquery-mentionsinput
        data[this.options.saveKey] = $(this.inputElements[0]).data('messageText');
        return data;
    },

    updateSavedValue: function(key, value) {
        this._super(key, value, true);
        if (key == 'value' && this.valueElements.length) {
            // render the updated value from the input instead of the return value,
            // remove the outer <strong>s from the mention spans; we lose linkification
            // from server, but keep the whitespace
            $(this.valueElements).html(this.getValueFromInputElement());
            $(this.valueElements)
                .find('.mention-rendered')
                .unwrap();
        }
    },
});
var EditInPlaceReactDate = (function($) {
    return EditInPlace.extend({
        init: function(container, options) {
            this.defaults = $.extend(true, {}, this.defaults, {
                hideValuesOnEdit: false, // don't hide value elements for dates
                addButtons: false,
                timeFormat: 'h:mm A',
                dateFormat: 'MMM D, YYYY',
            });

            this._super(container, options);
            if (_(this.options.minDate).isString() || _(this.options.minDate).isNumber())
                this.options.minDate = new Date(this.options.minDate * 1000);
            if (_(this.options.maxDate).isString() || _(this.options.maxDate).isNumber())
                this.options.maxDate = new Date(this.options.maxDate * 1000);
        },

        createInput: function(valueElement, options) {
            var $valueElement = $(valueElement);
            var $dummyInputElement = $('<input/>');
            $dummyInputElement.data('eip-twin', $valueElement);
            $valueElement.data('eip-twin', $dummyInputElement);

            this.inputElements = this.inputElements.add($dummyInputElement);

            // Do not render React date picker if we are just getting data to save.
            if (!options.createInputs) return;

            // Render react date picker.
            var mountPoint = document.createElement('div');

            var props = {
                anchor: valueElement,
                value: this.getDateFromValueElement($valueElement) || new Date(),
                minDate: this.options.minDate,
                maxDate: this.options.maxDate,
                location: 'bottom',
                shouldOmitTime: this.options.shouldOmitTime,
                onCancel: _.bind(function() {
                    this.inputWillBlur();
                    // Optimistically close date picker.
                    this.unmountReactDatePicker();
                }, this),
                onConfirm: _.bind(function(dateValue) {
                    $dummyInputElement.data('value', dateValue);
                    this.didSelect($dummyInputElement);
                }, this),
            };

            var dateTimePicker = React.createElement(Nut.ui.DateTimePickerPopover, props);
            ReactDOM.render(dateTimePicker, mountPoint);

            this.unmountReactDatePicker = _.bind(function() {
                if (!mountPoint) return;
                ReactDOM.unmountComponentAtNode(mountPoint);
                mountPoint = undefined;
                // Prevent this.close() from reaching this point if picker is
                // already closed.
                this.unmountReactDatePicker = undefined;
            }, this);
        },

        setDate: function(date) {
            this.inputElements.datepicker('setDate', date);
        },

        setMinDate: function(minDate, enforceImmediately) {
            this.setMinMaxDate(minDate, 'minDate', enforceImmediately);
        },

        setMaxDate: function(maxDate, enforceImmediately) {
            this.setMinMaxDate(maxDate, 'maxDate', enforceImmediately);
        },

        setMinMaxDate: function(date, minOrMax, enforceImmediately) {
            if (!this.inputElements.length) this.prepareInputs();
            this.options[minOrMax] = date;

            var formattedDate = $.datepicker.formatDate(this.options.dateFormat, date),
                valueElement = this.inputElements.data('eip-twin');

            // adjust current date if before the min
            if (minOrMax == 'minDate' && this.inputElements.data().value < date) {
                valueElement.text(formattedDate);
            } else if (minOrMax == 'minDate' && this.inputElements.data().value > date) {
                valueElement.text(formattedDate);
            }

            this.inputElements.datepicker('option', minOrMax, date);

            // if the user doesn't open the EIP, the minimum won't be forced. this forces it if the EIP is queried
            // without ever being opened
            if (typeof enforceImmediately != 'undefined' && enforceImmediately) {
                this.inputElements.addClass('state-was-changed');
            }
        },

        prepareInput: function(valueElement) {
            var inputElement = $(valueElement).data('eip-twin');

            inputElement.data('eip-original-value', this.getDateFromValueElement(valueElement));
        },

        didSelect: function($inputElement) {
            // since we can't get the formatted string any other time,
            // stash it for use in handleSaveResponse if we don't get
            // a formatted response from a callback
            var $valueElement = $inputElement.data('eip-twin');
            var formattedDate = moment($inputElement.data().value).format(this.options.dateFormat);
            var formattedTime = moment($inputElement.data().value).format(this.options.timeFormat);
            $valueElement.data('datepicker-formatted-string', formattedDate);
            $valueElement.data('datepicker-formatted-time-string', formattedTime);

            // track whether a user explicitly chose a date, otherwise, we're going to use default date
            // since datepicker clamps it min/max
            $inputElement.addClass('state-was-changed');

            this.saveAndClose();
        },

        isDirty: function() {
            var isDirty = false;

            this.inputElements.each(
                $.proxy(function(index, inputElement) {
                    if (!$(inputElement).hasClass('state-was-changed')) return;

                    var originalValue = $(inputElement).data('eip-original-value');
                    var newValue = this.getValueFromInputElement(inputElement);

                    if (!originalValue && newValue) {
                        isDirty = true;
                        return;
                    }

                    if (originalValue && newValue) {
                        isDirty = !(newValue.getTime() === originalValue.getTime());
                    }
                }, this)
            );

            return isDirty;
        },

        getDataToSave: function(options) {
            var options = _.extend({}, options || {}, {isReactDatePicker: true});

            var data = this._super(options),
                sel = options.delete ? null : this.inputElements.data().value,
                timestamp;

            if (
                sel &&
                (this.isDirty() || _.isUndefined(this.valueElements.first().data('timestamp')))
            ) {
                // Get the time to the local OS settings, subtract the local OS
                // timezone offset to get GMT, then add the user settings (localeData)
                // to get their configured time.
                var osTimezoneOffset = new Date().getTimezoneOffset() * 60;
                timestamp = sel.getTime() / 1000 - osTimezoneOffset + localeData.timezoneOffset;
                if (osTimezoneOffset != localeData.timezoneOffset) {
                    // @hack if the os timezone offset is different than the server, assume we're in DST and add an hour.
                    timestamp += 3600;
                }
            } else {
                // use the existing value
                timestamp = this.valueElements.first().data('timestamp');
            }

            if (timestamp) data[this.options.saveKey] = timestamp;

            return data;
        },

        getDateFromValueElement: function(valueElement) {
            if ($(valueElement).data('timestamp')) {
                return new Date($(valueElement).data('timestamp') * 1000);
            }

            return null;
        },

        getValueFromInputElement: function(inputElement) {
            return $(inputElement).data().value;
        },

        handleSaveResponse: function(response) {
            // current dependency on one date per eip container
            var valueElement = this.inputElements.first().data('eip-twin');

            if (response && $.isPlainObject(response[this.options.saveKey])) {
                var newValue = response[this.options.saveKey];

                valueElement.data('timestamp', newValue.timestamp);
                valueElement.html(newValue.humanDate);
                this.container.attr('title', newValue.humanDate);
            } else {
                // saved, but no immediate server save response, generate our own local one.
                // if we're not saving immediately, show the picked date in the value element
                // (less accurate than the date returned by the server)
                valueElement.text(this.getDateString());
            }
        },

        getDateString: function() {
            var valueElement = this.inputElements.first().data('eip-twin');
            var selectedDate = this.inputElements.data().value,
                newDateString = valueElement.data('datepicker-formatted-string'),
                today = new Date(),
                tomorrow = new Date(),
                yesterday = new Date();
            tomorrow.setDate(today.getDate() + 1);
            yesterday.setDate(today.getDate() - 1);

            var newTimeString = valueElement.data('datepicker-formatted-time-string');

            if (selectedDate.toDateString() == today.toDateString()) {
                newDateString = 'Today at ' + newTimeString;
            } else if (selectedDate.toDateString() == yesterday.toDateString()) {
                newDateString = 'Yesterday at ' + newTimeString;
            } else if (selectedDate.toDateString() == tomorrow.toDateString()) {
                newDateString = 'Tomorrow at ' + newTimeString;
            }

            return newDateString;
        },

        didDeleteSuccessfully: function(response) {
            // Reset the timestamp because there is technically not an original
            // value anymore.
            $(this.valueElements).data('timestamp', null);
            this._super(response);
            this.close();
        },

        close: function() {
            // Reset our dummy input elements so that we can createInput() and
            // render the react date picker on each run.
            this.inputElements = $('');
            if (this.unmountReactDatePicker) this.unmountReactDatePicker();
            this._super();
        },
    });
})(jQuery);
var Graph = Class.extend({
    init: function(placeholder, myGraphData) {
        this.placeholder = $(placeholder);
        this.placeholder.data('graph', this);

        var options;
        if (typeof myGraphData == 'undefined') {
            // use global var like classic reports
            options = graphData[this.placeholder.data('type')];
        } else {
            // options / series data were passed in constructor
            options = myGraphData;
        }

        this.setOptions(options);
        this.initTooltips();
        this.initClickableLinks();
        this.plot();
    },

    plot: function() {
        this.graph = $.plot(this.placeholder, this.data, this.options);
        return this;
    },

    setOptions: function(options) {
        this.options = $.extend({}, this.getDefaults(), options);
        if (this.options.segmentTicks) {
            this.options.xaxis.ticks = this.options.segmentTicks;
        }
        this.parseData();
        return this;
    },

    getDefaults: function() {
        return {
            colors: ['#EB993B', '#E1D4C4'],
            series: {
                hoverable: true,
                shadowSize: 0,
                lines: {show: false},
                points: {show: false},
                bars: {
                    show: false,
                    lineWidth: 0,
                    align: 'center',
                    barWidth: 'auto',
                    fill: 1, // alpha
                    margin: 25, // horizontal percentage dedicated to margin;
                },
            },
            legend: {
                noColumns: 10,
                suppressStyles: true,
                margin: 0,
                backgroundOpacity: 0,
                show: false,
            },
            xaxis: {
                mode: 'time',
                color: '#333333',
                // ticks: 3,
                // tickLength: 3,
                maxNumLabeledTicks: 7, // maximum number of x-axis labels on bar charts
                labelWidth: null, // size of tick labels in pixels
                labelHeight: null,
            },
            yaxis: {
                color: '#333333',
                position: 'left',
                ticks: 4,
                tickLength: 2,
                labelHeight: null,
            },
            yaxes: [
                {
                    tickLength: 2,
                    labelWidth: 37,
                    labelHeight: null,
                },
                {
                    tickLength: 0,
                    labelWidth: 0,
                    ticks: 0,
                },
            ],
            xaxes: [
                {
                    tickLength: 2,
                    labelHeight: 32,
                    labelWidth: null,
                },
                {
                    tickLength: 0,
                    labelWidth: 0,
                    ticks: 0,
                },
            ],
            grid: {
                show: true,
                borderWidth: 0,
                drawAllAxes: true,
                axisMargin: 0,
                labelMargin: 2,
                tickColor: '#333333',
                hoverable: true,
                mouseActiveRadius: 30,
            },
            hooks: {},
        };
    },

    initClickableLinks: function() {
        this.leadListLink = this.placeholder
            .find('.lead-list-link')
            .remove()
            .appendTo('body')
            .hide();

        this.leadListLink = $(this.leadListLink);

        if (this.leadListLink.length) {
            this.placeholder.on(
                'plotclick',
                _.bind(function(event, pos, item) {
                    if (item && item.datapoint) {
                        var date = item.datapoint[0];
                        var filters = _.clone(this.leadListLink.data());

                        //get type of date filter (e.g. createdTime, closedTime) then remove it
                        var dateFilter = filters.dateFilter;
                        delete filters.dateFilter;

                        //set date filter
                        var startOfDay = moment(date)
                            .startOf('day')
                            .unix();
                        var endOfDay = moment(date)
                            .endOf('day')
                            .unix();
                        filters[dateFilter] = startOfDay + ' TO ' + endOfDay;

                        //if we have an entity filter (1-sources, 3-users) apply it
                        if (this.data && this.data[0].filter) {
                            var filterEntity = this.data[0].filter;
                            var filterName = filterEntity.split('-')[1];

                            if (filterName === 'territories') filterName = 'territory';
                            if (filterName === 'users' || filterName === 'teams')
                                filterName = 'owner';
                            filters[filterName] = filterEntity;
                        }

                        //serialize filter
                        var filterString = $.param({filter: filters});
                        var href = '/leads?' + filterString + '&persist=false';

                        //apply href to confirm link
                        window.open(href, '_blank');
                    }
                }, this)
            );
        }
    },

    initTooltips: function() {
        this.tooltip = this.placeholder
            .find('.graph-tooltip')
            .remove()
            .appendTo('body')
            .hide();

        this.tooltip = $(this.tooltip);

        var mouseOverGraph = false;
        $(this.placeholder).hover(
            function() {
                mouseOverGraph = true;
            },
            function() {
                mouseOverGraph = false;
            }
        );
        this.hideTooltipTimer = setInterval(
            $.proxy(function() {
                if (!mouseOverGraph) this.tooltip.hide();
            }, this),
            500
        );

        if (this.tooltip.length) {
            var previousPoint;
            this.placeholder.on(
                'plothover',
                $.proxy(function(event, pos, item) {
                    document.body.style.cursor = 'default';
                    this.graph.unhighlight();

                    if (item && item.datapoint != previousPoint) {
                        if (this.options.grid && this.options.grid.clickable) {
                            this.graph.highlight(item.series, item.datapoint);
                            document.body.style.cursor = 'pointer';
                        }

                        previousPoint = item.datapoint;

                        this.populateTooltipWithItem(item);

                        var top = item.pageY - this.tooltip.outerHeight(),
                            left = item.pageX;

                        this.tooltip.css({top: top, left: left}).show();
                    }

                    if (!item) {
                        this.tooltip.hide();
                        previousPoint = null;
                    }
                }, this)
            );
        }
    },

    populateTooltipWithItem: function(item) {
        var x = item.datapoint[0],
            y = item.datapoint[1];

        this.tooltip.find('.value-x').text(x.toLocalString());
        this.tooltip.find('.value-y').text(y.toLocalString());

        // @TODO use proper JS date i18n lib
        if (this.tooltip.find('.value-x-date')) {
            var date = new Date(x);

            this.tooltip.find('.value-x-date').text($.plot.formatDate(date, '%b %y'));
        }

        if (this.tooltip.find('.value-x-date-day')) {
            var dateFormatted = moment(x).format('ddd, MMM D');
            this.tooltip.find('.value-x-date-day').text(dateFormatted);
        }

        if (this.tooltip.find('.value-y-currency')) {
            this.tooltip
                .find('.value-y-currency')
                .text(localeData.currencySymbol + y.toLocalString());
        }
    },

    // tick formatters
    tickFormatterCurrency: function(value, axis) {
        var prefix = localeData.currencySymbol,
            smartToFixed = function(integer) {
                // yields 1, 1.5, 2, 2.1, 2.5, 3 etc
                if (parseInt(integer, 10) == integer) {
                    return integer.toFixed(0);
                } else {
                    return integer.toFixed(1);
                }
            },
            rounded;

        if (value >= 1000000) {
            rounded = smartToFixed(value / 1000000);
            return prefix + rounded + 'm';
        } else if (value >= 1000) {
            rounded = smartToFixed(value / 1000);
            return prefix + rounded + 'k';
        } else {
            return prefix + value;
        }
    },
});
var GraphQuickReport = Graph.extend({
    getDefaults: function() {
        var myDefaults = {
            series: {
                bars: {
                    show: true,
                    margin: 30,
                },
            },
            xaxis: {
                color: Nut.colors.graphLabelGrey,
                ticks: 3,
            },
            xaxes: [
                {
                    labelHeight: 14, // going below 4 displaces the axis from the series
                },
            ],
            yaxis: {
                min: 0,
                ticks: 3,
                labelHeight: 0,
                color: Nut.colors.graphLabelGrey,
            },
            y2axis: {
                position: 'right',
                color: Nut.colors.graphLabelGrey,
                labelWidth: 24,
                ticks: 2,
                tickLength: 2,
            },
            grid: {},
            legend: {
                noColumns: 2,
                suppressStyles: false,
            },
        };
        return $.extend(true, {}, this._super(), myDefaults);
    },
});
var GraphPipeline = GraphQuickReport.extend({
    parseData: function() {
        this.data = [
            {
                data: this.options.series_data.lead_value,
                color: Nut.colors.graphBlue,
                label: 'Value',
                lines: {
                    show: true,
                    fill: 0.25,
                },
                bars: {
                    show: false,
                },
                yaxis: 1,
            },
            {
                data: this.options.series_data.open_leads,
                color: Nut.colors.graphOrange,
                label: 'Quantity',
                bars: {
                    show: true,
                    fill: 0.9,
                },
                yaxis: 2,
            },
        ];

        return this;
    },

    getDefaults: function() {
        var myDefaults = {
            yaxis: {
                tickFormatter: $.proxy(this.tickFormatterCurrency, this),
            },
            legend: {
                show: true,
            },
        };
        return $.extend(true, {}, this._super(), myDefaults);
    },

    populateTooltipWithItem: function(item) {
        this._super(item);

        var count = this.options.series_data.open_leads[item.dataIndex][1],
            value = this.options.series_data.lead_value[item.dataIndex][1];

        this.tooltip.find('.value-y-count').text(count.toLocalString());
        this.tooltip.find('.value-y-value').text(localeData.currencySymbol + value.toLocalString());
    },
});
var GraphEffort = GraphQuickReport.extend({
    parseData: function() {
        this.data = [
            {
                data: this.options.series_data.total_effort,
                color: Nut.colors.graphBlue,
                lines: {
                    show: true,
                    fill: 0.25,
                },
                bars: {
                    show: false,
                },
            },
            {
                data: this.options.series_data.successful_effort,
                color: Nut.colors.graphGreen,
                lines: {
                    show: true,
                    fill: 0.25,
                },
                bars: {
                    show: false,
                },
            },
        ];

        return this;
    },

    getDefaults: function() {
        var myDefaults = {};
        return $.extend(true, {}, this._super(), myDefaults);
    },

    populateTooltipWithItem: function(item) {
        this._super(item);

        var total = this.options.series_data.total_effort[item.dataIndex][1],
            success = this.options.series_data.successful_effort[item.dataIndex][1];

        this.tooltip.find('.value-y-total').text(total.toLocalString());
        this.tooltip.find('.value-y-success').text(success.toLocalString());
    },
});
var GraphSalesCycle = GraphQuickReport.extend({
    parseData: function() {
        this.data = [
            {
                data: this.options.series_data.average_days_open,
                color: Nut.colors.graphBlue,
                lines: {
                    show: true,
                    fill: 0.25,
                },
                bars: {
                    show: false,
                },
            },
        ];

        return this;
    },

    // round
    populateTooltipWithItem: function(item) {
        this._super(item);

        var y = item.datapoint[1];

        this.tooltip.find('.value-y').text(Math.round(y).toLocalString());
    },
});
var GraphSalesProcess = GraphQuickReport.extend({
    parseData: function() {
        this.data = [
            {
                data: this.options.series_data.percent_on_time,
                color: Nut.colors.graphBlue,
            },
        ];

        return this;
    },

    getDefaults: function() {
        var myDefaults = {
            series: {
                lines: {
                    show: true,
                    fill: 0.25,
                },
                bars: {show: false},
            },
            yaxis: {
                tickFormatter: function(value, axis) {
                    return value + '%';
                },
            },
        };
        return $.extend(true, {}, this._super(), myDefaults);
    },

    populateTooltipWithItem: function(item) {
        this._super(item);

        var count = this.options.series_data.percent_on_time[item.dataIndex][1];
        this.tooltip.find('.value-y-count').text(Math.round(count, 1));
    },
});
var GraphWinRate = GraphQuickReport.extend({
    parseData: function() {
        this.data = [
            {
                data: this.options.series_data.closed_leads,
                filter: this.options.filterEntity,
                color: Nut.colors.graphGrey,
                label: 'Leads closed',
            },
            {
                data: this.options.series_data.leads_won,
                color: Nut.colors.graphGreen,
                label: 'Leads won',
                lines: {
                    fill: 0.4,
                },
            },
            {
                data: this.options.series_data.win_rate,
                filter: this.options.filterEntity,
                color: Nut.colors.graphBlue,
                label: '% win rate',
                bars: {
                    show: true,
                    fill: 0.9,
                },
                lines: {
                    show: false,
                },
                yaxis: 2,
            },
        ];

        return this;
    },

    getDefaults: function() {
        var myDefaults = {
            series: {
                lines: {
                    show: true,
                    fill: 0.25,
                },
                bars: {show: false},
            },
            yaxis: {
                tickFormatter: function(value, axis) {
                    return value;
                },
            },
            y2axis: {
                tickFormatter: function(value, axis) {
                    return value + '%';
                },
                min: 0,
                max: 100,
            },
            legend: {
                show: true,
                noColumns: 3,
                backgroundColor: '#fff',
                backgroundOpacity: 0.5,
            },
            grid: {
                clickable: true,
            },
        };
        return $.extend(true, {}, this._super(), myDefaults);
    },

    populateTooltipWithItem: function(item) {
        this._super(item);

        var countWon = this.options.series_data.leads_won[item.dataIndex][1];
        countClosed = this.options.series_data.closed_leads[item.dataIndex][1];
        winRate = this.options.series_data.win_rate[item.dataIndex][1];

        this.tooltip.find('.value-y-count-won').text(countWon.toLocalString());
        this.tooltip.find('.value-y-count-closed').text(countClosed.toLocalString());
        this.tooltip.find('.value-y-win-rate').text(Math.round(winRate.toLocalString(), 1));
    },
});
Global = Nut.modules.Global = (function($) {
    return {
        opt: {
            controlDelay: {show: 150, hide: 150}, // delay before controls appear
            controlAnimate: {show: 75, hide: 100}, // animation duration for control slide-in
            // active: whether we're actively listening for key presses after a trigger key is pressed
            // timeout: placeholder for window timeout after a trigger key is pressed
            // delay: how long to set the timeout after the initial trigger key press
            keyShortcuts: {active: false, timeout: false, delay: 1000},
        },

        go: function() {
            for (var fn in this.init) {
                this.init[fn].apply(this);
            }
        },

        init: {
            configureFilePicker: function(attemptNum) {
                var attempt = attemptNum || 0;
                if (typeof filestack !== 'undefined') {
                    var filepickerClient = (window.filepickerClient = filestack.init(
                        'AEyJlLfWoTeSArprxwporz'
                    ));
                    return filepickerClient;
                } else if (attempt < 10) {
                    // Try again in a second...
                    setTimeout(function() {
                        Global.init.configureFilePicker(attempt++);
                    }, 1000);
                } else {
                    console.error('Problem loading filestack');
                }
            },

            setUpEvents: function() {
                // redirect to auth page when ajax request says too
                $(document).ajaxError(function(e, jqXHR) {
                    try {
                        var response = JSON.parse(jqXHR.responseText || '');
                    } catch (error) {
                        return;
                    }
                    if (Nut.isWebClient && response) {
                        if (response.isAuthRequiredResult) {
                            window.location.pathname = '/auth/';
                            return;
                        }

                        if (response.isSessionTimeoutResult) {
                            window.location.href = '/auth?sessionTimeout=1';
                            return;
                        }
                    }
                });

                // prevent default action on empty <a>s
                $(document).on('click', 'a', function(event) {
                    if ($(this).attr('href') == '#') event.preventDefault();
                });

                $(document).on('click', '.behavior-downloadLink', function(event) {
                    event.preventDefault();

                    var target = $(event.target),
                        downloadLink = $(this)
                            .find('a')
                            .first(),
                        href = downloadLink.attr('href');

                    var ajaxParams = {
                        headers: {
                            Accept: 'text/csv; charset=utf-8',
                        },
                        type: 'GET',
                        url: href,
                    };

                    Nut.ajax.get(ajaxParams).then(function() {
                        Nut.pollForNotifications(document.body.dataset.loggedInUser);
                    });
                    Popover.close();
                });

                // hoverintent
                $(document).on('mouseover', '.hoverintent, .hoverintentdelay', function(event) {
                    if (!$(this).data('hoverintentInit')) {
                        $(this).hoverIntent({
                            interval: 150, // ms before class is added
                            timeout: $(this).hasClass('hoverintentdelay') ? 1100 : 150, // ms delay before class is removed
                            over: function() {
                                $(this).addClass('state-hover-delay');
                            },
                            out: function() {
                                $(this).removeClass('state-hover-delay');
                            },
                        });
                        $(this).data('hoverintentInit', true);
                        $(this).trigger('mouseover');
                    }
                });

                Nut.watchForNotifyUpdates();

                // activity sync error actions
                $(document).on('click', '.activity-sync-retry, .activity-sync-ignore', function(
                    event
                ) {
                    var error = $(this).closest('.notify-message');
                    Nut.ajax.post({
                        url: '/activity/resolve-sync-error/' + error.data('errorId') + '.json',
                        data: {retry: $(this).is('.activity-sync-retry') ? 1 : 0},
                    });
                    error.remove();
                });

                // email send error actions
                $(document).on(
                    'click',
                    '.email-send-retry, .email-send-change, .email-send-ignore, .email-send-retry-noverify',
                    function(event) {
                        var $error = $(this).closest('.notify-message'),
                            userEmailId,
                            noVerify = $(this).hasClass('email-send-retry-noverify') ? 1 : 0;

                        if ($(this).is('.email-send-change')) {
                            userEmailId = $(this)
                                .siblings('.select-email-send-new-useremailid')
                                .val();
                        } else {
                            userEmailId = $error.data('userEmailId');
                        }
                        Nut.ajax.post({
                            url: '/email/resolve-send-for-user-error.json',
                            data: {
                                retry: !$(this).is('.email-send-ignore') ? 1 : 0,
                                userEmailId: userEmailId,
                                emailIds: $error.data('emailIds'),
                                noVerify: noVerify,
                            },
                        });
                        $error.remove();
                    }
                );

                // report export files
                $(document).on('click', '.action-acknowledge', function(event) {
                    var notify = $(this).closest('.notify-message');
                    Nut.ajax.post({
                        url: '/notify/ack-export.json',
                        data: {notifyId: notify.data('notifyId')},
                    });
                    notify.animate({opacity: 0}).slideUp(500);
                });

                // help box settings hiders, welcome to repsonsive-layout hider
                $(document).on('click', '.action-hide', function(event) {
                    event.preventDefault();

                    var dialog = $(this).data('duplicateSelector')
                        ? $($(this).data('duplicateSelector'))
                        : $(this).closest('.help-box-container, .hideable-container');

                    var setting = 'hide_welcome_' + dialog.data('hideMe');

                    _.each(dialog, function(item) {
                        $(item)
                            .animate({opacity: 0})
                            .slideUp(500);
                    });

                    Nut.ajax.post({
                        data: {setting: setting, value: 1},
                        url: '/my-account/save-user-setting.json',
                    });
                });
            },
            setupFileUploader: function(el) {
                // site logo uploader only has .behavior-upload if it's editable (i.e. user is an administrator)
                Uploader.setup('header .behavior-upload, #settings .behavior-upload', {
                    imagesOnly: true,
                    messageOrientation: 'south',
                    success: function(response) {
                        this.find('img').fadeOut(function() {
                            $(this)
                                .attr('src', response.url)
                                .fadeIn();
                        });
                    },
                });
            },
            updateAppearances: function() {
                $('.select-mini')
                    .filter(':not(:has(div.selector))')
                    .find('select')
                    .uniform();
            },
            showFullPageModal: function() {
                var modal = $('.full-page-modal');
                if (modal.length) {
                    _.delay(function() {
                        modal.toggleClass('state-active', true);
                    }, 200);
                }
            },
            preparePage: function() {
                $(document).ready(function() {
                    if ($('body').hasClass('embeddable-page')) {
                        $(document).on('click', 'a', function(event) {
                            //ignore modal opens, mailto links, and non '#' href links
                            if (
                                !$(event.target).attr('href') ||
                                $(event.target).attr('href') == '#'
                            )
                                return;
                            if ($(event.target).hasClass('action-open')) return;
                            if (
                                $(event.target)
                                    .attr('href')
                                    .indexOf('mailto') == 0
                            )
                                return;

                            event.preventDefault();
                            window.open(event.target.href, '_blank');
                        });
                    }

                    if (
                        $('.flash-message-standard:contains("Twitter account connected")').length >
                        0
                    ) {
                        Analytics.trackEvent(
                            Analytics.categories.integrations,
                            Analytics.actions.integrations.twitterActivated
                        );
                    }
                });
            },
        },
    };
})(jQuery);
Autocomplete = Nut.modules.Autocomplete = (function($) {
    // instantiated separately for each <input> encountered.
    var AutocompleteInput = function(input, options) {
        Nut.log('Autocomplete instantiating for input with options', input, options);

        this.timeout = null;
        this.input = $(input);

        this.previousValue = '';
        this.currentSelection = $();

        // cleanInputs stores a copy of as-typed inputs, to unpreviewInputs() after a result has been selected
        this.cleanInputs = {};

        this.isPreviewing = false;
        this.beganSelectClick = false;

        this.options = $.extend({}, this.defaults, options);

        // a container which will receive various state CSS classes, and contains the resultList somewhere below it
        this.container = function() {
            if (typeof this.options.getContainer == 'function')
                return this.options.getContainer.apply(this.input);

            return this.input.closest('div');
        }.apply(this);

        // a UL or OL which contains all results (and optional pseudo results)
        this.resultList = function() {
            if (typeof this.options.getResultList == 'function')
                return this.options.getResultList.apply(this.input);

            return this.container.find('ul').first();
        }.apply(this);

        if (this.options.multipleInputs) {
            this.inputContainer = function() {
                if (typeof this.options.getInputContainer == 'function')
                    return this.options.getInputContainer.apply(this.input);

                return this.input.closest('form');
            }.apply(this);
        }

        this.clearExistingResults(); // in case another input populated them

        if (_(this.options.url).isFunction()) {
            this.options.url = this.options.url(this.container);
        }

        if (_(this.options.collections).isFunction()) {
            this.options.collections = this.options.collections(this.container);
        }

        if (this.options.collections) this.options.minLength = 2;

        if (this.options.collections && !_(this.options.collections).isArray()) {
            this.options.collections = [this.options.collections];
        }

        // munge-in autocompleteData from original entity name and id data
        this.resultList
            .find('li.' + this.classes.itemInitial)
            .each(function() {
                $(this).data({
                    autocompleteResult: {
                        entityName: $(this).data('entityName'),
                        entityId: $(this).data('entityId'),
                    },
                });
            })

            // add classes to first and last initial result elements
            .first()
            .addClass(this.classes.itemFirst)
            .end()
            .last()
            .addClass(this.classes.itemLast)
            .end();

        var self = this;

        // bind events
        this.input
            .on(
                'keydown paste',
                $.proxy(function(event) {
                    // action events that don't alter the input
                    switch (event.keyCode) {
                        case Nut.keys.UP:
                            event.preventDefault();
                            this.listShow();
                            this.selectPrev();
                            break;

                        case Nut.keys.DOWN:
                            event.preventDefault();
                            this.listShow();
                            this.selectNext();
                            break;

                        case Nut.keys.RETURN:
                            event.preventDefault();
                            this.listShow();
                            if (
                                (this.isLoading() || this.isWaiting()) &&
                                typeof this.options.onShortCircuit == 'function'
                            ) {
                                // short circuit: enter was pushed while ajax is still requesting
                                // eventually this should get smarter, in case there's a hung request
                                // and the user is legitimately choosing a selected item
                                this.options.onShortCircuit.call(this);
                                return;
                            } else if (this.currentSelection.length) {
                                this.chooseElement(this.currentSelection);
                            } else {
                                if (this.options.tabOnEnter) {
                                    // tab to the next field
                                    this.handleTabOnEnter();
                                } else {
                                    // immediately search
                                    clearTimeout(this.timeout);
                                    this.afterDelay();
                                }
                            }
                            break;

                        case Nut.keys.ESC:
                            if (this.currentSelection.length) {
                                this.unpreviewInputs();
                                this.deselect();
                            } else {
                                this.input.blur();
                            }
                            break;

                        case Nut.keys.LEFT:
                        case Nut.keys.RIGHT:
                        case Nut.keys.CTRL:
                        case Nut.keys.SHIFT:
                        case Nut.keys.ALT:
                        case Nut.keys.TAB:
                            break;
                        default:
                            setTimeout($.proxy(this.beforeDelay, this), 1);
                            break;
                    }
                }, this)
            )
            .focus($.proxy(this.onFocus, this))
            .blur($.proxy(this.onBlur, this))
            .attr('autocomplete', 'off');

        // result item events
        // we need to use mousedown to capture the event before blur, in case of slow clickers
        $(this.resultList)
            .off('mousedown.autocomplete', 'li')
            .on('mousedown', function(event) {
                self.resultList.addClass(self.classes.listFocus);
            })
            .on('mousedown.autocomplete', 'li', function(event) {
                if (self.beganSelectClick) return; // awkward hack to avoid double clicks for now
                self.beganSelectClick = true;

                if (!this.isPreviewing) self.saveInputs();
                event.stopPropagation(); // this click event belongs to autocomplete and shoutl not bubble out
                self.chooseElement($(this));
                self.beganSelectClick = false;
                return false; // there could be multiple click handlers, on multi-input autocompletes. don't trigger them all
            })
            .mousewheel(function(e, delta, deltaX, deltaY) {
                // kill mousewheel events from bubbling to the body when scrolling in a resultlist
                var results = self.resultList,
                    top = results.scrollTop();

                if (deltaY > 0 && top - deltaY <= 0) {
                    results.scrollTop(0);
                    e.preventDefault();
                    e.stopPropagation();
                } else if (
                    deltaY < 0 &&
                    results[0].scrollHeight - top + deltaY <= results.innerHeight()
                ) {
                    results.scrollTop(results[0].scrollHeight - results.height());
                    e.preventDefault();
                    e.stopPropagation();
                }
            });

        this.container.addClass(this.classes.ready);
        if (this.options.focusOnInit) this.onFocus.call(this);
    };

    AutocompleteInput.prototype = {
        classes: {
            // container
            focus: 'autocomplete-state-focus state-focus',
            loading: 'autocomplete-state-loading state-loading',
            waiting: 'state-waiting', // pre-ajax, while waiting for add'l typing
            empty: 'autocomplete-state-empty state-empty',
            group: 'autocomplete-group',
            groupPrefix: 'autocomplete-group-',
            ready: 'autocomplete-ready',

            // result list
            item: 'autocomplete-result-item',
            itemSelected: 'autocomplete-state-selected',
            itemInitial: 'autocomplete-initial-item',
            nonItem: 'autocomplete-non-item',
            itemFirst: 'autocomplete-result-first',
            itemLast: 'autocomplete-result-last',
            listFocus: 'autocomplete-result-focus',
        },

        defaults: {
            url: null, // string or function that accepts $(container) as arg and returns str
            getData: null, // a function that returns the data normally retrieved from url. disables url. receives a single string argument of current input value
            collections: null, // function or actual backbone collection(s) to search, overriding getData and url
            delay: 150,
            minLength: 1, // default set to 2 if using a backbone collection
            additionalFields: {},
            addResultsAfter: false,
            groupBy: false,
            disablePreview: false, // don't preview the various fields of the result into <input> (as is done in cards)
            keepOnBlur: false, // keep showing result list on blur (but only meta items)
            keepOnEmpty: false, // once result list is visible, keep showing even if it contains no results
            tabOnEnter: false,
            focusOnInit: true,
            validateOnChoose: false, // show a popmini error if there's an invalid selection (and don't trigger blur)
            multipleInputs: false, // for when several autocomplete inputs are used together in a form

            // callbacks
            onSelect: $.noop, // called when a result is 'selected' which simply means arrowed-to
            onChoose: $.noop, // called when a result is actually chosen via click/enter
            onCancel: $.noop, // called on ESC keypress
            onBlur: $.noop, // called when *all* fields are blurred
            filterResponse: null, // callled with the ajax response
            onResults: $.noop, // called after an ajax response has been parsed and fully inserted
            onShortCircuit: null, // called if enter is pressed while ajax is still loading
            getInitialItems: null, // if you want to provide a bunch of initial items (in LI form), via code

            // extra classes
            extraFocusClass: '', // added/removed when showing/hiding the list (vs. 100ms before hiding)

            // called before showing / hiding resuls. return false to cancel built-in slide.
            listShow: function() {
                return true;
            },
            listHide: function() {
                return true;
            },

            // overriding built-in functions here:
            getContainer: false,
            getResultList: false,
            getInputContainer: false, // used in conjunction with multipleInputs option
            getAllResults: false,
            resultToElement: false, // accepts a single result object from ajax, returns a $li
            resultElementText: false, // parses result element content for title text
        },

        beforeDelay: function() {
            var currentValue = $.trim(this.input.val());

            if (!this.isPreviewing) this.saveInputs();

            this.listShow();

            var maxLength = currentValue.length;

            if (this.options.multipleInputs) {
                this.inputContainer.find('input').each(function() {
                    if ($.trim($(this).val()).length > maxLength)
                        maxLength = $.trim($(this).val()).length;
                });
            }

            if (maxLength < this.options.minLength) {
                this.clearExistingResults();
                this.showInitialResults();
                this.previousValue = currentValue;
                this.hideEmptyGroups();
                return;
            }

            if (currentValue == this.previousValue) return;

            this.previousValue = currentValue;

            this.container.addClass(this.classes.waiting);

            clearTimeout(this.timeout);
            this.timeout = setTimeout($.proxy(this.afterDelay, this), this.options.delay);
        },

        getFields: function() {
            var fields = [];
            var inputs = this.options.multipleInputs
                ? this.inputContainer.find('input')
                : this.container.find('input');
            inputs.each(function() {
                fields.push({
                    name: $(this).data('solrName')
                        ? $(this).data('solrName')
                        : $(this).attr('name'),
                    value: $(this).val(),
                });
            });

            return fields;
        },

        afterDelay: function() {
            var currentValue = $.trim(this.input.val()),
                name = $.trim(
                    this.input.data('solrName')
                        ? this.input.data('solrName')
                        : this.input.attr('name')
                ),
                data;

            if (!name) name = 'name';
            this.container.removeClass(this.classes.waiting).addClass(this.classes.loading);

            // data source: backbone collections
            if (this.options.collections) {
                var collections;

                if (_(this.options.collections).isFunction()) {
                    collections = this.options.collections(this.container);
                } else {
                    collections = this.options.collections;
                }

                if (collections.length == 1 && collections[0].queryable) {
                    // creates a new instance so our query doesn't throw out initialResults
                    var collectionCopy = new collections[0].constructor();

                    collectionCopy.remote = true; // disable dualstorage for this temporary collection
                    collectionCopy.query(currentValue, {
                        success: function(collection, response, options) {
                            collection = collection.map(function(cmodel) {
                                return {
                                    entityName: cmodel.type,
                                    entityId: cmodel.id,
                                    name: cmodel.get('name'),
                                };
                            });
                            this.receiveAjax({results: collection});
                        }.bind(this),
                        error: function(collection, response, options) {
                            Nut.error('Error retrieving autocomplete data ', response);
                        },
                    });
                } else {
                    data = _(collections).reduce(function(data, collection) {
                        return data.concat(
                            _.isFunction(collection.filterActive)
                                ? collection
                                      .filterActive()
                                      .filter(function(cmodel) {
                                          var value = cmodel.get(name);
                                          return (
                                              value &&
                                              value
                                                  .toUpperCase()
                                                  .indexOf(currentValue.toUpperCase()) >= 0
                                          );
                                      })
                                      .map(function(cmodel) {
                                          return {
                                              entityName: cmodel.type,
                                              entityId: cmodel.id,
                                              name: cmodel.get('name'),
                                          };
                                      })
                                : collection
                                      .filter(function(cmodel) {
                                          var value = cmodel.name;
                                          return (
                                              value &&
                                              value
                                                  .toUpperCase()
                                                  .indexOf(currentValue.toUpperCase()) >= 0
                                          );
                                      })
                                      .map(function(cmodel) {
                                          return {
                                              entityName: cmodel.type,
                                              entityId: cmodel.id,
                                              name: cmodel.name,
                                          };
                                      })
                        );
                    }, []);
                    this.receiveAjax({results: data});
                }

                // data source: getData callback
            } else if (_(this.options.getData).isFunction()) {
                data = this.options.getData.call(this, currentValue);

                this.receiveAjax({results: data});
                // data source: url callback (solr)
            } else {
                // compile the vars to be sent: input name and value + any add'l fields specified via this.options
                // we send the plain field / value for simple support, and a 'fields' dict for complex queries
                data = $.extend(
                    {
                        field: name,
                        value: currentValue,
                        fields: this.getFields(),
                    },
                    this.options.additionalFields
                );

                Nut.ajax.get({
                    url: this.options.url,
                    data: data,
                    success: $.proxy(this.receiveAjax, this),
                    error: function(xhr) {
                        Nut.error('Error retrieving autocomplete data ', xhr);
                    },
                });
            }
        },

        receiveAjax: function(response) {
            if (this.options.filterResponse != null)
                response.results = this.options.filterResponse.call(this, response.results);

            this.container.removeClass(this.classes.loading);

            if (response.results.length) {
                // create new result els first, so we can compare against existing
                var newElements = _.map(response.results, this.createResultElement, this);

                this.clearExistingResults();
                this.hideInitialResults();
                this.container.removeClass(this.classes.empty);

                _.each(newElements, this.addResultElement, this);
            } else {
                this.container.addClass(this.classes.empty);
                this.clearExistingResults();
                this.showInitialResults();
            }

            this.options.onResults.call(this);

            // after all results have been added, check to see if we need to show / hide any groups.
            this.hideEmptyGroups();
        },

        isLoading: function() {
            return this.container.hasClass(this.classes.loading);
        },

        isWaiting: function() {
            return this.container.hasClass(this.classes.waiting);
        },

        // called after all results have been added / removed
        // there are some hacky timeouts, this is called after all of them
        resultsDidFinishChanging: function() {
            this.selectFirstElementIfNecessary();
        },

        createResultElement: function(result) {
            var $el;

            if (typeof this.options.resultToElement == 'function') {
                $el = this.options.resultToElement.call(this, result);
            } else {
                $el = $(document.createElement('li'));
                $el.text(result.name);
            }

            if (typeof this.options.resultElementText == 'function') {
                $el.attr('title', this.options.resultElementText.call(this, $el));
            } else {
                $el.attr('title', $el.text().trim());
            }

            // check if we're recreating the currently-selected element, in which case we should select it.
            if (
                this.currentSelection.length &&
                _.isEqual(this.currentSelection.data('autocompleteResult'), result)
            ) {
                this.selectElementWithoutPreview($el);
            }

            return $el.addClass(this.classes.item).data('autocompleteResult', result);
        },

        addResultElement: function(resultElement) {
            var addResultTo;

            // if we have a custom before element (i.e. a prefix)
            if (this.options.groupBy) {
                // multiple types of results, each grouped in its own li
                var groupName = resultElement.data('autocompleteResult')[this.options.groupBy];
                if (!groupName) {
                    Nut.error('Received an ungroupable autocomplete element');
                    return;
                }
                groupName = groupName.toLowerCase();
                addResultTo = this.resultList.find(
                    '.' + this.classes.groupPrefix + groupName + ' ul'
                );
            } else {
                // simple add
                addResultTo = this.resultList;
            }

            // add classes to first and last result elements
            var items = addResultTo.find('li.' + this.classes.item);
            items
                .removeClass(this.classes.itemLast)
                .first()
                .addClass(this.classes.itemFirst);
            if (items.length === 0) resultElement.addClass(this.classes.itemFirst);
            resultElement.addClass(this.classes.itemLast);

            // if we already have results inserted, add after last item
            var lastExistingResult = addResultTo.find('li.' + this.classes.item).last();
            if (lastExistingResult.length) return lastExistingResult.after(resultElement);

            if (
                this.options.addResultsAfter &&
                addResultTo.find(this.options.addResultsAfter).length
            ) {
                addResultTo.find(this.options.addResultsAfter).after(resultElement);
            } else {
                // otherwise, just slap it onto the end of the list
                addResultTo.prepend(resultElement);
            }
        },

        clearExistingResults: function() {
            this.resultList.find('li.' + this.classes.item).remove();
        },

        hideInitialResults: function() {
            this.resultList.find('li.' + this.classes.itemInitial).hide();
        },
        showInitialResults: function() {
            if (this.options.collections) {
                var resultToElement = this.options.resultToElement;

                // we can do this way more efficiently with data-autocomplete-result with jquery 1.8. search bait: attr('data-
                var initialItems = _(this.options.collections).reduce(function(data, collection) {
                    var activeItems = _.isFunction(collection.filterActive)
                        ? collection.filterActive()
                        : collection;
                    return data.add(
                        _(activeItems)
                            .first(300)
                            .reduce(function(data, cmodel) {
                                var result = {
                                        entityName: cmodel.type,
                                        entityId: cmodel.id,
                                        name: _.isFunction(cmodel.get)
                                            ? cmodel.get('name')
                                            : cmodel.name,
                                    },
                                    newElement = resultToElement
                                        ? resultToElement(result).addClass(
                                              'autocomplete-initial-item'
                                          )
                                        : $(
                                              '<li class="autocomplete-initial-item">' +
                                                  _.escape(result.name) +
                                                  '</li>'
                                          );

                                return data.add(newElement.data('autocompleteResult', result));
                            }, data)
                    );
                }, $([]));

                this.resultList.find('li.' + this.classes.itemInitial).remove();
                this.resultList.append(initialItems);
            } else if (this.options.getInitialItems) {
                // i'd rather have done this as getInitialResults, but resultToItem assumes it'll always be real resuls, not initials
                this.resultList.find('li.' + this.classes.itemInitial).remove();
                this.resultList.append(this.options.getInitialItems.call(this));
            }
            var numInitial = this.resultList.find('li.' + this.classes.itemInitial).show().length;

            this.container.toggleClass(this.classes.empty, numInitial === 0);
            if (numInitial == 0 && !this.options.keepOnEmpty) {
                this.listHide();
            } else {
                this.listShow();
            }
        },

        hideEmptyGroups: function() {
            if (this.options.groupBy) {
                // this has to be delayed minutely, because results may have visibility
                // changed by events elsewhere, on keyup or (notably behavior-isbound)
                window.setTimeout(
                    $.proxy(function() {
                        this.container
                            .find('.' + this.classes.group)
                            .show() // first show all groups
                            .not(':has(li:visible)')
                            .hide(); // and then hide the ones that don't have items

                        this.resultsDidFinishChanging();
                    }, this),
                    5
                );
            }
        },

        saveInputs: function() {
            this.cleanInputs = {};

            this.container.find('input').each(function() {
                $(this).data('autocomplete-as-typed', $(this).val());
            });
        },

        unpreviewInputs: function() {
            this.container.find('input').each(function() {
                $(this).val($(this).data('autocomplete-as-typed'));
            });

            this.container.find('span.autocomplete-preview').text('');

            this.isPreviewing = false;
        },

        // see #1154
        handleTabOnEnter: function() {
            var inputs = this.container.find('input:visible'),
                myIndex = inputs.index(this.input);

            if (myIndex < inputs.length - 1) {
                // tab to next input
                this.input.blur();
                inputs[myIndex + 1].focus();
            } else {
                // select and choose the first item in the autocomplete (if one were already selected, we wouldn't be here)
                this.chooseElement(this.getAllResults().eq(0));
            }
        },

        // choose methods
        chooseElement: function(element) {
            if (this.options.validateOnChoose && !element.data('autocompleteResult')) {
                Popmini.error('Invalid selection', {
                    attachTo: this.container.find('input'),
                    orientation: 'north',
                    autoHide: 1000,
                });
                return;
            }

            if (this.currentSelection != element) this.selectElement(element);
            this.input.blur();
            this.listHide(); // normally this is delayed in the blur
            this.options.onChoose.call(this);
        },

        // list methods
        listHide: function() {
            this.resultList.removeClass(this.classes.listFocus);
            if (this.options.listHide.call(this)) this.resultList.slideUp(100);
        },
        listShow: function() {
            if (this.options.listShow.call(this)) this.resultList.slideDown(100);
        },
        listScroll: function() {
            var scrollPos = this.resultList.scrollTop(),
                delta =
                    this.currentSelection.position().top +
                    this.currentSelection.outerHeight() -
                    this.resultList.height();

            if (delta > 0) {
                this.resultList.scrollTop(scrollPos + delta);
            } else if (this.currentSelection.position().top < 0) {
                this.resultList.scrollTop(scrollPos + this.currentSelection.position().top);
            }
        },

        onFocus: function() {
            if (!this.container.hasClass(this.classes.focus)) {
                this.clearExistingResults();
                this.previousValue = '';
            }

            this.container.addClass(this.classes.focus + ' ' + this.options.extraFocusClass);

            // immediately re-search if > minlength
            var maxLength = $.trim(this.input.val()).length;

            if (this.options.multipleInputs) {
                this.inputContainer.find('input').each(function() {
                    if ($.trim($(this).val()).length > maxLength)
                        maxLength = $.trim($(this).val()).length;
                });
            }

            if (maxLength >= this.options.minLength) {
                clearTimeout(this.timeout);
                this.afterDelay();
                this.listShow();
            } else {
                this.showInitialResults();
            }

            this.selectFirstElementIfNecessary();
        },

        onBlur: function() {
            this.container.removeClass(this.classes.focus);

            // pause before hiding the list, in case another autocomplete (with this same container) wants to reuse it.
            window.setTimeout(
                $.proxy(function() {
                    if (this.options.multipleInputs) {
                        if ($(document.activeElement).is(this.inputContainer.find('input'))) return;
                    }

                    this.deselect();

                    if (!this.container.hasClass(this.classes.focus)) {
                        if (!this.resultList.hasClass(this.classes.listFocus)) {
                            if (!this.options.keepOnBlur) {
                                this.clearExistingResults();
                                this.listHide();
                            }
                            this.options.onBlur.call(this);
                        }

                        this.container.removeClass(this.options.extraFocusClass);
                    }
                }, this),
                200
            );
        },

        // used by navigation functions to move up and down through results
        getAllResults: function() {
            var allResults;

            if (typeof this.options.getAllResults == 'function') {
                allResults = this.options.getAllResults.call(this);
            } else {
                allResults = this.resultList.find('li:visible');
            }

            return allResults.not('.' + this.classes.nonItem);
        },

        //
        // selection methods
        //

        // called on focus & new ajax results
        // if a result is currently selected, keep it
        // if a metaitem is currently selected, try to select the first element
        selectFirstElementIfNecessary: function() {
            var inputs = this.container.find('input:visible'),
                allResults = this.getAllResults(),
                firstVisibleResult = allResults.filter('.' + this.classes.item + ':visible').eq(0),
                firstVisibleAll = allResults.filter('li:visible').eq(0);

            // if i'm the last input in the card (thus enter will choose first item), select it
            if (inputs.index(this.input) == inputs.length - 1) {
                // keep a currently-selected result selected
                if (this.currentSelection.is('.' + this.classes.item + ':visible')) return;

                if (firstVisibleResult.length) {
                    this.selectElementWithoutPreview(firstVisibleResult);
                } else {
                    // we're going to be selecting a non-result item
                    if (this.currentSelection.is(':visible')) return;
                    this.selectElementWithoutPreview(firstVisibleAll);
                }
            }
        },

        selectNext: function() {
            var all = this.getAllResults();
            var position = all.index(this.currentSelection) + 1;
            var toSelect = all.eq(position).length ? all.eq(position) : all.eq(0);
            this.selectElement(toSelect);
            this.listScroll();
        },

        selectPrev: function() {
            var all = this.getAllResults();
            var position = all.index(this.currentSelection) - 1;
            var toSelect = position != -2 ? all.eq(position) : all.last(); // jquery considers -2 valid, to my chagrin

            this.selectElement(toSelect);
            this.listScroll();
        },

        selectElementWithoutPreview: function(element) {
            this.deselect();
            element.addClass(this.classes.itemSelected);

            this.currentSelection = element;
        },

        selectElement: function(element) {
            this.deselect();
            element.addClass(this.classes.itemSelected);

            this.currentSelection = element;

            // iterate through the entire AJAX result, assigning values to associated <input>
            var result = element.data('autocompleteResult');
            if (result && !this.options.disablePreview) {
                this.isPreviewing = true;

                // set inputs to available results or blank.
                this.container.find('input:visible').each(function() {
                    var name = $(this).attr('name');
                    var value = result[name] ? result[name] : '';
                    value = typeof value == 'object' ? value[0] : value;

                    $(this).val(_.unescape(value));
                });

                // fill any preview fields (behave like <inputs>, but non-focusable)
                this.container.find('span.autocomplete-preview').each(function() {
                    var name = $(this).data('name');
                    var value = result[name] ? result[name] : '';
                    value = typeof value == 'object' ? value[0] : value;

                    $(this).text(value);
                });
            } else {
                this.unpreviewInputs();
            }
            this.options.onSelect.call(this);
        },

        deselect: function() {
            this.currentSelection.removeClass(this.classes.itemSelected);
            this.currentSelection = $();
        },
    };

    return {
        opt: {},

        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        init: {},

        setup: function(selector, options) {
            $(document).on('focus', selector, options, this.trigger);
        },

        trigger: function(event) {
            if (!$(this).data('autocompleteInput')) {
                var input = new AutocompleteInput(this, event.data);
                $(this).data('autocompleteInput', input);
            }
        },

        AutocompleteInput: AutocompleteInput,
    };
})(jQuery);
Form = Nut.modules.Form = (function($) {
    return {
        opt: {
            placeholder: {always: true, lion: true},
        },

        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        init: {
            setUpEvents: function() {
                $(document)
                    // add new modal field
                    .on('click', '.behavior-repeatable .action-add-repeatable', function(event) {
                        var container = $(this).closest('.behavior-repeatable'),
                            repeatable = container.find('.repeatable-blank');

                        repeatable = repeatable.clone().insertBefore(repeatable);
                        repeatable.removeClass('repeatable-blank');

                        Form.init.updateAppearances(container);

                        repeatable.find('.action-edit').click();
                    })
                    .on('EipDidDelete', '.behavior-repeatable', function(event) {
                        var deleted = $(event.target),
                            container = $(this);

                        deleted.fadeOut(function() {
                            deleted.remove();
                            Form.updateRepeatableAreas(container);
                        });
                    })
                    .on('EipDidClose', '.behavior-repeatable', function(event) {
                        Form.updateRepeatableAreas($(this));
                    });
            },

            updateAppearances: function(container) {
                if (!$(container).length) container = $('body');

                container
                    .find('.eip-container:not(.eip-config-custom,.eip-ready)')
                    .each(function() {
                        EditInPlace.factory(this, $(this).data());
                    });

                // v1 standard EIP fields (mostly limited to hoverview now, and deprecated, but need to pull the rest out)
                $('.fieldblock .eip-container:not(.eip-ready):visible').each(function() {
                    var options = $.extend(
                        {},
                        {
                            addButtons: false,
                        },
                        $(this).data()
                    );
                    EditInPlace.factory(this, options);
                });

                container
                    .find('.fieldrow.type-enum:not(.variant-readonly,.enum-ready)')
                    .each(function() {
                        $(this)
                            .addClass('enum-ready')
                            .find('select')
                            .on('change', Form.saveEnumSelection);
                        if ($(this).is('.type-multiple')) {
                            $(this)
                                .find('select')
                                .bsmSelect({
                                    removeLabel: '',
                                    extractLabel: function(option) {
                                        return $(
                                            '<div class="bsmListItemRemove"></div><span>' +
                                                option.html() +
                                                '</span>'
                                        );
                                    },
                                    title: 'Add an option...',
                                });
                        }
                    });

                Form.setUpViews(container);
                Form.updateRepeatableAreas(container);
            },

            formTags: function() {
                /* standard events on <form>s */
                $(document)
                    .on('focus', 'form', function(event) {
                        $(this)
                            .closest('div')
                            .addClass('state-selected');
                    })

                    .on('blur', 'form', function(event) {
                        var container = $(this).closest('div');

                        // delay removing the selected state, primarily so events aren't missed
                        window.setTimeout(function() {
                            container.removeClass('state-selected');
                        }, 1000);
                    })

                    .on(
                        'click',
                        'form .form-button, form .button-action, form .button-interaction, form a',
                        function(event) {
                            var form = $(this).closest('form');

                            if (
                                !form.hasClass('behavior-no-submit') &&
                                ($(this).hasClass('form-button-save') ||
                                    $(this).hasClass('action-submit'))
                            ) {
                                form.submit();
                            } else if (
                                $(this).hasClass('form-button-cancel') ||
                                $(this).hasClass('action-cancel')
                            ) {
                                form.get(0).reset();
                            }
                        }
                    )

                    // would be nice to make this work for areas where we can't put <form>s, e.g. integration.
                    .on('submit', 'form.async', function(event) {
                        Form.asyncSubmit(event, $(this));
                    });
            },
        },

        setUpViews: function(container) {
            container.find('input.collection-select:not(.select2-ready)').each(function() {
                // if this select2 is inside of a tempate (used by Form.getNewBlock)
                if ($(this).closest('.template').length) return;

                var view = new Nut.View.select2({
                    el: $(this),
                });
            });
        },

        updateRepeatableAreas: function(container) {
            container = container.is('.behavior-repeatable')
                ? container
                : container.find('.behavior-repeatable');

            container
                .addClass('state-empty')
                .filter(':has(li:visible, dd:visible)')
                .removeClass('state-empty');
        },

        asyncSubmit: function(event, form) {
            if (event) event.preventDefault();
            var module,
                dataToSubmit,
                result = $.Deferred().resolve();
            if (form.hasClass('behavior-custom')) {
                module = Nut.modules[form.data('module')];
            }
            if (form.find('.action-submit').is('.state-disabled')) return result;

            form.removeClass('state-error');
            if (!form.attr('action')) return result;

            var validationChecks = form.attr('class').match(/validate-[^ ]*/g);
            //call all validation routines and exit if any mark the form as invalid
            for (var i in validationChecks) {
                if (i == Number(i)) form.trigger(validationChecks[i]);
                if (form.hasClass('state-error')) {
                    return result;
                }
            }

            form.find('.action-submit').addClass('state-disabled');

            if (form.is('.behavior-hud-loader')) Nut.loadingHudStart();

            form.trigger('FormPostStart');

            if (module && _(module['getDataToSubmit']).isFunction()) {
                dataToSubmit = module['getDataToSubmit'].call(this, form);
            } else {
                dataToSubmit = Form.getDataToSubmit(form);
            }

            result = Nut.ajax.post({
                url: form.attr('action'),
                data: dataToSubmit,
                success: function(result) {
                    Nut.loadingHudStop();

                    form.trigger('FormPostEnd', result);
                    if (!result.success) {
                        form.find('.action-submit').removeClass('state-disabled');
                        return false;
                    }
                    var targetElement;

                    if (form.hasClass('behavior-reset-on-success')) form.get(0).reset();

                    if (form.hasClass('behavior-append') || form.hasClass('behavior-prepend')) {
                        if (form.data('target')) {
                            targetElement = $(form.data('target'));
                        } else {
                            targetElement = form.closest('div');
                        }
                        if (form.hasClass('behavior-append')) {
                            $(result.message).insertAfter(targetElement);
                        } else {
                            $(result.message).prependTo(targetElement);
                        }
                    } else if (form.hasClass('behavior-replace')) {
                        if (form.data('target')) {
                            targetElement = form.visualClosest($(form).data('target'));
                        } else {
                            targetElement = form.closest('div');
                        }
                        targetElement.before(result.message).remove();
                    }

                    if (form.hasClass('behavior-remove-section-placeholder')) {
                        var section = form.closest('section');
                        $(section.find('.box-empty-placeholder')).remove();
                    }

                    if (module && _(module['asyncFormDidSave']).isFunction()) {
                        module['asyncFormDidSave'].call(form, result);
                    }

                    if (form.hasClass('behavior-popover-close') && Popover.isOpen())
                        Popover.close();

                    form.find('.action-submit').removeClass('state-disabled');
                    Nut.updateAppearances();

                    // get collection and update from the server if it exists
                    var collection = form.data('collection');
                    if (collection) {
                        // These three forms' data key doesn't match the collection name,
                        // so we need to translate them.
                        if (['person tags', 'lead tags', 'company tags'].includes(collection)) {
                            collection = 'tags';
                        }

                        // We've added to a managed collection, and need to force a refresh
                        var managedCollectionName =
                            collection[0].toUpperCase() + collection.slice(1);
                        var managedCollection = Nut.managedCollections[managedCollectionName];

                        if (managedCollection) {
                            // get current timestamp to bust the collections cache
                            var currentTimestamp = Math.floor(new Date().getTime() / 1000);
                            window.collectionTimes[collection] = currentTimestamp;
                            managedCollection.fetch();
                        }
                    }

                    return true;
                },
                error: function(response) {
                    Nut.loadingHudStop();
                    form.trigger('FormPostEnd', response);
                    form.addClass('state-error');

                    var submitAttach = form.find('.action-submit');

                    if (response && response.message && submitAttach.length) {
                        Popmini.error(response.message, {attachTo: submitAttach});
                    }

                    form.find('.action-submit').removeClass('state-disabled');
                    return true;
                },
                validate: function(validation) {
                    Nut.loadingHudStop();
                    form.find('.action-submit').removeClass('state-disabled');
                    form.trigger('FormPostEnd', validation);
                    if (!validation.isValid && validation.messages)
                        Form.handleValidationResult(validation.messages, form);
                },
            });
            return result;
        },
        saveEnumSelection: function(event, params) {
            var select = $(this),
                container = select.closest('.type-enum'),
                data = container.serializeArrayToObject();
            if (params && params.option) {
                //must be a bsmSelect-enabled multi-select field
                data.value = _(params.option.parent().find(':selected')).map(function(val) {
                    return $(val).val();
                });
            }
            select.attr('disabled', 'disabled');
            Nut.ajax.post({
                url: container.data('url'),
                data: data,
                success: function() {
                    select.removeAttr('disabled');
                    container.blinkSuccess();
                },
                error: function(response) {
                    select.removeAttr('disabled');
                    container.blinkError();
                    if (response) Popmini.error(response.message, {attachTo: container});
                },
            });
        },

        getDataToSubmit: function(form) {
            return $.extend({}, form.serializeArrayToObject(), form.getEipData());
        },

        handleValidationResult: function(fieldErrorMessages, container) {
            _(fieldErrorMessages).each(function(messages, field) {
                if (typeof messages === 'object') messages = _(messages).values();
                if ($.isArray(messages)) messages = messages.join(', ');
                if (field) field = field.replace(/(\[\w*\])/g, ''); // escape brackets
                try {
                    var input = container.find(':input[name="' + field + '"][type!=hidden]');
                    if (!input.length) {
                        // i.e. type-email on my-account
                        input = container.find(':input[type!=hidden].type-' + field);
                    }
                } catch (e) {
                    // don't crash JS when trying to display a validation message
                    // we'll just mount the error message on the outer container.
                }
                Popmini.error(messages, {attachTo: input && input.length ? input : container});
            });
        },

        // clone a block on a builder-type form
        getNewBlock: function(template, cloneDataAndEvents) {
            var newId = _.uniqueId('_new-'),
                newBlock = template.clone(cloneDataAndEvents);

            // create new IDs for the stupid <label for>s
            newBlock.find('input[id$=_new], label[for$=_new]').each(function() {
                var attribute = $(this).is('input') ? 'id' : 'for';
                $(this).attr(
                    attribute,
                    $(this)
                        .attr(attribute)
                        .replace(/_new/, newId)
                );
            });

            // rename radio buttons so they don't conflict
            newBlock.find('input[type=radio][name*=new]').each(function() {
                $(this).attr(
                    'name',
                    $(this)
                        .attr('name')
                        .replace(/new/, newId)
                );
            });

            return newBlock.removeClass('template').data('entityId', newId);
        },

        // expand me, but will eventually support validating a form, using client-only validation with classname cues
        validateClientSide: function(elements) {
            // first check all fields for valid numbers
            var allValid = true;
            var valid;

            $(elements)
                .find('input.integer')
                .each(function() {
                    valid = !isNaN(parseInt($(this).val(), 10)) || $(this).val().length == 0;
                    $(this).toggleClass('validate-error', !valid);
                    if (!valid) allValid = false;
                });

            return allValid;
        },
    };
})(jQuery);
ModalContent = Nut.modules.ModalContent = (function($) {
    return {
        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        init: {
            setUpEvents: function() {
                $('#modal')
                    // update first tab after EIP title change
                    .on(
                        'EipDidSave',
                        '.modal-card-header .eip-container:has(.modal-card-title)',
                        function() {
                            var card = Modal.getCurrentCard(),
                                newTitle = card
                                    .find('.modal-card-tab-body-wrapper:first .modal-card-title')
                                    .text();

                            card.find('.modal-card-nav li:first span.text').text(newTitle);
                        }
                    )

                    // contact + account tab delete and undelete links
                    .on(
                        'click',
                        '.modal-card-buttonbar .action-delete',
                        ModalContent.deleteWasClicked
                    )
                    .on(
                        'click',
                        '.modal-card-buttonbar .action-undelete',
                        ModalContent.undeleteWasClicked
                    );

                $(document)
                    // watch for modal closing, possibly block if open EIP
                    .on('modalWillClose', function(event) {
                        var card = Modal.getCurrentCard(),
                            stillEditingFields = $(card).find('.state-editing');

                        if (stillEditingFields.length) {
                            event.preventDefault();

                            // close modal on save success
                            card.on(
                                'EipDidClose',
                                // This was not allowing other listeners to fire
                                // until it was wrapped in a function... race?
                                function() {
                                    Modal.close();
                                }
                            );

                            // cancel pending close on eip failure
                            card.on('EipDidFailSaving', function() {
                                card.off('EipDidClose', Modal.close);
                            });

                            stillEditingFields.each(function() {
                                $(this)
                                    .data('eip')
                                    .saveAndClose();
                            });
                        }
                    });
            },
        },

        deleteWasClicked: function(event) {
            event.preventDefault();
            var entity = Modal.getEntityForTab(this),
                url = $(this).data('action');

            var options = {
                attachTo: $(this),
                orientation: 'north',
            };

            if ($(this).hasClass('activity-delete')) {
                // We're currently deleting an activity that's synced with a calendar, so
                // we'll expand our deletion options
                // refs https://nutshell.atlassian.net/browse/NUT-6769
                $.extend(options, {
                    // In our Popmini callback…
                    //     yesOrNo == true
                    //     removeFromSyncedCalendar == false
                    yesText: 'Delete from Nutshell',

                    // In our Popmini callback…
                    //     yesOrNo == false
                    //     removeFromSyncedCalendar == false
                    noText: 'Cancel',

                    // In our Popmini callback…
                    //     yesOrNo == true
                    //     removeFromSyncedCalendar == true
                    alternateYesAction:
                        '<a href="#" class="button button-interaction size-small action-yes action-alternate"><span>Delete everywhere</span></a>',
                });
            }

            Popmini.question(
                $(this)
                    .find('.l10n-delete-confirm')
                    .text(),
                options,
                function(yesOrNo, removeFromSyncedCalendar) {
                    if (!yesOrNo) return;

                    Nut.ajax.post({
                        url: url,
                        data: {
                            // If the alternateYesAction is selected, removeFromSyncedCalendar will
                            // be truthy, but we need to serialize it to 1 or 0 so jQuery doesn't send
                            // up the boolean string
                            removeFromSyncedCalendar: removeFromSyncedCalendar ? 1 : 0,
                        },
                        success: function(response) {
                            $('body').trigger(Nut.events.global.entityWasDeleted, entity);
                            Modal.close();
                            Nut.updateAppearances();
                        },
                    });
                }
            );
        },

        undeleteWasClicked: function(event) {
            event.preventDefault();
            var card = Modal.getCurrentCard(),
                url = $(this).data('action');

            Nut.ajax.post({
                url: url,
                success: function(response) {
                    Modal.reloadCard(card);
                },
            });
        },
    };
})(jQuery);
ModalActivity = Nut.modules.ModalActivity = (function($) {
    return {
        go: function() {
            for (var fn in this.init) {
                this.init[fn].apply(this);
            }
        },

        init: {
            setUpEvents: function() {
                // modal buttons
                $('#modal')
                    .on('click', '.modal-tab-activity .action-log', function(event) {
                        // save changes (if necessary) before logging activity
                        if (
                            $(this)
                                .closest('.modal-card-tab-body-wrapper')
                                .is('.state-dirty')
                        ) {
                            // pass logWasClicked as a success callback so that it runs after changes are saved
                            ModalActivity.saveWasClicked(event, ModalActivity.logWasClicked);
                        } else {
                            ModalActivity.logWasClicked();
                        }
                    })

                    .on('click', '.modal-tab-activity .action-save', ModalActivity.saveWasClicked)

                    // on activity modal load
                    .on(Modal.events.cardDidLoad, '.modal-card.modal-entity-activity', function() {
                        $(window).trigger(Modal.events.activityModalDidLoad, [
                            {
                                activityId: ModalActivity.getId(),
                            },
                        ]);

                        // automatically click on title so user can begin editing
                        if (ModalActivity.getId() === 'new') {
                            ModalActivity.didChange();

                            $(this)
                                .find('.activity-name .action-edit')
                                .click();
                        }

                        // save current activity type to bind it to activity name as necessary
                        var select = ModalActivity.getTab().find('select[name=activityTypeId]');
                        select.data('old-value', select.find('option:selected').text());
                    })

                    .on(
                        'click',
                        '.modal-tab-activity .popover-opener-followup',
                        ModalActivity.addFollowupActivity
                    )

                    // remove participant click
                    .on('click', '.modal-tab-activity .capsule-container .delete', function() {
                        $(this)
                            .closest('.capsule-container')
                            .remove();
                        ModalActivity.didChange();
                    })

                    // add participant
                    .on(
                        Nut.events.capsule.willBeAdded,
                        '.modal-tab-activity .capsule-add-container',
                        function(event) {
                            var existingAssignee = $(this)
                                .closest('.activity-participants')
                                .find('.capsule-container')
                                .filter(function() {
                                    return (
                                        $(this).data('entityName') == event.resultData.entityName &&
                                        $(this).data('entityId') == event.resultData.entityId
                                    );
                                });
                            if (existingAssignee.size()) {
                                Popmini.error($('.l10n-error-duplicate-participant').text(), {
                                    attachTo: $(this),
                                });
                                event.preventDefault();
                            }
                        }
                    )

                    // EIP modification trigger
                    .on('EipDidStartEditing', '.modal-tab-activity', ModalActivity.didChange)

                    .on('change', '.modal-tab-activity select', ModalActivity.didChange)

                    // rename activity if name == activity type name on change
                    .on('change', '.modal-tab-activity select[name=activityTypeId]', function() {
                        var oldValue = $(this).data('oldValue'),
                            newValue = $(this)
                                .find('option:selected')
                                .text(),
                            eip = ModalActivity.getTab()
                                .find('.activity-name')
                                .data('eip');

                        $(this).data('old-value', newValue);

                        if (oldValue == eip.getDataToSave().name)
                            eip.publicSetValue('value', newValue);
                    })

                    // other input change triggers
                    .on('change', '.modal-tab-activity .time-boxes input[type=checkbox]', function(
                        event
                    ) {
                        var tab = ModalActivity.getTab(),
                            isAllDay = tab.find('input[name=is_all_day]').is(':checked'),
                            isUntimed = tab.find('input[name=is_untimed]').is(':checked');

                        tab.toggleClass('state-isallday', isAllDay);
                        tab.toggleClass('state-isuntimed', isUntimed);

                        tab.find('input[name=is_all_day]').attr('disabled', isUntimed);
                        tab.find('input[name=is_untimed]').attr('disabled', isAllDay);

                        ModalActivity.didChange();
                    })

                    .on(
                        Nut.events.capsule.wasAdded + ' ' + Nut.events.capsule.wasDeleted,
                        '.modal-tab-activity',
                        ModalActivity.didChange
                    )

                    // flag button
                    .on('click', '.modal-tab-activity .action-flag', function() {
                        ModalActivity.getTab().toggleClass('state-isflagged');
                        ModalActivity.didChange();
                    })

                    // media
                    .on(
                        Modal.events.cardDidLoad,
                        '.modal-card.modal-entity-activity',
                        ModalActivity.initializeMedia
                    )

                    // video
                    .on('click', '.modal-tab-activity .action-play', ModalActivity.playWasClicked)
                    .on(
                        'click',
                        '.modal-tab-activity .video-overlay .action-close',
                        ModalActivity.closeWasClicked
                    );

                $(document)
                    // watch for modal closing, possibly block if open EIP
                    .on('modalWillClose', function(event, noConfirm) {
                        // check if current tab is dirty.
                        var currentTab = Modal.$el.container.find(
                            '.modal-card-tab-body-wrapper.state-selected'
                        );
                        if (currentTab.is('.state-dirty') && noConfirm !== true) {
                            var message = currentTab
                                    .find('.l10n-state-dirty')
                                    .eq(0)
                                    .text(),
                                options = {
                                    attachTo: currentTab.find('.modal-close'),
                                    yesText: currentTab
                                        .find('.l10n-yes-text')
                                        .eq(0)
                                        .text(),
                                    noText: currentTab
                                        .find('.l10n-no-text')
                                        .eq(0)
                                        .text(),
                                    orientation: 'west',
                                    offsetTop: '10',
                                };
                            Popmini.question(message, options, function(didClickYes) {
                                if (didClickYes) {
                                    Modal.close(true);
                                } else {
                                    // trigger save and close
                                    ModalActivity.saveWasClicked({}, function() {
                                        Modal.close();
                                    });
                                }
                            });

                            event.preventDefault();
                            return;
                        }
                    });

                $(window).on(Modal.events.activityModalLogNoteUpdated, function(
                    event,
                    activityId,
                    content
                ) {
                    // hacky way to show the updated log note immediately
                    const logNoteTextArea = Modal.getCurrentCard().find('.activity-log-note-text');
                    if (logNoteTextArea.length) {
                        logNoteTextArea[0].innerHTML = content;
                    }

                    // remove empty state text if we just added a log note
                    const logNoteContainer = Modal.getCurrentCard().find('.activity-log-note');
                    if (logNoteContainer) {
                        logNoteContainer.removeClass('state-empty');
                    }

                    Lead.reloadActivities();
                    Timeline.refreshTimeline();
                    Activity.refreshActivities(activityId);
                    Peep.reloadActivities();
                });
            },
        },

        // get id of the current activity (visible modal)
        getId: function() {
            return ModalActivity.getTab().data('entityId');
        },

        getTab: function() {
            return Modal.getCurrentCard().find('.modal-tab-activity');
        },

        didChange: function() {
            var tab = ModalActivity.getTab();

            // already dirty
            if (tab.is('.state-dirty')) return;

            tab.addClass('state-dirty');
        },

        logWasClicked: function() {
            var tab = ModalActivity.getTab(),
                activityId = ModalActivity.getId();
            Nut.ajax.post({
                url: '/activity/log/' + activityId + '.json',
                success: function() {
                    $('.modal-tab-activity:visible')
                        .removeClass('status-scheduled status-overdue state-dirty')
                        .addClass('status-logged');
                    Lead.reloadActivities();
                    Timeline.refreshTimeline();
                    Activity.refreshActivities(activityId);
                    Peep.reloadActivities();
                },
                validate: function(validation) {
                    if (!validation.isValid && validation.messages) {
                        Popmini.error(validation.messages.join(', '), {
                            attachTo: tab.find('.action-log'),
                        });
                    }
                },
            });
        },

        saveWasClicked: function(event, successCallback) {
            var button = $(this),
                callBack = function() {
                    if (_.isFunction(successCallback)) {
                        successCallback();
                    } else {
                        if (button.is('.action-saveclose')) Modal.close();
                    }
                };

            var saveData = ModalActivity.getSaveData();

            // Without this, activities will be created with all related leads
            // attached. We submit a list of related leads to add to this
            // activity, which defaults to all leads so we can just make sure to
            // only add those leads. Otherwise, if the user removes leads from
            // this activity then saves, we will add all leads on the frontend
            saveData.dontAddRelatedLeads = true;

            var startDate = new Date(saveData.startTime_date * 1000),
                endDate = new Date(saveData.endTime_date * 1000);

            if (
                ModalActivity.getId() === 'new' &&
                startDate.toDateString() != endDate.toDateString() &&
                saveData.isTimed
            ) {
                Popmini.question(
                    'This activity spans multiple days. Are you sure you want to save?',
                    {attachTo: button},
                    function(response) {
                        if (response) {
                            ModalActivity.saveActivity(saveData, callBack);
                        } else {
                            Modal.loadingStop();
                            // @TODO maybe set saveable here
                        }
                    }
                );
            } else {
                ModalActivity.saveActivity(saveData, callBack);
            }
        },

        saveActivity: function(saveData, successCallback) {
            Modal.loadingStart();
            Nut.ajax.post({
                url: '/activity/edit/' + ModalActivity.getId() + '.json',
                data: saveData,
                error: function(response) {
                    var tab = ModalActivity.getTab(),
                        message = 'There was an error while saving your activity!';
                    if (response && response.message && response.message.length) {
                        message = response.message;
                    }
                    Popmini.error(message, {attachTo: tab.find('.action-save')});
                    Modal.loadingStop();
                    // @TODO reactivate buttons
                },
                validate: function(response) {
                    Modal.loadingStop();
                    ModalActivity.getTab().removeClass('state-dirty');
                    if (response.isValidationResult && !response.isValid) {
                        Form.handleValidationResult(response.messages, ModalActivity.getTab());
                    }
                },
                success: function(response) {
                    var tab = ModalActivity.getTab();
                    Modal.loadingStop();

                    tab.removeClass('state-dirty state-transient')
                        .data('entity-id', response.activityId)
                        .toggleClass('state-unloggable', !response.activityIsLoggable)
                        .toggleClass('state-hasleads', response.activityLeads.length !== 0)
                        .find('.activity-leads')
                        .html(response.activityLeads);

                    Peep.reloadActivities();
                    if (Lead.getId() != 'new') Lead.reloadActivities();
                    Timeline.refreshTimeline();
                    Activity.refreshActivities(response.activityId);
                    if (successCallback) successCallback();
                    // We need to update the lead stage if the created activity meets
                    // an automated stage goal
                    $(window).trigger(Lead.events.LeadBackboneModelDidUpdate);
                    typeof Modal.onSuccess === 'function' ? Modal.onSuccess() : undefined; // This is passed in as an option when we call Modal.open
                },
            });
        },

        getSaveData: function() {
            var tab = ModalActivity.getTab(),
                data = {
                    activityTypeId: tab.find('#activityTypeId').val(),
                    isAllDay: tab.find(':input[name="is_all_day"]').attr('checked') ? 1 : 0,
                    isTimed: tab.find(':input[name="is_untimed"]').attr('checked') ? 0 : 1,
                    isFlagged: tab.is('.state-isflagged') ? 1 : 0,
                    isLogged: tab.is('.status-logged') ? 1 : 0,
                    dontAddRelatedLeads: tab.find('[name=dontAddRelatedLeads]').val(),
                };

            $.extend(data, tab.getEipData());

            data.participants = [];
            $(tab)
                .find('.activity-participants .capsule-container')
                .each(function() {
                    data.participants.push({
                        entityType: $(this).data('entityName'),
                        id: $(this).data('entityId'),
                    });
                });

            data.leadIds = [];
            $(tab)
                .find('.activity-leads .capsule-container')
                .each(function() {
                    data.leadIds.push($(this).data('entityId'));
                });

            if (!tab.hasClass('state-iseditable')) {
                data = _.pick(data, 'activityTypeId', 'isFlagged', 'isLogged', 'logNote');
            }
            return data;
        },

        addFollowupActivity: function() {
            var activityId = ModalActivity.getId();

            Popover.load({
                url: '/activity/popover.json',
                data: {followupId: activityId},
                loadingOrientation: 'north',
                appendTo: $(this),
            });
        },

        initializeMedia: function() {
            if (typeof MacGap !== 'undefined') {
                var tab = ModalActivity.getTab();

                var $media = tab.find('audio,video');
                if ($media.length) {
                    var mediaUrl = $media.attr('src').replace(/view/, 'download');
                    $media.replaceWith('<a href="' + mediaUrl + '">Download recording</a>');
                }
            }
        },

        playWasClicked: function() {
            var tab = ModalActivity.getTab();

            tab.addClass('media-visible');
            $('video')
                .get(0)
                .play();
        },

        closeWasClicked: function() {
            var tab = ModalActivity.getTab();

            $('video')
                .get(0)
                .pause();
            tab.removeClass('media-visible');
        },
    };
})(jQuery);
ModalBulkEdit = Nut.modules.ModalBulkEdit = (function($) {
    return {
        events: {
            bulkEditDidApply: 'bulkEditDidApply',
        },

        go: function() {
            for (var fn in this.init) {
                this.init[fn].apply(this);
            }
        },

        init: {
            setUpEvents: function() {
                // modal header buttons
                $('#modal')
                    .on(Modal.events.cardDidLoad, '.modal-entity-bulk-edit', function() {
                        var collectionsToUpdate = ['Tags', 'Sources', 'Competitors', 'Audiences'];

                        // Make sure these collections are sync'd when we open the modal
                        collectionsToUpdate.forEach(function(collectionName) {
                            Nut.managedCollections[collectionName].fetch();
                        });

                        // initialize tag tokenizers
                        Tags.setUpTagAreas.call(Modal.getCurrentCard().find('.tags-bar'));
                        // initialize source tokenizers
                        Sources.setUpSourceAreas.call(Modal.getCurrentCard().find('.sources-bar'));
                        // initialize competitor tokenizers
                        Competitors.setUpCompetitorAreas.call(
                            Modal.getCurrentCard().find('.competitors-bar')
                        );
                        // initialize audience tokenizers
                        Audiences.setUpAudienceAreas.call(
                            Modal.getCurrentCard().find('.audiences-bar')
                        );
                    })
                    .on('click', '.modal-entity-bulk-edit .action-cancel', function() {
                        Modal.closeWithoutResetting();
                    })
                    .on('click', '.bulk-edit-assignment .button-add-link', function() {
                        $(this)
                            .hide()
                            .siblings('.capsule-add-container')
                            .show()
                            .find('input[name=name]')
                            .trigger('focusin')
                            .trigger('focus');
                    })
                    .on(
                        Nut.events.capsule.wasDeleted,
                        '.bulk-edit-assignment .capsule-container',
                        function() {
                            $(this)
                                .siblings('.button-add-link')
                                .show();
                        }
                    );
            },
        },

        applyWasClicked: function(data) {
            var card = Modal.getCurrentCard(),
                url = $(this).data('action');
            Popmini.question(
                'You will not be able to undo this edit. Are you sure you want to continue?',
                {attachTo: $(this)},
                function(response) {
                    if (response) {
                        Modal.loadingStart();
                        Nut.ajax.post({
                            url: url,
                            data: data,
                            validate: function(response) {
                                Modal.loadingStop();
                                Form.handleValidationResult(response.messages, card);
                            },
                            success: function(response) {
                                if (!data.newFilters) {
                                    location.hash = '/';
                                    location.reload();
                                } else {
                                    document.dispatchEvent(new Event('bulkEditDidApply'));
                                    Modal.loadingStop();
                                    Modal.closeWithoutResetting();
                                }
                            },
                        });
                    } else {
                        Modal.loadingStop();
                    }
                }
            );
        },
    };
})(jQuery);
ModalBulkEditAccount = Nut.modules.ModalBulkEditAccount = (function($) {
    return {
        go: function() {
            for (var fn in this.init) {
                this.init[fn].apply(this);
            }
        },

        init: {
            setUpEvents: function() {
                // modal header buttons
                $('#modal')
                    .on('click', '.modal-tab-edit-account .action-save', function() {
                        ModalBulkEdit.applyWasClicked.call(this, ModalBulkEditAccount.getData());
                    })
                    .on('click', '.modal-tab-edit-account .action-delete', function() {
                        var urlData = $.extend(
                            {},
                            Modal.getCurrentCard().data(Modal.optionDatakey).urlData
                        );
                        urlData.deleteAll = true;
                        ModalBulkEdit.applyWasClicked.call(this, urlData);
                    });
            },
        },

        getData: function() {
            var card = Modal.getCurrentCard(),
                // copy urlData and use that to initially populate data
                data = $.extend({}, card.data(Modal.optionDatakey).urlData),
                options = $('.bulk-edit-options'),
                owner = $('.bulk-edit-assignment').find('.capsule-container'),
                territoryId = options.find('select[name=territoryId]').val(),
                industryId = options.find('select[name=industryId]').val(),
                accountTypeId = options.find('select[name=accountTypeId]').val(),
                addTagIds = Tags.getTagIdsInTagBar(options.find('.tags-bar-type-bulk-add-account')),
                removeTagIds = Tags.getTagIdsInTagBar(
                    options.find('.tags-bar-type-bulk-remove-account')
                ),
                addAudienceIds = Audiences.getAudienceIdsInAudienceBar(
                    options.find('.audiences-bar-type-bulk-add-account')
                ),
                removeAudienceIds = Audiences.getAudienceIdsInAudienceBar(
                    options.find('.audiences-bar-type-bulk-remove-account')
                );

            if (owner.length)
                if (!owner.data('entityId')) {
                    data.unassignAll = true;
                } else {
                    data.owner = {
                        entityName: owner.data('entityName'),
                        entityId: owner.data('entityId'),
                    };
                }

            if (industryId) data.industryId = industryId;
            if (accountTypeId) data.accountTypeId = accountTypeId;
            if (territoryId) data.territoryId = territoryId;
            if (addTagIds) data.addTagIds = addTagIds;
            if (removeTagIds) data.removeTagIds = removeTagIds;
            if (addAudienceIds) data.addAudienceIds = addAudienceIds;
            if (removeAudienceIds) data.removeAudienceIds = removeAudienceIds;

            return data;
        },
    };
})(jQuery);
ModalBulkEditContact = Nut.modules.ModalBulkEditContact = (function($) {
    return {
        go: function() {
            for (var fn in this.init) {
                this.init[fn].apply(this);
            }
        },

        init: {
            setUpEvents: function() {
                // modal header buttons
                $('#modal')
                    .on('click', '.modal-tab-edit-contact .action-save', function() {
                        ModalBulkEdit.applyWasClicked.call(this, ModalBulkEditContact.getData());
                    })
                    .on('click', '.modal-tab-edit-contact .action-delete', function() {
                        var urlData = $.extend(
                            {},
                            Modal.getCurrentCard().data(Modal.optionDatakey).urlData
                        );
                        urlData.deleteAll = true;
                        ModalBulkEdit.applyWasClicked.call(this, urlData);
                    })
                    .on('click', '.modal-tab-edit-contact .unsubscribe-globally', function() {
                        $('.modal-tab-edit-contact .unsubscribe-warning').toggle(this.checked);
                    });
            },
        },

        getData: function() {
            var card = Modal.getCurrentCard(),
                // copy urlData and use that to initially populate data
                data = $.extend({}, card.data(Modal.optionDatakey).urlData),
                options = $('.bulk-edit-options'),
                owner = $('.bulk-edit-assignment').find('.capsule-container'),
                territoryId = options.find('select[name=territoryId]').val(),
                addTagIds = Tags.getTagIdsInTagBar(options.find('.tags-bar-type-bulk-add-contact')),
                removeTagIds = Tags.getTagIdsInTagBar(
                    options.find('.tags-bar-type-bulk-remove-contact')
                ),
                addAudienceIds = Audiences.getAudienceIdsInAudienceBar(
                    options.find('.audiences-bar-type-bulk-add-contact')
                ),
                removeAudienceIds = Audiences.getAudienceIdsInAudienceBar(
                    options.find('.audiences-bar-type-bulk-remove-contact')
                );
            unsubscribeGlobally = $('.modal-tab-edit-contact .unsubscribe-globally').is(':checked');

            if (owner.length)
                if (!owner.data('entityId')) {
                    data.unassignAll = true;
                } else {
                    data.owner = {
                        entityName: owner.data('entityName'),
                        entityId: owner.data('entityId'),
                    };
                }

            if (territoryId) data.territoryId = territoryId;
            if (addTagIds) data.addTagIds = addTagIds;
            if (removeTagIds) data.removeTagIds = removeTagIds;
            if (addAudienceIds) data.addAudienceIds = addAudienceIds;
            if (removeAudienceIds) data.removeAudienceIds = removeAudienceIds;
            if (unsubscribeGlobally) data.unsubscribeGlobally = unsubscribeGlobally;

            return data;
        },
    };
})(jQuery);
ModalBulkEditLead = Nut.modules.ModalBulkEditLead = (function($) {
    return {
        version: 0.1,

        go: function() {
            for (var fn in this.init) {
                this.init[fn].apply(this);
            }
        },

        init: {
            setUpEvents: function() {
                // modal header buttons
                $('#modal')
                    .on('click', '.modal-tab-edit-lead .action-save', function() {
                        ModalBulkEdit.applyWasClicked.call(this, ModalBulkEditLead.getData());
                    })
                    .on('click', '.modal-tab-edit-lead .action-delete', function() {
                        var urlData = $.extend(
                            {},
                            Modal.getCurrentCard().data(Modal.optionDatakey).urlData
                        );
                        urlData.deleteAll = true;
                        ModalBulkEdit.applyWasClicked.call(this, urlData);
                    })
                    .on(
                        'click',
                        '.modal-tab-edit-lead .bulk-edit-overlay-process .button-add-link',
                        function() {
                            $(this)
                                .siblings('select[name=template-processTemplateId]')
                                .clone()
                                .attr('name', 'processTemplateId')
                                .removeClass('hidden')
                                .insertBefore($(this));
                        }
                    )
                    .on(
                        'change',
                        '.modal-tab-edit-lead .bulk-edit-status select[name=status]',
                        function() {
                            Nut.ajax.get({
                                url: '/bulk-edit-lead/outcomes.json',
                                data: {status: $(this).val()},
                                success: function(response) {
                                    $('.modal-entity-bulk-edit .bulk-edit-status .outcomes').html(
                                        response.html
                                    );
                                },
                            });
                        }
                    );
            },
        },

        getData: function() {
            var card = Modal.getCurrentCard(),
                // copy urlData and use that to initially populate data
                data = $.extend({}, card.data(Modal.optionDatakey).urlData),
                options = $('.bulk-edit-options'),
                assignee = $('.bulk-edit-assignment').find('.capsule-container'),
                confidence = options.find('input[name=confidence]').val(),
                dueTime = options.find('.due-time'),
                status = options.find('select[name=status]').val(),
                outcome = options.find('select[name=outcomeId]').val(),
                primaryProcess = options.find('.bulk-edit-primary-process select').val(),
                stageset = options.find('.bulk-edit-stageset select').val(),
                stage = options.find('.bulk-edit-stage select').val(),
                priority = options.find('.bulk-edit-priority select').val(),
                overlayProcesses = options.find(
                    '.bulk-edit-overlay-process select[name=processTemplateId]'
                ),
                addTagIds = Tags.getTagIdsInTagBar(options.find('.tags-bar-type-bulk-add-lead')),
                removeTagIds = Tags.getTagIdsInTagBar(
                    options.find('.tags-bar-type-bulk-remove-lead')
                ),
                addSourceIds = Sources.getSourceIdsInSourceBar(
                    options.find('.sources-bar-type-bulk-add-lead')
                ),
                removeSourceIds = Sources.getSourceIdsInSourceBar(
                    options.find('.sources-bar-type-bulk-remove-lead')
                ),
                addCompetitorIds = Competitors.getCompetitorIdsInCompetitorBar(
                    options.find('.competitors-bar-type-bulk-add-lead')
                ),
                removeCompetitorIds = Competitors.getCompetitorIdsInCompetitorBar(
                    options.find('.competitors-bar-type-bulk-remove-lead')
                ),
                addAudienceIds = Audiences.getAudienceIdsInAudienceBar(
                    options.find('.audiences-bar-type-bulk-add-lead')
                ),
                removeAudienceIds = Audiences.getAudienceIdsInAudienceBar(
                    options.find('.audiences-bar-type-bulk-remove-lead')
                );

            if (status) data.status = status;
            if (outcome) data.outcome = outcome;
            if (confidence) data.confidence = confidence;
            if (primaryProcess) data.primaryProcess = primaryProcess;
            if (stageset) data.stageset = stageset;
            if (stage) data.stage = stage;
            if (priority) data.priority = priority;
            if (overlayProcesses)
                data.overlayProcesses = _(overlayProcesses).map(function(value) {
                    return $(value).val();
                });
            if (assignee.length)
                if (!assignee.data('entityId')) {
                    data.unassignAll = true;
                } else {
                    data.assignee = {
                        entityName: assignee.data('entityName'),
                        entityId: assignee.data('entityId'),
                    };
                }

            if (dueTime.length && dueTime.data('eip').isDirty())
                data.dueTime = dueTime.data('eip').getDataToSave().dueTime;

            if (addTagIds) data.addTagIds = addTagIds;
            if (removeTagIds) data.removeTagIds = removeTagIds;

            if (addSourceIds) data.addSourceIds = addSourceIds;
            if (removeSourceIds) data.removeSourceIds = removeSourceIds;

            if (addCompetitorIds) data.addCompetitorIds = addCompetitorIds;
            if (removeCompetitorIds) data.removeCompetitorIds = removeCompetitorIds;

            if (addAudienceIds) data.addAudienceIds = addAudienceIds;
            if (removeAudienceIds) data.removeAudienceIds = removeAudienceIds;

            return data;
        },
    };
})(jQuery);
Modal = Nut.modules.Modal = (function($) {
    return {
        opt: {
            tabNum: 0,
            width: 740,
            height: {min: 420, max: 900, percentage: 80},
            speed: {show: 200, hide: 0},
            loadingIndicatorSpeed: {show: 0, hide: 0},
        },

        // called on #modal, unless indicated otherwise
        events: {
            cardDidLoad: 'modalCardDidLoad', // called on .modal-card
            willClose: 'modalWillClose',
            didClose: 'modalDidClose', // called on <body>, fires when the animation begins
            activityModalDidLoad: 'activityModalDidLoad',
            activityModalLogNoteUpdated: 'activityModalLogNoteUpdated',
        },

        optionDatakey: 'MODAL-OPTIONS',
        pageUrl: window.location.href,

        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        init: {
            setUpElements: function() {
                Modal.$el = {
                    container: $('#modal-container'),
                    cardsContainer: $('#modal-cards'),
                };
                var modal = $.bbq.getState('modal');
                // load modal from URL hash if present. This is to support referencing activities
                // with the url /activity/open-modal/<id>
                if (modal) Modal.open({url: modal});
            },
            setUpEvents: function() {
                $(document).on('keydown', function(event) {
                    if (event.keyCode == Nut.keys.ESC && Modal.isOpen()) {
                        // don't close on ESC during EIP (add other exceptions here)
                        if ($('#modal').find('.eip-container.state-editing').length) return true;

                        Modal.close();
                        event.preventDefault();
                    }
                    return true;
                });

                // close button, esc key
                Modal.$el.cardsContainer.on('click', '.modal-close', Modal.close);

                // generic openers, ignored if clicking on a mention within the opener
                $(document).on('click', '.modal-opener-activity', function(event) {
                    if (
                        $(event.target).closest("[data-behavior~='ignore-modal-click']").length ||
                        event.isPropagationStopped()
                    )
                        return;
                    if (!$(this).containsSelection()) {
                        Modal.openFromElement($(this));
                        event.preventDefault(); // since we might have something like <a href="/activity/open-modal/123" class="modal-opener-activity">
                    }
                });
                // this is added in a bit of a hurry to make notifications work, but this seems to be a better way of handling
                // any remaining modal interactions, until we can deprecate this whole library.
                $(document).on('click', "a[href*='/open-modal/']", function(event) {
                    var url = $(this)
                        .attr('href')
                        .replace(/\/open-modal\//, '/view/');
                    Modal.open({url: url});
                    event.preventDefault();
                });

                return this;
            },
        },

        getEntityForTab: function(anyElementInTab) {
            var tab = $(anyElementInTab).closest('.modal-card-tab-body-wrapper');
            if (tab.hasClass('modal-tab-contacts')) {
                tab = tab.siblings('.modal-tab-account');
            }

            return {entityName: tab.data('entityName'), entityId: tab.data('entityId')};
        },

        // using min / max / percent, return an integer for the height of the #modal-card
        getHeight: function() {
            var percentageHeight = Math.round(
                $(window).height() * (Modal.opt.height.percentage / 100)
            );
            if (percentageHeight > Modal.opt.height.max) return Modal.opt.height.max;
            if (percentageHeight < Modal.opt.height.min) return Modal.opt.height.min;
            return percentageHeight;
        },

        getCards: function() {
            return Modal.$el.cardsContainer.find('.modal-card');
        },
        getCurrentCard: function() {
            return Modal.getCards().first();
        },

        addCard: function(html) {
            var card = (
                $(html).filter('.modal-card') || $('<div class="modal-card" />').html(html)
            ).appendTo(Modal.$el.cardsContainer);

            card.css({height: Modal.getHeight()});

            card.show();

            return this;
        },
        addBlankCard: function() {
            Modal.addCard(
                '<div class="modal-card modal-card-blank"><div class="modal-card-tab-body-wrapper"><div class="modal-card-tab-body"></div></div></div>'
            );
        },
        removeAllCards: function() {
            Modal.getCards().remove();
            return this;
        },
        reloadCard: function(card) {
            var data = {url: card.attr('src')};
            Modal.close();
            card.remove();
            Modal.open(data);
        },
        loadingStart: function() {
            Modal.$el.container.addClass('state-loading');
            $('#modal-loading')
                .show()
                .fadeTo(Modal.opt.loadingIndicatorSpeed.show, 1);
        },
        loadingStop: function() {
            Modal.$el.container.removeClass('state-loading');
            $('#modal-loading')
                .fadeTo(Modal.opt.loadingIndicatorSpeed.hide, 0)
                .hide();
        },
        updateCurrentPageUrl: function() {
            // Reset the current URL to display on close. This fixes a bug in
            // our react routing where dismissing the modal goes back to an old
            // page.
            Modal.pageUrl = window.location.href;
        },
        open: function(options) {
            options = $.extend(
                {},
                {
                    url: null,
                    urlData: null,
                },
                options
            );
            this.onSuccess = options.onSuccess;

            $('#modal-wrapper').css({
                width: this.opt.width,
                height: this.getHeight(),
                marginLeft: -this.opt.width / 2,
                marginTop: -this.getHeight() / 2,
            });

            $('#modal-wrapper').toggleClass(options.classNames, options.classNames !== undefined);

            // always close any popovers (esp. important for those in a state of transitioning to be open)
            Popover.close(true);

            var currentCard = Modal.getCurrentCard();

            // Use POST instead of GET because urlData can potentially be large, e.g. in the creatorWasSubmitted function
            // of timeline.js, which includes everything typed in the note field in urlData. If we used GET, Nginx will
            // respond with a HTTP 414 once the note gets over ~4kb
            Nut.ajax.post({
                url: options.url,
                data: options.urlData,
                success: function(response) {
                    $('.modal-card-blank').replaceWith(innerShiv(response.message, false));
                    Modal.$el.container.fadeIn(Modal.opt.speed.show);

                    var card = Modal.$el.cardsContainer.find('.modal-card:last');
                    if (!card.length) {
                        Nut.error('Failed to find card in Modal.open, url = ' + options.url);
                    }

                    // remember where this card came from so we don't load it again later
                    card.attr('src', options.url);

                    // save full options array (used by tab loading strategies)
                    card.data(Modal.optionDatakey, options);

                    currentCard.remove();

                    Modal.cardDidLoad(card);
                    // For new reports, which do more client-side navigation,we
                    // want to lazily update Modal.pageUrl so that it doesn't
                    // boot users back to the first report page they landed on
                    if (
                        window.location.pathname &&
                        window.location.pathname.startsWith('/reports/')
                    ) {
                        Modal.pageUrl = window.location.href;
                    }

                    if (window.history) {
                        // for now, we're not going to support back-ing to previous modal,
                        // so using replaceState instead of pushState
                        // bulk-edit-* endpoints return JSON and break browser refresh
                        // and back button navigation to themselves
                        if (!~options.url.indexOf('/bulk-edit')) {
                            window.history.replaceState(
                                {},
                                {},
                                options.url.replace('/view/', '/open-modal/')
                            );
                        }
                    }
                    Analytics.pageview(options.url);

                    // Set up comments list in the modal – should probably move this to a listener
                    if (
                        response.model &&
                        (response.model.type === 'activities' || response.model.type === 'chats') &&
                        options.url != '/activity/view/new'
                    ) {
                        var activity = Nut.Model.getModel(response.model);
                        activity.fetch({
                            success: function(model, response, options) {
                                var commentsList = new Nut.View.commentsList({
                                    model: model,
                                    detailView: false,
                                    modal: true,
                                });
                                commentsList.render();
                                Modal.$el.container
                                    .find('.activity-comments-list')
                                    .html(commentsList.$el);
                            },
                        });
                    }
                },
                validate: function(response) {
                    Modal.close();
                    Form.handleValidationResult(
                        response.messages,
                        options.openerElement ? options.openerElement : Modal.getCurrentCard()
                    );
                },
                error:
                    options.error ||
                    function(response) {
                        Modal.close();
                        var message =
                            response && response.message
                                ? 'Error: ' + response.message
                                : 'Sorry, an unknown error occured while communicating with the server.';
                        Nut.alert(message);
                    },
            });

            this.addBlankCard();
            this.startKillingMousewheel();

            return this;
        },
        openFromElement: function(element, options) {
            element = $(element);
            var entityName = element.get(0).className.match(/modal-opener-(\w+)/)[1];

            // there's some annoying nesting of click targets right now
            // probably a better way to get the relevant element
            if (element.is('.icon, .activity-content')) element = element.parent();
            if (element.is('.activity-sidebar')) element = element.parent();

            var id = element.data('entityId'),
                url = '/' + entityName + '/view/' + id,
                urlData = element.data('urlData');

            //adds anchor to url for commenting scrolling
            if (element.data('anchor')) {
                url += element.data('anchor');
            }

            options = $.extend({}, {url: url, urlData: urlData}, options);
            Modal.open(options);
        },

        cardDidLoad: function(card) {
            card.css({height: Modal.getHeight()});

            // set the height of the inner part of the modal explicitly, for overflow scroll
            $('.modal-card-body').each(function() {
                $(this).css('height', 0);
                $(this).css(
                    'height',
                    $(this)
                        .parent()
                        .height() -
                        Math.sum([
                            $(this)
                                .siblings('.modal-card-header')
                                .innerHeight(),
                            $(this)
                                .siblings('.modal-card-buttonbar')
                                .innerHeight(),
                        ]) -
                        $(this).innerHeight()
                );
            });

            card.trigger(Modal.events.cardDidLoad);

            Form.init.updateAppearances(card);
        },

        isOpen: function() {
            return Modal.$el.container.is(':visible');
        },
        //helper function to split out duplicated functionality for closing modals
        //necessary in order to have a function that doesn't reset the url and share code between both closing funcs
        closeHelper: function(noConfirm) {
            // now fire an event to make sure we can close
            var willClose = $.Event(Modal.events.willClose);
            $('body').trigger(willClose, noConfirm);
            if (willClose.isDefaultPrevented()) return false;

            // okay, fade out
            Modal.$el.container.fadeOut(Modal.opt.speed.hide, function() {
                Modal.clear();
            });
            $('#modal-wrapper').css({
                width: '0px',
                height: '0px',
            });

            $('body').trigger(Modal.events.didClose); // note that the animation is still occuring here.

            Modal.stopKillingMousewheel();
            //return Modal;
        },
        close: function(noConfirm) {
            Modal.closeHelper(noConfirm);
            Modal.resetPageUrl();
            return Modal;
        },
        closeWithoutResetting: function(noConfirm) {
            Modal.closeHelper(noConfirm);
            return Modal;
        },
        clear: function() {
            return this.removeAllCards();
        },
        resetPageUrl: function() {
            if (window.history) {
                window.history.replaceState({}, {}, Modal.pageUrl);
            }

            $.bbq.pushState({modal: null});
        },
        startKillingMousewheel: function() {
            $('body').mousewheel(function(e, delta, deltaX, deltaY) {
                if (!$(e.target).closest('.modal-card').length) {
                    // they're scrolling somewhere outside of the modal, but it could be another popup (i.e. timepicker)
                    // ideally such an element would have a class like "scrollable," but for now i'm just whitelisting
                    // timepicker
                    if ($(e.target).closest('.time-picker').length) return true;
                    if ($(e.target).closest('.select2-results').length) return true;

                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                // kill mousewheel events from bubbling to the body when scrolling in a modal
                var card = Modal.getCurrentCard().find('.modal-card-body:visible'),
                    top = card.scrollTop();

                if (deltaY > 0 && top - deltaY <= 0) {
                    card.scrollTop(0);
                    e.preventDefault();
                    e.stopPropagation();
                } else if (
                    deltaY < 0 &&
                    card[0].scrollHeight - card.scrollTop() + deltaY <= card.innerHeight()
                ) {
                    card.scrollTop(card.get(0).scrollHeight - card.height());
                    e.preventDefault();
                    e.stopPropagation();
                }
            });
        },
        stopKillingMousewheel: function() {
            $('body').unmousewheel();
        },
    };
})(jQuery);
Activity = Nut.modules.Activity = (function($) {
    return {
        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        events: {
            DidSubmitSuccessfully: 'DidSubmitSuccessfully',
        },

        init: {
            setUpEvents: function() {
                $(document)
                    .on('click', 'div.activity-quickcreate .action-flag', function() {
                        var qc = $(this).closest('.activity-quickcreate'),
                            isFlagged = qc.is('.state-flagged');

                        qc.toggleClass('state-flagged', !isFlagged);
                        qc.find('input[name=is_flagged]').val(!isFlagged ? 1 : 0);
                    })
                    .on(
                        'click',
                        'div.activity-quickcreate input[type=submit]:not(.state-disabled)',
                        function() {
                            $(this).addClass('state-disabled');
                            Activity.doSubmit($(this).closest('.activity-quickcreate'));
                        }
                    )
                    .on(
                        'click',
                        'div.popover-activity .action-submit:not(.state-disabled)',
                        function() {
                            $(this).addClass('state-disabled');
                            Activity.doSubmit(
                                $(this)
                                    .closest('.popover')
                                    .find('.activity-quickcreate')
                            );
                        }
                    )

                    // update min date for the "To" date when "From" date is changed, since the "To" date
                    // must always be before the "From"
                    .on('EipDidSave', '.activity-date-from', function(event) {
                        var container = $(this).closest('.activity-quickcreate').length
                                ? $(this).closest('.activity-quickcreate')
                                : $(this).closest('ul'),
                            fromDate = $('.eip-input', this).datepicker('getDate'),
                            toDateEip = container.find('.activity-date-to').data('eip');

                        if (typeof toDateEip !== 'undefined') {
                            toDateEip.setMinDate(fromDate, true);
                            toDateEip.setDate(fromDate); // @TODO maybe this should only happen if from/to date were originally the same
                        }
                    })

                    // tether the to / from times
                    .on('EipDidSave', '.activity-time-from, .activity-time-to', function(event) {
                        var container = $(this).closest('.activity-quickcreate').length
                                ? $(this).closest('.activity-quickcreate')
                                : $(this).closest('ul'),
                            fromEip = container.find('.activity-time-from').data('eip'),
                            toEip = container.find('.activity-time-to').data('eip'),
                            fromDate = container.find('.activity-date-from .value').text(),
                            toDate = container.find('.activity-date-to .value').text(),
                            duration = null;

                        if (!toDate) toDate = fromDate; // in quick-create we don't even have a to-date picker
                        if (fromDate != toDate) return; // no smarts for multi-day events

                        // be sure that we've instantiated both inputs
                        if (!fromEip.inputElements.length) fromEip.prepareInputs();
                        if (!toEip.inputElements.length) toEip.prepareInputs();

                        var fromTimepicker = $.timePicker(fromEip.inputElements[0]),
                            toTimepicker = $.timePicker(toEip.inputElements[0]);

                        if ($(this).is('.activity-time-from')) {
                            var oldFromTime = $(fromEip.inputElements[0]).data(
                                'eip-original-value-time'
                            );
                            // If the user cleared out the value, just reset to the original.
                            var newFromTime = fromTimepicker.getTime();
                            if (!newFromTime) {
                                fromTimepicker.setTime(new Date(oldFromTime));
                            } else {
                                // Move the to time so the duration doesn't change and isn't negative
                                duration = toTimepicker.getTime() - oldFromTime;
                                if (duration < 0) duration = 15 * 60 * 1000;
                                toTimepicker.setTime(new Date(newFromTime.getTime() + duration));
                            }
                        } else {
                            // to time was changed.
                            var oldToTime = $(toEip.inputElements[0]).data(
                                'eip-original-value-time'
                            );
                            // If the user cleared out the value, just reset to the original.
                            var newToTime = toTimepicker.getTime();
                            if (!newToTime) {
                                toTimepicker.setTime(oldToTime);
                            } else {
                                // check that it's not behind the from time
                                duration = newToTime - fromTimepicker.getTime();
                                if (duration < 0) {
                                    duration = 15 * 60 * 1000;
                                    fromTimepicker.setTime(
                                        new Date(toTimepicker.getTime().getTime() - duration)
                                    );
                                }
                            }
                        }
                    });
            },
        },

        doSubmit: function(qc) {
            Nut.ajax.post({
                url: '/activity/quick-create.json',
                data: Activity.getData(qc),
                success: function(response) {
                    Activity.didSubmit(qc, response);
                },
            });
        },

        getData: function(qc) {
            var data = qc.serializeArrayToObject();

            data.id = 'new';

            $.extend(data, qc.getEipData());
            delete data.value;

            var participant = qc.find('select[name=participants] option:selected');
            if (participant.val() == 'precedingParticipants') {
                data.participants = [participant.val()];
            } else {
                data.participants = [
                    'self',
                    {
                        entityType: participant.data('entityName'),
                        id: participant.data('entityId'),
                    },
                ];
            }

            data.endTime_date = data.startTime_date;

            return data;
        },

        didSubmit: function(qc, response) {
            Lead.reloadActivities();
            this.refreshActivities(response.activityId);

            if (qc.parents('.popover').length) {
                // this is a big ugly hack for now :(
                var tab = qc.visualClosest('.modal-tab-activity'),
                    followUp = tab.find('.activity-header-followup');
                followUp.html(
                    '<a href="#" data-entity-id="' +
                        response.activityId +
                        '" class="modal-opener-activity"><span>Follow up: </span>' +
                        _.escape(response.activityName) +
                        '</a>'
                );

                tab.addClass('state-hasfollowup');

                Popover.close();
            }
        },

        refreshActivities: function(id) {
            $(window).trigger(Activity.events.DidSubmitSuccessfully, id);
        },
    };
})(jQuery);
Audiences = Nut.modules.Audiences = (function($) {
    return {
        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        init: {
            setUpEvents: function() {
                $('.audiences-bar')
                    // adjust height of endcap elements for proper wrappinating
                    .on('keyup', 'input.audiences', function() {
                        $(this)
                            .find('.audiences-bar-label, .audiences-bar-link')
                            .css({height: ''})
                            .css({
                                height: $(this)
                                    .closest('.audiences-activated')
                                    .find('ul')
                                    .height(),
                            });
                    });
            },
            updateAppearances: $.noop,
        },

        setUpAudienceAreas: function() {
            $(this).each(function() {
                Audiences.setUpOneAudienceArea($(this));
            });
        },

        setUpOneAudienceArea: function(area) {
            if (area.is('.audiences-activated')) return;

            area.addClass('audiences-activated');
            var audienceInput = Audiences.getSelectInputElement(area).select2({
                multiple: true,
                query: function(query) {
                    query.callback({
                        results: _.chain(Audiences.getAvailableAudiences())
                            .filter(function(cmodel) {
                                if (cmodel.isWebFX) {
                                    return false;
                                }

                                return (
                                    cmodel.name
                                        .toUpperCase()
                                        .trim()
                                        .indexOf(query.term.toUpperCase().trim()) >= 0
                                );
                            })
                            .map(function(availableaudiences) {
                                return {id: availableaudiences.id, text: availableaudiences.name};
                            })
                            .value(),
                    });
                },
                initSelection: function(input, callback) {
                    var ids = input.val().split(','),
                        data = _(ids).map(function(id) {
                            var cmodel = Nut.managedCollections.Audiences.get(id),
                                name = cmodel ? cmodel.get('name') : '…';
                            return {id: id, text: name};
                        });
                    callback(data);
                },
                placeholder: 'Add audiences…',
                formatResult: Audiences.audienceFormatResult,
                width: '100%',
                // Only using this for bulk-edit right now.
                containerCssClass: 'select2-filter select2-filter-bulk-edit',
            });

            // update modal appearances on audience changes, for geometry
            if (area.closest('#modal').length) {
                area.on('change', 'input.audiences', Modal.init.updateAppearances);
            }
        },

        getAvailableAudiences: function() {
            return Nut.managedCollections.Audiences.map(function(model) {
                return model.toJSON();
            });
        },

        getAudienceIdsInAudienceBar: function(audienceBar) {
            var audienceData = Audiences.getSelectInputElement(audienceBar).select2('data'),
                audienceIds = _(audienceData).pluck('id');

            return audienceIds;
        },

        getSelectInputElement: function(audienceBar) {
            return audienceBar.find('input.audiences-select2');
        },

        audienceFormatResult: function(item) {
            var containerWidth =
                $(this.element)
                    .closest('.audiences-bar')
                    .width() > 200
                    ? '200px'
                    : '100%';
            return (
                "<div class='audiences-result' style='width:" +
                containerWidth +
                ";'>" +
                _.escape(item.text) +
                '</div>'
            );
        },
    };
})(jQuery);
Popover = Nut.modules.Popover = (function($) {
    return {
        opt: {
            speed: {show: 150, hide: 150},
            speedLoadingIndicator: {show: 150, hide: 0},
            callback: $.noop,
            openClass: 'state-popover-opened',
            topMargin: 30,
        },

        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        init: {
            setUpEvents: function() {
                $(document).on('click', '.popover-close, .popover .action-cancel', function(event) {
                    $(event.target)
                        .parents('.popover')
                        .removeClass('state-persist');
                    Popover.close(true);
                });

                $(document).on('keydown', function(event) {
                    if (event.keyCode == Nut.keys.ESC && Popover.isOpen()) {
                        Popover.close();
                    }
                });

                // openers (not all will be ajax'd)
                // required metadata: url, appendTo
                // appendTo should be a string that will be evaluated with closest()
                $(document).on('click', '.popover-opener-ajax', function(event) {
                    var $this = $(this);
                    if ($this.is('.state-disabled')) return;
                    if (event.isPropagationStopped()) return;
                    event.stopPropagation();
                    var url = $(this).data('url');
                    var targetSelector = $this.data('target-selector');
                    var $target = targetSelector ? $(targetSelector) : $this;

                    Popover.load({
                        url: url,
                        appendTo: Popover._getAppendToForElement($target),
                        error: function(error) {
                            const hashRegex = /You have reached your plan limit for (markets|custom fields)/;
                            const found = error.message.match(hashRegex);

                            if (found && found[1]) {
                                var limitType = {
                                    markets: 'MARKETS',
                                    ['custom fields']: 'CUSTOM_FIELDS',
                                }[found[1]];

                                $(window).trigger(Nut.events.limits.limitWasExceeded, limitType);
                            }
                        },
                    });
                });

                // required metadata: template
                // template is the id of the template (i.e. 'help' for '#popover-template-help')
                // Note that the template must be included server side with FrontendTemplate()->add
                // It will be passed the triggering element as 'popover'
                $(document).on('click', '.popover-opener-template', function(event) {
                    event.preventDefault();
                    event.stopPropagation();
                    var $el = $(this),
                        templateId = 'template-popover-' + $el.data('template'),
                        template = Nut.getTemplate(templateId, 'popover'),
                        popover = template($el);
                    Popover.open(popover, Popover._getAppendToForElement($el));
                });

                // specific shorthand openers
                $(document).on(
                    'click',
                    '.popover-opener-user, .popover-opener-team, .popover-opener-delay',
                    function(event) {
                        if ($(this).is('.state-focused')) return;
                        if (event.isPropagationStopped()) return;
                        event.stopPropagation();

                        var entityName = $(this)
                            .attr('class')
                            .match(/popover-opener-([^ ]+)/)[1];

                        var data = {};

                        if ('assignType' in $(this).data()) {
                            data.assignType = $(this).data('assignType');
                        }

                        var url =
                            '/popover/' +
                            entityName +
                            '/' +
                            $(this).data('entityId') +
                            '.json?' +
                            $.param(data);

                        Popover.load({
                            url: url,
                            appendTo: Popover._getAppendToForElement($(this)),
                        });
                    }
                );

                // inline opener
                // required metadata: container, popover <-- both of which are selectors to find the popover
                $(document).on('click', '.popover-opener-inline', function(event) {
                    Popover.openForInlineElement($(this));
                });

                return this;
            },
            visualClosest: function() {
                // extend jquery with a (pared-down) synonym of closest() that is aware of popovers
                // and treats them like they are embedded in the DOM tree

                $.fn.visualClosest = function(selector) {
                    if (!$(this).parents('.popover')) return $.fn.closest.apply(this, arguments);

                    // first check if the closest() is actually in the popover
                    if ($(this).closest(selector).length) return $(this).closest(selector);

                    var popoverAnchor = $('.popover:visible').data('appendTo');

                    return $(popoverAnchor).closest(selector);
                };

                return this;
            },
            updateAppearances: $.noop,
        },

        loadingStart: function(appendTo, orientation) {
            var loading = $('<div class="popover-loading"><div class="loading-sprite" /></div>')
                .appendTo('body')
                .show();
            Nut.animateLoadingSprite(loading.find('.loading-sprite'));

            // offsets are to mesh perfectly w/ the to-appear popover
            if (orientation == 'south') {
                loading.addClass('orientation-south').position({
                    of: appendTo,
                    my: 'center top',
                    at: 'center bottom',
                    offset: '0 -5',
                });
            } else if (orientation == 'east') {
                loading.addClass('orientation-east').position({
                    of: appendTo,
                    my: 'left center',
                    at: 'right center',
                    offset: '-5 0',
                });
            } else {
                loading.position({
                    of: appendTo,
                    my: 'center bottom',
                    at: 'center top',
                    offset: '0 5',
                });
            }

            loading.hide().fadeIn(this.opt.speedLoadingIndicator.show);
        },
        loadingStop: function(appendTo) {
            $('.popover-loading').fadeOut(this.opt.speedLoadingIndicator.hide, function() {
                $(this).remove();
            });
        },

        // required:
        // @url - request url
        // @appendTo - element to be used for popover's reletive positioning
        //
        // opt:
        // @data - request data
        // @loadingOrientation - direction to position the loading div while the request completes
        // @noLoadingIndicator - if set, don't show the loading div
        // @offset - additional offset against appendTo element
        // @nubOffset - additional offset of the nub arrow to the popover
        // @success - callback on success (called after the popover has been opened in Popover.open)
        // @error - callback on error
        load: function(options) {
            var loadingOrientation = options.loadingOrientation;
            if (!loadingOrientation) {
                if (
                    options.appendTo.find('.popover-opener-south').length > 0 ||
                    options.appendTo.is('.popover-opener-south')
                ) {
                    loadingOrientation = 'south';
                } else {
                    loadingOrientation = 'north';
                }
            }

            if (!options.noLoadingIndicator)
                Popover.loadingStart(options.appendTo, loadingOrientation);

            var ajaxOptions = {
                url: options.url,
                success: function(result) {
                    Popover.loadingStop(options.appendTo);
                    Popover.open(result.html, options.appendTo, options);
                },
                validate: function(result) {
                    Popover.loadingStop(options.appendTo);
                    Popmini.error(result.messages.join(', '), {attachTo: options.appendTo});
                },
                error: function(result) {
                    Popover.loadingStop(options.appendTo);
                    if (options.error) options.error(result);
                },
            };

            if (options.data) ajaxOptions.data = options.data;

            Nut.ajax.get(ajaxOptions);
        },
        openForInlineElement: function(element) {
            var container = $(element).closest($(element).data('container')),
                popover = container.find($(element).data('popover'));

            // if exact popover is already open, treat this as a close click
            var isAlreadyOpen = popover.is(':visible');
            Popover.close(true, true);

            if (isAlreadyOpen) return;

            var options = {
                noDetach: true,
            };
            Popover.open(popover, Popover._getAppendToForElement($(element)), options);
        },
        open: function(popover, appendTo, options) {
            popover = $(popover);
            appendTo = $(appendTo);
            options = options || {};

            // close any existing
            Popover.close();

            // record these so they can be referenced later
            popover.data('appendTo', appendTo);

            if (!options.noDetach) {
                popover.appendTo('body');
            }

            // need to show it for accurate positioning
            popover.show();

            // we remove inline styles when positioning, in case the popover is positioned multiple times.
            popover.css('left', null).css('top', null);
            popover
                .find('.popover-nub')
                .css('left', null)
                .css('top', null);

            //hovering popover's are excluded from height restrictions
            if (
                popover.height() > $(window).height() * 0.9 &&
                popover.hasClass('kill-scroll') &&
                !popover.hasClass('fixed-height')
            ) {
                popover.css('height', function() {
                    return $(window).height() * 0.8;
                });
                //remove nub from popover since it's more likely than not now in the wrong place.
                popover.find('.popover-nub').hide();
                Popover.startKillingMousewheel();
            }

            //This one doesn't care care about the height. First implemented for the notifications popover.
            if (popover.hasClass('kill-scroll-always')) {
                Popover.startKillingMousewheel();
            }

            // if the popover is attached to the body, reposition
            if (popover.is('.orientation-north')) {
                popover.position({
                    my: 'center bottom',
                    at: 'center top',
                    of: appendTo,
                    offset: options.offset ? options.offset : '0 2',
                    collision: 'fit',
                });
            } else if (popover.is('.orientation-south')) {
                popover.position({
                    my: 'center top',
                    at: 'center bottom',
                    of: appendTo,
                    offset: options.offset ? options.offset : '0 -2',
                    collision: 'none',
                });
            } else if (popover.is('.orientation-south-west')) {
                popover.position({
                    my: 'right top',
                    at: 'center bottom',
                    of: appendTo,
                    offset: options.offset ? options.offset : '0 -2',
                    collision: 'none',
                });
            } else if (popover.is('.orientation-south-east')) {
                popover.position({
                    my: 'left top',
                    at: 'center bottom',
                    of: appendTo,
                    offset: options.offset ? options.offset : '-21 0', // offset to match loader
                    collision: 'none',
                });
            } else if (popover.is('.orientation-east')) {
                popover.position({
                    my: 'left center',
                    at: 'right center',
                    of: appendTo,
                    offset: options.offset ? options.offset : '2 0',
                    collision: 'none',
                });
                popover.find('.popover-nub').position({
                    my: 'right center',
                    at: 'left center',
                    of: popover,
                    offset: options.nubOffset ? options.nubOffset : '1 0',
                });
            } else if (popover.is('.orientation-west')) {
                popover.position({
                    my: 'right center',
                    at: 'left center',
                    of: appendTo,
                    offset: options.offset ? options.offset : '-4 0',
                    collision: 'none',
                });
                popover.find('.popover-nub').position({
                    my: 'left center',
                    at: 'right center',
                    of: popover,
                    offset: options.nubOffset ? options.nubOffset : '-1 0',
                });
            }

            if (parseInt(popover.css('top')) < this.opt.topMargin) {
                popover.css('top', this.opt.topMargin + 'px');
            }

            // remove any existing off-click popover listener on the body, since it'd immediately close this popover
            // when the click finishes bubbling up. we rebind it after the popover open animation
            $('body').off('click.popoverBody');

            popover.hide().fadeIn(this.opt.speed.show, function() {
                $('body').on('click.popoverBody', popover, Popover.bodyWasClicked);

                Form.init.updateAppearances(popover);
                popover.find('input[type="text"]:first').select();
            });

            if (!options.noScrollIntoView && $(document).scrollTop() > popover.offset().top)
                $('body').animate(
                    {
                        scrollTop: popover.offset().top - 17,
                    },
                    {
                        duration: 300,
                    }
                );

            popover
                .find('.select-mini')
                .filter(':not(:has(div.selector))')
                .find('select')
                .uniform();

            if (options.success) options.success(popover);

            $(appendTo).trigger(Nut.events.popover.wasOpened);
            $(appendTo).addClass(Popover.opt.openClass);

            return this;
        },
        isOpen: function() {
            return $('.popover').is(':visible');
        },
        close: function(immediately, bodyEvent) {
            var shouldPersistBodyClickEvent,
                immediately = typeof immediately != 'undefined' && immediately;

            $('.popover:visible')
                .not('.react-popover')
                .each(function() {
                    var $this = $(this);

                    if ($this.hasClass('state-persist')) return;
                    if ($this.hasClass('persist-non-body') && !bodyEvent) {
                        shouldPersistBodyClickEvent = true;
                        return;
                    }

                    if ($this.data('appendTo')) {
                        $this
                            .data('appendTo')
                            .trigger(Nut.events.popover.wasClosed)
                            .removeClass(Popover.opt.openClass);
                    }

                    if ($this.hasClass('kill-scroll')) Popover.stopKillingMousewheel();

                    if (immediately) {
                        $this.hide();
                        if ($this.parent().is('body')) $this.remove();
                    } else {
                        $this.fadeOut(Popover.opt.speed.hide, function() {
                            if ($this.parent().is('body')) $this.remove();
                        });
                    }
                });

            if (!shouldPersistBodyClickEvent) {
                $('body').off('click.popoverBody');
            }

            return this;
        },
        bodyWasClicked: function(event) {
            if (event.isTrigger) return true; // some dumb things (like ProcessWasReloaded) click on arbitrary shit

            var target = $(event.target);

            if (event.data && event.data.hasClass('persist-non-body')) {
                if (target.closest('.modal-card').length) return;
            }

            if (
                target.closest('.ui-popover, .popover, #popmini, .ui-datepicker, .time-picker')
                    .length
            )
                return true;
            if (!target.closest('body').length) return true; // this means the item was removed, so we can't know if it was in a popover -- noop

            Popover.close(false, 'bodyEvent');
        },
        // returns a jQuery object to which the popover should be visually attached.
        // uses appendTo metadata if provided, else returns element
        _getAppendToForElement: function(element) {
            var appendTo = $(element);
            if (appendTo.data('appendTo')) {
                var selector = appendTo.data('appendTo');

                if (appendTo.find(selector).length) {
                    // look in, first
                    appendTo = appendTo.find(selector);
                } else if (appendTo.closest(selector).length) {
                    // look up
                    appendTo = appendTo.closest(selector);
                } else if ($(selector).length) {
                    // look everywhere
                    appendTo = $(selector);
                } else {
                    Nut.log('Unable to find specified anchor for popover: ' + selector);
                }
            }

            return appendTo;
        },
        startKillingMousewheel: function() {
            $('body').mousewheel(function(e, delta, deltaX, deltaY) {
                // .popover-middle contains the actual scrollable content within the popover
                var popover = $('.popover.kill-scroll:visible .popover-middle');
                var top = popover.scrollTop();

                if ($(e.target).closest(popover).length) {
                    if (deltaY > 0 && top - deltaY <= 0) {
                        popover.scrollTop(0);
                        e.preventDefault();
                        e.stopPropagation();
                    } else if (
                        deltaY < 0 &&
                        popover[0].scrollHeight - popover.scrollTop() + deltaY <=
                            popover.innerHeight()
                    ) {
                        popover.scrollTop(popover.get(0).scrollHeight - popover.height());
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }
            });
        },
        stopKillingMousewheel: function() {
            $('body').unmousewheel();
        },
    };
})(jQuery);
Popmini = Nut.modules.Popmini = (function($) {
    return {
        opt: {
            speed: {show: 150, hide: 300},
            orientations: ['orientation-north', 'orientation-north-east', 'orientation-north-west'],
        },

        defaults: {
            autoHide: 8000, // in ms, 0 to disable
            orientation: 'north',
            classes: '',
            attachTo: 'body',
            insertAfter: '',
            offsetTop: -10, // for triangle nub
        },

        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        init: {
            setupDefaults: function() {
                Popmini.defaults.yesText = $('#popmini .action-yes span').text();
                Popmini.defaults.noText = $('#popmini .action-no span').text();
            },
            updateAppearances: $.noop,
        },

        // returns the newly created popmini jquery dom element
        error: function(message, options) {
            options = $.extend({}, Popmini.defaults, options);
            options.classes = options.classes + ' type-error';

            return Popmini.open(message, options);
        },

        // returns the newly created popmini jquery dom element
        // callback is called w/ 2 bool parameters of whether the user clicked yes or the yes alternate
        question: function(message, options, callback) {
            options = $.extend({}, Popmini.defaults, options);
            options.classes = options.classes + ' type-question';
            options.autoHide = false;

            var p = Popmini.open(message, options);

            p.on('click', '.action-yes, .action-no', function(event) {
                // popmini element now inserted into other containers, so make sure we don't
                // propagate clicks outside of it
                event.stopPropagation();
                event.preventDefault();

                var $this = $(this);
                callback($this.is('.action-yes'), $this.is('.action-alternate'));
                Popmini.hide(p);
            });

            return p;
        },

        // returns the newly created popmini jquery dom element
        open: function(message, options) {
            var p = $('#popmini')
                .clone()
                .attr('id', '');
            p.on('click', '.action-close', function(event) {
                // popmini element now inserted into other containers, so make sure we don't
                // propagate clicks outside of it
                event.stopPropagation();
                event.preventDefault();

                Popmini.hide(p);
            });

            // for triangle nub when oriented below attachTo element
            if (
                _.isEmpty(options.offsetTop) &&
                options.orientation &&
                ~options.orientation.indexOf('south')
            )
                options.offsetTop = 10;

            options = $.extend({}, Popmini.defaults, options);

            p.find('.action-yes span').text(options.yesText);
            p.find('.action-no span').text(options.noText);

            p.addClass('popmini');
            if (options.orientation) p.addClass('orientation-' + options.orientation);
            p.addClass(options.classes);

            p.find('.message').text(message);

            if (options.alternateYesAction) {
                p.find('.buttons').prepend(options.alternateYesAction);
            }

            var horizontalOffset = 0,
                my,
                at,
                within;

            if (options.orientation == 'north-west') horizontalOffset = -120;
            if (options.orientation == 'north-east') horizontalOffset = 120;
            if (options.orientation == 'east') horizontalOffset = 10;
            if (options.orientation == 'west') horizontalOffset = -10;

            if (options.within) within = options.within;

            if (options.my && options.at) {
                my = options.my;
                at = options.at;
            } else if (~options.orientation.indexOf('north')) {
                // north
                my = 'center bottom';
                at = 'center top';
            } else if (~options.orientation.indexOf('east')) {
                my = 'left center';
                at = 'right top';
            } else if (~options.orientation.indexOf('west')) {
                my = 'right center';
                at = 'left top';
            } else {
                my = 'center top';
                at = 'center bottom';
            }

            if (options.insertAfter) $(options.insertAfter).after(p);
            else $('body').append(p);

            p.show()
                .position({
                    my: my,
                    at: at,
                    of: options.attachTo,
                    offset: horizontalOffset + ' ' + options.offsetTop,
                    within: within,
                })
                .hide();

            p.fadeIn(Popmini.opt.speed.show, Popmini.didAppear);

            if (!Modal.isOpen() && !Popmini.isVisible(p)) {
                $.scrollTo(p, 400);
            }

            if (options.autoHide) {
                p.currentTimeoutId = window.setTimeout(Popmini.hide, options.autoHide, p);
            }

            return p;
        },

        didAppear: $.noop,

        // expects a jquery element that can be removed from the dom
        hide: function(popmini) {
            window.clearTimeout(popmini.currentTimeoutId);
            popmini.fadeOut(Popmini.opt.speed.hide, function() {
                popmini.remove();
            });
        },

        // expects a jquery element
        isVisible: function(popmini) {
            var viewTop = $(window).scrollTop();
            var viewBottom = viewTop + $(window).height();
            var popminiTop = popmini.offset().top;
            var popminiBottom = popminiTop + popmini.height();
            return (
                popminiBottom >= viewTop &&
                popminiTop <= viewBottom &&
                popminiBottom <= viewBottom &&
                popminiTop >= viewTop
            );
        },
    };
})(jQuery);
Card = Nut.modules.Card = (function($) {
    return {
        opt: {
            popoverMouseoutDelay: 700, // after leaving card and hoverview, wait this long before closeing
            popoverMouseinConflictDelay: 80, // if another hoverview is open, wait this long before opening a new one, in case it was a mis-hover
        },

        mouseIsOverPopoverTarget: false,

        go: function() {
            for (var fn in this.init) {
                this.init[fn].apply(this);
            }
        },

        init: {
            // hopefully we can get rid of this someday
            updateAppearances: function() {
                $('.float-area').each(Card.updateCardAreas);
            },
            setUpEvents: function() {
                $('body').on(Nut.events.global.pageWasChanged, function() {
                    Card.tearDown();
                });

                // listen for changes to entities, and reload cards as necessary
                $('body').on(
                    [Nut.events.global.entityWasDeleted, Nut.events.global.entityWasEdited].join(
                        ' '
                    ),
                    Card.entityWasUpdated
                );

                // special hover JS for accounts, since state has to bubble up from account-top to entire container
                $(document).on(
                    'mouseenter mouseleave',
                    'div.account-card-container .tile-account-top',
                    function(event) {
                        var isMouseover = event.type == 'mouseenter';
                        $(this)
                            .closest('.account-card-container')
                            .toggleClass('state-hover', isMouseover);
                    }
                );

                var closeHoverviewPopoverSoon = function() {
                    if (Card.mouseIsOverPopoverTarget) return;
                    if ($('div.popover-hoverview').find('.state-editing').length) return;
                    if ($('.popover:not(.popover-hoverview):visible').length) return; // currently-open popover is not a hoverview

                    $(document).off('mouseenter.hoverviewWatch mouseleave.hoverviewWatch');

                    Popover.close();
                };

                var openHoverviewPopover = function(card) {
                    var cardData = card.data(),
                        existingHoverviewData = $('.popover-hoverview').data(),
                        appendTo;

                    // reopening the same hoverview, noop
                    if (
                        existingHoverviewData &&
                        cardData.entityName == existingHoverviewData.entityName &&
                        cardData.entityId == existingHoverviewData.entityId
                    ) {
                        return;
                    }

                    if (card.is('.mention-rendered') || card.is('.hoverview')) {
                        // append the hoverview directly to a mention
                        appendTo = card;
                    } else {
                        appendTo = card.is('.account-card-container')
                            ? card.find('.tile-account-top')
                            : card.find('.tile-contact');
                    }

                    // don't open hoverview if another popover is open
                    if ($('.popover:not(.popover-hoverview):visible').length) return true;

                    // start watching for whether mouse is on or off the hoverview or any popmini (ideally that'd be restrained)
                    $(document).on(
                        'mouseenter.hoverviewWatch mouseleave.hoverviewWatch',
                        'div.popover-hoverview, #popmini',
                        function(event) {
                            if (event.type == 'mouseenter') {
                                Card.mouseIsOverPopoverTarget = true;
                            } else {
                                Card.mouseIsOverPopoverTarget = false;

                                window.setTimeout(
                                    closeHoverviewPopoverSoon,
                                    Card.opt.popoverMouseoutDelay
                                );
                            }
                        }
                    );

                    Popover.load({
                        url: '/popover/hoverview.json',
                        appendTo: appendTo,
                        noLoadingIndicator: true,
                        noScrollIntoView: true,
                        offset: '14 0',
                        data: cardData,
                        success: function(card) {
                            if (!Card.mouseIsOverPopoverTarget) Popover.close(true);
                            if (
                                NutshellFrontendSettings.phonecaller_type &&
                                NutshellFrontendSettings.phonecaller_type != 'default'
                            )
                                Card.handlePhoneCaller(card);
                        },
                    });
                };

                // hoverview binding
                $(document).on(
                    'mouseenter mouseleave',
                    'div.account-card-container.tile-filled .tile-account-top, div.contact-card-container.tile-filled .tile-contact, .mention-rendered, .hoverview',
                    function(event) {
                        // exclude tiles in other popovers
                        if ($(this).closest('.popover').length) return;

                        var card = $(this).closest(
                            '.tile-container, .mention-rendered, .hoverview'
                        );

                        if (event.type == 'mouseenter') {
                            Card.mouseIsOverPopoverTarget = true;

                            if (!Popover.isOpen()) {
                                // immediately open
                                openHoverviewPopover(card);
                            } else {
                                var entranceTimeout = window.setTimeout(function() {
                                    openHoverviewPopover(card);
                                }, Card.opt.popoverMouseinConflictDelay);

                                card.data('entranceTimeout', entranceTimeout);
                            }
                        } else {
                            Card.mouseIsOverPopoverTarget = false;

                            window.clearTimeout(card.data('entranceTimeout'));
                            window.setTimeout(
                                closeHoverviewPopoverSoon,
                                Card.opt.popoverMouseoutDelay
                            );
                        }
                    }
                );

                $(document).on(
                    'click',
                    '.peep-cards .action-load-more-cards:not(.state-loading)',
                    Card.loadMoreCards
                );

                // card controls
                $(document)
                    .on('click', '.tile-container .action-remove', function(event) {
                        var card = $(this).closest('.tile-container'),
                            container = card.parent();

                        event.preventDefault();
                        event.stopPropagation();

                        if (card.is('.state-half-baked')) {
                            // we're a half baked card, only revert, don't ajax
                            card.fadeOut(function() {
                                $(this).remove();
                                container.trigger(Nut.events.card.transientWasDeleted);
                            });
                        } else {
                            var wasDeleted = $.Event(Nut.events.card.wasDeleted);
                            wasDeleted.card = card;

                            var confirmText = $.trim(
                                $(this)
                                    .find('.confirm')
                                    .text()
                            );
                            // @TODO convert to popmini confirm?
                            if (confirmText.length && !Nut.confirm(confirmText)) return true;

                            if (card.find('input[name=entityName]').length) {
                                // remove mapping
                                var inputs = card.find('input');

                                if (card.is('.account-card-container')) {
                                    inputs = inputs.filter(function() {
                                        return !$(this).closest('.contact-enclosure').length;
                                    });
                                }

                                Nut.ajax.post({
                                    url: '/mapping/delete-map.json',
                                    data: inputs.serialize(),
                                    success: function(response) {
                                        card.fadeOut(function() {
                                            if ($(response.html).is('.lead-sidebar-top')) {
                                                Lead.reloadCardEnclosure(response.html);
                                            } else {
                                                $(this).remove();
                                                container.trigger(wasDeleted);
                                            }
                                        });
                                    },
                                });
                            } else {
                                // this card is only a placeholder, just fade it out
                                card.fadeOut(function() {
                                    $(this).remove();
                                    container.trigger(wasDeleted);
                                });
                            }
                        }
                    })
                    .on('click', '.tile-container .action-star-contact', function() {
                        var card = $(this).closest('.tile-container'),
                            starContainer = $(this);
                        if (card.find('input[name=entityName]').length) {
                            var inputs = card.find('input');

                            starContainer.addClass('state-toggling');

                            Nut.ajax.post({
                                url: '/mapping/star-relationship.json',
                                data: inputs.serialize(),
                                success: function(response) {
                                    var newCard = $(response.html);
                                    newCard.find('.action-star-contact').addClass('state-toggling'); // our newborn star shouldn't immediately go into :hover
                                    card.replaceWith(newCard);

                                    // Now reload all the other visible cards
                                    // so that they unstar themselves.
                                    // but don't reload ourselves (since we literally just loaded, and we're messing around w/ hovers…)
                                    Card.refreshIndividualCards(newCard);
                                },
                            });
                        }
                    })
                    .on('mouseover', '.tile-container .action-star-contact', function() {
                        if (!$(this).is('.state-toggling')) {
                            // don't reinvoke the hover state if we're still hovering the same star having just toggled it
                            $(this).addClass('state-active');
                        }
                    })
                    .on('mouseout', '.tile-container .action-star-contact', function() {
                        $(this).removeClass('state-active state-toggling');
                    });

                // card forms
                $(document)
                    .on('focusin focusout', '.float-object-search-field input', function(event) {
                        $(this)
                            .parent('.float-object-search-field')
                            .toggleClass('state-focused', event.type == 'focusin');
                    })

                    .on('click', '.float-object-search-field', function() {
                        $(this)
                            .find('input')
                            .focus();
                    });

                // empty card area click creates a new card
                $(document).on('click', '.float-area div.empty-placeholder', function(event) {
                    var container = $(this).closest('.float-area'),
                        template = $(this).data('selector')
                            ? container.find($(this).data('selector'))
                            : container.find('.float-object-template');
                    //look to see if an empty card template has been created, if so
                    //do a noop to prevent multiple empties from being created
                    if (container.find('.state-half-baked').length !== 0) {
                        container
                            .find('input[name=name]')
                            .trigger('focusin')
                            .trigger('focus');
                        return;
                    }
                    var card = template
                        .clone(true)
                        .removeClass('float-object-template')
                        .addClass('state-half-baked')
                        .insertBefore(template)
                        .hide()
                        .fadeIn(function() {
                            container.trigger(Nut.events.card.transientWasAdded);
                            card.find('input[name=name]')
                                .trigger('focusin')
                                .trigger('focus');
                            // the trigger hack is necessary to trigger the live() autocomplete
                        });

                    card.parents('.account-card-container').addClass(
                        'tile-internal-contact-card-is-editing'
                    );

                    container.find('div.empty-placeholder').hide();
                });

                // update-as-you-type the "Create `andy fowler`" autocomplete option
                $(document).on('keydown paste', '.float-area input[name=name]', function() {
                    window.setTimeout(
                        $.proxy(function() {
                            var item = $(this)
                                    .closest('.tile-container')
                                    .find('li.float-object-list-create'),
                                val = $(this).data('autocomplete-as-typed');

                            item.find('span').text(val);

                            if (!val || val.length === 0) {
                                item.hide();
                            } else {
                                item.show();
                            }
                        }, this),
                        2
                    );
                });

                $(document).on(
                    [
                        Nut.events.card.wasAdded,
                        Nut.events.card.wasDeleted,
                        Nut.events.card.transientWasAdded,
                        Nut.events.card.transientWasDeleted,
                    ].join(' '),
                    '.float-area',
                    Card.updateCardAreas
                );

                $(document).on(Nut.events.card.wasAdded, '.tile-container', function() {
                    Form.init.updateAppearances($(this));
                });

                // set card autocomplete and save actions:
                var autocompleteOptions = {
                    addResultsAfter: '.float-object-list-create',
                    additionalFields: {
                        rows: 7,
                    },
                    keepOnBlur: false,
                    keepOnEmpty: true,
                    tabOnEnter: true,
                    getResultsList: function() {
                        return $(this)
                            .closest('.tile-container')
                            .find('ul');
                    },
                    getContainer: function() {
                        return $(this).closest('.tile-container');
                    },
                    onBlur: function() {
                        var allEmpty = true;
                        this.container.find('input:visible').each(function() {
                            if ($(this).val().length > 0) allEmpty = false;
                        });
                    },
                    onChoose: function() {
                        // runs in Autocomplete context.
                        this.options.doAddCard = _.bind(this.options.doAddCard, this);

                        // creating or adding (not opening modal)
                        var data = this.currentSelection.data('autocompleteResult'),
                            willBeAdded = $.Event(Nut.events.card.willBeAdded);

                        willBeAdded.resultData = data;

                        if (!data) data = this.container.find('input').serialize();

                        this.container.trigger(willBeAdded);
                        if (willBeAdded.isDefaultPrevented()) return;

                        this.options.doAddCard(data);
                    },
                    doAddCard: function(data) {
                        if (data.entityId) data = {relationshipId: data.entityId};
                        var container = this.container;

                        if (container.is('.product-card-container') && Lead.getId() == 'new') {
                            data.marketId = $('#marketId').val();
                        }

                        // when mapping a contact to a lead while inside an account card, this will ensure the
                        // contact is mapped to that account
                        var parentAccount = container.find('[name=parentAccount]').val();
                        if (parentAccount) data.parentAccount = parentAccount;

                        Nut.ajax.post({
                            url: container.data('addAction'),
                            data: data,
                            success: function(response) {
                                var $response = $(response.html);
                                if ($response.is('.lead-sidebar-top')) {
                                    Lead.reloadCardEnclosure($response);

                                    // hack to call cardWasAdded, even though the whole area was changed
                                    var type = Card.getCardEntityType(container),
                                        newestCard = $(
                                            '.column-accounts-contacts .' +
                                                type +
                                                '-card-container:visible:last'
                                        );

                                    newestCard.trigger(Nut.events.card.wasAdded);
                                } else {
                                    Card.appendCardToContainer($response, container.parent());
                                }
                            },
                            error: function(response) {
                                Form.handleValidationResult(response.messages, container);
                            },
                        });
                    },
                };

                // account cards autocomplete
                Autocomplete.setup(
                    'div.account-card-container .tile-account-top .form-autocomplete',
                    $.extend(
                        {
                            url: '/solr/find-potential-accounts.json',
                            resultToElement: function(result) {
                                var $region = result.primaryAddressRegion
                                    ? $(
                                          '<span class="region right-side">' +
                                              _.escape(result.primaryAddressRegion) +
                                              '</span>'
                                      )
                                    : null;

                                var $el = $(
                                    '<li><span class="name">' +
                                        _.escape(result.name) +
                                        '</span></li>'
                                );

                                if ($region) $el.append($region);

                                return $el;
                            },
                        },
                        autocompleteOptions
                    )
                );

                // contact cards autocomplete
                Autocomplete.setup(
                    'div.contact-card-container .form-autocomplete',
                    $.extend(
                        {
                            url: '/solr/find-potential-contacts.json',
                            resultElementText: function(result) {
                                return $(result)
                                    .find('.email-address')
                                    .text();
                            },
                            resultToElement: function(result) {
                                var $accountName =
                                    result.accounts && result.accounts[0]
                                        ? $(
                                              '<span class="account-name">' +
                                                  _.escape(result.accounts[0]) +
                                                  '</span>'
                                          )
                                        : null;
                                var $emailAddress = result.primaryEmailText
                                    ? $(
                                          '<span class="email-address right-side">' +
                                              _.escape(result.primaryEmailText) +
                                              '</span>'
                                      )
                                    : null;

                                var $el = $(
                                    '<li><span class="name">' +
                                        _.escape(result.name) +
                                        '</span></li>'
                                );

                                if ($accountName) $el.append($accountName);
                                if ($emailAddress) $el.append($emailAddress);

                                return $el;
                            },
                        },
                        autocompleteOptions
                    )
                );

                // product cards autocomplete
                Autocomplete.setup(
                    'div.product-card .form-autocomplete',
                    $.extend(
                        {
                            collections: Nut.managedCollections.Products,
                            delay: 300,
                        },
                        autocompleteOptions
                    )
                );

                // competitor cards autocomplete
                Autocomplete.setup(
                    'div.competitor-card .form-autocomplete',
                    $.extend(
                        {
                            collections: Nut.managedCollections.Competitors,
                        },
                        autocompleteOptions
                    )
                );
            },
        },

        tearDown: function() {
            // Close any open hoverviews and clear timeouts
            Card.mouseIsOverPopoverTarget = false;
            $('.tile-container, .mention-rendered, .hoverview').each(function(index, element) {
                var timeout = $(element).data('entranceTimeout');
                if (timeout) {
                    window.clearTimeout(timeout);
                }
            });
            Popover.close();
        },

        appendCardToContainer: function(newCard, container) {
            var accountContactContainer = container.closest('.column-accounts-contacts');

            // if we're in a funky mixed account-contact area
            if (accountContactContainer.length) {
                // new account card
                container = accountContactContainer;

                if (newCard.is('.account-card-container')) {
                    var lastVisibleAccount = container.find(
                        '.account-card-container.tile-filled:last'
                    );
                    if (lastVisibleAccount.length) {
                        lastVisibleAccount.after(newCard);
                    } else {
                        container.prepend(newCard);
                    }
                } else {
                    // new contact card, try to tuck into an account
                    var parentAccountContainer,
                        accountIds = newCard.data('accounts') || [];

                    _(accountIds).each(function(accountId) {
                        var accountCard = container.find(
                            '.account-card-container[data-entity-id=' + accountId + ']'
                        );
                        if (accountCard.length) {
                            parentAccountContainer = accountCard;
                        }
                    });

                    if (parentAccountContainer) {
                        parentAccountContainer
                            .removeClass('tile-internal-contact-card-is-editing')
                            .find('.contact-enclosure .float-object-template')
                            .before(newCard);
                    } else {
                        container.find('.other-contacts .contact-enclosure').append(newCard);
                    }
                }
            } else {
                if (container.is('.card-prepend')) {
                    container.find('.empty-placeholder').after(newCard);
                } else {
                    container.find('.tile-container:visible:last').after(newCard);
                }
            }

            container.find('.state-half-baked').remove();

            newCard.trigger(Nut.events.card.wasAdded);
        },

        reloadCard: function(card) {
            var cardData = card.find('input').serialize();
            cardData = cardData + '&relationship=' + encodeURI(card.find('span.value').text()); // kind of a hack
            Nut.ajax.get({
                url: '/mapping/reload.json',
                data: cardData,
                success: function(response) {
                    card.replaceWith(response.html);
                    Form.init.updateAppearances();
                },
            });
        },

        findIdenticalCards: function(card) {
            var entityName = $(card).data('entityName'),
                entityId = $(card).data('entityId');

            return $('div.tile-container')
                .filter(function() {
                    return (
                        $(this).data('entityName') == entityName &&
                        $(this).data('entityId') === entityId
                    );
                })
                .not(card);
        },

        // called when a card has been added or deleted from an area
        updateCardAreas: function() {
            var floatArea = $(this).closest('.float-area');

            // show placeholders if no half-baked cards
            if (floatArea.find('.state-half-baked:visible').length === 0)
                floatArea.find('.empty-placeholder:not(.empty-only)').fadeIn();

            // show empty-only placeholders
            if (floatArea.find('.float-object-container:visible').length === 0)
                floatArea.find('.empty-placeholder.empty-only').fadeIn();
        },

        getCardEntityType: function(card) {
            // too lazy to write a regex right now. i'm on a roll elsewhere.
            if (card.is('.contact-card-container')) {
                return 'contact';
            } else if (card.is('.account-card-container')) {
                return 'account';
            } else if (card.is('.product-card-container')) {
                return 'product';
            } else if (card.is('.competitor-card-container')) {
                return 'competitor';
            } else {
                return false;
            }
        },

        // received an event that an entity was changed. check for cards and reload.
        entityWasUpdated: function(event, dictionary) {
            var reloadEnclosure = false;

            $('.tile-container')
                .filter(function() {
                    return (
                        $(this).data('entityName') == dictionary.entityName &&
                        $(this).data('entityId') == dictionary.entityId
                    );
                })
                .each(function() {
                    if (Card.shouldCardReload($(this))) {
                        Card.reloadCard($(this));
                    } else {
                        reloadEnclosure = true;
                    }
                });

            if (reloadEnclosure) Lead.reloadCardEnclosure();
        },

        refreshIndividualCards: function(skipCard) {
            $('.tile-container')
                .filter(function() {
                    return $(this).data('entityName') && $(this).data('entityId');
                })
                .filter(function() {
                    if (skipCard && skipCard.is($(this))) {
                        return false;
                    }
                    return true;
                })
                .each(function() {
                    if (Card.shouldCardReload($(this))) {
                        Card.reloadCard($(this));
                    }
                });
        },

        shouldCardReload: function(card) {
            return !card.closest('.lead-sidebar-top').length && Lead.getId() != 'new';
        },

        handlePhoneCaller: function(card) {
            card.find(
                '[data-field-name="phone"] .fieldrow:not(.state-empty) .action-call-number'
            ).on('click', ClickToCall.handlePhoneCall);
        },

        loadMoreCards: function(e) {
            var isCompanyPage = Boolean(document.querySelector('body.accounts'));
            var baseUrl = isCompanyPage ? '/company/' : '/person/';
            var $this = $(e.currentTarget),
                pagerContainer = $this.parent(),
                entityId = $('.activities-container').data().entityId,
                cardCountTotal = pagerContainer.data().cardCount,
                offsetIndex = pagerContainer.data().offsetIndex,
                loadEventAction = $this.data().loadAction, // all, next, prev
                cardLimitPerPage = 5,
                url = baseUrl + entityId + '/get-filtered-cards.json?limit=' + cardLimitPerPage,
                newOffsetIndex,
                pagerState;

            var dispatchEventActions = {
                all: function() {
                    url = baseUrl + entityId + '/get-all-cards.json';
                    newOffsetIndex = 0;
                    pagerState = 'state-show-all';
                    var question =
                        'Are you sure you want to load all ' + cardCountTotal + isCompanyPage
                            ? ' people?'
                            : ' companies?';
                    // show popmini question if loading more than 300 cards
                    if (cardCountTotal > 300) {
                        Popmini.question(
                            question,
                            {attachTo: $this},
                            $.proxy(function(response) {
                                if (!response) return;
                                this.fetchCards();
                            }, this)
                        );
                    } else {
                        this.fetchCards();
                    }
                },

                next: function() {
                    var capstoneIndex = offsetIndex + cardLimitPerPage,
                        lastRowIndex = cardCountTotal - cardLimitPerPage,
                        isLastRow = capstoneIndex >= lastRowIndex;

                    newOffsetIndex = isLastRow ? lastRowIndex : capstoneIndex;
                    url += '&offset=' + newOffsetIndex;
                    pagerState = (!isLastRow ? 'state-has-next ' : '') + 'state-has-prev';
                    this.fetchCards();
                },

                prev: function() {
                    var isFirstRow = offsetIndex <= cardLimitPerPage;

                    newOffsetIndex = isFirstRow ? 0 : offsetIndex - cardLimitPerPage;
                    url += '&offset=' + newOffsetIndex;
                    pagerState = (!isFirstRow ? 'state-has-prev ' : '') + 'state-has-next';
                    this.fetchCards();
                },

                fetchCards: function() {
                    Nut.ajax.get({
                        url: url,
                        beforeSend: function() {
                            $this.toggleClass('state-loading', true);
                            pagerContainer.toggleClass('state-loading', true);
                        },
                        success: function(data) {
                            var newCards = $(data.html),
                                cardsContainer = isCompanyPage
                                    ? $('body').find('.contacts-list')
                                    : $('body').find('.accounts-list');

                            if (newCards.length) {
                                cardsContainer.find('.tile-container:not(.tile-empty)').remove();
                                cardsContainer.prepend(newCards);

                                $this.toggleClass('state-loading', false);
                                pagerContainer
                                    .data('offset-index', newOffsetIndex)
                                    .toggleClass(
                                        'state-loading state-default state-show-all state-has-prev state-has-next',
                                        false
                                    )
                                    .toggleClass(pagerState, true);

                                _.delay(Dupes.checkAll, 800);
                                _.delay(function() {
                                    Form.init.updateAppearances();
                                }, 800);
                            }
                        },
                        error: $.noop,
                    });
                },
            };

            dispatchEventActions[loadEventAction]();
        },
    };
})(jQuery);
Capsule = Nut.modules.Capsule = (function($) {
    return {
        opt: {
            controlDelay: {show: 75, hide: 75}, // delay before controls appear
            controlAnimate: {show: 75, hide: 100}, // animation duration for control slide-in
            removeAnimate: 500,
        },

        go: function() {
            for (var fn in this.init) {
                this.init[fn].apply(this);
            }
        },

        init: {
            setUpEvents: function() {
                // deleteable capsules
                $(document)
                    .on('mouseover', '.capsule-container.capsule-deleteable', function(event) {
                        if (!$(this).data('hoverintentInit')) {
                            $(this).hoverIntent({
                                interval: Capsule.opt.controlDelay.show, // ms before class is added
                                timeout: Capsule.opt.controlDelay.hide, // ms delay before class is removed
                                over: function() {
                                    $(this)
                                        .find('.delete')
                                        .addClass('state-visible', Capsule.opt.controlAnimate.show);
                                },
                                out: function() {
                                    $(this)
                                        .find('.delete')
                                        .removeClass(
                                            'state-visible',
                                            Capsule.opt.controlAnimate.hide
                                        );
                                },
                            });
                            $(this).data('hoverintentInit', true);
                            $(this).trigger('mouseover');
                        }
                    })

                    .on('click', '.capsule-container.capsule-deleteable .delete', function(event) {
                        var capsule = $(this).closest('.capsule-container'),
                            triggeredEvent = $.Event(Nut.events.capsule.willBeDeleted);

                        event.stopPropagation(); // prevent popovers, etc.
                        capsule.trigger(triggeredEvent);
                        if (!triggeredEvent.isDefaultPrevented())
                            capsule.fadeOut(Capsule.opt.removeAnimate, function() {
                                var siblingAdder = capsule.siblings(
                                    '.capsule-add-container.behavior-redisplay-after-delete'
                                );

                                if (siblingAdder.length) {
                                    siblingAdder
                                        .css('display', 'inline-block')
                                        .find('input')
                                        .trigger('focusin')
                                        .trigger('focus');
                                }

                                $(this)
                                    .trigger(Nut.events.capsule.wasDeleted)
                                    .remove();
                            });
                    });

                $(document).on('click', '.action-show-capsuleadder', function(event) {
                    $(this)
                        .hide()
                        .siblings('.capsule-add-container')
                        .css('display', 'inline-block')
                        .find('input')
                        .trigger('focusin')
                        .trigger('focus');
                });

                var autocompleteOptions = {
                    url: function(container) {
                        return container.attr('data-query');
                    },
                    collections: function(container) {
                        if (container.attr('data-collections')) {
                            return _(container.attr('data-collections').split(',')).map(function(
                                name
                            ) {
                                return name !== 'Unassigned'
                                    ? Nut.managedCollections[name]
                                    : [{id: '', type: 'users', name: 'Unassigned'}];
                            });
                        }
                    },
                    disablePreview: true,
                    getContainer: function() {
                        return $(this).closest('.capsule-add-container');
                    },
                    resultToElement: function(result) {
                        var el = $(
                            '<li><span class="icon"></span><span class="text">' +
                                _.escape(result.name) +
                                '</span></li>'
                        );
                        el.addClass(
                            'autocomplete-item-type-' +
                                result.entityName
                                    .toLowerCase()
                                    .replace(/ies$/, 'y')
                                    .replace(/s$/, '')
                        );
                        return el;
                    },
                    extraFocusClass: 'capsule-state-focus',
                    listShow: function() {
                        this.resultList.show();
                        return false;
                    },
                    listHide: function() {
                        this.resultList.hide();
                        return false;
                    },
                    validateOnChoose: true,
                    onChoose: function() {
                        // runs in Autocomplete context.
                        var resultData = this.currentSelection.data('autocompleteResult'),
                            data = {relationshipId: resultData.entityId},
                            container = this.container,
                            willBeAdded = $.Event(Nut.events.capsule.willBeAdded);
                        willBeAdded.resultData = resultData;

                        container.trigger(willBeAdded);
                        if (willBeAdded.isDefaultPrevented()) {
                            container.find('input').val('');
                            return;
                        }

                        // This is an exceptionl case to allow unassignment in bulk edit modals
                        if (resultData.entityId === '') {
                            var $unassignedCapsule = $(
                                '<div class="capsule-container popover-opener-user capsule-deleteable" data-assign-type="" data-append-to=".capsule-container" data-entity-name="" data-entity-id="">' +
                                    '<div class="delete"></div>' +
                                    '<div class="capsule capsule-type-user">' +
                                    '<div class="capsule-button">' +
                                    '<div class="icon"></div>' +
                                    '<span class="name">Unassigned</span>' +
                                    '</div>' +
                                    '</div>' +
                                    '</div>'
                            );

                            Capsule.addCapsule(container, $unassignedCapsule);
                            return;
                        }

                        Nut.ajax.post({
                            url:
                                container.data('addAction') ||
                                '/capsule/generic/' +
                                    resultData.entityId +
                                    '.json?entityName=' +
                                    resultData.entityName,
                            data: data,
                            error: function(response) {
                                Popmini.error(response.messages.join(', '), {
                                    attachTo: container.parent('sidebar-sources')
                                        ? container.siblings('.box-featured')
                                        : container,
                                });
                            },
                            success: function(response) {
                                container.find('input').val('');
                                var capsule = $(response.html);

                                Capsule.addCapsule(container, capsule);
                            },
                        });
                    },
                    onBlur: function() {
                        if (this.container.hasClass('behavior-hide-on-blur')) {
                            this.container.hide();
                            this.container.siblings('.action-show-capsuleadder').show();
                        }
                    },
                };

                Autocomplete.setup(
                    '.capsule-add-container.behavior-auto input',
                    autocompleteOptions
                );
            },
            updateAppearances: $.noop,
        },

        addCapsule: function(container, capsule) {
            var newCapsule;
            if (container.is('.capsule-insert-after')) {
                newCapsule = capsule.insertAfter(container);
            } else {
                newCapsule = capsule.insertBefore(container);
            }
            container
                .parent('.sidebar-sources')
                .find('.box-empty-placeholder')
                .remove();
            newCapsule.trigger(Nut.events.capsule.wasAdded);
            container.hide();
            container.siblings('.action-show-capsuleadder').show();
        },

        getEntities: function(capsuleContainers) {
            var entities = [];

            capsuleContainers.filter(':visible').each(function() {
                entities.push({
                    entityName: $(this).data('entityName'),
                    entityId: $(this).data('entityId'),
                });
            });

            return entities;
        },
    };
})(jQuery);
Table = Nut.modules.Table = (function($) {
    // private vars go here
    var self = this;
    var panning_chevron_distribution_factor = 150;
    var ignoredColumnClasses = '.column-header-pre, .column-header-post, .sticky-column';

    var undefined = window.undefined;

    return {
        opt: {},

        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        init: {
            setUpEvents: function() {
                // manage UI updates for sortable tables. Triggers custom 'orderByWasChanged' event when finished.
                $(document).on('click', 'table th.behavior-sortable', function() {
                    if ($(this).hasClass('state-sorted')) {
                        // swap sort state
                        if ($(this).hasClass('state-sorted-ascending')) {
                            $(this)
                                .addClass('state-sorted-descending')
                                .removeClass('state-sorted-ascending');
                        } else {
                            $(this)
                                .addClass('state-sorted-ascending')
                                .removeClass('state-sorted-descending');
                        }
                    } else {
                        // unsort other column
                        $(this)
                            .siblings('th')
                            .removeClass(
                                'state-sorted state-sorted-ascending state-sorted-descending'
                            );
                        $(this).addClass('state-sorted state-sorted-ascending');
                    }
                    $(this)
                        .closest('table')
                        .trigger(Nut.events.table.orderByWasChanged);

                    $.clearAllSelections(); // people frequently double-click+select when trying to descend sort
                });

                // =====================
                // = interactive table =
                // =====================

                $(document).on('click', 'a.interactive-table-pan-right', function() {
                    // don't register clicks when we are fading
                    if ($(this).css('opacity') == 1.0)
                        Table.slide('left', $(this).closest('.interactive-table'));
                });
                $(document).on('click', 'a.interactive-table-pan-left', function() {
                    // don't register clicks when we are fading
                    if ($(this).css('opacity') == 1.0)
                        Table.slide('right', $(this).closest('.interactive-table'));
                });

                // paging controls
                $(document)
                    .on('click', '.interactive-table .pages li a', function(event) {
                        var tableID = $(this)
                            .closest('.interactive-table')
                            .data('entityId');
                        if (Table.tablePageState(tableID) != $(this).text()) {
                            Table.tablePageState(tableID, $(this).text());
                        }
                    })
                    .on('click', '.interactive-table a.next-page-link', function(event) {
                        var tableID = $(this)
                            .closest('.interactive-table')
                            .data('entityId');
                        Table.tablePageState(tableID, Table.tablePageState(tableID) + 1);
                    })
                    .on('click', '.interactive-table a.prev-page-link', function(event) {
                        var tableID = $(this)
                            .closest('.interactive-table')
                            .data('entityId');
                        Table.tablePageState(tableID, Table.tablePageState(tableID) - 1);
                    });

                $('.interactive-table').each(function() {
                    var tableID = $(this).data('entityId');
                    Table.tablePageState(tableID, $('tbody', this).data('page'));
                });

                $(window).on('hashchange resize.interactive-table', Table.init.updateAppearances);

                // DEBUG
                // this.stickify($('table:eq(1)'));
            },
            updateAppearances: function() {
                $('.interactive-table').each(function() {
                    var wrapper = $(this);
                    var container = wrapper.find('.interactive-table-table-container');
                    var table = wrapper.find('.interactive-table-table');

                    // change pages if needed
                    var tableID = $(this).data('entityId');

                    // probably a server error, but don't choke clientside
                    if (!$(this).find('tbody').length) return;

                    if (Table.tablePageState(tableID) != $('tbody', this).data('page')) {
                        Table.changeTablePage($(this), Table.tablePageState(tableID));
                        // return for now because we'll do all this again when the new page is loaded.
                        return;
                    }

                    // update pagination controls
                    var footer = $('.interactive-table-footer', this);
                    var page = Table.tablePageState(tableID);
                    var totalPages = footer.find('.pages li').length;
                    footer.find('.pages .state-selected').removeClass('state-selected');
                    if (totalPages > 12) {
                        var windowTolerance = 4;
                        footer.find('.pages li').each(function(index) {
                            // 'index' is zero based, 'page' is one based
                            var indexOne = index + 1;
                            var isFirstOrLastPage = indexOne == 1 || indexOne == totalPages;
                            var withinTollerance =
                                indexOne >= page - windowTolerance &&
                                indexOne <= page + windowTolerance;

                            if (isFirstOrLastPage || withinTollerance) {
                                if (indexOne == page) {
                                    $(this).addClass('state-selected');
                                }
                                $(this).show();
                            } else {
                                $(this).hide();
                            }
                        });

                        // ellipses
                        // 2 is a buffer, since we always show first/last page
                        // no need to show ellipses if we are within two
                        footer.find('span.more-pages').hide();
                        if (page > windowTolerance + 2) {
                            footer
                                .find('.pages li:first')
                                .after(footer.find('span.more-pages:first').show());
                        }
                        if (page < totalPages - windowTolerance - 2) {
                            footer
                                .find('.pages li:last')
                                .before(footer.find('span.more-pages:last').show());
                        }
                    } else {
                        // add one to account for the ellipses children
                        footer
                            .find('.pages li:nth-child(' + (page + 1) + ')')
                            .addClass('state-selected');
                    }
                    footer
                        .find('.prev-page-link')
                        .css({visibility: page == 1 ? 'hidden' : 'visible'});
                    footer
                        .find('.next-page-link')
                        .css({visibility: page == totalPages ? 'hidden' : 'visible'});

                    // On initial page load footer is hidden to prevent Flash of Unstyled Content
                    footer.find('.interactive-table-footer-inner').show();

                    // should this be draggable?
                    wrapper[container.width() < table.width() ? 'addClass' : 'removeClass'](
                        'drag-enabled'
                    );

                    // tweak THs appearance for section headers
                    $('.interactive-table')
                        .removeClass('has-section-headers')
                        .filter(':has(.section-header)')
                        .addClass('has-section-headers');

                    $('table thead.headers-reorderable').sortable({
                        items: '.behavior-reorderable',
                        handle: '.action-handlebar',
                        axis: 'x',
                        tolerance: 'pointer',
                        placeholder: 'header-empty-placeholder',
                        helper: 'clone',
                        stop: Table.headerDidReorder,
                    });

                    $('table thead.headers-reorderable')
                        .on('mouseenter', 'th', function() {
                            $(this).addClass('state-hover');
                        })
                        .on('mouseleave', 'th', function() {
                            $(this).removeClass('state-hover');
                        });

                    if (wrapper.is(':not(.panning-disabled)')) {
                        // reenable dragging if needed
                        table.not('.ui-draggable').each(function() {
                            $(this)
                                .draggable({
                                    axis: 'x',
                                    handle: 'th.drag-handle',
                                })
                                .on(
                                    'dragstart',
                                    $.proxy(function() {
                                        return wrapper.hasClass('drag-enabled');
                                    }, this)
                                )
                                .bindDefer('drag dragstop', {}, Table.init.updateAppearances, 500);
                        });

                        // make sure table is aligned left if wrapper is wider or table is too far left
                        var rightGap =
                            container.width() - table.width() - $.nounit(table.css('left'));
                        if (rightGap >= 0) table.css({left: container.width() - table.width()});

                        // hide/show pan controls
                        $('a.interactive-table-pan-left', wrapper).toggleClass(
                            'disabled',
                            $.nounit(table.css('left')) >= 0
                        );
                        $('a.interactive-table-pan-right', wrapper).toggleClass(
                            'disabled',
                            $.nounit(table.css('left')) + table.width() <= container.width()
                        );

                        // move section headers to the right place
                        table.find('tr.section-header th div').css({
                            left:
                                -$.nounit(table.css('left')) +
                                $('a.interactive-table-pan-left:visible').width(),
                        });

                        // freeze table layout
                        // Table.freezeTableLayout(table);

                        // proper number and distribution of chevrons
                        $('.interactive-table-pan', wrapper).each(function() {
                            var quant =
                                Math.floor($(this).height() / panning_chevron_distribution_factor) +
                                1;
                            $(this).empty();
                            for (var i = 1; i < quant; i++)
                                $('<span>&nbsp;</span>')
                                    .css({top: (100 / quant) * i + '%'})
                                    .appendTo(this);
                        });

                        // position and update fake pre- and post-column headers
                        var i = Table.getTableInfo(table);
                        $.each(
                            [
                                {c: 'column-header-pre', v: i.leftCols ? '&hellip;' : '&nbsp;'},
                                {c: 'column-header-post', v: i.rightCols ? '&hellip;' : '&nbsp;'},
                            ],
                            function() {
                                $('div.' + this.c).html(this.v);
                            }
                        );
                        // make sure these are 0 if we disabled the panning things. Because sometimes it isn't.
                        if ($('a.interactive-table-pan-left').hasClass('disabled'))
                            $('div.column-header-pre').html('&nbsp;');
                        if ($('a.interactive-table-pan-right').hasClass('disabled'))
                            $('div.column-header-post').html('&nbsp;');

                        $('.interactive-table:has(.sticky-column)', wrapper).each(function() {
                            $(this)
                                .find('.interactive-table-sticky-column-table')
                                .width(
                                    $(this)
                                        .find('table.interactive-table-table .sticky-column')
                                        .outerWidth()
                                );
                        });

                        // set the right-most column to the proper width for when we pan completely right
                        // example: 2, 3, 4, 5, 6; width: 17, would return 2
                        // TODO: setting the width of a TH doesn't actually work for some reason. fix that.
                        // var width = wrapper.width();
                        // var memo = 0;
                        // var idealLastColumnWidthOffset = undefined;
                        // _.reduceRight(i.cellWidths, memo, function(a, b) {
                        // 	var sum = a + b;
                        // 	if ( _.isUndefined(idealLastColumnWidthOffset) && sum > width )
                        // 		idealLastColumnWidthOffset = sum - width;
                        // 	return sum;
                        // });
                        // var lastCol = $('tr:first th', wrapper).not(ignoredColumnClasses).filter(':last');
                        // lastCol.css('width', lastCol.width() + idealLastColumnWidthOffset);
                    }
                });
            },
        },

        // return null or an object like { direction: 'asc', column: 'name' }
        getSorting: function(table) {
            var sortColumn = $(table).find('th.behavior-sortable.state-sorted');

            if (!sortColumn.length) return null;

            var sortObject = {};

            sortObject.direction = sortColumn.hasClass('state-sorted-ascending') ? 'asc' : 'desc';
            sortObject.column = sortColumn.data('sortBy');

            return sortObject;
        },

        // =====================
        // = interactive table =
        // =====================
        tablePageState: function(id, page) {
            var tablePages = $.bbq.getState('tablePages') || {};
            if (!page) {
                return Number(tablePages['id' + id] || 1);
            } else {
                tablePages['id' + id] = page;
                //NOTE: this triggers Table.init.updateAppearances due to the listener on "hashchange" above.
                $.bbq.pushState({tablePages: tablePages});
                return null;
            }
        },
        changeTablePage: function(table, newPage) {
            var willBeChanged = $.Event(Nut.events.table.pageWillBeChanged);
            // dont try to get a negative page.
            if (newPage < 0) return false;
            table.trigger(willBeChanged, [newPage]);
            if (willBeChanged.isDefaultPrevented()) return false;

            $('tbody', table)
                .closest('table')
                .animate({opacity: 0.5});

            table.trigger(Nut.events.table.pageWasChanged, [newPage]);
            return true;
        },
        freezeTableLayout: function(table) {
            table.css('table-layout', 'normal');
            table.find('th:not(.column-header-pre, .column-header-post)').each(function() {
                $(this).width($(this).outerWidth());
            });
            table.css('table-layout', 'fixed');
        },
        getTableInfo: function(table, useAll) {
            var i = {
                cellWidths: $('tr:first th', table).map(function() {
                    return $(this)
                        .not(!useAll ? ignoredColumnClasses : '')
                        .outerWidth();
                }),
                tableLeft: $.nounit(table.css('left')),

                leftPreWidth: null,
                leftPostWidth: null,
                rightPreWidth: null,
                rightPostWidth: null,

                totalWidth: 0,
                totalCols: 0,

                leftCols: 0,
                rightCols: 0,
            };

            // reset classes
            $(table)
                .find('tr:first th')
                .removeClass('left right');

            $.each(i.cellWidths, function(n) {
                var cell = $(table)
                    .find('tr:first th')
                    .not(ignoredColumnClasses)
                    .eq(n);
                if (cell.is(ignoredColumnClasses)) return;

                i.prevTotalWidth = i.totalWidth;
                i.totalWidth += this;
                i.totalCols++;

                if (i.prevTotalWidth < -i.tableLeft) {
                    // || i.totalWidth == -i.tableLeft
                    // cell.css('background', 'green'); // DEBUG
                    cell.addClass('left');
                    if (null == i.leftPostWidth) i.leftPostWidth = i.totalWidth;
                    if (null == i.leftPreWidth) i.leftPreWidth = i.totalWidth - this;
                    if (null == i.leftPrevWidth)
                        i.leftPrevWidth = i.totalWidth - (this + (i.cellWidths[n - 1] || 0));
                }

                var tableRight = table.width() - table.parent().width();
                if (i.totalWidth > -i.tableLeft && i.prevTotalWidth < tableRight) {
                    // || i.totalWidth == tableRight
                    // cell.css('background', 'red'); // DEBUG
                    cell.addClass('right');
                    if (null == i.rightPostWidth) i.rightPostWidth = i.totalWidth;
                    if (null == i.rightPreWidth) i.rightPreWidth = i.totalWidth - this;
                    if (null == i.rightPrevWidth)
                        i.rightPrevWidth = i.totalWidth - (this + (i.cellWidths[n - 1] || 0));
                }
            });

            i.leftCols = $(table).find('tr:first th.left').length;
            i.rightCols = $(table).find('tr:first th.right').length;

            return i;
        },
        slide: function(direction, wrapper) {
            var table = wrapper.find('.interactive-table-table');
            var i = Table.getTableInfo(table);

            // decide what to snap to, the previous or next column divider, based on slide direction
            // also takes into consideration being in between columns
            var offset =
                direction != 'left'
                    ? -i.tableLeft > i.rightPreWidth
                        ? i.rightPreWidth
                        : i.rightPrevWidth
                    : i.rightPostWidth;

            // keep from going too far
            if (offset + wrapper.width() > table.width())
                offset = table.width() - wrapper.width() + 2;

            // move section headers in reverse as we slide the whole table
            table.find('tr.section-header th div').animate(
                {
                    left: offset + $('a.interactive-table-pan-left:visible', wrapper).width(),
                },
                {duration: 500, easing: 'easeInOutCubic', queue: false}
            );

            table.animate(
                {left: -offset},
                {
                    duration: 500,
                    easing: 'easeInOutCubic',
                    queue: false,
                    complete: Table.init.updateAppearances,
                }
            );
        },
        stickify: function(table, col) {
            if (col === undefined) col = 1;
            table
                .find('tr:first th')
                .eq(col)
                .addClass('sticky-column');
            var wrapper = table.closest('.interactive-table');
            var newTable = table
                .clone()
                .addClass('interactive-table-sticky-column-table')
                .removeClass('interactive-table-table ui-draggable');
            var toRemove = newTable
                .find('tr:first th, td')
                .not(':nth-child(' + (col * 1 + 1) + ')');

            newTable
                .css({
                    position: 'absolute',
                    zIndex: 5,
                    width: table
                        .find('tr:first th')
                        .filter(':nth-child(' + col + ')')
                        .width(),
                })
                .insertBefore(table);
            newTable.css({
                left:
                    newTable
                        .find('tr:first th')
                        .filter(':nth-child(' + (col * 1 + 1) + ')')
                        .position().left + 2,
            });

            toRemove.remove();
        },
        headerDidReorder: function(event, ui) {
            $(ui.item).trigger(Nut.events.table.headerWasReordered, [ui]);
        },
    };
})(jQuery);
/*
 * Contains code for the tiered select widget. Markup generated by Nut_View_Helper_TieredSelect
 */

TieredSelect = Nut.modules.TieredSelect = (function($) {
    return {
        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },
        init: {
            setUpEvents: function() {
                $(document)
                    .on('click', '.tselect-tier li', function(event) {
                        TieredSelect.selectElement($(this));
                    })

                    // leaving this code loose until all behaviors are certain
                    .on(
                        'mousedown',
                        '.tselect-container:not(.tselect-read-only) .tselect-opener:not(.state-disabled)',
                        function(event) {
                            var container = $(this).closest('.tselect-container');
                            // close others first
                            TieredSelect.close($('.tselect-container').not(container));
                            if (container.is('.state-closed')) {
                                $('body').on('click', TieredSelect.clickEventHappened);
                                container.addClass('state-open').removeClass('state-closed');
                                container
                                    .parents()
                                    .filter(function() {
                                        return $(this).css('position') == 'relative';
                                    })
                                    .first()
                                    .addClass('state-front'); // this gets around the relative positioning bug in IE7.
                            } else {
                                TieredSelect.close(container);
                                container
                                    .parents()
                                    .filter(function() {
                                        return $(this).css('position') == 'relative';
                                    })
                                    .first()
                                    .removeClass('state-front');
                            }
                            TieredSelect.init.updateAppearances();
                        }
                    );

                $(document).on('click', '#select-none', function(event) {
                    event.preventDefault();
                    var elements = document.querySelectorAll(
                        '.tselect-container:not(.tselect-read-only)'
                    );
                    elements.forEach(function(element) {
                        var disabler = $(element).find('input[value="0"]');
                        TieredSelect.selectElement(disabler.parent());
                    });
                });
                $(document).on('click', '#select-all', function(event) {
                    event.preventDefault();
                    var elements = document.querySelectorAll(
                        '.tselect-container:not(.tselect-read-only)'
                    );
                    elements.forEach(function(element) {
                        var disabler = $(element).find('input[value="1"]');
                        TieredSelect.selectElement(disabler.parent());
                    });
                });
            },
            updateAppearances: function() {
                $('.tselect:not(.initialized)').each(function() {
                    var selectedElement = $(this).find('li.state-selected');

                    // if no default items, we select the first ul & li in each tier, except the first li in the last
                    if (!selectedElement.length) {
                        $(this)
                            .find(
                                '.tselect-tier ul:first-child, .tselect-tier:not(:last-child) ul:first-child li:first-child'
                            )
                            .addClass('state-selected');
                    } else {
                        selectedElement.each(function() {
                            TieredSelect.selectElement($(this), true);
                        });
                    }

                    // add .first-child / .last-child classes
                    $(this)
                        .find('.tselect-tier:first-child')
                        .addClass('first-child')
                        .end()
                        .find('.tselect-tier:last-child')
                        .addClass('last-child');

                    // set value element
                    TieredSelect.updateValueElements($(this).addClass('initialized'));
                });

                $('.tselect-tier li').each(function() {
                    if ($(this).is('.state-disabled')) {
                        $(this)
                            .find('input')
                            .attr('disabled', 'disabled');
                    } else {
                        $(this)
                            .find('input')
                            .removeAttr('disabled');
                    }
                });
                $('.tselect-container.state-open').each(function() {
                    TieredSelect.updateOneAppearance($(this));
                });
            },
        },

        updateOneAppearance: function(element) {
            var container = $(element).closest('.tselect-container'),
                tselect = container.find('.tselect'),
                tiers = tselect.find('.tselect-tier'),
                totalWidth = 0,
                maxHeight = 0;

            TieredSelect.updateValueElements(container);

            // short-circuit some expensive positioning stuff below that was running on init.
            // @TODO this optimization caused problems in updateValueElements on the import mapping page
            // and i can't figure it out yet.
            // if (container.is('.state-closed')) return;

            // to measure internal widths without weird wrapping, we first need to expand
            tselect.width(1000).css('display', 'inline-block');

            // hack the .last-child class if a terminal item is selected (we want it to be that tier)
            // also hide any tiers more right-most than that which has a selected terminal item
            tiers
                .show()
                .filter(':has(ul.state-selected:has(.state-selected.tselect-terminal-item))')
                .nextAll()
                .hide();
            tiers
                .removeClass('last-child')
                .filter(':visible')
                .last()
                .addClass('last-child');

            tiers.filter(':visible').each(function() {
                $(this).css('height', '');
                maxHeight = Math.max(maxHeight, $(this).height());
                totalWidth += $(this).outerWidth();
            });

            tiers.height(maxHeight);
            tselect.width(totalWidth + 1); // +1 is to account for a firefox bug wrapping elements when container is only equal width of its children

            var opener = container.find('.tselect-opener');
            if (tselect.is('.tselect-access-top-right')) {
                // reports
                container.find('.tselect-wrapper').position({
                    of: opener,
                    my: 'right top',
                    at: 'right bottom',
                    offset: '-8px -1px',
                    collision: 'none',
                });
            } else if (tselect.is('.tselect-access-top-left')) {
                // process builder and CSV import
                container.find('.tselect-wrapper').position({
                    of: opener,
                    my: 'left top',
                    at: 'left bottom',
                    offset: '0px -1px',
                    collision: container.is('.tselect-allow-flip') ? 'none flip' : 'none',
                    using: function(pos) {
                        // woooooody's round up (for firefox)
                        pos.top = Math.ceil(pos.top);
                        pos.left = Math.ceil(pos.left);
                        $(this)
                            .css(pos)
                            .toggleClass('tselect-flipped-none', pos.top >= 0 && pos.left >= 0)
                            .toggleClass('tselect-flipped-vert', pos.top < 0 && pos.left >= 0)
                            .toggleClass('tselect-flipped-horz', pos.top >= 0 && pos.left < 0)
                            .toggleClass('tselect-flipped-both', pos.top < 0 && pos.left < 0);
                        // HACK, sort of, to compensate for .position() not flipping the offset coordinates.
                        $(this)
                            .filter('.tselect-flipped-vert, .tselect-flipped-both')
                            .css({
                                top:
                                    pos.top +
                                    ($.browser.msie && $.browser.version == 7
                                        ? -1
                                        : $.browser.msie
                                        ? 2
                                        : /* everything else */ 3),
                            });
                    },
                });
            }

            tselect.css('display', '');
        },

        // expects the <li>
        selectElement: function(element, isInitial) {
            var container = $(element).closest('.tselect-container');

            //if ( element.is('.state-disabled') ) return;

            // handle normal, selectable item
            if (
                element.closest('.tselect-tier').is('.tselect-selectable') &&
                !element.is('.state-disabled')
            ) {
                if (!element.find('input[type=checkbox]').length) {
                    element
                        .closest('.tselect-tier')
                        .find('li')
                        .removeClass('state-selected');
                    element.addClass('state-selected');
                } else {
                    if (!isInitial) element.toggleClass('state-selected');
                }
            }

            // confirm that only this ul chunk is selected
            element
                .closest('ul')
                .addClass('state-selected')
                .siblings()
                .removeClass('state-selected');

            // deselect following items in tiers, select top item
            // future travelers, this used to be ":not(:has(:checkbox))" which doesn't work in jquery >= 1.8.2
            element
                .closest('.tselect-tier')
                .nextAll()
                .find('li:not(:has(input[type=checkbox]))')
                .removeClass('state-selected')
                .filter(':first-child')
                .addClass('state-selected');

            // align all parent and child tiers
            TieredSelect.alignTiersWith(element.closest('.tselect-tier'));

            TieredSelect.updateOneAppearance(element);

            // selecting a terminal option
            if (element.is('.tselect-terminal-item')) {
                TieredSelect.updateValueElements(element);

                // with no checkboxes
                if (
                    !element.find('input[type=checkbox]').length &&
                    element.closest('.tselect').is('.initialized')
                ) {
                    TieredSelect.close(element);
                }
            }
        },

        // update the static <span>, the hidden input, and any checkboxes
        updateValueElements: function(element) {
            var container = $(element).closest('.tselect-container'),
                selectedElement = container.find(
                    '.tselect-tier.last-child ul.state-selected li.state-selected'
                ),
                valueElement = container.find('.tselect-value'),
                valueStack = [];
            container.find('ul.state-selected li.state-selected').each(function() {
                valueStack.push($.trim($(this).text()));
                $(this)
                    .find('input:checkbox')
                    .attr('checked', 'checked');

                // "break" if we don't want the rest of the value stack
                if ($(this).is('.tselect-terminal-item') && !$(this).find('input:checkbox').length)
                    return false;
            });

            container.find('li:not(.state-selected)').each(function() {
                $(this)
                    .find('input:checkbox')
                    .attr('checked', false);
            });

            // update value in opener
            if (container.is('.tselect-concatenate-names')) {
                valueElement.html('');
                $.each(valueStack, function(i, v) {
                    valueElement.append(i ? $('<div class="tselect-separator"/>').text(v) : v);
                });
            } else {
                valueElement.text(valueStack.slice(-1).toString());
            }

            if (selectedElement.length) {
                var pseudoInput = container.find('input.tselect-pseudo-input'),
                    oldValue = pseudoInput.val(),
                    newValue = selectedElement.find('input').val();
                pseudoInput.val(newValue);
                if (oldValue && oldValue != newValue) {
                    pseudoInput.change();
                }
            }
        },

        // use the provided tier and be sure that all other tiers are properly selected
        alignTiersWith: function(authoritativeTier) {
            var currentTier = authoritativeTier.prev(),
                previousTier = authoritativeTier,
                guid;

            // walk up previous tiers to be sure they're selected
            while (currentTier.length) {
                guid = previousTier.find('ul.state-selected').data('guid');
                currentTier
                    .find('li')
                    .removeClass('state-selected')
                    .filter(function() {
                        return $(this).data('guid') == guid;
                    })
                    .addClass('state-selected')
                    .parent('ul')
                    .addClass('state-selected');
                previousTier = currentTier;
                currentTier = currentTier.prev('.tselect-tier');
            }

            currentTier = authoritativeTier.next();
            previousTier = authoritativeTier;

            // walk down next tiers to be sure they're selected
            while (currentTier.length) {
                guid = previousTier.find('ul.state-selected li.state-selected').data('guid');
                currentTier
                    .find('ul')
                    .filter(function() {
                        return $(this).data('guid') == guid;
                    })
                    .addClass('state-selected')
                    .siblings()
                    .removeClass('state-selected');
                previousTier = currentTier;
                currentTier = currentTier.next('.tselect-tier');
            }
        },

        clickEventHappened: function(event) {
            var container = $(event.target).closest('.tselect-container');
            if (container.length === 0) {
                TieredSelect.close($('.tselect-container.state-open'));
            }
        },

        close: function(element) {
            var container = $(element).closest('.tselect-container');

            if (container.hasClass('state-open')) {
                $('body').off('click', TieredSelect.clickEventHappened);
                container
                    .addClass('state-closed')
                    .removeClass('state-open')
                    .trigger('tselectClosed');
            }
        },
    };
})(jQuery);
Process = Nut.modules.Process = (function($) {
    var ajaxProcessStepRunning = false;

    return {
        go: function() {
            for (var fn in this.init) {
                this.init[fn].apply(this);
            }
        },

        events: {
            StepDidSubmitSuccessfully: 'StepDidSubmitSuccessfully',
        },

        init: {
            setUpEvents: function() {
                // milestone switching
                $(document)
                    // process editing popover
                    .on(
                        'click',
                        '.processes .action-editprocesses',
                        Process.showEditProcessesPopover
                    )

                    .on(
                        'click',
                        '.popover-process-edit .action-reset, .popover-process-edit .action-delete',
                        Process.resetOrDeleteProcessWasClicked
                    )
                    .on(
                        'click',
                        '.popover-process-edit .action-submit',
                        Process.addProcessWasClicked
                    )

                    // entity-field-picker column coordinator
                    .on('click', 'div.entity-field-picker .col-left li', function() {
                        var picker = $(this).closest('.entity-field-picker'),
                            myIndex = $(this).index() + 1;

                        picker
                            .find('.col-right li:nth-child(' + myIndex + ')')
                            .addClass('state-selected')
                            .siblings()
                            .removeClass('state-selected');

                        $(this)
                            .addClass('state-selected')
                            .siblings()
                            .removeClass('state-selected');
                    })

                    // entity-field-picker open modal, add field
                    .on('click', 'div.entity-field-picker .action-add-field', function(event) {
                        var picker = $(this).closest('.entity-field-picker'),
                            myIndex =
                                $(this)
                                    .closest('li')
                                    .index() + 1,
                            fieldName = picker.data('fieldName');

                        var button = picker.find(
                            '.col-left li:nth-child(' + myIndex + ') .icon.button'
                        );

                        // My understanding is that this doesn't work, but leaving
                        // it anyway.
                        button.click();

                        var href = button.attr('href');
                        if (href) {
                            // navigate to the related entity page manually
                            window.open(href, '_blank');
                        }

                        // find the fieldblock, expecting a sibling adder
                        $('#modal').one(Modal.events.cardDidLoad, function() {
                            $(this)
                                .find('.fieldblock')
                                .filter(function() {
                                    return $(this).data('fieldname') == fieldName;
                                })
                                .siblings('.button-add-link')
                                .trigger('click');
                        });
                    })

                    // collapsable insets
                    .on(
                        'click',
                        '.dashboardable-inset .icon.flipdown, .dashboardable-inset.state-closed.behavior-openable',
                        function(e) {
                            e.stopPropagation();

                            $(this)
                                .closest('.dashboardable-inset')
                                .toggleClass('state-closed state-open');

                            // Recalculate and set height of proceess steps container.
                            Process.showStepsOfSelectedStage();
                        }
                    );

                // listen for changes which would force a process reload
                $('body')
                    .on(
                        Nut.events.global.entityWasDeleted +
                            ' ' +
                            Nut.events.global.entityWasEdited,
                        Process.reloadProcesses
                    )

                    .on('click', '.picker .action-choose', Process.setStepChoices)

                    .on('click', '.step .action-complete', Process.completeStep)

                    .on('click', '.step .action-skip', Process.skipStep)

                    // step assignee chosen action
                    .on(
                        Nut.events.capsule.willBeAdded,
                        '.step .buttons .capsule-add-container',
                        function(event) {
                            event.preventDefault();
                            var container = $(this),
                                step = container.closest('.step');

                            Nut.ajax.post({
                                url:
                                    '/step/assign/' +
                                    step.find('input[name="stepId"]').val() +
                                    '.json',
                                data: event.resultData,
                                success: function(response) {
                                    var oldCapsule = step.find(
                                            '.capsule-type-user, .capsule-type-team'
                                        ),
                                        newCapsule = $(response.message),
                                        assignedClass = 'assigned';

                                    if (oldCapsule && oldCapsule.parent()) {
                                        // in the case of reassignment, remove the original assignment and flag as
                                        // reassigned--this is just to help flow of the ReassignStep selenium test
                                        oldCapsule.parent().remove();
                                        assignedClass += ' reassigned';
                                    }
                                    newCapsule
                                        .insertAfter(container)
                                        .wrap("<div class='" + assignedClass + "' />");
                                    newCapsule.trigger(Nut.events.capsule.wasAdded);
                                    container.hide();
                                },
                            });
                        }
                    )

                    // step assignee capsule added
                    .on(Nut.events.capsule.wasAdded, '.step .assignee', function(event) {
                        $(this)
                            .closest('.step')
                            .addClass('status-assigned');
                    })

                    // focus lost while choosing step assignee
                    .on('blur', '.step .capsule-add-container input', function(event) {
                        var step = $(this).closest('.step');
                        $(this)
                            .closest('.capsule-add-container')
                            .hide();
                        step.removeClass('status-assigning');
                    })

                    // choose step assignee action
                    .on('click', '.step .action-assign', function(event) {
                        $(this)
                            .closest('.step')
                            .addClass('status-assigning');
                        $(this)
                            .siblings('.capsule-add-container')
                            .show()
                            .find('input')
                            .trigger('focus')
                            .trigger('focusin');
                    })

                    // post-step-unassign action
                    .on(
                        Nut.events.capsule.wasDeleted,
                        '.step .assignee .capsule-container',
                        function(event) {
                            if (
                                $(this)
                                    .siblings('.capsule-container:visible')
                                    .size() === 0
                            ) {
                                $(this)
                                    .closest('.step')
                                    .removeClass('status-assigned');
                            }
                        }
                    )

                    // step unassigned action
                    .on(
                        Nut.events.capsule.willBeDeleted,
                        '.step:not(.step-template) .assigned .capsule-container',
                        function(event) {
                            Nut.ajax.post({
                                url:
                                    '/step/unassign/' +
                                    $(this)
                                        .closest('.step')
                                        .find('input[name="stepId"]')
                                        .val() +
                                    '.json',
                                data: {
                                    entityName: $(this).data('entityName'),
                                    entityId: $(this).data('entityId'),
                                },
                            });
                        }
                    );

                //re-assign step action
                $('body')
                    .on('click', '.action-reassign-step', function(event) {
                        var step = $('.popover:visible')
                            .data('appendTo')
                            .closest('.step');
                        step.addClass('status-assigning');
                        step.find('.capsule-add-container')
                            .show()
                            .find('input')
                            .trigger('focus')
                            .trigger('focusin');
                        step.find('.capsule-add-container').on('blur', 'input', function(event) {});
                    })

                    .on('click', '.popover-delay .action-submit', Process.delayStep);
            },

            // Note that this is called by our lead-page, to bootstrap old JS.
            updateAppearances: function() {
                $('.step .eip-container:not(.eip-ready)').each(function() {
                    var options = {
                        url:
                            '/lead/edit-field-value/' +
                            $(this)
                                .find('input[name=entityId]')
                                .val() +
                            '.json',
                        addButtons: false,
                    };
                    if ($(this).is('.type-date'))
                        new EditInPlaceDate(this, $.extend({}, $(this).data(), options));
                    else EditInPlace.factory(this, options);
                });

                Process.showStepsOfSelectedStage();
            },
        },

        showStepsOfSelectedStage: function() {
            $selectedStage = $('.stages-carousel-wrapper .ui-lead-stage--selected');

            if (!_.any($selectedStage)) return;

            var selectedProcessHeight = Process.getSelectedMilestoneHeight();

            $('.processes')
                .toggleClass('state-onload', false)
                // manually set the height of the conainer and animate it with css
                .css({height: selectedProcessHeight});
        },

        showEditProcessesPopover: function(event) {
            Popover.load({
                url: '/process/popover.json',
                // currently just for leads
                data: {
                    entityName: 'Leads',
                    entityId: Lead.getId(),
                },
                appendTo: $(event.target),
                loadingOrientation: 'north',
            });
        },

        resetOrDeleteProcessWasClicked: function(event) {
            var target = $(event.target).closest('.action'),
                resetOrDelete = target.is('.action-delete') ? 'delete' : 'reset',
                questionText = target
                    .closest('.popover')
                    .find('.l10n-' + resetOrDelete)
                    .text(),
                href = target.attr('href');

            event.preventDefault();

            Popmini.question(
                questionText,
                {
                    attachTo: target.closest('.popover-area'),
                    orientation: 'north',
                },
                function(yes) {
                    if (!yes) return;
                    Nut.ajax.post({
                        url: href,
                        success: function(response) {
                            var $response = $(response.html);
                            Popover.close();
                            Process.processesWereUpdated($response);
                            window.currentLead.getStages();

                            // Manually fetch to update UI if not empty state.
                            // There is a janky issue that destroys the empty
                            // state UI if we call model.fetch() when the empty
                            // state has already been rendered. Should revisit
                            // this soon to figure what is going on.
                            var isEmptyState =
                                $response.find('.process-milestone-container').length === 0;
                            if (!isEmptyState) {
                                window.currentLead.fetch();
                            } else {
                                Process.showStepsOfSelectedStage();
                            }
                        },
                    });
                }
            );
        },

        addProcessWasClicked: function(event) {
            var processTemplateId = $(event.target)
                .closest('.popover-area')
                .find('select')
                .val();
            event.preventDefault();

            if (!processTemplateId) return;

            Nut.ajax.post({
                url: '/process/add-template.json',
                data: {
                    entityName: 'Leads',
                    entityId: Lead.getId(),
                    processTemplateId: processTemplateId,
                },
                success: function(response) {
                    Popover.close();
                    Process.processesWereUpdated($(response.html));

                    window.currentLead.fetch();
                    window.currentLead.getStages();
                },
            });
        },

        getStepId: function($step) {
            return $step.find('input[name=stepId]').val();
        },

        getStepTemplate: function($step) {
            return $step.parents('.steps-container').data('template');
        },

        setStepChoices: function(event) {
            var step = $(this).closest('.step'),
                fieldrow = $(this).closest('.fieldrow'),
                selected = fieldrow.siblings('.state-chosen').serializeArrayToObject(),
                data = fieldrow.serializeArrayToObject();

            if (jQuery.isArray(data.value)) {
                //we're dealing with a multiple-choice enum
                if (!selected.value) selected.value = [];
                data.value = fieldrow.is('.state-chosen')
                    ? selected.value
                    : $.merge(data.value, selected.value);
            }
            data.returnStepOnly = true; // at this point, it shouldn't affect the rest of the process.

            Nut.ajax.post({
                url: '/step/set-choice/' + Process.getStepId(step) + '.json',
                data: data,
                success: Process.updateCallback,
            });
        },

        reloadProcesses: function() {
            var leadId = Lead.getId();

            if (leadId == null) return; // only relevant for instantiated leads

            Nut.ajax.post({
                url: '/step/get-processes-for-lead/' + leadId + '.json',
                success: Process.updateCallback,
                error: function() {
                    // do nothing (no alerts)
                    ajaxProcessStepRunning = false;
                    Nut.log('Processes failed to load');
                },
            });
        },

        // examine the response, calling processesWereUpdated or stepWasUpdated appropriately
        updateCallback: function(response) {
            ajaxProcessStepRunning = false;
            if (!response.html) return;

            var $response = $(response.html),
                $processes = $response.filter('.processes');

            if (_.any($processes)) {
                Process.processesWereUpdated($processes);
            } else {
                Process.stepWasUpdated($response);
            }

            // the goals milestone container wasn't always being reloaded,
            // this grabs the new containers from the response, removes the old ones,
            // and then shoves the new ones into the DOM in front of the .processes
            var $milestones = $response.filter('.goals-milestone-container');
            $('.goals-milestone-container').remove();
            $('.processes').before($milestones);

            Nut.updateAppearances();
        },

        // callback for ajax requests that return a replace process. preserves current milestone.
        processesWereUpdated: function($response) {
            var $processes = $response.filter('.processes');

            // get the selected milestone's container's height
            var selectedMilestoneHeight = Process.getSelectedMilestoneHeight();

            // add that to our response's css so that our scroll
            // doesn't jump before setting it
            $processes.css({height: selectedMilestoneHeight});

            // replace our existing processes with the new
            $('.processes').replaceWith($processes);
        },

        getSelectedMilestoneHeight: function() {
            var $stepContainers = $('.process-milestone-container, .goals-milestone-container'),
                $selectedStage = $('.stages-carousel-wrapper .ui-lead-stage--selected');

            if (!_.any($selectedStage)) return 0;

            var selectedStageId = $selectedStage.data().stageId.replace('-stages', '');
            var $selectedContainers = $stepContainers
                .toggleClass('state-active', false)
                .filter('[data-milestone-id="' + selectedStageId + '"]')
                .toggleClass('state-active', true);

            return $selectedContainers.filter('.process-milestone-container').outerHeight() || 0;
        },

        stepWasUpdated: function(step) {
            $('li.step:has(input[name=stepId][value=' + Process.getStepId(step) + '])').replaceWith(
                step
            );
        },

        skipStep: function(event) {
            var self = $(this);
            Popmini.question(
                'Are you sure you want to skip this step?',
                {orientation: 'north-east', attachTo: self},
                function(didClickYes) {
                    if (!didClickYes) return;
                    Nut.ajax.post({
                        url: '/step/skip/' + Process.getStepId(self.closest('.step')) + '.json',
                        data: {
                            returnStepOnly: self.closest('.processes').length === 0,
                            template: Process.getStepTemplate(self.closest('.step')),
                        },
                        success: function(response) {
                            if (window.currentLead) {
                                window.currentLead.fetch();
                                Process.reloadProcesses();
                            } else {
                                Process.updateCallback(response);
                            }
                        },
                        error: function(response) {
                            var message =
                                response && response.message
                                    ? response.message
                                    : 'This step cannot be skipped';
                            Popmini.error(message, {
                                orientation: 'north-east',
                                attachTo: self,
                            });
                        },
                    });
                }
            );
        },

        delayStep: function(event) {
            var popover = $(this).parents('.popover');
            var stepContainer = $('.popover-delay').data('appendTo');
            var stepId = $('.popover-opener-delay', stepContainer).data('entityId');
            var note = $('input', popover);
            Nut.ajax.post({
                url: '/step/delay/' + stepId + '.json',
                data: {
                    delay: $('select', popover).val(),
                    note: note ? note.val() : false,
                    returnStepOnly: stepContainer.closest('.processes').length === 0,
                    template: Process.getStepTemplate(stepContainer),
                },
                success: function(response) {
                    Popover.close();
                    if (window.currentLead) {
                        window.currentLead.fetch();
                        Process.reloadProcesses();
                    } else {
                        Process.updateCallback(response);
                    }
                },
            });
        },

        completeStep: function(event) {
            var step = $(this).closest('.step'),
                stepId = Process.getStepId(step) + '-steps',
                icon = $(this);

            if (ajaxProcessStepRunning) {
                Popmini.error(
                    'Must wait until last operation on step "' +
                        ajaxProcessStepRunning +
                        '" completes ',
                    {attachTo: icon, orientation: 'north-east'}
                );
                return;
            }
            ajaxProcessStepRunning = step.children('.name').text();

            if (step.is('.status-completed')) {
                return Process.uncompleteStep(event, step, stepId, icon);
            } else {
                step.addClass('state-loading');
            }

            Nut.ajax.post({
                url: '/step/complete/' + stepId + '.json',
                data: {
                    returnStepOnly: step.closest('.processes').length === 0,
                    template: Process.getStepTemplate(step),
                },
                success: function(response) {
                    try {
                        Analytics.trackEvent(
                            Analytics.categories.usage,
                            Analytics.actions.usage.completeStep
                        );
                    } catch (e) {}

                    step.addClass('status-completed');
                    var milestonesCount = $('.process-milestone-container').length;
                    var nextMilestonesCount = $(response.html)
                        .filter('.processes')
                        .find('.process-milestone-container').length;

                    // Call to "refresh" this step, so any redux-land
                    // listeners can fire appropriate actions to keep our
                    // store in sync
                    if (stepId) {
                        Process.refreshStep(stepId);
                    }

                    if (window.currentLead) {
                        Process.reloadProcesses();
                        window.currentLead.getStages();
                        window.currentLead.fetch();
                    }
                },
                error: function(response) {
                    var message =
                        response && response.message
                            ? response.message
                            : 'This step cannot be completed';

                    Popmini.error(message, {
                        orientation: 'north-east',
                        attachTo: icon,
                    });
                    step.removeClass('state-loading');
                    ajaxProcessStepRunning = false;
                },
            });
        },

        uncompleteStep: function(event, step, stepId, icon) {
            Popmini.question(
                'Are you sure you want to uncomplete this step?',
                {orientation: 'north-east', attachTo: icon},
                function(didClickYes) {
                    if (!didClickYes) {
                        ajaxProcessStepRunning = false;
                        return;
                    }

                    step.removeClass('status-completed').addClass('state-loading status-workable');

                    var isStepDetached = step.closest('.processes').length === 0;
                    Nut.ajax.post({
                        url: '/step/uncomplete/' + stepId + '.json',
                        data: {
                            returnStepOnly: isStepDetached,
                            includeMeta: isStepDetached,
                            template: Process.getStepTemplate(step),
                        },
                        success: function(response) {
                            // Call to "refresh" this step, so any redux-land
                            // listeners can fire appropriate actions to keep our
                            // store in sync
                            if (stepId) {
                                Process.refreshStep(stepId);
                            }

                            if (window.currentLead) {
                                window.currentLead.fetch();
                                Process.reloadProcesses();
                            }
                        },
                        error: function(response) {
                            var message =
                                response && response.message
                                    ? response.message
                                    : 'This step cannot be un-completed';

                            Popmini.error(message, {
                                orientation: 'north-east',
                                attachTo: icon,
                            });
                            step.addClass('status-completed').removeClass(
                                'state-loading status-workable'
                            );
                            ajaxProcessStepRunning = false;
                        },
                    });
                }
            );
        },

        // Fires a window event which allows any listeners to respond
        // accordingly. Currently just used to alert redux that a step has
        // changed, and it should update its store with the appropriate
        // information
        refreshStep: function(id) {
            $(window).trigger(Process.events.StepDidSubmitSuccessfully, id);
        },
    };
})(jQuery);
Lead = Nut.modules.Lead = (function($) {
    return {
        dragDropOverlay: undefined,
        timeline: undefined,

        opt: {
            filterSlideSpeed: 200,
        },

        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },
        events: {
            LeadBackboneModelDidUpdate: 'LeadBackboneModelDidUpdate',
        },
        init: {
            setUpEvents: function() {
                $(document).on('change', '#lead .lead-sidebar-market select', Lead.editMarket);

                $(document).on('click', '#lead .action-new-activity', Lead.openNewActivityModal);

                $(document).on(
                    'click',
                    '#lead .mailchimp-popover .action-export-mailchimp',
                    Lead.exportToMailChimp
                );

                // reload process on card add/delete
                $(document).on(
                    [Nut.events.card.wasAdded, Nut.events.card.wasDeleted].join(' '),
                    '#lead',
                    Process.reloadProcesses
                );

                // reload activities on activity delete
                $(document).on(Nut.events.global.entityWasDeleted, function(event, eventDict) {
                    if (eventDict.entityName == 'Activities') Lead.reloadActivities();
                });

                // card events
                $(document).on(
                    Nut.events.card.wasAdded,
                    '#lead .product-card-container',
                    function() {
                        $(this)
                            .find('.popover-opener')
                            .trigger('click');
                    }
                );

                $(document)
                    .on(Nut.events.card.wasAdded, '#lead .column-accounts-contacts', function(
                        event
                    ) {
                        window.currentLead.addRelationFromCard($(event.target));
                    })
                    .on(
                        [Nut.events.card.wasDeleted, Nut.events.card.transientWasDeleted].join(' '),
                        '#lead .column-accounts-contacts',
                        function(event) {
                            // not sure if this ever gets called from MappingController::deleteMapAction, or if it will always return markup and proc
                            // reloadCardEnclosure, but just in case
                            window.currentLead.removeRelationFromCard($(event.card));
                        }
                    );

                // don't allow duplicate accounts/products/competitors/contacts/sources
                $(document).on(Nut.events.card.willBeAdded, '#lead .tile-container', function(
                    event
                ) {
                    var isEqual = function($card, resultData) {
                        return (
                            $card.data('id') == resultData.entityId ||
                            ($card.data('entityName') == resultData.entityName &&
                                $card.data('entityId') == resultData.entityId)
                        );
                    };
                    if (
                        event.resultData &&
                        !$('.product-type-service').size() &&
                        $('.tile-container, .capsule-container')
                            .filter(function() {
                                return isEqual($(this), event.resultData);
                            })
                            .size()
                    ) {
                        Popmini.error('Cannot add duplicate items', {
                            // attach to parent if capsule container invisible, which happens with
                            // sources since they are hidden on blur
                            attachTo: $(this).is(':visible') ? $(this) : $(this).parent(),
                        });
                        event.preventDefault();
                    }
                });

                $(document).on(Nut.events.card.wasDeleted, '#lead #lead-accounts', function(event) {
                    // @hack if the deleted card was the primary one and there exists another mapped
                    // account, then it must have been promoted to primary, so reload it
                    var firstAccountCard = $(this).find('.account-card-container:visible:first');
                    if (
                        event.card.find('.account-card').is('.account-card-primary') &&
                        firstAccountCard.size()
                    ) {
                        Nut.ajax.get({
                            url: '/mapping/reload.json',
                            data: {
                                childEntityName: 'PrimaryAccount',
                                entityName: 'Leads',
                                entityId: Lead.getId(),
                            },
                            success: function(response) {
                                firstAccountCard.replaceWith(response.html);
                                Nut.updateAppearances();
                            },
                        });
                    }
                });

                // product popovers
                $(document).on('click', '#lead .product-card.popover-opener', function(event) {
                    // can't use event.isPropagationStopped since card events attach to doc
                    if ($(event.target).is('.button')) return;

                    var url =
                        '/lead/product-popover/' +
                        $(this)
                            .closest('.product-card-container')
                            .data('mapId') +
                        '.json';
                    Popover.load({
                        url: url,
                        appendTo: $(this).closest('.float-object-container'),
                    });
                });

                $(document).on('FormPostEnd', '.lead-popover-product form', function(
                    event,
                    result
                ) {
                    Lead.reloadCardEnclosure();
                });

                // win lead popover table arithmetic
                $(document).on('keyup', '.lead-popover-win input', Lead.recalculateWinLeadForm);

                // product form arithmetic
                $(document).on('keyup', '.lead-popover-product input', Lead.recalculateProductForm);

                // delete lead from cancel popover
                $(document).on(
                    'click',
                    '.lead-popover-cancel .action-delete',
                    Lead.deleteWasClicked
                );

                // quick log
                $(document).on('click', '#lead .action-quicklog', function(event) {
                    var timelineCreator = $('.timeline-creator');

                    if (!timelineCreator.is('.state-activity'))
                        timelineCreator.find('.action-activity').trigger('click');

                    $.scrollTo(timelineCreator, 300, {
                        onAfter: function() {
                            timelineCreator.find('textarea').focus();
                        },
                    });
                });

                $(document).on('change', '#lead .assigned input[name="assignee"]', function() {
                    var $this = $(this);
                    var assignee = $this.select2('data');
                    var stepId = $this
                        .closest('.step')
                        .find('input[name="stepId"]')
                        .val();
                    if (assignee) {
                        assignee = {entityName: assignee.type, entityId: assignee.id};
                        Nut.ajax.post({
                            url: '/step/assign/' + stepId + '.json',
                            data: assignee,
                        });
                    }
                });

                $(document).on(
                    'select2-removed',
                    '#lead .assigned input[name="assignee"]',
                    function(e) {
                        var $this = $(this);
                        var stepId = $this
                            .closest('.step')
                            .find('input[name="stepId"]')
                            .val();
                        Nut.ajax.post({
                            url: '/step/unassign/' + stepId + '.json',
                            data: {entityId: e.val},
                        });
                    }
                );

                //lead close/cancel
                $(document).on('validate-outcome', '#lead form.async', function(event) {
                    if ($('select[name="outcomeId"] option:selected', $(this)).val() == 'null') {
                        Popmini.error('Please select a reason from the drop-down', {
                            attachTo: $(this),
                        });
                        $(this).addClass('state-error');
                        return false;
                    }
                });

                $(document).on('beforeunload', '#lead', Lead.pageIsUnloading);
            },

            updateAppearances: $.noop,

            preparePage: function() {
                if (!$('#lead').length) return;

                Lead.initializeSidebarEipContainers();
                Lead.setUpViews();
            },
        },

        setUpViews: function() {
            // our stubby hodgepodge view until we have better backbone support
            if (!_.isUndefined(window.currentLead)) {
                Nut.Model.Leads.findOrCreate({id: window.currentLead.id}).fetch({
                    success: function(model, response, options) {
                        // instantiate all of the relations now that the compound documents have been parsed
                        _.invoke(model.getRelations(), 'findRelated');

                        window.currentLead = model;

                        // Create event the "old fashioned" way for IE support
                        var event = document.createEvent('Event');
                        event.initEvent('Lead initialized', true, true);
                        document.dispatchEvent(event);

                        this.timelineCreatorRecipients = new Nut.View.timelineCreatorRecipients({
                            model: window.currentLead,
                            el: $('div.timeline-form'),
                        });

                        if (typeof filepickerClient !== 'undefined') {
                            var dropTargets = [
                                new Nut.View.DropTarget({
                                    collection: window.currentLead.getFiles(),
                                }),
                            ];
                            // Now that we could be client routed, need to ensure we don't create more than one overlay
                            if (this.dragDropOverlay) {
                                this.dragDropOverlay.registerDropTargets(dropTargets);
                            } else {
                                this.dragDropOverlay = new Nut.View.DragDropOverlay({
                                    dropTargets: dropTargets,
                                });
                            }
                            this.dragDropOverlay.render();
                        }

                        //timeline
                        Lead.timeline = Timeline3.setUpFeed({
                            entityId: window.currentLead.id,
                        });

                        window.currentLead.on('change:links_owner', function() {
                            Timeline.refreshTimeline();
                        });

                        // Firehose of events for react components
                        // to respond to any lead model changes
                        window.currentLead.on('change', function() {
                            $(window).trigger(Lead.events.LeadBackboneModelDidUpdate);
                        });
                    },
                    error: function(model, xhr, options) {
                        if (xhr.status !== 0) {
                            Nut.error(
                                'error fetching lead',
                                model.type,
                                model.get('id'),
                                xhr.status,
                                xhr.statusText
                            );
                        } else {
                            $('div.failedloading.flash-message-error').html(
                                'The current page did not finish loading because the request was interrupted. ' +
                                    'Some functionality may be unavailable until the page is reloaded.'
                            );
                        }
                        $('body').addClass('state-failedloading');
                    },
                });
            }
        },

        switchHistoryTab: function(filterAnchor) {
            Nut.ajax.get({
                url: filterAnchor.attr('href'),
                success: function(json) {
                    var history = $('#lead-history');
                    $('.history-table', history).remove();
                    history.append(json.message);
                    history.data('url', filterAnchor.attr('href'));
                    history.data('offset', '0');
                    $('.state-selected', history).removeClass('state-selected');
                    filterAnchor.parents('li').addClass('state-selected');
                    Nut.updateAppearances();
                },
            });
        },

        reloadActivities: function() {
            if (!$('#lead').length) return;

            Nut.ajax.get({
                url: '/lead/activities/' + Lead.getId() + '.json',
                success: function(response) {
                    $('#lead .activities-container').replaceWith(response.html);
                },
            });
        },

        reloadCustomFields: function() {
            Nut.ajax.get({
                url: '/lead/top-custom-fields/' + Lead.getId() + '.json',
                success: function(response) {
                    var $topCustomFieldSection = $('#lead section.top-custom-fields');
                    $topCustomFieldSection.html(response.html);

                    Form.init.updateAppearances($topCustomFieldSection);
                },
            });

            Nut.ajax.get({
                url: '/lead/sidebar-custom-fields/' + Lead.getId() + '.json',
                success: function(response) {
                    $('#lead .custom-fields-section').html(response.html);

                    Lead.initializeSidebarEipContainers();
                },
            });
        },

        // Not alphabetical, gabe said it was ok
        initializeSidebarEipContainers: function() {
            // sidebar custom fields
            // used to be done in form.js .fieldblock, but working to deprecate that
            $('.lead-sidebar .eip-container:not(.eip-ready):visible').each(function() {
                EditInPlace.factory(
                    this,
                    $.extend(
                        {},
                        {
                            editOnContainerClick: true,
                        },
                        $(this).data()
                    )
                );
            });
        },

        reloadCardEnclosure: function() {
            if (!$('#lead').length) return;

            // refresh the current lead to update the accounts/contacts relationships
            window.currentLead.fetch();
            var enclosureReplace = function(newEnclosure) {
                $('.lead-sidebar-top').replaceWith(newEnclosure);
                Form.init.updateAppearances($('.lead-sidebar-top'));
                Global.init.updateAppearances(); // for the select-mini
                window.setTimeout(Dupes.checkAll, 3000);
            };

            if (arguments.length && arguments[0]) {
                enclosureReplace(arguments[0]);
                return;
            }

            Nut.ajax.get({
                url: '/lead/card-enclosure/' + Lead.getId() + '.json',
                success: function(response) {
                    enclosureReplace($(response.html));
                },
            });
        },

        deleteWasClicked: function(event) {
            event.preventDefault();

            Popmini.question(
                'Are you sure you want to delete this lead?',
                {
                    attachTo: $(event.target),
                    orientation: 'south',
                },
                function(yes) {
                    if (!yes) return;

                    Nut.ajax.post({
                        data: {csrfToken: Nut.getCsrfTokenFromDom()},
                        // reload result
                        url: '/lead/delete-lead/' + Lead.getId() + '.json',
                    });
                }
            );
        },

        hardDeleteWasClicked: function(event) {
            event.preventDefault();

            Popmini.question(
                'Are you sure want to permanently delete this lead? This is a destructive operation that can never be undone.',
                {
                    attachTo: $(event.target),
                    orientation: 'south',
                },
                function(yes) {
                    if (!yes) return;

                    Nut.ajax.post({
                        url: '/lead/' + Lead.getId() + '/hard-delete.json',
                    });
                }
            );
        },

        recalculateWinLeadForm: function(event) {
            var popover = $('div.lead-popover-win');

            if (!Form.validateClientSide(popover)) {
                return true;
            }

            var total = 0;
            popover.find('table.products tr').each(function() {
                var price = $(this)
                        .find('td.price input')
                        .val(),
                    quantity = $(this)
                        .find('td.quantity input')
                        .val();

                price = (price && price.parseLocalFloat()) || 0;
                quantity = (quantity && quantity.parseLocalFloat()) || 0;

                if (quantity > 0) {
                    total += price * quantity;
                }
            });

            popover.find('.price-final span').text(total.toLocalString(2));
            return true;
        },

        recalculateProductForm: function(event) {
            var popover = $('div.lead-popover-product');

            if (!Form.validateClientSide(popover)) {
                return true;
            }

            var keyCode = event.keyCode || event.which;
            if (keyCode === 9) {
                // Tab-up means we moved from another textbox and this one didn't change
                return true;
            }

            var originalPrice = popover
                    .find('.price-original')
                    .text()
                    .parseLocalFloat(),
                price = popover
                    .find('.price-per input')
                    .val()
                    .parseLocalFloat(),
                quantity = popover
                    .find('.quantity input')
                    .val()
                    .parseLocalInt(),
                discount = popover
                    .find('.discount input')
                    .val()
                    .parseLocalInt();
            if (isNaN(price)) {
                price = 0;
            }
            if (isNaN(quantity)) {
                quantity = 0;
            }

            var fieldEdited = event.currentTarget.name;

            if (fieldEdited === 'price') {
                // Modify discount
                var discountPercentage;
                if (originalPrice === 0 || originalPrice === price) {
                    discountPercentage = 0;
                } else if (originalPrice > price) {
                    discountPercentage = 100 - Math.round((price / originalPrice) * 100);
                } else {
                    discountPercentage = (Math.round((price / originalPrice) * 100) - 100) * -1;
                }

                popover.find('.discount input').val(discountPercentage.toLocalString(0));
            } else if (fieldEdited === 'discount') {
                // Modify price-per
                price = originalPrice * (1 - discount / 100) || 0;
                popover.find('.price-per input').val(price.toLocalString(2));
            }

            // Calculate and set total price no matter what was edited
            popover.find('.price-total span').text((price * quantity).toLocalString(2));

            return true;
        },

        editMarket: function(event) {
            Nut.ajax.post({
                url: '/lead/edit-market/' + Lead.getId() + '.json',
                data: {marketId: $(event.target).val()},
                error: function(response) {
                    Popmini.error(response.messages.join(', '), {
                        attachTo: $('.lead-sidebar-market'),
                    });
                    $('.lead-sidebar-market').blinkError();
                },
            });
        },

        openNewActivityModal: function(event) {
            Modal.open({
                url: '/activity/view/new',
                urlData: {leadId: Lead.getId()},
            });
        },

        exportToMailChimp: function(e) {
            e.preventDefault();
            // Ensure we are currently on a lead page.
            if (!$('#lead-name').length || !window.currentLead) {
                return;
            }

            var postData = {
                listId: $('#mailchimp-list-id').val(),
                sendWelcome: $('#mailchimp-send-welcome').is(':checked') ? 1 : 0,
                target: 'MailChimp',
                modelId: window.currentLead.id,
            };

            Nut.ajax.post({
                url: '/leads/export',
                data: postData,
                success: function(response) {
                    Popover.close();
                    Popmini.open('Exporting lead to MailChimp. It should be visible shortly.', {
                        attachTo: $('#lead-name'),
                        classes: 'type-message',
                        orientation: 'south',
                    });
                },
            });
        },

        getId: function() {
            if (!$('#lead-enclosure').length) return null;
            return $('#lead-enclosure').data('entityId');
        },

        getApiId: function() {
            if (!$('#lead-enclosure').length) return null;
            return $('#lead-enclosure').data('apiId');
        },

        pageIsUnloading: function() {
            var stillEditingFields = $('#lead').find('.eip-container.state-editing');
            if (stillEditingFields.length) {
                stillEditingFields.each(function() {
                    $(this)
                        .data('eip')
                        .saveAndClose();
                });
            }
        },

        unloadLead: function() {
            window.currentLead = undefined;

            if (this.timeline) {
                this.timeline.tearDown();
            }
        },
    };
})(jQuery);
Timeline = Nut.modules.Timeline = (function($) {
    return {
        events: {
            TimelineRefresh: 'TimelineRefresh',
            TimelineLoadMore: 'TimelineLoadMore',
            TimelineDidFilter: 'TimelineDidFilter',
            UpdateLastContactedTime: 'UpdateLastContactedTime',
        },

        template: undefined, // Used to store the current email template

        states: {
            note: 'note',
            activity: 'activity',
        },

        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },
        init: {
            setUpEvents: function() {
                $(document)
                    .on('click', '.timeline-creator .toggle', Timeline.creatorToggleWasClicked)
                    .on('click', '.timeline-creator .action-submit', function() {
                        Timeline.creatorWasSubmitted($(this).closest('.timeline-creator'));
                    })
                    .on('keydown', '.timeline-creator textarea', function(event) {
                        if (event.ctrlKey || event.metaKey) {
                            if (event.which == Nut.keys.RETURN) {
                                event.preventDefault();
                                Timeline.creatorWasSubmitted($(this).closest('.timeline-creator'));
                            }
                        }
                    })
                    // listen for [enter] when selects are focused.
                    // note that browsers ignore the enter press when selecting an item when the <select> is open
                    // it's impossible to do this with a <form> + submit, unless we have a *visible* <input type="submit">
                    .on('keypress', '.timeline-creator select', function(event) {
                        if (event.which != Nut.keys.RETURN) return true;
                        event.preventDefault();
                        Timeline.creatorWasSubmitted($(this).closest('.timeline-creator'));
                    })

                    .on('click', '.timeline-creator .action-more', function() {
                        Timeline.creatorActivityMore($(this).closest('.timeline-creator'));
                    })

                    // handling the focus style and revealing the action bar when a user begins typing
                    .on('focus', '.timeline-creator textarea', function() {
                        $('div.timeline-creator').addClass('state-focus state-showactionbar');
                    })
                    .on('blur', '.timeline-creator textarea', function() {
                        $('div.timeline-creator').removeClass('state-focus');
                    });

                // listen for changes to activity types
                $(document)
                    .on(
                        'change',
                        '.timeline-creator select[name=activityTypeId]',
                        Timeline.creatorActivityTypeWasChanged
                    )
                    // firefox doesn't fire change events after arrow keys, despite a 12-year-old bug:
                    // https://bugzilla.mozilla.org/show_bug.cgi?id=126379
                    // http://stackoverflow.com/questions/6587059/javascript-onchange-arrow-keys
                    // listen to arrow keys, just in case.
                    .on('keyup', '.timeline-creator select[name=activityTypeId]', function(event) {
                        if (event.which == Nut.keys.UP || event.which == Nut.keys.DOWN) {
                            Timeline.creatorActivityTypeWasChanged.call(this);
                        }
                    });

                $(document)
                    // email expand/collapse
                    .on(
                        'click',
                        '.timeline-container .type-email.state-collapsed .content, .timeline-container .type-email .ellipsis',
                        Timeline.emailBodyWasClicked
                    )
                    .on(
                        'click',
                        '.timeline-container .type-email .action-delete',
                        Timeline.emailDeleteWasClicked
                    )

                    // note delete
                    .on(
                        'click',
                        '.timeline-container .type-note .action-delete',
                        Timeline.noteDeleteWasClicked
                    )

                    // upcoming activities (technically still timelines)
                    .on('click', '.timeline-upcoming .timeline-entry.type-activity', function() {
                        Modal.open({url: '/activity/view/' + $(this).data('entityId')});
                    });
            },

            preparePage: function() {
                var $timelineCreator = $('div.timeline-creator');
                // note that this code depends (and always has) on timeline creators existing at initial page load
                if ($timelineCreator.length) {
                    var textarea = $timelineCreator.find('textarea.mention');

                    textarea.elastic({breathingRoom: false});
                    Mentions.MentionsInput(textarea);
                    $timelineCreator
                        .find('.placeholder_wrapper .placeholder_overlay')
                        .css('z-index', 1);

                    window.onbeforeunload = function() {
                        if (
                            $timelineCreator.length &&
                            Timeline.getInputValue($timelineCreator).length
                        ) {
                            return 'You have an unsaved timeline entry that will be lost if you leave or reload this page.';
                        }
                    };
                }
            },
        },

        getTimelineContainer: function() {
            return $('div.timeline-creator')
                .siblings('.timeline-container')
                .first();
        },

        // this is mostly relic from timeline2 feed, but still used by some creator stuff, so i'm leaving it
        getLoadData: function(timelineContainer) {
            var data = {
                entityName: timelineContainer.data('entityName'),
                entityId: timelineContainer.data('entityId'),
            };

            return data;
        },

        creatorToggleWasClicked: function() {
            var creator = $(this).closest('.timeline-creator'),
                clickedItemMatch = $(this)
                    .attr('class')
                    .match(/action-(\w+)/);
            // Email tab doesn't have an action, so this won't fire for emails
            if (clickedItemMatch) {
                Timeline.creatorToggleToState(creator, clickedItemMatch[1]);
            }
        },

        creatorToggleToState: function(creator, state, options) {
            if (options === undefined) options = {};

            var $input = creator.find('textarea');
            if (creator.hasClass('state-' + state) && !_.isEmpty($input.val())) {
                return;
            }
            // update creator state
            creator.removeClass('state-note state-activity').addClass('state-' + state);

            // update placeholder
            creator
                .find('.mention')
                .attr('placeholder', creator.find('.textarea-container .l10n-' + state).text());

            var $activityTypeId = creator.find('select[name=activityTypeId]'),
                clearPlaceholder = false,
                inputVal = $input.val().replace(/\s+$/, '');

            if (
                state === 'activity' &&
                $activityTypeId.val() === 'null' &&
                $activityTypeId.children().length > 1
            ) {
                $activityTypeId.val($activityTypeId.children()[1].value);
                if (!options.field) $input.focus();
            } else if (state === 'note') {
                $activityTypeId.val('null');
                if (!options.field) $input.focus();
            }

            // force elastic refresh
            $input.keyup();

            if (options.scrollTo) {
                creator[0].scrollIntoView(true);
            }

            if (options.field) {
                creator.find(options.field).focus();
            }
        },

        // if type == null, make a note, if id, make an activity
        creatorActivityTypeWasChanged: function() {
            var creator = $(this).closest('.timeline-creator'),
                newValue = $(this).val();

            if (newValue == 'null') {
                Timeline.creatorToggleToState(creator, 'note');
            } else {
                Timeline.creatorToggleToState(creator, 'activity');
            }
        },

        creatorWasSubmitted: function(timelineCreator) {
            var timelineContainer = $(timelineCreator)
                .siblings('.timeline-container')
                .first();

            if (timelineCreator.is('.state-note')) {
                Timeline.creatorPostNote(timelineContainer, timelineCreator);
            } else if (timelineCreator.is('.state-activity')) {
                Timeline.creatorPostActivity(timelineContainer, timelineCreator);
            }
        },

        creatorPostNote: function(timelineContainer, timelineCreator) {
            var containerData = timelineContainer.data(),
                dataToPost = {
                    note: Timeline.creatorGetText(timelineCreator),
                    entityName: containerData.entityName,
                    entityId: containerData.entityId,
                };
            if (!dataToPost.note.length) return;

            Nut.ajax.post({
                url: '/timeline/new-note.json',
                data: dataToPost,
                success: function() {
                    Timeline.creatorDidCreateSuccessfully(timelineContainer, timelineCreator);

                    try {
                        Analytics.trackEvent(
                            Analytics.categories.usage,
                            Analytics.actions.usage.addTimelineNote,
                            {parentType: containerData.entityName.toLowerCase()}
                        );
                    } catch (e) {
                        Nut.log('Error tracking ', e);
                    }
                },
            });
        },

        creatorPostActivity: function(timelineContainer, timelineCreator) {
            var dataToPost = {
                id: 'new',
                logNote: Timeline.creatorGetText(timelineCreator),
                participants: ['self'],
                isLogged: 1,
                activityTypeId: timelineCreator.find('select[name=activityTypeId]').val(),
                activityDuration: timelineCreator.find('select[name=activityDuration]').val(),
            };

            var containerData = timelineContainer.data();
            if (containerData.entityName == 'Leads') {
                dataToPost.leadIds = [containerData.entityId.toString()];
                dataToPost.dontAddRelatedLeads = 1;
                dataToPost.addCompaniesFromLead = 1;
                dataToPost.addPeopleFromLead = 1;
            } else if (
                containerData.entityName == 'Contacts' ||
                containerData.entityName == 'Accounts'
            ) {
                dataToPost.participants.push({
                    entityType: containerData.entityName,
                    id: containerData.entityId,
                });
                dataToPost.dontAddRelatedLeads = 0;
            }

            Nut.ajax.post({
                url: '/activity/quick-create.json',
                data: dataToPost,
                success: function(response) {
                    Timeline.creatorDidCreateSuccessfully(timelineContainer, timelineCreator);
                    Timeline.updateLastContactedTime();
                    Activity.refreshActivities(response.activityId);

                    try {
                        // analytics KIT tracking
                        if (containerData.keepInTouch) {
                            Analytics.trackEvent(
                                Analytics.categories.usage,
                                Analytics.actions.usage.logTimelineActivityViaKIT,
                                {parentType: containerData.entityName.toLowerCase()}
                            );
                        }
                        Analytics.trackEvent(
                            Analytics.categories.usage,
                            Analytics.actions.usage.logTimelineActivity,
                            {parentType: containerData.entityName.toLowerCase()}
                        );
                    } catch (e) {
                        Nut.log('Error tracking ', e);
                    }
                },
            });
        },

        creatorGetText: function(timelineCreator) {
            return timelineCreator.find('textarea').data('messageText');
        },

        creatorActivityMore: function(timelineCreator) {
            var timelineContainer = $(timelineCreator)
                    .siblings('.timeline-container')
                    .first(),
                data = timelineContainer.data();
            data.note = timelineCreator.find('textarea').data('messageText');
            data.page = 1;

            data.participants = [];
            if (data.entityName == 'Leads') {
                data.leadId = data.entityId;
                data.dontAddRelatedLeads = 1;
            } else if (data.entityName == 'Contacts' || data.entityName == 'Accounts') {
                data.participants.push({entityName: data.entityName, entityId: data.entityId});
            }

            data.id = 'new';
            data.activityTypeId = timelineCreator.find('select[name=activityTypeId]').val();
            data.activityDuration = timelineCreator.find('select[name=activityDuration]').val();
            data = $.extend(data, timelineCreator.getEipData());

            // clear the text here, unlike notes, where we wait for successful saving
            timelineCreator.find('textarea').mentionsInput('reset');

            Modal.open({
                url: '/activity/view/new',
                urlData: data,
                openerElement: timelineCreator.find('.action-submit'),
            });
        },

        creatorDidCreateSuccessfully: function(timelineContainer, timelineCreator) {
            timelineCreator
                .find('textarea')
                .mentionsInput('reset')
                .keyup();
            Timeline.refreshTimeline();
        },

        refreshTimeline: function() {
            var timelineContainer = Timeline.getTimelineContainer();
            timelineContainer.trigger(Timeline.events.TimelineRefresh);
        },

        updateLastContactedTime: function() {
            var timelineContainer = Timeline.getTimelineContainer();
            timelineContainer.trigger(Timeline.events.UpdateLastContactedTime);
        },

        emailBodyWasClicked: function(event) {
            // stop propagation so that the ellipisis and body events don't cancel each other out
            event.stopPropagation();
            var emailRow = $(this).closest('.timeline-entry');
            if ($(this).containsSelection()) return;

            if ($(this).closest('.variant-mini').length) return;

            // don't swap the states until we have the info loaded, much smoother
            if (emailRow.is('.state-collapsed') && !emailRow.is('.state-loaded')) {
                Nut.loadingHudStart();
                Nut.ajax.get({
                    url: '/email/body/' + emailRow.data('entityId') + '.json',
                    success: function(result) {
                        emailRow
                            .toggleClass('state-expanded state-collapsed')
                            .addClass('state-loaded')
                            .find('.body')
                            .html(result.html);
                        Nut.loadingHudStop();
                    },
                    error: function(result) {
                        Nut.loadingHudStop();
                        return Popmini.error('Error loading message: ' + result.message, {
                            attachTo: emailRow,
                        });
                    },
                });
            } else {
                emailRow.toggleClass('state-expanded state-collapsed');
            }
        },

        emailDeleteWasClicked: function(event) {
            // stop propagation so it doesn't fetch the email body
            event.stopPropagation();
            var emailRow = $(this).closest('.timeline-entry.type-email'),
                timelineContainer = $(this).closest('.timeline-container');

            Popmini.question(
                'Are you sure you want to delete this email?',
                {attachTo: emailRow.find('.action-delete')},
                function(didClickYes) {
                    if (!didClickYes) return;

                    Nut.ajax.post({
                        data: Timeline.getLoadData(timelineContainer),
                        url: '/timeline/delete-email/' + emailRow.data('entityId') + '.json',
                        success: function(result) {
                            if (emailRow.closest('tr.activity-detail').length) {
                                Timeline.fadeAndRemoveRow(emailRow.closest('tr.activity-detail'));
                            } else {
                                Timeline.fadeAndRemoveRow(emailRow);
                            }
                        },
                    });
                }
            );
        },

        noteDeleteWasClicked: function() {
            var noteRow = $(this).closest('.timeline-entry.type-note');
            var timelineContainer = $(this).closest('.timeline-container');
            var data = Timeline.getLoadData(timelineContainer);

            Popmini.question(
                'Are you sure you want to delete this note?',
                {attachTo: noteRow.find('.action-delete')},
                function(didClickYes) {
                    if (!didClickYes) return;

                    Nut.ajax.get({
                        data: data,
                        url: '/timeline/delete-note/' + noteRow.data('entityId') + '.json',
                        success: function(result) {
                            Timeline.fadeAndRemoveRow(noteRow);
                        },
                    });
                }
            );
        },

        fadeAndRemoveRow: function(row) {
            row.animate(
                {
                    height: 0,
                    paddingTop: 0,
                    paddingBottom: 0,
                    opacity: 0,
                },
                400,
                function() {
                    row.remove();
                }
            );
        },

        /**
         * Gets the value of the timeline creator input.
         *
         * @param  {Object} $timelineCreator the timeline-creator element
         * @return {string} The value of the input, with trailing whitespace removed.
         */
        getInputValue: function($timelineCreator) {
            var $input = $timelineCreator.find('.textarea-container textarea'),
                inputVal = $input.val().replace(/\s+$/, '');
            return inputVal;
        },

        /**
         *
         * @param {Object}  [$el] either the timeline-creator itself, or the calling element used to find the closest timeline-creator
         * @param {string}  [state] State to open; one of "note", "activity", "email" (see Timeline.states)
         * @param {string}  [options] associative array containing options
         * @param {string}  [options.field] Selector to focus an element within the creator after opening (see Timeline.fields)
         * @param {boolean} [options.scrollTo] Scroll to the timeline creator after it's opened
         */
        openCreator: function($el, state, options) {
            var creator = $el.is('.timeline-creator') ? $el : $el.closest('.timeline-creator');
            options = _.extend({}, options);

            Timeline.creatorToggleToState(creator, state, options);
        },
    };
})(jQuery);
Timeline3 = Nut.modules.Timeline3 = (function($) {
    return {
        go: function() {
            return;
        },

        events: {},
        init: {},

        setUpFeed: function(options) {
            var options = _.extend(
                    {
                        filters: false,
                        entityId: false,
                        displayComments: true,
                        el: $('.timeline3-list'),
                        pollTimeout: 30000,
                        pollBackgroundTimeout: 120000,
                    },
                    options
                ),
                timelineEl = options.el;

            if (timelineEl.length) {
                //create events collection with passed filters
                var timelineEventCollection = new Nut.Model.EventsCollection({
                    queryFilters: options.filters,
                    entityId: options.entityId,
                });

                //create timeline view and pass collection
                var eventsView = new Nut.View.timelineEvents({
                    el: timelineEl,
                    collection: timelineEventCollection,
                    entity: options.entityId ? true : false,
                    displayComments: options.displayComments,
                    pollTimeout: options.pollTimeout,
                    pollBackgroundTimeout: options.pollBackgroundTimeout,
                });

                //initial fetch and css class handling
                timelineEventCollection.fetch({
                    success: function(model, response) {
                        //prevents race condition where 'stopLoading' was called (via the initialize function),
                        //but user had already scrolled far enough to force another 'loadPrevious', AND the
                        //current request hadn't finished yet.
                        eventsView.$el.removeClass('state-loading');

                        //filtering can return an empty state now, don't want to show the
                        //default 'no-more-entries' view
                        if (!response || !response.events.length)
                            eventsView.$el.addClass('state-empty');

                        //this check also needs to occur here because only the subsequent requests actually hit
                        //the 'fetchMeta' method, which handles all display classes.
                        if (response && response.meta.last)
                            eventsView.$el.addClass('no-more-entries');
                    },
                    error: function(model, response) {
                        eventsView.$el.removeClass('state-loading');
                    },
                });

                return eventsView;
            }
        },
    };
})(jQuery);
/*
 * uploader.js
 *
 * @author Daniel Bergey
 * @copyright (2010) Nutshell, LLC
 *
 *  Contains logic uploading files and avatars.
 */
Uploader = Nut.modules.Uploader = (function($) {
    var self = this;

    return {
        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },
        init: {},
        setup: function(selector, options) {
            options = $.extend(
                {
                    messageOrientation: 'north',
                    progress: $.noop,
                    success: $.noop,
                    submit: $.noop,
                    cancel: $.noop,
                    error: $.noop,
                },
                options
            );

            this.setUpFileUploader(selector, options);
        },
        setUpFileUploader: function(selector, options) {
            var el = $(selector);
            if (!el.length) return;
            Nut.log('Set up file uploader within element:', el.selector);
            // generalized image-upload script
            $(el)
                .not('> input[type=file]')
                .each(function() {
                    var btn = $(this);
                    Nut.log('Setting up file uploader for button: ', btn.get(0));
                    var uploader = new qq.FileUploaderBasic({
                        button: btn.get(0),
                        action: btn.data('uploadAction'),
                        multiple: false,
                        allowedExtensions: options.imagesOnly ? ['jpg', 'jpeg', 'png', 'gif'] : [],
                        sizeLimit: 0,
                        minSizeLimit: 0,
                        onSubmit: function(id, fileName) {
                            Nut.log('File upload started:', id, fileName);
                            btn.addClass('state-progress');
                            btn.append('<div class="upload-progress-indicator" />');
                            btn.find('input[type=file]').attr('disabled', 'disabled');
                            options.submit.call(btn);
                        },
                        onProgress: function(id, fileName, loaded, total) {
                            Nut.log('File upload progressing: ', id, fileName, loaded, total);
                            btn.attr(
                                'class',
                                btn.attr('class').replace(/state-progress-\d\d?/, '')
                            );
                            btn.addClass('state-progress');
                            btn.addClass('state-progress-' + Math.floor(loaded / total / (1 / 16)));
                            options.progress.call(btn);
                        },
                        onComplete: function(id, fileName, responseJSON) {
                            if (responseJSON.success) {
                                Nut.log('File upload completed:', id, fileName, responseJSON);
                                options.success.call(btn, responseJSON);

                                // remove progress when new image is loaded
                                btn.attr(
                                    'class',
                                    btn.attr('class').replace(/state-progress-\d\d?/, '')
                                )
                                    .removeClass('state-progress')
                                    .find('.upload-progress-indicator')
                                    .remove();
                                btn.find('input[type=file]').attr('disabled', null);
                            } else {
                                Nut.log('Upload failed:', id, fileName, responseJSON);
                                options.error.call(btn, responseJSON);

                                var errorMessage;
                                if (responseJSON['error']) {
                                    errorMessage = responseJSON['error'];
                                } else if (responseJSON['status'] == 413) {
                                    errorMessage = 'Sorry, the file is too large.';
                                } else {
                                    errorMessage = 'Sorry, the file failed to upload successfully.';
                                }

                                this.showMessage(errorMessage);

                                btn.removeClass('state-progress')
                                    .find('.upload-progress-indicator')
                                    .remove();
                                btn.find('input[type=file]').attr('disabled', null);
                            }
                        },
                        onCancel: function(id, fileName) {
                            Nut.log('Upload cancelled:', id, fileName);
                            btn.removeClass('state-progress');
                        },
                        messages: {
                            typeError:
                                'Please choose an image with one of these file extensions: {extensions}.',
                            sizeError: '{file} is too large. The maximum file size is {sizeLimit}.',
                            minSizeError:
                                '{file} is too small. The minimum file size is {minSizeLimit}.',
                            emptyError: '{file} is empty. Please select files again without it.',
                            onLeave:
                                'A file is currently being uploaded. If you leave this page, the upload will be cancelled.',
                        },
                        showMessage: function(message) {
                            Popmini.error(message, {
                                attachTo: btn,
                                orientation: options.messageOrientation,
                            });
                        },
                    });
                });
        },
    };
})(jQuery);
Dupes = Nut.modules.Dupes = (function($) {
    return {
        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },
        init: {
            setUpEvents: function() {
                document.addEventListener('Peep initialized', function() {
                    setTimeout(Dupes.checkAll, 800);
                });

                document.addEventListener('Lead initialized', function() {
                    setTimeout(Dupes.checkAll, 800);
                });

                $(document)
                    .on('click', '.tile-container .behavior-dupecheck', function(event) {
                        event.preventDefault();
                        event.stopPropagation();
                        Dupes.showPopoverForCard($(this).closest('.tile-container'));
                    })

                    // one-time checks for new cards
                    .on(
                        Nut.events.card.wasAdded,
                        '.account-card-container, .contact-card-container',
                        function() {
                            Dupes.checkElement($(this));
                        }
                    )

                    .on('click', '.popover-duplicate .action-submit', Dupes.mergeWasClicked)
                    .on('click', '.popover-duplicate .action-cancel', Dupes.notDupeWasClicked);
            },
        },

        checkAll: function() {
            if (typeof Peep !== 'undefined' && Peep.getPeepId()) {
                Peep.checkForDuplicates();
            }
            $('div.account-card-container:visible, div.contact-card-container:visible').each(
                function() {
                    Dupes.checkElement($(this));
                }
            );
        },

        checkElement: function(element) {
            if (element.data('entityName') && element.data('entityId')) {
                Dupes.check(element.data('entityName'), element.data('entityId'), element);
            }
        },

        check: function(type, id, element) {
            // ignore deleted elements, since they're quite likely deleted duplicates
            if (element.is('.state-deleted, .state-half-baked')) return;

            // Somehow this can be undefined sometimes, perhaps when page is quickly changed.
            if (!id || !type || id === 'undefined' || type === 'undefined') return;

            if (typeof window.trackJs !== 'undefined') {
                window.trackJs.console.info('id: ' + id + ', type: ' + type);
            }

            Nut.ajax.get({
                url: '/de-duplication/has-duplicates.json',
                data: {
                    entityName: type,
                    entityId: id,
                },
                success: function(response) {
                    Dupes.elementWasChecked(element, response);
                },
                // noop on duplicate error checks
                error: function(response) {
                    if (typeof window.trackJs !== 'undefined') {
                        window.trackJs.track(response);
                    }
                },
            });
        },

        elementWasChecked: function(element, response) {
            element.toggleClass('state-duplicate', response.hasDuplicates);
        },

        showPopoverForCard: function(card) {
            Dupes.showPopover(card.data('entityName'), card.data('entityId'), card);
        },

        showPopover: function(type, id, element) {
            Popover.load({
                url: '/de-duplication/popover.json',
                data: {
                    entityName: type,
                    entityId: id,
                    parentEntityName: element.data('parentEntityName'),
                },
                appendTo: element,
                noLoadingIndicator: true,
                error: function(response) {
                    Dupes.check(type, id, element);
                    Popmini.open(response.message, {attachTo: element, classes: 'type-message'});
                },
            });
        },

        mergeWasClicked: function() {
            var parentElement = Dupes.getParentEntity.call(this),
                popover = $(this).closest('.popover'),
                replacedCard = popover.find('.tile-container'),
                data = {
                    entityName: popover.data('entityName'),
                    entity2Id: popover.data('entityId'),
                    entity1Id: popover.data('entityMergeWithId'),
                };
            Nut.loadingHudStart();
            Nut.ajax.post({
                url: '/de-duplication/merge-entities.json',
                data: data,
                success: function(response) {
                    Nut.loadingHudStop();
                    Dupes.elementWasMerged(parentElement, replacedCard, response);
                },
                error: Dupes.ajaxThrewError,
            });
        },

        elementWasMerged: function(element, replacedCard, response) {
            Popover.close();
            if (element.is('.peep-container')) {
                window.location.reload();
            }
            Card.findIdenticalCards(replacedCard).each(function() {
                if (Card.shouldCardReload($(this))) Card.reloadCard($(this));
            });

            if (element.is('.tile-container') && Card.shouldCardReload(element)) {
                Card.reloadCard(element);
            }

            Lead.reloadCardEnclosure();
        },

        notDupeWasClicked: function() {
            var parentElement = Dupes.getParentEntity.call(this),
                popover = $(this).closest('.popover'),
                data = {
                    entityName: popover.data('entityName'),
                    entity2Id: popover.data('entityId'),
                    entity1Id: popover.data('entityMergeWithId'),
                };
            Nut.ajax.post({
                url: '/de-duplication/mark-not-dupe.json',
                data: data,
                success: function(response) {
                    Popover.close();
                    parentElement.removeClass('state-duplicate');
                    window.setTimeout(Dupes.checkAll, 2000);
                },
                error: Dupes.ajaxThrewError,
            });
        },

        getParentEntity: function() {
            var popover = $(this).closest('.popover'),
                mergeWithId = popover.data('entityMergeWithId'),
                entityName = popover.data('entityName');
            if (typeof Peep !== 'undefined' && Peep.getPeepId() == mergeWithId) {
                return $('.peep-container');
            }
            return $(
                '.tile-container[data-entity-name="' +
                    entityName +
                    '"][data-entity-id="' +
                    mergeWithId +
                    '"]'
            );
        },

        ajaxThrewError: function(response) {
            Nut.loadingHudStop();
            if (response && response.message) {
                Nut.alert('Error: ' + response.message);
            }
            Popover.close();
        },
    };
})(jQuery);
DatePicker = Nut.modules.DatePicker = (function($) {
    return {
        go: $.noop,
        init: {
            setUpEvents: $.noop,
            updateAppearances: $.noop,
        },
        prettify: function(input, datePicker) {
            _.defer(function() {
                if (
                    datePicker &&
                    !datePicker.dpDiv.find('.ui-datepicker-calendar-wrapper').length
                ) {
                    $(
                        '<a class="ui-datepicker-current ui-corner-all" data-handler="today" data-event="click" title="Today"><span class="ui-icon ui-icon-bullet">Current</span></a>'
                    ).prependTo(datePicker.dpDiv.find('.ui-datepicker-header'));
                    var wrapper = $(
                            '<div class="popover-middle ui-datepicker-calendar-wrapper" />'
                        ),
                        nub = $('<div class="popover-nub" />');

                    datePicker.dpDiv.addClass('popover orientation-south state-persist');
                    datePicker.dpDiv.children().wrapAll(wrapper);
                    datePicker.dpDiv.append(nub);

                    datePicker.dpDiv.position({
                        my: 'center top',
                        at: 'center bottom',
                        of: input,
                        offset: '0 4',
                        collision: 'fit',
                    });
                    nub.position({
                        my: 'center bottom',
                        at: 'center top',
                        of: datePicker.dpDiv,
                        offset: '0 1',
                        collision: 'fit',
                    });
                }
            });
        },
    };
})(jQuery);
Tags = Nut.modules.Tags = (function($) {
    return {
        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        init: {
            setUpEvents: function() {
                $('.tags-bar')
                    // adjust height of endcap elements for proper wrappinating
                    .on('keyup', 'input.tags', function() {
                        $(this)
                            .find('.tags-bar-label, .tags-bar-link')
                            .css({height: ''})
                            .css({
                                height: $(this)
                                    .closest('.tags-activated')
                                    .find('ul')
                                    .height(),
                            });
                    });
            },
            updateAppearances: $.noop,
        },

        setUpTagAreas: function() {
            $(this).each(function() {
                Tags.setUpOneTagArea($(this));
            });
        },

        setUpOneTagArea: function(area) {
            if (area.is('.tags-activated')) return;

            var entityNames = area.is('[data-search-entity-names]')
                ? area.data('searchEntityNames')
                : [area.data('entityName')];

            var placeholder = 'Add tags…';

            var containerCss = area.is(
                '.tags-bar-type-bulk-add-lead, .tags-bar-type-bulk-remove-lead, .tags-bar-type-bulk-add-account, .tags-bar-type-bulk-remove-account, .tags-bar-type-bulk-add-contact, .tags-bar-type-bulk-remove-contact'
            )
                ? 'select2-filter select2-filter-bulk-edit'
                : 'select2-filter';

            area.addClass('tags-activated');
            var tagInput = Tags.getSelectInputElement(area).select2({
                multiple: true,
                query: function(query) {
                    query.callback({
                        results: _.chain(Tags.getAvailableTagsForEntityNames(entityNames))
                            .filter(function(cmodel) {
                                return (
                                    cmodel.name
                                        .toUpperCase()
                                        .trim()
                                        .indexOf(query.term.toUpperCase().trim()) >= 0
                                );
                            })
                            .map(function(availabletags) {
                                return {
                                    id: availabletags.id,
                                    text: availabletags.name,
                                    count: availabletags.count,
                                };
                            })
                            .value(),
                    });
                },
                initSelection: function(input, callback) {
                    var ids = input.val().split(','),
                        data = _(ids).map(function(id) {
                            var cmodel = Nut.managedCollections.Tags.get(id),
                                name = cmodel ? cmodel.get('name') : '…';
                            return {id: id, text: name};
                        });
                    callback(data);
                },
                placeholder: placeholder,
                formatResult: Tags.tagFormatResult,
                width: '100%',
                containerCssClass: containerCss,
                createSearchChoice: function(term, data) {
                    var searchChoiceMatchesTag = data.find(function(tag) {
                        return tag.text.toUpperCase().trim() === term.toUpperCase().trim();
                    });

                    if (
                        !searchChoiceMatchesTag &&
                        !Boolean(document.querySelector('.ui-modal__bulk-edit'))
                    ) {
                        return {
                            id: term,
                            count: 0,
                            type: 'tags',
                            tagType: entityNames[0],
                            text: term,
                            name: term,
                            isNew: true,
                        };
                    }
                },
                createSearchChoicePosition: 'bottom',
            });

            area.on('change', function(event) {
                if ($(event.target).is('input.select2-input')) {
                    // ignore events from select2's internal placeholder input, because select2 needs to capture
                    // it so it can update tagInput and trigger a new 'change' event
                    return;
                }
                area.trigger(Nut.events.tag.wasChanged);
            });

            // if a report type, show the operators (now they're always visible on sfilters)
            if (area.is('.tags-bar-type-report-filter')) {
                Tags.showHideOperator(area);
                area.on('change', 'input.tags', function() {
                    Tags.showHideOperator(area);
                });
            }

            // update modal appearances on tag changes, for geometry
            if (area.closest('#modal').length) {
                area.on('change', 'input.tags', Modal.init.updateAppearances);
            }
        },

        // eventually this should return backbone models, but plain objects for BC right now
        getAvailableTagsForEntityNames: function(entityNames) {
            return _(
                Nut.managedCollections.Tags.filter(function(cmodel) {
                    return _(entityNames).include(cmodel.get('tagType'));
                })
            ).map(function(model) {
                return model.toJSON();
            });
        },

        getTagIdsInTagBar: function(tagBar) {
            var tagData = Tags.getSelectInputElement(tagBar).select2('data'),
                tagIds = _(tagData).pluck('id');
            return tagIds;
        },

        getTagOperatorInTagBar: function(tagBar) {
            return $(tagBar)
                .find('input[name=tags-operator]:checked')
                .val();
        },

        entityTagsWereChanged: function(event) {
            var tagBar = $(event.target).closest('.tags-bar'),
                tags = Tags.getSelectInputElement(tagBar).select2('data'),
                entityName = tagBar.data('entityName'),
                entityId = tagBar.data('entityId');

            Nut.ajax.post({
                url: '/tag/update-tags-for-entity.json',
                data: {
                    entityName: entityName,
                    entityId: entityId,
                    tags: tags,
                },
                success: function(response) {
                    // iterate over tag IDs and blink newly-added ones
                    _(Tags.getSelectInputElement(tagBar).select2('data')).each(function(tagData) {
                        // check for edge case where tag was deleted (and thus not added) and remove
                        if (!_(response.currentTagIds).contains(tagData.id)) {
                            $(this)
                                .find('.select2-search-choice-close')
                                .trigger('click');
                        }
                    });

                    // if we created new tags, update the clientside copies, and the managed tags collection
                    if (response && response.createdTags) {
                        var createdTags = response.createdTags;
                        Object.keys(createdTags).forEach(function(clientId) {
                            var tag = tags.find(function(t) {
                                return t.id === clientId;
                            });
                            if (tag) {
                                tag.id = createdTags[clientId];
                                delete tag.isNew;
                                Nut.managedCollections.Tags.add(tag);
                            }
                        });
                    }

                    $('body').trigger(Nut.events.global.entityWasEdited, {
                        entityName: entityName,
                        entityId: entityId,
                    });
                },
            });
        },

        showHideOperator: function(tagBar) {
            var tagIds = Tags.getTagIdsInTagBar(tagBar);
            tagBar.find('.tags-operator').toggle(tagIds.length > 1);
        },

        getSelectInputElement: function(tagBar) {
            return tagBar.find('input.tags-select2');
        },

        tagFormatResult: function(item) {
            var secondaryContentSpan = item.isNew
                ? "<span class='tags-create-new'>+ Create</span>"
                : "<span class='tags-count'>" + item.count + '</span>';
            var containerWidth =
                $(this.element)
                    .closest('.tags-bar')
                    .width() > 200
                    ? '200px'
                    : '100%';
            return (
                "<div class='tags-result' style='width:" +
                containerWidth +
                ";'>" +
                secondaryContentSpan +
                _.escape(item.text) +
                ' ' +
                '</div>'
            );
        },
    };
})(jQuery);
Sources = Nut.modules.Sources = (function($) {
    return {
        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        init: {
            setUpEvents: function() {
                $('.sources-bar')
                    // adjust height of endcap elements for proper wrappinating
                    .on('keyup', 'input.sources', function() {
                        $(this)
                            .find('.sources-bar-label, .sources-bar-link')
                            .css({height: ''})
                            .css({
                                height: $(this)
                                    .closest('.sources-activated')
                                    .find('ul')
                                    .height(),
                            });
                    });
            },
            updateAppearances: $.noop,
        },

        setUpSourceAreas: function() {
            $(this).each(function() {
                Sources.setUpOneSourceArea($(this));
            });
        },

        setUpOneSourceArea: function(area) {
            if (area.is('.sources-activated')) return;

            area.addClass('sources-activated');
            var sourceInput = Sources.getSelectInputElement(area).select2({
                multiple: true,
                query: function(query) {
                    query.callback({
                        results: _.chain(Sources.getAvailableSources())
                            .filter(function(cmodel) {
                                return (
                                    cmodel.name
                                        .toUpperCase()
                                        .trim()
                                        .indexOf(query.term.toUpperCase().trim()) >= 0
                                );
                            })
                            .map(function(availablesources) {
                                return {id: availablesources.id, text: availablesources.name};
                            })
                            .value(),
                    });
                },
                initSelection: function(input, callback) {
                    var ids = input.val().split(','),
                        data = _(ids).map(function(id) {
                            var cmodel = Nut.managedCollections.Sources.get(id),
                                name = cmodel ? cmodel.get('name') : '…';
                            return {id: id, text: name};
                        });
                    callback(data);
                },
                placeholder: 'Add sources…',
                formatResult: Sources.sourceFormatResult,
                width: '100%',
                // Only using this for bulk-edit right now.
                containerCssClass: 'select2-filter select2-filter-bulk-edit',
            });

            // update modal appearances on source changes, for geometry
            if (area.closest('#modal').length) {
                area.on('change', 'input.sources', Modal.init.updateAppearances);
            }
        },

        getAvailableSources: function() {
            return Nut.managedCollections.Sources.map(function(model) {
                return model.toJSON();
            });
        },

        getSourceIdsInSourceBar: function(sourceBar) {
            var sourceData = Sources.getSelectInputElement(sourceBar).select2('data'),
                sourceIds = _(sourceData).pluck('id');

            return sourceIds;
        },

        getSelectInputElement: function(sourceBar) {
            return sourceBar.find('input.sources-select2');
        },

        sourceFormatResult: function(item) {
            var containerWidth =
                $(this.element)
                    .closest('.sources-bar')
                    .width() > 200
                    ? '200px'
                    : '100%';
            return (
                "<div class='sources-result' style='width:" +
                containerWidth +
                ";'>" +
                _.escape(item.text) +
                '</div>'
            );
        },
    };
})(jQuery);
Competitors = Nut.modules.Competitors = (function($) {
    return {
        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        init: {
            setUpEvents: function() {
                $('.competitors-bar')
                    // adjust height of endcap elements for proper wrappinating
                    .on('keyup', 'input.competitors', function() {
                        $(this)
                            .find('.competitors-bar-label, .competitors-bar-link')
                            .css({height: ''})
                            .css({
                                height: $(this)
                                    .closest('.competitors-activated')
                                    .find('ul')
                                    .height(),
                            });
                    });
            },
            updateAppearances: $.noop,
        },

        setUpCompetitorAreas: function() {
            $(this).each(function() {
                Competitors.setUpOneCompetitorArea($(this));
            });
        },

        setUpOneCompetitorArea: function(area) {
            if (area.is('.competitors-activated')) return;

            area.addClass('competitors-activated');
            var competitorInput = Competitors.getSelectInputElement(area).select2({
                multiple: true,
                query: function(query) {
                    query.callback({
                        results: _.chain(Competitors.getAvailableCompetitors())
                            .filter(function(cmodel) {
                                return (
                                    cmodel.name
                                        .toUpperCase()
                                        .trim()
                                        .indexOf(query.term.toUpperCase().trim()) >= 0
                                );
                            })
                            .map(function(availablecompetitors) {
                                return {
                                    id: availablecompetitors.id,
                                    text: availablecompetitors.name,
                                };
                            })
                            .value(),
                    });
                },
                initSelection: function(input, callback) {
                    var ids = input.val().split(','),
                        data = _(ids).map(function(id) {
                            var cmodel = Nut.managedCollections.Competitors.get(id),
                                name = cmodel ? cmodel.get('name') : '…';
                            return {id: id, text: name};
                        });
                    callback(data);
                },
                placeholder: 'Add competitors…',
                formatResult: Competitors.competitorFormatResult,
                width: '100%',
                // Only using this for bulk-edit right now.
                containerCssClass: 'select2-filter select2-filter-bulk-edit',
            });

            // update modal appearances on competitor changes, for geometry
            if (area.closest('#modal').length) {
                area.on('change', 'input.competitors', Modal.init.updateAppearances);
            }
        },

        getAvailableCompetitors: function() {
            return Nut.managedCollections.Competitors.map(function(model) {
                return model.toJSON();
            });
        },

        getCompetitorIdsInCompetitorBar: function(competitorBar) {
            var competitorData = Competitors.getSelectInputElement(competitorBar).select2('data'),
                competitorIds = _(competitorData).pluck('id');

            return competitorIds;
        },

        getSelectInputElement: function(competitorBar) {
            return competitorBar.find('input.competitors-select2');
        },

        competitorFormatResult: function(item) {
            var containerWidth =
                $(this.element)
                    .closest('.competitors-bar')
                    .width() > 200
                    ? '200px'
                    : '100%';
            return (
                "<div class='competitors-result' style='width:" +
                containerWidth +
                ";'>" +
                _.escape(item.text) +
                '</div>'
            );
        },
    };
})(jQuery);
var Task = (Nut.modules.Task = (function($) {
    return {
        go: function() {
            for (var fn in this.init) {
                this.init[fn].apply(this);
            }
        },

        events: {
            TaskDidSubmitSuccessfully: 'TaskDidSubmitSuccessfully',
        },

        defaultRequestOptions: {
            successCallback: null,
            errorDefaultText: 'There was an error updating this task',
            popminiConfirmOrientation: 'north-east',
            popminiErrorOrientation: 'north-east',
        },

        init: {
            setUpEvents: function() {
                $(document)
                    .on(
                        'click focus',
                        '.tasks-container .task.state-collapsed.state-openable',
                        function() {
                            $(this)
                                .closest('.task')
                                .removeClass('state-collapsed');
                        }
                    )
                    // collapse task on blur if nothing happened
                    .on('blur', '.tasks-container .task.state-new input.name', function() {
                        var blurredTask = $(this).closest('.task');
                        window.setTimeout(function() {
                            // don't collapse if still focused inside
                            if ($(document.activeElement).closest('.task.state-new').length) return;

                            // don't collapse if we're assigning, i.e. we click into assignment
                            // before adding other details to the new task
                            if (blurredTask.hasClass('state-assigning')) return;

                            // don't collapse if not empty
                            if (blurredTask.find('input[name=title]').val().length) return;
                            if (blurredTask.find('textarea').val().length) return;

                            // don't collapse if date/time eip happening
                            if (blurredTask.find('.date .state-editing').length) return;

                            blurredTask.addClass('state-collapsed');
                        }, 200);
                    })

                    .on(
                        'click',
                        '.tasks-container .task:not(.state-new) .action-complete',
                        function() {
                            var taskRow = $(this).closest('.task');
                            var taskId = taskRow.find('input[name=taskId]').val();

                            Task.executeAction.apply(this, [
                                {
                                    successCallback: function(response) {
                                        taskRow.replaceWith(response.html);

                                        if (taskId) Task.refreshTask(taskId);

                                        try {
                                            Analytics.trackEvent(
                                                Analytics.categories.usage,
                                                Analytics.actions.usage.completeTask
                                            );
                                        } catch (e) {}
                                    },
                                },
                            ]);
                        }
                    )

                    .on(
                        'click',
                        '.tasks-container .task:not(.state-new) .action-delete-task',
                        function() {
                            var taskRow = $(this).closest('.task');
                            Task.executeAction.apply(this, [
                                {
                                    successCallback: function() {
                                        taskRow.fadeOut(function() {
                                            taskRow.remove();
                                        });
                                    },
                                    errorDefaultText: 'There was an error deleting this task',
                                    popminiConfirmOrientation: 'north-west',
                                    popminiErrorOrientation: 'north',
                                    popminiErrorAttachTo: taskRow,
                                },
                            ]);
                        }
                    )

                    // hacky focus styling for textareas, since they're so deep in the markup
                    .on('focus blur', '.tasks-container .task textarea', function(event) {
                        var task = $(this).closest('.task');
                        task.find('.dashboardable-inset').toggleClass(
                            'state-focus',
                            event.type == 'focusin'
                        );
                    })

                    // something about timepicker is preventing submit on enter
                    .on('keydown', '.tasks-container .task input.name', function(event) {
                        if (event.keyCode == Nut.keys.RETURN) {
                            var form = $(this)
                                .closest('.task')
                                .find('form.async');
                            form.trigger('submit');
                        }
                    })

                    .on(
                        'select2-selected',
                        '.tasks-container .task:not(.state-new) input[name=assignee]',
                        Task.assignTask
                    )

                    // really just use open/close events to keep the new task modal from hiding
                    .on('select2-open', '.tasks-container .task input[name=assignee]', function(
                        event
                    ) {
                        $(this)
                            .closest('.task')
                            .addClass('state-assigning');
                        $('div.select2-drop-active input.select2-input').attr(
                            'placeholder',
                            'Assign to…'
                        );
                    })
                    .on('select2-close', '.tasks-container .task input[name=assignee]', function(
                        event
                    ) {
                        $(this)
                            .closest('.task')
                            .removeClass('state-assigning');
                    });
            },

            updateAppearances: function(container) {
                if (!$(container).length) container = $('body');

                $('.task .moment').each(function() {
                    var $el = $(this),
                        timestamp = $el.data('date'),
                        momentObj = moment.unix(timestamp);

                    $el.text(momentObj.fromNow());
                    $el.attr('title', momentObj.format('dddd, MMMM Do YYYY, h:mm:ss a'));
                });

                container
                    .find('.task textarea:not(.has-elastic)')
                    .addClass('has-elastic')
                    .elastic({breathingRoom: false});
                Form.init.updateAppearances(container);
            },
        },

        asyncFormDidSave: function(result) {
            var $container = $('.tasks li.state-new'),
                $select2input = $container.find('input[name=assignee]'),
                $nameInput = $container.find('input[name=title]'),
                defaultAssignee = $select2input.data('defaultValue');
            $container.before(result.html);

            try {
                Analytics.trackEvent(
                    Analytics.categories.usage,
                    Analytics.actions.usage.createTask
                );
            } catch (e) {
                Nut.log('Error tracking ', e);
            }

            if (defaultAssignee) {
                $select2input.select2('val', defaultAssignee);
            }
            if ($nameInput.length) $nameInput.focus().trigger('keydown');
        },

        getTaskTemplate: function($task) {
            return $task.parents('.tasks-container').data('template');
        },

        getDataToSubmit: function(form) {
            var data = Form.getDataToSubmit(form),
                select = $(form)
                    .closest('.task')
                    .find('input[name=assignee]');
            if (!_.isUndefined(data.name)) delete data.name;
            if (!_.isUndefined(data.taskId)) delete data.taskId;
            data.assignee = select.val();

            return data;
        },

        refreshTask: function(id) {
            $(window).trigger(Task.events.TaskDidSubmitSuccessfully, id);
        },

        assignTask: function(event) {
            var taskRow = $(this).closest('.task');
            Task.executeAction.apply(this, [
                {
                    data: {entityId: event.choice.id},
                    errorDefaultText: 'There was an error assigning this task',
                    popminiConfirmOrientation: 'north-west',
                    popminiErrorOrientation: 'north',
                    popminiErrorAttachTo: taskRow,
                },
            ]);
        },

        executeAction: function(options) {
            var icon = $(this),
                taskRow = icon.closest('.task'),
                container = taskRow.parent(),
                actionUrl = icon.data('url'),
                data = options.data || {template: Task.getTaskTemplate(taskRow)},
                confirmText = icon.find('.js-confirm').text();

            options = $.extend(
                Task.defaultRequestOptions,
                {
                    popminiErrorAttachTo: icon,
                },
                options
            );

            var processClick = function() {
                taskRow.addClass('state-loading');

                Nut.ajax.post({
                    url: actionUrl,
                    data: data,
                    success: function(response) {
                        if (typeof options.successCallback == 'function')
                            options.successCallback(response);
                        else if (typeof response.html !== 'undefined')
                            taskRow.replaceWith(response.html);
                        Task.init.updateAppearances(container);
                    },
                    validate: function(validation) {
                        if (!validation.isValid) {
                            Form.handleValidationResult(validation.messages, taskRow);
                        }
                        taskRow.removeClass('state-loading');
                    },
                    error: function(response) {
                        var message =
                            response && response.message
                                ? response.message
                                : options.errorDefaultText;

                        Popmini.error(message, {
                            orientation: options.popminiErrorOrientation,
                            attachTo: options.popminiErrorAttachTo,
                        });
                        taskRow.removeClass('state-loading');
                    },
                });
            };

            if (confirmText) {
                Popmini.question(
                    confirmText,
                    {orientation: options.popminiConfirmOrientation, attachTo: icon},
                    function(confirmed) {
                        if (confirmed) processClick();
                    }
                );
            } else {
                processClick();
            }
        },
    };
})(jQuery));
var Mentions = (Nut.modules.Mentions = (function($) {
    return {
        cachedCollections: {},

        opt: {
            maxInitialResults: 25, // on @{followed by up to 2 chars}, only show first X results
            maxServerResults: 25,
            caseSensitive: true, // for local collections and non-@ mentions, case sensitivity on query versus name
        },

        go: function() {
            return;
        },

        init: {},

        filterResults: function(results, query, usingTriggerChar) {
            query = Mentions.opt.caseSensitive && !usingTriggerChar ? query : query.toLowerCase();
            return _.filter(results, function(result) {
                if (_.isUndefined(result.name) || _.isNull(result.name)) {
                    return false;
                }
                if (!Mentions.opt.caseSensitive || usingTriggerChar) {
                    return result.name.toLowerCase().indexOf(query) > -1;
                }
                return result.name.indexOf(query) > -1;
            });
        },

        getLocalData: function(collectionNames, query, usingTriggerChar) {
            var results = [];
            _.each(collectionNames, function(collectionName) {
                if (results.length > Mentions.opt.maxInitialResults) return;
                var collection;
                if (_.isUndefined(Mentions.cachedCollections[collectionName])) {
                    var localData = [];
                    if (typeof Peep !== 'undefined' && !_.isUndefined(Peep.currentPeep)) {
                        collection = Peep.currentPeep.get(collectionName);
                    }

                    if (typeof collection === 'undefined') {
                        collection = Nut.managedCollections[collectionName];
                    }
                    if (collection && collection.models) {
                        _.map(collection.models, function(entity) {
                            var listElement = Mentions.resultToElement(entity);
                            localData.push({
                                id: entity.id,
                                type: entity.get('type'),
                                name: entity.get('name'),
                                rawListItem: listElement,
                            });
                        });
                    }

                    Mentions.cachedCollections[collectionName] = localData;
                }

                collection = Mentions.cachedCollections[collectionName];
                if (collection) {
                    if (query && query.length) {
                        results = results.concat(
                            Mentions.filterResults(collection, query, usingTriggerChar)
                        );
                    } else {
                        results = results.concat(collection);
                    }
                }
            });
            return results;
        },

        resultToElement: function(result) {
            // for server responses, we'll have an unparsed object
            if (!_.isUndefined(result.entityName)) {
                result.type = result.entityName.toLowerCase();
                if (result.id && _.isNumber(result.id)) {
                    // convert solr response ids to i.e. 1-users
                    result.id = result.id + '-' + result.type;
                    result.entityId = result.id;
                }
                // users and leads aren't Relations (yet?)
                if (_.isFunction(Nut.Model[result.entityName].findOrCreate)) {
                    result = Nut.Model[result.entityName].findOrCreate(result);
                } else {
                    result = new Nut.Model[result.entityName](result, {parse: true});
                }
            }
            var entityType = result
                    .get('type')
                    .toLowerCase()
                    .replace(/s$/, ''),
                template = Nut.getTemplate('template-peep-list-' + entityType, entityType);
            return $(template(result));
        },

        // initializes a textarea with jquery-mentions-input
        MentionsInput: function(element) {
            Nut.log('MentionsInput instantiating for input element ', element);
            if (!element.is('textarea')) return;
            var mentionsCollections = element.data('collections').split(',');

            element.mentionsInput({
                showAvatars: true,
                onCaret: false,
                minCharsNoTrigger: false,
                elastic: false,
                templates: {
                    mentionItemSyntax: _.template('@[<%= type %>:<%= id %>]'),
                    mentionItemHighlight: _.template(
                        '<strong><span class="mention-rendered ignore-modal" data-entity-name="<%= type %>" data-entity-id="<%= id %>"><%- value %></span></strong>'
                    ),
                },
                onTriggerChar: function(query, callback) {
                    if (element.closest('.timeline-creator').is('.state-email')) {
                        return;
                    }
                    Nut.log('Showing local @ list with query ', query);
                    var filteredData = Mentions.getLocalData(mentionsCollections, query, true);
                    callback.call(this, _.first(filteredData, Mentions.opt.maxInitialResults));
                },
                onDataRequest: function(mode, query, callback, usingTriggerChar) {
                    if (element.closest('.timeline-creator').is('.state-email')) {
                        return;
                    }
                    Nut.log('MentionsInput request with query ', query);
                    var filteredData = Mentions.getLocalData(
                            mentionsCollections,
                            query,
                            usingTriggerChar
                        ),
                        knownEntities = _.map(filteredData, function(entity) {
                            return {
                                entityType: entity.type,
                                id: entity.id,
                            };
                        }),
                        data = {
                            rows: Mentions.opt.maxServerResults,
                            fields: [
                                {
                                    name: 'name',
                                    value: query,
                                },
                            ],
                            knownLocalEntities: knownEntities,
                        };

                    Nut.log('MentionsInput displaying local results ', filteredData);
                    callback.call(this, filteredData);

                    // using POST due to nginx query limits; local entities list is potentially large
                    Nut.ajax.post({
                        url: '/solr/find-mentionable-entities.json',
                        data: data,
                        success: function(response) {
                            var results = _.isUndefined(response.results) ? [] : response.results;
                            results = _.map(results, function(entity) {
                                var listElement = Mentions.resultToElement(entity);
                                return {
                                    id: entity.entityId,
                                    type: entity.entityName,
                                    name: entity.name,
                                    rawListItem: listElement,
                                };
                            });
                            Nut.log('MentionsInput displaying results from server', results);

                            // return unique results based on ids
                            results = _.uniq(filteredData.concat(results), false, function(item) {
                                return item.id;
                            });
                            callback.call(this, results);
                        },
                        error: function(xhr) {
                            Nut.error('Error retrieving autocomplete data ', xhr);
                            callback.call(this, filteredData);
                        },
                    });
                },
            });
        },
    };
})(jQuery));
Invite = Nut.modules.Invite = (function($) {
    return {
        go: function() {},
        init: {},
        asyncFormDidSave: function(response) {
            if (response.html) {
                Analytics.trackEvent(
                    Analytics.categories.onboarding,
                    Analytics.actions.onboarding.inviteUser
                );

                Popover.close();
                Popover.open(response.html, $('#invite-your-team'));
            }
        },
    };
})(jQuery);
var Analytics = (Nut.modules.Analytics = _.extend(
    typeof AnalyticsConfiguration === 'undefined' ? {} : AnalyticsConfiguration,
    (function($) {
        return {
            // https://www.intercom.com/help/en/articles/2894-customize-the-intercom-messenger-technical
            intercomMessengerLaunchSelector: '#support-intercom-messenger',
            runWhenLoggedOut: true,
            intercomWasInitialized: false,
            opt: {},

            go: function() {
                Analytics.start();

                if (typeof analytics !== 'undefined' && Analytics.enableSegment == 1) {
                    try {
                        // Segment integration - User Identity
                        analytics.identify(Analytics.identity, Analytics.identifyParams, {
                            integrations: {
                                Intercom: Analytics.intercomIntegration,
                            },
                        });

                        Analytics.intercomWasInitialized = true;

                        // Intercom deduplicates requests sent to them by timestamp. When we call `identify()` above
                        // and then immediatly the `page()` call below, the requests get de-deuplicated and we loose
                        // meta-data in Intercom like "company web sessions" and "last seen". See NUT-4354. Wrapping
                        // the call to `page()` in a `setTimeout()` splits up the requests enough to not be de-duped.
                        setTimeout(function() {
                            analytics.page();
                        }, 2000);

                        analytics.ready(function() {
                            try {
                                // Update our Intercom settings to target one of our app help
                                // menu items as the user interactable element to bring up
                                // [Intercom's Messenger UI][1]. We wrap all of this in a
                                // try-catch to just be super safe that we don't crash because
                                // of some Intercom wonkiness.
                                //
                                // [1]: https://docs.intercom.io/configure-intercom-for-your-product-or-site/customize-the-intercom-messenger/customize-the-intercom-messenger-technical
                                Intercom('update', {
                                    custom_launcher_selector: '#support-intercom-messenger',
                                });
                            } catch (err) {}
                        });
                    } catch (err) {}
                }
            },

            init: {},

            categories: {
                onboarding: 'onboarding',
                billing: 'billing',
                filtering: 'filtering',
                integrations: 'integrations',
                imports: 'imports',
                usage: 'usage',
                views: 'views',
            },

            /**
             * To the best of our abilities, application event names should follow the
             * `noun_pastTenseVerb` naming convention.
             *
             * For now, to get an action to appear in Segment/Intercom, add it to the
             * `segmentEvents` collection.
             */
            actions: {
                onboarding: {
                    inviteUser: 'inviteUser',
                },
                billing: {
                    purchase: 'purchase',
                },
                filtering: {
                    didAddFilters: 'didAddFilters',
                    didRemoveAllFilters: 'didRemoveAllFilters',
                    didRemoveFilters: 'didRemoveFilters',
                    didAddFields: 'didAddFields',
                    didRemoveFields: 'didRemoveFields',
                    didUpdateFilters: 'didUpdateFilters',
                    didReorderColumns: 'didReorderColumns',
                    didResizeColumn: 'didResizeColumn',
                    didPerformExportAction: 'didPerformExportAction',
                    didPerformDeleteAction: 'didPerformDeleteAction',
                    didPerformMergeAction: 'didPerformMergeAction',
                    didPerformEmailAction: 'didPerformEmailAction',
                    didPerformMailchimpAction: 'didPerformMailchimpAction',
                    didPerformConstantContactAction: 'didPerformConstantContactAction',
                    didPerformEditAction: 'didPerformEditAction',
                    didPerformAudienceAction: 'didPerformAudienceAction',
                },
                integrations: {
                    twitterActivated: 'twitterActivated',
                },
                imports: {
                    choseImportType: 'choseImportType',
                    previewImport: 'previewImport',
                    completeImport: 'completeImport',
                },
                usage: {
                    uploadFile: 'uploadFileAttachment',
                    addComment: 'addComment',
                    addTimelineNote: 'addTimelineNote',
                    logTimelineActivity: 'logTimelineActivity',
                    logTimelineActivityViaKIT: 'logTimelineActivityViaKIT',
                    createTask: 'createTask', // remove this as we migrate to REST API
                    completeTask: 'completeTask',
                    completeStep: 'completeStep',
                    openNotifications: 'openNotifications',
                    createGlobalContact: 'createGlobalContact',
                    createGlobalAccount: 'createGlobalAccount',
                },
                views: {
                    showGlobalCreateContact: 'showGlobalCreateContact',
                    showGlobalCreateAccount: 'showGlobalCreateAccount',
                    showCalendarView: 'CalendarViewSeen',
                    showOnboardingPhoneModal: 'OnboardingClickToCallSeen',
                    initializeMapView: 'initializeMapView',
                    initializeListBetaLeads: 'initializeListBetaLeads',
                    initializeListBetaContacts: 'initializeListBetaCompanies',
                    initializeListBetaAccounts: 'initializeListBetaPeople',
                    leadsExploreView: 'leads_explore_viewed',
                },
            },

            /**
             * A list of whitelisted events to track within Segment, and therefore Intercom.
             *
             * This exists because we're shimming in our old-school Analytics track to
             * proxy to Intercom. The originaly demand for this was to add Intercom
             * tracking for ODRs (see https://nutshell.atlassian.net/browse/NUT-4923).
             * Instead of suddenly sending over (potentially) a bunch of cruft to Intercom,
             * let's start small and work our way up. We can either add more events ot this
             * whitelist, or remove it entirely to send open the floodgates to Segment &
             * Intercom.
             */
            segmentEvents: function() {
                return [Analytics.actions.views.leadsExploreView];
            },

            // we assume GTM code is already loaded (just after opening <body>)
            // note, this may be before dom isReady
            start: function() {
                // trigger page view event immediately
                dataLayer.push({
                    event: 'initialPageLoad',
                    appPrefix: this.getPrefix(),
                    userId: window.analyticsConfig.userId
                        ? window.analyticsConfig.userId
                        : undefined,
                    evalValue: window.analyticsConfig.isEval ? 'Eval' : 'Customer',
                    adminValue: window.analyticsConfig.isAdmin ? 'Admin' : 'Standard',
                    isMacApp: window.analyticsConfig.isMacApp ? 'Yes' : 'No',
                    isDemoSilo: window.analyticsConfig.isDemoSilo ? 'Yes' : 'No',
                    pageUrl: window.analyticsConfig.url ? window.analyticsConfig.url : undefined,
                    pageTitle: window.analyticsConfig.url
                        ? window.analyticsConfig.url
                        : document.title,
                });

                // Hotjar snippet to tag all page loads that are
                // `analyticsConfig.isDemoSilo` with 'demosilo'
                window.hj =
                    window.hj ||
                    function() {
                        (hj.q = hj.q || []).push(arguments);
                    };
                if (window.analyticsConfig.isDemoSilo) {
                    hj('tagRecording', ['demosilo']);
                }
            },

            // url better start with a slash.
            pageview: function(url) {
                dataLayer.push({
                    event: 'specialPageview',
                    pageUrl: url,
                    pageTitle: url,
                });
            },

            // eventCategory, eventAction defined in the Analytics.js object
            // properties: { key: value, ... }, only the first value will be sent to GA as the eventLabel,
            // we're just discarding the rest for now but could eventually be stored in GTM
            // you can also inclue a property of 'googleValue' which must be an int for a google numeric value
            trackEvent: function(eventCategory, eventAction, properties) {
                if (_.isUndefined(eventCategory)) Nut.error('eventCategory must be defined');
                if (_.isUndefined(eventAction)) Nut.error('eventAction must be defined');

                properties = typeof properties == 'undefined' ? {} : properties;
                var eventLabel = (eventValue = undefined);

                // the first value pair from properties is used as the label for google analytics
                var propertyValues = _.values(properties);
                if (propertyValues && propertyValues[0]) {
                    eventLabel = propertyValues[0];
                }

                // if we've got properties.googleValue, coerce to an int and use as value
                if (
                    !_.isUndefined(properties.googleValue) &&
                    !isNaN(parseInt(properties.googleValue, 10))
                ) {
                    eventValue = Math.round(parseInt(properties.googleValue));
                }

                dataLayer.push({
                    event: 'nutshellEvent',
                    eventName: eventAction,
                    eventCategory: eventCategory,
                    eventLabel: eventLabel,
                    eventValue: eventValue,
                });

                // Segment / Intercom tracking
                try {
                    if (
                        typeof analytics !== 'undefined' &&
                        Analytics.enableSegment == 1 &&
                        _.contains(Analytics.segmentEvents(), eventAction)
                    ) {
                        // see https://segment.com/docs/sources/website/analytics.js/#track
                        analytics.track(
                            eventAction,
                            _.extend({app: 'web'}, properties),
                            {},
                            function() {
                                // Forcing an Intercom update if it's available to trigger any Intercom
                                // workflows based on events, which normally requires a page reload. See
                                // @andyfowler's summary here:
                                // https://github.com/nutshellcrm/nutshell/pull/3704#issuecomment-264194628
                                if (_.isFunction(window.Intercom)) {
                                    window.Intercom('update');
                                }
                            }
                        );
                    }
                } catch (err) {
                    // Don't want to crash if anything goes awry with this analytics.track()
                    // call, silencing errors
                }
            },

            purchase: function(price) {
                Analytics.trackEvent(
                    Analytics.categories.billing,
                    Analytics.actions.billing.purchase,
                    {price: price, googleValue: price}
                );
            },

            getPrefix: function() {
                return window.analyticsConfig.isDemo ? '/demo' : '/app';
            },
        };
    })(jQuery)
));
ClickToCall = Nut.modules.ClickToCall = (function($) {
    return {
        go: $.noop,

        init: {},

        getEntityId: function(model, $el) {
            if (model) return model.id;
            return $el.parents('.popover-hoverview').data().entityApiId;
        },

        handlePhoneCall: function(e, model) {
            // click-to-call only handles the case of legacy "beta" in-app calling and new
            // Nutshell Pro click-to-call
            // Everything else falls through
            var phonecallerType = NutshellFrontendSettings.phonecaller_type;
            var isClickToCallEnabled =
                phonecallerType === 'clicktocall' && NutCapabilities.clickToCall.isAvailable;

            // Fail early just in case we clicked a rogue call button.
            if (!isClickToCallEnabled) return;

            e.stopPropagation();
            e.preventDefault();

            var $this = $(e.currentTarget).hasClass('action-call-number')
                ? $(e.currentTarget)
                : $(e.currentTarget).parent('.action-call-number');
            var formattedPhoneNumber = $this.find('[data-name=number]').length
                ? $this.find('[data-name=number]').text()
                : $this.text();
            var rawPhoneNumber = $this.data().rawPhoneNumber;
            var entityId = ClickToCall.getEntityId(model, $this);
            var altYesActionBtn =
                '<a href="#" class="button button-interaction size-small action-yes action-alternate"><span>Call & record</span></a>';

            // disable button while popmini is open
            if ($this.hasClass('state-call-prompt')) return;
            $this.toggleClass('state-call-prompt', true);

            Popmini.question(
                'Call ' + formattedPhoneNumber + '?',
                {
                    attachTo: $this,
                    insertAfter: $this,
                    yesText: 'Call',
                    noText: 'Cancel',
                    alternateYesAction: altYesActionBtn,
                    classes: 'type-popover',
                    orientation: 'south-west',
                    my: 'right top',
                    at: 'right bottom',
                },
                function(confirmed, shouldRecord) {
                    $this.toggleClass('state-call-prompt', false);
                    if (!confirmed) return;

                    $this.parent().toggleClass('state-calling-active', true);

                    ClickToCall.clickToCall(
                        $this,
                        entityId,
                        rawPhoneNumber,
                        formattedPhoneNumber,
                        shouldRecord
                    );
                }
            );

            return true;
        },

        clickToCall: function($this, entityId, rawPhoneNumber, formattedPhoneNumber, shouldRecord) {
            Nut.Phone.call({
                entityId: entityId,
                phoneNumber: {
                    E164: rawPhoneNumber,
                    numberFormatted: formattedPhoneNumber,
                },
                shouldRecord: shouldRecord,
            });
        },
    };
})(jQuery);
/*eslint-disable*/
Capabilities = Nut.modules.Capabilities = (function($) {
    return {
        go: function() {
            for (var fn in this.init) {
                this.init[fn].apply(this);
            }
        },

        init: {
            updateAppearances: function() {
                var anchors = $('[data-capabilities-id]');

                anchors.each(Capabilities.renderLabel);
            },
        },

        renderLabel: function() {
            var $this = $(this),
                badgeType = $this.data().capabilitiesBadgeType,
                capability = NutCapabilities[$this.data().capabilitiesId];

            if (!capability) return;

            if (capability.label && !$this.data().capabilitiesClickAnchorOnly) {
                var nutshellFeatureLabelWrapperNode = $('<div>').addClass(
                    'nutshell-pro-label-wrapper'
                );

                var props = badgeType ? {type: badgeType} : {};
                var nutshellFeatureLabel = React.createElement(Nut.ui.FeatureLabel, props, []);

                ReactDOM.render(nutshellFeatureLabel, nutshellFeatureLabelWrapperNode.get(0));

                $this.addClass('has-feature-label').append(nutshellFeatureLabelWrapperNode);
            }

            if (!capability.isAvailable) {
                $this
                    .addClass('state-feature-unavailable')
                    .on('click', Capabilities.handleClickLabelAnchor);
            }
        },

        handleClickLabelAnchor: function(e) {
            e.preventDefault();
            e.stopImmediatePropagation();

            var capabilitiesId = $(this).data().capabilitiesId;
            var featureUnavailableModalWrapperNode = document.createElement('div');

            var props = {
                isOpen: true,
                onModalClose: function() {
                    ReactDOM.unmountComponentAtNode(featureUnavailableModalWrapperNode);
                },
            };

            var FeatureUnavailableModal = React.createElement(
                Nut.ui.FeatureUnavailableModal,
                props,
                []
            );

            ReactDOM.render(FeatureUnavailableModal, featureUnavailableModalWrapperNode);
        },
    };
})(jQuery);
Peep = Nut.modules.Peep = (function($) {
    return {
        fullContactPoll: null,
        timeline: null,
        currentView: null,

        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        events: {
            hackyAddPeepValueToLeadForm: 'hackyAddPeepValueToLeadForm',
            openConstantContactEmailPreview: 'openConstantContactEmailPreview',
            openMarketingEmailPreview: 'openMarketingEmailPreview',
            openViewFormSubmissionModal: 'openViewFormSubmissionModal',
        },

        init: {
            setUpEvents: function() {
                $(document).on(
                    'click',
                    '.peep-body .action-new-activity',
                    Peep.openNewActivityModal
                );
                $(document).on(
                    'click',
                    '.peep-container .peep-subheader .behavior-dupecheck',
                    function(event) {
                        event.preventDefault();
                        event.stopPropagation();
                        Dupes.showPopover(Peep.getPeepType(), Peep.getPeepId(), $(this));
                    }
                );

                $(document).on(
                    'click',
                    '#peep .mailchimp-popover .action-export-mailchimp',
                    Peep.exportToMailChimp
                );

                $(document).on('click', '.action-call-number', Peep.callPhoneNumber);

                $(document).on(Nut.events.global.entityWasDeleted, function(event, entity) {
                    Peep.reloadActivities();
                    Peep.reloadTimeline();
                });

                $(window).on(Timeline.events.TimelineRefresh, function() {
                    Peep.fetchCurrentPeep();
                });
            },
            setupCardEvents: function() {
                // hack until we do better card support, since card templates depend on a click
                // within their float-area. this just triggers a hidden element based on green link
                $(document).on('click', '.peep-cards .action-new', function() {
                    $(this)
                        .closest('.peep-cards')
                        .removeClass('state-empty')
                        .find('.float-area:first .empty-placeholder.hidden')
                        .click();
                });

                // hacks to set state-empty on card adding / removing
                $(document)
                    .on(Nut.events.card.wasAdded, '.peep-cards', Peep.cardWasAdded)
                    .on(
                        [Nut.events.card.wasDeleted, Nut.events.card.transientWasDeleted].join(' '),
                        '.peep-cards',
                        Peep.cardWasDeleted
                    );
            },

            preparePage: function() {
                Peep.initializeSidebarEipContainers();
                Peep.setUpViews();
            },
        },

        setUpViews: function() {
            Peep.fetchCurrentPeep(true);
        },

        getPeepType: function() {
            return $('.peep-container').data('entityName');
        },

        getPeepId: function() {
            return $('.peep-container').data('entityId');
        },

        initializeSidebarEipContainers: function() {
            // sidebar custom fields
            $('.peep-customfields .eip-container:not(.eip-ready):visible').each(function() {
                EditInPlace.factory(
                    this,
                    $.extend(
                        {},
                        {
                            editOnContainerClick: true,
                        },
                        $(this).data()
                    )
                );
            });
        },

        fetchCurrentPeep: function(isInitialLoad) {
            var peepType = Peep.getPeepType();
            var self = this;

            if (!peepType) return;

            if (!_.isUndefined(Peep.currentPeep)) {
                Nut.Model[peepType]
                    .findOrCreate({
                        id: Peep.currentPeep.id,
                    })
                    .fetch({
                        success: function(model, response) {
                            self.currentView = new Nut.View.Peep({model: model});

                            Peep.currentPeep = model;

                            // instantiate all of the relations now that the compound documents have been parsed
                            _.invoke(Peep.currentPeep.getRelations(), 'findRelated');
                            // maybe invoke Peep.currentPeep.fetchRelated(relationName) in case the compound docs are missing?

                            if (isInitialLoad) {
                                model.trigger('initialLoad');
                                if (_.isFunction(model.pollForFullcontactChanges)) {
                                    Peep.fullContactPoll = model.pollForFullcontactChanges();
                                }

                                Peep.timeline = window.Timeline3.setUpFeed({
                                    entityId: Peep.currentPeep.id,
                                });
                            }
                            // Create event the "old fashioned" way for IE support
                            var event = document.createEvent('Event');
                            event.initEvent('Peep initialized', true, true);
                            document.dispatchEvent(event);
                        },
                        error: function(model, xhr, options) {
                            if (xhr.status !== 0) {
                                Nut.error(
                                    'error fetching peep',
                                    model.type,
                                    model.get('id'),
                                    xhr.status,
                                    xhr.statusText
                                );
                            } else {
                                $('div.failedloading.flash-message-error').html(
                                    'The current page did not finish loading because the request was interrupted. ' +
                                        'Some functionality may be unavailable until the page is reloaded.'
                                );
                            }
                            $('.peep-container').addClass('state-failedloading');
                        },
                    });
            }
        },

        reloadTimeline: function() {
            // known bug that we don't support this as of timeline3
        },

        unloadPeep: function() {
            if (this.fullContactPoll) {
                clearInterval(this.fullContactPoll);
            }
            if (this.timeline) {
                this.timeline.tearDown();
            }
            if (this.currentView) {
                this.currentView.tearDown();
            }

            Peep.currentPeep = undefined;
        },

        callPhoneNumber: function(e) {
            ClickToCall.handlePhoneCall(e, Peep.currentPeep);
        },

        loadActivities: function(activityContainer) {
            Nut.ajax.get({
                url: '/activity/list-scheduled.json',
                exclusive: {tag: 'activities', order: 'LIFO'},
                data: {
                    entityName: activityContainer.data('entityName'),
                    entityId: activityContainer.data('entityId'),
                },
                success: function(result) {
                    // check for result, since exclusive requests call success callback w/ no response
                    if (result) {
                        Peep.activitiesDidLoad(activityContainer, result);
                    }
                },
            });
        },

        exportToMailChimp: function(e) {
            e.preventDefault();
            // Ensure we are currently on a peep page.
            if (!$('.peep-subheader .type-name').length) {
                return;
            }

            var postData = {
                listId: $('#mailchimp-list-id').val(),
                sendWelcome: $('#mailchimp-send-welcome').is(':checked') ? 1 : 0,
                target: 'MailChimp',
                modelId: Peep.currentPeep.id,
            };

            var urlBase = Peep.currentPeep.get('type') === 'accounts' ? 'companies' : 'people';

            Nut.ajax.post({
                url: '/' + urlBase + '/export',
                data: postData,
                success: function(response) {
                    Popover.close();
                    Popmini.open('Exporting person to MailChimp. It should be visible shortly.', {
                        attachTo: $('.peep-subheader .type-name'),
                        classes: 'type-message',
                        orientation: 'south',
                    });
                },
            });
        },

        activitiesDidLoad: function(activityContainer, result) {
            var list = activityContainer.find('.timeline-list').empty();

            if ($(result.list).length) $(list).prepend(result.list);

            activityContainer.toggleClass(
                'state-empty',
                !activityContainer.find('.timeline-entry').length
            );
        },

        reloadActivities: function() {
            if (!$('.peep-container .activities-container').length) return;

            Peep.loadActivities($('.peep-container .activities-container'));
        },

        openNewActivityModal: function(event) {
            Modal.open({
                url: '/activity/view/new',
                urlData: {
                    participants: [{entityName: Peep.getPeepType(), entityId: Peep.getPeepId()}],
                },
            });
        },

        checkForDuplicates: function() {
            _.defer(Dupes.check, Peep.getPeepType(), Peep.getPeepId(), $('.peep-container'));
        },

        cardWasAdded: function(event) {
            $(this).removeClass('state-empty');
            if (Peep.currentPeep) {
                Peep.currentPeep.addRelationFromCard($(event.target));
            }
        },

        cardWasDeleted: function(event) {
            if (!$(this).find('.float-area .tile-filled').length) $(this).addClass('state-empty');
            if (Peep.currentPeep) {
                Peep.currentPeep.removeRelationFromCard($(event.card));
            }
        },

        reloadCards: function() {
            var peep = Peep.currentPeep;

            if (!peep) return;

            var url =
                peep.type == 'contacts'
                    ? '/person/' + peep.get('id') + '/cards.json'
                    : '/company/' + peep.get('id') + '/cards.json';

            Nut.ajax.get({
                url: url,
                success: function(result) {
                    var areaToReplace = $('.peep-cards'),
                        newCards = $(result.html);

                    // don't replace cards if they're in the middle of editing one!
                    if (areaToReplace.find('.state-half-baked:visible').length) return true;
                    if (areaToReplace.find('.eip-container.state-editing').length) return true;

                    areaToReplace.replaceWith(newCards);
                    Form.init.updateAppearances(newCards);
                    Dupes.checkAll();
                },
            });
        },
    };
})(jQuery);
QuickReport = Nut.modules.QuickReport = (function($) {
    return {
        opt: {},

        go: function() {
            for (var fn in this.init) this.init[fn].apply(this);
        },

        init: {
            setUpEvents: function() {
                // changes to options trigger updates
                $(document)
                    .on('change', '.quickreport-options input', QuickReport.optionWasChanged)
                    .on(
                        Nut.events.capsule.wasAdded + ' ' + Nut.events.capsule.wasDeleted,
                        '.quickreport-options div',
                        QuickReport.optionWasChanged
                    );

                // only reload one quickreport if internal option changes
                $(document).on('change', '.quickreport input', function() {
                    QuickReport.updateData($(this).closest('.quickreport'));
                });

                var doneResizingWindow = void 0;
                $(window).on('resize', function(e) {
                    if (typeof doneResizingWindow === 'number') clearTimeout(doneResizingWindow);
                    doneResizingWindow = setTimeout(function() {
                        QuickReport.optionWasChanged();
                    }, 1000);
                });

                return this;
            },
            updateAppearances: function() {
                QuickReport.optionWasChanged();
            },
        },

        updateData: function(quickreport) {
            var placeholder = quickreport.find('.graph'),
                type = placeholder.data('type'),
                data = {
                    report: type,
                };

            _.extend(data, QuickReport.getOptions(quickreport));

            quickreport.addClass('state-loading');

            Nut.ajax.get({
                url: '/quick-report',
                data: data,
                exclusive: {tag: 'quickreport-' + type, order: 'LIFO'},
                error: function() {
                    quickreport.removeClass('state-loading');
                },
                success: function(response) {
                    QuickReport.didReceiveData(response, quickreport);
                },
            });
        },

        didReceiveData: function(response, quickreport) {
            var placeholder = $(quickreport).find('.graph'),
                type = placeholder.data('type'),
                graph = placeholder.data('graph');

            quickreport
                .removeClass('state-loading')
                .find('.stats')
                .html(response.stats);

            if (graph) {
                graph.setOptions(response).plot();
            } else {
                graph = new window['Graph' + type](placeholder, response);
            }

            $('.quickreport-period').text(response.period);
        },

        optionWasChanged: function(event) {
            $('.quickreport').each(function() {
                QuickReport.updateData($(this));
            });
        },

        getOptions: function(quickreport) {
            var filterCapsule = $('.select2-wrapper input.collection-select'),
                options = {
                    period: $('input[name=quickreport-period]').val(),
                },
                localOptions = quickreport.serializeArrayToObject();

            if (filterCapsule.length && filterCapsule.val().length) {
                options.filterEntity = filterCapsule.val();
            }

            // quickreport-specific options
            if (_.size(localOptions)) options.options = localOptions;

            return options;
        },
    };
})(jQuery);
